import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './HimalayaStyle.css';
import FulfilledOrdersSection from './FulfilledOrdersSection';
import PLUFilter from './components/PLUFilter';
import DashboardUserView from './components/DashboardUserView';
import PaymentInputRow from './components/PaymentInputRow';

// ═══════════════════════════════════════════════════════════════════════════════
// 💰 VERSION 3.0 BALANCE CALCULATION - SINGLE SOURCE OF TRUTH
// ═══════════════════════════════════════════════════════════════════════════════
// THESE FUNCTIONS ARE THE ONLY WAY TO CALCULATE BALANCES IN FRONTEND
// ALL CODE MUST USE THESE - NO INLINE CALCULATIONS ALLOWED
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * 💰 VERSION 3.0: Calculate outstanding balance for a single order.
 * 
 * RULES:
 * 1. If payment_status is 'paid' or 'fully_paid' → Return 0 (trust user's action)
 * 2. Otherwise: Outstanding = Grand Total - (Cash Paid + Bank Paid + Credit Paid)
 * 
 * This ensures orders marked as paid by the user don't appear in Credit Accounts.
 */
const calculateOrderOutstanding = (order) => {
  // 💰 BULLETPROOF: Calculate purely from amounts. Do NOT trust payment_status flag.
  // ANY positive outstanding balance counts, no tolerance.
  const grandTotal = parseFloat(order.grand_total || 0);
  const cashPaid = parseFloat(order.cash_paid_amount || 0);
  const bankPaid = parseFloat(order.bank_paid_amount || 0);
  const creditPaid = parseFloat(order.credit_paid_amount || 0);
  const totalPaid = cashPaid + bankPaid + creditPaid;
  
  const outstanding = grandTotal - totalPaid;
  
  if (outstanding <= 0) {
    return 0;
  }
  
  return Math.max(0, Math.round(outstanding * 100) / 100);
};

/**
 * 💰 BULLETPROOF: An order is fully paid only when there is truly zero outstanding.
 */
const isOrderFullyPaid = (order) => {
  return calculateOrderOutstanding(order) <= 0;
};

/**
 * 💰 BULLETPROOF: Calculate total credit balance for a customer from orders.
 * Includes ANY order (regardless of status or payment_method) with outstanding > 0.
 */
const calculateCustomerCreditBalance = (orders, accountPaymentsTotal = 0) => {
  let totalOutstanding = 0;
  let orderCount = 0;
  
  for (const order of orders) {
    if ((order.status || '').toLowerCase() === 'deleted') continue;
    
    const outstanding = calculateOrderOutstanding(order);
    if (outstanding > 0) {
      totalOutstanding += outstanding;
      orderCount++;
    }
  }
  
  const netBalance = Math.max(0, totalOutstanding - accountPaymentsTotal);
  
  return {
    totalOutstanding: Math.round(totalOutstanding * 100) / 100,
    accountPayments: accountPaymentsTotal,
    netBalance: Math.round(netBalance * 100) / 100,
    orderCount
  };
};

// ═══════════════════════════════════════════════════════════════════════════════

// CONFIGURE AXIOS DEFAULTS FOR BETTER NETWORK RESILIENCE
axios.defaults.timeout = 15000; // 15 second default timeout
axios.defaults.retry = 3; // Retry failed requests 3 times
axios.defaults.retryDelay = 1000; // 1 second delay between retries

// Add axios interceptor for automatic retries on network errors AND forced logout on 401
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // 🔒 CRITICAL: Force logout on 401 (session expired/password changed)
    // This handles the cross-device forced logout when password is changed
    if (error.response?.status === 401) {
      const errorMessage = error.response?.data?.detail || '';
      // Check if this is a session expiration due to password change
      if (errorMessage.includes('Session expired') || errorMessage.includes('new password')) {
        console.log('🔒 Session invalidated - forcing logout');
        // Clear all auth tokens from localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('dashboard_token');
        localStorage.removeItem('dashboard_username');
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_username');
        localStorage.removeItem('customer_token');
        // Force redirect to login page
        window.location.href = '/?forced_logout=true';
        return Promise.reject(error);
      }
    }
    
    // Only retry if we haven't exceeded retry count and it's a network/timeout error
    if (
      !config.__retryCount && 
      config.retry && 
      (error.code === 'ECONNABORTED' || 
       error.message.includes('Network Error') || 
       error.response?.status >= 500)
    ) {
      config.__retryCount = 0;
    }
    
    if (
      config.__retryCount < config.retry &&
      (error.code === 'ECONNABORTED' || 
       error.message.includes('Network Error') || 
       error.response?.status >= 500)
    ) {
      config.__retryCount += 1;
      console.log(`🔄 Retrying request (${config.__retryCount}/${config.retry}):`, config.url);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, config.retryDelay || 1000));
      
      return axios(config);
    }
    
    return Promise.reject(error);
  }
);

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get WebSocket URL from API URL
const getWebSocketUrl = () => {
  const apiUrl = API || window.location.origin;
  const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
  const wsHost = apiUrl.replace(/^https?:\/\//, '');
  return `${wsProtocol}://${wsHost}/ws/sync`;
};

// Error Boundary Component to catch JavaScript errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          background: '#f8f8f8',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div>
            <h2 style={{ color: '#d4af37', marginBottom: '20px' }}>PLUSH LIVING LTD</h2>
            <h3 style={{ color: '#333', marginBottom: '15px' }}>Something went wrong</h3>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              We&apos;re experiencing a technical issue. Please try refreshing the page.
            </p>
            <button 
              onClick={() => window.location.reload()} 
              style={{
                background: '#d4af37',
                color: '#2a2a2a',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Refresh Page
            </button>
            <details style={{ marginTop: '20px', textAlign: 'left', maxWidth: '600px' }}>
              <summary style={{ cursor: 'pointer', color: '#666' }}>Technical Details</summary>
              <pre style={{ 
                fontSize: '12px', 
                color: '#999', 
                background: '#f0f0f0', 
                padding: '10px', 
                borderRadius: '5px',
                marginTop: '10px',
                overflow: 'auto'
              }}>
                {this.state.error && this.state.error.toString()}
              </pre>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main App Component - Plush Living LTD
function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [customer, setCustomer] = useState(null);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false); // Explicitly false for public users
  const [loggedInAdminUsername, setLoggedInAdminUsername] = useState(''); // Track which admin is logged in
  // Admin permissions state
  const [adminPermissions, setAdminPermissions] = useState({
    allowedSections: null,  // null = all access, array = specific sections only
    spectateSections: null, // array of view-only sections
    role: null,             // 'spectator', 'full_admin', 'custom', etc.
    priceAccessLevels: ['retail'],
    canAccessDashboard: false,  // Can view Business Dashboard
    canAccessSettings: false    // Can view Settings
  });
  const [showAdminPortalButton, setShowAdminPortalButton] = useState(false); // NEW: Track admin parameter
  const [showProfileDashboard, setShowProfileDashboard] = useState(false);
  const [headerShouldShow, setHeaderShouldShow] = useState(true); // Explicit header control
  const [wholesaleStatus, setWholesaleStatus] = useState(null);
  const [wholesaleApplications, setWholesaleApplications] = useState([]);
  const [loadingWholesaleApplications, setLoadingWholesaleApplications] = useState(false);
  const [activeProfileTab, setActiveProfileTab] = useState('profile');
  const [productsPage, setProductsPage] = useState(0);
  const [galleryItems, setGalleryItems] = useState([]);
  const [showAddGalleryModal, setShowAddGalleryModal] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard'); // Admin portal active tab
  const [selectedOrder, setSelectedOrder] = useState(null); // For viewing order details
  const [showOrderViewModal, setShowOrderViewModal] = useState(false);
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false);
  const [creditNoteData, setCreditNoteData] = useState({ order_ids: [], items: [], reason: '', notes: '', total_amount: 0 });
  const [showOrderEditModal, setShowOrderEditModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnOrder, setReturnOrder] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  // Dashboard user state
  const [isDashboardUser, setIsDashboardUser] = useState(false);
  const [dashboardUserInAdminMode, setDashboardUserInAdminMode] = useState(false); // Track when dashboard user is viewing admin sections
  const [dashboardData, setDashboardData] = useState(null);
  const [showDashboardLogin, setShowDashboardLogin] = useState(false);
  const [dashboardUsername, setDashboardUsername] = useState('');
  const [dashboardPassword, setDashboardPassword] = useState('');
  const [showOriginalOrderModal, setShowOriginalOrderModal] = useState(false); // For viewing original order before edit
  const [originalOrderToView, setOriginalOrderToView] = useState(null); // The original order snapshot

  // Function to return dashboard user from admin view back to dashboard
  const returnToDashboardUser = () => {
    setDashboardUserInAdminMode(false);
    setIsDashboardUser(true);
    setIsAdminLoggedIn(false);
    setCurrentPage('home');
  };

  // Fetch Dashboard Summary Data - moved to App level so it can be called on page load
  const fetchDashboardData = async (token) => {
    try {
      const response = await axios.get(`${API}/api/dashboard/summary`, {
        headers: { 'Authorization': `Bearer ${token || localStorage.getItem('dashboard_token')}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  // Dashboard Logout - at App level for global access
  const handleDashboardLogout = () => {
    localStorage.removeItem('dashboard_token');
    setIsDashboardUser(false);
    setDashboardUserInAdminMode(false);
    setDashboardData(null);
  };

  // Dashboard User Login Handler - at App level for global access
  const handleDashboardLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/api/dashboard/login`, {
        username: dashboardUsername,
        password: dashboardPassword
      });
      
      if (response.data.access_token) {
        localStorage.setItem('dashboard_token', response.data.access_token);
        localStorage.setItem('dashboard_username', dashboardUsername); // Store username for logout check
        setIsDashboardUser(true);
        setShowDashboardLogin(false);
        setDashboardUsername('');
        setDashboardPassword('');
        
        // Fetch dashboard data AND fulfilled orders for INSTANT sync with admin
        fetchDashboardData(response.data.access_token);
        loadFulfilledOrders();
      }
    } catch (error) {
      alert('Invalid credentials. Please try again.');
    }
  };

  // Validate admin session on page load with simplified approach
  useEffect(() => {
    const validateAdminSession = async () => {
      const adminToken = localStorage.getItem('admin_token');
      const storedUsername = localStorage.getItem('admin_username');
      const storedPermissions = localStorage.getItem('admin_permissions');
      
      if (!adminToken) {
        setIsAdminLoggedIn(false);
        setLoggedInAdminUsername('');
        return;
      }

      // Simply trust the token exists and set login state
      // The backend endpoints will handle token validation
      setIsAdminLoggedIn(true);
      setLoggedInAdminUsername(storedUsername || '');
      
      // Restore permissions from localStorage
      if (storedPermissions) {
        try {
          const perms = JSON.parse(storedPermissions);
          setAdminPermissions(perms);
          console.log('📋 Admin permissions restored from localStorage:', perms);
        } catch (e) {
          console.error('Failed to parse stored permissions:', e);
        }
      }
      
      console.log('✅ Admin session restored from localStorage as:', storedUsername);
    };

    validateAdminSession();
  }, []);

  // Monitor localStorage changes for admin token with simplified validation
  useEffect(() => {
    const handleStorageChange = async () => {
      const adminToken = localStorage.getItem('admin_token');
      
      if (!adminToken && isAdminLoggedIn) {
        setIsAdminLoggedIn(false);
        console.log('❌ Admin token removed, logging out');
        return;
      }

      if (adminToken && !isAdminLoggedIn) {
        setIsAdminLoggedIn(true);
        console.log('✅ Admin token found, logging in');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isAdminLoggedIn]);

  // NEW: Track URL parameter changes for admin portal button visibility
  useEffect(() => {
    const checkAdminParameter = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const adminParam = urlParams.get('admin') === 'true';
      const isLocalhost = window.location.hostname === 'localhost';
      setShowAdminPortalButton(adminParam || isLocalhost);
    };

    // Check on mount
    checkAdminParameter();

    // Listen for URL changes (including browser back/forward)
    const handleUrlChange = () => {
      checkAdminParameter();
    };

    window.addEventListener('popstate', handleUrlChange);
    
    // Cleanup listener
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, []);

  const [products, setProducts] = useState([]);
  const [productsLastUpdated, setProductsLastUpdated] = useState(Date.now()); // Force memoization refresh
  // Real-time database sync state variables moved to useEffect section above
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedVariationSize, setSelectedVariationSize] = useState(null);
  const [selectedVariationColor, setSelectedVariationColor] = useState(null);
  const [filteredColors, setFilteredColors] = useState([]);

  // Helper function to get all available media (images + video) for a product/variation
  const getAllProductMedia = (product, selectedVariation) => {
    const media = {
      images: [],
      video: null
    };
    
    console.log('🎬 getAllProductMedia called for:', product.name, {
      selectedVariation: selectedVariation?.color || 'none',
      product_video_base64: !!product.video_base64,
      product_video_url: !!product.video_url,
      variation_video_base64: !!selectedVariation?.video_base64,
      variation_video_url: !!selectedVariation?.video_url
    });

    // Check if variation has its own media
    let variationHasMedia = false;
    
    // Collect images from variation first (if variation is selected)
    if (selectedVariation) {
      if (selectedVariation.image_base64) {
        const imgSrc = selectedVariation.image_base64.startsWith('data:') 
          ? selectedVariation.image_base64 
          : `data:image/jpeg;base64,${selectedVariation.image_base64}`;
        media.images.push(imgSrc);
        variationHasMedia = true;
      }
      
      if (selectedVariation.images && Array.isArray(selectedVariation.images) && selectedVariation.images.length > 0) {
        selectedVariation.images.forEach(img => {
          if (img && typeof img === 'string') {
            const imgSrc = img.startsWith('data:') ? img : `data:image/jpeg;base64,${img}`;
            if (!media.images.includes(imgSrc)) {
              media.images.push(imgSrc);
            }
          }
        });
        variationHasMedia = true;
      }

      // Variation video
      if (selectedVariation.video_base64 && selectedVariation.video_base64.trim()) {
        media.video = selectedVariation.video_base64.startsWith('data:') 
          ? selectedVariation.video_base64 
          : `data:video/mp4;base64,${selectedVariation.video_base64}`;
        console.log('✅ Found variation video for:', product.name);
        variationHasMedia = true;
      }
      
      // Also check video_url for variation
      if (!media.video && selectedVariation.video_url && selectedVariation.video_url.trim()) {
        media.video = selectedVariation.video_url;
        console.log('✅ Found variation video URL for:', product.name);
        variationHasMedia = true;
      }
    }

    // Only add product-level images if NO variation is selected OR variation has no media
    if (!selectedVariation || !variationHasMedia) {
      // Collect images from main product
      if (product.image_base64) {
        const imgSrc = product.image_base64.startsWith('data:') 
          ? product.image_base64 
          : `data:image/jpeg;base64,${product.image_base64}`;
        if (!media.images.includes(imgSrc)) {
          media.images.push(imgSrc);
        }
      }

      if (product.images && Array.isArray(product.images)) {
        product.images.forEach(img => {
          if (img && typeof img === 'string') {
            const imgSrc = img.startsWith('data:') ? img : `data:image/jpeg;base64,${img}`;
            if (!media.images.includes(imgSrc)) {
              media.images.push(imgSrc);
            }
          }
        });
      }

      // Main product video (if no variation video)
      if (!media.video && product.video_base64 && product.video_base64.trim()) {
        media.video = product.video_base64.startsWith('data:') 
          ? product.video_base64 
          : `data:video/mp4;base64,${product.video_base64}`;
        console.log('✅ Found main product video for:', product.name);
      }
      
      if (!media.video && product.video_url && product.video_url.trim()) {
        media.video = product.video_url;
        console.log('✅ Found main product video URL for:', product.name);
      }
    }
    
    console.log('🎬 Final media result for', product.name, {
      images_count: media.images.length,
      has_video: !!media.video,
      video_type: media.video ? (media.video.startsWith('data:') ? 'base64' : 'url') : 'none',
      variation_has_media: variationHasMedia
    });

    return media;
  };

  // Helper function to get price display for a product (handles variations)
  const getProductPriceDisplay = (product, priceType = 'retail') => {
    const priceUnit = (product.measurement_unit === 'per_square_meter' || product.measurement_unit === 'per_sqm') ? '/m²' : '';
    const priceField = priceType === 'retail' ? 'retail_price' : 
                       priceType === 'wholesale' ? 'wholesale_price' : 
                       priceType === 'fitter' ? 'fitter_price' : 
                       priceType === 'cost' ? 'cost_price' : 'retail_price';
    
    // Show price range for products with variations
    if (product.variations && product.variations.length > 0) {
      const prices = product.variations
        .map(v => parseFloat(v[priceField]) || 0)
        .filter(p => p > 0);
      
      if (prices.length > 0) {
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        
        if (minPrice === maxPrice) {
          return `£${minPrice.toFixed(2)}${priceUnit}`;
        } else {
          return `£${minPrice.toFixed(2)} - £${maxPrice.toFixed(2)}${priceUnit}`;
        }
      }
    }
    
    // Fallback to main product price
    const price = parseFloat(product[priceField]) || 0;
    return price > 0 ? `£${price.toFixed(2)}${priceUnit}` : 'Contact for Price';
  };

  const [loading, setLoading] = useState(true);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [deletingProductId, setDeletingProductId] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  // PDF UI state
  const [showCreatePdfPanel, setShowCreatePdfPanel] = useState(false);
  const [pdfSelectedProducts, setPdfSelectedProducts] = useState([]);
  const [pdfPriceType, setPdfPriceType] = useState('retail');

  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showCreateCatalogModal, setShowCreateCatalogModal] = useState(false);
  const [showProductReportModal, setShowProductReportModal] = useState(false);
  const [productReportData, setProductReportData] = useState(null);
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');
  const [selectedProductsForReport, setSelectedProductsForReport] = useState([]); // For multi-product report
  const [productReportFromDate, setProductReportFromDate] = useState(''); // Date range for Individual / Combined product reports
  const [productReportToDate, setProductReportToDate] = useState('');
  const [editingProductId, setEditingProductId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [productVariations, setProductVariations] = useState([
    { 
      id: 1, 
      size: '', 
      squareMeters: '', 
      color: '', 
      storageLocation: '', 
      retail: '', 
      wholesale: '', 
      fitter: '', 
      cost: '', 
      stock: '', 
      // Enhanced image and video support
      colorImage: null,  // Primary/main image (legacy)
      images: [null, null, null, null],  // Array for 4 images
      video: null  // Video file
    }
  ]);
  // NEW: State for multiple product images
  const [uploadedImages, setUploadedImages] = useState([]);
  const [currentMeasurementUnit, setCurrentMeasurementUnit] = useState('size'); // Track measurement unit for dynamic form
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    company_name: '',
    address: '',
    contact_number: '',
    customer_type: 'wholesale' // wholesale or fitter
  });
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  
  // 🔧 FIX ISSUES Modal - for comprehensive data audit and fixing
  const [showFixIssuesModal, setShowFixIssuesModal] = useState(false);
  const [fixIssuesData, setFixIssuesData] = useState(null);
  const [fixIssuesLoading, setFixIssuesLoading] = useState(false);
  const [selectedIssuesToFix, setSelectedIssuesToFix] = useState({});
  
  const [newOrder, setNewOrder] = useState({
    sale_type: 'retail', // wholesale, retail, fitter
    customer_id: '', // or 'guest'
    order_items: [],
    subtotal: 0,
    vat_amount: 0,
    grand_total: 0,
    include_fitting: 'no', // yes or no
    cut_piece: 'no', // yes or no
    fitting_charge: 0,
    cut_piece_charge: 0,
    discount: 0, // Admin discount amount
    vat_rate: 0.20, // VAT rate (20% = 0.20, 0% = 0.00)
    custom_date: '', // Custom date for Zobayr (boss) only - allows backdating orders
    old_balance_override: null, // Editable old balance - null means use auto-calculated
    calculated_old_balance: 0 // Auto-calculated old balance for display
  });
  const [currentOrderItem, setCurrentOrderItem] = useState({
    product_id: '',
    product_name: '',
    size: '',
    color: '',
    storage_location: '',
    quantity: 1,
    sqm: 0,
    wide: 0,
    length: 0,
    wide_unit: 'meters', // meters or feet
    length_unit: 'meters', // meters or feet
    unit_price: 0,
    total_price: 0,
    measurement_unit: 'size',
    has_vat: true // Per-item VAT toggle - default ON for most items
  });
  const [customers, setCustomers] = useState([]);
  const [isSavingCustomer, setIsSavingCustomer] = useState(false);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [orders, setOrders] = useState([]);
  const [fulfilledOrders, setFulfilledOrders] = useState([]);
  const [deletedOrders, setDeletedOrders] = useState([]);  // Deleted orders for boss verification
  const [loadingDeletedOrders, setLoadingDeletedOrders] = useState(false);
  const [editedOrders, setEditedOrders] = useState([]);  // Edited orders for boss review
  const [loadingEditedOrders, setLoadingEditedOrders] = useState(false);
  const [dailyActivity, setDailyActivity] = useState(null);  // Daily activity report for boss
  const [loadingDailyActivity, setLoadingDailyActivity] = useState(false);
  const [dailyActivityDate, setDailyActivityDate] = useState(new Date().toISOString().split('T')[0]);  // Selected date for daily activity
  
  // Session Management state (for Zobayr/boss only)
  const [activeSessions, setActiveSessions] = useState([]);
  const [loadingActiveSessions, setLoadingActiveSessions] = useState(false);
  const [terminatingSession, setTerminatingSession] = useState(null);
  
  const [creditAccountsOrders, setCreditAccountsOrders] = useState([]);
  const [loadingCreditAccounts, setLoadingCreditAccounts] = useState(false);
  const [loadingFulfilledOrders, setLoadingFulfilledOrders] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Expenses state
  const [expenses, setExpenses] = useState([]);
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [expenseTab, setExpenseTab] = useState('list'); // 'list', 'onetime' or 'recurring'
  const [newExpense, setNewExpense] = useState({
    date: new Date().toISOString().split('T')[0],
    expense_description: '',
    amount: '',
    payment_method: 'cash'
  });
  const [newRecurringExpense, setNewRecurringExpense] = useState({
    expense_description: '',
    amount: '',
    payment_method: 'cash',
    frequency: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    end_date: ''
  });
  const [editingRecurringExpense, setEditingRecurringExpense] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showExpensePdfModal, setShowExpensePdfModal] = useState(false);
  const [expensePdfFromDate, setExpensePdfFromDate] = useState('');
  const [expensePdfToDate, setExpensePdfToDate] = useState('');
  
  // Product Enquiries state
  const [productEnquirySearch, setProductEnquirySearch] = useState('');
  const [productEnquiryResults, setProductEnquiryResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [showCustomInvoiceModal, setShowCustomInvoiceModal] = useState(false);
  const [customInvoiceOrder, setCustomInvoiceOrder] = useState(null);
  const [customInvoiceAmount, setCustomInvoiceAmount] = useState('');
  const [showStandaloneInvoiceModal, setShowStandaloneInvoiceModal] = useState(false);
  const [standaloneInvoice, setStandaloneInvoice] = useState({
    customer_id: '',
    customer_name: '',
    customer_number: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    include_vat: true
  });
  const [cataloguePriceType, setCataloguePriceType] = useState('retail');
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [queries, setQueries] = useState([]);
  const [loadingQueries, setLoadingQueries] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [expandedCustomerId, setExpandedCustomerId] = useState(null);
  const [showQueryDetailsModal, setShowQueryDetailsModal] = useState(false);
  const [storeLocations, setStoreLocations] = useState([]);
  const [loadingStoreLocations, setLoadingStoreLocations] = useState(false);
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
  const [showCredentialModal, setShowCredentialModal] = useState(false);
  const [showAdminOverrideModal, setShowAdminOverrideModal] = useState(false);
  const [adminAccounts, setAdminAccounts] = useState([]);
  const [loadingAdminAccounts, setLoadingAdminAccounts] = useState(false);
  
  // Warehouse Breakdown Report State
  const [showWarehouseBreakdownModal, setShowWarehouseBreakdownModal] = useState(false);
  const [warehouseReportStartDate, setWarehouseReportStartDate] = useState('');
  const [warehouseReportEndDate, setWarehouseReportEndDate] = useState('');
  
  // Audit Modal State (REMOVED - feature deprecated)
  
  // Add state for product context in contact form
  const [contactProductContext, setContactProductContext] = useState(null);

  // Function to load full product details with images when needed
  // Function to load full product details with images when needed
  const loadFullProductDetails = async (productId) => {
    try {
      console.log(`🔍 Loading full product details for ID: ${productId}`);
      const response = await axios.get(`${API}/api/products/${productId}`, { timeout: 30000 });
      
      if (response.data) {
        console.log('✅ Full product details loaded:', response.data);
        return response.data;
      } else {
        console.warn('⚠️ No product data returned');
        return null;
      }
    } catch (error) {
      console.error('❌ Error loading full product details:', error);
      return null;
    }
  };

  // Credit countdown calculation function
  const calculateDaysRemaining = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };


  // Enhanced product click handler that loads full details with images
  const handleProductClick = async (product) => {
    try {
      // Reset image slider to first image
      setCurrentImageIndex(0);
      // Reset variation selection
      setSelectedVariationSize(null);
      setSelectedVariationColor(null);
      
      // Initialize filtered colors with all available colors
      if (product.variations) {
        const allColors = [...new Set(product.variations.map(v => v.color).filter(Boolean))];
        setFilteredColors(allColors);
      }
      
      // If the product already has image data, use it directly
      if (product.image_base64 || (product.variations && product.variations.some(v => v.image_base64))) {
        console.log('📱 Using cached product data with images');
        setSelectedProduct(product);
        return;
      }
      
      // Otherwise, fetch full product details
      console.log('🔄 Loading full product details...');
      const fullProduct = await loadFullProductDetails(product.id);
      
      if (fullProduct) {
        console.log('✅ Full product loaded successfully');
        setSelectedProduct(fullProduct);
        // Update filtered colors with full product data
        if (fullProduct.variations) {
          const allColors = [...new Set(fullProduct.variations.map(v => v.color).filter(Boolean))];
          setFilteredColors(allColors);
        }
      } else {
        console.warn('⚠️ Falling back to cached product data');
        setSelectedProduct(product);
      }
    } catch (error) {
      console.error('❌ Error in product selection:', error);
      // Fallback to the original product data
      setSelectedProduct(product);
    }
  };

  // INSTANT LOADING: Fixed function to load products for public display without affecting loading states
  const loadProductsForPublic = useCallback(async () => {
    try {
      setLoading(true); // Set loading state
      console.log('🔄 Loading products for public display...');
      
      // Use the correct backend URL
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://plush-living.co.uk';
      const apiUrl = `${backendUrl}/api/products?limit=100`; // Load all products for public users
      
      console.log('📡 Products API URL:', apiUrl);
      
      const response = await axios.get(apiUrl, { timeout: 30000 });
      
      if (Array.isArray(response.data)) {
        // ENHANCED PUBLIC VIEW: Load all media for complete public product experience
        const optimizedProducts = response.data.map(product => ({
          ...product,
          // Keep all images and media for public display
          image_base64: product.image_base64,
          images: product.images || [], // Keep all enhanced media images
          video_base64: product.video_base64, // Keep video for public playback
          variations: product.variations?.map(v => ({
            ...v,
            // Keep all variation media for public display
            image_base64: v.image_base64,
            images: v.images || [], // Keep all enhanced variation images
            video_base64: v.video_base64 // Keep variation videos
          })) || []
        }));
        
        setProducts(optimizedProducts);
        setProductsLastUpdated(Date.now());
        console.log(`✅ Loaded ${optimizedProducts.length} products for public display`);
      } else {
        console.warn('⚠️ Unexpected products response format:', response.data);
        setProducts([]);
      }
    } catch (error) {
      console.error('❌ Error loading products for public display:', error);
      console.error('Error details:', error.response?.data || error.message);
      setProducts([]);
    } finally {
      setLoading(false); // Clear loading state
      console.log('🏁 Public product loading completed');
    }
  }, []);

  // Helper function to get auth token - works for both Admin and Boss (Zobayr)
  // Boss uses dashboard_token when accessing admin features via dashboard mode
  const getAuthToken = useCallback(() => {
    const adminToken = localStorage.getItem('admin_token');
    const dashboardToken = localStorage.getItem('dashboard_token');
    // Prefer admin_token, fall back to dashboard_token (for Zobayr accessing admin features)
    return adminToken || dashboardToken;
  }, []);

  // Define loadProducts function early to prevent initialization errors
  const loadProducts = useCallback(async (page = 0, pageSize = 20) => {
    try {
      setLoading(true);
      console.log('🔄 Loading products from:', `${API}/api/products`);
      
      // Check if admin or dashboard user is logged in and include authentication
      const authToken = getAuthToken(); // Use unified auth token (admin or dashboard)
      const headers = {};
      
      if (authToken && (isAdminLoggedIn || isDashboardUser)) {
        headers['Authorization'] = `Bearer ${authToken}`;
        console.log('🔐 Loading as admin/dashboard user with token:', authToken.substring(0, 20) + '...');
      } else {
        console.log('🌐 Loading as public user (no wholesale prices)', {
          hasToken: !!authToken,
          isAdminLoggedIn,
          isDashboardUser,
          tokenLength: authToken?.length || 0
        });
      }
      
      console.log('🌍 Making API request to:', `${API}/api/products`);
      
      const response = await axios.get(`${API}/api/products`, {
        headers,
        timeout: 30000 // Increased to 30 seconds for large product datasets
      });
      console.log(`📦 Received ${response.data?.length || 0} products from API`);
      console.log('✅ Products response received:', response.data);
      
      // Handle response - backend returns array directly
      if (Array.isArray(response.data)) {
        // PERFORMANCE FIX: Remove heavy base64 images to speed up loading
        const optimizedProducts = response.data.map(product => ({
          ...product,
          // Remove heavy base64 images from initial load - reduces 12.91 MB to <1 MB
          image_base64: null,
          images: [], // Load images only when needed
          variations: product.variations?.map(v => ({
            ...v,
            image_base64: null // Remove variation images from initial load
          })) || []
        }));
        
        setProducts(optimizedProducts);
        setProductsLastUpdated(Date.now()); // Force memoization refresh
        
        // Update products state with new array reference to trigger re-renders
        if (optimizedProducts.length > 0) {
          // Wholesale price info available for debugging
          const firstProduct = optimizedProducts[0];
          if (firstProduct.variations && firstProduct.variations.length > 0) {
            const firstVariation = firstProduct.variations[0];
          }
        }
      } else {
        console.warn('⚠️ Unexpected products response format:', response.data);
        setProducts([]);
        setProductsLastUpdated(Date.now()); // Force memoization refresh even for empty state
      }
      
      // For now, disable pagination since we're loading all products
      setHasMoreProducts(false);
      setProductsPage(0);
      
    } catch (error) {
      console.error('❌ Error loading products:', error);
      console.error('💥 API Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      if (error.response) {
        console.error('❌ Error response:', error.response.status, error.response.data);
      }
      setProducts([]);
      setProductsLastUpdated(Date.now()); // Force memoization refresh even on error
    } finally {
      setLoading(false);
      console.log('🏁 Product loading completed, loading state cleared');
    }
  }, [API, isAdminLoggedIn, isDashboardUser, getAuthToken]);

  // Admin search queries - separate input and actual search terms
  const [productsSearchInput, setProductsSearchInput] = useState("");
  const [customersSearchInput, setCustomersSearchInput] = useState("");
  const [ordersSearchInput, setOrdersSearchInput] = useState("");
  const [fulfilledSearchInput, setFulfilledSearchInput] = useState("");
  const [invoicesSearchInput, setInvoicesSearchInput] = useState("");
  const [creditSearchInput, setCreditSearchInput] = useState("");
  const [wholesaleSearchInput, setWholesaleSearchInput] = useState("");
  const [expandedCreditCustomers, setExpandedCreditCustomers] = useState({});
  const [selectedCreditCustomers, setSelectedCreditCustomers] = useState({}); // For credit note PDF download
  
  // Credit Accounts - Date Filter and Lump Sum Payment
  const [creditDateFilterStart, setCreditDateFilterStart] = useState("");
  const [creditDateFilterEnd, setCreditDateFilterEnd] = useState("");
  const [showLumpSumModal, setShowLumpSumModal] = useState(false);
  const [lumpSumCustomer, setLumpSumCustomer] = useState(null);
  const [paymentHistoryCustomer, setPaymentHistoryCustomer] = useState(null);
  const [paymentHistoryData, setPaymentHistoryData] = useState(null);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  // 💰 SERVER-COMPUTED dashboard stats (single source of truth across browsers/devices)
  const [serverDashboardStats, setServerDashboardStats] = useState(null);
  const [lumpSumAmount, setLumpSumAmount] = useState("");
  const [lumpSumMethod, setLumpSumMethod] = useState("cash");
  const [lumpSumNotes, setLumpSumNotes] = useState("");
  const [lumpSumLoading, setLumpSumLoading] = useState(false);
  const [lumpSumPaymentDate, setLumpSumPaymentDate] = useState("");
  const [customerAccountPayments, setCustomerAccountPayments] = useState({}); // Stores account payments per customer
  
  const [productsSearch, setProductsSearch] = useState("");
  const [customersSearch, setCustomersSearch] = useState("");
  const [ordersSearch, setOrdersSearch] = useState("");
  const [fulfilledSearch, setFulfilledSearch] = useState("");
  const [invoicesSearch, setInvoicesSearch] = useState("");
  const [creditSearch, setCreditSearch] = useState("");
  const [wholesaleSearch, setWholesaleSearch] = useState("");

  // Deferred values for performance - keeps input responsive while deferring heavy filtering
  const deferredFulfilledSearchInput = React.useDeferredValue(fulfilledSearchInput);
  const deferredCustomersSearchInput = React.useDeferredValue(customersSearchInput);
  const deferredInvoicesSearchInput = React.useDeferredValue(invoicesSearchInput);
  const deferredCreditSearchInput = React.useDeferredValue(creditSearchInput);

  // Memoized search handlers to prevent recreation on every render
  const handleFulfilledSearchChange = React.useCallback((e) => {
    setFulfilledSearchInput(e.target.value);
  }, []);

  const handleCustomersSearchChange = React.useCallback((e) => {
    setCustomersSearchInput(e.target.value);
  }, []);

  const handleInvoicesSearchChange = React.useCallback((e) => {
    setInvoicesSearchInput(e.target.value);
  }, []);

  const handleCreditSearchChange = React.useCallback((e) => {
    setCreditSearchInput(e.target.value);
  }, []);

  // Filter and sort state for orders
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const [orderIdSort, setOrderIdSort] = useState("");

  // Filter and sort state for fulfilled orders  
  const [fulfilledPaymentFilter, setFulfilledPaymentFilter] = useState("");
  const [reportPeriod, setReportPeriod] = useState("daily"); // For sales report generator
  const [reportFromDate, setReportFromDate] = useState(""); // Custom date range - from
  const [reportToDate, setReportToDate] = useState(""); // Custom date range - to
  const [fulfilledOrderIdSort, setFulfilledOrderIdSort] = useState("");

  // Sync status state
  const [syncStatus, setSyncStatus] = useState({ status: "INITIALIZING", message: "Starting..." });

  // Load sync status
  const loadSyncStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/sync/status`);
      setSyncStatus(response.data);
    } catch (error) {
      console.error('Error loading sync status:', error);
      setSyncStatus({ status: "ERROR", message: "Failed to load status" });
    }
  };

  // Auto-refresh sync status every 30 seconds
  useEffect(() => {
    loadSyncStatus();
    const statusInterval = setInterval(loadSyncStatus, 30000); // 30 seconds
    
    return () => clearInterval(statusInterval);
  }, []);

  // 🔒 SESSION VALIDATION - Check if session is still valid every 60 seconds
  // This ensures users are kicked out promptly when admin terminates their session
  // 🔒 PROACTIVE SESSION VALIDATION - Polls every 15 seconds to check if session was terminated
  useEffect(() => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
    if (!token) return;
    
    let isCheckingSession = false; // Prevent overlapping checks
    
    const validateSession = async () => {
      if (isCheckingSession) return; // Skip if already checking
      isCheckingSession = true;
      
      try {
        // Make a lightweight API call to validate session
        const response = await fetch(`${API}/api/auth/validate-session`, {
          method: 'GET',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.status === 401) {
          console.log('🔒 Session terminated remotely - forcing immediate logout');
          // Session has been terminated - clear ALL storage and redirect immediately
          localStorage.removeItem('admin_token');
          localStorage.removeItem('dashboard_token');
          localStorage.removeItem('admin_username');
          localStorage.removeItem('dashboard_username');
          localStorage.removeItem('token');
          localStorage.removeItem('customer_token');
          // Show alert and redirect
          alert('Your session has been terminated. You will be logged out now.');
          window.location.href = '/?forced_logout=true';
          return;
        }
      } catch (error) {
        // Network error - don't logout, just log
        console.log('⚠️ Session validation network error:', error.message);
      } finally {
        isCheckingSession = false;
      }
    };
    
    // Validate immediately on mount
    validateSession();
    
    // Then validate every 15 seconds (more responsive than 60s)
    const sessionCheckInterval = setInterval(validateSession, 15000);
    
    return () => clearInterval(sessionCheckInterval);
  }, [isAdminLoggedIn, isDashboardUser]);

  // Success message state for paid amount updates
  const [paidAmountSuccessMessage, setPaidAmountSuccessMessage] = useState("");
  
  // State for highlighting specific order
  const [highlightedOrderId, setHighlightedOrderId] = useState("");

  // Accounting modals and date filters
  const [showInvoiceDownloadModal, setShowInvoiceDownloadModal] = useState(false);
  const [showBusinessAnalysisModal, setShowBusinessAnalysisModal] = useState(false);
  const [invoiceDownloadFromDate, setInvoiceDownloadFromDate] = useState("");
  const [invoiceDownloadToDate, setInvoiceDownloadToDate] = useState("");
  const [businessAnalysisFromDate, setBusinessAnalysisFromDate] = useState("");
  const [businessAnalysisToDate, setBusinessAnalysisToDate] = useState("");

  // Add useEffect to log activeTab changes at main component level
  useEffect(() => {
    console.log('Admin activeTab changed to:', activeTab);
  }, [activeTab]);

  // 📡 Real-Time Sync for Admin via Polling (1 second interval)
  const lastAdminUpdateRef = useRef(null);
  useEffect(() => {
    if (!isAdminLoggedIn) return;
    
    let mounted = true;
    let pollInterval = null;
    
    const pollForUpdates = async () => {
      if (!mounted) return;
      
      try {
        const response = await axios.get(`${API}/api/sync/poll`);
        const { last_update } = response.data;
        
        // Check if there's a new update
        if (last_update && last_update.timestamp !== lastAdminUpdateRef.current) {
          const isFirstPoll = lastAdminUpdateRef.current === null;
          lastAdminUpdateRef.current = last_update.timestamp;
          
          // Only trigger refresh if this isn't the first poll
          if (!isFirstPoll) {
            console.log('📡 Admin: Real-time update detected:', last_update.type);
            loadFulfilledOrders(false); // Silent background refresh - no loading spinner
            loadOrders();
          }
        }
      } catch (error) {
        console.warn('📡 Admin: Polling error:', error.message);
      }
    };
    
    // Initial poll
    pollForUpdates();
    
    // Poll every 4 minutes for background updates (prevents input focus interruption)
    pollInterval = setInterval(pollForUpdates, 240000);
    
    return () => {
      mounted = false;
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [isAdminLoggedIn]);

  // Auto-load customers when ANY tab that needs customer data is opened
  useEffect(() => {
    if (isAdminLoggedIn && ['customers', 'orders', 'fulfilled-orders', 'accounting'].includes(activeTab)) {
      loadCustomers();
    }
  }, [isAdminLoggedIn, activeTab]);

  // ⚡ PERFORMANCE: Eagerly pre-load the 3 most-used tabs (Fulfilled Orders, Orders, Credit Accounts)
  // in parallel right after admin login. Everything else stays lazy-loaded so initial login is fast
  // AND switching to these constantly-used tabs is instant (data already in memory).
  useEffect(() => {
    if (!isAdminLoggedIn) return;
    let cancelled = false;
    const preloadPriorityData = async () => {
      console.log('⚡ Pre-loading priority data: Fulfilled Orders, Orders, Credit Accounts (in parallel)');
      const t0 = Date.now();
      try {
        await Promise.all([
          loadFulfilledOrders(false),    // Silent — no spinner
          loadOrders(),
          loadCreditAccountsOrders(true),
          loadAllCustomerAccountPayments(),
          loadCustomers(),
          fetchServerDashboardStats()
        ]);
        if (!cancelled) {
          console.log(`⚡ Priority data ready in ${Date.now() - t0}ms`);
        }
      } catch (e) {
        console.warn('Pre-load failed (will lazy-load on tab open instead):', e?.message);
      }
    };
    preloadPriorityData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminLoggedIn]);

  // Auto-load deleted orders when deleted-orders tab is opened
  useEffect(() => {
    if (isAdminLoggedIn && activeTab === 'deleted-orders') {
      loadDeletedOrders();
    }
    // Auto-load edited orders when edited-orders tab is opened
    if (isAdminLoggedIn && activeTab === 'edited-orders') {
      loadEditedOrders();
    }
    // Auto-load daily activity when daily-activity tab is opened
    if (isAdminLoggedIn && activeTab === 'daily-activity') {
      loadDailyActivity();
    }
    // Auto-load active sessions when session-management tab is opened (Zobayr only)
    if (isAdminLoggedIn && activeTab === 'session-management') {
      loadActiveSessions();
    }
  }, [isAdminLoggedIn, activeTab]);

  // Auto-load products when admin accesses tabs that need product data (catalogue, products)
  useEffect(() => {
    if (isAdminLoggedIn && ['products', 'catalogue'].includes(activeTab)) {
      loadProducts(0, 100); // Load more products for admin functionality
    }
  }, [activeTab, isAdminLoggedIn, loadProducts]);

  // 💰 Fetch server-computed dashboard stats — keeps numbers identical across browsers/devices
  const fetchServerDashboardStats = async () => {
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token') || localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API}/api/admin-portal/dashboard-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.data && res.data.computed_at_server) {
        setServerDashboardStats(res.data);
      }
    } catch (e) {
      console.warn('Server dashboard stats unavailable, falling back to client calc', e?.message);
    }
  };

  // Auto-refresh server stats when fulfilled orders tab loads or data changes
  useEffect(() => {
    if (isAdminLoggedIn && (activeTab === 'fulfilled-orders' || activeTab === 'dashboard')) {
      fetchServerDashboardStats();
    }
  }, [isAdminLoggedIn, activeTab, fulfilledOrders, creditAccountsOrders, customerAccountPayments, expenses]);

  // 👻 INSTANT LOAD: Always re-fetch the fulfilled orders list the moment the tab opens.
  // Fixes the "no fulfilled orders found" empty state caused when the initial silent
  // pre-load failed/raced (e.g. slow network, transient auth, page refresh).
  useEffect(() => {
    if (isAdminLoggedIn && activeTab === 'fulfilled-orders') {
      loadFulfilledOrders(fulfilledOrders.length === 0);
    }
    if (isAdminLoggedIn && activeTab === 'orders') {
      loadOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminLoggedIn, activeTab]);

  // Pre-load ALL customer account payments when Credit Accounts tab is opened (prevents balance glitching)
  // Also load credit accounts orders when tab is clicked
  useEffect(() => {
    if (isAdminLoggedIn && (activeTab === 'credit-accounts' || activeTab === 'warehouse-breakdown')) {
      console.log('Loading credit accounts data for tab:', activeTab);
      loadAllCustomerAccountPayments();
      loadCreditAccountsOrders();
    }
  }, [isAdminLoggedIn, activeTab]);

  // 💳 Payment History: load the customer's full orders + payment history on demand
  useEffect(() => {
    if (!paymentHistoryCustomer) {
      setPaymentHistoryData(null);
      return;
    }
    let cancelled = false;
    const fetchHistory = async () => {
      setPaymentHistoryLoading(true);
      try {
        const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token') || localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };
        // Fetch all orders for this customer + lump sum payments
        const [ordersRes, payRes] = await Promise.all([
          axios.get(`${API}/api/customers/${paymentHistoryCustomer.id}/orders`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/api/credit-accounts/${paymentHistoryCustomer.id}/account-payments`, { headers }).catch(() => ({ data: { payments: [], total_account_payments: 0 } }))
        ]);
        if (cancelled) return;
        const allOrders = Array.isArray(ordersRes.data) ? ordersRes.data : (ordersRes.data?.orders || []);
        setPaymentHistoryData({
          orders: allOrders,
          accountPayments: payRes.data?.payments || [],
          accountPaymentsTotal: payRes.data?.total_account_payments || 0
        });
      } catch (e) {
        console.error('Payment history fetch failed', e);
        if (!cancelled) setPaymentHistoryData({ orders: [], accountPayments: [], accountPaymentsTotal: 0 });
      } finally {
        if (!cancelled) setPaymentHistoryLoading(false);
      }
    };
    fetchHistory();
    return () => { cancelled = true; };
  }, [paymentHistoryCustomer]);

  // Load fulfilled orders (excluding returned orders) - works for both admin and dashboard users
  // showLoading=true for initial load or manual refresh, false for background polling
  const loadFulfilledOrders = async (showLoading = true) => {
    try {
      // Only show loading spinner on initial load or manual refresh
      if (showLoading) {
        setLoadingFulfilledOrders(true);
      }
      
      // FIXED: Prioritize admin_token for admin users, then fall back to dashboard_token
      const adminToken = localStorage.getItem('admin_token');
      const dashboardToken = localStorage.getItem('dashboard_token');
      
      // For admin portal, always use admin_token if available
      const token = adminToken || dashboardToken;
      
      if (!token) {
        console.warn('⚠️ No authentication token found for loading fulfilled orders');
        if (showLoading) {
          setFulfilledOrders([]);
        }
        return;
      }
      
      // Use admin endpoint if admin token, otherwise dashboard endpoint
      const endpoint = adminToken ? '/api/orders/fulfilled' : '/api/orders/fulfilled-all';
      
      console.log(`🔄 Loading fulfilled orders from ${endpoint}...`);
      
      const res = await axios.get(`${API}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 30000 // 30 second timeout
      });
      
      // 👻 NO MORE GHOST BILLS: Keep returned/cancelled bills visible — they're still real
      // bills the business recorded. Frontend renders a status badge to distinguish them.
      const allFulfilledOrders = res.data || [];
      const activeFulfilledOrders = allFulfilledOrders;
      
      console.log(`📊 Fulfilled orders loaded: ${allFulfilledOrders.length} total (incl. returned/cancelled — no ghosts)`);
      
      // OPTIMIZATION: Only update state if the orders actually changed
      // This prevents unnecessary re-renders that interrupt filter input
      setFulfilledOrders(prevOrders => {
        // Quick check: if length is different, definitely update
        if (prevOrders.length !== activeFulfilledOrders.length) {
          return activeFulfilledOrders;
        }
        
        // Deep comparison: check if any order has changed
        const hasChanges = activeFulfilledOrders.some((newOrder, index) => {
          const oldOrder = prevOrders[index];
          if (!oldOrder) return true;
          // Compare key fields that might change
          return newOrder.id !== oldOrder.id ||
                 newOrder.status !== oldOrder.status ||
                 newOrder.payment_status !== oldOrder.payment_status ||
                 newOrder.credit_paid_amount !== oldOrder.credit_paid_amount ||
                 newOrder.cash_paid_amount !== oldOrder.cash_paid_amount ||
                 newOrder.bank_paid_amount !== oldOrder.bank_paid_amount;
        });
        
        if (hasChanges) {
          console.log('📊 Orders data changed, updating state');
          return activeFulfilledOrders;
        }
        
        console.log('📊 Orders data unchanged, skipping state update');
        return prevOrders; // Return same reference to prevent re-render
      });
      
    } catch (e) {
      console.error('❌ Error loading fulfilled orders:', e);
      console.error('Error details:', e.response?.status, e.response?.data, e.message);
      
      // Only clear orders on error if this is an initial load
      if (showLoading) {
        setFulfilledOrders([]);
      }
      
      // If it's an auth error, the token might be invalid
      if (e.response?.status === 401 || e.response?.status === 403) {
        console.error('🔐 Authentication error - token may be invalid');
      }
    } finally {
      if (showLoading) {
        setLoadingFulfilledOrders(false);
      }
    }
  };

  // Load deleted orders for boss verification
  const loadDeletedOrders = async () => {
    try {
      setLoadingDeletedOrders(true);
      // FIXED: Use whichever token is available (admin_token or dashboard_token)
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const res = await axios.get(`${API}/api/deleted-orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const deleted = res.data || [];
      console.log(`🗑️ Deleted orders loaded: ${deleted.length} orders pending review`);
      setDeletedOrders(deleted);
      
    } catch (e) {
      console.error('Error loading deleted orders:', e);
      setDeletedOrders([]);
    } finally {
      setLoadingDeletedOrders(false);
    }
  };

  // Verify a deleted order (boss confirms deletion was legitimate)
  const verifyDeletedOrder = async (orderId) => {
    try {
      // FIXED: Use whichever token is available
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      await axios.put(`${API}/api/deleted-orders/${orderId}/verify`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert('✅ Deleted order verified successfully!');
      loadDeletedOrders(); // Refresh list
    } catch (e) {
      console.error('Error verifying deleted order:', e);
      alert('❌ Failed to verify deleted order');
    }
  };

  // Restore a deleted order
  const restoreDeletedOrder = async (orderId) => {
    if (!window.confirm('⚠️ Are you sure you want to restore this deleted order? It will appear in the fulfilled orders list marked in RED.')) {
      return;
    }
    try {
      // FIXED: Use whichever token is available
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const res = await axios.post(`${API}/api/deleted-orders/${orderId}/restore`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert(`✅ Order restored successfully!\n\nOrder ID: ${res.data.order_id}`);
      loadDeletedOrders(); // Refresh deleted list
      loadFulfilledOrders(); // Refresh fulfilled list to show restored order
    } catch (e) {
      console.error('Error restoring order:', e);
      alert('❌ Failed to restore order: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Load edited orders for boss review (orders with edit_date)
  const loadEditedOrders = async () => {
    try {
      setLoadingEditedOrders(true);
      // FIXED: Use whichever token is available
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const res = await axios.get(`${API}/api/edited-orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const edited = res.data || [];
      console.log(`✏️ Edited orders loaded: ${edited.length} orders modified`);
      setEditedOrders(edited);
      
    } catch (e) {
      console.error('Error loading edited orders:', e);
      setEditedOrders([]);
    } finally {
      setLoadingEditedOrders(false);
    }
  };

  // 🔒 ZOBAYR ONLY: Cancelled bills viewer / restorer
  const [cancelledOrders, setCancelledOrders] = useState([]);
  const [showCancelledModal, setShowCancelledModal] = useState(false);
  const [loadingCancelled, setLoadingCancelled] = useState(false);

  const loadCancelledOrders = async () => {
    try {
      setLoadingCancelled(true);
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const res = await axios.get(`${API}/api/cancelled-orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCancelledOrders(res.data || []);
    } catch (e) {
      console.error('Error loading cancelled orders:', e);
      setCancelledOrders([]);
      alert('❌ Failed to load cancelled bills: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingCancelled(false);
    }
  };

  const restoreCancelledOrder = async (orderId) => {
    if (!window.confirm('Restore this cancelled bill?\n\nIt will reappear in Fulfilled Orders with all its original data.')) return;
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const res = await axios.post(`${API}/api/cancelled-orders/${orderId}/restore`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.data?.success) {
        alert('✅ Bill restored to Fulfilled Orders');
        await loadCancelledOrders();
        await refreshAllData();
      }
    } catch (e) {
      alert('❌ Failed to restore: ' + (e.response?.data?.detail || e.message));
    }
  };



  // Load daily activity report for boss
  const loadDailyActivity = async (date = dailyActivityDate) => {
    try {
      setLoadingDailyActivity(true);
      // FIXED: Use whichever token is available (admin_token or dashboard_token)
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const res = await axios.get(`${API}/api/daily-activity?date=${date}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log(`📅 Daily activity loaded for ${date}:`, res.data);
      setDailyActivity(res.data);
      
    } catch (e) {
      console.error('Error loading daily activity:', e);
      setDailyActivity(null);
    } finally {
      setLoadingDailyActivity(false);
    }
  };

  // ============ SESSION MANAGEMENT FUNCTIONS (for Zobayr/boss only) ============
  
  // Load all active sessions
  const loadActiveSessions = async () => {
    try {
      setLoadingActiveSessions(true);
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const res = await axios.get(`${API}/api/sessions/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('🔐 Active sessions loaded:', res.data);
      setActiveSessions(res.data.sessions || []);
    } catch (e) {
      console.error('Error loading active sessions:', e);
      if (e.response?.status === 403) {
        alert('Access denied. Only Zobayr (boss) can manage sessions.');
      }
      setActiveSessions([]);
    } finally {
      setLoadingActiveSessions(false);
    }
  };

  // Terminate a specific session
  const terminateSession = async (sessionId, username) => {
    if (!window.confirm(`Are you sure you want to log out "${username}" from this session?`)) return;
    
    try {
      setTerminatingSession(sessionId);
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      await axios.delete(`${API}/api/sessions/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert(`Session for "${username}" has been terminated. They will be logged out on their next action.`);
      loadActiveSessions(); // Refresh the list
    } catch (e) {
      console.error('Error terminating session:', e);
      alert('Failed to terminate session: ' + (e.response?.data?.detail || e.message));
    } finally {
      setTerminatingSession(null);
    }
  };

  // Terminate all sessions for a specific user
  const terminateAllUserSessions = async (username) => {
    if (!window.confirm(`Are you sure you want to log out "${username}" from ALL their sessions?`)) return;
    
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const res = await axios.delete(`${API}/api/sessions/user/${username}/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert(res.data.message);
      loadActiveSessions(); // Refresh the list
    } catch (e) {
      console.error('Error terminating user sessions:', e);
      alert('Failed to terminate sessions: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Terminate all sessions except current
  const terminateAllOtherSessions = async () => {
    if (!window.confirm('Are you sure you want to log out ALL users except yourself?')) return;
    
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const res = await axios.delete(`${API}/api/sessions/all-except-mine`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert(res.data.message);
      loadActiveSessions(); // Refresh the list
    } catch (e) {
      console.error('Error terminating all sessions:', e);
      alert('Failed to terminate sessions: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Terminate ALL sessions including current - forces EVERYONE to re-login
  const terminateAllSessions = async () => {
    if (!window.confirm('⚠️ WARNING: This will log out EVERYONE including yourself!\n\nAll users will need to log in again with their password.\n\nAre you sure?')) return;
    
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const res = await axios.delete(`${API}/api/sessions/terminate-all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert(res.data.message + '\n\nYou will now be logged out.');
      // Clear local storage and redirect to login
      localStorage.removeItem('admin_token');
      localStorage.removeItem('dashboard_token');
      window.location.reload();
    } catch (e) {
      console.error('Error terminating all sessions:', e);
      alert('Failed to terminate sessions: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Clear inactive session history
  const clearSessionHistory = async () => {
    if (!window.confirm('Clear all inactive/terminated sessions from history?\n\nThis only removes old sessions that are no longer active.')) return;
    
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const res = await axios.delete(`${API}/api/sessions/clear-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert(res.data.message);
      loadActiveSessions(); // Refresh the list
    } catch (e) {
      console.error('Error clearing session history:', e);
      alert('Failed to clear history: ' + (e.response?.data?.detail || e.message));
    }
  };

  // 📦 Stock Audit & Heal (zobayr-only)
  const [showStockAudit, setShowStockAudit] = useState(false);
  const [stockAuditRows, setStockAuditRows] = useState([]);
  const [stockAuditSummary, setStockAuditSummary] = useState(null);
  const [loadingStockAudit, setLoadingStockAudit] = useState(false);

  const loadStockAudit = async () => {
    setLoadingStockAudit(true);
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const res = await axios.get(`${API}/api/stock-audit`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStockAuditRows(res.data.rows || []);
      setStockAuditSummary(res.data.summary || null);
      setShowStockAudit(true);
    } catch (e) {
      console.error('stock-audit error', e);
      alert('❌ Failed to load stock audit: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingStockAudit(false);
    }
  };

  const runStockHeal = async () => {
    const leakCount = stockAuditRows.filter(r => (r.issues || []).length > 0).length;
    const confirmMsg = leakCount > 0
      ? `Restore stock for ${leakCount} variation(s) that were leaked by failed order attempts?\n\nEvery restoration is logged in the variation's restock_history with note='stock-heal'.`
      : 'No leaks detected. Run heal anyway (will be a no-op)?';
    if (!window.confirm(confirmMsg)) return;
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const res = await axios.post(`${API}/api/stock-audit/heal`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const healed = res.data.healed || [];
      const healedCount = res.data.healed_count || 0;
      if (healedCount === 0) {
        alert('✅ No stock leaks detected. Nothing to heal.');
      } else {
        const lines = healed.slice(0, 10).map(h => `• ${h.product_name} (${h.color || 'N/A'}/${h.size || 'N/A'}) → +${h.restored_units} ${h.unit}`);
        if (healed.length > 10) lines.push(`… and ${healed.length - 10} more`);
        alert(`✅ Restored stock for ${healedCount} variation(s):\n\n` + lines.join('\n'));
      }
      // Refresh the audit
      await loadStockAudit();
    } catch (e) {
      console.error('stock-audit heal error', e);
      alert('❌ Stock heal failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  // 💾 Automated MongoDB Backups (zobayr-only)
  const [showBackupsModal, setShowBackupsModal] = useState(false);
  const [backupsList, setBackupsList] = useState([]);
  const [backupsMeta, setBackupsMeta] = useState({ count: 0, retention: 48, interval_seconds: 3600 });
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);

  const loadBackups = async () => {
    setLoadingBackups(true);
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const res = await axios.get(`${API}/api/backups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBackupsList(res.data.backups || []);
      setBackupsMeta({
        count: res.data.count || 0,
        retention: res.data.retention || 48,
        interval_seconds: res.data.interval_seconds || 3600,
      });
      setShowBackupsModal(true);
    } catch (e) {
      console.error('backups list error', e);
      alert('❌ Failed to load backups: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingBackups(false);
    }
  };

  const triggerManualBackup = async () => {
    setCreatingBackup(true);
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const res = await axios.post(`${API}/api/backups/create`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const b = res.data.backup;
      alert(`✅ Backup created: ${b.filename}\nSize: ${b.size_human}\nDocs: ${b.total_docs} across ${b.collection_count} collections`);
      // Refresh list
      const listRes = await axios.get(`${API}/api/backups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBackupsList(listRes.data.backups || []);
      setBackupsMeta({
        count: listRes.data.count || 0,
        retention: listRes.data.retention || 48,
        interval_seconds: listRes.data.interval_seconds || 3600,
      });
    } catch (e) {
      console.error('manual backup error', e);
      alert('❌ Failed to create backup: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCreatingBackup(false);
    }
  };

  const downloadBackup = async (filename) => {
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const res = await axios.get(`${API}/api/backups/download/${encodeURIComponent(filename)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('download backup error', e);
      alert('❌ Failed to download backup: ' + (e.response?.data?.detail || e.message));
    }
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`Delete backup "${filename}"? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      await axios.delete(`${API}/api/backups/${encodeURIComponent(filename)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Refresh list
      const listRes = await axios.get(`${API}/api/backups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBackupsList(listRes.data.backups || []);
      setBackupsMeta({
        count: listRes.data.count || 0,
        retention: listRes.data.retention || 48,
        interval_seconds: listRes.data.interval_seconds || 3600,
      });
    } catch (e) {
      console.error('delete backup error', e);
      alert('❌ Failed to delete backup: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Print/Download daily activity report
  const printDailyActivityReport = () => {
    if (!dailyActivity) return;
    
    // Build orders HTML
    let ordersHtml = '';
    if (dailyActivity.customers_with_orders.length === 0) {
      ordersHtml = '<p style="color: #718096;">No orders for this date.</p>';
    } else {
      dailyActivity.customers_with_orders.forEach(customer => {
        ordersHtml += '<h3>' + customer.customer_name + ' (' + customer.order_count + ' bill' + (customer.order_count > 1 ? 's' : '') + ')</h3>';
        ordersHtml += '<table><thead><tr><th>Bill #</th><th>PLU</th><th>Time</th><th>Edited</th><th>By</th><th class="amount">Amount</th></tr></thead><tbody>';
        customer.orders.forEach(order => {
          const billNum = order.id ? order.id.substring(0, 10) + '...' : 'N/A';
          const timeStr = order.time ? new Date(order.time).toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'}) : 'N/A';
          const editedBadge = order.edited ? '<span class="edited-badge">EDITED</span>' : '-';
          ordersHtml += '<tr><td>' + billNum + '</td><td>' + (order.plu_code || 'N/A') + '</td><td>' + timeStr + '</td><td>' + editedBadge + '</td><td>' + (order.created_by || 'admin') + '</td><td class="amount">£' + order.total.toFixed(2) + '</td></tr>';
        });
        ordersHtml += '<tr class="customer-total"><td colspan="5" style="text-align: right;"><strong>TOTAL for ' + customer.customer_name + ':</strong></td><td class="amount"><strong>£' + customer.total_amount.toFixed(2) + '</strong></td></tr>';
        ordersHtml += '</tbody></table>';
      });
    }
    
    // Build payments HTML
    let paymentsHtml = '';
    if (dailyActivity.customers_with_payments.length === 0) {
      paymentsHtml = '<p style="color: #718096;">No payments for this date.</p>';
    } else {
      dailyActivity.customers_with_payments.forEach(customer => {
        paymentsHtml += '<h3>' + customer.customer_name + ' (' + customer.payment_count + ' payment' + (customer.payment_count > 1 ? 's' : '') + ')</h3>';
        paymentsHtml += '<table><thead><tr><th>Bill #</th><th>PLU</th><th>Bill Date</th><th>Payment Time</th><th>Type</th><th>By</th><th class="amount">Amount</th></tr></thead><tbody>';
        customer.payments.forEach(payment => {
          const billNum = payment.order_id ? payment.order_id.substring(0, 10) + '...' : 'N/A';
          const billDate = payment.order_date ? new Date(payment.order_date).toLocaleDateString('en-GB') : 'N/A';
          const payTime = payment.payment_date ? new Date(payment.payment_date).toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'}) : 'N/A';
          paymentsHtml += '<tr><td>' + billNum + '</td><td>' + (payment.plu_code || 'N/A') + '</td><td>' + billDate + '</td><td>' + payTime + '</td><td>' + (payment.payment_type || 'N/A') + '</td><td>' + (payment.recorded_by || 'admin') + '</td><td class="amount">£' + payment.amount.toFixed(2) + '</td></tr>';
        });
        paymentsHtml += '<tr class="customer-total"><td colspan="6" style="text-align: right;"><strong>TOTAL for ' + customer.customer_name + ':</strong></td><td class="amount"><strong>£' + customer.total_paid.toFixed(2) + '</strong></td></tr>';
        paymentsHtml += '</tbody></table>';
      });
    }
    
    const printWindow = window.open('', '_blank');
    const html = '<!DOCTYPE html><html><head><title>Daily Activity Report - ' + dailyActivity.date_formatted + '</title>' +
      '<style>' +
      'body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }' +
      'h1 { text-align: center; color: #1a365d; border-bottom: 2px solid #1a365d; padding-bottom: 10px; }' +
      'h2 { color: #2d3748; margin-top: 30px; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; }' +
      'h3 { color: #4a5568; margin: 20px 0 10px 0; background: #f7fafc; padding: 8px; border-radius: 4px; }' +
      '.summary { display: flex; justify-content: space-around; margin: 20px 0; background: #edf2f7; padding: 15px; border-radius: 8px; }' +
      '.summary-item { text-align: center; }' +
      '.summary-item .value { font-size: 24px; font-weight: bold; color: #2d3748; }' +
      '.summary-item .label { font-size: 12px; color: #718096; }' +
      'table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 12px; }' +
      'th, td { border: 1px solid #e2e8f0; padding: 8px; text-align: left; }' +
      'th { background: #4a5568; color: white; }' +
      'tr:nth-child(even) { background: #f7fafc; }' +
      '.customer-total { font-weight: bold; background: #e2e8f0 !important; }' +
      '.edited-badge { background: #ed8936; color: white; padding: 2px 6px; border-radius: 4px; font-size: 10px; }' +
      '.amount { text-align: right; }' +
      '.section { page-break-inside: avoid; margin-bottom: 30px; }' +
      '@media print { body { padding: 10px; } .no-print { display: none; } }' +
      '</style></head><body>' +
      '<h1>Daily Activity Report</h1>' +
      '<p style="text-align: center; font-size: 18px; color: #4a5568;">' + dailyActivity.date_formatted + '</p>' +
      '<div class="summary">' +
      '<div class="summary-item"><div class="value">' + dailyActivity.total_orders + '</div><div class="label">Total Orders</div></div>' +
      '<div class="summary-item"><div class="value">£' + dailyActivity.total_orders_value.toFixed(2) + '</div><div class="label">Orders Value</div></div>' +
      '<div class="summary-item"><div class="value">' + dailyActivity.total_payments + '</div><div class="label">Total Payments</div></div>' +
      '<div class="summary-item"><div class="value">£' + dailyActivity.total_payments_value.toFixed(2) + '</div><div class="label">Payments Value</div></div>' +
      '</div>' +
      '<div class="section"><h2>BILLS TAKEN TODAY</h2>' + ordersHtml + '</div>' +
      '<div class="section"><h2>PAYMENTS RECEIVED TODAY</h2>' + paymentsHtml + '</div>' +
      '<p style="text-align: center; color: #a0aec0; font-size: 11px; margin-top: 30px;">Generated on ' + new Date().toLocaleString('en-GB') + ' | Plush Living LTD</p>' +
      '<script>window.onload = function() { window.print(); }</script>' +
      '</body></html>';
    
    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Load credit accounts orders (only those with outstanding balance > 0)
  const loadCreditAccountsOrders = async (forceRefresh = false) => {
    try {
      setLoadingCreditAccounts(true);
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      console.log(`📊 CREDIT ACCOUNTS: Starting load... Token present: ${!!token}, API: ${API}, Force: ${forceRefresh}`);
      
      if (!token) {
        console.error('📊 CREDIT ACCOUNTS: No token available!');
        setCreditAccountsOrders([]);
        return;
      }
      
      // Always add cache-busting parameter for fresh data
      const timestamp = `?t=${Date.now()}`;
      const res = await axios.get(`${API}/api/credit-accounts/orders${timestamp}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });
      
      const creditOrders = res.data || [];
      console.log(`📊 CREDIT ACCOUNTS: Loaded ${creditOrders.length} orders with outstanding balance`);
      
      // Debug: Log first 3 orders
      creditOrders.slice(0, 3).forEach(o => {
        console.log(`📊 Order ${o.id?.substring(0,8)}: GT=${o.grand_total}, Outstanding=${o.outstanding_balance}`);
      });
      
      setCreditAccountsOrders(creditOrders);
      // Expose to window for sale order template (which uses string concatenation)
      window.__creditAccountsOrders = creditOrders;
      
    } catch (e) {
      console.error('📊 CREDIT ACCOUNTS ERROR:', e);
      console.error('📊 Error response:', e.response?.status, e.response?.data);
      setCreditAccountsOrders([]);
    } finally {
      setLoadingCreditAccounts(false);
    }
  };

  // Load ALL customer account payments at once (prevents balance glitching in Credit Accounts tab)
  const loadAllCustomerAccountPayments = async () => {
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const response = await axios.get(`${API}/api/credit-accounts/account-payments-all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data && response.data.payments) {
        // Group payments by customer_id
        const paymentsByCustomer = {};
        response.data.payments.forEach(payment => {
          const customerId = payment.customer_id;
          if (!paymentsByCustomer[customerId]) {
            paymentsByCustomer[customerId] = {
              total: 0,
              payments: []
            };
          }
          paymentsByCustomer[customerId].total += parseFloat(payment.amount || 0);
          paymentsByCustomer[customerId].payments.push(payment);
        });
        
        console.log('💰 Pre-loaded account payments for', Object.keys(paymentsByCustomer).length, 'customers');
        setCustomerAccountPayments(paymentsByCustomer);
        // Expose to window for sale order template
        window.__customerAccountPayments = paymentsByCustomer;
      }
    } catch (error) {
      console.error('Error pre-loading customer account payments:', error);
    }
  };

  const handleDeleteAccountPayment = async (paymentId, customerName, amount) => {
    if (!window.confirm(`Delete this account payment of £${parseFloat(amount).toFixed(2)} for ${customerName}?\n\nThis action cannot be undone.`)) return;
    
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      await axios.delete(`${API}/api/credit-accounts/account-payment/${paymentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert('Account payment deleted successfully.');
      // Refresh all data
      await refreshAllData();
    } catch (error) {
      console.error('Error deleting account payment:', error);
      alert('Error deleting payment: ' + (error.response?.data?.detail || error.message));
    }
  };

  // GLOBAL DATA REFRESH - call after ANY payment, order create/edit/delete, or account change
  const refreshAllData = async () => {
    console.log('🔄 GLOBAL REFRESH: Refreshing all data...');
    const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
    if (!token) return;
    
    try {
      await Promise.all([
        loadFulfilledOrders(false),
        loadCreditAccountsOrders(true),
        loadAllCustomerAccountPayments(),
        loadOrders(),
        fetchDashboardData(token)
      ]);
      console.log('🔄 GLOBAL REFRESH: Complete');
    } catch (e) {
      console.error('🔄 GLOBAL REFRESH error:', e);
    }
  };

  // Load fulfilled orders (non-credit + fully paid credit orders)
  const loadFulfilledOrdersNew = async () => {
    try {
      setLoadingFulfilledOrders(true);
      const res = await axios.get(`${API}/api/orders/fulfilled-paid`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      const paidOrders = res.data || [];
      console.log(`📊 Fully paid orders loaded: ${paidOrders.length} orders (non-credit + fully paid credit)`);
      setFulfilledOrders(paidOrders);
      
    } catch (e) {
      console.error('Error loading fully paid orders:', e);
      // Fallback to old method if new endpoint fails
      await loadFulfilledOrders();
    } finally {
      setLoadingFulfilledOrders(false);
    }
  };

  // Auto-load fulfilled orders when admin logs in
  useEffect(() => {
    if (isAdminLoggedIn) {
      // Small delay to ensure token is properly stored in localStorage
      const loadData = async () => {
        // Wait a moment for localStorage to be ready
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const token = localStorage.getItem('admin_token');
        if (token) {
          console.log('🔐 Admin logged in, loading fulfilled orders...');
          loadFulfilledOrders();
          loadCreditAccountsOrders();
        } else {
          console.warn('⚠️ Admin logged in but no token found, retrying...');
          // Retry after a short delay
          setTimeout(() => {
            if (localStorage.getItem('admin_token')) {
              loadFulfilledOrders();
              loadCreditAccountsOrders();
            }
          }, 500);
        }
      };
      loadData();
    }
  }, [isAdminLoggedIn]);

  // Auto-run edit tracking migration in background when admin logs in
  useEffect(() => {
    const runAutoMigration = async () => {
      if (isAdminLoggedIn) {
        try {
          const token = localStorage.getItem('admin_token');
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/migrate-edit-tracking`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          // Silent background operation - no alerts or UI updates
        } catch (error) {
          // Silent fail - migration will retry on next login
          console.log('Background migration skipped');
        }
      }
    };
    runAutoMigration();
  }, [isAdminLoggedIn]);

  // Check for ?admin=true URL parameter to show admin portal button
  // Check for ?dashboard=true to show dashboard login
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isAdminParam = urlParams.get('admin');
    const isDashboardParam = urlParams.get('dashboard');
    
    if (isAdminParam === 'true') {
      setShowAdminPortalButton(true);
      console.log('✅ Admin parameter detected - ADMIN PORTAL button enabled');
    }
    
    // Dashboard URL parameter is now disabled - dashboard access is controlled via admin permissions
    if (isDashboardParam === 'true') {
      console.log('⚠️ Dashboard parameter detected but ignored - use admin login with dashboard permission');
      // Don't show dashboard login - users must log in via admin and have permission
    }
    
    // Check for existing dashboard token - but only if user has permission
    const dashboardToken = localStorage.getItem('dashboard_token');
    const storedPermissions = localStorage.getItem('admin_permissions');
    if (dashboardToken && storedPermissions) {
      try {
        const perms = JSON.parse(storedPermissions);
        const storedUsername = localStorage.getItem('admin_username') || '';
        const isZobayr = storedUsername.toLowerCase() === 'zobayr';
        // Only allow dashboard access if user is zobayr OR has explicit permission
        if (isZobayr || perms.canAccessDashboard) {
          setIsDashboardUser(true);
          fetchDashboardData(dashboardToken);
          loadFulfilledOrders();
        } else {
          // User doesn't have permission - clear dashboard token
          localStorage.removeItem('dashboard_token');
          console.log('⚠️ Dashboard access denied - no permission');
        }
      } catch (e) {
        console.error('Error parsing permissions:', e);
      }
    }
  }, []);

  // Initialize dashboard user on app load
  useEffect(() => {
    const initDashboardUser = async () => {
      try {
        await axios.post(`${API}/api/dashboard/init`);
      } catch (error) {
        // User may already exist, ignore
      }
    };
    initDashboardUser();
  }, []);

  // Load customers immediately when admin logs in
  useEffect(() => {
    if (isAdminLoggedIn) {
      loadCustomers();
    }
  }, [isAdminLoggedIn]);

  // Auto-load wholesale applications when admin logs in
  useEffect(() => {
    if (isAdminLoggedIn) {
      fetchWholesaleApplications();
    }
  }, [isAdminLoggedIn]);

  // Auto-load invoices when admin logs in
  useEffect(() => {
    const loadInvoices = async () => {
      try {
        setLoadingInvoices(true);
        const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
        const res = await axios.get(`${API}/api/invoices`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setInvoices(res.data || []);
      } catch (e) {
        console.error('Error loading invoices:', e);
        setInvoices([]);
      } finally {
        setLoadingInvoices(false);
      }
    };
    if (isAdminLoggedIn) {
      loadInvoices();
    }
  }, [isAdminLoggedIn]);

  // Auto-load queries when admin logs in
  useEffect(() => {
    const loadQueries = async () => {
      try {
        setLoadingQueries(true);
        const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
        const res = await axios.get(`${API}/api/queries`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setQueries(res.data || []);
      } catch (e) {
        console.error('Error loading queries:', e);
        setQueries([]);
      } finally {
        setLoadingQueries(false);
      }
    };
    if (isAdminLoggedIn) {
      loadQueries();
    }
  }, [isAdminLoggedIn]);

  // Auto-load expenses when admin logs in
  useEffect(() => {
    const loadExpenses = async () => {
      try {
        setLoadingExpenses(true);
        const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
        const [expensesRes, recurringRes] = await Promise.all([
          axios.get(`${API}/api/expenses`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          axios.get(`${API}/api/expenses/recurring`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);
        setExpenses(expensesRes.data || []);
        setRecurringExpenses(recurringRes.data || []);
      } catch (e) {
        console.error('Error loading expenses:', e);
        setExpenses([]);
        setRecurringExpenses([]);
      } finally {
        setLoadingExpenses(false);
      }
    };
    if (isAdminLoggedIn) {
      loadExpenses();
    }
  }, [isAdminLoggedIn]);

  // Load products when admin or dashboard user logs in
  useEffect(() => {
    const loadProducts = async () => {
      if (isAdminLoggedIn || isDashboardUser) {
        console.log('🔄 Loading products for admin/dashboard user...');
        try {
          const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
          const res = await axios.get(`${API}/api/products`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          console.log(`✅ Loaded ${res.data.length} products`);
          setProducts(res.data || []);
        } catch (e) {
          console.error('Error loading products:', e);
        }
      }
    };
    loadProducts();
  }, [isAdminLoggedIn, isDashboardUser]);

  // Auto-load store locations when admin logs in
  useEffect(() => {
    const loadStoreLocations = async () => {
      try {
        setLoadingStoreLocations(true);
        const res = await axios.get(`${API}/api/store-locations`);
        setStoreLocations(res.data || []);
      } catch (e) {
        console.error('Error loading store locations:', e);
        setStoreLocations([]);
      } finally {
        setLoadingStoreLocations(false);
      }
    };
    if (isAdminLoggedIn) {
      loadStoreLocations();
    }
  }, [isAdminLoggedIn]);

  // Load store locations for footer (public access)
  useEffect(() => {
    const loadPublicStoreLocations = async () => {
      try {
        const res = await axios.get(`${API}/api/store-locations`);
        setStoreLocations(res.data || []);
      } catch (e) {
        console.error('Error loading store locations for footer:', e);
        // If API fails, just leave empty - no fallback location
        setStoreLocations([]);
      }
    };
    loadPublicStoreLocations();
  }, []);

  // Header visibility control
  useEffect(() => {
    const shouldShow = !isAdminLoggedIn;
    setHeaderShouldShow(shouldShow);
  }, [isAdminLoggedIn]);

  // Admin portal functions at main component level
  const handleCardClick = (sectionId) => {
    // Handle Business Dashboard - switch to dashboard view
    if (sectionId === 'business-dashboard') {
      const isManager = isManagerAccount();
      // Check: Only managers OR users with can_access_dashboard permission
      if (!isManager && !adminPermissions.canAccessDashboard) {
        alert('Access denied. Business Dashboard access not granted.');
        return;
      }
      // Fetch dashboard data and switch to dashboard user view
      const token = localStorage.getItem('admin_token');
      if (token) {
        localStorage.setItem('dashboard_token', token);
        setIsDashboardUser(true);
        fetchDashboardData(token);
        loadFulfilledOrders();
      }
      return;
    }
    
    setActiveTab(sectionId);
    
    // Load data when specific tabs are accessed
    if (sectionId === 'orders') {
      loadOrders();
    } else if (sectionId === 'products') {
      loadProducts(); // Reload products when admin accesses Products section
    }
  };

  const handleCloseModal = () => {
    setActiveTab('dashboard');
  };

  // Order management functions
  const handleViewOrder = async (orderId) => {
    // Guard: Check if orderId is valid
    if (!orderId) {
      console.error('❌ handleViewOrder called with invalid orderId:', orderId);
      alert('Order ID is missing. Please refresh the page and try again.');
      return;
    }
    
    try {
      // 💰 Ensure credit-accounts data is fresh before opening the modal so Old Balance is ALWAYS accurate
      const ensureCreditData = (async () => {
        if (!window.__creditAccountsOrders || window.__creditAccountsOrders.length === 0) {
          try { await loadCreditAccountsOrders(true); } catch (_) {}
        }
        if (!window.__customerAccountPayments || Object.keys(window.__customerAccountPayments).length === 0) {
          try { await loadAllCustomerAccountPayments(); } catch (_) {}
        }
      })();
      
      // Always fetch from backend to ensure enriched customer data for printing
      console.log('🔍 Fetching order with enriched customer data for viewing... Order ID:', orderId);
      const response = await axios.get(`${API}/api/orders/${orderId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      await ensureCreditData; // Wait for credit data so Old Balance row never shows stale/empty
      setSelectedOrder(response.data);
      setShowOrderViewModal(true);
    } catch (error) {
      console.error('Error fetching order details:', error, 'Order ID:', orderId);
      // Fallback: search BOTH fulfilledOrders AND orders arrays (user may be viewing from either list)
      const orderFromList = fulfilledOrders?.find(order => order.id === orderId) || orders?.find(order => order.id === orderId);
      if (orderFromList) {
        console.log('✅ Using cached order data from local list');
        setSelectedOrder(orderFromList);
        setShowOrderViewModal(true);
      } else {
        console.error('❌ Order not found in any local list. Fulfilled orders:', fulfilledOrders?.map(o => o.id), 'Active orders:', orders?.map(o => o.id));
        alert(`Unable to fetch order details for ID: ${orderId}. The order may have been deleted or there is a connection issue. Please refresh the page.`);
      }
    }
  };

  const handleEditOrder = async (orderId) => {
    // Guard: Check if orderId is valid
    if (!orderId) {
      console.error('❌ handleEditOrder called with invalid orderId:', orderId);
      alert('Order ID is missing. Please refresh the page and try again.');
      return;
    }
    
    try {
      // Always fetch from backend to ensure enriched customer data
      console.log('🔍 Fetching order with enriched customer data for editing... Order ID:', orderId);
      const response = await axios.get(`${API}/api/orders/${orderId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      const orderData = response.data;
      
      // Set up the order for editing by populating newOrder state
      // CRITICAL FIX: Assign NEW unique numeric IDs to each item to ensure proper matching
      // This overwrites any existing IDs (like MongoDB ObjectIds) with consistent numeric IDs
      const baseId = Date.now();
      const itemsWithUniqueIds = (orderData.items || []).map((item, index) => ({
        ...item,
        id: baseId + index // Always assign a fresh unique ID
      }));
      const originalItemsWithIds = JSON.parse(JSON.stringify(itemsWithUniqueIds));
      
      console.log('📝 Loading order for edit - assigned IDs:', itemsWithUniqueIds.map(i => ({ id: i.id, name: i.product_name, sqm: i.sqm })));
      
      setNewOrder({
        ...orderData,
        original_date: orderData.date, // Store original order date
        edit_date: new Date().toISOString().split('T')[0], // Set edit date to today
        order_items: itemsWithUniqueIds, // Items with guaranteed unique IDs
        original_items: originalItemsWithIds, // Store original items for comparison (also with IDs)
        isEditMode: true, // Flag to indicate this is an edit operation
        editOrderId: orderId // Store the original order ID
      });
      
      // Switch to the order creation tab which will now act as full edit interface
      setActiveTab('orders');
      setShowCreateOrderModal(true);
      
    } catch (error) {
      console.error('Error fetching order for editing:', error, 'Order ID:', orderId);
      alert(`Unable to fetch order details for editing (ID: ${orderId}). Please refresh and try again.`);
    }
  };

  // Handle Return Order (with payment reversal)
  const handleReturnOrder = async (orderId) => {
    try {
      console.log('🔄 Opening return modal for order:', orderId);
      
      // Fetch full order details
      const response = await axios.get(`${API}/api/orders/${orderId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      const order = response.data;
      
      // Get payment information
      const grandTotal = parseFloat(order.grand_total || 0);
      const cashPaid = parseFloat(order.cash_paid_amount || 0);
      const bankPaid = parseFloat(order.bank_paid_amount || 0);
      const creditPaid = parseFloat(order.credit_paid_amount || 0);
      const totalPaid = cashPaid + bankPaid + creditPaid;
      
      // Build warning message
      let warningMessage = `⚠️ RETURN ORDER WARNING\n\n`;
      warningMessage += `Order Total: £${grandTotal.toFixed(2)}\n`;
      warningMessage += `Customer: ${order.customer_name || 'Guest'}\n\n`;
      warningMessage += `📦 Returned products will be RESTORED to stock\n\n`;
      
      // Show payment reversals
      if (totalPaid > 0) {
        warningMessage += `💰 PAYMENTS TO BE REVERSED:\n`;
        if (cashPaid > 0) warningMessage += `   - Cash: £${cashPaid.toFixed(2)} will be deducted\n`;
        if (bankPaid > 0) warningMessage += `   - Bank: £${bankPaid.toFixed(2)} will be deducted\n`;
        if (creditPaid > 0) warningMessage += `   - Credit: £${creditPaid.toFixed(2)} will be deducted\n`;
        warningMessage += `\n`;
      }
      
      // Check if split payment exists
      const hasSplitPayment = (cashPaid > 0 && bankPaid > 0) || 
                              (cashPaid > 0 && creditPaid > 0) || 
                              (bankPaid > 0 && creditPaid > 0);
      
      if (hasSplitPayment && totalPaid > 0) {
        warningMessage += `⚠️ SPLIT PAYMENT DETECTED\n`;
        warningMessage += `You will need to specify how much to deduct from each payment method.\n\n`;
        warningMessage += `Continue with return?`;
        
        if (!confirm(warningMessage)) {
          return;
        }
        
        // Show split payment refund allocation modal, then proceed to return items selection
        await showReturnRefundAllocationModal(order);
        
      } else {
        // Simple payment - show warning and continue
        warningMessage += `Continue with return process?`;
        
        if (!confirm(warningMessage)) {
          return;
        }
        
        // Store refund data for later
        window.pendingReturnRefund = {
          refund_cash: cashPaid,
          refund_bank: bankPaid,
          refund_credit: creditPaid
        };
        
        // Open return modal to select items
        setReturnOrder(order);
        const remainingItems = calculateRemainingQuantities(order);
        const initialReturnItems = remainingItems.map(item => ({
          ...item,
          return_quantity: item.remaining_quantity,
          max_returnable: item.remaining_quantity,
          selected: true
        }));
        setReturnItems(initialReturnItems);
        setShowReturnModal(true);
      }
      
    } catch (error) {
      console.error('Error preparing return:', error);
      alert('Failed to prepare return. Please try again.');
    }
  };
  
  // Show modal for admin to allocate refund amounts for returns
  const showReturnRefundAllocationModal = async (order) => {
    const cashPaid = parseFloat(order.cash_paid_amount || 0);
    const bankPaid = parseFloat(order.bank_paid_amount || 0);
    const creditPaid = parseFloat(order.credit_paid_amount || 0);
    const totalPaid = cashPaid + bankPaid + creditPaid;
    
    // Create modal HTML (same as delete but for returns)
    const modalHtml = `
      <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 10000;">
        <div style="background: white; padding: 30px; border-radius: 12px; max-width: 500px; width: 90%;">
          <h3 style="margin-top: 0; color: #ff9800;">💰 Return Refund Allocation</h3>
          <p style="margin-bottom: 20px;">This order had split payments. Specify how much to deduct from each payment method:</p>
          
          <div style="margin-bottom: 15px;">
            <strong>Total Paid: £${totalPaid.toFixed(2)}</strong>
          </div>
          
          ${cashPaid > 0 ? `
          <div style="margin-bottom: 15px;">
            <label style="display: block; font-weight: bold; color: #28a745;">💵 Cash Paid: £${cashPaid.toFixed(2)}</label>
            <div style="display: flex; gap: 10px; align-items: center; margin-top: 5px;">
              <span>Deduct: £</span>
              <input type="number" id="return-refund-cash" value="${cashPaid.toFixed(2)}" step="0.01" min="0" max="${cashPaid}" style="width: 100px; padding: 5px;" />
            </div>
          </div>
          ` : ''}
          
          ${bankPaid > 0 ? `
          <div style="margin-bottom: 15px;">
            <label style="display: block; font-weight: bold; color: #007bff;">🏦 Bank Paid: £${bankPaid.toFixed(2)}</label>
            <div style="display: flex; gap: 10px; align-items: center; margin-top: 5px;">
              <span>Deduct: £</span>
              <input type="number" id="return-refund-bank" value="${bankPaid.toFixed(2)}" step="0.01" min="0" max="${bankPaid}" style="width: 100px; padding: 5px;" />
            </div>
          </div>
          ` : ''}
          
          ${creditPaid > 0 ? `
          <div style="margin-bottom: 15px;">
            <label style="display: block; font-weight: bold; color: #ffc107;">💳 Credit Paid: £${creditPaid.toFixed(2)}</label>
            <div style="display: flex; gap: 10px; align-items: center; margin-top: 5px;">
              <span>Deduct: £</span>
              <input type="number" id="return-refund-credit" value="${creditPaid.toFixed(2)}" step="0.01" min="0" max="${creditPaid}" style="width: 100px; padding: 5px;" />
            </div>
          </div>
          ` : ''}
          
          <div style="margin-top: 20px; padding: 10px; background: #fff3cd; border: 1px solid #ffc107; border-radius: 5px;">
            <strong>⚠️ Note:</strong> These amounts will be deducted from your totals. Stock will be restored.
          </div>
          
          <div style="margin-top: 20px; display: flex; gap: 10px;">
            <button id="confirm-return-refund" style="flex: 1; padding: 10px; background: #ff9800; color: white; border: none; border-radius: 5px; font-weight: bold; cursor: pointer;">
              Continue with Return
            </button>
            <button id="cancel-return-refund" style="flex: 1; padding: 10px; background: #6c757d; color: white; border: none; border-radius: 5px; font-weight: bold; cursor: pointer;">
              Cancel
            </button>
          </div>
        </div>
      </div>
    `;
    
    // Inject modal into DOM
    const modalDiv = document.createElement('div');
    modalDiv.id = 'return-refund-allocation-modal';
    modalDiv.innerHTML = modalHtml;
    document.body.appendChild(modalDiv);
    
    // Handle confirm button
    return new Promise((resolve, reject) => {
      document.getElementById('confirm-return-refund').onclick = () => {
        const refundCash = parseFloat(document.getElementById('return-refund-cash')?.value || 0) || 0;
        const refundBank = parseFloat(document.getElementById('return-refund-bank')?.value || 0) || 0;
        const refundCredit = parseFloat(document.getElementById('return-refund-credit')?.value || 0) || 0;
        
        // Validate amounts
        if (refundCash > cashPaid || refundBank > bankPaid || refundCredit > creditPaid) {
          alert('Refund amounts cannot exceed paid amounts!');
          return;
        }
        
        // Store refund data for later use
        window.pendingReturnRefund = {
          refund_cash: refundCash,
          refund_bank: refundBank,
          refund_credit: refundCredit
        };
        
        // Remove modal
        document.getElementById('return-refund-allocation-modal').remove();
        
        // Now open return items selection modal
        setReturnOrder(order);
        const remainingItems = calculateRemainingQuantities(order);
        const initialReturnItems = remainingItems.map(item => ({
          ...item,
          return_quantity: item.remaining_quantity,
          max_returnable: item.remaining_quantity,
          selected: true
        }));
        setReturnItems(initialReturnItems);
        setShowReturnModal(true);
        
        resolve();
      };
      
      // Handle cancel button
      document.getElementById('cancel-return-refund').onclick = () => {
        document.getElementById('return-refund-allocation-modal').remove();
        reject('Cancelled by user');
      };
    });
  };

  // Process Return (restore stock and update order with payment reversal)
  const processReturn = async () => {
    try {
      console.log('🔄 Processing return for order:', returnOrder.id);
      
      // Validate return items
      const selectedItems = returnItems.filter(item => item.selected && item.return_quantity > 0);
      
      if (selectedItems.length === 0) {
        alert('Please select at least one item to return.');
        return;
      }
      
      // Validate quantities
      for (const item of selectedItems) {
        const maxReturnable = item.max_returnable || item.remaining_quantity || item.quantity;
        if (item.return_quantity > maxReturnable) {
          alert(`Return quantity for ${item.product_name} cannot exceed available quantity (${maxReturnable}).`);
          return;
        }
      }
      
      // Get refund data from pending window variable
      const refundData = window.pendingReturnRefund || {
        refund_cash: 0,
        refund_bank: 0,
        refund_credit: 0
      };
      
      // Process return via backend with refund data
      const returnData = {
        order_id: returnOrder.id,
        return_items: selectedItems.map(item => ({
          product_id: item.product_id,
          variation_id: item.variation_id,
          product_name: item.product_name,
          size: item.size,
          color: item.color,
          return_quantity: parseInt(item.return_quantity),
          original_quantity: item.quantity,
          unit_price: item.unit_price,
          measurement_unit: item.measurement_unit
        })),
        return_reason: 'Admin return/cancellation',
        processed_by: 'admin',
        refund_allocation: refundData
      };
      
      console.log('🔄 Sending return data with refunds:', returnData);
      
      const response = await axios.post(`${API}/api/admin-portal/process-return`, returnData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      if (response.status === 200) {
        const refundSummary = [];
        if (refundData.refund_cash > 0) refundSummary.push(`Cash: £${refundData.refund_cash.toFixed(2)}`);
        if (refundData.refund_bank > 0) refundSummary.push(`Bank: £${refundData.refund_bank.toFixed(2)}`);
        if (refundData.refund_credit > 0) refundSummary.push(`Credit: £${refundData.refund_credit.toFixed(2)}`);
        
        const successMsg = refundSummary.length > 0 
          ? `✅ Return processed successfully!\n\n📦 Stock restored\n💰 Payments reversed: ${refundSummary.join(', ')}`
          : 'Return processed successfully! Stock has been restored.';
        
        alert(successMsg);
        
        // Clean up
        window.pendingReturnRefund = null;
        setShowReturnModal(false);
        setReturnOrder(null);
        setReturnItems([]);
        
        // Refresh orders and products
        await loadProducts();
        setProductsLastUpdated(Date.now());
        
        // Refresh ALL data globally
        refreshAllData();
        
        // Refresh selected order if needed
        try {
          if (selectedOrder && selectedOrder.id === returnOrder.id) {
            const ordRes = await axios.get(`${API}/api/orders/${returnOrder.id}`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
            });
            if (ordRes.status === 200) {
              setSelectedOrder(ordRes.data);
            }
          }
        } catch (e) {
          console.warn('Could not refresh selected order after return:', e);
        }
        await loadProducts();
        setProductsLastUpdated(Date.now());
        setLastSyncTime(Date.now());
        
      } else {
        alert('Return processing failed. Please try again.');
      }
      
    } catch (error) {
      console.error('Error processing return:', error);
      alert('Failed to process return. Please try again.');
    }
  };

  // Handle Delete Order (permanently delete and restore stock + reverse payments)
  const handleDeleteOrder = async (orderId) => {
    try {
      // Find the order to get payment details
      const order = fulfilledOrders?.find(o => o.id === orderId) || orders?.find(o => o.id === orderId);
      
      if (!order) {
        alert('Order not found');
        return;
      }
      
      const grandTotal = parseFloat(order.grand_total || 0);
      const cashPaid = parseFloat(order.cash_paid_amount || 0);
      const bankPaid = parseFloat(order.bank_paid_amount || 0);
      const creditPaid = parseFloat(order.credit_paid_amount || 0);
      const totalPaid = cashPaid + bankPaid + creditPaid;
      const paymentMethod = (order.payment_method || '').toLowerCase();
      
      // Build warning message
      let warningMessage = `⚠️ DELETE ORDER WARNING\n\n`;
      warningMessage += `Order Total: £${grandTotal.toFixed(2)}\n`;
      warningMessage += `Customer: ${order.customer_name || 'Guest'}\n\n`;
      warningMessage += `📦 Products will be RETURNED to stock\n\n`;
      
      // Show payment deductions
      if (totalPaid > 0) {
        warningMessage += `💰 PAYMENTS TO BE REVERSED:\n`;
        if (cashPaid > 0) warningMessage += `   - Cash: £${cashPaid.toFixed(2)} will be deducted\n`;
        if (bankPaid > 0) warningMessage += `   - Bank: £${bankPaid.toFixed(2)} will be deducted\n`;
        if (creditPaid > 0) warningMessage += `   - Credit: £${creditPaid.toFixed(2)} will be deducted\n`;
        warningMessage += `\n`;
      }
      
      // Check if split payment exists (need admin to choose refund allocation)
      const hasSplitPayment = (cashPaid > 0 && bankPaid > 0) || 
                              (cashPaid > 0 && creditPaid > 0) || 
                              (bankPaid > 0 && creditPaid > 0);
      
      if (hasSplitPayment && totalPaid > 0) {
        // For split payments, show modal to let admin choose refund allocation
        warningMessage += `⚠️ SPLIT PAYMENT DETECTED\n`;
        warningMessage += `You will need to specify how much to deduct from each payment method.\n\n`;
        warningMessage += `Continue?`;
        
        if (!confirm(warningMessage)) {
          return;
        }
        
        // Show split payment refund allocation modal
        await showRefundAllocationModal(order);
        
      } else {
        // Simple payment or no payment - just confirm and delete
        warningMessage += `Are you sure you want to DELETE this order?\n`;
        warningMessage += `This action CANNOT be undone.`;
        
        if (!confirm(warningMessage)) {
          return;
        }
        
        // Delete with automatic payment reversal
        await executeOrderDeletion(orderId, {
          refund_cash: cashPaid,
          refund_bank: bankPaid,
          refund_credit: creditPaid
        });
      }
      
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Failed to delete order. Please try again.');
    }
  };
  
  // Show modal for admin to allocate refund amounts
  const showRefundAllocationModal = async (order) => {
    const cashPaid = parseFloat(order.cash_paid_amount || 0);
    const bankPaid = parseFloat(order.bank_paid_amount || 0);
    const creditPaid = parseFloat(order.credit_paid_amount || 0);
    const totalPaid = cashPaid + bankPaid + creditPaid;
    
    // Create modal HTML
    const modalHtml = `
      <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 10000;">
        <div style="background: white; padding: 30px; border-radius: 12px; max-width: 500px; width: 90%;">
          <h3 style="margin-top: 0; color: #dc3545;">💰 Refund Allocation Required</h3>
          <p style="margin-bottom: 20px;">This order had split payments. Specify how much to deduct from each payment method:</p>
          
          <div style="margin-bottom: 15px;">
            <strong>Total Paid: £${totalPaid.toFixed(2)}</strong>
          </div>
          
          ${cashPaid > 0 ? `
          <div style="margin-bottom: 15px;">
            <label style="display: block; font-weight: bold; color: #28a745;">💵 Cash Paid: £${cashPaid.toFixed(2)}</label>
            <div style="display: flex; gap: 10px; align-items: center; margin-top: 5px;">
              <span>Deduct: £</span>
              <input type="number" id="refund-cash" value="${cashPaid.toFixed(2)}" step="0.01" min="0" max="${cashPaid}" style="width: 100px; padding: 5px;" />
            </div>
          </div>
          ` : ''}
          
          ${bankPaid > 0 ? `
          <div style="margin-bottom: 15px;">
            <label style="display: block; font-weight: bold; color: #007bff;">🏦 Bank Paid: £${bankPaid.toFixed(2)}</label>
            <div style="display: flex; gap: 10px; align-items: center; margin-top: 5px;">
              <span>Deduct: £</span>
              <input type="number" id="refund-bank" value="${bankPaid.toFixed(2)}" step="0.01" min="0" max="${bankPaid}" style="width: 100px; padding: 5px;" />
            </div>
          </div>
          ` : ''}
          
          ${creditPaid > 0 ? `
          <div style="margin-bottom: 15px;">
            <label style="display: block; font-weight: bold; color: #ffc107;">💳 Credit Paid: £${creditPaid.toFixed(2)}</label>
            <div style="display: flex; gap: 10px; align-items: center; margin-top: 5px;">
              <span>Deduct: £</span>
              <input type="number" id="refund-credit" value="${creditPaid.toFixed(2)}" step="0.01" min="0" max="${creditPaid}" style="width: 100px; padding: 5px;" />
            </div>
          </div>
          ` : ''}
          
          <div style="margin-top: 20px; padding: 10px; background: #fff3cd; border: 1px solid #ffc107; border-radius: 5px;">
            <strong>⚠️ Warning:</strong> These amounts will be deducted from your totals. Stock will be restored.
          </div>
          
          <div style="margin-top: 20px; display: flex; gap: 10px;">
            <button id="confirm-refund" style="flex: 1; padding: 10px; background: #dc3545; color: white; border: none; border-radius: 5px; font-weight: bold; cursor: pointer;">
              Delete Order & Refund
            </button>
            <button id="cancel-refund" style="flex: 1; padding: 10px; background: #6c757d; color: white; border: none; border-radius: 5px; font-weight: bold; cursor: pointer;">
              Cancel
            </button>
          </div>
        </div>
      </div>
    `;
    
    // Inject modal into DOM
    const modalDiv = document.createElement('div');
    modalDiv.id = 'refund-allocation-modal';
    modalDiv.innerHTML = modalHtml;
    document.body.appendChild(modalDiv);
    
    // Handle confirm button
    return new Promise((resolve, reject) => {
      document.getElementById('confirm-refund').onclick = async () => {
        const refundCash = parseFloat(document.getElementById('refund-cash')?.value || 0) || 0;
        const refundBank = parseFloat(document.getElementById('refund-bank')?.value || 0) || 0;
        const refundCredit = parseFloat(document.getElementById('refund-credit')?.value || 0) || 0;
        
        // Validate amounts don't exceed paid amounts
        if (refundCash > cashPaid || refundBank > bankPaid || refundCredit > creditPaid) {
          alert('Refund amounts cannot exceed paid amounts!');
          return;
        }
        
        // Remove modal
        document.getElementById('refund-allocation-modal').remove();
        
        // Execute deletion with refund allocation
        await executeOrderDeletion(order.id, {
          refund_cash: refundCash,
          refund_bank: refundBank,
          refund_credit: refundCredit
        });
        
        resolve();
      };
      
      // Handle cancel button
      document.getElementById('cancel-refund').onclick = () => {
        document.getElementById('refund-allocation-modal').remove();
        reject('Cancelled by user');
      };
    });
  };
  
  // Execute order deletion with payment reversal
  const executeOrderDeletion = async (orderId, refundData) => {
    try {
      console.log('🗑️ Deleting order with refund:', orderId, refundData);
      
      // Send delete request with refund data
      const response = await axios.delete(`${API}/api/orders/${orderId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` },
        data: refundData
      });
      
      if (response.status === 200) {
        const refundSummary = [];
        if (refundData.refund_cash > 0) refundSummary.push(`Cash: £${refundData.refund_cash.toFixed(2)}`);
        if (refundData.refund_bank > 0) refundSummary.push(`Bank: £${refundData.refund_bank.toFixed(2)}`);
        if (refundData.refund_credit > 0) refundSummary.push(`Credit: £${refundData.refund_credit.toFixed(2)}`);
        
        alert(`✅ Order deleted successfully!\n\n📦 Stock restored\n💰 Payments reversed: ${refundSummary.join(', ')}`);
        
        // Refresh all data globally
        await loadProducts();
        setProductsLastUpdated(Date.now());
        setLastSyncTime(Date.now());
        refreshAllData();
        
      } else {
        alert('Order deletion failed. Please try again.');
      }
      
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Failed to delete order: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSaveOrderChanges = async (updatedOrder) => {
    try {
      const response = await axios.put(`${API}/api/orders/${updatedOrder.id}`, updatedOrder, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      // Update order in local state
      setOrders(orders.map(order => 
        order.id === updatedOrder.id ? response.data : order
      ));
      
      // Also update fulfilled orders if it exists there
      setFulfilledOrders(fulfilledOrders.map(order => 
        order.id === updatedOrder.id ? response.data : order
      ));
      
      setShowOrderEditModal(false);
      setSelectedOrder(null);
      alert('Order updated successfully.');
      refreshAllData();
    } catch (error) {
      console.error('Error updating order:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Error updating order: ${errorMessage}`);
    }
  };

  // Helper function to convert units to meters
  const convertToMeters = (value, unit) => {
    if (unit === 'feet') {
      return value * 0.3048; // 1 foot = 0.3048 meters
    }
    return value; // already in meters
  };

  // Helper function to calculate remaining quantities after returns
  const calculateRemainingQuantities = (order) => {
    if (!order.items) {
      return [];
    }
    
    // If no return items, all items are fully remaining - still need display fields
    if (!order.return_items || order.return_items.length === 0) {
      return order.items.map(item => ({
        ...item,
        original_quantity: parseFloat(item.quantity || 0),
        returned_quantity: 0,
        remaining_quantity: parseFloat(item.quantity || 0),
        display_quantity: parseFloat(item.quantity || 0),
        display_total_price: (parseFloat(item.unit_price || 0) * parseFloat(item.quantity || 0)).toFixed(2)
      }));
    }
    
    return order.items.map(item => {
      // Find matching return items for this product
      const returnedForThisItem = order.return_items
        .filter(returnItem => 
          returnItem.product_id === item.product_id &&
          returnItem.size === item.size &&
          returnItem.color === item.color
        )
        .reduce((total, returnItem) => total + (returnItem.return_quantity || returnItem.returned_quantity || 0), 0);
      
      const originalQuantity = parseFloat(item.quantity || 0);
      const remainingQuantity = Math.max(0, originalQuantity - returnedForThisItem);
      
      return {
        ...item,
        original_quantity: originalQuantity,
        returned_quantity: returnedForThisItem,
        remaining_quantity: remainingQuantity,
        display_quantity: remainingQuantity, // What to show in Sale Order
        display_total_price: (parseFloat(item.unit_price || 0) * remainingQuantity).toFixed(2)
      };
    }).filter(item => item.remaining_quantity > 0); // Only show items with remaining quantity
  };

  // ===========================================
  // BULLETPROOF VAT CALCULATION - SINGLE SOURCE OF TRUTH
  // ===========================================
  // Rule 1: If has_vat === true OR has_vat is null/undefined → INCLUDE VAT
  // Rule 2: If has_vat === false → DO NOT INCLUDE VAT
  // Rule 3: Service charges (fitting/cutting) are ALWAYS VATable
  // Rule 4: This function is THE ONLY source for VAT calculation
  // ===========================================
  const calculateBulletproofVat = (items, fittingCharge = 0, cutPieceCharge = 0) => {
    // Validate inputs
    const safeItems = Array.isArray(items) ? items : [];
    const safeFitting = parseFloat(fittingCharge) || 0;
    const safeCutting = parseFloat(cutPieceCharge) || 0;
    
    let vatableTotal = 0;
    let vatableCount = 0;
    let nonVatableCount = 0;
    
    safeItems.forEach(item => {
      // Get item price
      const qty = parseFloat(item.display_quantity || item.quantity || 0);
      const price = parseFloat(item.unit_price || 0);
      const itemTotal = qty * price;
      
      // CRITICAL: Check has_vat flag
      // Only has_vat === false means NO VAT
      // Everything else (true, null, undefined, missing) means VAT APPLIES
      const hasVat = item.has_vat !== false;
      
      if (hasVat) {
        vatableTotal += itemTotal;
        vatableCount++;
      } else {
        nonVatableCount++;
      }
    });
    
    // Add service charges (always VATable)
    const totalVatable = vatableTotal + safeFitting + safeCutting;
    
    // Calculate 20% VAT
    const vatAmount = totalVatable * 0.20;
    
    console.log('🔒 BULLETPROOF VAT:', {
      items: safeItems.length,
      vatableCount,
      nonVatableCount,
      vatableTotal: vatableTotal.toFixed(2),
      serviceCharges: (safeFitting + safeCutting).toFixed(2),
      totalVatable: totalVatable.toFixed(2),
      vatAmount: vatAmount.toFixed(2)
    });
    
    return {
      vatAmount,
      vatableCount,
      nonVatableCount,
      totalItems: safeItems.length,
      vatableTotal,
      totalVatable
    };
  };

  // ===========================================
  // IRONCLAD ORDER TOTALS CALCULATOR
  // Single source of truth for all bill calculations
  // ===========================================
  const calculateIronclad = (items, fittingCharge = 0, cutPieceCharge = 0, discount = 0) => {
    // Force all inputs to be numbers with proper precision
    const safeItems = Array.isArray(items) ? items : [];
    const safeFitting = Math.round((parseFloat(fittingCharge) || 0) * 100) / 100;
    const safeCutting = Math.round((parseFloat(cutPieceCharge) || 0) * 100) / 100;
    const safeDiscount = Math.round((parseFloat(discount) || 0) * 100) / 100;
    const serviceCharges = safeFitting + safeCutting;
    
    // Calculate subtotal from items (sum of all item totals)
    let subtotal = 0;
    let vatableTotal = 0;
    
    safeItems.forEach(item => {
      const itemTotal = Math.round((parseFloat(item.total_price) || 0) * 100) / 100;
      subtotal += itemTotal;
      
      // Only add to vatable if has_vat is not explicitly false
      if (item.has_vat !== false) {
        vatableTotal += itemTotal;
      }
    });
    
    // Round subtotal
    subtotal = Math.round(subtotal * 100) / 100;
    vatableTotal = Math.round(vatableTotal * 100) / 100;
    
    // VAT is 20% of (vatable items + service charges)
    const totalVatable = vatableTotal + serviceCharges;
    const vatAmount = Math.round(totalVatable * 0.20 * 100) / 100;
    
    // Grand total = subtotal + service charges + VAT - discount
    const grandTotal = Math.round((subtotal + serviceCharges + vatAmount - safeDiscount) * 100) / 100;
    
    return {
      subtotal,
      serviceCharges,
      vatableTotal,
      vatAmount,
      discount: safeDiscount,
      grandTotal,
      // Prevent any NaN or undefined
      isValid: !isNaN(grandTotal) && grandTotal >= 0
    };
  };

  // ===========================================
  // GET FINAL VAT FOR AN ORDER
  // Uses stored value if valid, otherwise calculates fresh
  // ===========================================
  const getFinalVatAmount = (order, itemsToDisplay = null) => {
    const items = itemsToDisplay || order.items || [];
    const fittingCharge = parseFloat(order.fitting_charge || 0);
    const cutPieceCharge = parseFloat(order.cut_piece_charge || 0);
    
    // Calculate what VAT should be
    const calc = calculateBulletproofVat(items, fittingCharge, cutPieceCharge);
    
    // If no vatable items at all, VAT is 0
    if (calc.vatableCount === 0 && fittingCharge === 0 && cutPieceCharge === 0) {
      return { ...calc, vatAmount: 0, isCalculated: false };
    }
    
    // Use stored VAT if it's close to calculated (within 5%)
    const storedVat = parseFloat(order.vat_amount || 0);
    if (storedVat > 0.01 && calc.vatAmount > 0.01) {
      const diff = Math.abs(storedVat - calc.vatAmount);
      if (diff < calc.vatAmount * 0.05) {
        return { ...calc, vatAmount: storedVat, isCalculated: false };
      }
    }
    
    // Otherwise use calculated
    return { ...calc, isCalculated: true };
  };

  // ===========================================
  // BULLETPROOF OLD BALANCE CALCULATION
  // ===========================================
  // Calculates the customer's outstanding balance BEFORE a specific order was placed
  // This is the sum of all PREVIOUS credit orders' unpaid amounts
  // ===========================================
  const calculateBulletproofOldBalance = (order, allOrders) => {
    // 🔒 BULLETPROOF: This function now ONLY returns the stored value
    // NO dynamic calculation that could cause race conditions or glitches
    // The backend is the SINGLE SOURCE OF TRUTH for old_balance_at_order_time
    
    if (!order) {
      console.warn('⚠️ calculateBulletproofOldBalance: No order provided');
      return 0;
    }
    
    // Skip guest customers - they never have old balance
    if (!order.customer_id || order.customer_id === 'guest') {
      return 0;
    }
    
    // ALWAYS use the stored value from the database - this is set by backend when order is created
    // This eliminates race conditions where frontend calculates before data is fully loaded
    const storedBalance = parseFloat(order.old_balance_at_order_time || 0);
    
    console.log('💰 BULLETPROOF Old Balance (from DB):', {
      customer: order.customer_name,
      orderId: order.id,
      storedValue: storedBalance.toFixed(2)
    });
    
    return storedBalance;
  };

  // ===========================================
  // PERMISSION HELPERS FOR SPECTATE MODE
  // ===========================================
  // Helper: check if current user is a manager (full unrestricted access)
  const isManagerAccount = () => {
    const uname = loggedInAdminUsername.toLowerCase();
    return uname === 'zobayr' || uname === 'admin';
  };

  // Check if user has FULL access to a section (can edit/create/delete)
  const hasFullAccess = (section) => {
    // Managers always have full access
    if (isManagerAccount()) return true;
    
    // If no allowed_sections defined, user has full access to everything
    if (!adminPermissions.allowedSections) return true;
    
    // Check if section is in allowed sections
    return adminPermissions.allowedSections.includes(section);
  };
  
  // Check if user can VIEW a section (either full access or spectate)
  const canViewSection = (section) => {
    // Managers always can view
    if (isManagerAccount()) return true;
    
    // Can view if has full access OR spectate access
    if (!adminPermissions.allowedSections && !adminPermissions.spectateSections) return true;
    
    const hasFullAccessToSection = adminPermissions.allowedSections?.includes(section);
    const hasSpectateAccess = adminPermissions.spectateSections?.includes(section);
    
    return hasFullAccessToSection || hasSpectateAccess;
  };
  
  // Check if section is in spectate-only mode (can view but not edit)
  const isSpectateOnly = (section) => {
    // Managers never spectate only
    if (isManagerAccount()) return false;
    
    // If has full access, not spectate only
    if (hasFullAccess(section)) return false;
    
    // Check if in spectate sections
    return adminPermissions.spectateSections?.includes(section) || false;
  };
  
  // Special check: Can worker CREATE orders? (Yes, but limited other actions)
  const canCreateOrderOnly = () => {
    // Managers are never limited
    if (isManagerAccount()) return false;
    // Workers and spectators have this limited permission
    return adminPermissions.role === 'spectator' || adminPermissions.role === 'worker';
  };

  // Print Sale Order Function - Shows current quantities after returns
  const printSaleOrder = async (order) => {
    if (!order) {
      console.error('❌ Print function called with no order');
      return;
    }

    console.log('🖨️ Printing Sale Order - Customer Number:', order.customer_number);

    // 💰 PERMANENT FIX: Always ensure credit data is loaded so Old Balance prints correctly
    // for EVERY customer including guests (no special-casing — treated identically).
    try {
      if (!window.__creditAccountsOrders || window.__creditAccountsOrders.length === 0) {
        await loadCreditAccountsOrders(true);
      }
      if (!window.__customerAccountPayments || Object.keys(window.__customerAccountPayments).length === 0) {
        await loadAllCustomerAccountPayments();
      }
    } catch (e) {
      console.warn('Could not refresh credit data before print:', e?.message);
    }

    // Calculate remaining quantities after returns for display
    
    // Calculate remaining quantities after returns
    const itemsToDisplay = calculateRemainingQuantities(order);
    
    // Recalculate totals based on remaining quantities
    const recalculatedSubtotal = itemsToDisplay.reduce((total, item) => {
      return total + (parseFloat(item.unit_price || 0) * parseFloat(item.display_quantity || 0));
    }, 0);
    
    const additionalServices = parseFloat(order.fitting_charge || 0) + parseFloat(order.cut_piece_charge || 0);
    const recalculatedTotal = recalculatedSubtotal + additionalServices;
    
    // CRITICAL FIX: Use helper to get effective VAT - calculates properly for old orders with vat_amount=0
    const { vatAmount: recalculatedVatAmount, vatableCount, totalItems, isCalculated } = getFinalVatAmount(order, itemsToDisplay);
    
    if (isCalculated) {
      console.log('📊 VAT recalculated from items (old order fix):', recalculatedVatAmount.toFixed(2));
    }
    
    const discount = parseFloat(order.discount || 0);
    const recalculatedGrandTotal = recalculatedTotal + recalculatedVatAmount - discount;
    
    // Debug the calculation step by step
    console.log('🔍 STEP BY STEP CALCULATION DEBUG:');
    console.log('Items to display:', itemsToDisplay.map(item => ({
      name: item.product_name,
      display_quantity: item.display_quantity,
      unit_price: item.unit_price,
      line_total: parseFloat(item.unit_price || 0) * parseFloat(item.display_quantity || 0)
    })));
    
    console.log('📊 Calculation breakdown:', {
      recalculatedSubtotal: recalculatedSubtotal.toFixed(2),
      fitting_charge: order.fitting_charge,
      cut_piece_charge: order.cut_piece_charge,
      additionalServices: additionalServices.toFixed(2),
      recalculatedTotal: recalculatedTotal.toFixed(2),
      vatAmount: recalculatedVatAmount.toFixed(2),
      discount: discount.toFixed(2),
      recalculatedGrandTotal: recalculatedGrandTotal.toFixed(2)
    });

    // FIXED: Guest customers have their name stored directly in customer_name by backend
    let customerName, customerAddress, customerContact;
    
    // Guest customer check - backend already populates customer_name for guests
    if (order.customer_id === null || !order.customer_id || order.customer_id === 'guest') {
      // Backend copies guest_customer_name to customer_name when creating order
      customerName = order.customer_name || 'Guest Customer';
      customerAddress = order.customer_address || 'Address not provided';
      customerContact = order.customer_contact || 'Contact not provided';
    } else {
      // Use enriched customer data if available (same as preview)
      if (order.customer_name && order.customer_name !== 'Customer Not Found') {
        customerName = order.customer_name;
      } else {
        // Fallback to manual lookup (same as preview)
        const customer = customers.find(c => c.id === order.customer_id);
        customerName = customer ? customer.name : 'Customer Not Found';
      }
      
      // Address resolution (same as preview)
      if (order.customer_address && order.customer_address !== 'Address not provided') {
        customerAddress = order.customer_address;
      } else {
        const customer = customers.find(c => c.id === order.customer_id);
        customerAddress = customer && customer.address ? customer.address : 'Address not provided';
      }
      
      // Contact resolution (same as preview)
      if (order.customer_contact && order.customer_contact !== 'Contact not provided') {
        customerContact = order.customer_contact;
      } else {
        const customer = customers.find(c => c.id === order.customer_id);
        customerContact = customer && (customer.contact_number || customer.phone) ? (customer.contact_number || customer.phone) : 'Contact not provided';
      }
    }

    console.log('🎯 Final customer data for print (same logic as preview):', {
      customerName,
      customerAddress,
      customerContact,
      notes: order.notes // Log notes for debugging
    });

    // NOTE: Old Balance calculation removed per user request - bills now show only order total
    // let oldBalance = calculateBulletproofOldBalance(order, fulfilledOrders);

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Sale Order - #${order.id ? order.id.substring(0, 8) : 'N/A'}</title>
        <style>
          @page { 
            margin: 0.2in; 
            size: A4; 
          }
          body { 
            font-family: Arial, sans-serif; 
            margin: 0; 
            padding: 10px;
            color: #000;
            font-size: 11px;
            background: #fff;
            line-height: 1.2;
          }
          .document { 
            background: #fff;
            padding: 0;
            margin: 0;
            position: relative;
            max-height: 100vh;
            overflow: hidden;
          }
          .header { 
            text-align: center;
            padding: 20px 15px;
            background: #fff;
            color: #000;
            margin-bottom: 10px;
          }
          .company-name { 
            font-size: 28px; 
            font-weight: bold; 
            letter-spacing: 2px;
            margin: 0;
          }
          .company-tagline { 
            font-size: 14px; 
            color: #d4af37;
            margin-top: 5px;
          }
          .company-address { 
            font-size: 12px;
            color: #666;
            margin-top: 5px;
          }
          .customer-section { 
            padding: 10px 15px;
            background: #fff;
          }
          .customer-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 10px;
          }
          .customer-table td { 
            padding: 4px 8px; 
            border: 1px solid #000; 
            font-size: 11px;
          }
          .customer-label { 
            background: #f0f0f0; 
            font-weight: bold; 
            width: 180px;
          }
          .order-header { 
            text-align: center; 
            padding: 10px 15px;
            background: #fff;
          }
          .order-title { 
            font-size: 20px; 
            font-weight: bold; 
            margin-bottom: 5px;
          }
          .order-date { 
            float: right; 
            font-size: 12px; 
            margin-top: -30px;
          }
          .items-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin: 10px 0;
          }
          .items-table th { 
            background: #d4af37; 
            color: #000; 
            padding: 6px 4px; 
            text-align: center; 
            font-weight: bold;
            border: 1px solid #000;
            font-size: 10px;
          }
          .items-table td { 
            padding: 4px 4px; 
            border: 1px solid #000; 
            text-align: center;
            vertical-align: top;
            font-size: 10px;
            line-height: 1.2;
          }
          .description-cell { 
            text-align: left !important;
            max-width: 200px;
          }
          .item-name { 
            font-weight: bold; 
            margin-bottom: 2px;
            display: block;
            line-height: 1.2;
            font-size: 10px;
          }
          .item-details { 
            font-size: 9px; 
            color: #666;
            font-style: italic;
            display: block;
            margin-top: 2px;
            line-height: 1.1;
          }
          .totals-section { 
            margin-top: 10px; 
            display: flex; 
            justify-content: space-between; 
            align-items: flex-end;
            padding: 0 15px;
          }
          .total-stock { 
            background: #d4af37; 
            color: #000; 
            padding: 8px 12px; 
            font-weight: bold;
            font-size: 12px;
          }
          .totals-table { 
            border-collapse: collapse;
          }
          .totals-table td { 
            padding: 4px 8px; 
            border: 1px solid #000;
            min-width: 100px;
            font-size: 10px;
          }
          .total-label { 
            background: #f0f0f0; 
            font-weight: bold;
          }
          .final-total { 
            background: #d4af37; 
            font-weight: bold; 
            font-size: 12px;
          }
          .payment-agreement { 
            margin-top: 15px; 
            padding: 10px 15px;
            background: #f8f8f8;
            font-size: 9px;
            line-height: 1.3;
            border: 1px solid #ddd;
          }
          @media print {
            body { 
              background: #fff !important; 
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .no-print { display: none !important; }
            .document { 
              height: auto !important; 
              max-height: none !important; 
            }
            @page {
              margin: 0.2in;
              size: A4;
            }
          }
        </style>
      </head>
      <body>
        <div class="document">
          <div class="header">
            <div class="company-name">PLUSH LIVING LTD</div>
            <div class="company-tagline">Making your house a home</div>
            <div class="company-address">Unit 30, 197 adderley road, Birmingham, B8 1AN</div>
          </div>

          <div class="customer-section">
            <table class="customer-table">
              <tr>
                <td class="customer-label">customer name:</td>
                <td>${customerName}</td>
              </tr>
              <tr>
                <td class="customer-label">customer #:</td>
                <td><strong>${order.customer_number || 'N/A'}</strong></td>
              </tr>
              <tr>
                <td class="customer-label">customer Address:</td>
                <td>${customerAddress}</td>
              </tr>
              <tr>
                <td class="customer-label">customer contact detail:</td>
                <td>${customerContact}</td>
              </tr>
            </table>
          </div>

          <div class="order-header">
            <div class="order-title">SALE ORDER</div>
            <div class="order-date">
              <strong>DATE</strong> ${order.date ? new Date(order.date).toLocaleDateString('en-GB', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              }) : new Date().toLocaleDateString('en-GB', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              })}
              ${order.edit_date ? '<br><span style="color: #f59e0b; font-style: italic; font-size: 12px;">Edited ' + new Date(order.edit_date).toLocaleDateString('en-GB') + '</span>' : ''}
            </div>
          </div>

          <table class="items-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Size</th>
                <th>Qnty</th>
                <th>Price</th>
                <th>Total Price</th>
              </tr>
            </thead>
            <tbody>
              ${itemsToDisplay.length > 0 ? itemsToDisplay.map(item => 
                '<tr>' +
                  '<td class="description-cell">' +
                    '<div class="item-name">' + (item.product_name || item.name || 'Item') + 
                    (item.returned_quantity > 0 ? ' <span style="color: #888; font-size: 0.9em;">(' + item.returned_quantity + ' returned)</span>' : '') + '</div>' +
                    '<div class="item-details" style="margin-top: 5px; line-height: 1.2;">' + 
                    ((item.color || item.storage_location || item.storageLocation) ? 
                      [item.color, item.storage_location || item.storageLocation].filter(Boolean).join(' | ') : 
                      'Product details'
                    ) + '</div>' +
                  '</td>' +
                  '<td>' + (
                    // For square meter products with dimensions and units, display with units
                    (item.wide && item.length && item.wide_unit && item.length_unit) ? 
                      (item.wide + (item.wide_unit === 'feet' ? 'FT' : 'M') + 'x' + item.length + (item.length_unit === 'feet' ? 'FT' : 'M')) :
                    // For square meter products with dimensions but no units, use basic format
                    (item.wide && item.length) ? (item.wide + 'x' + item.length) :
                    // If size contains dimensions format with units, use it
                    (item.size && item.size.includes && item.size.includes('x') && item.size !== 'N/A') ? item.size :
                    // For square meter products, use size field
                    (item.measurement_unit === 'per_square_meter' && item.size) ? item.size :
                    // Default fallback
                    (item.size || 'N/A')
                  ) + '</td>' +
                  '<td>' + item.display_quantity + '</td>' +
                  '<td>£' + (item.unit_price ? parseFloat(item.unit_price).toFixed(2) : (item.price ? parseFloat(item.price).toFixed(2) : '0.00')) + '</td>' +
                  '<td>£' + item.display_total_price + '</td>' +
                '</tr>'
              ).join('') : 
                '<tr>' +
                  '<td class="description-cell">' +
                    '<div class="item-name">No remaining items</div>' +
                    '<div class="item-details">All items have been returned</div>' +
                  '</td>' +
                  '<td>-</td>' +
                  '<td>-</td>' +
                  '<td>-</td>' +
                  '<td>-</td>' +
                '</tr>'
              }
              ${Array.from({ length: Math.max(0, 5 - (order.items?.length || 1)) }, () => 
                '<tr>' +
                  '<td>&nbsp;</td>' +
                  '<td>&nbsp;</td>' +
                  '<td>&nbsp;</td>' +
                  '<td>&nbsp;</td>' +
                  '<td>&nbsp;</td>' +
                '</tr>'
              ).join('')}
            </tbody>
          </table>

          <div class="totals-section">
            <div class="total-stock">
              Total Stock: ${itemsToDisplay.reduce((total, item) => total + parseFloat(item.display_quantity || 0), 0).toFixed(1)}
              ${order.return_items && order.return_items.length > 0 ? 
                ` <span style="color: #888; font-size: 0.9em;">(${order.return_items.reduce((total, returnItem) => total + (returnItem.return_quantity || returnItem.returned_quantity || 0), 0)} returned)</span>` 
                : ''
              }
            </div>
            <table class="totals-table">
              <tr>
                <td class="total-label">Total Stock</td>
                <td>£${recalculatedSubtotal.toFixed(2)}</td>
              </tr>
              ${parseFloat(order.cut_piece_charge || 0) > 0 ? 
                '<tr>' +
                  '<td class="total-label">Cutting Cost</td>' +
                  '<td>£' + parseFloat(order.cut_piece_charge || 0).toFixed(2) + '</td>' +
                '</tr>' : ''
              }
              ${parseFloat(order.fitting_charge || 0) > 0 ? 
                '<tr>' +
                  '<td class="total-label">Fitting Cost</td>' +
                  '<td>£' + parseFloat(order.fitting_charge || 0).toFixed(2) + '</td>' +
                '</tr>' : ''
              }
              <tr>
                <td class="total-label">Subtotal</td>
                <td>£${recalculatedTotal.toFixed(2)}</td>
              </tr>
              ${(() => {
                // VAT LOGIC: Show if VAT amount > 0 (uses stored per-item VAT calculation)
                const shouldShowVat = recalculatedVatAmount > 0;
                
                return shouldShowVat ? 
                  '<tr>' +
                    '<td class="total-label">VAT (20%)</td>' +
                    '<td>£' + recalculatedVatAmount.toFixed(2) + '</td>' +
                  '</tr>' : '';
              })()}
              ${parseFloat(order.discount || 0) > 0 ? 
                '<tr style="color: #d32f2f;">' +
                  '<td class="total-label">Discount</td>' +
                  '<td>-£' + parseFloat(order.discount || 0).toFixed(2) + '</td>' +
                '</tr>' : ''
              }
              <tr>
                <td class="total-label" style="font-weight: 600;">Total Bill</td>
                <td style="font-weight: 600;">£${(recalculatedTotal + recalculatedVatAmount - parseFloat(order.discount || 0)).toFixed(2)}</td>
              </tr>
              ${(() => {
                // 💰 BULLETPROOF Old Balance — ALWAYS shown on every sale order for EVERY customer
                // including guests (matched by PLU-GUEST-G#### customer_number). Treated identically
                // to real customers per business rule.
                let liveOldBalance = 0;
                const orderIsGuest = !order.customer_id || order.customer_id === 'guest';
                const lookupKey = orderIsGuest ? order.customer_number : order.customer_id;
                
                if (lookupKey) {
                  const allCreditOrders = window.__creditAccountsOrders || [];
                  const customerCreditOrders = allCreditOrders.filter(o => {
                    if (o.id === order.id) return false;
                    const otherIsGuest = !o.customer_id || o.customer_id === 'guest';
                    const otherKey = otherIsGuest ? o.customer_number : o.customer_id;
                    return otherKey === lookupKey;
                  });
                  const totalOtherOutstanding = customerCreditOrders.reduce((sum, o) => {
                    return sum + parseFloat(o.outstanding_balance || 0);
                  }, 0);
                  
                  // Subtract account/lump-sum payments (guests use customer_number as their key)
                  const customerPayments = window.__customerAccountPayments || {};
                  const acctPaymentsData = customerPayments[lookupKey] || { total: 0 };
                  const acctPaymentsTotal = parseFloat(acctPaymentsData.total || 0);
                  
                  liveOldBalance = Math.max(0, Math.round((totalOtherOutstanding - acctPaymentsTotal) * 100) / 100);
                }
                
                const totalBill = recalculatedTotal + recalculatedVatAmount - parseFloat(order.discount || 0);
                const grandTotalWithOldBalance = totalBill + liveOldBalance;
                
                // ALWAYS render the Old Balance row (even when £0.00) — this is required for consistency
                return '<tr>' +
                  '<td class="total-label">Old Balance</td>' +
                  '<td>£' + liveOldBalance.toFixed(2) + '</td>' +
                '</tr>' +
                '<tr class="final-total">' +
                  '<td class="total-label">Grand Total</td>' +
                  '<td>£' + grandTotalWithOldBalance.toFixed(2) + '</td>' +
                '</tr>';
              })()}
            </table>
          </div>

          ${order.notes ? `
          <div class="notes-section" style="margin-top: 15px; padding: 12px; background: #fff9e6; border-left: 3px solid #d4af37; border-radius: 4px;">
            <strong style="color: #000; font-size: 12px; display: block; margin-bottom: 8px;">📝 SPECIAL INSTRUCTIONS / NOTES:</strong>
            <p style="margin: 0; color: #333; font-size: 11px; line-height: 1.5; white-space: pre-wrap;">${order.notes}</p>
          </div>
          ` : ''}

          <div class="payment-agreement">
            <strong>PAYMENT AGREEMENT:</strong> We, Plush Living LTD, are giving our well-known customers a 30 day credit. This credit is being given out of our trust towards both the customer and also the shop. For business of the month, if this payment is delayed by payment in lieu of goods by us we shall demand before the public on a non-notional from the conditions stated by the customer.<br><br>
            
            <strong>BANK DETAILS FOR PAYMENT:</strong><br>
            <strong>Lloyds Bank</strong><br>
            <strong>Plush Living LTD</strong><br>
            <strong>Sort Code: 30-54-66</strong> | <strong>Account Number: 62274460</strong><br><br>
            
            <strong>Contact:</strong> Tel: 0121 649 1673 | Email: PlushLivingLTD@outlook.com | Website: Plush-livingltd.co.uk<br>
            <strong>VAT Registration: 493471464</strong> | <strong>Company Registration: 16405782</strong>
          </div>
        </div>

        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            }, 500);
          }
        </script>
      </body>
      </html>
    `;

    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    const printWindow = window.open(url, '_blank', 'width=800,height=600');
    if (!printWindow) {
      alert('Please allow popups to print documents');
    }
  };

  // Generate Sales Report Function
  const generateSalesReport = async (period, customFromDate = null, customToDate = null) => {
    if (!fulfilledOrders || fulfilledOrders.length === 0) {
      alert('No sales data available for report generation.');
      return;
    }

    // Calculate date range based on period
    const today = new Date();
    let startDate, endDate, periodLabel;
    
    switch(period) {
      case 'daily':
        startDate = new Date();
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
        periodLabel = `Daily Report - ${new Date().toLocaleDateString('en-GB')}`;
        break;
      case 'weekly':
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 6);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
        periodLabel = `Weekly Report - ${startDate.toLocaleDateString('en-GB')} to ${new Date().toLocaleDateString('en-GB')}`;
        break;
      case 'monthly':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
        periodLabel = `Monthly Report - ${startDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`;
        break;
      case 'yearly':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = new Date(today.getFullYear(), 11, 31, 23, 59, 59, 999);
        periodLabel = `Yearly Report - ${today.getFullYear()}`;
        break;
      case 'custom':
        if (!customFromDate || !customToDate) {
          alert('Please select both From and To dates for custom report.');
          return;
        }
        startDate = new Date(customFromDate);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(customToDate);
        endDate.setHours(23, 59, 59, 999);
        periodLabel = `Custom Report - ${startDate.toLocaleDateString('en-GB')} to ${endDate.toLocaleDateString('en-GB')}`;
        break;
      default:
        return;
    }

    // 💰 FETCH ACCOUNT PAYMENTS (Lump Sum Payments) for the period
    // These are payments made towards old credit balances stored separately
    let accountPayments = [];
    let totalAccountPaymentsCash = 0;
    let totalAccountPaymentsBank = 0;
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      const response = await axios.get(`${API}/api/credit-accounts/account-payments-all?start_date=${startDateStr}&end_date=${endDateStr}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data) {
        accountPayments = response.data.payments || [];
        totalAccountPaymentsCash = response.data.total_cash || 0;
        totalAccountPaymentsBank = response.data.total_bank || 0;
        console.log('💰 Account payments fetched for report:', response.data);
      }
    } catch (error) {
      console.error('Failed to fetch account payments for report:', error);
    }

    // SIMPLIFIED: Track orders and payments in the period
    const orderRecords = [];
    const oldBillPayments = [];
    let totalCashReceived = 0;
    let totalBankReceived = 0;
    let totalCreditReceived = 0; // Credit payments received on old bills
    let totalCreditGiven = 0;
    let totalOrderValue = 0;
    
    // Calculate expenses for the period
    let totalExpensesCash = 0;
    let totalExpensesBank = 0;
    expenses.forEach(expense => {
      const expenseDate = new Date(expense.date);
      if (!isNaN(expenseDate.getTime()) && expenseDate >= startDate && expenseDate <= endDate) {
        const amount = parseFloat(expense.amount || 0);
        const paymentMethod = (expense.payment_method || '').toLowerCase();
        if (paymentMethod === 'bank') {
          totalExpensesBank += amount;
        } else {
          totalExpensesCash += amount;
        }
      }
    });
    const totalExpenses = totalExpensesCash + totalExpensesBank;
    
    // Scan ALL orders
    fulfilledOrders.forEach(order => {
      const orderStatus = (order.status || '').toLowerCase();
      if (orderStatus === 'returned' || orderStatus === 'cancelled' || orderStatus === 'deleted') {
        return;
      }
      
      const orderDate = new Date(order.date);
      const orderInPeriod = orderDate >= startDate && orderDate <= endDate;
      const grandTotal = parseFloat(order.grand_total || 0);
      const cashPaidTotal = parseFloat(order.cash_paid_amount || 0);
      const bankPaidTotal = parseFloat(order.bank_paid_amount || 0);
      // CRITICAL FIX: Include credit_paid_amount in total paid calculation
      const creditPaidTotal = parseFloat(order.credit_paid_amount || 0);
      const totalPaid = cashPaidTotal + bankPaidTotal + creditPaidTotal;
      const balanceRemaining = Math.max(0, grandTotal - totalPaid);
      
      // For orders CREATED in the period - show as main order rows
      if (orderInPeriod) {
        totalOrderValue += grandTotal;
        totalCashReceived += cashPaidTotal;
        totalBankReceived += bankPaidTotal;
        if (balanceRemaining > 0) {
          totalCreditGiven += balanceRemaining;
        }
        
        orderRecords.push({
          date: orderDate,
          orderId: order.id,
          customerName: order.customer_name || 'N/A',
          customerNumber: order.customer_number || 'N/A',
          orderTotal: grandTotal,
          cashPaid: cashPaidTotal,
          bankPaid: bankPaidTotal,
          balance: balanceRemaining,
          status: balanceRemaining > 0 ? 'CREDIT' : 'PAID',
          isOldBillPayment: false
        });
      } else {
        // For OLD orders - check if any payments were made during this period
        // ALL payments on OLD orders count as "Credit Received/Collected" - not new sales
        const cashPayments = order.cash_payments || [];
        const bankPayments = order.bank_payments || [];
        const creditPayments = order.credit_payments || [];
        const paymentHistory = order.payment_history || [];
        
        // Helper function to get payment date from various possible field names
        const getPaymentDate = (payment) => {
          // Try multiple date fields that might exist
          const dateStr = payment.paid_at || payment.date || payment.payment_date || payment.created_at || payment.timestamp;
          if (!dateStr) {
            console.warn('⚠️ Payment has no date field:', payment);
            return null;
          }
          const d = new Date(dateStr);
          if (isNaN(d.getTime())) {
            console.warn('⚠️ Invalid payment date:', dateStr, 'in payment:', payment);
            return null;
          }
          return d;
        };
        
        // Track credit collected by payment method for display purposes
        let periodCreditCash = 0;    // Credit collected via cash
        let periodCreditBank = 0;    // Credit collected via bank
        let lastPaymentDate = null;  // Track the actual payment date for display
        
        // Flag to track if we found any payments in the payment arrays
        let foundInArrays = false;
        
        // Debug: log order ID if it has any payments
        const hasAnyPayments = (order.cash_payments?.length > 0) || 
                               (order.bank_payments?.length > 0) || 
                               (order.credit_payments?.length > 0);
        
        cashPayments.forEach(payment => {
          const paymentDate = getPaymentDate(payment);
          // CRITICAL: Only include if payment date is within report period
          if (paymentDate && paymentDate >= startDate && paymentDate <= endDate) {
            periodCreditCash += parseFloat(payment.amount || 0);
            foundInArrays = true;
            // Track the latest payment date
            if (!lastPaymentDate || paymentDate > lastPaymentDate) {
              lastPaymentDate = paymentDate;
            }
          }
        });
        
        bankPayments.forEach(payment => {
          const paymentDate = getPaymentDate(payment);
          if (paymentDate && paymentDate >= startDate && paymentDate <= endDate) {
            periodCreditBank += parseFloat(payment.amount || 0);
            foundInArrays = true;
            if (!lastPaymentDate || paymentDate > lastPaymentDate) {
              lastPaymentDate = paymentDate;
            }
          }
        });
        
        // Check credit payments (payments made against credit balance)
        creditPayments.forEach(payment => {
          const paymentDate = getPaymentDate(payment);
          if (paymentDate && paymentDate >= startDate && paymentDate <= endDate) {
            const paymentMethod = (payment.method || '').toLowerCase();
            const amt = parseFloat(payment.amount || 0);
            // Credit payments can be via cash or bank
            if (paymentMethod === 'cash') {
              periodCreditCash += amt;
            } else if (paymentMethod === 'bank') {
              periodCreditBank += amt;
            } else {
              // Default: count as cash if method not specified
              periodCreditCash += amt;
            }
            foundInArrays = true;
            if (!lastPaymentDate || paymentDate > lastPaymentDate) {
              lastPaymentDate = paymentDate;
            }
          }
        });
        
        // Only check payment_history if we didn't find anything in the arrays (fallback for older data)
        if (!foundInArrays && paymentHistory.length > 0) {
          paymentHistory.forEach(payment => {
            const paymentDate = getPaymentDate(payment);
            if (paymentDate && paymentDate >= startDate && paymentDate <= endDate) {
              const paymentType = (payment.payment_type || payment.type || payment.method || '').toLowerCase();
              const amt = parseFloat(payment.amount || 0);
              if (paymentType.includes('bank')) {
                periodCreditBank += amt;
              } else {
                periodCreditCash += amt; // Default to cash
              }
              if (!lastPaymentDate || paymentDate > lastPaymentDate) {
                lastPaymentDate = paymentDate;
              }
            }
          });
        }
        
        const totalPeriodCreditReceived = periodCreditCash + periodCreditBank;
        
        // If payments were made to old bill during this period - count ALL as credit received
        // CRITICAL: Only include if we found valid payment dates within the period
        if (totalPeriodCreditReceived > 0 && lastPaymentDate) {
          // This is credit collected, NOT new sales
          totalCreditReceived += totalPeriodCreditReceived;
          
          oldBillPayments.push({
            date: lastPaymentDate, // Use actual payment date (never fallback to today)
            orderId: order.id,
            customerName: order.customer_name || 'N/A',
            customerNumber: order.customer_number || 'N/A',
            orderTotal: grandTotal,
            orderDate: orderDate,
            cashPaid: periodCreditCash,
            bankPaid: periodCreditBank,
            creditReceived: totalPeriodCreditReceived,
            balance: balanceRemaining,
            status: balanceRemaining > 0 ? 'CREDIT' : 'PAID',
            isOldBillPayment: true
          });
        }
      }
    });
    
    // Sort by date
    orderRecords.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // 💰 ADD ACCOUNT PAYMENTS (Lump Sum Payments) to Credit Received
    // These are payments recorded via "Record Payment" button in Credit Accounts
    accountPayments.forEach(payment => {
      const paymentDate = new Date(payment.created_at);
      // Normalize payment method - handle uppercase, lowercase, and edge cases
      const rawMethod = (payment.payment_method || '').toString().trim();
      const paymentMethod = rawMethod.toLowerCase();
      const amount = parseFloat(payment.amount || 0);
      
      // Determine cash vs bank based on payment method - be more flexible
      const isCash = paymentMethod === 'cash' || paymentMethod === 'CASH';
      const isBank = paymentMethod === 'bank' || paymentMethod === 'BANK' || 
                     paymentMethod.includes('bank') || paymentMethod.includes('transfer');
      
      // If neither cash nor bank is detected, check if amount should default to bank
      // Log warning for debugging
      if (!isCash && !isBank && amount > 0) {
        console.warn(`⚠️ Account payment with unclear method: "${rawMethod}" for ${payment.customer_name}, amount £${amount}. Defaulting to Cash.`);
      }
      
      // Default: if not explicitly bank, treat as cash
      const finalIsCash = isCash || (!isBank);
      const finalIsBank = isBank && !isCash;
      
      totalCreditReceived += amount;
      
      oldBillPayments.push({
        date: paymentDate,
        orderId: 'ACCOUNT',
        customerName: payment.customer_name || 'N/A',
        customerNumber: '', // Account payments not tied to specific order
        orderTotal: amount,
        orderDate: paymentDate,
        cashPaid: finalIsCash ? amount : 0,
        bankPaid: finalIsBank ? amount : 0,
        creditReceived: amount,
        balance: 0,
        status: 'ACCOUNT PMT',
        isOldBillPayment: true,
        isAccountPayment: true,
        notes: payment.notes || '',
        rawPaymentMethod: rawMethod  // Store original for debugging
      });
    });
    
    if (orderRecords.length === 0 && oldBillPayments.length === 0) {
      alert(`No sales or payments found for ${periodLabel}`);
      return;
    }

    const totalReceived = totalCashReceived + totalBankReceived + totalCreditReceived;

    // Build order rows HTML
    let orderRowsHTML = '';
    if (orderRecords.length > 0) {
      orderRecords.forEach(record => {
        const dateStr = record.date.toLocaleDateString('en-GB');
        const timeStr = record.date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        const statusClass = record.status === 'PAID' ? 'status-paid' : 'status-credit';
        const cashDisplay = record.cashPaid > 0 ? '£' + record.cashPaid.toFixed(2) : '-';
        const bankDisplay = record.bankPaid > 0 ? '£' + record.bankPaid.toFixed(2) : '-';
        const balanceDisplay = record.balance > 0 ? '£' + record.balance.toFixed(2) : '-';
        
        orderRowsHTML += `
          <tr>
            <td>${dateStr}</td>
            <td>${timeStr}</td>
            <td>${record.orderId.substring(0, 8)}...</td>
            <td>${record.customerName}</td>
            <td>${record.customerNumber}</td>
            <td><strong>£${record.orderTotal.toFixed(2)}</strong></td>
            <td style="color: #16a34a;">${cashDisplay}</td>
            <td style="color: #2563eb;">${bankDisplay}</td>
            <td style="color: #dc2626;">${balanceDisplay}</td>
            <td><span class="${statusClass}">${record.status}</span></td>
          </tr>
        `;
      });
    } else {
      orderRowsHTML = '<tr><td colspan="10" style="text-align: center; color: #666;">No new orders in this period</td></tr>';
    }
    
    // Build old bill payments HTML (Credit Collected from old orders)
    let oldBillHTML = '';
    if (oldBillPayments.length > 0) {
      let oldBillRows = '';
      oldBillPayments.forEach(record => {
        // Show PAYMENT date (when payment was made), not order date
        const paymentDateStr = record.date.toLocaleDateString('en-GB');
        const origDateStr = record.orderDate.toLocaleDateString('en-GB');
        const statusClass = record.status === 'PAID' ? 'status-paid' : (record.status === 'ACCOUNT PMT' ? 'status-account' : 'status-credit');
        const balanceDisplay = record.balance > 0 ? '£' + record.balance.toFixed(2) : '-';
        const totalPayment = record.creditReceived || 0;
        
        // Show payment method breakdown
        let paymentMethod = '';
        if (record.cashPaid > 0 && record.bankPaid > 0) {
          paymentMethod = `Cash: £${record.cashPaid.toFixed(2)} + Bank: £${record.bankPaid.toFixed(2)}`;
        } else if (record.cashPaid > 0) {
          paymentMethod = 'Cash';
        } else if (record.bankPaid > 0) {
          paymentMethod = 'Bank';
        }
        
        // Display differently for account payments vs order-specific payments
        const orderIdDisplay = record.isAccountPayment ? '<span style="color: #7c3aed; font-weight: bold;">ACCOUNT PMT</span>' : record.orderId.substring(0, 8) + '...';
        const customerNumDisplay = record.isAccountPayment ? '-' : record.customerNumber;
        const notesDisplay = record.notes ? `<div style="font-size: 10px; color: #666;">${record.notes}</div>` : '';
        
        oldBillRows += `
          <tr class="old-bill ${record.isAccountPayment ? 'account-payment' : ''}">
            <td>${paymentDateStr}<br><span style="font-size: 10px; color: #888;">Order: ${origDateStr}</span>${notesDisplay}</td>
            <td>${orderIdDisplay}</td>
            <td>${record.customerName}</td>
            <td>${customerNumDisplay}</td>
            <td>£${record.orderTotal.toFixed(2)}</td>
            <td style="color: #7c3aed; font-weight: bold;">£${totalPayment.toFixed(2)}</td>
            <td>${paymentMethod}</td>
            <td style="color: #dc2626;">${balanceDisplay}</td>
            <td><span class="${statusClass}">${record.status}</span></td>
          </tr>
        `;
      });
      
      oldBillHTML = `
        <h3 style="color: #7c3aed; margin-top: 30px;">💳 Credit Collected (Payments for Old Bills)</h3>
        <p style="color: #666; font-size: 12px; margin-bottom: 10px;">Payments received today for orders placed on previous days (includes Account Payments)</p>
        <table>
          <thead>
            <tr>
              <th>Payment Date</th>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Customer #</th>
              <th>Amount</th>
              <th>Amount Collected</th>
              <th>Payment Method</th>
              <th>Remaining Balance</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${oldBillRows}
          </tbody>
        </table>
      `;
    }

    // Generate HTML report - CLEAN ONE ROW PER ORDER FORMAT
    const reportHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Sales Report - Plush Living Ltd</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #b8860b; padding-bottom: 20px; }
          .company-name { font-size: 28px; font-weight: bold; color: #b8860b; }
          .report-title { font-size: 22px; margin-top: 10px; color: #333; }
          .summary { background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 30px; }
          .summary-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #ddd; }
          .summary-row:last-child { border-bottom: none; font-weight: bold; font-size: 18px; }
          .label { font-weight: 600; }
          .value { color: #b8860b; font-weight: bold; }
          .value.cash { color: #16a34a; }
          .value.bank { color: #2563eb; }
          .value.credit { color: #dc2626; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #0f1a35; color: white; padding: 10px; text-align: left; font-size: 12px; }
          td { padding: 8px; border-bottom: 1px solid #ddd; font-size: 12px; }
          tr:hover { background: #f8f9fa; }
          .status-paid { background: #dcfce7; color: #166534; padding: 4px 8px; border-radius: 4px; font-weight: bold; }
          .status-credit { background: #fef2f2; color: #dc2626; padding: 4px 8px; border-radius: 4px; font-weight: bold; }
          .status-account { background: #e9d5ff; color: #7c3aed; padding: 4px 8px; border-radius: 4px; font-weight: bold; }
          .old-bill { background: #fef3c7; }
          .account-payment { background: #ede9fe; }
          .footer { margin-top: 40px; text-align: center; color: #666; font-size: 12px; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">PLUSH LIVING LTD</div>
          <div class="report-title">${periodLabel}</div>
          <div style="margin-top: 10px; color: #666;">Generated: ${new Date().toLocaleString('en-GB')}</div>
        </div>

        <div class="summary">
          <h3 style="margin-top: 0; color: #b8860b;">💰 Summary</h3>
          <div class="summary-row">
            <span class="label">💵 Cash Received (Today's Sales):</span>
            <span class="value cash">£${totalCashReceived.toFixed(2)}</span>
          </div>
          <div class="summary-row">
            <span class="label">🏦 Bank Received (Today's Sales):</span>
            <span class="value bank">£${totalBankReceived.toFixed(2)}</span>
          </div>
          <div class="summary-row" style="border-top: 1px dashed #ccc; padding-top: 8px; margin-top: 8px;">
            <span class="label">💳 Credit Collected (Old Bills):</span>
            <span class="value" style="color: #7c3aed; font-weight: bold;">£${totalCreditReceived.toFixed(2)}</span>
          </div>
          <div class="summary-row" style="border-top: 2px solid #b8860b; padding-top: 10px; margin-top: 10px;">
            <span class="label"><strong>📊 Total Received:</strong></span>
            <span class="value" style="font-size: 18px;"><strong>£${totalReceived.toFixed(2)}</strong></span>
          </div>
          <div class="summary-row">
            <span class="label">📋 Credit Given (Unpaid):</span>
            <span class="value credit">£${totalCreditGiven.toFixed(2)}</span>
          </div>
          <div class="summary-row">
            <span class="label">📦 Total Order Value:</span>
            <span class="value">£${totalOrderValue.toFixed(2)}</span>
          </div>
          <div class="summary-row" style="border-top: 2px dashed #dc3545; padding-top: 10px; margin-top: 10px;">
            <span class="label" style="color: #dc3545;">💸 Total Expenses (Cash):</span>
            <span class="value" style="color: #dc3545;">£${totalExpensesCash.toFixed(2)}</span>
          </div>
          <div class="summary-row">
            <span class="label" style="color: #dc3545;">💸 Total Expenses (Bank):</span>
            <span class="value" style="color: #dc3545;">£${totalExpensesBank.toFixed(2)}</span>
          </div>
          <div class="summary-row" style="border-top: 1px solid #dc3545; padding-top: 8px; margin-top: 8px;">
            <span class="label"><strong style="color: #dc3545;">📊 Total Expenses:</strong></span>
            <span class="value" style="color: #dc3545; font-size: 16px;"><strong>£${totalExpenses.toFixed(2)}</strong></span>
          </div>
          <div class="summary-row" style="border-top: 3px double #0f1a35; padding-top: 12px; margin-top: 12px; background: #f0f9ff;">
            <span class="label"><strong style="font-size: 16px;">💰 Net Cash Position:</strong></span>
            <span class="value" style="font-size: 18px; color: ${(totalReceived - totalExpenses) >= 0 ? '#16a34a' : '#dc2626'};"><strong>£${(totalReceived - totalExpenses).toFixed(2)}</strong></span>
          </div>
        </div>

        <h3 style="color: #b8860b;">📝 New Orders</h3>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Time</th>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Customer #</th>
              <th>Order Total</th>
              <th>Cash Paid</th>
              <th>Bank Paid</th>
              <th>Balance</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${orderRowsHTML}
          </tbody>
        </table>

        ${oldBillHTML}

        <div class="footer">
          <p>Plush Living Ltd | Unit 30 Adderley Rd, Saltley, Birmingham B8 1AN</p>
          <p>Tel: 0121 649 1673 | Email: Plushlivingltd@outlook.com | VAT No: 493471464</p>
        </div>
      </body>
      </html>
    `;

    // Open in new window and print
    const printWindow = window.open('', '_blank');
    printWindow.document.write(reportHTML);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Print Professional VAT Invoice Function - Professional Business Format
  const printVATInvoice = (order) => {
    if (!order) return;
    
    console.log('🖨️ VAT Invoice function called with order:', order.id);
    console.log('📊 DEBUGGING VAT INVOICE - Order data:', {
      vat_rate: order.vat_rate,
      vat_amount: order.vat_amount,
      discount: order.discount,
      grand_total: order.grand_total,
      cash_paid: order.cash_paid_amount,
      bank_paid: order.bank_paid_amount,
      credit_paid: order.credit_paid_amount,
      selectedInvoice: selectedInvoice
    });

    // ═══════════════════════════════════════════════════════════════
    // PARTIAL PAYMENT DETECTION LOGIC
    // ═══════════════════════════════════════════════════════════════
    // RULE: Show "Assorted" format ONLY for partial payments
    //       Show full product details for fully paid invoices
    // ═══════════════════════════════════════════════════════════════
    
    let totalPaid = 0;
    let isPartialPayment = false;
    const grandTotal = parseFloat(order.grand_total || 0);
    
    if (selectedInvoice && selectedInvoice.total_amount) {
      // Scenario 1: Viewing from Invoices tab
      // Use the specific invoice amount (e.g., £80 for this payment)
      totalPaid = parseFloat(selectedInvoice.total_amount || 0);
      isPartialPayment = totalPaid > 0 && totalPaid < grandTotal;
      
      console.log('💰 Invoice View - Using invoice amount:', {
        invoiceAmount: totalPaid,
        grandTotal: grandTotal,
        isPartialPayment: isPartialPayment,
        rule: isPartialPayment ? 'Show ASSORTED (Partial)' : 'Show FULL DETAILS (Paid)'
      });
    } else {
      // Scenario 2: Viewing from Orders tab
      // Calculate total from all payments on the order
      const cashPaid = parseFloat(order.cash_paid_amount || 0);
      const bankPaid = parseFloat(order.bank_paid_amount || 0);
      const creditPaid = parseFloat(order.credit_paid_amount || 0);
      totalPaid = cashPaid + bankPaid + creditPaid;
      isPartialPayment = totalPaid > 0 && totalPaid < grandTotal;
      
      console.log('💰 Order View - Calculated from payments:', {
        cashPaid,
        bankPaid,
        creditPaid,
        totalPaid,
        grandTotal,
        isPartialPayment,
        rule: isPartialPayment ? 'Show ASSORTED (Partial)' : 'Show FULL DETAILS (Paid)'
      });
    }
    
    // ═══════════════════════════════════════════════════════════════
    // FINAL DECISION:
    // isPartialPayment = true  → Show "Assorted" simplified format
    // isPartialPayment = false → Show full product details
    // ═══════════════════════════════════════════════════════════════

    // Calculate remaining quantities after returns for VAT Invoice
    const itemsToDisplay = calculateRemainingQuantities(order);
    
    // Recalculate totals based on remaining quantities for VAT Invoice
    const recalculatedSubtotal = itemsToDisplay.reduce((total, item) => {
      return total + (parseFloat(item.unit_price || 0) * parseFloat(item.display_quantity || 0));
    }, 0);

    // Services are excluded if the order is fully returned (by status flag or zero remaining qty)
    const remainingQtySum = itemsToDisplay.reduce((s, i) => s + parseFloat(i.display_quantity || 0), 0);
    let additionalServices = (parseFloat(order.fitting_charge || 0) + parseFloat(order.cut_piece_charge || 0));
    const isFullReturnClient = (order && (order.status === 'returned' || order.is_full_return === true)) || remainingQtySum === 0;
    if (isFullReturnClient) additionalServices = 0;

    const recalculatedTotal = recalculatedSubtotal + additionalServices;
    
    // CRITICAL FIX: Use helper to get effective VAT - calculates properly for old orders with vat_amount=0
    const { vatAmount: recalculatedVatAmount, vatableCount: vatInvoiceVatableCount, totalItems: vatInvoiceTotalItems, isCalculated: vatInvoiceIsCalculated } = getFinalVatAmount(order, itemsToDisplay);
    
    if (vatInvoiceIsCalculated) {
      console.log('📊 VAT Invoice: VAT recalculated from items (old order fix):', recalculatedVatAmount.toFixed(2));
    }
    
    const discount = parseFloat(order.discount || 0);
    const recalculatedGrandTotal = Math.max(0, recalculatedTotal + recalculatedVatAmount - discount);

    // Find customer details - use guest customer details from order if available
    const customer = customers.find(c => c.id === order.customer_id);
    
    // Check if this is a guest customer
    const isGuestCustomer = (
      order.customer_id === null || 
      !order.customer_id || 
      order.customer_id === 'guest'
    );
    
    // For guest customers, use order data; for regular customers, use customer data
    const customerName = isGuestCustomer ? 
      (order.customer_name && order.customer_name !== 'Guest Customer' ? order.customer_name : '') : 
      (customer?.name || '');
    const customerCompany = isGuestCustomer ? 
      (order.company_name && order.company_name !== 'Guest Customer' ? order.company_name : '') : 
      (customer?.company_name || '');
    const customerAddress = isGuestCustomer ? 
      (order.customer_address && order.customer_address !== 'Address not provided' ? order.customer_address : '') : 
      (customer?.address || '');
    const customerContact = isGuestCustomer ? 
      (order.customer_contact && order.customer_contact !== 'Contact not provided' ? order.customer_contact : '') : 
      (customer?.contact_number || '');

    // NOTE: Old Balance calculation removed per user request - VAT invoices now show only order total
    // let vatInvoiceOldBalance = calculateBulletproofOldBalance(order, fulfilledOrders);

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>VAT Invoice - #${order.id ? order.id.substring(0, 8).toUpperCase() : 'N/A'}</title>
        <style>
          @page { 
            margin: 0.2in; 
            size: A4;
          }
          body { 
            font-family: 'Arial', sans-serif; 
            margin: 0; 
            padding: 0;
            color: #333;
            line-height: 1.2;
            font-size: 11px;
          }
          .invoice-container {
            max-width: 100%;
            margin: 0 auto;
            background: white;
            padding: 10px;
          }
          .header {
            background-color: white;
            color: #000;
            padding: 15px;
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #d4af37;
          }
          .company-name {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 5px;
            letter-spacing: 1px;
          }
          .company-tagline {
            font-size: 12px;
            opacity: 0.9;
            margin-bottom: 10px;
          }
          .company-details {
            font-size: 10px;
            line-height: 1.4;
          }
          .invoice-title {
            background-color: white;
            border-left: 3px solid #000;
            padding: 10px;
            margin-bottom: 15px;
          }
          .invoice-title h1 {
            margin: 0;
            font-size: 20px;
            color: #000;
          }
          .invoice-meta {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
          }
          .invoice-details, .customer-details {
            width: 48%;
          }
          .section-title {
            font-size: 12px;
            font-weight: bold;
            color: #000;
            margin-bottom: 5px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .details-box {
            background: #f8fafc;
            padding: 10px;
            border-radius: 4px;
            border: 1px solid #e2e8f0;
          }
          .customer-info {
            font-size: 11px;
            line-height: 1.4;
          }
          .customer-info strong {
            display: block;
            font-size: 12px;
            color: #1e40af;
            margin-bottom: 3px;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 15px 0;
            background: white;
            box-shadow: 0 1px 2px rgba(0,0,0,0.1);
          }
          .items-table th {
            background-color: white;
            color: #000;
            padding: 8px 6px;
            text-align: left;
            font-weight: bold;
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            border-bottom: 2px solid #000;
          }
          .items-table td {
            padding: 6px 6px;
            border-bottom: 1px solid #e2e8f0;
            font-size: 10px;
            line-height: 1.2;
          }
          .item-description {
            font-weight: bold;
            color: #000;
            margin-bottom: 2px;
          }
          .item-details {
            color: #64748b;
            font-size: 9px;
          }
          .text-right {
            text-align: right;
          }
          .summary-table {
            width: 300px;
            margin-left: auto;
            margin-top: 20px;
            border-collapse: collapse;
          }
          .summary-table td {
            padding: 6px 10px;
            border-bottom: 1px solid #e2e8f0;
            font-size: 11px;
          }
          .summary-label {
            font-weight: bold;
            background: #f8fafc;
            color: #475569;
          }
          .summary-value {
            text-align: right;
            color: #1e293b;
          }
          .total-row {
            background-color: #1e40af !important;
            color: white !important;
            font-weight: bold;
            font-size: 12px;
          }
          .vat-notice {
            background-color: #fef3c7;
            border: 1px solid #f59e0b;
            padding: 10px;
            margin: 15px 0;
            border-radius: 4px;
            font-size: 10px;
            text-align: center;
          }
          .footer {
            margin-top: 20px;
            padding-top: 15px;
            border-top: 2px solid #e2e8f0;
            text-align: center;
            color: #64748b;
            font-size: 9px;
            line-height: 1.3;
          }
          @media print {
            body { 
              background: #fff !important; 
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .no-print { display: none !important; }
            @page {
              margin: 0.2in;
              size: A4;
            }
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <!-- Company Header -->
          <div class="header">
            <div class="company-name">PLUSH LIVING LTD</div>
            <div class="company-tagline">Making your house a home</div>
            <div class="company-details">
              Unit 30 Adderley Rd, Saltley, Birmingham B8 1AN<br>
              Tel: 0121 649 1673 | Email: Plushlivingltd@outlook.com<br>
              Website: Plush-livingltd.co.uk<br>
              <strong>VAT No: 493471464</strong> | <strong>Company Registration No: 16405782</strong><br>
              <br>
              <strong>BANK DETAILS FOR PAYMENT:</strong><br>
              <strong>Lloyds Bank - Plush Living LTD</strong><br>
              <strong>Sort Code: 30-54-66</strong> | <strong>Account Number: 62274460</strong>
            </div>
          </div>

          <!-- Invoice Title -->
          <div class="invoice-title">
            <h1>VAT INVOICE</h1>
          </div>

          <!-- Invoice & Customer Details -->
          <div class="invoice-meta">
            <div class="invoice-details">
              <div class="section-title">Invoice Details</div>
              <div class="details-box">
                <strong>Invoice No:</strong> ${order.vat_invoice_number || ('PLV-' + (order.id ? order.id.substring(0, 8).toUpperCase() : 'N/A'))}<br>
                <strong>Invoice Date:</strong> ${order.date ? new Date(order.date).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')}${order.edit_date ? '<br><span style="color: #f59e0b; font-style: italic; font-size: 11px;">Edited ' + new Date(order.edit_date).toLocaleDateString('en-GB') + '</span>' : ''}<br>
                <strong>Payment Terms:</strong> 30 Days Net<br>
                <strong>Due Date:</strong> ${new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString('en-GB')}
              </div>
            </div>
            
            <div class="customer-details">
              <div class="section-title">Invoice To</div>
              <div class="details-box">
                <div class="customer-info">
                  <strong>${customerName}</strong><br>
                  <strong>Customer #: ${order.customer_number || 'N/A'}</strong><br>
                  ${customerCompany ? `${customerCompany}<br>` : ''}
                  ${customerAddress ? `${customerAddress}<br>` : ''}
                  ${customerContact ? `Tel: ${customerContact}` : ''}
                </div>
              </div>
            </div>
          </div>

          <!-- Items Table -->
          <table class="items-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Stock</th>
                <th>Specification</th>
                <th>Qty</th>
                <th>Unit Price</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <!-- ═══════════════════════════════════════════════════════════════ -->
              <!-- INVOICE FORMAT RULE:                                            -->
              <!-- PARTIAL PAYMENT: Show "Assorted" with payment amount only       -->
              <!-- FULL PAYMENT:    Show complete product details and breakdown    -->
              <!-- ═══════════════════════════════════════════════════════════════ -->
              ${isPartialPayment ? `
                <!-- PARTIAL PAYMENT FORMAT -->
                <tr>
                  <td><strong>Assorted</strong></td>
                  <td>Carpets/Rugs</td>
                  <td>-</td>
                  <td>1</td>
                  <td class="amount-column">£${totalPaid.toFixed(2)}</td>
                  <td class="amount-column">£${totalPaid.toFixed(2)}</td>
                </tr>
              ` : (itemsToDisplay && itemsToDisplay.length ? itemsToDisplay.map(item => `
                <!-- FULL PAYMENT FORMAT -->
                <tr>
                  <td><strong>${item.product_name || 'Product'}</strong><br>
                  <small>${(item.color || item.storage_location || item.storageLocation) ? 
                    [item.color, item.storage_location || item.storageLocation].filter(Boolean).join(' | ') : 
                    'Product details'
                  }</small></td>
                  <td>${item.storage_location || item.storageLocation || '-'}</td>
                  <td>${(() => {
                    // For square meter products with dimensions and units
                    if (item.wide && item.length && item.wide_unit && item.length_unit) {
                      return `Size: ${item.wide}${item.wide_unit === 'feet' ? 'FT' : 'M'}x${item.length}${item.length_unit === 'feet' ? 'FT' : 'M'}`;
                    }
                    // For square meter products with dimensions but no units
                    if (item.wide && item.length) {
                      return `Size: ${item.wide}x${item.length}`;
                    }
                    // If size already contains dimensions format with units, use it
                    if (item.size && typeof item.size === 'string' && item.size.includes('x') && item.size !== 'N/A') {
                      return `Size: ${item.size}`;
                    }
                    // For square meter products, use size field
                    if (item.measurement_unit === 'per_square_meter' && item.size) {
                      return `Size: ${item.size}`;
                    }
                    // For regular products, display the size field
                    return item.size ? `Size: ${item.size}` : '';
                  })()}</td>
                  <td>${(item.display_quantity || 0).toFixed(3)} ${item.measurement_unit === 'per_square_meter' ? 'm²' : 'units'}</td>
                  <td class="amount-column">£${(parseFloat(item.unit_price || 0)).toFixed(2)}</td>
                  <td class="amount-column">£${(parseFloat(item.unit_price || 0) * parseFloat(item.display_quantity || 0)).toFixed(2)}</td>
                </tr>
              `).join('') : '')}
              
              ${!isPartialPayment && (order.fitting_charge && parseFloat(order.fitting_charge) > 0) ? `
                <tr class="service-row">
                  <td><strong>Fitting Service</strong></td>
                  <td>-</td>
                  <td>Professional installation service</td>
                  <td>1</td>
                  <td class="amount-column">£${(order.fitting_charge || 0).toFixed(2)}</td>
                  <td class="amount-column">£${(order.fitting_charge || 0).toFixed(2)}</td>
                </tr>
              ` : ''}
              
              ${!isPartialPayment && (order.cut_piece_charge && parseFloat(order.cut_piece_charge) > 0) ? `
                <tr class="service-row">
                  <td><strong>Cut Piece Service</strong></td>
                  <td>-</td>
                  <td>Professional cutting service</td>
                  <td>1</td>
                  <td class="amount-column">£${(order.cut_piece_charge || 0).toFixed(2)}</td>
                  <td class="amount-column">£${(order.cut_piece_charge || 0).toFixed(2)}</td>
                </tr>
              ` : ''}
            </tbody>
          </table>

          <!-- Totals Section -->
          <div class="totals-section">
            <table class="totals-table">
              ${isPartialPayment ? `
                <!-- PARTIAL PAYMENT TOTALS -->
                <tr class="total-row">
                  <td class="label">Amount Paid:</td>
                  <td class="amount">£${totalPaid.toFixed(2)}</td>
                </tr>
              ` : `
                <!-- FULL PAYMENT TOTALS - New Bill Format -->
                <tr>
                  <td class="label">Total Stock:</td>
                  <td class="amount">£${recalculatedSubtotal.toFixed(2)}</td>
                </tr>
                ${parseFloat(order.cut_piece_charge || 0) > 0 ? 
                  '<tr>' +
                    '<td class="label">Cutting Cost:</td>' +
                    '<td class="amount">£' + parseFloat(order.cut_piece_charge || 0).toFixed(2) + '</td>' +
                  '</tr>' : ''
                }
                ${parseFloat(order.fitting_charge || 0) > 0 ? 
                  '<tr>' +
                    '<td class="label">Fitting Cost:</td>' +
                    '<td class="amount">£' + parseFloat(order.fitting_charge || 0).toFixed(2) + '</td>' +
                  '</tr>' : ''
                }
                <tr>
                  <td class="label">Subtotal:</td>
                  <td class="amount">£${recalculatedTotal.toFixed(2)}</td>
                </tr>
                ${recalculatedVatAmount > 0 ? 
                  '<tr>' +
                    '<td class="label">VAT (20%):</td>' +
                    '<td class="amount">£' + recalculatedVatAmount.toFixed(2) + '</td>' +
                  '</tr>' : ''
                }
                ${parseFloat(order.discount || 0) > 0 ? 
                  '<tr style="color: #d32f2f;">' +
                    '<td class="label">Discount:</td>' +
                    '<td class="amount">-£' + parseFloat(order.discount || 0).toFixed(2) + '</td>' +
                  '</tr>' : ''
                }
                <tr>
                  <td class="label" style="font-weight: 600;">Total Bill:</td>
                  <td class="amount" style="font-weight: 600;">£${(recalculatedTotal + recalculatedVatAmount - parseFloat(order.discount || 0)).toFixed(2)}</td>
                </tr>
                <tr class="total-row">
                  <td class="label">Grand Total:</td>
                  <td class="amount">£${(recalculatedTotal + recalculatedVatAmount - parseFloat(order.discount || 0)).toFixed(2)}</td>
                </tr>
              `}
            </table>
          </div>

          <!-- Footer -->
          <div class="footer">
            <strong>PLUSH LIVING LTD</strong> - Making your house a home<br>
            Thank you for your business. Payment is due within 30 days of invoice date.<br>
            For any queries regarding this invoice, please contact us at Plushlivingltd@outlook.com or visit Plush-livingltd.co.uk
          </div>
        </div>
      </body>
      </html>
    `;

    // Create and open print window
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    const printWindow = window.open(url, '_blank', 'width=800,height=1000');
    if (!printWindow) {
      alert('Please allow popups to print invoices');
    } else {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };
    }
  };

  // Admin product variation management
  const addProductVariation = () => {
    const newVariation = {
      id: productVariations.length + 1,
      size: '',
      squareMeters: '',
      color: '',
      storageLocation: '',
      retail: '',
      wholesale: '',
      fitter: '',
      cost: '',
      stock: '',
      // Enhanced image and video support
      colorImage: null,  // Primary/main image (legacy)
      images: [null, null, null, null],  // Array for 4 images
      video: null  // Video file
    };
    setProductVariations([...productVariations, newVariation]);
  };

  const removeProductVariation = (id) => {
    if (productVariations.length > 1) {
      setProductVariations(productVariations.filter(variation => variation.id !== id));
    }
  };

  // Enhanced image and video upload handlers
  const updateVariationImage = (variationId, imageIndex, file) => {
    if (file) {
      // Validate image file size: 800MB max
      const fileSizeMB = file.size / (1024 * 1024);
      const maxSizeMB = 800;
      
      if (fileSizeMB > maxSizeMB) {
        alert(`❌ Image too large!\n\nFile: ${file.name}\nSize: ${fileSizeMB.toFixed(2)}MB\nMax allowed: ${maxSizeMB}MB\n\nPlease choose a smaller image.`);
        return;
      }
      
      console.log(`✅ Image size OK: ${file.name} - ${fileSizeMB.toFixed(2)}MB`);
    }
    
    setProductVariations(productVariations.map(variation => {
      if (variation.id === variationId) {
        // Ensure images array exists
        const currentImages = variation.images || [null, null, null, null];
        const newImages = [...currentImages];
        newImages[imageIndex] = file;
        return { ...variation, images: newImages };
      }
      return variation;
    }));
    
    if (file) {
      console.log(`Image ${imageIndex + 1} uploaded for variation ${variationId}:`, file.name);
    }
  };

  const updateVariationVideo = (variationId, file) => {
    if (file) {
      // Validate video file size: 2GB max
      const fileSizeMB = file.size / (1024 * 1024);
      const maxSizeMB = 2048; // 2GB
      
      if (fileSizeMB > maxSizeMB) {
        alert(`❌ Video too large!\n\nFile: ${file.name}\nSize: ${fileSizeMB.toFixed(2)}MB\nMax allowed: ${maxSizeMB}MB (2GB)\n\nPlease choose a smaller video.`);
        return;
      }
      
      console.log(`✅ Video size OK: ${file.name} - ${fileSizeMB.toFixed(2)}MB`);
    }
    
    setProductVariations(productVariations.map(variation => 
      variation.id === variationId ? { ...variation, video: file } : variation
    ));
    
    if (file) {
      console.log(`Video uploaded for variation ${variationId}:`, file.name);
    }
  };

  const removeVariationImage = (variationId, imageIndex) => {
    setProductVariations(productVariations.map(variation => {
      if (variation.id === variationId) {
        // Ensure images array exists
        const currentImages = variation.images || [null, null, null, null];
        const newImages = [...currentImages];
        newImages[imageIndex] = null;
        return { ...variation, images: newImages };
      }
      return variation;
    }));
  };

  const removeVariationVideo = (variationId) => {
    setProductVariations(productVariations.map(variation => 
      variation.id === variationId ? { ...variation, video: null } : variation
    ));
  };

  const updateProductVariation = (id, field, value) => {
    setProductVariations(productVariations.map(variation => 
      variation.id === id ? { ...variation, [field]: value } : variation
    ));
    
    // Show feedback for image uploads
    if (field === 'colorImage' && value) {
      console.log(`Image uploaded for variation ${id}:`, value.name);
      // Optional: Show toast notification or other UI feedback
    }
  };

  const resetProductVariations = () => {
    setProductVariations([
      { 
        id: 1, 
        size: '', 
        squareMeters: '', 
        color: '', 
        storageLocation: '', 
        retail: '', 
        wholesale: '', 
        fitter: '', 
        cost: '', 
        stock: '', 
        // Enhanced image and video support
        colorImage: null,  // Primary/main image (legacy)
        images: [null, null, null, null],  // Array for 4 images
        video: null  // Video file
      }
    ]);
  };

  const closeAddProductModal = () => {
    setShowAddProductModal(false);
    setIsEditMode(false);
    setEditingProductId(null);
    setCurrentMeasurementUnit('size'); // Reset to default
    resetProductVariations();
  };

  // Product statistics calculation functions
  const calculateProductStats = () => {
    if (!products || products.length === 0) {
      return {
        totalProducts: 0,
        totalStock: '0 units',
        totalSold: '0 units'
      };
    }

    const totalProducts = products.length;
    
    // Calculate total stock - separate fixed price and per-sqm
    let fixedPriceStock = 0;
    let sqmStock = 0;
    
    // For sold calculation, we could track this in a separate field in the future
    // For now, we'll simulate some sold items based on low stock products
    let fixedPriceSold = 0;
    let sqmSold = 0;

    products.forEach(product => {
      let productStock = 0;
      
      // FIXED: Handle both measurement units for stock calculation
      if (product.variations && product.variations.length > 0) {
        productStock = product.variations.reduce((total, variation) => {
          // For per_square_meter products, use square_meters_available
          if (product.measurement_unit === 'per_square_meter') {
            return total + (variation.square_meters_available || variation.square_meters || 0);
          } else {
            // For size-based products, use stock_quantity
            return total + (variation.stock_quantity || variation.quantity || 0);
          }
        }, 0);
      } else {
        // For products without variations, use main product quantity based on measurement unit
        if (product.measurement_unit === 'per_square_meter') {
          productStock = product.square_meters_available || product.square_meters || 0;
        } else {
          productStock = product.quantity || 0;
        }
      }
      
      if (product.measurement_unit === 'per_square_meter') {
        sqmStock += productStock;
        // Simulate sold: if stock is low (< 10), assume some were sold
        if (productStock < 10 && productStock > 0) {
          sqmSold += Math.floor(Math.random() * 5) + 1; // Random sold amount for demo
        }
      } else {
        fixedPriceStock += productStock;
        // Simulate sold for fixed price items
        if (productStock < 10 && productStock > 0) {
          fixedPriceSold += Math.floor(Math.random() * 3) + 1; // Random sold amount for demo
        }
      }
    });

    // Format stock display with proper decimal rounding
    let stockDisplay = '';
    if (fixedPriceStock > 0 && sqmStock > 0) {
      stockDisplay = `${Math.round(fixedPriceStock)} units, ${sqmStock.toFixed(2)} sqm`;
    } else if (fixedPriceStock > 0) {
      stockDisplay = `${Math.round(fixedPriceStock)} units`;
    } else if (sqmStock > 0) {
      stockDisplay = `${sqmStock.toFixed(2)} sqm`;
    } else {
      stockDisplay = '0 units';
    }

    // Format sold display with proper decimal rounding
    let soldDisplay = '';
    if (fixedPriceSold > 0 && sqmSold > 0) {
      soldDisplay = `${Math.round(fixedPriceSold)} units, ${sqmSold.toFixed(2)} sqm`;
    } else if (fixedPriceSold > 0) {
      soldDisplay = `${Math.round(fixedPriceSold)} units`;
    } else if (sqmSold > 0) {
      soldDisplay = `${sqmSold.toFixed(2)} sqm`;
    } else {
      soldDisplay = '0 units';
    }

    return {
      totalProducts,
      totalStock: stockDisplay,
      totalSold: soldDisplay
    };
  };

  const stats = calculateProductStats();

  // Product management functions
  const handleEditProduct = async (productId) => {
    // SPECTATOR CHECK: Block spectators from editing products
    if (isSpectateOnly('products')) {
      alert('🔒 Spectate Mode: You cannot edit products. Contact a manager.');
      return;
    }
    
    try {
      console.log('🔍 Loading full product details for editing...');
      
      // Fetch full product details with images instead of using stripped data
      let productToEdit = await loadFullProductDetails(productId);
      
      if (!productToEdit) {
        // Fallback to cached data if API fails
        const cachedProduct = products.find(p => p.id === productId);
        if (!cachedProduct) {
          alert('Product not found');
          return;
        }
        console.warn('⚠️ Using cached product data (images may not be available)');
        productToEdit = cachedProduct;
      } else {
        console.log('✅ Full product details loaded for editing:', productToEdit);
      }
      
      // Set the measurement unit for dynamic form behavior
      setCurrentMeasurementUnit(productToEdit.measurement_unit || 'size');
      
      // If product has variations, load all of them
      let variationsToLoad = [];
      
      if (productToEdit.variations && productToEdit.variations.length > 0) {
        // Load all existing variations with enhanced media support
        variationsToLoad = productToEdit.variations.map((variation, index) => {
          // Safely convert enhanced media with error handling
          let convertedImages = [null, null, null, null];
          let convertedVideo = null;
          
          try {
            // Try to load enhanced media format images (using base64 strings directly)
            if (variation.images && Array.isArray(variation.images)) {
              convertedImages = variation.images.map((img) => {
                if (!img) return null;
                // Keep as base64 string for display, not convert to File object
                return img.startsWith('data:') ? img : `data:image/jpeg;base64,${img}`;
              });
              // Pad with nulls to ensure 4 slots
              while (convertedImages.length < 4) {
                convertedImages.push(null);
              }
            }
            
            // Try to load enhanced media format video (using base64 strings directly)
            if (variation.video_base64) {
              try {
                convertedVideo = variation.video_base64.startsWith('data:') ? 
                  variation.video_base64 : 
                  `data:video/mp4;base64,${variation.video_base64}`;
              } catch (err) {
                console.warn(`Failed to convert video for variation ${index}:`, err);
                convertedVideo = null;
              }
            }
          } catch (err) {
            console.warn(`Error loading enhanced media for variation ${index}:`, err);
          }
          
          return {
            id: index + 1,
            size: variation.size || '',
            squareMeters: variation.square_meters || '',
            color: variation.color || '',
            storageLocation: variation.storage_location || '',
            retail: variation.retail_price || productToEdit.retail_price || '',
            wholesale: variation.wholesale_price || productToEdit.wholesale_price || '',
            fitter: variation.fitter_price || '',
            cost: variation.cost_price || productToEdit.cost_price || '',
            stock: variation.stock_quantity || variation.quantity || '',
            // Enhanced media support with safe loading
            images: convertedImages,
            video: convertedVideo,
            // Legacy fields for backward compatibility
            image_base64: variation.image_base64 || '',
            image_url: variation.image_url || ''
          };
        });
      } else {
        // Create single variation from main product data with enhanced media support
        let convertedImages = [null, null, null, null];
        let convertedVideo = null;
        
        try {
          // Safely convert main product enhanced media (using base64 strings directly)
          if (productToEdit.images && Array.isArray(productToEdit.images)) {
            convertedImages = productToEdit.images.map((img) => {
              if (!img) return null;
              // Keep as base64 string for display, not convert to File object
              return img.startsWith('data:') ? img : `data:image/jpeg;base64,${img}`;
            });
            // Pad with nulls to ensure 4 slots
            while (convertedImages.length < 4) {
              convertedImages.push(null);
            }
          }
          
          // Try to load main product video (using base64 strings directly)
          if (productToEdit.video_base64) {
            try {
              convertedVideo = productToEdit.video_base64.startsWith('data:') ? 
                productToEdit.video_base64 : 
                `data:video/mp4;base64,${productToEdit.video_base64}`;
            } catch (err) {
              console.warn(`Failed to convert main product video:`, err);
              convertedVideo = null;
            }
          }
        } catch (err) {
          console.warn(`Error loading main product enhanced media:`, err);
        }
        
        variationsToLoad = [{
          id: 1,
          size: productToEdit.size || '',
          squareMeters: '',
          color: productToEdit.color || '',
          storageLocation: productToEdit.storage_location || '',
          retail: productToEdit.retail_price || '',
          wholesale: productToEdit.wholesale_price || '',
          fitter: '',
          cost: productToEdit.cost_price || '',
          stock: productToEdit.quantity || '',
          // Enhanced media support with safe loading
          images: convertedImages,
          video: convertedVideo,
          // Legacy fields for backward compatibility  
          image_base64: productToEdit.image_base64 || '',
          image_url: productToEdit.image_url || (productToEdit.image_urls && productToEdit.image_urls[0]) || ''
        }];
      }
      
      console.log('Loading variations for edit:', variationsToLoad);
      
      // Set all variations in the form
      setProductVariations(variationsToLoad);
      
      // Store the product ID being edited
      setEditingProductId(productId);
      
      // Open the Add Product modal (which will now act as Edit Product modal)
      setShowAddProductModal(true);
      
      // Set form title to indicate editing
      setIsEditMode(true);
    } catch (error) {
      console.error('Error preparing product for edit:', error);
      alert('Error loading product for editing');
    }
  };

  // Bulk delete functionality
  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) {
      alert('Please select products to delete.');
      return;
    }

    const confirmDelete = window.confirm(`Are you sure you want to delete ${selectedProducts.length} selected products? This action cannot be undone.`);
    if (!confirmDelete) return;

    const token = localStorage.getItem('admin_token');
    if (!token) {
      alert('Please log in as admin first.');
      return;
    }

    try {
      console.log('Bulk deleting products:', selectedProducts);
      
      // Optimistically remove selected products from UI
      const remainingProducts = products.filter(p => !selectedProducts.includes(p.id));
      setProducts(remainingProducts);
      setSelectedProducts([]); // Clear selection

      // Delete each product from backend
      const deletePromises = selectedProducts.map(productId =>
        axios.delete(`${API}/api/products/${productId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      );

      await Promise.all(deletePromises);
      
      console.log('Bulk deletion completed');
      alert(`${selectedProducts.length} products deleted successfully!`);
      
      // Reload products to ensure consistency
      await loadProducts();
      
    } catch (error) {
      console.error('Bulk deletion error:', error);
      
      // Revert optimistic update on error
      setProducts(products);
      
      if (error.response?.status === 401) {
        alert('Authentication failed. Please log in again.');
        localStorage.removeItem('admin_token');
        setIsAdminLoggedIn(false);
        setCurrentPage('home');
      } else {
        alert('Error deleting some products. Please try again.');
        await loadProducts(); // Reload to show current state
      }
    }
  };

  // Toggle product selection
  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  // Select all products
  const selectAllProducts = (checked) => {
    if (checked) {
      setSelectedProducts(filteredProducts.map(p => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  // Filter products based on search term with safety checks
  const filteredProducts = React.useMemo(() => {
    console.log(`🔄 Filtering products: ${products?.length || 0} products, search: "${productsSearch}", updated: ${new Date(productsLastUpdated).toLocaleTimeString()}`);
    try {
      if (!products || !Array.isArray(products)) {
        return [];
      }
      
      const needle = (productsSearch || "").trim().toLowerCase();
      if (!needle) {
        return products;
      }
      
      const searchLower = needle;
      
      return products.filter(product => { /* productsSearch drives this memo; search input wired above */
        if (!product) return false;
        
        const name = (product.name || '').toLowerCase();
        const description = (product.description || '').toLowerCase();
        const category = (product.category || '').toLowerCase();
        const collection = (product.collection || '').toLowerCase();
        
        const matches = name.includes(searchLower) ||
               description.includes(searchLower) ||
               category.includes(searchLower) ||
               collection.includes(searchLower);
        
        return matches;
      });
    } catch (error) {
      console.error('Error filtering products:', error);
      return products || [];
    }
  }, [products, productsSearch, productsLastUpdated]);

  const handleDeleteProduct = async (productId) => {
    // SPECTATOR CHECK: Block spectators from deleting products
    if (isSpectateOnly('products')) {
      alert('🔒 Spectate Mode: You cannot delete products. Contact a manager.');
      return;
    }
    
    try {
      const productToDelete = products.find(p => p.id === productId);
      if (productToDelete) {
        const confirmDelete = window.confirm(`Are you sure you want to delete "${productToDelete.name}"? This action cannot be undone.`);
        if (confirmDelete) {
          
          const token = localStorage.getItem('admin_token');
          if (!token) {
            alert('Please log in as admin first.');
            return;
          }

          console.log('Deleting product:', productId);
          
          // Set loading state for this specific product
          setDeletingProductId(productId);

          // Optimistically remove from UI first for immediate feedback
          const updatedProducts = products.filter(p => p.id !== productId);
          setProducts(updatedProducts);

          try {
            const response = await axios.delete(`${API}/api/products/${productId}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            if (response.status === 200) {
              console.log('Product deleted successfully from backend');
              alert('Product deleted successfully!');
              
              // IMMEDIATE FRONTEND UPDATE: Event-driven sync - instant UI refresh after deletion
              console.log('🚀 IMMEDIATE UPDATE: Product deleted - instant frontend refresh');
              setProductsLastUpdated(Date.now());
              setLastSyncTime(Date.now());
              
              // Instant reload for immediate UI feedback (event-driven)
              await loadProducts();
              
              // Stock level updated successfully
            }
          } catch (deleteError) {
            console.error('Backend deletion failed:', deleteError);
            
            // Revert the optimistic update if backend deletion failed
            setProducts(products);
            
            if (deleteError.response?.status === 401) {
              alert('Authentication failed. Please log in again.');
              localStorage.removeItem('admin_token');
              setIsAdminLoggedIn(false);
              setCurrentPage('home');
            } else if (deleteError.response?.status === 404) {
              alert('Product not found. It may have already been deleted.');
              // Keep the product removed from UI since it doesn't exist
              await loadProducts();
            } else {
              alert('Error deleting product: ' + (deleteError.response?.data?.detail || deleteError.message));
            }
          } finally {
            // Clear loading state
            setDeletingProductId(null);
          }
        }
      }
    } catch (error) {
      console.error('Error in delete function:', error);
      alert('Error deleting product: ' + error.message);
      setDeletingProductId(null);
    }
  };

  // NEW: Handle multiple image uploads with format validation
  const handleMultipleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    // Validate file types and sizes
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const isValid = validTypes.includes(file.type.toLowerCase());
      if (!isValid) {
        alert(`File "${file.name}" is not supported. Please upload JPEG or PNG images only.`);
        return false;
      }
      
      // Validate file size: 800MB for images
      const fileSizeMB = file.size / (1024 * 1024);
      const maxSizeMB = 800;
      
      if (fileSizeMB > maxSizeMB) {
        alert(`❌ Image too large!\n\nFile: ${file.name}\nSize: ${fileSizeMB.toFixed(2)}MB\nMax allowed: ${maxSizeMB}MB\n\nPlease choose a smaller image.`);
        return false;
      }
      
      console.log(`✅ Image size OK: ${file.name} - ${fileSizeMB.toFixed(2)}MB`);
      return true;
    });
    
    if (validFiles.length === 0) {
      return; // No valid files to process
    }
    
    const imagePromises = validFiles.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve({
            file: file,
            preview: e.target.result,
            base64: e.target.result.split(',')[1] // Remove data URL prefix
          });
        };
        reader.readAsDataURL(file);
      });
    });
    
    const newImages = await Promise.all(imagePromises);
    setUploadedImages(prev => [...prev, ...newImages]);
  };

  // NEW: Remove uploaded image
  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  // Handle Add/Edit Product Form Submission
  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    console.log('=== ADD/EDIT PRODUCT FORM SUBMISSION ===');
    console.log('Edit mode:', isEditMode);
    console.log('Editing product ID:', editingProductId);
    
    try {
      // Get form data
      const formData = new FormData(e.target);
      
      console.log('FormData entries:');
      for (let [key, value] of formData.entries()) {
        console.log(`  ${key}: ${value}`);
      }
      
      console.log('Product variations:', productVariations);
      
      // Helper function to convert file to base64
      const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          if (!file) {
            resolve(null);
            return;
          }
          
          const reader = new FileReader();
          reader.onload = () => {
            // Remove the data URL prefix to get just the base64 string
            const base64 = reader.result.split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      };

      // Helper function to create preview URLs from base64 strings for editing
      const createPreviewFromBase64 = (base64String) => {
        if (!base64String) return null;
        
        try {
          // Ensure proper data URL format
          if (!base64String.startsWith('data:')) {
            return `data:image/jpeg;base64,${base64String}`;
          }
          return base64String;
        } catch (error) {
          console.error('Error creating preview from base64:', error);
          return null;
        }
      };
      
      // Get the first variation for main product data
      const firstVariation = productVariations[0] || {};
      
      // Convert main product image to base64 if it exists (legacy support)
      let mainImageBase64 = null;
      if (firstVariation.colorImage) {
        mainImageBase64 = await fileToBase64(firstVariation.colorImage);
      } else if (firstVariation.image_base64) {
        mainImageBase64 = firstVariation.image_base64;
      }
      
      // Process main product images (from first variation)
      const mainProductImages = [];
      if (firstVariation.images && Array.isArray(firstVariation.images)) {
        for (const imageFile of firstVariation.images) {
          if (imageFile) {
            try {
              const base64Image = await fileToBase64(imageFile);
              mainProductImages.push(base64Image);
            } catch (error) {
              console.error('Error processing main product image:', error);
            }
          }
        }
      }
      
      // Process main product video (from first variation)
      let mainProductVideo = null;
      if (firstVariation.video) {
        try {
          mainProductVideo = await fileToBase64(firstVariation.video);
        } catch (error) {
          console.error('Error processing main product video:', error);
        }
      }
      
      // Process variations and convert images/video to base64
      const processedVariations = await Promise.all(
        productVariations.filter(v => v.color || v.size || v.squareMeters || v.retail).map(async (variation) => {
          // Process main/legacy image
          let imageBase64 = null;
          if (variation.colorImage) {
            imageBase64 = await fileToBase64(variation.colorImage);
          } else if (variation.image_base64) {
            imageBase64 = variation.image_base64;
          }
          
          // Process multiple images (up to 4)
          const processedImages = [];
          if (variation.images && Array.isArray(variation.images)) {
            for (const imageFile of variation.images) {
              if (imageFile) {
                try {
                  const base64Image = await fileToBase64(imageFile);
                  processedImages.push(base64Image);
                } catch (error) {
                  console.error('Error processing image:', error);
                }
              }
            }
          }
          
          // Process video
          let videoBase64 = null;
          if (variation.video) {
            try {
              videoBase64 = await fileToBase64(variation.video);
              console.log(`Video processed for variation: ${variation.video.name}`);
            } catch (error) {
              console.error('Error processing video:', error);
              alert(`Error processing video ${variation.video.name}: ${error.message}`);
            }
          }
          
          return {
            color: variation.color || '',
            size: variation.size || '',
            storage_location: variation.storageLocation || '',
            square_meters: parseFloat(variation.squareMeters) || null,
            retail_price: parseFloat(variation.retail) || 0,
            wholesale_price: parseFloat(variation.wholesale) || 0,
            fitter_price: parseFloat(variation.fitter) || 0,
            cost_price: parseFloat(variation.cost) || 0,
            stock_quantity: parseInt(variation.stock) || 0,
            square_meters_available: parseFloat(variation.squareMeters) || null, // For sqm products, this is the same as square_meters
            quantity: parseInt(variation.stock) || 0,
            // Enhanced media support
            image_base64: imageBase64, // Primary/main image (legacy)
            images: processedImages, // Array of up to 4 images
            video_base64: videoBase64 // Video file
          };
        })
      );
      
      const productData = {
        name: formData.get('productName'),
        description: formData.get('description') || '',
        design: formData.get('design') || '',
        collection: formData.get('collection') || 'General',
        category: formData.get('category'),
        retail_price: parseFloat(firstVariation.retail) || 0,
        wholesale_price: parseFloat(firstVariation.wholesale) || 0,
        cost_price: parseFloat(firstVariation.cost) || 0,
        quantity: parseInt(firstVariation.stock) || 0,
        color: firstVariation.color || '',
        size: firstVariation.size || '',
        measurement_unit: formData.get('measurementUnit') || 'size',
        // Enhanced media support
        image_base64: mainImageBase64, // Primary/main image (legacy)
        images: mainProductImages, // Array of up to 4 images
        video_base64: mainProductVideo, // Video file
        variations: processedVariations
      };

      console.log('Final product data to send:', productData);

      // Validate required fields
      if (!productData.name || !productData.name.trim()) {
        alert('Please enter a product name');
        return;
      }

      if (!productData.category) {
        alert('Please select a category');
        return;
      }

      if (!productData.retail_price || productData.retail_price <= 0) {
        alert('Please enter a valid retail price in the first variation');
        return;
      }

      console.log('Validation passed, sending to backend...');
      console.log('API endpoint:', `${API}/api/products${isEditMode ? `/${editingProductId}` : ''}`);
      console.log('HTTP method:', isEditMode ? 'PUT' : 'POST');
      console.log('Admin token:', localStorage.getItem('admin_token') ? 'Present' : 'Missing');

      let response;
      
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      if (isEditMode && editingProductId) {
        // Update existing product
        response = await axios.put(`${API}/api/products/${editingProductId}`, productData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      } else {
        // Create new product
        response = await axios.post(`${API}/api/products`, productData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }

      console.log('Response received:', response);

      if (response.data) {
        console.log('Product saved successfully:', response.data);
        
        // CRITICAL FIX: Update products state IMMEDIATELY (optimistic update)
        if (isEditMode && editingProductId) {
          // Update existing product in state instantly
          setProducts(prevProducts => 
            prevProducts.map(p => p.id === editingProductId ? response.data : p)
          );
          console.log('✅ Optimistically updated product in state');
        } else {
          // Add new product to state instantly
          setProducts(prevProducts => [...prevProducts, response.data]);
          console.log('✅ Optimistically added new product to state');
        }
        
        // Then reload from backend to ensure consistency
        console.log('🚀 IMMEDIATE UPDATE: Reloading products from backend...');
        
        // Instant reload for both admin and public views (event-driven)
        await Promise.all([
          loadProducts(), // Admin view
          loadProductsForPublic() // Public view - ensures images show immediately  
        ]);
        
        // Update sync timestamps after successful reload
        setProductsLastUpdated(Date.now());
        setLastSyncTime(Date.now());
        
        // NOW close modal after products are reloaded
        closeAddProductModal();
        
        // Show success message AFTER reload completes
        alert(`Product ${isEditMode ? 'updated' : 'added'} successfully!`);
      }
    } catch (error) {
      console.error('=== ADD/EDIT PRODUCT ERROR ===');
      console.error('Full error object:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      if (error.response?.status === 401) {
        alert('Authentication failed. Please log in again.');
        localStorage.removeItem('admin_token');
        setIsAdminLoggedIn(false);
        setCurrentPage('home');
      } else {
        console.error('Product Save Error Details:', error.response || error);
        const errorMessage = error.response?.data?.detail || 
                           error.response?.data?.message || 
                           error.message || 
                           'Unknown error occurred';
        console.error('Processed error message:', errorMessage);
        alert(`Error ${isEditMode ? 'updating' : 'adding'} product: ` + errorMessage);
      }
    }
  };

  // Handle Add Gallery Item Form Submission
  const handleAddGalleryItem = async (e) => {
    e.preventDefault();
    
    try {
      const formData = new FormData(e.target);
      
      // Get form data
      const galleryData = {
        title: formData.get('title'),
        description: formData.get('description') || '',
        type: formData.get('itemType'),
        category: formData.get('category'),
        location: formData.get('location') || '',
        date: new Date().toISOString().split('T')[0]
      };

      // Handle file upload
      const mediaFile = formData.get('mediaFile');
      if (mediaFile) {
        // Validate file size: 800MB for images, 2GB for videos
        const fileSizeMB = mediaFile.size / (1024 * 1024);
        const isImage = mediaFile.type.startsWith('image/');
        const isVideo = mediaFile.type.startsWith('video/');
        
        const maxSizeMB = isVideo ? 2048 : 800; // 2GB for video, 800MB for image
        
        if (fileSizeMB > maxSizeMB) {
          alert(`❌ File too large!\n\nFile size: ${fileSizeMB.toFixed(2)}MB\nMax allowed: ${maxSizeMB}MB\n\nPlease choose a smaller file.`);
          return;
        }
        
        console.log(`✅ File size OK: ${fileSizeMB.toFixed(2)}MB (limit: ${maxSizeMB}MB)`);
        
        // Convert file to base64 for storage
        const fileToBase64 = (file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64 = reader.result.split(',')[1];
              resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        };

        const base64Data = await fileToBase64(mediaFile);
        galleryData.media_base64 = base64Data;
        galleryData.filename = mediaFile.name;
        galleryData.file_type = mediaFile.type;
      }

      console.log('Gallery item data:', galleryData);

      // For now, add to local state (later connect to backend)
      const newGalleryItem = {
        id: Date.now(),
        ...galleryData,
        url: galleryData.media_base64 ? `data:${galleryData.file_type};base64,${galleryData.media_base64}` : 'data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'400\' height=\'300\'><rect width=\'400\' height=\'300\' fill=\'%23f3f4f6\'/><text x=\'50%\' y=\'50%\' dominant-baseline=\'middle\' text-anchor=\'middle\' fill=\'%23999\' font-size=\'18\'>No Image</text></svg>'
      };

      setGalleryItems(prev => [...prev, newGalleryItem]);
      


      alert('Gallery item added successfully!');
      setShowAddGalleryModal(false);
      
    } catch (error) {
      console.error('Error adding gallery item:', error);
      alert('Error adding gallery item: ' + error.message);
    }
  };

  // Handle Add Customer Form Submission
  const handleAddCustomer = async (e) => {
    e.preventDefault();
    if (isSavingCustomer) return;
    setIsSavingCustomer(true);
    try {
      const payload = {
        name: newCustomer.name,
        address: newCustomer.address,
        company_name: newCustomer.company_name || "",
        contact_number: newCustomer.contact_number || "",
        email: newCustomer.email || undefined,
      };
      const response = await axios.post(`${API}/api/admin-portal/customers`, payload, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      if (response.status === 200) {
        alert('Customer added successfully!');
        setShowAddCustomerModal(false);
        setNewCustomer({
          name: '',
          company_name: '',
          address: '',
          contact_number: '',
          customer_type: 'wholesale'
        });
        await loadCustomers(); // refresh list
      }
    } catch (error) {
      console.error('Error adding customer:', error);
      alert('Error adding customer: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSavingCustomer(false);
    }
  };

  const closeAddCustomerModal = () => {
    setShowAddCustomerModal(false);
    setNewCustomer({
      name: '',
      company_name: '',
      address: '',
      contact_number: '',
      customer_type: 'wholesale'
    });
  };

  // Load customers for order creation
  const loadCustomers = async () => {
    try {
      // Use either admin_token or dashboard_token (for Zobayr accessing via boss mode)
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      if (!token) {
        console.warn('No auth token for loading customers');
        return;
      }
      const response = await axios.get(`${API}/api/admin-portal/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  // Category Management Functions
  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const response = await axios.get(`${API}/api/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const categoriesData = response.data.categories.map((name, index) => ({
        id: `cat_${index}`,
        name: name
      }));
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleAddCategory = async (categoryName) => {
    if (!categoryName.trim()) {
      alert('Please enter a category name');
      return;
    }

    try {
      await axios.post(`${API}/api/categories`, {
        name: categoryName.trim()
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      alert(`Category "${categoryName}" added successfully!`);
      await loadCategories(); // Refresh categories list
    } catch (error) {
      console.error('Error adding category:', error);
      alert(error.response?.data?.detail || 'Failed to add category');
    }
  };

  const handleDeleteCategory = async (categoryName) => {
    if (!categoryName) {
      alert('Please select a category to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the category "${categoryName}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/api/categories/${encodeURIComponent(categoryName)}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      alert(`Category "${categoryName}" deleted successfully!`);
      await loadCategories(); // Refresh categories list
    } catch (error) {
      console.error('Error deleting category:', error);
      alert(error.response?.data?.detail || 'Failed to delete category');
    }
  };


  // Customer modals state and handlers
  const [showCustomerViewModal, setShowCustomerViewModal] = useState(false);
  const [showCustomerEditModal, setShowCustomerEditModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowCustomerViewModal(true);
  };

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowCustomerEditModal(true);
  };

  const handleDeleteCustomer = async (customerId) => {
    try {
      const ok = window.confirm('Delete this customer?');
      if (!ok) return;
      await axios.delete(`${API}/api/admin-portal/customers/${customerId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      await loadCustomers();
      alert('Customer deleted');
    } catch (e) {
      console.error('Error deleting customer:', e);
      alert('Error deleting customer: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleSaveCustomer = async (updated) => {
    try {
      const payload = {
        name: updated.name,
        address: updated.address,
        company_name: updated.company_name || '',
        contact_number: updated.contact_number || '',
        email: updated.email || undefined,
      };
      await axios.put(`${API}/api/admin-portal/customers/${updated.id}`, payload, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      await loadCustomers();
      setShowCustomerEditModal(false);
      setSelectedCustomer(null);
      alert('Customer updated');
    } catch (e) {
      console.error('Error updating customer:', e);
      alert('Error updating customer: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Search helpers
  const customersFiltered = React.useMemo(() => {
    const needle = (deferredCustomersSearchInput || '').toLowerCase().trim();
    if (!needle) return customers;
    
    // Check if input is purely numeric (PLU number like "001", "002")
    if (/^\d+$/.test(needle)) {
      // Exact PLU match: check if customer's PLU ends with the entered number
      const allowedPLU = `plu-${needle}`;
      return customers.filter(c => {
        if (!c) return false;
        const customerNumber = (c.customer_number || '').toLowerCase();
        return customerNumber === allowedPLU;
      });
    }
    
    // Otherwise, do text search on name, company, email, phone
    return customers.filter(c => {
      if (!c) return false;
      const name = (c.name || '').toLowerCase();
      const company = (c.company_name || '').toLowerCase();
      const email = (c.email || '').toLowerCase();
      const phone = (c.contact_number || c.phone || '').toLowerCase();
      return name.includes(needle) || company.includes(needle) || email.includes(needle) || phone.includes(needle);
    });
  }, [customers, deferredCustomersSearchInput]);

  const ordersFiltered = React.useMemo(() => {
    let filtered = orders;
    
    // Apply search filter
    const needle = (ordersSearch || '').toLowerCase().trim();
    if (needle) {
      filtered = filtered.filter(o => {
        if (!o) return false;
        const oid = (o.id || '').toLowerCase();
        const cid = (o.customer_id || '').toLowerCase();
        const status = (o.status || '').toLowerCase();
        return oid.includes(needle) || cid.includes(needle) || status.includes(needle);
      });
    }
    
    // Apply payment status filter
    if (paymentStatusFilter) {
      filtered = filtered.filter(o => {
        if (!o) return false;
        const paymentMethod = (o.payment_method || '').toLowerCase();
        const grandTotal = parseFloat(o.grand_total || 0);
        const paidAmount = parseFloat(o.credit_paid_amount || 0);
        
        if (paymentStatusFilter === 'credit') {
          return paymentMethod === 'credit';
        } else if (paymentStatusFilter === 'paid') {
          return paymentMethod !== 'credit' || (paymentMethod === 'credit' && paidAmount >= grandTotal);
        }
        return true;
      });
    }
    
    // Apply sorting
    if (orderIdSort) {
      filtered = [...filtered].sort((a, b) => {
        const idA = (a.id || '').toLowerCase();
        const idB = (b.id || '').toLowerCase();
        
        if (orderIdSort === 'asc') {
          return idA.localeCompare(idB);
        } else if (orderIdSort === 'desc') {
          return idB.localeCompare(idA);
        }
        return 0;
      });
    }
    
    return filtered;
  }, [orders, ordersSearch, paymentStatusFilter, orderIdSort]);

  const fulfilledFiltered = React.useMemo(() => {
    const startFilter = performance.now();
    let filtered = fulfilledOrders;
    
    // Apply search filter - NOW INCLUDES CUSTOMER NUMBER AND NAME
    // Using deferredFulfilledSearchInput for performance - keeps input responsive
    const needle = (deferredFulfilledSearchInput || '').toLowerCase().trim();
    if (needle) {
      // Check if input is purely numeric (PLU number like "001", "002")
      if (/^\d+$/.test(needle)) {
        // Exact PLU match
        const allowedPLU = `plu-${needle}`;
        filtered = filtered.filter(o => {
          if (!o) return false;
          const customerNumber = (o.customer_number || '').toLowerCase();
          return customerNumber === allowedPLU;
        });
      } else {
        // Text search on name, company, order ID
        filtered = filtered.filter(o => {
          if (!o) return false;
          const customerName = (o.customer_name || '').toLowerCase();
          if (customerName.includes(needle)) return true;
          
          const companyName = (o.company_name || '').toLowerCase();
          if (companyName.includes(needle)) return true;
          
          const oid = (o.id || '').toLowerCase();
          if (oid.includes(needle)) return true;
          
          return false;
        });
      }
    }
    
    console.log(`⏱️ Filter took ${(performance.now() - startFilter).toFixed(2)}ms for ${fulfilledOrders.length} orders, resulted in ${filtered.length} matches`);
    
    // Apply payment status filter (additional filtering)
    if (fulfilledPaymentFilter) {
      filtered = filtered.filter(o => {
        if (!o) return false;
        const paymentMethod = (o.payment_method || '').toLowerCase();
        const grandTotal = parseFloat(o.grand_total || 0);
        const paidAmount = parseFloat(o.credit_paid_amount || 0);
        const isFullyPaid = paymentMethod === 'credit' && (paidAmount >= grandTotal || (o.payment_status||'').toLowerCase() === 'fully_paid');
        
        if (fulfilledPaymentFilter === 'credit') {
          return paymentMethod === 'credit' && !isFullyPaid; // Only unpaid credit orders
        } else if (fulfilledPaymentFilter === 'paid') {
          return paymentMethod !== 'credit' || isFullyPaid; // Non-credit or fully paid credit orders
        }
        return true;
      });
    }
    
    // Apply Order ID sort if specified, otherwise use default credit-first sorting
    if (fulfilledOrderIdSort) {
      filtered = [...filtered].sort((a, b) => {
        const idA = (a.id || '').toLowerCase();
        const idB = (b.id || '').toLowerCase();
        
        // First sort by payment status (credit first within alphabetical groups)
        const aIsCredit = (a.payment_method||'').toLowerCase() === 'credit';
        const bIsCredit = (b.payment_method||'').toLowerCase() === 'credit';
        
        const aGrandTotal = parseFloat(a.grand_total || 0);
        const aPaidAmount = parseFloat(a.credit_paid_amount || 0);
        const aIsFullyPaid = aIsCredit && (aPaidAmount >= aGrandTotal || (a.payment_status||'').toLowerCase() === 'fully_paid');
        
        const bGrandTotal = parseFloat(b.grand_total || 0);
        const bPaidAmount = parseFloat(b.credit_paid_amount || 0);
        const bIsFullyPaid = bIsCredit && (bPaidAmount >= bGrandTotal || (b.payment_status||'').toLowerCase() === 'fully_paid');
        
        const aIsCreditNotPaid = aIsCredit && !aIsFullyPaid;
        const bIsCreditNotPaid = bIsCredit && !bIsFullyPaid;
        
        // Sort by product/customer name to group similar orders together (e.g., all "Alum Rock Carpet" in one section)
        const aName = (a.customer_name || a.items?.[0]?.product_name || '').toLowerCase();
        const bName = (b.customer_name || b.items?.[0]?.product_name || '').toLowerCase();
        const orderIdComparison = fulfilledOrderIdSort === 'asc' ? 
          aName.localeCompare(bName) : 
          bName.localeCompare(aName);
        
        if (orderIdComparison === 0) {
          // Same order ID, prioritize credit orders
          if (aIsCreditNotPaid && !bIsCreditNotPaid) return -1;
          if (!aIsCreditNotPaid && bIsCreditNotPaid) return 1;
          return 0;
        }
        
        return orderIdComparison;
      });
    } else {
      // Default sorting: Sort by date (newest first) so new fulfilled orders appear at top
      filtered = [...filtered].sort((a, b) => {
        const aDate = new Date(a.date || 0);
        const bDate = new Date(b.date || 0);
        return bDate - aDate; // Newest first
      });
    }
    
    return filtered;
  }, [fulfilledOrders, deferredFulfilledSearchInput, fulfilledPaymentFilter, fulfilledOrderIdSort]);

  const invoicesFiltered = React.useMemo(() => {
    const needle = (deferredInvoicesSearchInput || '').toLowerCase().trim();
    if (!needle) return invoices;
    
    // Check if input is purely numeric (PLU number like "001", "002")
    if (/^\d+$/.test(needle)) {
      // Exact PLU match
      const allowedPLU = `plu-${needle}`;
      return invoices.filter(inv => {
        if (!inv) return false;
        const customerNumber = (inv.customer_number || '').toLowerCase();
        return customerNumber === allowedPLU;
      });
    }
    
    // Text search on invoice number, order ID, customer name
    return invoices.filter(inv => {
      if (!inv) return false;
      const inum = (inv.invoice_number || '').toLowerCase();
      const oid = (inv.order_id || '').toLowerCase();
      const cname = (inv.customer_name || '').toLowerCase();
      return inum.includes(needle) || oid.includes(needle) || cname.includes(needle);
    });
  }, [invoices, deferredInvoicesSearchInput]);

  // Show all invoices in accounting table - same as Invoices page
  const displayedInvoices = invoices; // Use invoices array to match the Invoices page

  const handleDownloadInvoicesPDF = async () => {
    if (!invoiceDownloadFromDate || !invoiceDownloadToDate) {
      alert('Please select both from and to dates for invoice download');
      return;
    }

    // Show loading message
    const loadingAlert = document.createElement('div');
    loadingAlert.innerHTML = '🔄 Filtering invoices, please wait...';
    loadingAlert.style.position = 'fixed';
    loadingAlert.style.top = '20px';
    loadingAlert.style.right = '20px';
    loadingAlert.style.background = '#007bff';
    loadingAlert.style.color = 'white';
    loadingAlert.style.padding = '15px';
    loadingAlert.style.borderRadius = '5px';
    loadingAlert.style.zIndex = '10000';
    document.body.appendChild(loadingAlert);

    try {
      const from = new Date(invoiceDownloadFromDate + 'T00:00:00');
      const to = new Date(invoiceDownloadToDate + 'T23:59:59');
      
      console.log(`🔍 Invoice filtering: ${from.toLocaleDateString()} to ${to.toLocaleDateString()}`);
      
      // Use invoices array as source (same as accounting table and Invoices page)
      const sourceData = invoices;
      console.log(`📋 Using invoices array as source (${sourceData.length} invoices)`);
      
      if (!sourceData || sourceData.length === 0) {
        alert('No invoices available for download');
        return;
      }
      
      // Filter invoices by date range
      const filteredInvoices = sourceData.filter(invoice => {
        const invoiceDate = new Date(invoice.date || invoice.invoice_date);
        
        if (isNaN(invoiceDate.getTime())) {
          console.log(`⚠️ Invoice ${invoice.id} has invalid date, skipping`);
          return false;
        }
        
        const isInRange = invoiceDate >= from && invoiceDate <= to;
        
        console.log(`📅 Invoice ${invoice.id}: ${invoiceDate.toLocaleDateString()} (${isInRange ? 'INCLUDED' : 'EXCLUDED'})`);
        
        return isInRange;
      });

      console.log(`✅ Filtered invoices: ${filteredInvoices.length}`);
      
      if (filteredInvoices.length === 0) {
        const allDates = sourceData
          .map(invoice => new Date(invoice.date || invoice.invoice_date).toLocaleDateString())
          .filter((date, index, arr) => arr.indexOf(date) === index)
          .sort();
        
        alert(`No invoices found in date range ${invoiceDownloadFromDate} to ${invoiceDownloadToDate}.\n\nAvailable dates: ${allDates.join(', ')}`);
        return;
      }

      // Generate full VAT invoices for filtered orders
      const invoicesHtml = filteredInvoices.map(invoice => {
        const invoiceDate = invoice.date || invoice.invoice_date || invoice.created_at;
        const customerName = invoice.customer_name || invoice.customer || 'N/A';
        const paymentMethod = invoice.payment_method || 'N/A';
        const invoiceId = invoice.id || `PLV-${new Date(invoiceDate).getFullYear()}${String(new Date(invoiceDate).getMonth() + 1).padStart(2, '0')}${String(new Date(invoiceDate).getDate()).padStart(2, '0')}`;
        
        // USE STORED VALUES FROM DATABASE - DO NOT RECALCULATE!
        // These values are already correct when the invoice was created
        const subtotal = parseFloat(invoice.subtotal || 0);
        const vatAmount = parseFloat(invoice.vat_amount || 0);
        const grandTotal = parseFloat(invoice.total_amount || invoice.grand_total || invoice.total || 0);

        return `
          <div style="page-break-after: always; margin-bottom: 50px;">
            <!-- Company Header -->
            <div style="background: white; color: #000; padding: 30px; text-align: center; margin-bottom: 30px; border-bottom: 2px solid #d4af37;">
              <h1 style="margin: 0; font-size: 28px; letter-spacing: 2px;">PLUSH LIVING LTD</h1>
              <p style="margin: 5px 0; font-size: 14px; color: #d4af37; font-style: italic;">Making your house a home</p>
              <p style="margin: 5px 0; font-size: 12px;">Unit 30 Adderley Rd, Saltley, Birmingham B8 1AN</p>
              <p style="margin: 5px 0; font-size: 12px;">Tel: 0121 840 1673 | Email: Plushlivingltd@outlook.com</p>
              <p style="margin: 5px 0; font-size: 12px;">Website: Plush-livingltd.co.uk</p>
              <p style="margin: 5px 0; font-size: 12px;">VAT No: 493471464 | Company Registration No: 16405762</p>
              <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #d4af37;">
                <p style="margin: 0; font-size: 12px; font-weight: bold;">BANK DETAILS FOR PAYMENT:</p>
                <p style="margin: 0; font-size: 12px;">Lloyds Bank - Plush Living LTD</p>
                <p style="margin: 0; font-size: 12px;">Sort Code: 30-54-66 | Account Number: 62274460</p>
              </div>
            </div>

            <!-- Invoice Header -->
            <div style="display: flex; justify-content: space-between; margin-bottom: 30px;">
              <div>
                <h2 style="color: #000; margin: 0 0 20px 0; font-size: 32px;">VAT INVOICE</h2>
              </div>
            </div>

            <!-- Invoice Details and Customer Info -->
            <div style="display: flex; justify-content: space-between; margin-bottom: 30px;">
              <div style="flex: 1; margin-right: 50px;">
                <h3 style="color: #000; margin-bottom: 15px; font-size: 18px;">INVOICE DETAILS</h3>
                <p><strong>Invoice No:</strong> ${invoiceId}</p>
                <p><strong>Invoice Date:</strong> ${invoiceDate ? new Date(invoiceDate).toLocaleDateString('en-GB') : 'N/A'}</p>
                <p><strong>Payment Terms:</strong> 30 Days Net</p>
                <p><strong>Due Date:</strong> ${invoiceDate ? new Date(new Date(invoiceDate).getTime() + 30*24*60*60*1000).toLocaleDateString('en-GB') : 'N/A'}</p>
              </div>
              
              <div style="flex: 1;">
                <h3 style="color: #000; margin-bottom: 15px; font-size: 18px;">INVOICE TO</h3>
                <div style="background: #f5f5f5; padding: 15px; border-radius: 5px;">
                  <p style="margin: 0; font-weight: bold;">${customerName}</p>
                  <p style="margin: 5px 0; font-weight: bold;">Customer #: ${invoice.customer_number || 'N/A'}</p>
                  ${invoice.customer_address ? `<p style="margin: 5px 0;">${invoice.customer_address}</p>` : ''}
                  ${invoice.customer_phone ? `<p style="margin: 5px 0;">Tel: ${invoice.customer_phone}</p>` : ''}
                </div>
              </div>
            </div>

            <!-- Items Table -->
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
              <thead>
                <tr style="background: #3f51b5; color: white;">
                  <th style="padding: 12px; text-align: left; border: none;">DESCRIPTION</th>
                  <th style="padding: 12px; text-align: left; border: none;">SPECIFICATION</th>
                  <th style="padding: 12px; text-align: center; border: none;">QTY</th>
                  <th style="padding: 12px; text-align: center; border: none;">UNIT PRICE</th>
                  <th style="padding: 12px; text-align: right; border: none;">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                ${(invoice.items || []).map(item => `
                  <tr>
                    <td style="padding: 12px; border-bottom: 1px solid #eee;">${item.product_name || 'Product'}</td>
                    <td style="padding: 12px; border-bottom: 1px solid #eee;">${item.size || item.specification || ''}</td>
                    <td style="padding: 12px; text-align: center; border-bottom: 1px solid #eee;">${parseFloat(item.quantity || 0).toFixed(2)} ${item.unit || (item.measurement_unit === 'per_square_meter' ? 'm²' : 'units')}</td>
                    <td style="padding: 12px; text-align: center; border-bottom: 1px solid #eee;">£${parseFloat(item.unit_price || 0).toFixed(2)}</td>
                    <td style="padding: 12px; text-align: right; border-bottom: 1px solid #eee;">£${parseFloat(item.total_price || item.unit_price * item.quantity || 0).toFixed(2)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>

            <!-- Totals -->
            <div style="display: flex; justify-content: flex-end; margin-bottom: 30px;">
              <div style="width: 300px;">
                <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee;">
                  <span>Subtotal:</span>
                  <span>£${subtotal.toFixed(2)}</span>
                </div>
                <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee;">
                  <span>VAT (20%):</span>
                  <span>£${vatAmount.toFixed(2)}</span>
                </div>
                <div style="display: flex; justify-content: space-between; padding: 12px 0; font-weight: bold; font-size: 18px; background: #3f51b5; color: white; padding: 12px;">
                  <span>Grand Total:</span>
                  <span>£${grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div style="text-align: center; color: #666; font-size: 12px; margin-top: 40px;">
              <p><strong>PLUSH LIVING LTD</strong> - Making your house a home</p>
              <p>Thank you for your business. Payment is due within 30 days of invoice date.</p>
              <p>For any queries regarding this invoice, please contact us at Plushlivingltd@outlook.com or visit Plush-livingltd.co.uk</p>
            </div>
          </div>
        `;
      }).join('');

      const fullInvoiceHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>VAT Invoices - ${invoiceDownloadFromDate} to ${invoiceDownloadToDate}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
              @media print {
                body { margin: 0; }
                .page-break { page-break-after: always; }
              }
            </style>
          </head>
          <body>
            ${invoicesHtml}
          </body>
        </html>
      `;

      // Open full VAT invoices in new window
      const reportWindow = window.open('', '_blank');
      reportWindow.document.write(fullInvoiceHtml);
      reportWindow.document.close();
      
      // Close the modal and remove loading message
      setShowInvoiceDownloadModal(false);
      document.body.removeChild(loadingAlert);
      
    } catch (error) {
      console.error('Error downloading invoices:', error);
      alert('Error generating invoice PDF');
      document.body.removeChild(loadingAlert);
    }
  };

  const calculateBusinessMetrics = (orders, customers, products) => {
    const metrics = {
      // Sales Analysis
      totalRevenue: 0,
      creditSales: 0,
      cashSales: 0,
      bankSales: 0,
      
      // Cost Analysis  
      totalCost: 0,
      grossProfit: 0,
      netProfit: 0,
      
      // Credit Analysis
      outstandingCredit: 0,
      creditCustomers: 0,
      
      // Product Analysis
      productsSold: {},
      fastestSelling: [],
      slowestSelling: [],
      
      // Stock Analysis by Measurement Unit
      currentStockValue: 0,
      totalStockQuantity: 0,
      
      stockBySize: {
        totalValue: 0,
        totalQuantity: 0,
        productCount: 0
      },
      stockBySquareMeter: {
        totalValue: 0,
        totalQuantity: 0,
        productCount: 0
      },
      
      // Order Analysis
      totalOrders: orders.length,
      averageOrderValue: 0,
      
      // Time period
      periodStart: null,
      periodEnd: null
    };

    console.log('🔍 Business Analysis - Starting calculations with:', {
      orders: orders.length,
      customers: customers.length, 
      products: products.length
    });

    // Create product and variation lookup for accurate cost calculation
    const productLookup = {};
    const variationLookup = {};
    
    products.forEach(product => {
      productLookup[product.id] = product;
      if (product.variations && Array.isArray(product.variations)) {
        product.variations.forEach(variation => {
          variationLookup[variation.id] = {
            product_id: product.id,
            variation: variation,
            product: product
          };
        });
      }
    });

    // CALCULATION 1: USE EXACT NUMBERS FROM ADMIN INTERFACE (same as fulfilled orders table)
    console.log('💰 Using EXACT payment data from admin interface for', orders.length, 'orders');
    
    orders.forEach((order, index) => {
      // Use SAME logic as admin interface (lines 7843-7855)
      const paymentMethod = (order.payment_method||'').toLowerCase();
      const isCredit = paymentMethod === 'credit';
      const isCash = paymentMethod === 'cash';  
      const isBank = paymentMethod === 'bank';
      const grandTotal = parseFloat(order.grand_total||0)||0;
      
      // CRITICAL FIX: Always use ALL payment types for accurate calculation
      const cashPaid = parseFloat(order.cash_paid_amount||0)||0;
      const bankPaid = parseFloat(order.bank_paid_amount||0)||0;
      const creditPaid = parseFloat(order.credit_paid_amount||0)||0;
      const totalPaid = cashPaid + bankPaid + creditPaid;
      
      const fullyPaid = (Math.max(0, grandTotal - totalPaid) <= 0.0001 || (order.payment_status||'').toLowerCase()==='fully_paid');
      
      console.log(`Order ${index + 1}: ${paymentMethod.toUpperCase()} - Total: £${grandTotal}, Paid: £${totalPaid}, FullyPaid: ${fullyPaid}`);
      
      // Count ACTUAL money received by payment method
      if (isCash) {
        // For cash orders: use total if fully paid, otherwise use recorded payments
        const actualCashReceived = cashPaid > 0 ? cashPaid : (fullyPaid ? grandTotal : 0);
        metrics.cashSales += actualCashReceived;
        metrics.totalRevenue += actualCashReceived;
        console.log(`  → Cash Sales: £${actualCashReceived}`);
      } else if (isBank) {
        // For bank orders: use total if fully paid, otherwise use recorded payments
        const actualBankReceived = bankPaid > 0 ? bankPaid : (fullyPaid ? grandTotal : 0);
        metrics.bankSales += actualBankReceived;
        metrics.totalRevenue += actualBankReceived;
        console.log(`  → Bank Sales: £${actualBankReceived}`);
      } else if (isCredit) {
        // For credit: count actual payments received, rest is outstanding
        const allPaymentsReceived = totalPaid;
        if (allPaymentsReceived > 0) {
          // ALL credit payments go to cash sales (as per your business model)
          metrics.cashSales += allPaymentsReceived;
          metrics.totalRevenue += allPaymentsReceived;
          console.log(`  → Credit Payment to Cash: £${allPaymentsReceived}`);
        }
        
        // Outstanding credit = total - ALL paid (cash + bank + credit)
        const outstanding = Math.max(0, grandTotal - totalPaid);
        if (outstanding > 0) {
          metrics.outstandingCredit += outstanding;
          console.log(`  → Credit Outstanding: £${outstanding}`);
        }
      }

      // CALCULATION 2: COST OF GOODS SOLD (using actual product costs with hierarchy)
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach(item => {
          const productName = item.product_name || 'Unknown Product';
          const quantity = parseFloat(item.quantity || 0);
          const unitPrice = parseFloat(item.unit_price || 0);
          
          // Use cost hierarchy: item cost → variation cost → product cost → estimated cost
          let unitCost = parseFloat(item.unit_cost || 0);
          
          if (!unitCost && item.variation_id && variationLookup[item.variation_id]) {
            const variationData = variationLookup[item.variation_id];
            const variation = variationData.variation;
            unitCost = parseFloat(variation.cost) || 
                      parseFloat(variation.wholesale) || 
                      parseFloat(variation.wholesale_price) || 
                      (parseFloat(variation.retail_price) * 0.7) ||
                      0;
          }
          
          if (!unitCost && item.product_id && productLookup[item.product_id]) {
            const product = productLookup[item.product_id];
            unitCost = parseFloat(product.cost) || 
                      parseFloat(product.wholesale) ||
                      parseFloat(product.wholesale_price) ||
                      (parseFloat(product.retail_price) * 0.7) ||
                      0;
          }
          
          // Final fallback: estimate based on product type and unit price
          if (!unitCost && unitPrice > 0) {
            if (productName.toLowerCase().includes('carpet') || productName.toLowerCase().includes('silky')) {
              unitCost = unitPrice * 0.65; // 35% margin
            } else if (productName.toLowerCase().includes('underlay')) {
              unitCost = unitPrice * 0.75; // 25% margin  
            } else if (productName.toLowerCase().includes('herringbone') || productName.toLowerCase().includes('oak')) {
              unitCost = unitPrice * 0.68; // 32% margin
            } else {
              unitCost = unitPrice * 0.70; // 30% margin (default)
            }
          }
          
          const itemCost = unitCost * quantity;
          const itemRevenue = unitPrice * quantity;
          const itemProfit = itemRevenue - itemCost;
          
          console.log(`  Item: ${productName}, Qty: ${quantity}, Cost: £${unitCost.toFixed(2)}, Revenue: £${itemRevenue.toFixed(2)}, Profit: £${itemProfit.toFixed(2)}`);
          
          // Track product performance
          if (!metrics.productsSold[productName]) {
            metrics.productsSold[productName] = {
              quantity: 0,
              revenue: 0,
              cost: 0,
              profit: 0
            };
          }
          
          metrics.productsSold[productName].quantity += quantity;
          metrics.productsSold[productName].revenue += itemRevenue;
          metrics.productsSold[productName].cost += itemCost;
          metrics.productsSold[productName].profit += itemProfit;
          
          metrics.totalCost += itemCost;
        });
      }

      // Set time period
      const orderDate = new Date(order.date);
      if (!metrics.periodStart || orderDate < metrics.periodStart) {
        metrics.periodStart = orderDate;
      }
      if (!metrics.periodEnd || orderDate > metrics.periodEnd) {
        metrics.periodEnd = orderDate;
      }
    });

    // CALCULATION 3: OUTSTANDING CREDIT (using same logic as Credit Accounts page)
    // 💰 IRON-CLAD: Use creditAccountsOrders as SINGLE SOURCE OF TRUTH.
    // This includes ALL bills with outstanding balance regardless of status (fulfilled, pending, etc.)
    // and regardless of payment_method (cash, bank, credit).
    const outstandingCreditOrders = (creditAccountsOrders || []).filter(order => {
      const outstanding = parseFloat(order.outstanding_balance || 0);
      return outstanding > 0;
    });
    
    // Calculate total outstanding credit using same logic as Credit Accounts page
    metrics.outstandingCredit = outstandingCreditOrders.reduce((total, order) => {
      // Use outstanding_balance from API (already computed by backend)
      return total + parseFloat(order.outstanding_balance || 0);
    }, 0);
    
    // Count unique credit customers with outstanding balances
    // 💰 IRON-CLAD: Treat each guest customer as unique (group by customer_number/order id when customer_id is null)
    const creditCustomerIds = new Set();
    outstandingCreditOrders.forEach(order => {
      const key = order.customer_id || `guest:${order.customer_number || order.id}`;
      creditCustomerIds.add(key);
    });
    metrics.creditCustomers = creditCustomerIds.size;

    // 💰 IRON-CLAD: Subtract account-level lump sum payments to match Credit Accounts page exactly.
    // Cap each customer's deduction at their gross outstanding so overpayments don't drag the total negative.
    try {
      // Aggregate gross outstanding per customer key
      const grossByCustomer = {};
      outstandingCreditOrders.forEach(order => {
        const cid = order.customer_id;
        if (!cid) return; // guest customers have no account payments
        if (!grossByCustomer[cid]) grossByCustomer[cid] = 0;
        grossByCustomer[cid] += parseFloat(order.outstanding_balance || 0);
      });
      let totalAppliedAccountPayments = 0;
      Object.entries(customerAccountPayments || {}).forEach(([cid, data]) => {
        const acctTotal = parseFloat((data && data.total) || 0);
        if (acctTotal <= 0) return;
        const gross = grossByCustomer[cid] || 0;
        totalAppliedAccountPayments += Math.min(acctTotal, gross);
      });
      metrics.outstandingCredit = Math.max(0, metrics.outstandingCredit - totalAppliedAccountPayments);
    } catch (e) {
      console.warn('Could not subtract account payments from outstandingCredit', e);
    }

    // CALCULATION 4: STOCK VALUE (using actual inventory with enhanced cost hierarchy)
    console.log('📦 Calculating stock value from', products.length, 'products');
    products.forEach((product, index) => {
      console.log(`Product ${index + 1}:`, product.name || product.title, 'measurement_unit:', product.measurement_unit, 'variations:', product.variations?.length);
      
      const isSquareMeterProduct = product.measurement_unit === 'per_square_meter';
      
      if (product.variations && Array.isArray(product.variations)) {
        product.variations.forEach((variation, vIndex) => {
          // Get stock quantity based on measurement unit
          let stockQty = 0;
          if (isSquareMeterProduct) {
            stockQty = parseFloat(variation.square_meters_available || variation.square_meters || 0);
          } else {
            stockQty = parseFloat(variation.stock_quantity || variation.quantity || 0);
          }
          
          // Calculate stock value using enhanced cost hierarchy
          let costPrice = 0;
          costPrice = parseFloat(variation.cost) || 
                     parseFloat(variation.wholesale) ||
                     parseFloat(variation.wholesale_price) ||
                     (parseFloat(variation.retail) * 0.7) ||
                     (parseFloat(variation.retail_price) * 0.7) ||
                     0;
                     
          const stockValue = stockQty * costPrice;
          
          console.log(`  Variation ${vIndex + 1}: qty=${stockQty}, cost=£${costPrice.toFixed(2)}, value=£${stockValue.toFixed(2)}, unit=${product.measurement_unit}`);
          
          // Update overall metrics
          metrics.currentStockValue += stockValue;
          metrics.totalStockQuantity += stockQty;
          
          // Update separate metrics by measurement unit
          if (isSquareMeterProduct) {
            metrics.stockBySquareMeter.totalValue += stockValue;
            metrics.stockBySquareMeter.totalQuantity += stockQty;
          } else {
            metrics.stockBySize.totalValue += stockValue;
            metrics.stockBySize.totalQuantity += stockQty;
          }
        });
        
        // Count products by measurement unit
        if (isSquareMeterProduct) {
          metrics.stockBySquareMeter.productCount++;
        } else {
          metrics.stockBySize.productCount++;
        }
      } else {
        // Handle products without variations
        const stockQty = parseFloat(product.quantity || 0);
        
        // Calculate cost using enhanced hierarchy
        let costPrice = 0;
        costPrice = parseFloat(product.cost) || 
                   parseFloat(product.wholesale) ||
                   parseFloat(product.wholesale_price) ||
                   (parseFloat(product.price) * 0.7) ||
                   (parseFloat(product.retail_price) * 0.7) ||
                   0;
                   
        const stockValue = stockQty * costPrice;
        
        console.log(`  No variations - qty=${stockQty}, cost=£${costPrice.toFixed(2)}, value=£${stockValue.toFixed(2)}, unit=${product.measurement_unit}`);
        
        // Update overall metrics
        metrics.currentStockValue += stockValue;
        metrics.totalStockQuantity += stockQty;
        
        // Update separate metrics by measurement unit
        if (isSquareMeterProduct) {
          metrics.stockBySquareMeter.totalValue += stockValue;
          metrics.stockBySquareMeter.totalQuantity += stockQty;
          metrics.stockBySquareMeter.productCount++;
        } else {
          metrics.stockBySize.totalValue += stockValue;
          metrics.stockBySize.totalQuantity += stockQty;
          metrics.stockBySize.productCount++;
        }
      }
    });

    // CALCULATION 5: PROFIT CALCULATIONS
    metrics.grossProfit = metrics.totalRevenue - metrics.totalCost;
    
    // Use realistic operating expenses (12% of revenue is industry standard)
    const estimatedOperatingExpenses = metrics.totalRevenue * 0.12;
    metrics.netProfit = metrics.grossProfit - estimatedOperatingExpenses;
    
    // CALCULATION 6: AVERAGES AND PERFORMANCE METRICS
    metrics.averageOrderValue = metrics.totalOrders > 0 ? metrics.totalRevenue / metrics.totalOrders : 0;

    // Sort products by sales performance
    const productEntries = Object.entries(metrics.productsSold);
    metrics.fastestSelling = productEntries
      .sort((a, b) => b[1].quantity - a[1].quantity)
      .slice(0, 5);
    
    metrics.slowestSelling = productEntries
      .sort((a, b) => a[1].quantity - b[1].quantity)
      .slice(0, 5);

    // Log final results for debugging
    console.log('📊 Final Business Metrics:', {
      totalRevenue: `£${metrics.totalRevenue.toFixed(2)}`,
      totalCost: `£${metrics.totalCost.toFixed(2)}`,
      grossProfit: `£${metrics.grossProfit.toFixed(2)}`,
      netProfit: `£${metrics.netProfit.toFixed(2)}`,
      outstandingCredit: `£${metrics.outstandingCredit.toFixed(2)}`,
      currentStockValue: `£${metrics.currentStockValue.toFixed(2)}`,
      creditSales: `£${metrics.creditSales.toFixed(2)}`,
      cashSales: `£${metrics.cashSales.toFixed(2)}`,
      bankSales: `£${metrics.bankSales.toFixed(2)}`
    });

    return metrics;
  };

  const generateBusinessReportHTML = (metrics, fromDate, toDate) => {
    const periodStr = fromDate && toDate ? 
      `${new Date(fromDate).toLocaleDateString('en-GB')} to ${new Date(toDate).toLocaleDateString('en-GB')}` :
      fromDate ? `From ${new Date(fromDate).toLocaleDateString('en-GB')}` :
      toDate ? `Until ${new Date(toDate).toLocaleDateString('en-GB')}` :
      'All Time';

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Business Analysis Report - Plush Living Ltd</title>
        <style>
          @page { 
            margin: 0.2in; 
            size: A4;
          }
          body { 
            font-family: 'Arial', sans-serif; 
            margin: 0; 
            padding: 10px;
            color: #1B365D;
            background: white;
            font-size: 10px;
            line-height: 1.2;
          }
          .report-header {
            text-align: center;
            margin-bottom: 20px;
            padding: 15px;
            background: linear-gradient(135deg, #1B365D 0%, #4A90A4 100%);
            color: white;
            border-radius: 6px;
          }
          .company-title {
            font-size: 20px;
            font-weight: bold;
            margin-bottom: 5px;
            color: #FFD700;
          }
          .report-title {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          .report-period {
            font-size: 12px;
            font-style: italic;
            color: #87CEEB;
          }
          
          .metrics-grid {
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            gap: 8px;
            margin: 15px 0;
          }
          .metric-card {
            background: white;
            border: 1px solid #1B365D;
            border-radius: 4px;
            padding: 8px;
            text-align: center;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          .metric-title {
            font-size: 9px;
            color: #1B365D;
            font-weight: bold;
            margin-bottom: 4px;
            text-transform: uppercase;
            line-height: 1.1;
          }
          .metric-value {
            font-size: 14px;
            font-weight: bold;
            color: #4A90A4;
            margin-bottom: 2px;
          }
          .metric-subtitle {
            font-size: 8px;
            color: #666;
            line-height: 1.1;
          }
          
          .section {
            margin: 20px 0;
            padding: 10px;
            background: #F8F9FA;
            border-left: 3px solid #FFD700;
            border-radius: 3px;
          }
          .section-title {
            font-size: 12px;
            font-weight: bold;
            color: #1B365D;
            margin-bottom: 8px;
            text-transform: uppercase;
          }
          
          .analysis-table {
            width: 100%;
            border-collapse: collapse;
            margin: 8px 0;
            font-size: 9px;
          }
          .analysis-table th {
            background: #1B365D;
            color: white;
            padding: 4px 6px;
            text-align: left;
            font-weight: bold;
            border: 1px solid #1B365D;
            font-size: 8px;
          }
          .analysis-table td {
            padding: 3px 6px;
            border: 1px solid #ddd;
            background: white;
            font-size: 9px;
            line-height: 1.2;
          }
          .analysis-table tr:nth-child(even) td {
            background: #F8F9FA;
          }
          
          .profit-positive {
            color: #22c55e;
            font-weight: bold;
          }
          .profit-negative {
            color: #ef4444;
            font-weight: bold;
          }
          .highlight {
            background: #FFD700;
            color: #1B365D;
            padding: 1px 3px;
            border-radius: 2px;
            font-weight: bold;
          }
          
          @media print {
            body { 
              background: white !important; 
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .section { break-inside: avoid; }
            .analysis-table { break-inside: auto; }
            @page {
              margin: 0.2in;
              size: A4;
            }
          }
        </style>
      </head>
      <body>
        <div class="report-header">
          <div class="company-title">PLUSH LIVING LTD</div>
          <div class="report-title">Comprehensive Business Analysis Report</div>
          <div class="report-period">Period: ${periodStr}</div>
          <div class="report-period">Generated: ${new Date().toLocaleDateString('en-GB')} at ${new Date().toLocaleTimeString('en-GB')}</div>
        </div>

        <!-- Key Metrics Overview -->
        <div class="metrics-grid">
          <div class="metric-card">
            <div class="metric-title">Total Revenue</div>
            <div class="metric-value">£${metrics.totalRevenue.toFixed(2)}</div>
            <div class="metric-subtitle">${metrics.totalOrders} orders</div>
          </div>
          <div class="metric-card">
            <div class="metric-title">Gross Profit</div>
            <div class="metric-value ${metrics.grossProfit >= 0 ? 'profit-positive' : 'profit-negative'}">£${metrics.grossProfit.toFixed(2)}</div>
            <div class="metric-subtitle">${((metrics.grossProfit/metrics.totalRevenue)*100).toFixed(1)}% margin</div>
          </div>
          <div class="metric-card">
            <div class="metric-title">Current Stock Value</div>
            <div class="metric-value">£${metrics.currentStockValue.toFixed(2)}</div>
            <div class="metric-subtitle">${metrics.stockBySize.totalQuantity.toFixed(0)} units + ${metrics.stockBySquareMeter.totalQuantity.toFixed(0)} sqm</div>
          </div>
          <div class="metric-card">
            <div class="metric-title">Outstanding Credit</div>
            <div class="metric-value">£${metrics.outstandingCredit.toFixed(2)}</div>
            <div class="metric-subtitle">${metrics.creditCustomers} customers</div>
          </div>
          <div class="metric-card">
            <div class="metric-title">Avg Order Value</div>
            <div class="metric-value">£${metrics.averageOrderValue.toFixed(2)}</div>
            <div class="metric-subtitle">per transaction</div>
          </div>
        </div>

        <!-- Warehouse Stock Analysis -->
        <div class="section">
          <div class="section-title">Current Warehouse Stock Analysis</div>
          <table class="analysis-table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>Value</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Total Stock Value</strong></td>
                <td><strong>£${metrics.currentStockValue.toFixed(2)}</strong></td>
                <td>Current value of all inventory at cost price</td>
              </tr>
              <tr>
                <td><strong>Total Stock Quantity</strong></td>
                <td><strong>${metrics.totalStockQuantity.toFixed(0)} mixed units</strong></td>
                <td>Combined quantity (units + square meters)</td>
              </tr>
              <tr class="highlight">
                <td colspan="3" style="text-align: center; font-weight: bold; color: #1B365D;">PRODUCTS BY SIZE (Individual Units)</td>
              </tr>
              <tr>
                <td>Size-Based Products Count</td>
                <td>${metrics.stockBySize.productCount} products</td>
                <td>Products sold by individual pieces/units</td>
              </tr>
              <tr>
                <td>Size-Based Stock Value</td>
                <td>£${metrics.stockBySize.totalValue.toFixed(2)}</td>
                <td>Value of products sold by size</td>
              </tr>
              <tr>
                <td>Size-Based Stock Quantity</td>
                <td>${metrics.stockBySize.totalQuantity.toFixed(0)} units</td>
                <td>Individual pieces available</td>
              </tr>
              <tr class="highlight">
                <td colspan="3" style="text-align: center; font-weight: bold; color: #1B365D;">PRODUCTS BY SQUARE METER</td>
              </tr>
              <tr>
                <td>Square Meter Products Count</td>
                <td>${metrics.stockBySquareMeter.productCount} products</td>
                <td>Products sold by square meter area</td>
              </tr>
              <tr>
                <td>Square Meter Stock Value</td>
                <td>£${metrics.stockBySquareMeter.totalValue.toFixed(2)}</td>
                <td>Value of products sold by square meter</td>
              </tr>
              <tr>
                <td>Square Meter Stock Quantity</td>
                <td>${metrics.stockBySquareMeter.totalQuantity.toFixed(0)} sqm</td>
                <td>Total square meters available</td>
              </tr>
              <tr>
                <td>Stock-to-Revenue Ratio</td>
                <td>${((metrics.currentStockValue/metrics.totalRevenue)*100).toFixed(1)}%</td>
                <td>Stock value as percentage of revenue</td>
              </tr>
              <tr>
                <td>Average Value per Mixed Unit</td>
                <td>£${metrics.totalStockQuantity > 0 ? (metrics.currentStockValue/metrics.totalStockQuantity).toFixed(2) : '0.00'}</td>
                <td>Note: Mixed calculation (units + sqm)</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Sales Analysis -->
        <div class="section">
          <div class="section-title">Sales Analysis by Payment Method</div>
          <table class="analysis-table">
            <thead>
              <tr>
                <th>Payment Method</th>
                <th>Amount</th>
                <th>Percentage</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Cash Sales (Received)</strong></td>
                <td>£${metrics.cashSales.toFixed(2)}</td>
                <td>${metrics.totalRevenue > 0 ? ((metrics.cashSales/metrics.totalRevenue)*100).toFixed(1) : '0.0'}%</td>
                <td><span class="profit-positive">✓ Money Received</span></td>
              </tr>
              <tr>
                <td><strong>Bank Transfer Sales (Received)</strong></td>
                <td>£${metrics.bankSales.toFixed(2)}</td>
                <td>${metrics.totalRevenue > 0 ? ((metrics.bankSales/metrics.totalRevenue)*100).toFixed(1) : '0.0'}%</td>
                <td><span class="profit-positive">✓ Money Received</span></td>
              </tr>
              <!-- Credit payments are now categorized as Cash or Bank based on payment channel -->
              <tr style="border-top: 2px solid #ddd;">
                <td><strong>Credit Outstanding (Owed to You)</strong></td>
                <td>£${metrics.outstandingCredit.toFixed(2)}</td>
                <td><span style="color: #666;">Not counted in revenue</span></td>
                <td><span class="highlight">⚠ Accounts Receivable</span></td>
              </tr>
            </tbody>
          </table>
          
          <!-- Financial Summary -->
          <div style="margin-top: 20px; padding: 15px; background: #f8f9fa; border-left: 4px solid #007bff; border-radius: 4px;">
            <h4 style="margin: 0 0 10px 0; color: #007bff;">Financial Summary</h4>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
              <div>
                <strong>💰 Money in Bank:</strong><br>
                <span style="font-size: 1.2em; color: #28a745;">£${metrics.totalRevenue.toFixed(2)}</span><br>
                <small style="color: #666;">Cash + Bank + Credit Payments</small>
              </div>
              <div>
                <strong>📋 Money Owed to You:</strong><br>
                <span style="font-size: 1.2em; color: #ffc107;">£${metrics.outstandingCredit.toFixed(2)}</span><br>
                <small style="color: #666;">Credit Outstanding (Not Revenue)</small>
              </div>
            </div>
          </div>
        </div>

        <!-- Enhanced Profitability Analysis -->
        <div class="section">
          <div class="section-title">Profitability Analysis</div>
          <table class="analysis-table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>Amount (£)</th>
                <th>Percentage (%)</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Total Revenue (Money Received)</strong></td>
                <td><strong>£${metrics.totalRevenue.toFixed(2)}</strong></td>
                <td><strong>100.0%</strong></td>
                <td>Actual cash received (excludes credit outstanding)</td>
              </tr>
              <tr>
                <td>Less: Cost of Goods Sold</td>
                <td>£${metrics.totalCost.toFixed(2)}</td>
                <td>${((metrics.totalCost/metrics.totalRevenue)*100).toFixed(1)}%</td>
                <td>Direct cost of products sold</td>
              </tr>
              <tr class="highlight">
                <td><strong>Gross Profit</strong></td>
                <td><strong>£${metrics.grossProfit.toFixed(2)}</strong></td>
                <td class="profit-positive"><strong>${((metrics.grossProfit/metrics.totalRevenue)*100).toFixed(1)}%</strong></td>
                <td>Revenue minus cost of goods sold</td>
              </tr>
              <tr>
                <td>Less: Operating Expenses (Est.)</td>
                <td>£${(metrics.totalRevenue * 0.12).toFixed(2)}</td>
                <td>12.0%</td>
                <td>Estimated operating expenses (rent, salaries, utilities)</td>
              </tr>
              <tr class="highlight">
                <td><strong>Net Profit</strong></td>
                <td><strong>£${metrics.netProfit.toFixed(2)}</strong></td>
                <td class="profit-${metrics.netProfit >= 0 ? 'positive' : 'negative'}"><strong>${((metrics.netProfit/metrics.totalRevenue)*100).toFixed(1)}%</strong></td>
                <td>Final profit after all expenses</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Top Selling Products -->
        <div class="section">
          <div class="section-title">Top 5 Fastest Selling Products</div>
          <table class="analysis-table">
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Quantity Sold</th>
                <th>Revenue</th>
                <th>Profit</th>
              </tr>
            </thead>
            <tbody>
              ${metrics.fastestSelling.map(([name, data]) => `
                <tr>
                  <td><strong>${name}</strong></td>
                  <td>${data.quantity.toFixed(2)} sqm</td>
                  <td>£${data.revenue.toFixed(2)}</td>
                  <td class="${data.profit >= 0 ? 'profit-positive' : 'profit-negative'}">£${data.profit.toFixed(2)}</td>
                </tr>
              `).join('')}
              ${metrics.fastestSelling.length === 0 ? '<tr><td colspan="4" style="text-align: center; color: #666;">No sales data available</td></tr>' : ''}
            </tbody>
          </table>
        </div>

        <!-- Slowest Selling Products -->
        <div class="section">
          <div class="section-title">5 Slowest Selling Products</div>
          <table class="analysis-table">
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Quantity Sold</th>
                <th>Revenue</th>
                <th>Performance</th>
              </tr>
            </thead>
            <tbody>
              ${metrics.slowestSelling.map(([name, data]) => `
                <tr>
                  <td><strong>${name}</strong></td>
                  <td>${data.quantity.toFixed(2)} sqm</td>
                  <td>£${data.revenue.toFixed(2)}</td>
                  <td><span style="color: #f59e0b;">⚠ Review Required</span></td>
                </tr>
              `).join('')}
              ${metrics.slowestSelling.length === 0 ? '<tr><td colspan="4" style="text-align: center; color: #666;">No sales data available</td></tr>' : ''}
            </tbody>
          </table>
        </div>

        <!-- Credit Management -->
        <div class="section">
          <div class="section-title">Credit Management Summary</div>
          <table class="analysis-table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>Value</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Total Credit Sales</td>
                <td>£${metrics.creditSales.toFixed(2)}</td>
                <td>${metrics.creditSales > 0 ? 'Active' : 'None'}</td>
              </tr>
              <tr>
                <td>Outstanding Credit</td>
                <td>£${metrics.outstandingCredit.toFixed(2)}</td>
                <td class="${metrics.outstandingCredit > 0 ? 'profit-negative' : 'profit-positive'}">
                  ${metrics.outstandingCredit > 0 ? '⚠ Action Required' : '✓ All Clear'}
                </td>
              </tr>
              <tr>
                <td>Credit Customers</td>
                <td>${metrics.creditCustomers}</td>
                <td>${metrics.creditCustomers > 0 ? 'Active' : 'None'}</td>
              </tr>
              <tr>
                <td>Credit Collection Rate</td>
                <td>${metrics.creditSales > 0 ? (((metrics.creditSales - metrics.outstandingCredit)/metrics.creditSales)*100).toFixed(1) : '0.0'}%</td>
                <td class="${metrics.creditSales > 0 && ((metrics.creditSales - metrics.outstandingCredit)/metrics.creditSales) > 0.8 ? 'profit-positive' : 'profit-negative'}">
                  ${metrics.creditSales > 0 && ((metrics.creditSales - metrics.outstandingCredit)/metrics.creditSales) > 0.8 ? '✓ Good' : '⚠ Monitor'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style="margin-top: 50px; padding: 20px; background: #F8F9FA; border: 1px solid #1B365D; text-align: center;">
          <div style="color: #1B365D; font-weight: bold; margin-bottom: 10px;">
            Report generated by Plush Living Ltd Business Intelligence System
          </div>
          <div style="color: #666; font-size: 11px;">
            This report provides comprehensive business insights for informed decision-making.
            For questions or additional analysis, contact the management team.
          </div>
        </div>

        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            }, 1000);
          }
        </script>
      </body>
      </html>
    `;

    console.log('🔍 CATALOG DEBUG: Generated HTML content length:', htmlContent.length);
    console.log('🔍 CATALOG DEBUG: Image HTML sample:', htmlContent.match(/<img[^>]*>/gi)?.[0] || 'No img tags found');
    console.log('🔍 CATALOG DEBUG: Container HTML sample:', htmlContent.match(/<div[^>]*product-image-container[^>]*>/gi)?.[0] || 'No container found');
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);

    const newWindow = window.open(url, '_blank', 'width=1200,height=800');
    if (!newWindow) {
      const link = document.createElement('a');
      link.href = url;
      link.download = `business_analysis_${new Date().toISOString().slice(0, 10)}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      alert('Business analysis downloaded as HTML. Open in your browser and use Ctrl+P to save as PDF.');
    } else {
      alert('Business analysis report opened in new window. Use Ctrl+P to print/save as PDF.');
    }
  };

  const handleDownloadAccountingCSV = () => {
    const rows = displayedInvoices.map(inv => ({
      invoice_number: inv.invoice_number,
      order_id: inv.order_id,
      invoice_date: inv.invoice_date,
      customer_name: inv.customer_name,
      subtotal: inv.subtotal,
      vat_amount: inv.vat_amount,
      total_amount: inv.total_amount,
      item_count: Array.isArray(inv.items) ? inv.items.length : 0,
    }));
    const headers = Object.keys(rows[0] || {
      invoice_number: '', order_id: '', invoice_date: '', customer_name: '', subtotal: '', vat_amount: '', total_amount: '', item_count: ''
    });
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => JSON.stringify(r[h] ?? '')).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoices_${new Date().toISOString().slice(0,10).replace(/-/g,'')}_all.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Load orders for admin dashboard
  const loadOrders = async () => {
    try {
      setLoadingOrders(true);
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const response = await axios.get(`${API}/api/admin-portal/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // 👻 NO MORE GHOST BILLS: Don't filter out returned/cancelled from the Orders tab either.
      // Every bill recorded stays visible. Status badges in the UI tell them apart.
      const allOrders = response.data || [];
      const activeOrders = allOrders;
      
      console.log(`📊 Orders loaded: ${allOrders.length} total (incl. returned/cancelled — no ghosts)`);
      setOrders(activeOrders);
      
    } catch (error) {
      console.error('Error loading orders:', error);
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };


  // Mark order as fulfilled
  const handleMarkFulfilled = async (orderId) => {
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const res = await axios.put(`${API}/api/orders/${orderId}/fulfill`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.status === 200) {
        alert('Order marked as fulfilled');
        refreshAllData();
      }
    } catch (e) {
      console.error('Error marking fulfilled:', e);
      alert('Error marking fulfilled: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Generate invoice from a fulfilled order
  const handleGenerateInvoice = async (order) => {
    // Prompt user for invoice amount
    const grandTotal = parseFloat(order.grand_total || 0);
    const customAmountStr = prompt(
      `Invoice Amount:\n\nOrder Total: £${grandTotal.toFixed(2)}\n\nEnter custom amount to invoice (or leave blank for full amount):`,
      ''
    );
    
    // If user cancelled, don't generate invoice
    if (customAmountStr === null) {
      return;
    }
    
    // Determine amount to invoice
    let amountToInvoice = null;
    if (customAmountStr.trim() !== '') {
      const customAmount = parseFloat(customAmountStr);
      if (isNaN(customAmount) || customAmount <= 0) {
        alert('Please enter a valid positive amount.');
        return;
      }
      amountToInvoice = customAmount;
    }
    
    await generateInvoiceWithAmount(order, amountToInvoice);
  };

  // Generate invoice with specified amount (for credit orders) or full amount
  const generateInvoiceWithAmount = async (order, customAmount) => {
    try {
      const invoiceNumber = `INV-${new Date().toISOString().slice(0,10).replace(/-/g,'')}-${(Math.random()*10000|0).toString().padStart(4,'0')}`;
      
      let subtotal, vat_amount, total_amount;
      
      if (customAmount !== null && customAmount !== undefined) {
        // For credit orders with custom amount
        total_amount = parseFloat(customAmount);
        subtotal = total_amount / 1.2; // Calculate subtotal assuming 20% VAT
        vat_amount = total_amount - subtotal;
      } else {
        // For regular orders with full amount
        subtotal = parseFloat(order.subtotal || 0);
        vat_amount = parseFloat(order.vat_amount ?? (subtotal * 0.2));
        total_amount = parseFloat(order.grand_total ?? (subtotal + vat_amount));
      }

      // Get customer details for invoice
      let customerName = 'Guest Customer';
      let customerId = 'guest';
      
      if (order.customer_id && order.customer_id !== 'guest') {
        const customer = customers.find(c => c.id === order.customer_id);
        if (customer) {
          customerName = customer.name;
          customerId = customer.id;
        } else {
          customerName = 'Customer Not Found';
          customerId = order.customer_id;
        }
      } else {
        // FIXED: For guest customers, use order.customer_name
        customerName = order.customer_name || 'Guest Customer';
      }

      const payload = {
        order_id: order.id,
        invoice_number: invoiceNumber,
        customer_id: customerId,
        customer_name: customerName,
        customer_number: order.customer_number || 'N/A',
        subtotal,
        vat_amount,
        total_amount,
        items: order.items || []
      };

      const res = await axios.post(`${API}/api/invoices`, payload, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });

      if (res.status === 200) {
        alert('Invoice generated');
        
        // Update the local fulfilled orders to reflect the invoice status
        setFulfilledOrders(prev => prev.map(o => 
          o.id === order.id 
            ? { ...o, is_invoiced: true, invoice_id: res.data.id, invoice_number: res.data.invoice_number } 
            : o
        ));
        
        try {
          const invRes = await axios.get(`${API}/api/invoices`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
          });
          setInvoices(invRes.data || []);
        } catch (e) {}
      }
    } catch (e) {
      console.error('Error generating invoice:', e);
      alert('Error generating invoice: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Download detailed credit bill for customer (HTML, per-payment rows)
  const handleDownloadBill = async (order) => {
    try {
      // Use Customer Management as source of truth
      const customer = customers.find(c => c.id === order.customer_id);
      if (!customer) {
        alert('Customer information not found in Customer Management');
        return;
      }

      // Get all credit orders for this customer from current fulfilledOrders
      const customerCreditOrders = fulfilledOrders.filter(o => 
        o.customer_id === order.customer_id && 
        (o.payment_method || '').toLowerCase() === 'credit'
      );

      // Totals computed from payments
      const totalOrdered = customerCreditOrders.reduce((sum, o) => sum + parseFloat(o.grand_total || 0), 0);
      const totalPaid = customerCreditOrders.reduce((sum, o) => {
        const payments = Array.isArray(o.credit_payments) ? o.credit_payments : [];
        const opaid = payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
        return sum + opaid;
      }, 0);
      const totalBalance = Math.max(0, totalOrdered - totalPaid);

      // Build rows: each payment is a separate row with full order details
      const rowsHtml = customerCreditOrders.map(o => {
        const gt = parseFloat(o.grand_total || 0);
        const payments = Array.isArray(o.credit_payments) ? [...o.credit_payments].sort((a,b) => new Date(a.paid_at) - new Date(b.paid_at)) : [];
        if (!payments.length) {
          return `
            <tr>
              <td>${o.date ? new Date(o.date).toLocaleDateString() : 'N/A'}</td>
              <td>#${o.id.substring(0, 8)}...</td>
              <td>${o.items?.length || 0} items</td>
              <td>£${gt.toFixed(2)}</td>
              <td class="balance-paid">£0.00</td>
              <td>—</td>
              <td class="balance-owed">£${gt.toFixed(2)}</td>
            </tr>
          `;
        }
        let running = 0;
        return payments.map(p => {
          const amt = parseFloat(p.amount || 0);
          running += amt;
          const balAfter = Math.max(0, gt - running);
          return `
            <tr>
              <td>${o.date ? new Date(o.date).toLocaleDateString() : 'N/A'}</td>
              <td>#${o.id.substring(0, 8)}...</td>
              <td>${o.items?.length || 0} items</td>
              <td>£${gt.toFixed(2)}</td>
              <td class="balance-paid">£${amt.toFixed(2)}</td>
              <td>${p.paid_at ? new Date(p.paid_at).toLocaleString() : '—'}</td>
              <td class="${balAfter > 0 ? 'balance-owed' : 'balance-paid'}">£${balAfter.toFixed(2)}</td>
            </tr>
          `;
        }).join('');
      }).join('');

      // Generate HTML content
      const billContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Credit Statement - ${customer.company_name || customer.name}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .company-info { background: #f8f9fa; padding: 15px; margin-bottom: 20px; }
            .summary { background: #e3f2fd; padding: 15px; margin-bottom: 20px; }
            .orders-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            .orders-table th, .orders-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .orders-table th { background-color: #f2f2f2; }
            .total-row { font-weight: bold; background-color: #f8f9fa; }
            .balance-owed { color: #dc3545; font-weight: bold; font-size: 1.2em; }
            .balance-paid { color: #28a745; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>PLUSH LIVING LTD</h1>
            <h2>Credit Account Statement</h2>
            <p>Date: ${new Date().toLocaleDateString()}</p>
          </div>

          <div class="company-info">
            <h3>Customer Information</h3>
            <p><strong>Company:</strong> ${customer.company_name || 'N/A'}</p>
            <p><strong>Contact Person:</strong> ${customer.name}</p>
            <p><strong>Email:</strong> ${customer.email}</p>
            <p><strong>Phone:</strong> ${customer.contact_number || customer.phone || 'N/A'}</p>
            <p><strong>Address:</strong> ${customer.address || 'N/A'}</p>
          </div>

          <div class="summary">
            <h3>Account Summary</h3>
            <p><strong>Total Orders Value:</strong> £${totalOrdered.toFixed(2)}</p>
            <p><strong>Total Amount Paid:</strong> <span class="balance-paid">£${totalPaid.toFixed(2)}</span></p>
            <p><strong>Outstanding Balance:</strong> <span class="balance-owed">£${totalBalance.toFixed(2)}</span></p>
          </div>

          <h3>Credit Order Details (each payment listed)</h3>
          <table class="orders-table">
            <thead>
              <tr>
                <th>Order Date</th>
                <th>Order ID</th>
                <th>Items</th>
                <th>Order Total</th>
                <th>Amount Paid</th>
                <th>Payment Time</th>
                <th>Balance After</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
            <tfoot>
              <tr class="total-row">
                <td colspan="3"><strong>TOTALS</strong></td>
                <td><strong>£${totalOrdered.toFixed(2)}</strong></td>
                <td class="balance-paid"><strong>£${totalPaid.toFixed(2)}</strong></td>
                <td></td>
                <td class="balance-owed"><strong>£${totalBalance.toFixed(2)}</strong></td>
              </tr>
            </tfoot>
          </table>

          <div style="margin-top: 40px; font-size: 0.9em; color: #666;">
            <p><strong>Payment Terms:</strong> Credit accounts must be settled within 30 days of order fulfillment.</p>
            <p><strong>Contact:</strong> For payment arrangements or queries, please contact us at plushlivingltd@outlook.com</p>
            <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
          </div>
        </body>
        </html>
      `;

      // Create blob and download as HTML
      const blob = new Blob([billContent], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Credit_Statement_${customer.company_name || customer.name}_${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Credit statement downloaded for:', customer.name);
    } catch (error) {
      console.error('Error generating credit statement:', error);
      alert('Error generating credit statement. Please try again.');
    }
  };

  // Mark query as read
  const handleMarkQueryRead = async (queryId) => {
    try {
      await axios.put(`${API}/api/queries/${queryId}/mark-read`, {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      // Update the query status in the local state
      setQueries(prevQueries => 
        prevQueries.map(query => 
          query.id === queryId ? { ...query, status: 'read' } : query
        )
      );
    } catch (error) {
      console.error('Error marking query as read:', error);
    }
  };

  const handleViewQueryDetails = (query) => {
    setSelectedQuery(query);
    setShowQueryDetailsModal(true);
    
    // Automatically mark as read when viewing details
    if (query.status === 'unread') {
      handleMarkQueryRead(query.id);
    }
  };

  // Delete query function
  const handleDeleteQuery = async (queryId, queryName) => {
    // Confirm deletion
    const confirmed = window.confirm(`Are you sure you want to delete the query from "${queryName}"? This action cannot be undone.`);
    
    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(`${API}/api/queries/${queryId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      // Remove the query from the local state
      setQueries(prevQueries => 
        prevQueries.filter(query => query.id !== queryId)
      );
      
      alert('Query deleted successfully');
    } catch (error) {
      console.error('Error deleting query:', error);
      alert('Error deleting query: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleGenerateCatalogue = async (catalogueData) => {
    try {
      console.log('Generating catalogue with data:', catalogueData);
      
      // Prepare the request data
      const requestData = {
        product_ids: catalogueData.products.map(product => product.id),
        price_type: catalogueData.priceType
      };
      
      console.log('Sending request to backend:', requestData);
      
      // Make request to backend
      const pdfToken = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const response = await axios.post(`${API}/api/products/pdf`, requestData, {
        headers: { 
          'Authorization': `Bearer ${pdfToken}`,
          'Content-Type': 'application/json'
        },
        responseType: 'blob' // Important for PDF download
      });
      
      if (response.status === 200) {
        // Create blob URL for PDF
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        
        // Create download link
        const link = document.createElement('a');
        link.href = url;
        link.download = `plush_catalog_${new Date().toISOString().slice(0, 10)}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up
        window.URL.revokeObjectURL(url);
        
        alert(`✅ Catalogue generated successfully! Download contains ${catalogueData.products.length} products with ${catalogueData.priceType} pricing.`);
      } else {
        throw new Error(`Server responded with status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error generating catalogue:', error);
      alert(`❌ Error generating catalogue: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
    }
  };

  // Store location management functions
  const handleAddLocation = async (locationData) => {
    try {
      // Verify admin/dashboard token exists
      const adminToken = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      if (!adminToken) {
        alert('Please login as admin first');
        return;
      }
      
      const response = await axios.post(`${API}/api/store-locations`, locationData, {
        headers: { 
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });
      
      // Reload locations
      const res = await axios.get(`${API}/api/store-locations`, {
        timeout: 10000
      });
      setStoreLocations(res.data || []);
      
      setShowAddLocationModal(false);
      alert('Store location added successfully!');
    } catch (error) {
      console.error('Detailed error adding store location:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      let errorMessage = 'Error adding store location';
      
      if (error.response?.status === 403) {
        errorMessage = 'Please login as admin to add store locations';
      } else if (error.response?.status === 422) {
        errorMessage = `Validation error: ${error.response?.data?.detail || 'Invalid data provided'}`;
      } else if (error.response?.data?.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      alert(errorMessage);
    }
  };

  const handleEditLocation = async (locationId, locationData) => {
    try {
      await axios.put(`${API}/api/store-locations/${locationId}`, locationData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      // Reload locations
      const res = await axios.get(`${API}/api/store-locations`);
      setStoreLocations(res.data || []);
      
      setEditingLocation(null);
      alert('Store location updated successfully');
    } catch (e) {
      console.error('Error updating store location:', e);
      alert('Error updating store location');
    }
  };

  const handleDeleteLocation = async (locationId) => {
    try {
      const ok = window.confirm('Delete this store location?');
      if (!ok) return;
      
      await axios.delete(`${API}/api/store-locations/${locationId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      // Reload locations
      const res = await axios.get(`${API}/api/store-locations`);
      setStoreLocations(res.data || []);
      
      alert('Store location deleted successfully');
    } catch (e) {
      console.error('Error deleting store location:', e);
      alert('Error deleting store location');
    }
  };

  // Admin credential management functions
  const handleChangeCredentials = async (credentialData) => {
    try {
      const response = await axios.post(`${API}/api/admin/change-credentials`, credentialData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      alert('Credentials updated successfully! Please log in again with your new credentials.');
      
      // If username was changed, logout and redirect to login
      if (credentialData.new_username) {
        localStorage.removeItem('admin_token');
        setIsAdminLoggedIn(false);
        setCurrentPage('home');
      }
      
      setShowCredentialModal(false);
    } catch (error) {
      console.error('Error updating credentials:', error);
      alert(error.response?.data?.detail || 'Error updating credentials');
    }
  };

  const loadAdminAccounts = async () => {
    try {
      setLoadingAdminAccounts(true);
      // Use admin_token or dashboard_token (for zobayr accessing via admin mode)
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      const response = await axios.get(`${API}/api/admin/all-accounts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAdminAccounts(response.data.admin_accounts || []);
    } catch (error) {
      console.error('Error loading admin accounts:', error);
      setAdminAccounts([]);
    } finally {
      setLoadingAdminAccounts(false);
    }
  };

  const handleResetAdminPassword = async (username, newPassword) => {
    try {
      // Use whichever token is available (admin or dashboard)
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      if (!token) {
        alert('❌ No valid login token found. Please log in again.');
        return;
      }
      
      const response = await axios.post(`${API}/api/admin/reset-password`, {
        target_username: username,
        new_password: newPassword
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Check if password was changed for the currently logged-in user
      const currentAdminUsername = localStorage.getItem('admin_username');
      const currentDashboardUsername = localStorage.getItem('dashboard_username');
      const isCurrentUser = username === currentAdminUsername || username === currentDashboardUsername;
      
      if (response.data.force_logout && isCurrentUser) {
        alert(`Password reset successfully for ${username}.\n\n⚠️ You will need to log in again with your new password.`);
        // Force logout the current user
        localStorage.removeItem('admin_token');
        localStorage.removeItem('dashboard_token');
        localStorage.removeItem('admin_username');
        localStorage.removeItem('dashboard_username');
        setIsAdminLoggedIn(false);
        setIsDashboardUser(false);
        setCurrentPage('home');
      } else {
        alert(`Password reset successfully for ${username}.\n\nNote: ${username} will need to log in again with the new password.`);
        loadAdminAccounts(); // Reload to get updated passwords
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      alert(error.response?.data?.detail || 'Error resetting password');
    }
  };

  const handleCreateAdmin = async (adminData) => {
    try {
      await axios.post(`${API}/api/admin/create-admin`, {
        username: adminData.username,
        password: adminData.password,
        email: adminData.email,
        price_access_levels: adminData.price_access || ["retail"],
        allowed_sections: adminData.allowed_sections || null,
        spectate_sections: adminData.spectate_sections || null,
        role: adminData.role || null,
        can_access_dashboard: adminData.can_access_dashboard || false,
        can_access_settings: adminData.can_access_settings || false
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      alert(`Admin account '${adminData.username}' created successfully`);
      loadAdminAccounts(); // Reload admin accounts
      setShowCreateAdminModal(false);
    } catch (error) {
      console.error('Error creating admin:', error);
      alert(error.response?.data?.detail || 'Error creating admin account');
    }
  };

  const handleDeleteAdmin = async (username) => {
    try {
      const confirmed = window.confirm(`Are you sure you want to delete account '${username}'? This will only remove their login access - no financial data will be affected.`);
      if (!confirmed) return;
      
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      await axios.delete(`${API}/api/admin/delete-admin/${username}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      alert(`Account '${username}' deleted successfully`);
      loadAdminAccounts(); // Reload admin accounts
    } catch (error) {
      console.error('Error deleting account:', error);
      alert(error.response?.data?.detail || 'Error deleting account');
    }
  };

  const handleViewInvoice = async (inv) => {
    try {
      // Store the invoice data for VAT invoice printing
      setSelectedInvoice(inv);
      
      // Always load the live order so invoices reflect returns and current totals
      const res = await axios.get(`${API}/api/orders/${inv.order_id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      if (res.status === 200) {
        setSelectedOrder(res.data);
        setShowOrderViewModal(true);
      } else {
        // Fallback to mapped invoice if order fetch fails
        const mappedOrder = {
          id: inv.order_id,
          customer_id: inv.customer_id,
          items: inv.items,
          subtotal: inv.subtotal,
          vat_amount: inv.vat_amount,
          grand_total: inv.total_amount,
          date: inv.invoice_date,
          customer_name: inv.customer_name,
        };
        setSelectedOrder(mappedOrder);
        setShowOrderViewModal(true);
      }
    } catch (e) {
      console.error('Failed to load order for invoice view, using invoice snapshot:', e);
      const mappedOrder = {
        id: inv.order_id,
        customer_id: inv.customer_id,
        items: inv.items,
        subtotal: inv.subtotal,
        vat_amount: inv.vat_amount,
        grand_total: inv.total_amount,
        date: inv.invoice_date,
        customer_name: inv.customer_name,
      };
      setSelectedOrder(mappedOrder);
      setShowOrderViewModal(true);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    try {
      const ok = window.confirm('Delete this invoice?');
      if (!ok) return;
      await axios.delete(`${API}/api/invoices/${invoiceId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      const invRes = await axios.get(`${API}/api/invoices`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      setInvoices(invRes.data || []);
    } catch (e) {
      console.error('Error deleting invoice:', e);
      alert('Error deleting invoice: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleCreateStandaloneInvoice = async () => {
    // Validation
    if (!standaloneInvoice.customer_id || !standaloneInvoice.amount) {
      alert('Please select a customer and enter an amount');
      return;
    }

    const amount = parseFloat(standaloneInvoice.amount);
    if (amount <= 0) {
      alert('Please enter a valid amount greater than 0');
      return;
    }

    try {
      // Calculate VAT
      let subtotal = amount;
      let vatAmount = 0;
      let totalAmount = amount;

      if (standaloneInvoice.include_vat) {
        // Amount includes VAT, so we need to extract it
        // Total = Subtotal + (Subtotal * 0.20)
        // Total = Subtotal * 1.20
        // Subtotal = Total / 1.20
        subtotal = amount / 1.20;
        vatAmount = amount - subtotal;
        totalAmount = amount;
      } else {
        // Amount does NOT include VAT, so we add it on top
        subtotal = amount;
        vatAmount = amount * 0.20;
        totalAmount = amount + vatAmount;
      }

      // Create standalone invoice
      const invoiceData = {
        customer_id: standaloneInvoice.customer_id,
        customer_name: standaloneInvoice.customer_name,
        customer_number: standaloneInvoice.customer_number,
        amount: totalAmount,
        subtotal: subtotal,
        vat_amount: vatAmount,
        description: standaloneInvoice.description || 'Standalone Invoice',
        date: standaloneInvoice.date,
        payment_method: standaloneInvoice.payment_method,
        include_vat: standaloneInvoice.include_vat
      };

      const response = await axios.post(`${API}/api/invoices/standalone`, invoiceData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });

      // Reload invoices
      const invRes = await axios.get(`${API}/api/invoices`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      setInvoices(invRes.data || []);

      // Close modal and reset form
      setShowStandaloneInvoiceModal(false);
      setStandaloneInvoice({
        customer_id: '',
        customer_name: '',
        customer_number: '',
        amount: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        payment_method: 'cash',
        include_vat: true
      });

      alert('✅ Invoice created successfully!');
    } catch (error) {
      console.error('Error creating standalone invoice:', error);
      alert('Error creating invoice: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Expense handler functions
  const handleCreateExpense = async (e) => {
    e.preventDefault();
    
    if (!newExpense.expense_description || !newExpense.amount) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/api/expenses`, newExpense, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      // Reload expenses
      const res = await axios.get(`${API}/api/expenses`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      setExpenses(res.data || []);
      
      // Reset form
      setNewExpense({
        date: new Date().toISOString().split('T')[0],
        expense_description: '',
        amount: '',
        payment_method: 'cash'
      });
      
      alert('✅ Expense added successfully!');
    } catch (error) {
      console.error('Error creating expense:', error);
      alert('Error adding expense: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    
    try {
      await axios.delete(`${API}/api/expenses/${expenseId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      // Reload expenses
      const res = await axios.get(`${API}/api/expenses`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      setExpenses(res.data || []);
    } catch (error) {
      console.error('Error deleting expense:', error);
      alert('Error deleting expense: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateExpense = async (expenseId, updatedData) => {
    try {
      await axios.put(`${API}/api/expenses/${expenseId}`, updatedData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      // Reload expenses
      const res = await axios.get(`${API}/api/expenses`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      setExpenses(res.data || []);
      setEditingExpense(null);
      alert('Expense updated successfully!');
    } catch (error) {
      console.error('Error updating expense:', error);
      alert('Error updating expense: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCreateRecurringExpense = async (e) => {
    e.preventDefault();
    
    if (!newRecurringExpense.expense_description || !newRecurringExpense.amount) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const endpoint = editingRecurringExpense 
        ? `${API}/api/expenses/recurring/${editingRecurringExpense.id}`
        : `${API}/api/expenses/recurring`;
      
      const method = editingRecurringExpense ? 'put' : 'post';
      
      await axios[method](endpoint, newRecurringExpense, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      // Reload recurring expenses
      const res = await axios.get(`${API}/api/expenses/recurring`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      setRecurringExpenses(res.data || []);
      
      // Reset form
      setNewRecurringExpense({
        expense_description: '',
        amount: '',
        payment_method: 'cash',
        frequency: 'monthly',
        start_date: new Date().toISOString().split('T')[0],
        end_date: ''
      });
      setEditingRecurringExpense(null);
      
      alert(editingRecurringExpense ? '✅ Recurring expense updated!' : '✅ Recurring expense added!');
    } catch (error) {
      console.error('Error saving recurring expense:', error);
      alert('Error saving recurring expense: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditRecurringExpense = (expense) => {
    setNewRecurringExpense({
      expense_description: expense.expense_description,
      amount: expense.amount,
      payment_method: expense.payment_method,
      frequency: expense.frequency,
      start_date: expense.start_date,
      end_date: expense.end_date || ''
    });
    setEditingRecurringExpense(expense);
  };

  const handleDeleteRecurringExpense = async (expenseId) => {
    if (!window.confirm('Delete this recurring expense?')) return;
    
    try {
      await axios.delete(`${API}/api/expenses/recurring/${expenseId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      
      // Reload recurring expenses
      const res = await axios.get(`${API}/api/expenses/recurring`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
      });
      setRecurringExpenses(res.data || []);
    } catch (error) {
      console.error('Error deleting recurring expense:', error);
      alert('Error deleting recurring expense: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDownloadExpensesPdf = async () => {
    if (!expensePdfFromDate || !expensePdfToDate) {
      alert('Please select both from and to dates');
      return;
    }

    try {
      const response = await axios.get(`${API}/api/expenses/pdf`, {
        params: {
          from_date: expensePdfFromDate,
          to_date: expensePdfToDate
        },
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expenses_${expensePdfFromDate}_to_${expensePdfToDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setShowExpensePdfModal(false);
      alert('✅ Expenses PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading expenses PDF:', error);
      alert('Error downloading PDF: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Product Enquiries handler
  const handleProductEnquirySearch = async (searchTerm) => {
    setProductEnquirySearch(searchTerm);
    
    if (!searchTerm || searchTerm.trim().length < 2) {
      setProductEnquiryResults([]);
      return;
    }

    try {
      setSearchingProducts(true);
      
      const lowerSearch = searchTerm.toLowerCase();
      
      // Search from existing products state
      // Search in: product name, collection, color, design, and variation colors
      const filtered = products.filter(product => {
        // Search in product name
        if (product.name?.toLowerCase().includes(lowerSearch)) return true;
        
        // Search in collection
        if (product.collection?.toLowerCase().includes(lowerSearch)) return true;
        
        // Search in product color
        if (product.color?.toLowerCase().includes(lowerSearch)) return true;
        
        // Search in product design
        if (product.design?.toLowerCase().includes(lowerSearch)) return true;
        
        // Search in variation colors
        if (product.variations && Array.isArray(product.variations)) {
          return product.variations.some(v => 
            v.color?.toLowerCase().includes(lowerSearch) ||
            v.design?.toLowerCase().includes(lowerSearch)
          );
        }
        
        return false;
      });
      
      console.log(`🔍 Search "${searchTerm}": Found ${filtered.length} products`);
      setProductEnquiryResults(filtered);
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setSearchingProducts(false);
    }
  };

  // Stock validation functions
  const getAvailableStock = (product, size = null, color = null) => {
    if (!product || !product.variations) return 0;
    
    if (product.measurement_unit === 'per_square_meter') {
      // FIXED: For square meter products, find variation by color only (case-insensitive)
      const searchColor = (color || "").trim().toLowerCase();
      const variation = product.variations.find(v => {
        const variationColor = (v.color || "").trim().toLowerCase();
        return (searchColor && variationColor && searchColor === variationColor) || (!searchColor && !variationColor);
      });
      return variation ? (variation.square_meters || variation.square_meters_available || 0) : 0;
    } else {
      // FIXED: For size-based products, find variation by size and color (case-insensitive)
      const searchColor = (color || "").trim().toLowerCase();
      const variation = product.variations.find(v => {
        const variationColor = (v.color || "").trim().toLowerCase();
        const colorMatch = (searchColor && variationColor && searchColor === variationColor) || (!searchColor && !variationColor);
        const sizeMatch = (size && v.size === size) || (!size && !v.size);
        return sizeMatch && colorMatch;
      });
      return variation ? (variation.stock_quantity || variation.quantity || 0) : 0;
    }
  };

  const isProductInStock = (product) => {
    if (!product || !product.variations) return false;
    
    // Check if any variation has stock
    return product.variations.some(variation => {
      if (product.measurement_unit === 'per_square_meter') {
        return (variation.square_meters || variation.square_meters_available || 0) > 0;
      } else {
        return (variation.stock_quantity || variation.quantity || 0) > 0;
      }
    });
  };

  const getInStockProducts = () => {
    return products.filter(product => isProductInStock(product));
  };

  const validateOrderQuantity = (product, size, color, requestedQty) => {
    const availableStock = getAvailableStock(product, size, color);
    
    // Check if requested quantity exceeds available stock
    if (requestedQty > availableStock) {
      return {
        valid: false,
        message: `Only ${product.measurement_unit === 'per_square_meter' ? parseFloat(availableStock).toFixed(2) : Math.round(availableStock)} ${product.measurement_unit === 'per_square_meter' ? 'm²' : 'units'} available in stock`,
        maxAvailable: availableStock
      };
    }
    
    return { valid: true, maxAvailable: availableStock };
  };

  const handleProductSelection = (productId) => {
    const selectedProduct = products.find(p => p.id === productId);
    if (selectedProduct) {
      const measurementUnit = selectedProduct.measurement_unit || 'size';
      
      if (measurementUnit === 'per_square_meter') {
        // For square meter products, show all available colors directly
        const colors = [...new Set(selectedProduct.variations?.map(v => v.color).filter(Boolean))];
        setAvailableColors(colors);
        setAvailableSizes([]); // No sizes needed for square meter products
        
        setCurrentOrderItem({
          ...currentOrderItem,
          product_id: productId,
          product_name: selectedProduct.name,
          measurement_unit: measurementUnit,
          size: '', // Will be calculated as width x length when dimensions are entered
          color: '',
          sqm: '', // leave empty until admin enters value
          unit_price: 0,
          total_price: 0,
          wide: '', // Initialize width
          length: '', // Initialize length
          wide_unit: 'meters', // Default to meters
          length_unit: 'meters' // Default to meters
        });
      } else {
        // For size-based products, get available sizes and colors
        const sizes = [...new Set(selectedProduct.variations?.map(v => v.size).filter(Boolean))];
        const colors = [...new Set(selectedProduct.variations?.map(v => v.color).filter(Boolean))];
        
        setAvailableSizes(sizes);
        setAvailableColors(colors);
        
        setCurrentOrderItem({
          ...currentOrderItem,
          product_id: productId,
          product_name: selectedProduct.name,
          measurement_unit: measurementUnit,
          size: '',
          color: '',
          quantity: '',
          unit_price: 0,
          total_price: 0
        });
      }
    }
  };

  // Handle size selection for order item
  const handleSizeSelection = (size) => {
    const selectedProduct = products.find(p => p.id === currentOrderItem.product_id);
    if (selectedProduct) {
      // Filter colors available for this size
      const colorsForSize = selectedProduct.variations
        ?.filter(v => v.size === size)
        .map(v => v.color)
        .filter(Boolean);
      
      setAvailableColors([...new Set(colorsForSize)]);
      
      setCurrentOrderItem({
        ...currentOrderItem,
        size: size,
        color: '',
        unit_price: 0,
        total_price: 0
      });
    }
  };

  // Handle color selection and price calculation
  const handleColorSelection = (color) => {
    const selectedProduct = products.find(p => p.id === currentOrderItem.product_id);
    if (selectedProduct) {
      let variation;
      
      if (currentOrderItem.measurement_unit === 'per_square_meter') {
        // FIXED: For square meter products, find variation by color only (case-insensitive)
        const searchColor = (color || "").trim().toLowerCase();
        variation = selectedProduct.variations?.find(v => {
          const variationColor = (v.color || "").trim().toLowerCase();
          return (searchColor && variationColor && searchColor === variationColor) || (!searchColor && !variationColor);
        });
      } else {
        // FIXED: For size-based products, find variation by size and color (case-insensitive)
        const searchColor = (color || "").trim().toLowerCase();
        variation = selectedProduct.variations?.find(v => {
          const variationColor = (v.color || "").trim().toLowerCase();
          const colorMatch = (searchColor && variationColor && searchColor === variationColor) || (!searchColor && !variationColor);
          const sizeMatch = (currentOrderItem.size && v.size === currentOrderItem.size) || (!currentOrderItem.size && !v.size);
          return sizeMatch && colorMatch;
        });
      }
      
      let unitPrice = 0;
      if (variation) {
        // Get price based on sale type with intelligent fallback pricing
        switch (newOrder.sale_type) {
          case 'wholesale':
            unitPrice = variation.wholesale_price || selectedProduct.wholesale_price || 0;
            break;
          case 'retail':
            unitPrice = variation.retail_price || selectedProduct.retail_price || 0;
            break;
          case 'fitter':
            unitPrice = variation.fitter_price || 0;
            break;
          default:
            unitPrice = variation.retail_price || selectedProduct.retail_price || 0;
        }
        
        // If price is placeholder £1 or zero, use cost-based pricing
        if (unitPrice === 0 || unitPrice === 1) {
          const costPrice = variation.cost_price || selectedProduct.cost_price || 0;
          if (costPrice > 0) {
            // Apply markup based on sale type
            switch (newOrder.sale_type) {
              case 'wholesale':
                unitPrice = costPrice * 1.3; // 30% markup for wholesale
                break;
              case 'fitter':
                unitPrice = costPrice * 1.2; // 20% markup for fitter
                break;
              default: // retail
                unitPrice = costPrice * 1.8; // 80% markup for retail
                break;
            }
          }
        }
      }

      setCurrentOrderItem({
        ...currentOrderItem,
        color: color,
        storage_location: variation?.storage_location || '',
        unit_price: unitPrice,
        total_price: unitPrice * (currentOrderItem.measurement_unit === 'per_square_meter' ? (currentOrderItem.sqm || 1) : (currentOrderItem.quantity || 1))
      });
    }
  };

  // Handle quantity/sqm change
  const handleQuantityChange = (value) => {
    const numValue = value === '' ? '' : (parseFloat(value) || 0);
    if (currentOrderItem.measurement_unit === 'per_square_meter') {
      setCurrentOrderItem({
        ...currentOrderItem,
        sqm: numValue,
        total_price: typeof numValue === 'number' ? currentOrderItem.unit_price * numValue : 0
      });
    } else {
      setCurrentOrderItem({
        ...currentOrderItem,
        quantity: numValue === '' ? '' : parseInt(numValue),
        total_price: typeof numValue === 'number' ? currentOrderItem.unit_price * parseInt(numValue) : 0
      });
    }
  };

  // Add item to order
  const addItemToOrder = () => {
    if (currentOrderItem.product_id && currentOrderItem.size && currentOrderItem.color) {
      // Validate stock before adding item
      const selectedProduct = products.find(p => p.id === currentOrderItem.product_id);
      const requestedQty = currentOrderItem.measurement_unit === 'per_square_meter' ? currentOrderItem.sqm : currentOrderItem.quantity;
      const validation = validateOrderQuantity(selectedProduct, currentOrderItem.size, currentOrderItem.color, requestedQty);
      
      if (!validation.valid) {
        alert(`Cannot add item: ${validation.message}`);
        return;
      }
      
      // CRITICAL: Ensure has_vat is explicitly TRUE unless explicitly set to false
      const hasVatValue = currentOrderItem.has_vat === false ? false : true;
      const newItem = { 
        ...currentOrderItem, 
        id: Date.now(),
        has_vat: hasVatValue
      };
      
      console.log('✅ Item added to order:', {
        product: newItem.product_name,
        has_vat: newItem.has_vat,
        total_price: newItem.total_price,
        unit_price: newItem.unit_price,
        quantity: newItem.quantity || newItem.sqm
      });
      
      const updatedItems = [...newOrder.order_items, newItem];
      
      // IRONCLAD CALCULATION - Single source of truth for all totals
      const calc = calculateIronclad(
        updatedItems,
        newOrder.fitting_charge,
        newOrder.cut_piece_charge,
        newOrder.discount
      );
      
      console.log('📊 IRONCLAD Calculation:', {
        subtotal: calc.subtotal.toFixed(2),
        serviceCharges: calc.serviceCharges.toFixed(2),
        vatAmount: calc.vatAmount.toFixed(2),
        discount: calc.discount.toFixed(2),
        grandTotal: calc.grandTotal.toFixed(2)
      });
      
      setNewOrder({
        ...newOrder,
        order_items: updatedItems,
        subtotal: calc.subtotal,
        vat_amount: calc.vatAmount,
        grand_total: calc.grandTotal
      });
      
      // Reset current item (full reset)
      setCurrentOrderItem({
        product_id: '',
        product_name: '',
        size: '',
        color: '',
        storage_location: '',
        quantity: 1,
        sqm: 0,
        wide: 0,
        length: 0,
        unit_price: 0,
        total_price: 0,
        measurement_unit: 'size',
        has_vat: true // Reset to default ON
      });
      setAvailableSizes([]);
      setAvailableColors([]);
    }
  };

  // Add item to order and keep product selected for quick variant addition
  const addItemAndKeepProduct = () => {
    if (currentOrderItem.product_id && currentOrderItem.color) {
      // Validate stock before adding item
      const selectedProduct = products.find(p => p.id === currentOrderItem.product_id);
      const requestedQty = currentOrderItem.measurement_unit === 'per_square_meter' ? currentOrderItem.sqm : currentOrderItem.quantity;
      const validation = validateOrderQuantity(selectedProduct, currentOrderItem.size, currentOrderItem.color, requestedQty);
      
      if (!validation.valid) {
        alert(`Cannot add item: ${validation.message}`);
        return;
      }
      
      // CRITICAL: Ensure has_vat is explicitly TRUE unless explicitly set to false
      const hasVatValue = currentOrderItem.has_vat === false ? false : true;
      const newItem = { 
        ...currentOrderItem, 
        id: Date.now(),
        has_vat: hasVatValue
      };
      
      console.log('✅ Item added (keeping product):', {
        product: newItem.product_name,
        has_vat: newItem.has_vat,
        total_price: newItem.total_price,
        unit_price: newItem.unit_price,
        quantity: newItem.quantity || newItem.sqm
      });
      
      const updatedItems = [...newOrder.order_items, newItem];
      
      // IRONCLAD CALCULATION - Single source of truth for all totals
      const calc = calculateIronclad(
        updatedItems,
        newOrder.fitting_charge,
        newOrder.cut_piece_charge,
        newOrder.discount
      );
      
      setNewOrder({
        ...newOrder,
        order_items: updatedItems,
        subtotal: calc.subtotal,
        vat_amount: calc.vatAmount,
        grand_total: calc.grandTotal
      });
      
      // Keep product selected, reset only color/size/quantity for quick variant addition
      const keepProductId = currentOrderItem.product_id;
      const keepProductName = currentOrderItem.product_name;
      const keepMeasurementUnit = currentOrderItem.measurement_unit;
      const keepUnitPrice = currentOrderItem.unit_price;
      const keepWideUnit = currentOrderItem.wide_unit || 'meters';
      const keepLengthUnit = currentOrderItem.length_unit || 'meters';
      
      // Reset only variant-specific fields
      setCurrentOrderItem({
        product_id: keepProductId,
        product_name: keepProductName,
        size: '',
        color: '',
        storage_location: '',
        quantity: 1,
        sqm: 0,
        wide: 0,
        length: 0,
        wide_unit: keepWideUnit,
        length_unit: keepLengthUnit,
        unit_price: keepUnitPrice,
        total_price: 0,
        measurement_unit: keepMeasurementUnit,
        has_vat: true // Reset to default ON for new variant
      });
      
      // Reload available sizes and colors for the same product
      if (selectedProduct) {
        const measurementUnit = selectedProduct.measurement_unit || 'size';
        
        if (measurementUnit === 'per_square_meter') {
          // For square meter products, show all colors directly (no size selection needed)
          const colors = [...new Set(selectedProduct.variations?.map(v => v.color).filter(Boolean))];
          setAvailableColors(colors);
          setAvailableSizes([]); // No sizes for square meter products
        } else {
          // For size-based products, reset size and colors (colors will populate when size is selected)
          const sizes = [...new Set(selectedProduct.variations?.map(v => v.size).filter(Boolean) || [])];
          setAvailableSizes(sizes);
          setAvailableColors([]); // Reset colors - will populate when size is selected
        }
      }
    }
  };

  // Remove item from order
  const removeItemFromOrder = (itemId) => {
    const updatedItems = newOrder.order_items.filter(item => item.id !== itemId);
    
    // IRONCLAD CALCULATION - Single source of truth
    const calc = calculateIronclad(
      updatedItems,
      newOrder.fitting_charge,
      newOrder.cut_piece_charge,
      newOrder.discount
    );
    
    setNewOrder({
      ...newOrder,
      order_items: updatedItems,
      subtotal: calc.subtotal,
      vat_amount: calc.vatAmount,
      grand_total: calc.grandTotal
    });
  };

  // Remove item from order by index - FIXED version for handling multiple items of same product
  const removeItemFromOrderByIndex = (indexToRemove) => {
    const updatedItems = newOrder.order_items.filter((_, idx) => idx !== indexToRemove);
    
    // IRONCLAD CALCULATION - Single source of truth
    const calc = calculateIronclad(
      updatedItems,
      newOrder.fitting_charge,
      newOrder.cut_piece_charge,
      newOrder.discount
    );
    
    setNewOrder({
      ...newOrder,
      order_items: updatedItems,
      subtotal: calc.subtotal,
      vat_amount: calc.vatAmount,
      grand_total: calc.grandTotal
    });
  };

  // Handle create order submission
  const handleCreateOrder = async (e) => {
    e.preventDefault();
    
    try {
      // Validate guest customer details
      if (newOrder.customer_id === 'guest' && !newOrder.guest_customer_name) {
        alert('Please enter customer name for guest orders.');
        return;
      }
      
      // If current line is filled but not added, try to add it automatically
      if (newOrder.order_items.length === 0 && currentOrderItem.product_id && currentOrderItem.color) {
        const selectedProduct = products.find(p => p.id === currentOrderItem.product_id);
        const requestedQty = currentOrderItem.measurement_unit === 'per_square_meter' ? (parseFloat(currentOrderItem.sqm) || 0) : (parseInt(currentOrderItem.quantity) || 0);
        const validation = validateOrderQuantity(selectedProduct, currentOrderItem.size, currentOrderItem.color, requestedQty);
        if (!validation.valid || requestedQty <= 0) {
          alert(validation.message || 'Please enter a valid quantity.');
          return;
        }
        // add current item
        addItemToOrder();
      }

      if (newOrder.order_items.length === 0) {
        alert('Please add at least one item to the order.');
        return;
      }
      
      // PRE-ORDER STOCK VALIDATION - ONLY for NEW orders, NOT for editing existing fulfilled orders
      // When editing an existing order, stock has already been deducted, so we skip validation
      if (!newOrder.isEditMode) {
        console.log('🔍 Performing REAL-TIME stock validation for NEW order...');
        let stockValidationErrors = [];
      
      for (const item of newOrder.order_items) {
        const product = products.find(p => p.id === item.product_id);
        if (!product) {
          stockValidationErrors.push(`❌ Product ${item.product_name} not found`);
          continue;
        }
        
        // FIXED: Find matching variation using SAME logic as backend
        let variation = null;
        
        if (product.measurement_unit === 'per_square_meter') {
          // For per_square_meter products: Only match by color (case-insensitive) - SAME AS BACKEND
          const itemColor = (item.color || "").trim().toLowerCase();
          variation = product.variations?.find(v => {
            const variationColor = (v.color || "").trim().toLowerCase();
            return (itemColor && variationColor && itemColor === variationColor) || (!itemColor && !variationColor);
          });
        } else {
          // For size-based products: Match by both size and color (case-insensitive) - SAME AS BACKEND  
          const itemColor = (item.color || "").trim().toLowerCase();
          const itemSize = item.size || "";
          variation = product.variations?.find(v => {
            const variationColor = (v.color || "").trim().toLowerCase();
            const variationSize = v.size || "";
            const colorMatch = (itemColor && variationColor && itemColor === variationColor) || (!itemColor && !variationColor);
            const sizeMatch = (itemSize && variationSize === itemSize) || (!itemSize && !variationSize);
            return sizeMatch && colorMatch;
          });
        }
        
        if (!variation) {
          stockValidationErrors.push(`❌ Variation not found for ${item.product_name} (${item.size || 'N/A'}, ${item.color || 'N/A'})`);
          continue;
        }
        
        // Check current stock availability
        const requestedQty = item.measurement_unit === 'per_square_meter' ? item.sqm : item.quantity;
        let availableStock = 0;
        
        if (product.measurement_unit === 'per_square_meter') {
          availableStock = variation.square_meters_available || variation.square_meters || 0;
        } else {
          availableStock = variation.stock_quantity || variation.quantity || 0;
        }
        
        if (requestedQty > availableStock) {
          const unit = product.measurement_unit === 'per_square_meter' ? 'm²' : 'units';
          stockValidationErrors.push(
            `❌ INSUFFICIENT STOCK: ${item.product_name}\n   Available: ${availableStock} ${unit}\n   Requested: ${requestedQty} ${unit}`
          );
        } else {
          console.log(`✅ Stock OK: ${item.product_name} - Available: ${availableStock}, Requested: ${requestedQty}`);
        }
      }
      
      // Block order if stock validation fails
      if (stockValidationErrors.length > 0) {
        alert(`⚠️ CANNOT CREATE ORDER - INSUFFICIENT STOCK:\n\n${stockValidationErrors.join('\n\n')}\n\n🔄 Refreshing stock levels now...`);
        
        // Force immediate refresh to show current stock
        setProductsLastUpdated(Date.now());
        await loadProducts();
        return;
      }
      
      console.log('✅ Stock validation passed - proceeding with order creation');
      } else {
        console.log('📝 Edit mode - skipping stock validation (stock already deducted)');
        
        // CHECK FOR QUANTITY REDUCTIONS - Need to restock returned items
        // SIMPLIFIED FIX: Match by unique ID (assigned when order was loaded for editing)
        if (newOrder.original_items && newOrder.original_items.length > 0) {
          const stockReturns = [];
          
          // Build a map of current items by their unique ID for O(1) lookup
          const currentItemsById = new Map();
          newOrder.order_items.forEach(item => {
            if (item.id) {
              currentItemsById.set(item.id, item);
            }
          });
          
          console.log('📊 Stock return check - Original items:', newOrder.original_items.length, ', Current items:', newOrder.order_items.length);
          
          for (const originalItem of newOrder.original_items) {
            // Match by unique ID that was assigned when order was loaded
            const currentItem = originalItem.id ? currentItemsById.get(originalItem.id) : null;
            
            const originalQty = originalItem.measurement_unit === 'per_square_meter' 
              ? parseFloat(originalItem.sqm || originalItem.quantity || 0)
              : parseFloat(originalItem.quantity || 0);
            
            let currentQty = 0;
            if (currentItem) {
              currentQty = currentItem.measurement_unit === 'per_square_meter'
                ? parseFloat(currentItem.sqm || currentItem.quantity || 0)
                : parseFloat(currentItem.quantity || 0);
            }
            
            console.log(`🔍 Comparing: ${originalItem.product_name} - Original Qty: ${originalQty}, Current Qty: ${currentQty}, Match found: ${!!currentItem}`);
            
            // Only flag for return if quantity was REDUCED (not if item still exists with same qty)
            if (currentQty < originalQty) {
              const returnQty = originalQty - currentQty;
              console.log(`⚠️ Stock return needed: ${returnQty} of ${originalItem.product_name}`);
              stockReturns.push({
                product_id: originalItem.product_id,
                product_name: originalItem.product_name,
                size: originalItem.size,
                color: originalItem.color,
                return_quantity: returnQty,
                unit: originalItem.measurement_unit === 'per_square_meter' ? 'sqm' : 'units'
              });
            }
          }
          
          // If there are items to return, show confirmation
          if (stockReturns.length > 0) {
            const returnMessage = stockReturns.map(item => 
              `• ${item.return_quantity.toFixed(2)} ${item.unit} of ${item.product_name} (${item.size || 'N/A'}, ${item.color || 'N/A'})`
            ).join('\n');
            
            const confirmed = window.confirm(
              `📦 STOCK RETURN REQUIRED\n\nThe following items will be returned to stock:\n\n${returnMessage}\n\nDo you want to proceed with the return and update the order?`
            );
            
            if (!confirmed) {
              return; // Cancel the update
            }
            
            // Process stock returns
            console.log('🔄 Processing stock returns:', stockReturns);
            for (const returnItem of stockReturns) {
              try {
                // Find the product to update stock
                const product = products.find(p => p.id === returnItem.product_id);
                if (product) {
                  // Find the variation
                  const variation = product.variations?.find(v => 
                    (v.size === returnItem.size || (!v.size && !returnItem.size)) &&
                    (v.color?.toLowerCase() === returnItem.color?.toLowerCase() || (!v.color && !returnItem.color))
                  );
                  
                  if (variation) {
                    // Calculate new stock (add returned quantity back)
                    const currentStock = product.measurement_unit === 'per_square_meter'
                      ? parseFloat(variation.square_meter_count || 0)
                      : parseFloat(variation.in_stock || 0);
                    const newStock = currentStock + returnItem.return_quantity;
                    
                    // Update the variation stock via API
                    const updatedVariations = product.variations.map(v => {
                      if (v === variation) {
                        if (product.measurement_unit === 'per_square_meter') {
                          return { ...v, square_meter_count: newStock };
                        } else {
                          return { ...v, in_stock: newStock };
                        }
                      }
                      return v;
                    });
                    
                    await axios.put(`${API}/api/products/${product.id}?skip_restock=true`, {
                      ...product,
                      variations: updatedVariations
                    }, {
                      headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
                    });
                    
                    console.log(`✅ Restocked ${returnItem.return_quantity} of ${returnItem.product_name}`);
                  }
                }
              } catch (restockError) {
                console.error('Error restocking item:', restockError);
              }
            }
            
            // Refresh products to show updated stock
            await loadProducts();
          }
        }
      }
      
      const orderData = {
        sale_type: newOrder.sale_type,
        customer_id: newOrder.customer_id === 'guest' ? null : newOrder.customer_id,
        // Guest customer temporary details (not saved to customers database)
        ...(newOrder.customer_id === 'guest' && {
          guest_customer_name: newOrder.guest_customer_name,
          guest_customer_phone: newOrder.guest_customer_phone,
          guest_customer_address: newOrder.guest_customer_address,
          guest_customer_postcode: newOrder.guest_customer_postcode
        }),
        items: newOrder.order_items.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          size: item.size,
          color: item.color,
          storage_location: item.storage_location,
          quantity: item.measurement_unit === 'per_square_meter' ? item.sqm : item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
          measurement_unit: item.measurement_unit,
          wide: item.wide, // Include width for square meter products
          length: item.length, // Include length for square meter products
          wide_unit: item.wide_unit, // Include width unit (meters/feet)
          length_unit: item.length_unit, // Include length unit (meters/feet)
          sqm: item.sqm, // Include calculated square meters
          has_vat: item.has_vat === false ? false : true // CRITICAL: Explicit boolean - default TRUE
        })),
        subtotal: newOrder.subtotal,
        vat_amount: newOrder.vat_amount,
        vat_rate: newOrder.vat_rate, // FIXED: Include VAT rate
        grand_total: newOrder.grand_total,
        discount: newOrder.discount, // FIXED: Include discount
        status: 'pending',
        // Service charges
        include_fitting: newOrder.include_fitting,
        fitting_charge: newOrder.fitting_charge,
        cut_piece: newOrder.cut_piece,
        cut_piece_charge: newOrder.cut_piece_charge,
        // FIXED: Include notes field for special instructions
        notes: newOrder.notes || null,
        // Old balance override - allows manual correction of customer's old balance
        old_balance_override: newOrder.old_balance_override,
        // Custom date for Zobayr (boss) - allows backdating orders
        ...(newOrder.custom_date && isManagerAccount() && {
          custom_date: newOrder.custom_date
        }),
        // Date handling for edit mode
        ...(newOrder.isEditMode && {
          original_date: newOrder.original_date,
          edit_date: newOrder.edit_date
        })
      };
      
      // Determine if we're creating or updating
      let response;
      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
      if (newOrder.isEditMode && newOrder.editOrderId) {
        // UPDATE existing order
        response = await axios.put(`${API}/api/orders/${newOrder.editOrderId}`, orderData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        // CREATE new order
        console.log('🔄 Creating new order with items:', orderData.items.length);
        response = await axios.post(`${API}/api/admin-portal/orders`, orderData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      
      if (response.status === 200) {
        const successMessage = newOrder.isEditMode ? 'Order updated successfully!' : 'Order created successfully!';
        alert(successMessage);
        
        // IMMEDIATE STOCK UPDATE: Event-driven sync - instant frontend refresh after order creation/update
        console.log(`🚀 IMMEDIATE UPDATE: Order ${newOrder.isEditMode ? 'updated' : 'created'} - instant stock level refresh`);
        setProductsLastUpdated(Date.now());
        setLastSyncTime(Date.now());
        
        // Instant reload for immediate stock accuracy (event-driven)
        await loadProducts();
        
        console.log('✅ Stock levels updated immediately - backend maintains 10-second consistency');
        
        // Show visual feedback that stock has been updated
        const orderItems = newOrder.order_items;
        if (orderItems.length > 0) {
          const updatedItemsInfo = orderItems.map(item => 
            `${item.product_name}: -${item.quantity} ${item.measurement_unit === 'per_square_meter' ? 'm²' : 'units'}`
          ).join(', ');
          console.log(`📊 Stock ${newOrder.isEditMode ? 'adjusted' : 'reduced'}: ${updatedItemsInfo}`);
        }
        
        closeCreateOrderModal();
        // Reload ALL data to ensure everything is up to date
        refreshAllData();
        
        // Also reload invoices since new orders may generate invoices
        try {
          const invRes = await axios.get(`${API}/api/invoices`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
          });
          setInvoices(invRes.data || []);
        } catch (e) {
          console.error('Error reloading invoices:', e);
        }
      }
    } catch (error) {
      console.error(`Error ${newOrder.isEditMode ? 'updating' : 'creating'} order:`, error);
      alert(`Error ${newOrder.isEditMode ? 'updating' : 'creating'} order: ` + (error.response?.data?.message || error.message));
    }
  };

  const closeCreateOrderModal = () => {
    setShowCreateOrderModal(false);
    setNewOrder({
      sale_type: 'retail',
      customer_id: '',
      order_items: [],
      subtotal: 0,
      vat_amount: 0,
      grand_total: 0,
      include_fitting: 'no',
      cut_piece: 'no',
      fitting_charge: 0,
      cut_piece_charge: 0,
      // Reset guest customer fields
      guest_customer_name: '',
      guest_customer_phone: '',
      guest_customer_address: '',
      guest_customer_postcode: '',
      // Reset edit mode flags
      isEditMode: false,
      editOrderId: null,
      original_date: null,
      edit_date: null,
      // Reset custom date (Zobayr backdating)
      custom_date: ''
    });
    setCurrentOrderItem({
      product_id: '',
      product_name: '',
      size: '',
      color: '',
      storage_location: '',
      quantity: 1,
      sqm: 1,
      unit_price: 0,
      total_price: 0,
      measurement_unit: 'size',
      has_vat: true // CRITICAL: Must include has_vat in reset - default ON for most items
    });
    setAvailableSizes([]);
    setAvailableColors([]);
  };

  // ============================================================================
  // 📊 PRODUCT SALES REPORT (Individual + Combined)
  //     Mirrors the Warehouse Breakdown Report format — includes date filtering,
  //     COGS, Gross Profit, Margin %, customer breakdown, and per-bill detail.
  // ============================================================================
  const buildProductSalesReport = (selectedProducts, isSingle) => {
    // ---- 1. Date range parsing (uses productReportFromDate / productReportToDate) ----
    const startDate = productReportFromDate ? new Date(productReportFromDate) : null;
    const endDate = productReportToDate ? new Date(productReportToDate + 'T23:59:59') : null;
    const dateRangeText = (startDate && endDate)
      ? (startDate.toLocaleDateString('en-GB') + ' to ' + endDate.toLocaleDateString('en-GB'))
      : (startDate ? ('From ' + startDate.toLocaleDateString('en-GB')) 
      : (endDate ? ('Until ' + endDate.toLocaleDateString('en-GB')) : 'All Time'));

    // ---- 2. Build cost lookup from current products DB ----
    const productCostLookup = {};
    (products || []).forEach(p => {
      const pName = p.name || p.title || 'Unknown';
      if (p.variations && Array.isArray(p.variations)) {
        p.variations.forEach(v => {
          const color = v.color || 'N/A';
          const size = v.size || 'N/A';
          const cost = parseFloat(v.cost_price || v.cost || v.wholesale || p.cost_price || p.cost || 0);
          productCostLookup[pName + '::' + color + '::' + size] = cost;
          if (productCostLookup[pName + '::' + color] == null) productCostLookup[pName + '::' + color] = cost;
          if (productCostLookup[pName] == null) productCostLookup[pName] = cost;
        });
      } else {
        productCostLookup[pName] = parseFloat(p.cost_price || p.cost || 0);
      }
    });

    // ---- 3. Filter orders (exclude returned/cancelled/deleted, apply date range) ----
    const allOrdersRaw = [...(fulfilledOrders || []), ...(orders || [])];
    const filteredOrders = allOrdersRaw.filter(order => {
      const status = (order.status || '').toLowerCase();
      if (status === 'returned' || status === 'cancelled' || status === 'deleted') return false;
      if (!startDate && !endDate) return true;
      const oDate = new Date(order.date);
      if (startDate && oDate < startDate) return false;
      if (endDate && oDate > endDate) return false;
      return true;
    });

    // ---- 4. Collect sales for each selected product ----
    // productData[productName] = { productName, sales:[...], totalQty, totalValue, totalCost, totalVAT, unit, customers:Set }
    const productData = {};
    selectedProducts.forEach(product => {
      productData[product.name] = {
        productName: product.name,
        productId: product.id,
        sales: [],
        totalQty: 0,
        totalValue: 0,
        totalCost: 0,
        totalVAT: 0,
        unit: product.measurement_unit === 'per_square_meter' ? 'm\u00B2' : 'units',
        customerSet: new Set(),
        currentStock: 0
      };
      // Compute current stock across all variations
      if (product.variations && Array.isArray(product.variations)) {
        product.variations.forEach(v => {
          const isSqm = product.measurement_unit === 'per_square_meter';
          const s = isSqm 
            ? parseFloat(v.square_meters_available || v.square_meters || v.stock_quantity || v.quantity || 0)
            : parseFloat(v.stock_quantity || v.quantity || 0);
          productData[product.name].currentStock += (s || 0);
        });
      } else {
        productData[product.name].currentStock = parseFloat(
          product.stock_quantity || product.square_meters_available || product.square_meters || product.quantity || 0
        ) || 0;
      }
    });

    filteredOrders.forEach(order => {
      const orderDateStr = order.date ? new Date(order.date).toLocaleDateString('en-GB') : 'N/A';
      const billNumber = order.id ? String(order.id).substring(0, 8).toUpperCase() : 'N/A';
      const orderVAT = parseFloat(order.vat_amount || 0);
      const hasVAT = orderVAT > 0;
      const orderSubtotal = (order.items || []).reduce((s, i) => s + parseFloat(i.total_price || i.amount || 0), 0);
      const customerName = order.customer_name || 'Guest Customer';
      const customerNumber = order.customer_number || 'GUEST';
      const paymentMethod = order.payment_method || 'N/A';

      (order.items || []).forEach(item => {
        // Match this item to any selected product (name OR product_id)
        const itemName = (item.product_name || '').trim().toLowerCase();
        const matched = selectedProducts.find(p =>
          item.product_id === p.id ||
          (itemName && itemName === (p.name || '').trim().toLowerCase())
        );
        if (!matched) return;

        const size = item.size || 'N/A';
        const color = item.color || 'N/A';
        const unitPrice = parseFloat(item.unit_price || item.price || 0);
        const totalPrice = parseFloat(item.total_price || item.amount || 0);

        let qty = 0;
        let unit = 'units';
        if (item.measurement_unit === 'per_square_meter') {
          qty = parseFloat(item.sqm || item.square_meters || 0);
          if (qty === 0 && unitPrice > 0) qty = totalPrice / unitPrice;
          unit = 'm\u00B2';
        } else {
          qty = parseFloat(item.quantity || item.qty || 0);
          if (qty === 0 && unitPrice > 0) qty = totalPrice / unitPrice;
          if (qty === 0) qty = 1;
        }

        // Prorate VAT to this line
        let itemVAT = 0;
        if (hasVAT && orderSubtotal > 0) {
          itemVAT = (totalPrice / orderSubtotal) * orderVAT;
        }

        // Cost lookup: prefer stored item cost, fall back to product DB cost
        let unitCost = parseFloat(item.unit_cost || item.cost_price || item.cost || 0);
        if (!unitCost) {
          unitCost = parseFloat(
            productCostLookup[matched.name + '::' + color + '::' + size] ??
            productCostLookup[matched.name + '::' + color] ??
            productCostLookup[matched.name] ?? 0
          );
        }
        const totalCost = unitCost * qty;

        const pd = productData[matched.name];
        pd.sales.push({
          customerName, customerNumber, billNumber, orderDate: orderDateStr,
          size, color, quantity: qty, unit, unitPrice, totalPrice,
          hasVAT, itemVAT, unitCost, totalCost, paymentMethod,
          rawDate: order.date ? new Date(order.date) : new Date(0)
        });
        pd.totalQty += qty;
        pd.totalValue += totalPrice;
        pd.totalCost += totalCost;
        pd.totalVAT += itemVAT;
        pd.unit = unit;
        pd.customerSet.add(customerName);
      });
    });

    // Sort sales per product by newest first
    Object.values(productData).forEach(pd => pd.sales.sort((a, b) => b.rawDate - a.rawDate));

    // ---- 5. Grand totals across all selected products ----
    let grandQty = 0, grandValue = 0, grandCost = 0, grandVAT = 0, grandTxns = 0;
    const allCustomerSet = new Set();
    Object.values(productData).forEach(pd => {
      grandQty += pd.totalQty;
      grandValue += pd.totalValue;
      grandCost += pd.totalCost;
      grandVAT += pd.totalVAT;
      grandTxns += pd.sales.length;
      pd.customerSet.forEach(c => allCustomerSet.add(c));
    });
    const grandProfit = grandValue - grandCost;
    const grandMargin = grandValue > 0 ? (grandProfit / grandValue) * 100 : 0;
    const profitColor = grandProfit >= 0 ? '#28a745' : '#dc3545';
    const profitLabel = grandProfit >= 0 ? 'PROFIT' : 'LOSS';

    // ---- 6. Build product summary table rows ----
    let summaryRowsHTML = '';
    const productNames = Object.keys(productData).sort();
    productNames.forEach(name => {
      const pd = productData[name];
      const profit = pd.totalValue - pd.totalCost;
      const pColor = profit >= 0 ? '#28a745' : '#dc3545';
      const pLabel = profit >= 0 ? 'Profit' : 'Loss';
      const margin = pd.totalValue > 0 ? ((profit / pd.totalValue) * 100).toFixed(1) : '0.0';
      summaryRowsHTML += '<tr>' +
        '<td><strong>' + name + '</strong></td>' +
        '<td style="text-align:center">' + pd.totalQty.toFixed(2) + ' ' + pd.unit + '</td>' +
        '<td style="text-align:right;color:#6c757d">\u00A3' + pd.totalCost.toFixed(2) + '</td>' +
        '<td style="text-align:right;font-weight:600">\u00A3' + pd.totalValue.toFixed(2) + '</td>' +
        '<td style="text-align:right">\u00A3' + pd.totalVAT.toFixed(2) + '</td>' +
        '<td style="text-align:right;font-weight:700;color:' + pColor + '">\u00A3' + Math.abs(profit).toFixed(2) + '<br><span style="font-size:10px;font-weight:400">' + pLabel + '</span></td>' +
        '<td style="text-align:center">' + margin + '%</td>' +
        '<td style="text-align:center">' + pd.customerSet.size + '</td>' +
        '<td style="text-align:center">' + pd.sales.length + '</td>' +
        '<td style="text-align:right;font-size:11px">' + pd.currentStock.toFixed(2) + ' ' + pd.unit + '</td>' +
        '</tr>';
    });

    // ---- 7. Build per-product customer breakdown blocks ----
    let productDetailHTML = '';
    productNames.forEach(name => {
      const pd = productData[name];
      const profit = pd.totalValue - pd.totalCost;
      const pColor = profit >= 0 ? '#28a745' : '#dc3545';
      const pLabel = profit >= 0 ? 'Profit' : 'Loss';
      const margin = pd.totalValue > 0 ? ((profit / pd.totalValue) * 100).toFixed(1) : '0.0';

      // Group this product's sales by customer
      const byCustomer = {};
      pd.sales.forEach(s => {
        const key = s.customerName + '__' + s.customerNumber;
        if (!byCustomer[key]) {
          byCustomer[key] = {
            customerName: s.customerName,
            customerNumber: s.customerNumber,
            sales: [],
            totalQty: 0, totalValue: 0, totalCost: 0, totalVAT: 0
          };
        }
        byCustomer[key].sales.push(s);
        byCustomer[key].totalQty += s.quantity;
        byCustomer[key].totalValue += s.totalPrice;
        byCustomer[key].totalCost += s.totalCost;
        byCustomer[key].totalVAT += s.itemVAT;
      });

      let customerBlocksHTML = '';
      Object.values(byCustomer)
        .sort((a, b) => b.totalValue - a.totalValue)
        .forEach(cg => {
          const cProfit = cg.totalValue - cg.totalCost;
          const cColor = cProfit >= 0 ? '#28a745' : '#dc3545';
          const cLabel = cProfit >= 0 ? 'Profit' : 'Loss';
          let rowsHTML = '';
          cg.sales.forEach(s => {
            const rowProfit = s.totalPrice - s.totalCost;
            const rowColor = rowProfit >= 0 ? '#28a745' : '#dc3545';
            rowsHTML += '<tr>' +
              '<td style="font-family:monospace;font-weight:600">' + s.billNumber + '</td>' +
              '<td>' + s.orderDate + '</td>' +
              '<td>' + (s.size !== 'N/A' ? s.size : '') + (s.color !== 'N/A' ? (s.size !== 'N/A' ? ' / ' : '') + s.color : '') + '</td>' +
              '<td style="text-align:center">' + s.quantity.toFixed(2) + ' ' + s.unit + '</td>' +
              '<td style="text-align:right">\u00A3' + s.unitPrice.toFixed(2) + '</td>' +
              '<td style="text-align:right;color:#6c757d">\u00A3' + s.unitCost.toFixed(2) + '</td>' +
              '<td style="text-align:right;font-weight:600">\u00A3' + s.totalPrice.toFixed(2) + '</td>' +
              '<td style="text-align:right;color:#6c757d">\u00A3' + s.totalCost.toFixed(2) + '</td>' +
              '<td style="text-align:right">' + (s.hasVAT ? '\u00A3' + s.itemVAT.toFixed(2) : '-') + '</td>' +
              '<td style="text-align:right;font-weight:700;color:' + rowColor + '">\u00A3' + Math.abs(rowProfit).toFixed(2) + '</td>' +
              '<td>' + s.paymentMethod + '</td>' +
              '</tr>';
          });
          customerBlocksHTML += '<div style="margin:15px 0;border:1px solid #dee2e6;border-radius:6px;overflow:hidden">' +
            '<div style="background:#1a365d;color:white;padding:10px 15px;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px">' +
            '<div style="font-size:13px;font-weight:600">\uD83D\uDC64 ' + cg.customerName + ' <span style="opacity:0.75;font-size:11px">(#' + cg.customerNumber + ')</span></div>' +
            '<div style="font-size:11px">' +
            'Qty: <strong>' + cg.totalQty.toFixed(2) + '</strong> &middot; ' +
            'Value: <strong>\u00A3' + cg.totalValue.toFixed(2) + '</strong> &middot; ' +
            'Cost: <strong>\u00A3' + cg.totalCost.toFixed(2) + '</strong> &middot; ' +
            'VAT: <strong>\u00A3' + cg.totalVAT.toFixed(2) + '</strong> &middot; ' +
            '<span style="color:' + (cProfit >= 0 ? '#90ee90' : '#ff6b6b') + ';font-weight:700">' + cLabel + ': \u00A3' + Math.abs(cProfit).toFixed(2) + '</span></div></div>' +
            '<table style="width:100%;border-collapse:collapse;font-size:11px">' +
            '<thead><tr style="background:#e9ecef">' +
            '<th style="padding:8px;text-align:left;border:1px solid #dee2e6">Bill #</th>' +
            '<th style="padding:8px;text-align:left;border:1px solid #dee2e6">Date</th>' +
            '<th style="padding:8px;text-align:left;border:1px solid #dee2e6">Size / Color</th>' +
            '<th style="padding:8px;text-align:center;border:1px solid #dee2e6">Qty</th>' +
            '<th style="padding:8px;text-align:right;border:1px solid #dee2e6">Unit Price</th>' +
            '<th style="padding:8px;text-align:right;border:1px solid #dee2e6">Unit Cost</th>' +
            '<th style="padding:8px;text-align:right;border:1px solid #dee2e6">Total</th>' +
            '<th style="padding:8px;text-align:right;border:1px solid #dee2e6">Total Cost</th>' +
            '<th style="padding:8px;text-align:right;border:1px solid #dee2e6">VAT</th>' +
            '<th style="padding:8px;text-align:right;border:1px solid #dee2e6">Profit</th>' +
            '<th style="padding:8px;text-align:left;border:1px solid #dee2e6">Payment</th>' +
            '</tr></thead><tbody>' + rowsHTML + '</tbody></table></div>';
        });

      productDetailHTML += '<div class="product-group" style="margin-bottom:30px;border:2px solid #2c5282;border-radius:8px;overflow:hidden;page-break-inside:avoid">' +
        '<div style="background:linear-gradient(135deg,#e9ecef 0%,#dee2e6 100%);padding:15px;border-bottom:2px solid #2c5282">' +
        '<h3 style="margin:0;color:#2c5282;font-size:18px">\uD83D\uDCE6 ' + name + '</h3>' +
        '<div style="font-size:12px;color:#555;margin-top:8px;line-height:1.6">' +
        'Total Sold: <strong>' + pd.totalQty.toFixed(2) + ' ' + pd.unit + '</strong> &middot; ' +
        'Total Cost: <strong>\u00A3' + pd.totalCost.toFixed(2) + '</strong> &middot; ' +
        'Total Value: <strong>\u00A3' + pd.totalValue.toFixed(2) + '</strong> &middot; ' +
        'VAT: <strong>\u00A3' + pd.totalVAT.toFixed(2) + '</strong> &middot; ' +
        '<span style="color:' + pColor + ';font-weight:700">' + pLabel + ': \u00A3' + Math.abs(profit).toFixed(2) + '</span> &middot; ' +
        'Margin: <strong>' + margin + '%</strong> &middot; ' +
        'Customers: <strong>' + pd.customerSet.size + '</strong> &middot; ' +
        'Transactions: <strong>' + pd.sales.length + '</strong> &middot; ' +
        'Available Stock: <strong>' + pd.currentStock.toFixed(2) + ' ' + pd.unit + '</strong>' +
        '</div></div>' +
        '<div style="padding:15px">' +
        (pd.sales.length === 0
          ? '<div style="text-align:center;padding:25px;color:#999;font-style:italic">No sales found for this product in the selected period.</div>'
          : customerBlocksHTML) +
        '</div></div>';
    });

    // ---- 8. Assemble the final HTML (mirrors Warehouse Breakdown Report layout) ----
    const reportTitle = isSingle
      ? ('Product Sales Report: ' + selectedProducts[0].name)
      : 'Combined Product Sales Report';
    const productsListText = selectedProducts.map(p => p.name).join(', ');

    const htmlContent = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + reportTitle + '</title>' +
      '<style>' +
      '@page{margin:0.5in;size:A4}' +
      'body{font-family:Arial,sans-serif;margin:0;padding:20px;color:#333;line-height:1.4;font-size:12px}' +
      '.header{background:linear-gradient(135deg,#1a365d 0%,#2c5282 100%);color:white;padding:25px;text-align:center;margin-bottom:30px;border-radius:8px}' +
      '.header h1{margin:0;font-size:28px}.header p{margin:5px 0 0 0;opacity:0.9}' +
      '.summary-section{background:#f8f9fa;border:3px solid #2c5282;border-radius:10px;padding:25px;margin-bottom:30px}' +
      '.summary-section h2{color:#2c5282;margin:0 0 20px 0;text-align:center;font-size:22px;border-bottom:2px solid #2c5282;padding-bottom:10px}' +
      '.summary-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}' +
      '.summary-box{background:white;border:2px solid #ddd;border-radius:8px;padding:20px;text-align:center}' +
      '.summary-label{font-size:14px;color:#666;margin-bottom:8px;font-weight:600}' +
      '.summary-amount{font-size:28px;font-weight:700}' +
      '.breakdown-section{margin-bottom:30px}' +
      '.breakdown-section h2{background:#2c5282;color:white;padding:15px 20px;margin:0 0 20px 0;border-radius:8px;font-size:20px}' +
      'table{width:100%;border-collapse:collapse;margin-bottom:20px;font-size:11px}' +
      'th{background:#e9ecef;padding:10px 8px;text-align:left;border:1px solid #dee2e6;font-weight:700;color:#333}' +
      'td{padding:8px;border:1px solid #dee2e6}' +
      'tr:nth-child(even){background:#f8f9fa}' +
      '.footer{margin-top:40px;padding-top:20px;border-top:2px solid #ddd;text-align:center;font-size:11px;color:#666}' +
      '@media print{.no-print{display:none}}' +
      '</style></head><body>' +

      '<div class="header"><h1>PLUSH LIVING LTD</h1>' +
      '<p>' + reportTitle + '</p>' +
      '<p style="margin-top:8px;font-size:13px">' + (isSingle ? 'Product' : 'Products') + ': <strong>' + productsListText + '</strong></p>' +
      '<p style="margin-top:6px;font-size:13px">Period: <strong>' + dateRangeText + '</strong></p>' +
      '<p style="font-size:11px;opacity:0.85">Generated: ' + new Date().toLocaleDateString('en-GB') + ' at ' + new Date().toLocaleTimeString('en-GB') + '</p></div>' +

      // FINANCIAL SUMMARY (same style as Warehouse Breakdown)
      '<div class="summary-section"><h2>FINANCIAL SUMMARY</h2>' +
      '<p style="color:#666;margin-bottom:15px;text-align:center">' + selectedProducts.length + ' product(s) &middot; ' + grandTxns + ' transaction(s) &middot; ' + allCustomerSet.size + ' unique customer(s) in ' + dateRangeText + '.</p>' +
      '<div class="summary-grid">' +
      '<div class="summary-box" style="border-color:#1a365d;grid-column:1/-1">' +
      '<div class="summary-label" style="font-size:16px">TOTAL SALES VALUE (ex-VAT line totals)</div>' +
      '<div class="summary-amount" style="color:#1a365d;font-size:38px">\u00A3' + grandValue.toFixed(2) + '</div>' +
      '<div style="font-size:12px;color:#666;margin-top:6px">Total stock sold: <strong>' + grandQty.toFixed(2) + '</strong> units/m\u00B2</div>' +
      '</div></div>' +
      '<div class="summary-grid" style="margin-top:15px">' +
      '<div class="summary-box" style="border-color:#6c757d"><div class="summary-label">COST OF GOODS SOLD</div>' +
      '<div class="summary-amount" style="color:#6c757d">\u00A3' + grandCost.toFixed(2) + '</div>' +
      '<div style="font-size:11px;color:#666;margin-top:5px">(cost_price \u00D7 qty)</div></div>' +
      '<div class="summary-box" style="border-color:' + profitColor + '"><div class="summary-label">GROSS ' + profitLabel + '</div>' +
      '<div class="summary-amount" style="color:' + profitColor + '">\u00A3' + Math.abs(grandProfit).toFixed(2) + '</div>' +
      '<div style="font-size:11px;color:#666;margin-top:5px">= Sales Value \u2212 COGS</div></div>' +
      '<div class="summary-box" style="border-color:#20c997"><div class="summary-label">MARGIN %</div>' +
      '<div class="summary-amount" style="color:#20c997">' + grandMargin.toFixed(1) + '%</div>' +
      '<div style="font-size:11px;color:#666;margin-top:5px">Profit / Sales Value</div></div>' +
      '</div>' +
      '<div class="summary-grid" style="margin-top:15px">' +
      '<div class="summary-box" style="border-color:#28a745"><div class="summary-label">TOTAL QTY SOLD</div>' +
      '<div class="summary-amount" style="color:#28a745">' + grandQty.toFixed(2) + '</div></div>' +
      '<div class="summary-box" style="border-color:#6f42c1"><div class="summary-label">VAT COLLECTED</div>' +
      '<div class="summary-amount" style="color:#6f42c1">\u00A3' + grandVAT.toFixed(2) + '</div></div>' +
      '<div class="summary-box" style="border-color:#17a2b8"><div class="summary-label">CUSTOMERS</div>' +
      '<div class="summary-amount" style="color:#17a2b8">' + allCustomerSet.size + '</div>' +
      '<div style="font-size:11px;color:#666;margin-top:5px">' + grandTxns + ' transactions</div></div>' +
      '</div></div>' +

      // PRODUCT SUMMARY TABLE
      '<div class="breakdown-section"><h2>PRODUCTS SOLD SUMMARY</h2>' +
      '<p style="color:#666;margin-bottom:20px">One row per selected product with sold qty, cost, sale value, VAT, profit, margin, customers, and current available stock.</p>' +
      '<table><thead><tr>' +
      '<th>Product Name</th>' +
      '<th style="text-align:center">Qty Sold</th>' +
      '<th style="text-align:right">Total Cost</th>' +
      '<th style="text-align:right">Sale Value</th>' +
      '<th style="text-align:right">VAT</th>' +
      '<th style="text-align:right">Profit / Loss</th>' +
      '<th style="text-align:center">Margin</th>' +
      '<th style="text-align:center">Customers</th>' +
      '<th style="text-align:center">Txns</th>' +
      '<th style="text-align:right">Avail. Stock</th>' +
      '</tr></thead>' +
      '<tbody>' + (summaryRowsHTML || '<tr><td colspan="10" style="text-align:center;padding:20px;color:#999">No sales in this period.</td></tr>') + '</tbody>' +
      '<tfoot><tr style="background:#1a365d;color:white;font-weight:700">' +
      '<td style="padding:12px">GRAND TOTAL</td>' +
      '<td style="text-align:center;padding:12px">' + grandQty.toFixed(2) + '</td>' +
      '<td style="text-align:right;padding:12px">\u00A3' + grandCost.toFixed(2) + '</td>' +
      '<td style="text-align:right;padding:12px;font-size:14px">\u00A3' + grandValue.toFixed(2) + '</td>' +
      '<td style="text-align:right;padding:12px">\u00A3' + grandVAT.toFixed(2) + '</td>' +
      '<td style="text-align:right;padding:12px;font-size:14px;color:' + (grandProfit >= 0 ? '#90ee90' : '#ff6b6b') + '">\u00A3' + Math.abs(grandProfit).toFixed(2) + '<br><span style="font-size:11px">' + profitLabel + '</span></td>' +
      '<td style="text-align:center;padding:12px">' + grandMargin.toFixed(1) + '%</td>' +
      '<td style="text-align:center;padding:12px">' + allCustomerSet.size + '</td>' +
      '<td style="text-align:center;padding:12px">' + grandTxns + '</td>' +
      '<td style="text-align:right;padding:12px">-</td>' +
      '</tr></tfoot></table></div>' +

      // PER-PRODUCT CUSTOMER BREAKDOWN
      '<div class="breakdown-section" style="page-break-before:always"><h2>IN-DEPTH PRODUCT BREAKDOWN</h2>' +
      '<p style="color:#666;margin-bottom:20px">Detailed customer-level breakdown per product \u2014 every bill, size/color, qty, unit price, unit cost, total, VAT, and profit.</p>' +
      productDetailHTML + '</div>' +

      '<div class="footer"><strong>PLUSH LIVING LTD</strong><br>Unit 30 Adderley Rd, Saltley, Birmingham B8 1AN<br>' +
      'Tel: 0121 840 1673 | Email: Plushlivingltd@outlook.com | Website: Plush-livingltd.co.uk</div>' +
      '<script>window.onload=function(){setTimeout(function(){window.print();},500);}</script>' +
      '</body></html>';

    return htmlContent;
  };

  // Generate Individual Product Sales Report - grouped by customer, mirrors Warehouse Breakdown
  const generateIndividualProductReport = (product) => {
    console.log('📊 Generating sales report for product:', product.name);
    try {
      const htmlContent = buildProductSalesReport([product], true);
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank', 'width=900,height=700');
      if (!printWindow) {
        alert('Please allow popups to view the report');
      }
    } catch (err) {
      console.error('❌ Error generating individual product report:', err);
      alert('Error generating report: ' + err.message);
    }
  };

  // Generate Multi-Product Sales Report - date-filtered, mirrors Warehouse Breakdown
  const generateMultiProductReport = (selectedProducts) => {
    if (!selectedProducts || selectedProducts.length === 0) {
      alert('Please select at least one product for the report');
      return;
    }
    console.log('📊 Generating combined sales report for', selectedProducts.length, 'products');
    try {
      const htmlContent = buildProductSalesReport(selectedProducts, false);
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank', 'width=900,height=700');
      if (!printWindow) {
        alert('Please allow popups to view the report');
      }
      // Clear selection after generating report
      setSelectedProductsForReport([]);
    } catch (err) {
      console.error('❌ Error generating multi product report:', err);
      alert('Error generating report: ' + err.message);
    }
  };

  // Generate Product Report function
  const generateProductReport = async () => {
    if (!reportDateFrom || !reportDateTo) {
      alert('Please select both from and to dates');
      return;
    }

    try {
      console.log('📊 Generating Product Report from', reportDateFrom, 'to', reportDateTo);
      
      // CRITICAL FIX: Load fresh products with full stock data using admin auth
      console.log('🔄 Loading fresh product data with admin authentication...');
      let productsWithStock = [];
      try {
        const productsResponse = await axios.get(`${API}/api/products`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` },
          timeout: 30000
        });
        productsWithStock = productsResponse.data || [];
        console.log(`✅ Loaded ${productsWithStock.length} products with full stock data`);
      } catch (error) {
        console.error('❌ Error loading products for report:', error);
        alert('Failed to load product data. Please try again.');
        return;
      }
      
      // Load fulfilled orders if not available
      let allOrdersData = [];
      
      if (!fulfilledOrders || fulfilledOrders.length === 0) {
        console.log('⚠️ No fulfilled orders available, loading them now...');
        try {
          const response = await axios.get(`${API}/api/orders/fulfilled`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
          });
          const loadedOrders = response.data || [];
          console.log(`📦 Loaded ${loadedOrders.length} fulfilled orders for product report`);
          allOrdersData = [...orders, ...loadedOrders];
        } catch (error) {
          console.error('Error loading fulfilled orders:', error);
          allOrdersData = [...orders]; // Use only pending orders if fulfilled load fails
        }
      } else {
        allOrdersData = [...orders, ...fulfilledOrders];
      }
      
      // Get all products and orders within date range
      const fromDate = new Date(reportDateFrom);
      const toDate = new Date(reportDateTo);
      toDate.setHours(23, 59, 59, 999); // Include entire end date

      // Filter orders within date range - EXCLUDE RETURNED AND CANCELLED ORDERS
      const ordersInRange = allOrdersData.filter(order => {
        const orderDate = new Date(order.date);
        
        // CRITICAL: Exclude returned and cancelled orders from product sales calculations
        const orderStatus = (order.status || '').toLowerCase();
        if (orderStatus === 'returned' || orderStatus === 'cancelled' || orderStatus === 'deleted') {
          console.log(`⚠️ Excluding ${orderStatus} order ${order.id} from product report`);
          return false;
        }
        
        return orderDate >= fromDate && orderDate <= toDate;
      });

      console.log(`Found ${ordersInRange.length} orders in date range (from ${allOrdersData.length} total orders)`);
      console.log('Sample orders for debugging:', ordersInRange.slice(0, 3).map(o => ({
        id: o.id,
        date: o.date,
        status: o.status,
        items_count: o.items?.length || 0
      })));
      console.log('📋 Processing products:', productsWithStock.length);
      
      if (ordersInRange.length > 0) {
        console.log('📊 Sample order items:', ordersInRange[0]?.items?.map(item => ({
          product_name: item.product_name,
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price
        })) || []);
      }

      // Initialize report data
      const productReportList = [];
      let totalSalesValue = 0;
      let totalStockValue = 0;
      let totalVariations = 0;

      // Process each product and its variations individually
      productsWithStock.forEach(product => {
        const isSquareMeter = product.measurement_unit === 'per_square_meter';
        console.log(`Processing product: ${product.name} (${isSquareMeter ? 'square meter' : 'size-based'}) with ${product.variations?.length || 0} variations`);
        
        if (product.variations && product.variations.length > 0) {
          // Process each variation as a separate row
          product.variations.forEach(variation => {
            totalVariations++;
            
            // UNIVERSAL STOCK LOOKUP - Check all possible stock field names based on measurement unit
            let variationStock = 0;
            if (isSquareMeter) {
              // For square meter products, check: square_meters_available, square_meters, stock_quantity, quantity
              variationStock = parseFloat(
                variation.square_meters_available || 
                variation.square_meters || 
                variation.stock_quantity || 
                variation.quantity
              ) || 0;
            } else {
              // For size-based products, check: stock_quantity, quantity, square_meters_available
              variationStock = parseFloat(
                variation.stock_quantity || 
                variation.quantity ||
                variation.square_meters_available
              ) || 0;
            }
            
            const variationSize = variation.size || 'Default Size';
            const variationColor = variation.color || 'Default Color';
            
            console.log(`  → Variation: ${variationSize} - ${variationColor}, Stock: ${variationStock} ${isSquareMeter ? 'm²' : 'units'}`);
            
            // Get pricing from variation or product
            const retailPrice = parseFloat(variation.retail_price || product.retail_price) || 0;
            const wholesalePrice = parseFloat(variation.wholesale_price || product.wholesale_price) || 0;
            const fitterPrice = parseFloat(variation.fitter_price || product.fitter_price) || 0;
            const costPrice = parseFloat(variation.cost_price || variation.cost || product.cost) || 0;

            // Initialize variation report entry
            const reportEntry = {
              productName: product.name,
              variationName: `${variationSize}${variationColor && variationColor !== 'Default Color' ? ` - ${variationColor}` : ''}`,
              fullName: `${product.name} (${variationSize}${variationColor && variationColor !== 'Default Color' ? ` - ${variationColor}` : ''})`,
              quantitySold: 0,
              availableStock: variationStock,
              unit: isSquareMeter ? 'm²' : 'units',
              restockDate: variation.restock_date || product.restock_date || null,
              retailSalesValue: 0,
              wholesaleSalesValue: 0,
              fitterSalesValue: 0,
              costValue: 0,
              stockRetailValue: variationStock * retailPrice,
              stockWholesaleValue: variationStock * wholesalePrice,
              stockFitterValue: variationStock * fitterPrice,
              isVariation: true,
              productId: product.id,
              variationId: variation.id || `${product.id}-${variationSize}-${variationColor}`
            };

            // UNIVERSAL SALES MATCHING - Calculate sales from orders for this specific variation
            ordersInRange.forEach(order => {
              if (order.items && order.items.length > 0) {
                order.items.forEach(item => {
                  // ENHANCED MATCHING LOGIC with multiple fallback strategies
                  const itemName = (item.product_name || '').trim().toLowerCase();
                  const productName = (product.name || '').trim().toLowerCase();
                  const itemColor = (item.color || '').trim().toLowerCase();
                  const variationColorLower = (variationColor || '').trim().toLowerCase();
                  const itemSize = (item.size || '').trim().toLowerCase();
                  const variationSizeLower = (variationSize || '').trim().toLowerCase();
                  
                  // Product name matching with multiple strategies
                  const matchesProduct = (
                    itemName === productName || 
                    item.product_id === product.id ||
                    itemName.includes(productName) ||
                    productName.includes(itemName)
                  );
                  
                  // Size matching with fallbacks
                  const matchesSize = (
                    !item.size || 
                    itemSize === variationSizeLower || 
                    variationSizeLower === 'default size' ||
                    itemSize.includes(variationSizeLower) ||
                    variationSizeLower.includes(itemSize)
                  );
                  
                  // Color matching with fallbacks
                  const matchesColor = (
                    !item.color || 
                    itemColor === variationColorLower || 
                    variationColorLower === 'default color' ||
                    itemColor.includes(variationColorLower) ||
                    variationColorLower.includes(itemColor)
                  );
                  
                  if (matchesProduct && matchesSize && matchesColor) {
                    const quantity = parseFloat(item.quantity) || 0;
                    const unitPrice = parseFloat(item.unit_price) || 0;
                    // Get customer type from order sale_type or customer_type
                    const customerType = order.sale_type || order.customer_type || 'retail';

                    console.log(`✅ SALE MATCH: ${product.name} - ${variationSize} - ${variationColor} | Qty: ${quantity} ${isSquareMeter ? 'm²' : 'units'} | Price: £${unitPrice} | Type: ${customerType}`);

                    reportEntry.quantitySold += quantity;
                    reportEntry.costValue += quantity * costPrice;

                    // Categorize sales by customer type
                    if (customerType === 'wholesale') {
                      reportEntry.wholesaleSalesValue += quantity * unitPrice;
                    } else if (customerType === 'fitter') {
                      reportEntry.fitterSalesValue += quantity * unitPrice;
                    } else {
                      reportEntry.retailSalesValue += quantity * unitPrice;
                    }

                    totalSalesValue += quantity * unitPrice;
                  }
                });
              }
            });

            totalStockValue += reportEntry.stockRetailValue;
            productReportList.push(reportEntry);
          });
        } else {
          // Product has no variations, treat as single item
          totalVariations++;
          
          // UNIVERSAL STOCK LOOKUP for products without variations
          let productStock = 0;
          if (isSquareMeter) {
            productStock = parseFloat(
              product.square_meters_available || 
              product.square_meters || 
              product.stock_quantity || 
              product.quantity
            ) || 0;
          } else {
            productStock = parseFloat(
              product.stock_quantity || 
              product.quantity ||
              product.square_meters_available
            ) || 0;
          }
          
          console.log(`  → No variations, treating as single item with stock: ${productStock} ${isSquareMeter ? 'm²' : 'units'}`);
          
          const retailPrice = parseFloat(product.retail_price) || 0;
          const wholesalePrice = parseFloat(product.wholesale_price) || 0;
          const fitterPrice = parseFloat(product.fitter_price) || 0;
          const costPrice = parseFloat(product.cost) || 0;

          const reportEntry = {
            productName: product.name,
            variationName: 'Standard',
            fullName: product.name,
            quantitySold: 0,
            availableStock: productStock,
            unit: isSquareMeter ? 'm²' : 'units',
            restockDate: product.restock_date || null,
            retailSalesValue: 0,
            wholesaleSalesValue: 0,
            fitterSalesValue: 0,
            costValue: 0,
            stockRetailValue: productStock * retailPrice,
            stockWholesaleValue: productStock * wholesalePrice,
            stockFitterValue: productStock * fitterPrice,
            isVariation: false,
            productId: product.id
          };

          // UNIVERSAL SALES MATCHING for products without variations
          ordersInRange.forEach(order => {
            if (order.items && order.items.length > 0) {
              order.items.forEach(item => {
                // Enhanced product matching with multiple strategies
                const itemName = (item.product_name || '').trim().toLowerCase();
                const productName = (product.name || '').trim().toLowerCase();
                const matchesProduct = (
                  itemName === productName || 
                  item.product_id === product.id ||
                  itemName.includes(productName) ||
                  productName.includes(itemName)
                );
                                     
                if (matchesProduct) {
                  const quantity = parseFloat(item.quantity) || 0;
                  const unitPrice = parseFloat(item.unit_price) || 0;
                  const customerType = order.sale_type || order.customer_type || 'retail';

                  console.log(`✅ SALE MATCH (no variations): ${product.name} | Qty: ${quantity} ${isSquareMeter ? 'm²' : 'units'} | Price: £${unitPrice} | Type: ${customerType}`);

                  reportEntry.quantitySold += quantity;
                  reportEntry.costValue += quantity * costPrice;

                  if (customerType === 'wholesale') {
                    reportEntry.wholesaleSalesValue += quantity * unitPrice;
                  } else if (customerType === 'fitter') {
                    reportEntry.fitterSalesValue += quantity * unitPrice;
                  } else {
                    reportEntry.retailSalesValue += quantity * unitPrice;
                  }

                  totalSalesValue += quantity * unitPrice;
                }
              });
            }
          });

          totalStockValue += reportEntry.stockRetailValue;
          productReportList.push(reportEntry);
        }
      });

      // Group by product name and sort
      const groupedProducts = [];
      const productGroups = new Map();

      productReportList.forEach(entry => {
        if (!productGroups.has(entry.productName)) {
          productGroups.set(entry.productName, []);
        }
        productGroups.get(entry.productName).push(entry);
      });

      // Convert to final format with product headers and variations
      productGroups.forEach((variations, productName) => {
        // Sort variations by sales value within each product group
        variations.sort((a, b) => {
          const totalA = a.retailSalesValue + a.wholesaleSalesValue + a.fitterSalesValue;
          const totalB = b.retailSalesValue + b.wholesaleSalesValue + b.fitterSalesValue;
          return totalB - totalA;
        });

        variations.forEach(variation => {
          groupedProducts.push(variation);
        });
      });

      // Calculate summary data
      const summary = {
        totalProducts: productsWithStock.length,
        totalVariations: totalVariations,
        productsSold: productReportList.filter(p => p.quantitySold > 0).length,
        totalSalesValue,
        totalStockValue
      };

      console.log('📈 Product Report Summary:', summary);
      console.log('📊 Total variations processed:', totalVariations);
      console.log('📋 Total report entries generated:', productReportList.length);
      console.log('📦 Grouped products count:', groupedProducts.length);

      // Set product report data with both products list and summary
      setProductReportData({
        products: groupedProducts,
        summary: summary
      });
    } catch (error) {
      console.error('❌ Error generating product report:', error);
      alert('Error generating product report');
    }
  };

  // Download Product Report as PDF
  const downloadProductReport = () => {
    if (!productReportData || !productReportData.products || productReportData.products.length === 0) {
      alert('No product report data to download. Please generate a report first.');
      return;
    }

    try {
      console.log('📥 Preparing Product Report PDF download...');
      
      // Generate HTML for PDF
      const reportHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Product Report - Plush Living Ltd</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #b8860b; padding-bottom: 20px; }
            .company-name { font-size: 28px; font-weight: bold; color: #b8860b; }
            .report-title { font-size: 22px; margin-top: 10px; color: #333; }
            .summary { background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 30px; }
            .summary-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #ddd; }
            .summary-row:last-child { border-bottom: none; font-weight: bold; font-size: 18px; }
            .label { font-weight: 600; }
            .value { color: #b8860b; font-weight: bold; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #0f1a35; color: white; padding: 12px; text-align: left; font-size: 12px; }
            td { padding: 10px; border-bottom: 1px solid #ddd; font-size: 11px; }
            tr:hover { background: #f8f9fa; }
            .footer { margin-top: 40px; text-align: center; color: #666; font-size: 12px; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-name">PLUSH LIVING LTD</div>
            <div class="report-title">Product Report</div>
            <div style="margin-top: 10px; color: #666;">Period: ${reportDateFrom} to ${reportDateTo}</div>
            <div style="margin-top: 5px; color: #666;">Generated: ${new Date().toLocaleString('en-GB')}</div>
          </div>

          <div class="summary">
            <h3 style="margin-top: 0; color: #b8860b;">Summary</h3>
            <div class="summary-row">
              <span class="label">Total Products:</span>
              <span class="value">${productReportData.summary?.totalProducts || 0}</span>
            </div>
            <div class="summary-row">
              <span class="label">Total Variations:</span>
              <span class="value">${productReportData.summary?.totalVariations || 0}</span>
            </div>
            <div class="summary-row">
              <span class="label">Products with Sales:</span>
              <span class="value">${productReportData.summary?.productsSold || 0}</span>
            </div>
            <div class="summary-row">
              <span class="label">Total Sales Value:</span>
              <span class="value">£${(productReportData.summary?.totalSalesValue || 0).toFixed(2)}</span>
            </div>
            <div class="summary-row">
              <span class="label">Total Stock Value:</span>
              <span class="value">£${(productReportData.summary?.totalStockValue || 0).toFixed(2)}</span>
            </div>
          </div>

          <h3 style="color: #0f1a35;">Product Details</h3>
          <table>
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Variation</th>
                <th>Qty Sold</th>
                <th>Available Stock</th>
                <th>Unit</th>
                <th>Restock Date</th>
              </tr>
            </thead>
            <tbody>
              ${productReportData.products.map(item => `
                <tr>
                  <td>${item.productName || 'N/A'}</td>
                  <td>${item.variationName || 'Standard'}</td>
                  <td>${(item.quantitySold || 0).toFixed(2)}</td>
                  <td>${(item.availableStock || 0).toFixed(2)}</td>
                  <td>${item.unit || ''}</td>
                  <td>${item.restockDate || 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="footer">
            <p>PLUSH LIVING LTD - Product Report</p>
            <p>This is a computer-generated report</p>
          </div>
        </body>
        </html>
      `;
      
      // Create blob and open in new window for printing/saving
      const blob = new Blob([reportHTML], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      const printWindow = window.open(url, '_blank', 'width=1000,height=800');
      if (printWindow) {
        printWindow.onload = function() {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
        console.log('✅ Product Report PDF opened for download');
      } else {
        alert('Please allow popups to download the PDF report');
      }
    } catch (error) {
      console.error('❌ Error downloading product report:', error);
      alert('Error downloading product report: ' + error.message);
    }
  };

  // Business Analysis Function
  const generateBusinessAnalysis = async () => {
    if (!businessAnalysisFromDate || !businessAnalysisToDate) {
      alert('Please select both from and to dates for business analysis');
      return;
    }

    // Show loading message
    const loadingAlert = document.createElement('div');
    loadingAlert.innerHTML = '📊 Generating business analysis, please wait...';
    loadingAlert.style.position = 'fixed';
    loadingAlert.style.top = '20px';
    loadingAlert.style.right = '20px';
    loadingAlert.style.background = '#28a745';
    loadingAlert.style.color = 'white';
    loadingAlert.style.padding = '15px';
    loadingAlert.style.borderRadius = '5px';
    loadingAlert.style.zIndex = '10000';
    loadingAlert.setAttribute('data-loading', 'business-analysis');
    document.body.appendChild(loadingAlert);

    try {
      const from = new Date(businessAnalysisFromDate + 'T00:00:00');
      const to = new Date(businessAnalysisToDate + 'T23:59:59');
      
      console.log('📊 Generating Business Analysis from', businessAnalysisFromDate, 'to', businessAnalysisToDate);
      
      // Use fulfilled orders data for business analysis (contains payment methods and cost data)
      console.log('📊 Business Analysis - Using fulfilled orders for cash/bank/credit calculation');
      console.log(`Available fulfilled orders: ${fulfilledOrders ? fulfilledOrders.length : 'undefined'}`);
      
      let ordersToAnalyze;
      
      if (!fulfilledOrders || fulfilledOrders.length === 0) {
        console.log('⚠️ No fulfilled orders available, trying to load them first...');
        
        // Try to load fulfilled orders if not available
        try {
          const response = await axios.get(`${API}/api/orders/fulfilled`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
          });
          
          const loadedOrders = response.data || [];
          console.log(`📦 Loaded ${loadedOrders.length} fulfilled orders for analysis`);
          
          if (loadedOrders.length === 0) {
            alert('No fulfilled orders data available for business analysis');
            return;
          }
          
          // Use the loaded orders for analysis
          ordersToAnalyze = loadedOrders;
        } catch (error) {
          console.error('Error loading fulfilled orders for analysis:', error);
          alert('Error loading fulfilled orders data for business analysis');
          return;
        }
      } else {
        ordersToAnalyze = fulfilledOrders;
      }
      
      // Ensure products array is loaded for cost price calculations
      console.log(`📦 Current products array length: ${products ? products.length : 'undefined'}`);
      
      if (!products || products.length === 0) {
        console.log('⚠️ No products available for cost calculation, loading them...');
        
        try {
          const productsResponse = await axios.get(`${API}/api/products`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
          });
          
          const loadedProducts = productsResponse.data || [];
          console.log(`📦 Loaded ${loadedProducts.length} products for cost calculation`);
          
          if (loadedProducts.length === 0) {
            console.log('⚠️ No products found in database for cost calculation');
          }
          
          // Use loaded products for cost calculation
          var productsForAnalysis = loadedProducts;
        } catch (error) {
          console.error('Error loading products for cost calculation:', error);
          var productsForAnalysis = [];
        }
      } else {
        var productsForAnalysis = products;
      }
      
      // Debug: Show available products for cost calculation
      console.log(`📦 Available products: ${productsForAnalysis.length}`);
      if (productsForAnalysis.length > 0) {
        console.log('📦 Sample products:', productsForAnalysis.slice(0, 3).map(p => ({ name: p.name, cost: p.cost, cost_price: p.cost_price })));
      }
      
      // Show sample order structure for debugging
      if (ordersToAnalyze.length > 0) {
        console.log('📋 Sample order structure:', ordersToAnalyze[0]);
        console.log('📋 Available fields:', Object.keys(ordersToAnalyze[0]));
      }

      // Filter fulfilled orders by date range - EXCLUDE RETURNED AND CANCELLED ORDERS
      const ordersInRange = ordersToAnalyze.filter(order => {
        const orderDate = new Date(order.date);
        
        if (isNaN(orderDate.getTime())) {
          console.log(`⚠️ Order ${order.id} has invalid date, skipping`);
          return false;
        }
        
        // CRITICAL: Exclude returned and cancelled orders from sales reports
        const orderStatus = (order.status || '').toLowerCase();
        if (orderStatus === 'returned' || orderStatus === 'cancelled' || orderStatus === 'deleted') {
          console.log(`⚠️ Excluding ${orderStatus} order ${order.id} from sales report`);
          return false;
        }
        
        const inRange = orderDate >= from && orderDate <= to;
        
        if (inRange) {
          console.log(`✅ Order ${order.id} (${orderDate.toLocaleDateString()}) - Status: ${orderStatus}, Payment: ${order.payment_method}, Total: £${order.grand_total}`);
        }
        
        return inRange;
      });

      console.log(`📊 Orders in date range: ${ordersInRange.length} out of ${ordersToAnalyze.length} total`);
      
      if (ordersInRange.length === 0) {
        const allDates = ordersToAnalyze
          .map(order => new Date(order.date).toLocaleDateString())
          .filter((date, index, arr) => arr.indexOf(date) === index)
          .sort();
        
        alert(`No fulfilled orders found in date range ${businessAnalysisFromDate} to ${businessAnalysisToDate}.\n\nAvailable order dates: ${allDates.join(', ')}`);
        return;
      }

      // CALCULATE SALES - CORRECT LOGIC BASED ON ACTUAL PAYMENT FIELDS
      let totalRevenue = 0;
      let cashSales = 0;
      let bankSales = 0;
      let creditSales = 0; // Outstanding/unpaid only
      
      console.log('💰 Calculating revenue using ACTUAL payment amounts (not payment_method)...');
      
      for (const order of ordersInRange) {
        const paymentMethod = (order.payment_method || '').toLowerCase();
        const grandTotal = parseFloat(order.grand_total || 0) || 0;
        
        // CORRECT CALCULATION: Use actual payment amount fields
        // Cash = cash_paid_amount (regardless of payment_method)
        const cashPaid = parseFloat(order.cash_paid_amount || 0) || 0;
        if (cashPaid > 0) {
          cashSales += cashPaid;
          console.log(`  💵 Cash received: ${order.customer_name} £${cashPaid.toFixed(2)} (order type: ${paymentMethod})`);
        }
        
        // Bank = bank_paid_amount (regardless of payment_method)
        const bankPaid = parseFloat(order.bank_paid_amount || 0) || 0;
        if (bankPaid > 0) {
          bankSales += bankPaid;
          console.log(`  🏦 Bank received: ${order.customer_name} £${bankPaid.toFixed(2)} (order type: ${paymentMethod})`);
        }
        
        // Credit Sales = ONLY unpaid balance on credit orders
        if (paymentMethod === 'credit') {
          const creditPaid = parseFloat(order.credit_paid_amount || 0) || 0;
          const totalPaid = cashPaid + bankPaid + creditPaid;
          const unpaid = Math.max(0, grandTotal - totalPaid);
          
          if (unpaid > 0) {
            creditSales += unpaid;
            console.log(`  💳 Credit outstanding: ${order.customer_name} Total: £${grandTotal.toFixed(2)}, Total Paid: £${totalPaid.toFixed(2)}, Outstanding: £${unpaid.toFixed(2)}`);
          } else {
            console.log(`  ✅ Credit fully paid: ${order.customer_name} Total: £${grandTotal.toFixed(2)}, Paid: £${totalPaid.toFixed(2)}`);
          }
        }
      }
      
      // Total revenue = Cash + Bank (actual money received, excluding unpaid credit)
      totalRevenue = cashSales + bankSales;
      
      console.log(`💰 Sales Summary (CORRECTED - by actual payment amounts):`);
      console.log(`   - Cash Sales: £${cashSales.toFixed(2)} (all cash_paid_amount)`);
      console.log(`   - Bank Sales: £${bankSales.toFixed(2)} (all bank_paid_amount)`);
      console.log(`   - Credit Sales (Outstanding): £${creditSales.toFixed(2)} (unpaid balance only)`);
      console.log(`   - Total Revenue (Cash + Bank): £${totalRevenue.toFixed(2)}`);

      // Calculate TOTAL COST PRICE for all fulfilled orders
      let totalCostPrice = 0;
      console.log('💰 Calculating cost for all fulfilled orders...');
      
      ordersInRange.forEach(order => {
        let orderCostPrice = 0;
        
        if (order.items && Array.isArray(order.items)) {
          order.items.forEach(orderItem => {
            const quantity = parseFloat(orderItem.quantity) || 0;
            let unitCostPrice = 0;
            
            // Find product
            const product = productsForAnalysis.find(p => 
              p.id === orderItem.product_id || 
              p.name === orderItem.product_name ||
              (p.name && orderItem.product_name && p.name.toLowerCase().trim() === orderItem.product_name.toLowerCase().trim())
            );
            
            if (product) {
              // Try variation cost first if item has size/color
              if (product.variations && product.variations.length > 0 && (orderItem.size || orderItem.color)) {
                const variation = product.variations.find(v => 
                  v.size === orderItem.size && v.color === orderItem.color
                );
                if (variation) {
                  unitCostPrice = parseFloat(variation.cost_price) || parseFloat(variation.cost) || 0;
                }
              }
              
              // If no variation cost, use product cost
              if (unitCostPrice === 0) {
                unitCostPrice = parseFloat(product.cost_price) || parseFloat(product.cost) || 0;
              }
            }
            
            const itemCost = quantity * unitCostPrice;
            orderCostPrice += itemCost;
          });
        }
        
        totalCostPrice += orderCostPrice;
        console.log(`  📦 ${order.customer_name}: Cost £${orderCostPrice.toFixed(2)}`);
      });
      
      console.log(`💰 Total Cost Price: £${totalCostPrice.toFixed(2)}`);
      
      // Calculate profit using ONLY cash and bank revenue (exclude unpaid credit)
      const grossProfit = totalRevenue - totalCostPrice;
      const grossProfitMargin = totalRevenue > 0 ? ((grossProfit / totalRevenue) * 100) : 0;
      
      // Net profit (assume 12% operating expenses on actual revenue received)
      const operatingExpenses = totalRevenue * 0.12;
      const netProfit = grossProfit - operatingExpenses;
      const netProfitMargin = totalRevenue > 0 ? ((netProfit / totalRevenue) * 100) : 0;

      console.log('💰 Business Analysis Summary:');
      console.log(`  Total Revenue (Cash + Bank only): £${totalRevenue.toFixed(2)}`);
      console.log(`  - Cash Sales: £${cashSales.toFixed(2)}`);
      console.log(`  - Bank Sales: £${bankSales.toFixed(2)}`);
      console.log(`  - Credit Sales (UNPAID - excluded from profit): £${creditSales.toFixed(2)}`);
      console.log(`  Total Cost Price: £${totalCostPrice.toFixed(2)}`);
      console.log(`  Gross Profit: £${grossProfit.toFixed(2)} (${grossProfitMargin.toFixed(2)}%)`);
      console.log(`  Operating Expenses (12%): £${operatingExpenses.toFixed(2)}`);
      console.log(`  Net Profit: £${netProfit.toFixed(2)} (${netProfitMargin.toFixed(2)}%)`);
      console.log(`  Orders Analyzed: ${ordersInRange.length}`);

      // Generate Business Analysis Report
      const reportHtml = `
        <div style="font-family: Arial, sans-serif; padding: 20px; max-width: 800px;">
          <h1 style="text-align: center; color: #333; margin-bottom: 30px;">Business Analysis Report</h1>
          <p style="text-align: center; color: #666; margin-bottom: 40px;">
            Period: ${businessAnalysisFromDate} to ${businessAnalysisToDate}
          </p>
          
          <div style="margin-bottom: 30px;">
            <h2 style="color: #007bff; border-bottom: 2px solid #007bff; padding-bottom: 10px;">Sales Summary</h2>
            <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin: 20px 0;">
              <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; text-align: center;">
                <h3 style="color: #28a745; margin: 0;">Cash Sales</h3>
                <p style="font-size: 24px; font-weight: bold; margin: 10px 0;">£${cashSales.toFixed(2)}</p>
              </div>
              <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; text-align: center;">
                <h3 style="color: #17a2b8; margin: 0;">Bank Sales</h3>
                <p style="font-size: 24px; font-weight: bold; margin: 10px 0;">£${bankSales.toFixed(2)}</p>
              </div>
              <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; text-align: center;">
                <h3 style="color: #ffc107; margin: 0;">Credit Sales</h3>
                <p style="font-size: 24px; font-weight: bold; margin: 10px 0;">£${creditSales.toFixed(2)}</p>
                <small style="color: #666;">(Outstanding/Unpaid)</small>
              </div>
            </div>
          </div>

          <div style="margin-bottom: 30px;">
            <h2 style="color: #007bff; border-bottom: 2px solid #007bff; padding-bottom: 10px;">Profit Analysis</h2>
            <div style="background: #f8f9fa; padding: 20px; border-radius: 8px;">
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
                <div>
                  <p><strong>Total Revenue (Cash + Bank):</strong> £${totalRevenue.toFixed(2)}</p>
                  <p><strong>Total Cost Price:</strong> £${totalCostPrice.toFixed(2)}</p>
                  <p><strong>Gross Profit:</strong> £${grossProfit.toFixed(2)}</p>
                  <p><strong>Operating Expenses (12%):</strong> £${operatingExpenses.toFixed(2)}</p>
                  <p><strong>Net Profit:</strong> £${netProfit.toFixed(2)}</p>
                </div>
                <div>
                  <p><strong>Gross Profit Margin:</strong> ${grossProfitMargin.toFixed(2)}%</p>
                  <p><strong>Net Profit Margin:</strong> ${netProfitMargin.toFixed(2)}%</p>
                  <p><strong>Orders Analyzed:</strong> ${ordersInRange.length}</p>
                  <p><strong>Outstanding Credit:</strong> £${creditSales.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>

          <div style="margin-top: 30px; padding: 15px; background: #e9ecef; border-radius: 8px;">
            <h3 style="color: #495057; margin-top: 0;">Important Notes</h3>
            <ul style="margin: 0; color: #666;">
              <li>Revenue includes only Cash and Bank payments received (actual money)</li>
              <li>Credit Sales (£${creditSales.toFixed(2)}) are outstanding/unpaid and excluded from profit calculations</li>
              <li>Cost prices calculated using product cost data</li>
              <li>Generated on: ${new Date().toLocaleString()}</li>
            </ul>
          </div>
        </div>
      `;

      // Open report in new window for printing/saving
      const reportWindow = window.open('', '_blank');
      
      if (!reportWindow) {
        alert('Please allow popups for this site to view the Business Analysis report');
        if (loadingAlert && document.body.contains(loadingAlert)) {
          document.body.removeChild(loadingAlert);
        }
        setShowBusinessAnalysisModal(false);
        return;
      }
      
      reportWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Business Analysis Report - ${businessAnalysisFromDate} to ${businessAnalysisToDate}</title>
            <style>
              @media print {
                body { margin: 0; }
                .no-print { display: none; }
              }
            </style>
          </head>
          <body>
            ${reportHtml}
            <div class="no-print" style="text-align: center; margin-top: 30px;">
              <button onclick="window.print()" style="background: #007bff; color: white; border: none; padding: 10px 20px; border-radius: 5px; cursor: pointer;">Print Report</button>
              <button onclick="window.close()" style="background: #6c757d; color: white; border: none; padding: 10px 20px; border-radius: 5px; cursor: pointer; margin-left: 10px;">Close</button>
            </div>
          </body>
        </html>
      `);
      reportWindow.document.close();
      
      // Close the modal and remove loading message
      setShowBusinessAnalysisModal(false);
      if (loadingAlert && document.body.contains(loadingAlert)) {
        document.body.removeChild(loadingAlert);
      }
      
    } catch (error) {
      console.error('Error generating business analysis:', error);
      alert('Error generating business analysis report');
    } finally {
      // Remove loading message if it exists
      const existingLoading = document.querySelector('[data-loading="business-analysis"]');
      if (existingLoading) {
        document.body.removeChild(existingLoading);
      }
    }
  };

  // PDF Generation function with BUKHARA COLLECTION format - Elegant High-End Design
  const handleGeneratePDF = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    // Get selected products from the actual products array
    const selectedProductsData = [];
    const productCheckboxes = e.target.querySelectorAll('input[name="products"]:checked');
    productCheckboxes.forEach(cb => {
      const product = products.find(p => p.id === cb.value);
      if (product) {
        selectedProductsData.push(product);
      }
    });

    const priceType = formData.get('priceType');
    const includeRetail = priceType === 'retail' || priceType === 'both';
    const includeWholesale = priceType === 'wholesale' || priceType === 'both';

    if (selectedProductsData.length === 0) {
      alert('Please select at least one product to include in the catalogue.');
      return;
    }

    try {
      const currentDate = new Date().toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });

      console.log('🔍 CATALOG DEBUG: Generating PDF for products:', selectedProductsData.map(p => p.name));

      // Create elegant HTML content with large product images
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Plush Living - Luxury Catalogue</title>
          <style>
            @page { 
              margin: 0;
              size: A4;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: content-box !important;
              max-width: none !important;
              max-height: none !important;
              overflow: visible !important;
            }
            body { 
              font-family: 'Georgia', 'Times New Roman', serif;
              background: #000;
              color: #fff;
              overflow: visible !important;
              max-width: none !important;
              max-height: none !important;
            }
            
            /* Cover Page */
            .cover-page {
              height: 100vh;
              background: linear-gradient(135deg, #b8860b 0%, #1a1a1a 50%, #b8860b 100%);
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              text-align: center;
              position: relative;
              page-break-after: always;
            }
            .cover-overlay {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: radial-gradient(circle at center, rgba(212, 175, 55, 0.1) 0%, rgba(0,0,0,0.3) 70%);
            }
            .cover-content {
              z-index: 10;
              position: relative;
            }
            .luxury-title {
              font-size: 4.5rem;
              font-weight: 300;
              letter-spacing: 8px;
              color: #d4af37;
              text-transform: uppercase;
              margin-bottom: 1rem;
              text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
            }
            .luxury-subtitle {
              font-size: 2rem;
              font-weight: 300;
              letter-spacing: 4px;
              color: #fff;
              margin-bottom: 3rem;
              text-transform: uppercase;
            }
            .luxury-tagline {
              font-size: 1.2rem;
              font-style: italic;
              color: #d4af37;
              letter-spacing: 2px;
              margin-bottom: 4rem;
            }
            .cover-details {
              font-size: 1rem;
              color: #ccc;
              letter-spacing: 1px;
              line-height: 1.8;
            }
            .gold-line {
              width: 300px;
              height: 2px;
              background: linear-gradient(90deg, transparent, #d4af37, transparent);
              margin: 2rem auto;
            }
            
            /* Product Pages */
            .product-page {
              height: 100vh;
              background: linear-gradient(135deg, #1a1a1a 0%, #b8860b 100%);
              display: flex;
              flex-direction: column;
              position: relative;
              page-break-after: always;
              padding: 40px;
            }
            .product-header {
              text-align: center;
              margin-bottom: 30px;
              z-index: 10;
              position: relative;
            }
            .product-name {
              font-size: 2.5rem;
              font-weight: 300;
              color: #d4af37;
              letter-spacing: 3px;
              text-transform: uppercase;
              margin-bottom: 10px;
              text-shadow: 1px 1px 2px rgba(0,0,0,0.7);
            }
            .product-category {
              font-size: 1.2rem;
              color: #ccc;
              letter-spacing: 2px;
              text-transform: uppercase;
            }
            
            .product-content {
              display: flex;
              height: calc(100vh - 200px);
              gap: 40px;
              z-index: 10;
              position: relative;
            }
            .product-image-container {
              flex: 2;
              min-height: 600px !important;
              display: flex;
              align-items: center;
              justify-content: center;
              background: rgba(255,255,255,0.05);
              border: 1px solid #d4af37;
              border-radius: 10px;
              padding: 5px;
              position: relative;
              overflow: visible !important;
            }
            .product-image {
              object-fit: cover;
              border-radius: 5px;
              box-shadow: 0 10px 30px rgba(212, 175, 55, 0.3);
            }
            .no-image-placeholder {
              width: 100%;
              height: 100%;
              display: flex;
              align-items: center;
              justify-content: center;
              background: linear-gradient(45deg, #333, #555);
              color: #d4af37;
              font-size: 1.5rem;
              letter-spacing: 2px;
              text-transform: uppercase;
            }
            
            .product-details {
              flex: 1;
              padding: 40px 20px;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
            }
            .details-section {
              background: rgba(255,255,255,0.05);
              border: 1px solid #d4af37;
              border-radius: 10px;
              padding: 30px;
              margin-bottom: 20px;
            }
            .details-title {
              font-size: 1.4rem;
              color: #d4af37;
              margin-bottom: 20px;
              text-transform: uppercase;
              letter-spacing: 1px;
              border-bottom: 1px solid #d4af37;
              padding-bottom: 10px;
            }
            .detail-item {
              display: flex;
              justify-content: space-between;
              margin-bottom: 15px;
              font-size: 1.1rem;
            }
            .detail-label {
              color: #ccc;
              font-weight: 300;
            }
            .detail-value {
              color: #fff;
              font-weight: 400;
            }
            .price-highlight {
              color: #d4af37 !important;
              font-size: 1.3rem !important;
              font-weight: bold !important;
            }
            
            /* Background decoration */
            .page-decoration {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-image: 
                radial-gradient(circle at 20% 80%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(212, 175, 55, 0.1) 0%, transparent 50%);
              pointer-events: none;
            }
            
            /* Back Cover */
            .back-cover {
              height: 100vh;
              background: linear-gradient(135deg, #1a1a1a 0%, #b8860b 100%);
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              text-align: center;
              position: relative;
              padding: 80px;
            }
            .contact-section {
              background: rgba(255,255,255,0.05);
              border: 2px solid #d4af37;
              border-radius: 20px;
              padding: 60px;
              max-width: 600px;
            }
            .contact-title {
              font-size: 2.5rem;
              color: #d4af37;
              margin-bottom: 40px;
              text-transform: uppercase;
              letter-spacing: 3px;
            }
            .contact-info {
              font-size: 1.2rem;
              color: #fff;
              margin-bottom: 20px;
              letter-spacing: 1px;
            }
            .contact-highlight {
              color: #d4af37;
              font-weight: bold;
            }
            
            @media print {
              body { background: #000 !important; }
              .product-page, .cover-page, .back-cover { 
                background: #000 !important; 
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <!-- Cover Page -->
          <div class="cover-page">
            <div class="cover-overlay"></div>
            <div class="cover-content">
              <div class="luxury-title">PLUSH LIVING</div>
              <div class="luxury-subtitle">Luxury Collection</div>
              <div class="gold-line"></div>
              <div class="luxury-tagline">"Where Elegance Meets Excellence"</div>
              <div class="cover-details">
                Making your house a home<br>
                Curated Collection ${currentDate}<br>
                ${selectedProductsData.length} Exclusive Products
              </div>
            </div>
          </div>

          ${selectedProductsData.map(product => `
            <!-- Product Page for ${product.name || 'Unnamed Product'} -->
            <div class="product-page" style="width: 100%; height: 100vh; margin: 0; padding: 0; position: relative; overflow: visible !important; max-width: none !important; max-height: none !important;">
              
              <!-- Large Product Image Taking Most of the Page -->
              <div style="width: 8000% !important; height: 16000% !important; margin: 0; padding: 0; box-sizing: content-box !important; overflow: visible !important; max-width: none !important; max-height: none !important; position: relative; z-index: 999;">
                ${product.image ? `
                  <img src="${product.image}" alt="${product.name}" style="width: 100% !important; height: 100% !important; min-width: 100% !important; min-height: 100% !important; max-width: none !important; max-height: none !important; object-fit: cover !important; border: none; border-radius: 0; box-shadow: none; overflow: visible !important; position: relative; z-index: 1000;" />
                ` : `
                  <div style="width: 100%; height: 100%; background: #f5f5f5; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 24px; color: #999;">
                    No Image Available
                  </div>
                `}
              </div>
              
              <!-- Product Info at Bottom -->
              <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 20%; background: linear-gradient(135deg, #2c2c2c 0%, #1a1a1a 100%); padding: 20px; box-sizing: border-box; display: flex; align-items: center; justify-content: space-between;">
                <div style="color: #d4af37; font-size: 48px; font-weight: bold;">${product.name || 'Luxury Product'}</div>
                <div style="color: #ffffff; font-size: 32px;">${product.category || 'Premium Collection'}</div>
              </div>
              
            </div>
          `).join('')}

          <!-- Back Cover -->
          <div class="back-cover">
            <div class="page-decoration"></div>
            <div class="contact-section">
              <div class="contact-title">Contact Us</div>
              <div class="gold-line"></div>
              <div class="contact-info">
                <strong class="contact-highlight">PLUSH LIVING LTD</strong>
              </div>
              <div class="contact-info">
                Unit 30, 197 Adderley Road<br>
                Birmingham, B8 1AN<br>
                United Kingdom
              </div>
              <div class="contact-info" style="margin-top: 30px;">
                <strong>Phone:</strong> <span class="contact-highlight">0121 649 1673</span><br>
                <strong>Email:</strong> <span class="contact-highlight">Plushlivingltd@outlook.com</span><br>
                <strong>Website:</strong> <span class="contact-highlight">Plush-livingltd.co.uk</span>
              </div>
              <div class="gold-line" style="margin-top: 40px;"></div>
              <div style="font-style: italic; color: #d4af37; font-size: 1.1rem; margin-top: 20px;">
                "Experience Luxury. Define Excellence."
              </div>
            </div>
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.onafterprint = function() {
                  window.close();
                };
              }, 2000);
            }
          </script>
        </body>
        </html>
      `;

      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);

      const newWindow = window.open(url, '_blank', 'width=1200,height=800');
      if (!newWindow) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `plush_luxury_catalogue_${new Date().toISOString().slice(0, 10)}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        alert('Luxury catalogue downloaded as HTML. Open in your browser and use Ctrl+P to save as PDF.');
      } else {
        alert('Print dialog opened. Use "Save as PDF" or "Print to PDF" option to save the luxury catalogue.');
      }
      
      URL.revokeObjectURL(url);
      setShowCreateCatalogModal(false);

    } catch (error) {
      console.error('Error generating catalogue:', error);
      alert('Error generating catalogue: ' + error.message);
    }
  };

  // Load products on mount and load categories for admin + PRELOAD ALL DATA FOR INSTANT SWITCHING
  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) {
      setIsAdminLoggedIn(true);
      loadCategories(); // Load categories for admin
    }
    
    // CRITICAL FIX: Load products immediately for public users to prevent eternal loading screen
    // Public users need products loaded on initial mount for homepage display
    loadStoreLocations(); // Store locations for home page (small dataset)
    loadProductsForPublic(); // Load products for public users immediately
    
    console.log('🚀 INSTANT LOADING: Essential data and products loaded for public view');
  }, []);

  // Reload products when admin logs in
  useEffect(() => {
    // Load products ONCE when admin status is determined
    if (isAdminLoggedIn) {
      loadProducts(); // Admin products (all products, no limit)
      loadCategories();
    } else {
      loadFeaturedProductsLazy(); // Public featured products only (8 products)
    }
  }, [isAdminLoggedIn]);

  // EVENT-DRIVEN FRONTEND SYNC: Immediate updates after changes, backend maintains 10-second consistency
  const [eventDrivenSyncEnabled, setEventDrivenSyncEnabled] = useState(true);
  const [lastSyncTime, setLastSyncTime] = useState(Date.now());
  
  useEffect(() => {
    // Frontend: NO auto-refresh - only immediate updates after changes
    // Backend: 10-second consistency check happens server-side
    
    if (eventDrivenSyncEnabled && isAdminLoggedIn) {
      console.log('✅ Event-driven frontend sync enabled - immediate updates after changes');
      console.log('🔄 Backend maintains 10-second consistency checks automatically');
      
      // No interval for frontend - all updates are event-driven (immediate after changes)
      return () => {
        console.log('🛑 Event-driven sync disabled');
      };
    } else if (eventDrivenSyncEnabled && !isAdminLoggedIn) {
      // For public users, occasional sync for new products/offers (less frequent)
      const interval = setInterval(async () => {
        console.log('🔄 Public sync: Updating product catalog...');
        try {
          await loadProductsForPublic();
          setLastSyncTime(Date.now());
        } catch (error) {
          console.error('❌ Public sync error:', error);
        }
      }, 60000); // Public users get updates every 60 seconds (less frequent)

      return () => clearInterval(interval);
    }
  }, [eventDrivenSyncEnabled, isAdminLoggedIn, loadProductsForPublic]);

  // Auto-enable event-driven sync when Create Order modal is open
  useEffect(() => {
    if (showCreateOrderModal) {
      console.log('📝 Order creation active - ensuring event-driven sync and loading products');
      setEventDrivenSyncEnabled(true);
      
      // CRITICAL FIX: Always reload products when order modal opens to ensure admin gets all 29 products
      // Previously only loaded if products.length === 0, causing issue where public 20 products were shown
      console.log('📝 Reloading ALL products for order creation (admin needs full catalog)...');
      loadProducts();
    } else {
      console.log('📝 Order creation closed - maintaining event-driven sync for admin');
      // Keep sync enabled for admin users, only disable for public users
      if (!isAdminLoggedIn) {
        setEventDrivenSyncEnabled(false);
      }
    }
  }, [showCreateOrderModal, loadProducts, isAdminLoggedIn]);

  // FIXED: Load store locations with proper error handling and correct API URL
  const loadStoreLocations = async () => {
    try {
      console.log('🚀 Loading store locations for home page');
      
      // Use the correct backend URL
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://plush-living.co.uk';
      const apiUrl = `${backendUrl}/api/store-locations`;
      
      console.log('📡 Store locations API URL:', apiUrl);
      
      const response = await axios.get(apiUrl, { timeout: 30000 });
      console.log('Store locations response:', response.data);
      
      if (Array.isArray(response.data)) {
        setStoreLocations(response.data);
        console.log(`✅ Loaded ${response.data.length} store locations`);
      } else if (response.data.locations && Array.isArray(response.data.locations)) {
        setStoreLocations(response.data.locations);
        console.log(`✅ Loaded ${response.data.locations.length} store locations from nested data`);
      } else {
        console.warn('Unexpected store locations response format:', response.data);
        setStoreLocations([]);
      }
    } catch (error) {
      console.error('Error loading store locations:', error);
      console.error('Error details:', error.response?.data || error.message);
      setStoreLocations([]);
    }
  };

  // Load featured products function - optimized for speed
  const loadFeaturedProducts = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const headers = adminToken ? { 'Authorization': `Bearer ${adminToken}` } : {};
      
      const response = await axios.get(`${API}/api/products?limit=8&skip=0`, {
        headers: headers
      });
      
      if (response.data && Array.isArray(response.data)) {
        setFeaturedProducts(response.data);
      }
    } catch (error) {
      console.error('Error loading featured products:', error);
    }
  };

  // FIXED: Robust featured products loading with proper API URL handling
  const loadFeaturedProductsLazy = async () => {
    try {
      console.log('🚀 Loading featured products for home page');
      
      // Use the correct backend URL
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://plush-living.co.uk';
      const apiUrl = `${backendUrl}/api/products?limit=8&skip=0`;
      
      console.log('📡 Featured products API URL:', apiUrl);
      
      const response = await axios.get(apiUrl, { timeout: 30000 });
      
      if (response.data && Array.isArray(response.data)) {
        setFeaturedProducts(response.data);
        console.log(`✅ Loaded ${response.data.length} featured products for public display`);
        console.log('📦 First product:', response.data[0]?.name);
      } else {
        console.warn('Unexpected featured products response:', response.data);
        setFeaturedProducts([]);
      }
    } catch (error) {
      console.error('Error loading featured products:', error);
      console.error('Error details:', error.response?.data || error.message);
      setFeaturedProducts([]);
    }
  };

  const handleProfileCallback = async () => {
    // Support both hash and query param formats from the OAuth provider
    const hash = window.location.hash || '';
    const searchParams = new URLSearchParams(window.location.search || '');
    // Robustly parse session_id from hash and query
    // Last-known-working: read session_id only from hash; fall back to query
    const hp = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
    const qp = new URLSearchParams(window.location.search || '');

    const sessionId = hp.get('session_id') || qp.get('session_id');
    if (!sessionId) return;

    try {
      const response = await axios.post(`${API}/auth/customer/session`, { session_id: sessionId });
      
      if (response.data && response.data.session_token) {
        localStorage.setItem('customer_token', response.data.session_token);
        setCustomer(response.data.customer);
        
        // Clear both hash and query to tidy the URL
        const cleanUrl = window.location.pathname;
        window.history.replaceState(null, null, cleanUrl);
        setCurrentPage('home');
      } else {
        console.warn('OAuth session exchange did not return a session token');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail || error.message || 'Unknown error';
      console.error('Authentication error:', detail);
      alert(`Sign-in failed: ${detail}`);
      // Clean URL on failure as well
      const cleanUrl = window.location.pathname;
      window.history.replaceState(null, null, cleanUrl);
    }
  };

  const handleCustomerLogout = () => {
    localStorage.removeItem('customer_token');
    setCustomer(null);
    setWholesaleStatus(null);
    setShowProfileDashboard(false);
  };

  const handleWholesaleApplicationSubmit = async (e) => {
    e.preventDefault();
    
    const formData = new FormData(e.target);
    const applicationData = {
      company_name: formData.get('companyName'),
      company_email: formData.get('companyEmail'),
      company_address: formData.get('companyAddress')
    };
    
    try {
      const token = localStorage.getItem('customer_token');
      const response = await axios.post(`${API}/customer/wholesale-application`, applicationData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.message) {
        alert('Wholesale application submitted successfully! You will be notified once it\'s reviewed.');
        setActiveProfileTab('wholesale');
        
        // Refresh wholesale status
        const statusResponse = await axios.get(`${API}/customer/wholesale-application`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWholesaleStatus(statusResponse.data);
      }
    } catch (error) {
      console.error('Application submission error:', error);
      alert(error.response?.data?.detail || 'Failed to submit application. Please try again.');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    const formData = new FormData(e.target);
    const profileData = {
      name: formData.get('name'),
      email: formData.get('email'),
      phone: formData.get('phone')
    };
    
    try {
      const token = localStorage.getItem('customer_token');
      const response = await axios.put(`${API}/auth/customer/profile`, profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        // Update local customer state
        setCustomer(response.data);
        alert('Profile updated successfully!');
        setActiveProfileTab('profile');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      alert(error.response?.data?.detail || 'Failed to update profile. Please try again.');
    }
  };

  // Wholesale Applications Management Functions
  const fetchWholesaleApplications = async () => {
    setLoadingWholesaleApplications(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/api/admin-portal/wholesale-applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWholesaleApplications(response.data || []);
    } catch (error) {
      console.error('Error fetching wholesale applications:', error);
      // Don't show alert on auto-load, just set empty array
      setWholesaleApplications([]);
    } finally {
      setLoadingWholesaleApplications(false);
    }
  };

  const downloadCustomerReport = (customer, customerOrders) => {
    if (!customer || !customerOrders || customerOrders.length === 0) {
      alert('No purchase history found for this customer');
      return;
    }
    
    // Calculate totals from ALL orders (for Paid column)
    let totalGrandTotal = 0;
    let totalCashPaid = 0;
    let totalBankPaid = 0;
    let totalCreditPaid = 0;
    
    customerOrders.forEach(order => {
      const grandTotal = parseFloat(order.grand_total || 0);
      const cashPaid = parseFloat(order.cash_paid_amount || 0);
      const bankPaid = parseFloat(order.bank_paid_amount || 0);
      const creditPaid = parseFloat(order.credit_paid_amount || 0);
      
      totalGrandTotal += grandTotal;
      totalCashPaid += cashPaid;
      totalBankPaid += bankPaid;
      totalCreditPaid += creditPaid;
    });
    
    // 💰 BULLETPROOF: Include lump-sum (account-level) payments in cash/bank breakdown
    const accountPaymentsData = customerAccountPayments[customer.id] || { total: 0, payments: [] };
    (accountPaymentsData.payments || []).forEach(p => {
      const amt = parseFloat(p.amount || 0);
      const method = (p.payment_method || '').toUpperCase();
      if (method === 'BANK') {
        totalBankPaid += amt;
      } else {
        totalCashPaid += amt;
      }
    });
    
    // 💰 BULLETPROOF: Credit = outstanding from creditAccountsOrders - account payments (same as UI)
    let totalCredit = 0;
    (creditAccountsOrders || []).filter(o => o.customer_id === customer.id).forEach(order => {
      totalCredit += parseFloat(order.outstanding_balance || 0);
    });
    totalCredit = Math.max(0, totalCredit - accountPaymentsData.total);
    
    // 💰 BULLETPROOF: Total Paid = Total Bill - Credit Owed (guaranteed match)
    const totalPaid = Math.max(0, totalGrandTotal - totalCredit);

    // Generate HTML report
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Customer Report - ${customer.name}</title>
        <style>
          @page { 
            margin: 0.5in; 
            size: A4;
          }
          body { 
            font-family: 'Arial', sans-serif; 
            margin: 0; 
            padding: 20px;
            color: #333;
            line-height: 1.4;
          }
          .header {
            background-color: #3f51b5;
            color: white;
            padding: 20px;
            text-align: center;
            margin-bottom: 30px;
          }
          .header h1 {
            margin: 0;
            font-size: 24px;
          }
          .customer-info {
            background: #f8f9fa;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 20px;
            border: 1px solid #ddd;
          }
          .customer-info h2 {
            margin: 0 0 10px 0;
            color: #333;
          }
          .summary {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            margin-bottom: 30px;
          }
          .summary-box {
            padding: 15px;
            background: white;
            border: 2px solid #ddd;
            border-radius: 8px;
            text-align: center;
          }
          .summary-box.paid {
            border-color: #28a745;
          }
          .summary-box.credit {
            border-color: #dc3545;
          }
          .summary-label {
            font-size: 14px;
            color: #666;
            margin-bottom: 5px;
          }
          .summary-amount {
            font-size: 24px;
            font-weight: bold;
          }
          .summary-amount.paid {
            color: #28a745;
          }
          .summary-amount.credit {
            color: #dc3545;
          }
          .order {
            margin-bottom: 30px;
            page-break-inside: avoid;
          }
          .order-header {
            background: #f8f9fa;
            padding: 10px 15px;
            border-radius: 6px 6px 0 0;
            border: 1px solid #ddd;
            border-bottom: none;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .order-header strong {
            font-size: 16px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 15px;
            border: 1px solid #ddd;
          }
          th {
            background-color: #f8f9fa;
            padding: 10px;
            text-align: left;
            border: 1px solid #ddd;
            font-weight: 600;
          }
          td {
            padding: 10px;
            border: 1px solid #ddd;
          }
          .totals {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 10px;
            padding: 15px;
            background: #f8f9fa;
            border: 1px solid #ddd;
            border-radius: 0 0 6px 6px;
          }
          .total-item {
            text-align: center;
          }
          .total-label {
            font-size: 12px;
            color: #666;
            margin-bottom: 5px;
          }
          .total-value {
            font-size: 16px;
            font-weight: 600;
          }
          .total-value.grand {
            color: #007bff;
            font-size: 18px;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #ddd;
            text-align: center;
            font-size: 12px;
            color: #666;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>PLUSH LIVING LTD</h1>
          <p style="margin: 5px 0;">Customer Purchase History Report</p>
        </div>

        <div class="customer-info">
          <h2>${customer.name || 'N/A'}</h2>
          <p style="margin: 5px 0;"><strong>Customer #:</strong> ${customer.customer_number || 'N/A'}</p>
          <p style="margin: 5px 0;"><strong>Report Generated:</strong> ${new Date().toLocaleDateString('en-GB')}</p>
        </div>

        <div class="summary">
          <div class="summary-box paid">
            <div class="summary-label">Total Paid</div>
            <div class="summary-amount paid">£${totalPaid.toFixed(2)}</div>
            <div style="margin-top: 10px; font-size: 14px; color: #666;">
              💵 Cash: £${totalCashPaid.toFixed(2)}<br>
              🏦 Bank: £${totalBankPaid.toFixed(2)}
            </div>
          </div>
          <div class="summary-box credit">
            <div class="summary-label">Total Credit Outstanding</div>
            <div class="summary-amount credit">£${totalCredit.toFixed(2)}</div>
          </div>
        </div>

        <h3 style="margin-bottom: 20px; color: #333;">Purchase History</h3>

        ${customerOrders.map(order => {
          const subtotal = parseFloat(order.subtotal || 0);
          const fittingCharge = parseFloat(order.fitting_charge || 0);
          const cutPieceCharge = parseFloat(order.cut_piece_charge || 0);
          const subtotalWithServices = subtotal + fittingCharge + cutPieceCharge;
          // FIXED: Use stored vat_amount for per-item VAT accuracy
          const vatAmount = parseFloat(order.vat_amount || 0);
          const grandTotal = parseFloat(order.grand_total || 0);
          const orderDate = new Date(order.date).toLocaleDateString('en-GB');

          return `
            <div class="order">
              <div class="order-header">
                <div>
                  <strong>Order #${order.id.substring(0, 8)}</strong>
                  <span style="margin-left: 15px; color: #666;">${orderDate}</span>
                </div>
                <div style="font-weight: 600;">
                  Grand Total: £${grandTotal.toFixed(2)}
                </div>
              </div>

              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th style="text-align: center;">Quantity</th>
                    <th style="text-align: right;">Unit Price</th>
                    <th style="text-align: right;">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${order.items && order.items.length > 0 ? order.items.map(item => `
                    <tr>
                      <td>
                        <div><strong>${item.product_name || 'N/A'}</strong></div>
                        <div style="font-size: 12px; color: #666;">
                          ${item.size && item.size !== 'N/A' ? `Size: ${item.size}` : ''}
                          ${item.color ? ` | Color: ${item.color}` : ''}
                        </div>
                      </td>
                      <td style="text-align: center;">
                        ${(() => {
                          let qty = 0;
                          const unitPrice = parseFloat(item.unit_price || 1);
                          const totalPrice = parseFloat(item.total_price || 0);
                          if (item.measurement_unit === 'per_square_meter') {
                            qty = parseFloat(item.sqm || 0);
                            if (qty === 0 && unitPrice > 0) qty = totalPrice / unitPrice;
                            return qty.toFixed(2) + ' m²';
                          } else {
                            qty = parseFloat(item.quantity || 0);
                            if (qty === 0 && unitPrice > 0) qty = totalPrice / unitPrice;
                            return Math.round(qty) + ' units';
                          }
                        })()}
                      </td>
                      <td style="text-align: right;">£${parseFloat(item.unit_price || 0).toFixed(2)}</td>
                      <td style="text-align: right;"><strong>£${parseFloat(item.total_price || 0).toFixed(2)}</strong></td>
                    </tr>
                  `).join('') : '<tr><td colspan="4" style="text-align: center; color: #666;">No items found</td></tr>'}
                </tbody>
              </table>

              <div style="padding: 15px; background: white; border: 1px solid #ddd; border-radius: 6px;">
                <div style="display: grid; grid-template-columns: 1fr auto; gap: 10px; margin-bottom: 8px;">
                  <div style="color: #666;">Items Subtotal:</div>
                  <div style="font-weight: 600; text-align: right;">£${(order.items?.reduce((sum, item) => sum + parseFloat(item.total_price || 0), 0) || 0).toFixed(2)}</div>
                </div>
                ${order.fitting_charge > 0 ? `
                <div style="display: grid; grid-template-columns: 1fr auto; gap: 10px; margin-bottom: 8px;">
                  <div style="color: #666;">Fitting Charge:</div>
                  <div style="font-weight: 600; text-align: right;">£${parseFloat(order.fitting_charge || 0).toFixed(2)}</div>
                </div>
                ` : ''}
                ${order.cut_piece_charge > 0 ? `
                <div style="display: grid; grid-template-columns: 1fr auto; gap: 10px; margin-bottom: 8px;">
                  <div style="color: #666;">Cut Piece Charge:</div>
                  <div style="font-weight: 600; text-align: right;">£${parseFloat(order.cut_piece_charge || 0).toFixed(2)}</div>
                </div>
                ` : ''}
                ${order.discount > 0 ? `
                <div style="display: grid; grid-template-columns: 1fr auto; gap: 10px; margin-bottom: 8px;">
                  <div style="color: #dc3545;">Discount:</div>
                  <div style="font-weight: 600; text-align: right; color: #dc3545;">-£${parseFloat(order.discount || 0).toFixed(2)}</div>
                </div>
                ` : ''}
                <div style="border-top: 1px solid #ddd; margin-top: 8px; padding-top: 8px; display: grid; grid-template-columns: 1fr auto; gap: 10px; margin-bottom: 8px;">
                  <div style="color: #666;">Subtotal (No VAT):</div>
                  <div style="font-weight: 600; font-size: 18px; text-align: right;">£${subtotal.toFixed(2)}</div>
                </div>
                <div style="display: grid; grid-template-columns: 1fr auto; gap: 10px; margin-bottom: 8px;">
                  <div style="color: #666;">VAT (20%):</div>
                  <div style="font-weight: 600; font-size: 18px; text-align: right;">£${vatAmount.toFixed(2)}</div>
                </div>
                <div style="border-top: 2px solid #333; margin-top: 8px; padding-top: 8px; display: grid; grid-template-columns: 1fr auto; gap: 10px;">
                  <div style="font-size: 20px; font-weight: 700; color: #333;">Grand Total (Inc VAT):</div>
                  <div style="font-size: 22px; font-weight: 700; color: #007bff; text-align: right;">£${grandTotal.toFixed(2)}</div>
                </div>
              </div>
            </div>
          `;
        }).join('')}

        <div class="footer">
          <strong>PLUSH LIVING LTD</strong><br>
          Unit 30 Adderley Rd, Saltley, Birmingham B8 1AN<br>
          Tel: 0121 840 1673 | Email: Plushlivingltd@outlook.com<br>
          Website: Plush-livingltd.co.uk
        </div>
      </body>
      </html>
    `;

    // Create and open print window
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    const printWindow = window.open(url, '_blank', 'width=800,height=1000');
    if (!printWindow) {
      alert('Please allow popups to download reports');
    } else {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };
    }
  };

  const downloadAllCustomersReport = () => {
    // Show the date filter modal instead of generating immediately
    setShowWarehouseBreakdownModal(true);
    // Set default dates: start of current month to today
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    setWarehouseReportStartDate(startOfMonth.toISOString().split('T')[0]);
    setWarehouseReportEndDate(today.toISOString().split('T')[0]);
  };

  // Generate the actual warehouse breakdown report with date filtering
  const generateWarehouseBreakdownReport = () => {
    if (!customers || customers.length === 0) {
      alert('No customers found');
      return;
    }

    // Parse date filters
    const startDate = warehouseReportStartDate ? new Date(warehouseReportStartDate) : null;
    const endDate = warehouseReportEndDate ? new Date(warehouseReportEndDate + 'T23:59:59') : null;
    
    const dateRangeText = startDate && endDate 
      ? `${startDate.toLocaleDateString('en-GB')} to ${endDate.toLocaleDateString('en-GB')}`
      : 'All Time';

    // Filter orders by date
    const filteredOrders = fulfilledOrders.filter(order => {
      if (order.status === 'returned' || order.status === 'cancelled') return false;
      if (!startDate && !endDate) return true;
      const orderDate = new Date(order.date);
      if (startDate && orderDate < startDate) return false;
      if (endDate && orderDate > endDate) return false;
      return true;
    });

    // Filter expenses by date
    const filteredExpenses = (expenses || []).filter(expense => {
      if (!startDate && !endDate) return true;
      const expenseDate = new Date(expense.date || expense.created_at);
      if (startDate && expenseDate < startDate) return false;
      if (endDate && expenseDate > endDate) return false;
      return true;
    });

    // Calculate totals for all customers
    // Build list of real customers + virtual entries for each unique guest customer
    const guestCustomerMap = {};
    filteredOrders.forEach(order => {
      if (!order.customer_id) {
        const guestKey = `guest:${order.customer_number || order.id}`;
        if (!guestCustomerMap[guestKey]) {
          guestCustomerMap[guestKey] = {
            id: null,
            _guestKey: guestKey,
            name: order.customer_name || 'Guest Customer',
            company_name: order.company_name || '',
            customer_number: order.customer_number || 'GUEST',
            isGuest: true
          };
        }
      }
    });
    const allCustomersList = [...customers, ...Object.values(guestCustomerMap)];

    const allCustomersData = allCustomersList
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map(customer => {
        const customerOrders = customer.isGuest
          ? filteredOrders.filter(order => !order.customer_id && (order.customer_number || order.id) === customer.customer_number || (order._guestKey === customer._guestKey))
          : filteredOrders.filter(order => order.customer_id === customer.id);
        // For guest customers, use a stricter match on customer_number/order.id
        const finalCustomerOrders = customer.isGuest
          ? filteredOrders.filter(order => !order.customer_id && `guest:${order.customer_number || order.id}` === customer._guestKey)
          : customerOrders;
        
        let totalGrandTotal = 0, totalCashPaid = 0, totalBankPaid = 0;
        
        finalCustomerOrders.forEach(order => {
          totalGrandTotal += parseFloat(order.grand_total || 0);
          totalCashPaid += parseFloat(order.cash_paid_amount || 0);
          totalBankPaid += parseFloat(order.bank_paid_amount || 0);
        });
        
        // 💰 BULLETPROOF: Credit Outstanding for THIS PERIOD only.
        // Use creditAccountsOrders (the single source of truth) filtered to bills created in this period.
        // Note: account-level lump-sum payments are NOT subtracted here — they are tracked separately
        // because they don't reduce a specific bill's outstanding_balance. This keeps the period math reconciling:
        //   Total Sales (period) ≈ Cash + Bank + Credit Outstanding + credit_paid_amount
        const customerCreditOrders = (creditAccountsOrders || []).filter(o => {
          const customerMatch = customer.isGuest
            ? (!o.customer_id && `guest:${o.customer_number || o.id}` === customer._guestKey)
            : o.customer_id === customer.id;
          if (!customerMatch) return false;
          if (!startDate && !endDate) return true;
          const orderDate = new Date(o.date);
          if (startDate && orderDate < startDate) return false;
          if (endDate && orderDate > endDate) return false;
          return true;
        });
        let totalCredit = 0;
        customerCreditOrders.forEach(order => {
          totalCredit += parseFloat(order.outstanding_balance || 0);
        });
        
        return { customer, customerOrders: finalCustomerOrders, totalGrandTotal, totalPaid: totalCashPaid + totalBankPaid, totalCashPaid, totalBankPaid, totalCredit };
      })
      .filter(data => data.customerOrders.length > 0);

    // ========== CALCULATE SUMMARY TOTALS ==========
    let grandTotalCash = 0, grandTotalBank = 0, grandTotalCredit = 0, grandTotalVAT = 0, grandTotalSales = 0;
    let totalOrderCount = 0;
    allCustomersData.forEach(data => {
      grandTotalCash += data.totalCashPaid;
      grandTotalBank += data.totalBankPaid;
      grandTotalCredit += data.totalCredit;
      grandTotalSales += data.totalGrandTotal;
      totalOrderCount += data.customerOrders.length;
      data.customerOrders.forEach(order => { grandTotalVAT += parseFloat(order.vat_amount || 0); });
    });

    // 💰 BULLETPROOF: Add lump-sum (account-level) payments to grand totals (period-filtered).
    // These are real cash/bank receipts that don't sit on any single order's cash_paid_amount/bank_paid_amount.
    Object.values(customerAccountPayments || {}).forEach(data => {
      (data.payments || []).forEach(p => {
        const pDate = new Date(p.payment_date || p.created_at || p.date);
        // Date filter (same as filteredOrders)
        if (startDate && pDate < startDate) return;
        if (endDate && pDate > endDate) return;
        const amt = parseFloat(p.amount || 0);
        const method = (p.payment_method || '').toUpperCase();
        if (method === 'BANK') grandTotalBank += amt;
        else grandTotalCash += amt; // cash/cheque/other → cash bucket
      });
    });

    // Calculate expenses
    let totalExpensesCash = 0, totalExpensesBank = 0;
    filteredExpenses.forEach(expense => {
      const amount = parseFloat(expense.amount || 0);
      const method = (expense.payment_method || '').toLowerCase();
      if (method === 'bank' || method === 'bank transfer') totalExpensesBank += amount;
      else totalExpensesCash += amount;
    });

    // ========== BUILD PRODUCT COST LOOKUP (for COGS/profit calc) ==========
    // Maps "productName::color::size" → cost_price from current product DB
    const productCostLookup = {};
    (products || []).forEach(product => {
      const productName = product.name || product.title || 'Unknown';
      if (product.variations && Array.isArray(product.variations)) {
        product.variations.forEach(variation => {
          const color = variation.color || 'N/A';
          const size = variation.size || 'N/A';
          const cost = parseFloat(variation.cost_price || variation.cost || variation.wholesale || product.cost_price || 0);
          // Index by name+color+size, name+color, and name alone for graceful fallback
          productCostLookup[`${productName}::${color}::${size}`] = cost;
          if (productCostLookup[`${productName}::${color}`] == null) productCostLookup[`${productName}::${color}`] = cost;
          if (productCostLookup[productName] == null) productCostLookup[productName] = cost;
        });
      }
    });
    
    // ========== BUILD DETAILED PRODUCT SALES ==========
    const detailedProductSales = [];
    const productQuantitiesSold = {};

    allCustomersData.forEach(data => {
      const { customer, customerOrders } = data;
      customerOrders.forEach(order => {
        const orderDate = new Date(order.date).toLocaleDateString('en-GB');
        const billNumber = order.id ? order.id.substring(0, 8).toUpperCase() : 'N/A';
        const hasVAT = parseFloat(order.vat_amount || 0) > 0;
        const vatAmount = parseFloat(order.vat_amount || 0);
        
        if (order.items && order.items.length > 0) {
          order.items.forEach(item => {
            const productName = item.product_name || item.name || 'Unknown Product';
            const size = item.size || 'N/A';
            const color = item.color || 'N/A';
            const unitPrice = parseFloat(item.unit_price || item.price || 0);
            const totalPrice = parseFloat(item.total_price || item.amount || 0);
            
            let qty = 0, unit = 'units';
            if (item.measurement_unit === 'per_square_meter') {
              qty = parseFloat(item.sqm || item.square_meters || 0);
              if (qty === 0 && unitPrice > 0) qty = totalPrice / unitPrice;
              unit = 'm\u00B2';
            } else {
              qty = parseFloat(item.quantity || item.qty || 0);
              if (qty === 0 && unitPrice > 0) qty = totalPrice / unitPrice;
              if (qty === 0) qty = 1;
            }

            // Match by product name + color ONLY (ignore size - it varies between orders and DB)
            const productKey = productName + '__' + color;
            if (!productQuantitiesSold[productKey]) {
              productQuantitiesSold[productKey] = { productName, color, unit, quantitySold: 0 };
            }
            productQuantitiesSold[productKey].quantitySold += qty;

            let itemVAT = 0;
            if (hasVAT && order.items.length > 0) {
              const orderSubtotal = order.items.reduce((sum, i) => sum + parseFloat(i.total_price || i.amount || 0), 0);
              if (orderSubtotal > 0) itemVAT = (totalPrice / orderSubtotal) * vatAmount;
            }
            
            // 💰 Cost lookup: prefer item.unit_cost (if stored on order), else product DB cost
            let unitCost = parseFloat(item.unit_cost || item.cost_price || item.cost || 0);
            if (!unitCost) {
              unitCost = parseFloat(
                productCostLookup[`${productName}::${color}::${size}`] ??
                productCostLookup[`${productName}::${color}`] ??
                productCostLookup[productName] ?? 0
              );
            }
            const totalCost = unitCost * qty;
            
            detailedProductSales.push({
              productName, size, color, quantity: qty, unit, unitPrice, totalPrice, hasVAT, itemVAT,
              unitCost, totalCost,
              customerName: customer.name || 'N/A', customerNumber: customer.customer_number || 'N/A',
              billNumber, orderDate, paymentMethod: order.payment_method || 'N/A'
            });
          });
        } else if (parseFloat(order.grand_total || 0) > 0) {
          // Order has no items but has a grand total — include as unitemized
          const totalPrice = parseFloat(order.grand_total || 0);
          detailedProductSales.push({
            productName: 'Unitemized Order', size: 'N/A', color: 'N/A',
            quantity: 1, unit: 'order', unitPrice: totalPrice, totalPrice,
            hasVAT, itemVAT: vatAmount,
            unitCost: 0, totalCost: 0,
            customerName: customer.name || 'N/A', customerNumber: customer.customer_number || 'N/A',
            billNumber, orderDate, paymentMethod: order.payment_method || 'N/A'
          });
        }
      });
    });

    detailedProductSales.sort((a, b) => a.productName.localeCompare(b.productName) || a.customerName.localeCompare(b.customerName));

    // ========== CALCULATE PRODUCTS ON FLOOR ==========
    // Current DB stock = what's physically on the floor. Match sold by name+color.
    const productsOnFloorSqm = [];
    const productsOnFloorSize = [];
    const floorProductKeys = new Set();
    (products || []).forEach(product => {
      const measurementUnit = product.measurement_unit || 'size';
      const isSqm = measurementUnit === 'per_square_meter';
      
      if (product.variations && Array.isArray(product.variations)) {
        product.variations.forEach(variation => {
          const productName = product.name || product.title || 'Unknown';
          const color = variation.color || 'N/A';
          const size = variation.size || '';
          const productKey = productName + '__' + color;
          
          let currentStock = 0;
          if (isSqm) {
            currentStock = parseFloat(variation.square_meters_available || variation.square_meters || 0);
          } else {
            currentStock = parseFloat(variation.stock_quantity || variation.quantity || 0);
          }
          
          const soldData = productQuantitiesSold[productKey];
          const soldInPeriod = soldData ? soldData.quantitySold : 0;
          
          // Restock data from variation history (filtered by date range)
          let restockInPeriod = 0;
          let restockDetails = [];
          if (variation.restock_history && Array.isArray(variation.restock_history)) {
            variation.restock_history.forEach(r => {
              const rDate = new Date(r.date);
              const inRange = (!startDate || rDate >= startDate) && (!endDate || rDate <= endDate);
              if (inRange) {
                restockInPeriod += parseFloat(r.quantity_added || 0);
                restockDetails.push(new Date(r.date).toLocaleDateString('en-GB') + ': ' + parseFloat(r.quantity_added || 0).toFixed(2) + (isSqm ? ' m\u00B2' : ' units'));
              }
            });
          }
          
          // Starting Stock = Remaining + Sold - Restocked
          const startingStock = currentStock + soldInPeriod - restockInPeriod;
          
          const entry = {
            productName, color, size,
            startingStock: Math.max(0, startingStock),
            currentStock, soldInPeriod, restockInPeriod,
            restockDetails,
            retailPrice: parseFloat(variation.retail_price || 0),
            wholesalePrice: parseFloat(variation.wholesale_price || 0),
            fitterPrice: parseFloat(variation.fitter_price || 0),
            costPrice: parseFloat(variation.cost_price || 0)
          };
          
          floorProductKeys.add(productKey);
          if (isSqm) {
            productsOnFloorSqm.push(entry);
          } else {
            productsOnFloorSize.push(entry);
          }
        });
      }
    });
    
    // Add sold-only products (no longer in inventory)
    Object.keys(productQuantitiesSold).forEach(productKey => {
      if (!floorProductKeys.has(productKey)) {
        const soldData = productQuantitiesSold[productKey];
        if (soldData.quantitySold > 0) {
          const entry = {
            productName: soldData.productName, color: soldData.color,
            size: '', startingStock: soldData.quantitySold,
            currentStock: 0, soldInPeriod: soldData.quantitySold,
            restockInPeriod: 0, restockDetails: [],
            retailPrice: 0, wholesalePrice: 0, fitterPrice: 0, costPrice: 0
          };
          if (soldData.unit === 'm\u00B2') {
            productsOnFloorSqm.push(entry);
          } else {
            productsOnFloorSize.push(entry);
          }
        }
      }
    });
    
    productsOnFloorSqm.sort((a, b) => a.productName.localeCompare(b.productName) || a.color.localeCompare(b.color));
    productsOnFloorSize.sort((a, b) => a.productName.localeCompare(b.productName) || a.color.localeCompare(b.color));

    // ========== GROUP SALES BY PRODUCT ==========
    const groupedByProduct = {};
    detailedProductSales.forEach(sale => {
      if (!groupedByProduct[sale.productName]) groupedByProduct[sale.productName] = [];
      groupedByProduct[sale.productName].push(sale);
    });

    // ========== GENERATE HTML ==========
    let productBreakdownHTML = '';
    Object.keys(groupedByProduct).sort().forEach(productName => {
      const sales = groupedByProduct[productName];
      const totalQty = sales.reduce((sum, s) => sum + s.quantity, 0);
      const totalValue = sales.reduce((sum, s) => sum + s.totalPrice, 0);
      const totalVAT = sales.reduce((sum, s) => sum + s.itemVAT, 0);
      const totalCost = sales.reduce((sum, s) => sum + (s.totalCost || 0), 0);
      const profit = totalValue - totalCost;
      const profitColor = profit >= 0 ? '#28a745' : '#dc3545';
      const profitLabel = profit >= 0 ? 'Profit' : 'Loss';
      const unit = sales[0]?.unit || 'units';
      
      let salesRowsHTML = '';
      sales.forEach(sale => {
        salesRowsHTML += '<tr>' +
          '<td><strong>' + sale.customerName + '</strong><br><span style="font-size:10px;color:#666">#' + sale.customerNumber + '</span></td>' +
          '<td style="font-family:monospace;font-weight:600">' + sale.billNumber + '</td>' +
          '<td>' + sale.orderDate + '</td>' +
          '<td>' + (sale.size !== 'N/A' ? sale.size : '') + (sale.color !== 'N/A' ? (sale.size !== 'N/A' ? ' / ' : '') + sale.color : '') + '</td>' +
          '<td style="text-align:center">' + sale.quantity.toFixed(2) + ' ' + sale.unit + '</td>' +
          '<td style="text-align:right">£' + sale.unitPrice.toFixed(2) + '</td>' +
          '<td style="text-align:right;font-weight:600">£' + sale.totalPrice.toFixed(2) + '</td>' +
          '<td style="text-align:right">' + (sale.hasVAT ? '£' + sale.itemVAT.toFixed(2) : '-') + '</td>' +
          '<td>' + sale.paymentMethod + '</td>' +
          '</tr>';
      });
      
      productBreakdownHTML += '<div class="product-group">' +
        '<div class="product-header"><h3>' + productName + '</h3>' +
        '<div style="font-size:13px;color:#555;margin-top:5px">Total Sold: <strong>' + totalQty.toFixed(2) + ' ' + unit + '</strong> | Total Cost: <strong>\u00A3' + totalCost.toFixed(2) + '</strong> | Total Value: <strong>\u00A3' + totalValue.toFixed(2) + '</strong> | VAT: <strong>\u00A3' + totalVAT.toFixed(2) + '</strong> | <span style="color:' + profitColor + ';font-weight:700">' + profitLabel + ': \u00A3' + Math.abs(profit).toFixed(2) + '</span></div></div>' +
        '<table><thead><tr>' +
        '<th>Customer</th><th>Bill #</th><th>Date</th><th>Size/Color</th><th style="text-align:center">Qty</th>' +
        '<th style="text-align:right">Unit Price</th><th style="text-align:right">Total</th><th style="text-align:right">VAT</th><th>Payment</th>' +
        '</tr></thead><tbody>' + salesRowsHTML + '</tbody></table></div>';
    });

    // ========== FLOOR PRODUCTS HTML ==========
    // Per m² products table
    let floorSqmHTML = '';
    productsOnFloorSqm.forEach(item => {
      let stockClass = 'in-stock';
      if (item.currentStock <= 0) stockClass = 'out-stock';
      else if (item.currentStock <= 10) stockClass = 'low-stock';
      const restockText = item.restockInPeriod > 0 
        ? item.restockDetails.join('<br>') 
        : '-';
      floorSqmHTML += '<tr>' +
        '<td><strong>' + item.productName + '</strong></td>' +
        '<td>' + item.color + '</td>' +
        '<td style="text-align:right">' + item.startingStock.toFixed(2) + ' m\u00B2</td>' +
        '<td style="text-align:right;color:#dc3545;font-weight:600">' + item.soldInPeriod.toFixed(2) + ' m\u00B2</td>' +
        '<td style="text-align:right;color:#28a745;font-size:11px">' + restockText + '</td>' +
        '<td class="' + stockClass + '" style="font-weight:700;text-align:right">' + item.currentStock.toFixed(2) + ' m\u00B2</td></tr>';
    });
    
    // Size-based products table
    let floorSizeHTML = '';
    productsOnFloorSize.forEach(item => {
      let stockClass = 'in-stock';
      if (item.currentStock <= 0) stockClass = 'out-stock';
      else if (item.currentStock <= 3) stockClass = 'low-stock';
      const restockText = item.restockInPeriod > 0 
        ? item.restockDetails.join('<br>') 
        : '-';
      floorSizeHTML += '<tr>' +
        '<td><strong>' + item.productName + '</strong></td>' +
        '<td>' + (item.size || 'N/A') + '</td>' +
        '<td>' + item.color + '</td>' +
        '<td style="text-align:right">' + Math.round(item.startingStock) + '</td>' +
        '<td style="text-align:right;color:#dc3545;font-weight:600">' + Math.round(item.soldInPeriod) + '</td>' +
        '<td style="text-align:right;color:#28a745;font-size:11px">' + restockText + '</td>' +
        '<td class="' + stockClass + '" style="font-weight:700;text-align:right">' + Math.round(item.currentStock) + '</td></tr>';
    });

    const totalExpenses = totalExpensesCash + totalExpensesBank;
    const netRevenue = grandTotalCash + grandTotalBank - totalExpenses;
    
    // 💰 Break out cash/bank that came from LUMP-SUM account payments vs from new bills.
    // This is what makes "0 orders but £3000 collected" no longer confusing.
    let lumpSumCash = 0, lumpSumBank = 0;
    Object.values(customerAccountPayments || {}).forEach(data => {
      (data.payments || []).forEach(p => {
        const pDate = new Date(p.payment_date || p.created_at || p.date);
        if (startDate && pDate < startDate) return;
        if (endDate && pDate > endDate) return;
        const amt = parseFloat(p.amount || 0);
        const method = (p.payment_method || '').toUpperCase();
        if (method === 'BANK') lumpSumBank += amt; else lumpSumCash += amt;
      });
    });
    const cashFromNewBills = Math.max(0, grandTotalCash - lumpSumCash);
    const bankFromNewBills = Math.max(0, grandTotalBank - lumpSumBank);
    const lumpSumTotal = lumpSumCash + lumpSumBank;

    // ========== TOTAL PRODUCTS SOLD SUMMARY TABLE ==========
    let productSummaryHTML = '';
    let grandTotalQtySold = 0;
    let grandTotalSalesValue = 0;
    let grandTotalSalesVAT = 0;
    let grandTotalSalesCost = 0;
    const productSummaryRows = [];
    Object.keys(groupedByProduct).sort().forEach(productName => {
      const sales = groupedByProduct[productName];
      const totalQty = sales.reduce((sum, s) => sum + s.quantity, 0);
      const totalValue = sales.reduce((sum, s) => sum + s.totalPrice, 0);
      const totalVAT = sales.reduce((sum, s) => sum + s.itemVAT, 0);
      const totalCost = sales.reduce((sum, s) => sum + (s.totalCost || 0), 0);
      // Profit = revenue (ex VAT) - cost.  totalValue includes ex-VAT pricing for this app, so:
      const profit = totalValue - totalCost;
      const profitColor = profit >= 0 ? '#28a745' : '#dc3545';
      const profitLabel = profit >= 0 ? 'Profit' : 'Loss';
      const unit = sales[0]?.unit || 'units';
      const uniqueCustomers = [...new Set(sales.map(s => s.customerName))].length;
      grandTotalQtySold += totalQty;
      grandTotalSalesValue += totalValue;
      grandTotalSalesVAT += totalVAT;
      grandTotalSalesCost += totalCost;
      productSummaryRows.push('<tr>' +
        '<td><strong>' + productName + '</strong></td>' +
        '<td style="text-align:center">' + totalQty.toFixed(2) + ' ' + unit + '</td>' +
        '<td style="text-align:right;color:#6c757d">\u00A3' + totalCost.toFixed(2) + '</td>' +
        '<td style="text-align:right;font-weight:600">\u00A3' + totalValue.toFixed(2) + '</td>' +
        '<td style="text-align:right">\u00A3' + totalVAT.toFixed(2) + '</td>' +
        '<td style="text-align:right;font-weight:700;color:' + profitColor + '">\u00A3' + Math.abs(profit).toFixed(2) + '<br><span style="font-size:10px;font-weight:400">' + profitLabel + '</span></td>' +
        '<td style="text-align:center">' + uniqueCustomers + '</td>' +
        '<td style="text-align:center">' + sales.length + '</td></tr>');
    });
    const grandTotalProfit = grandTotalSalesValue - grandTotalSalesCost;
    const grandProfitColor = grandTotalProfit >= 0 ? '#90ee90' : '#ff6b6b';
    const grandProfitLabel = grandTotalProfit >= 0 ? 'PROFIT' : 'LOSS';
    productSummaryHTML = '<div class="breakdown-section" style="page-break-before:always"><h2>TOTAL PRODUCTS SOLD SUMMARY</h2>' +
      '<p style="color:#666;margin-bottom:20px">Overview of all products sold during the selected period (' + dateRangeText + ').</p>' +
      '<table><thead><tr><th>Product Name</th><th style="text-align:center">Total Qty Sold</th>' +
      '<th style="text-align:right">Total Cost</th>' +
      '<th style="text-align:right">Total Value</th><th style="text-align:right">VAT</th>' +
      '<th style="text-align:right">Profit / Loss</th>' +
      '<th style="text-align:center">Customers</th><th style="text-align:center">Transactions</th></tr></thead>' +
      '<tbody>' + productSummaryRows.join('') + '</tbody>' +
      '<tfoot><tr style="background:#1a365d;color:white;font-weight:700">' +
      '<td style="padding:12px">GRAND TOTAL</td>' +
      '<td style="text-align:center;padding:12px">' + Object.keys(groupedByProduct).length + ' products</td>' +
      '<td style="text-align:right;padding:12px;font-size:14px">\u00A3' + grandTotalSalesCost.toFixed(2) + '</td>' +
      '<td style="text-align:right;padding:12px;font-size:16px">\u00A3' + grandTotalSalesValue.toFixed(2) + '</td>' +
      '<td style="text-align:right;padding:12px">\u00A3' + grandTotalSalesVAT.toFixed(2) + '</td>' +
      '<td style="text-align:right;padding:12px;font-size:16px;color:' + grandProfitColor + '">\u00A3' + Math.abs(grandTotalProfit).toFixed(2) + '<br><span style="font-size:11px">' + grandProfitLabel + '</span></td>' +
      '<td style="text-align:center;padding:12px">' + allCustomersData.length + '</td>' +
      '<td style="text-align:center;padding:12px">' + detailedProductSales.length + '</td></tr></tfoot></table></div>';

    // ========== IN-DEPTH CASH & BANK TRANSACTIONS + CREDITS GIVEN IN PERIOD ==========
    // Collect every cash / bank / credit movement in the selected period so the report is a
    // fully itemized dossier (bill payments + lump-sum account payments).
    const cashTxns = [];  // { date, customer, customerNumber, billNumber, description, amount }
    const bankTxns = [];
    const creditGiven = [];  // { date, customer, customerNumber, billNumber, grandTotal, cashPaid, bankPaid, creditPortion, outstanding, status }

    allCustomersData.forEach(data => {
      const { customer, customerOrders } = data;
      const customerName = customer.name || 'Guest Customer';
      const customerNumber = customer.customer_number || 'GUEST';
      customerOrders.forEach(order => {
        const orderDateStr = order.date ? new Date(order.date).toLocaleDateString('en-GB') : 'N/A';
        const billNumber = order.id ? String(order.id).substring(0, 8).toUpperCase() : 'N/A';
        const cashPaid = parseFloat(order.cash_paid_amount || 0);
        const bankPaid = parseFloat(order.bank_paid_amount || 0);
        const grandTotal = parseFloat(order.grand_total || 0);
        const outstanding = parseFloat(order.outstanding_balance || 0);
        // Credit portion = amount left to be paid AFTER cash+bank at bill creation time (approx: grand_total - cash - bank)
        const creditPortion = Math.max(0, grandTotal - cashPaid - bankPaid);
        if (cashPaid > 0) {
          cashTxns.push({
            date: orderDateStr, rawDate: order.date ? new Date(order.date) : new Date(0),
            customer: customerName, customerNumber, billNumber,
            description: 'Bill payment', amount: cashPaid
          });
        }
        if (bankPaid > 0) {
          bankTxns.push({
            date: orderDateStr, rawDate: order.date ? new Date(order.date) : new Date(0),
            customer: customerName, customerNumber, billNumber,
            description: 'Bill payment', amount: bankPaid
          });
        }
        if (creditPortion > 0 || outstanding > 0) {
          creditGiven.push({
            date: orderDateStr, rawDate: order.date ? new Date(order.date) : new Date(0),
            customer: customerName, customerNumber, billNumber,
            grandTotal, cashPaid, bankPaid, creditPortion, outstanding,
            status: outstanding > 0 ? 'Owing' : 'Cleared'
          });
        }
      });
    });

    // Add lump-sum (account-level) payments to cash/bank txn lists (period-filtered)
    Object.entries(customerAccountPayments || {}).forEach(([custId, data]) => {
      const cust = customers.find(c => c.id === custId);
      const custName = cust?.name || (data.customer_name || 'Unknown');
      const custNum = cust?.customer_number || 'N/A';
      (data.payments || []).forEach(p => {
        const pDate = new Date(p.payment_date || p.created_at || p.date);
        if (startDate && pDate < startDate) return;
        if (endDate && pDate > endDate) return;
        const amt = parseFloat(p.amount || 0);
        const method = (p.payment_method || '').toUpperCase();
        const entry = {
          date: pDate.toLocaleDateString('en-GB'), rawDate: pDate,
          customer: custName, customerNumber: custNum, billNumber: '-',
          description: 'Lump-sum account payment', amount: amt
        };
        if (method === 'BANK') bankTxns.push(entry); else cashTxns.push(entry);
      });
    });

    cashTxns.sort((a, b) => b.rawDate - a.rawDate);
    bankTxns.sort((a, b) => b.rawDate - a.rawDate);
    creditGiven.sort((a, b) => b.rawDate - a.rawDate);

    const cashTxnTotal = cashTxns.reduce((s, t) => s + t.amount, 0);
    const bankTxnTotal = bankTxns.reduce((s, t) => s + t.amount, 0);
    const creditGivenTotal = creditGiven.reduce((s, t) => s + t.creditPortion, 0);
    const creditOutstandingTotal = creditGiven.reduce((s, t) => s + t.outstanding, 0);

    // Build the three HTML sections
    let cashTxnRows = '';
    cashTxns.forEach(t => {
      cashTxnRows += '<tr>' +
        '<td>' + t.date + '</td>' +
        '<td><strong>' + t.customer + '</strong> <span style="color:#666;font-size:11px">#' + t.customerNumber + '</span></td>' +
        '<td style="font-family:monospace">' + t.billNumber + '</td>' +
        '<td>' + t.description + '</td>' +
        '<td style="text-align:right;font-weight:600;color:#166534">\u00A3' + t.amount.toFixed(2) + '</td></tr>';
    });
    const cashTxnHTML = '<div class="breakdown-section" style="page-break-before:always">' +
      '<h2 style="background:#166534!important">IN-DEPTH CASH TRANSACTIONS</h2>' +
      '<p style="color:#666;margin-bottom:20px">Every cash payment received during the selected period (' + dateRangeText + ') \u2014 bill payments and lump-sum account payments.</p>' +
      '<table><thead><tr><th>Date</th><th>Customer</th><th>Bill #</th><th>Description</th><th style="text-align:right">Amount</th></tr></thead>' +
      '<tbody>' + (cashTxnRows || '<tr><td colspan="5" style="text-align:center;padding:20px;color:#999;font-style:italic">No cash transactions in this period.</td></tr>') + '</tbody>' +
      '<tfoot><tr style="background:#1a365d;color:white;font-weight:700">' +
      '<td colspan="4" style="text-align:right;padding:12px">TOTAL CASH RECEIVED:</td>' +
      '<td style="text-align:right;padding:12px;font-size:16px">\u00A3' + cashTxnTotal.toFixed(2) + '</td></tr>' +
      '<tr style="background:#e9ecef;font-weight:600"><td colspan="4" style="text-align:right;padding:8px">Transactions:</td>' +
      '<td style="text-align:right;padding:8px">' + cashTxns.length + '</td></tr></tfoot></table></div>';

    let bankTxnRows = '';
    bankTxns.forEach(t => {
      bankTxnRows += '<tr>' +
        '<td>' + t.date + '</td>' +
        '<td><strong>' + t.customer + '</strong> <span style="color:#666;font-size:11px">#' + t.customerNumber + '</span></td>' +
        '<td style="font-family:monospace">' + t.billNumber + '</td>' +
        '<td>' + t.description + '</td>' +
        '<td style="text-align:right;font-weight:600;color:#1e40af">\u00A3' + t.amount.toFixed(2) + '</td></tr>';
    });
    const bankTxnHTML = '<div class="breakdown-section" style="page-break-before:always">' +
      '<h2 style="background:#1e40af!important">IN-DEPTH BANK TRANSACTIONS</h2>' +
      '<p style="color:#666;margin-bottom:20px">Every bank payment received during the selected period (' + dateRangeText + ') \u2014 bill payments and lump-sum account payments.</p>' +
      '<table><thead><tr><th>Date</th><th>Customer</th><th>Bill #</th><th>Description</th><th style="text-align:right">Amount</th></tr></thead>' +
      '<tbody>' + (bankTxnRows || '<tr><td colspan="5" style="text-align:center;padding:20px;color:#999;font-style:italic">No bank transactions in this period.</td></tr>') + '</tbody>' +
      '<tfoot><tr style="background:#1a365d;color:white;font-weight:700">' +
      '<td colspan="4" style="text-align:right;padding:12px">TOTAL BANK RECEIVED:</td>' +
      '<td style="text-align:right;padding:12px;font-size:16px">\u00A3' + bankTxnTotal.toFixed(2) + '</td></tr>' +
      '<tr style="background:#e9ecef;font-weight:600"><td colspan="4" style="text-align:right;padding:8px">Transactions:</td>' +
      '<td style="text-align:right;padding:8px">' + bankTxns.length + '</td></tr></tfoot></table></div>';

    let creditGivenRows = '';
    creditGiven.forEach(c => {
      const statusColor = c.status === 'Owing' ? '#dc3545' : '#059669';
      const statusBg = c.status === 'Owing' ? '#fee2e2' : '#d1fae5';
      creditGivenRows += '<tr>' +
        '<td>' + c.date + '</td>' +
        '<td><strong>' + c.customer + '</strong> <span style="color:#666;font-size:11px">#' + c.customerNumber + '</span></td>' +
        '<td style="font-family:monospace">' + c.billNumber + '</td>' +
        '<td style="text-align:right">\u00A3' + c.grandTotal.toFixed(2) + '</td>' +
        '<td style="text-align:right;color:#166534">\u00A3' + c.cashPaid.toFixed(2) + '</td>' +
        '<td style="text-align:right;color:#1e40af">\u00A3' + c.bankPaid.toFixed(2) + '</td>' +
        '<td style="text-align:right;font-weight:700;color:#dc3545">\u00A3' + c.creditPortion.toFixed(2) + '</td>' +
        '<td style="text-align:right;font-weight:600">\u00A3' + c.outstanding.toFixed(2) + '</td>' +
        '<td style="text-align:center"><span style="padding:2px 8px;border-radius:10px;font-size:10px;font-weight:600;background:' + statusBg + ';color:' + statusColor + '">' + c.status + '</span></td></tr>';
    });
    const creditGivenHTML = '<div class="breakdown-section" style="page-break-before:always">' +
      '<h2 style="background:#dc3545!important">CREDITS GIVEN IN PERIOD</h2>' +
      '<p style="color:#666;margin-bottom:20px">Every bill from this period that included a credit portion (unpaid at time of sale). Shows the credit granted vs. what is still outstanding today.</p>' +
      '<table><thead><tr>' +
      '<th>Date</th><th>Customer</th><th>Bill #</th>' +
      '<th style="text-align:right">Grand Total</th>' +
      '<th style="text-align:right">Cash Paid</th>' +
      '<th style="text-align:right">Bank Paid</th>' +
      '<th style="text-align:right">Credit Given</th>' +
      '<th style="text-align:right">Still Owing</th>' +
      '<th style="text-align:center">Status</th>' +
      '</tr></thead>' +
      '<tbody>' + (creditGivenRows || '<tr><td colspan="9" style="text-align:center;padding:20px;color:#999;font-style:italic">No credit sales in this period.</td></tr>') + '</tbody>' +
      '<tfoot><tr style="background:#1a365d;color:white;font-weight:700">' +
      '<td colspan="3" style="padding:12px">TOTALS (' + creditGiven.length + ' bill' + (creditGiven.length === 1 ? '' : 's') + ')</td>' +
      '<td style="text-align:right;padding:12px">\u00A3' + creditGiven.reduce((s,c) => s + c.grandTotal, 0).toFixed(2) + '</td>' +
      '<td style="text-align:right;padding:12px">\u00A3' + creditGiven.reduce((s,c) => s + c.cashPaid, 0).toFixed(2) + '</td>' +
      '<td style="text-align:right;padding:12px">\u00A3' + creditGiven.reduce((s,c) => s + c.bankPaid, 0).toFixed(2) + '</td>' +
      '<td style="text-align:right;padding:12px;font-size:16px">\u00A3' + creditGivenTotal.toFixed(2) + '</td>' +
      '<td style="text-align:right;padding:12px;font-size:16px">\u00A3' + creditOutstandingTotal.toFixed(2) + '</td>' +
      '<td style="padding:12px"></td></tr></tfoot></table></div>';

    // ========== IN-DEPTH CREDIT ACCOUNTS (current outstanding running total across ALL time) ==========
    let creditAccountsHTML = '';
    const creditCustomerGroups = {};
    (creditAccountsOrders || []).forEach(order => {
      // 💰 IRON-CLAD: Treat each guest customer as unique
      const isGuest = !order.customer_id;
      const custId = isGuest
        ? `guest:${order.customer_number || order.id}`
        : order.customer_id;
      if (!creditCustomerGroups[custId]) {
        const cust = isGuest ? null : customers.find(c => c.id === custId);
        creditCustomerGroups[custId] = {
          isGuest,
          realCustomerId: isGuest ? null : custId,
          customerName: cust?.name || order.customer_name || 'Unknown',
          customerNumber: order.customer_number || cust?.customer_number || 'N/A',
          companyName: cust?.company_name || order.company_name || '',
          orders: [],
          totalOutstanding: 0
        };
      }
      const outstanding = parseFloat(order.outstanding_balance || 0);
      if (outstanding > 0) {
        creditCustomerGroups[custId].orders.push(order);
        creditCustomerGroups[custId].totalOutstanding += outstanding;
      }
    });
    // Apply account-level payments (only for real customers; guests have none)
    Object.entries(creditCustomerGroups).forEach(([custId, group]) => {
      if (!group.isGuest) {
        const acctPayments = customerAccountPayments[group.realCustomerId] || { total: 0 };
        group.totalOutstanding = Math.max(0, group.totalOutstanding - acctPayments.total);
      }
    });
    
    const creditGroupsList = Object.values(creditCustomerGroups)
      .filter(g => g.totalOutstanding > 0)
      .sort((a, b) => b.totalOutstanding - a.totalOutstanding);
    
    let creditRowsHTML = '';
    let grandTotalOutstanding = 0;
    creditGroupsList.forEach(group => {
      grandTotalOutstanding += group.totalOutstanding;
      creditRowsHTML += '<tr>' +
        '<td><strong>' + group.customerName + '</strong>' +
        (group.companyName ? ' <span style="color:#666;font-size:11px">(' + group.companyName + ')</span>' : '') + '</td>' +
        '<td>' + group.customerNumber + '</td>' +
        '<td style="text-align:right;font-weight:700;color:#dc3545;font-size:15px">\u00A3' + group.totalOutstanding.toFixed(2) + '</td></tr>';
    });
    
    creditAccountsHTML = '<div class="breakdown-section" style="page-break-before:always">' +
      '<h2 style="background:#dc3545!important">CURRENT OUTSTANDING CREDIT ACCOUNTS (ALL TIME)</h2>' +
      '<p style="color:#666;margin-bottom:20px">Running total of credit still owed by each customer across all time \u2014 matches the Credit Accounts view exactly. (See "Credits Given In Period" above for period-only figures.)</p>' +
      '<table><thead><tr><th>Customer</th><th>Account</th>' +
      '<th style="text-align:right">Amount Owed</th></tr></thead>' +
      '<tbody>' + creditRowsHTML + '</tbody>' +
      '<tfoot><tr style="background:#1a365d;color:white;font-weight:700">' +
      '<td style="padding:12px">TOTAL</td>' +
      '<td style="padding:12px">' + creditGroupsList.length + ' customers</td>' +
      '<td style="text-align:right;padding:12px;font-size:16px">\u00A3' + grandTotalOutstanding.toFixed(2) + '</td>' +
      '</tr></tfoot></table></div>';

    // ========== IN-DEPTH EXPENSES ==========
    let expensesHTML = '';
    const sortedExpenses = [...filteredExpenses].sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at));
    let expenseRowsHTML = '';
    let expTotalCash = 0, expTotalBank = 0;
    sortedExpenses.forEach(expense => {
      const amount = parseFloat(expense.amount || 0);
      const method = (expense.payment_method || '').toLowerCase();
      const isBankMethod = method === 'bank' || method === 'bank transfer';
      if (isBankMethod) expTotalBank += amount; else expTotalCash += amount;
      const expDate = expense.date ? new Date(expense.date).toLocaleDateString('en-GB') : 'N/A';
      expenseRowsHTML += '<tr>' +
        '<td>' + expDate + '</td>' +
        '<td><strong>' + (expense.expense_description || 'No description') + '</strong></td>' +
        '<td style="text-align:center"><span style="padding:2px 8px;border-radius:10px;font-size:10px;font-weight:600;background:' + (isBankMethod ? '#dbeafe;color:#1e40af' : '#dcfce7;color:#166534') + '">' + (isBankMethod ? 'Bank' : 'Cash') + '</span></td>' +
        '<td style="text-align:right;font-weight:600">\u00A3' + amount.toFixed(2) + '</td></tr>';
    });
    
    expensesHTML = '<div class="breakdown-section" style="page-break-before:always">' +
      '<h2 style="background:#fd7e14!important">IN-DEPTH EXPENSES</h2>' +
      '<p style="color:#666;margin-bottom:20px">All expenses recorded during the selected period (' + dateRangeText + ').</p>' +
      '<table><thead><tr><th>Date</th><th>Description</th><th style="text-align:center">Method</th><th style="text-align:right">Amount</th></tr></thead>' +
      '<tbody>' + expenseRowsHTML + '</tbody>' +
      '<tfoot>' +
      '<tr style="background:#e9ecef;font-weight:600"><td colspan="3" style="text-align:right;padding:10px">Cash Expenses:</td><td style="text-align:right;padding:10px">\u00A3' + expTotalCash.toFixed(2) + '</td></tr>' +
      '<tr style="background:#e9ecef;font-weight:600"><td colspan="3" style="text-align:right;padding:10px">Bank Expenses:</td><td style="text-align:right;padding:10px">\u00A3' + expTotalBank.toFixed(2) + '</td></tr>' +
      '<tr style="background:#1a365d;color:white;font-weight:700"><td colspan="3" style="text-align:right;padding:12px">TOTAL EXPENSES:</td>' +
      '<td style="text-align:right;padding:12px;font-size:16px">\u00A3' + (expTotalCash + expTotalBank).toFixed(2) + '</td></tr>' +
      '</tfoot></table></div>';

    const htmlContent = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Warehouse Breakdown Report</title>' +
      '<style>' +
      '@page{margin:0.5in;size:A4}' +
      'body{font-family:Arial,sans-serif;margin:0;padding:20px;color:#333;line-height:1.4;font-size:12px}' +
      '.header{background:linear-gradient(135deg,#1a365d 0%,#2c5282 100%);color:white;padding:25px;text-align:center;margin-bottom:30px;border-radius:8px}' +
      '.header h1{margin:0;font-size:28px}.header p{margin:5px 0 0 0;opacity:0.9}' +
      '.summary-section{background:#f8f9fa;border:3px solid #2c5282;border-radius:10px;padding:25px;margin-bottom:30px}' +
      '.summary-section h2{color:#2c5282;margin:0 0 20px 0;text-align:center;font-size:22px;border-bottom:2px solid #2c5282;padding-bottom:10px}' +
      '.summary-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}' +
      '.summary-box{background:white;border:2px solid #ddd;border-radius:8px;padding:20px;text-align:center}' +
      '.summary-box.cash{border-color:#28a745}.summary-box.bank{border-color:#007bff}.summary-box.credit{border-color:#dc3545}.summary-box.expenses{border-color:#fd7e14}' +
      '.summary-label{font-size:14px;color:#666;margin-bottom:8px;font-weight:600}' +
      '.summary-amount{font-size:28px;font-weight:700}' +
      '.summary-amount.cash{color:#28a745}.summary-amount.bank{color:#007bff}.summary-amount.credit{color:#dc3545}.summary-amount.expenses{color:#fd7e14}' +
      '.breakdown-section{margin-bottom:30px;page-break-before:always}' +
      '.breakdown-section h2{background:#2c5282;color:white;padding:15px 20px;margin:0 0 20px 0;border-radius:8px;font-size:20px}' +
      'table{width:100%;border-collapse:collapse;margin-bottom:20px;font-size:11px}' +
      'th{background:#e9ecef;padding:10px 8px;text-align:left;border:1px solid #dee2e6;font-weight:700;color:#333}' +
      'td{padding:8px;border:1px solid #dee2e6}tr:nth-child(even){background:#f8f9fa}tr:hover{background:#e9ecef}' +
      '.product-group{margin-bottom:25px;border:2px solid #2c5282;border-radius:8px;overflow:hidden}' +
      '.product-header{background:linear-gradient(135deg,#e9ecef 0%,#dee2e6 100%);padding:12px 15px;border-bottom:2px solid #2c5282}' +
      '.product-header h3{margin:0;color:#2c5282;font-size:16px}' +
      '.floor-section{page-break-before:always}' +
      '.floor-section h2{background:#28a745;color:white;padding:15px 20px;margin:0 0 20px 0;border-radius:8px;font-size:20px}' +
      '.in-stock{color:#28a745;font-weight:700}.low-stock{color:#fd7e14;font-weight:700}.out-stock{color:#dc3545;font-weight:700}' +
      '.footer{margin-top:40px;padding-top:20px;border-top:2px solid #ddd;text-align:center;font-size:11px;color:#666}' +
      '</style></head><body>' +
      
      '<div class="header"><h1>PLUSH LIVING LTD</h1><p>Complete Warehouse Breakdown Report</p>' +
      '<p style="margin-top:10px;font-size:14px">Period: ' + dateRangeText + '</p>' +
      '<p style="font-size:12px">Generated: ' + new Date().toLocaleDateString('en-GB') + ' at ' + new Date().toLocaleTimeString('en-GB') + '</p></div>' +
      
      '<div class="summary-section"><h2>FINANCIAL SUMMARY</h2>' +
      '<p style="color:#666;margin-bottom:15px">' + totalOrderCount + ' new orders' +
        (lumpSumTotal > 0 ? ' + \u00A3' + lumpSumTotal.toFixed(2) + ' in lump-sum account payments' : '') +
        ' in this period (' + dateRangeText + ').</p>' +
      '<div class="summary-grid">' +
      '<div class="summary-box" style="border-color:#1a365d;grid-column:1/-1"><div class="summary-label" style="font-size:16px">TOTAL SALES — New Bills (incl. VAT)</div><div class="summary-amount" style="color:#1a365d;font-size:38px">\u00A3' + grandTotalSales.toFixed(2) + '</div>' +
      '<div style="font-size:12px;color:#666;margin-top:6px">Total stock sold: <strong>' + grandTotalQtySold.toFixed(2) + '</strong> units/m\u00B2 &nbsp;&middot;&nbsp; Net (ex-VAT): \u00A3' + grandTotalSalesValue.toFixed(2) + '</div>' +
      '</div>' +
      '</div>' +
      '<div class="summary-grid" style="margin-top:15px">' +
      '<div class="summary-box cash"><div class="summary-label">CASH COLLECTED</div><div class="summary-amount cash">\u00A3' + grandTotalCash.toFixed(2) + '</div>' +
        '<div style="font-size:11px;color:#666;margin-top:5px">From bills: \u00A3' + cashFromNewBills.toFixed(2) + (lumpSumCash > 0 ? '<br>Lump-sum: \u00A3' + lumpSumCash.toFixed(2) : '') + '</div>' +
      '</div>' +
      '<div class="summary-box bank"><div class="summary-label">BANK COLLECTED</div><div class="summary-amount bank">\u00A3' + grandTotalBank.toFixed(2) + '</div>' +
        '<div style="font-size:11px;color:#666;margin-top:5px">From bills: \u00A3' + bankFromNewBills.toFixed(2) + (lumpSumBank > 0 ? '<br>Lump-sum: \u00A3' + lumpSumBank.toFixed(2) : '') + '</div>' +
      '</div>' +
      '<div class="summary-box" style="border-color:#17a2b8"><div class="summary-label">CASH + BANK</div><div class="summary-amount" style="color:#17a2b8">\u00A3' + (grandTotalCash + grandTotalBank).toFixed(2) + '</div></div>' +
      '</div>' +
      '<div class="summary-grid" style="margin-top:15px">' +
      '<div class="summary-box credit"><div class="summary-label">CREDIT OUTSTANDING</div><div class="summary-amount credit">\u00A3' + grandTotalCredit.toFixed(2) + '</div><div style="font-size:11px;color:#666;margin-top:5px">(Bills from this period only)</div></div>' +
      '<div class="summary-box expenses"><div class="summary-label">TOTAL EXPENSES</div><div class="summary-amount expenses">\u00A3' + totalExpenses.toFixed(2) + '</div><div style="font-size:11px;color:#666;margin-top:5px">Cash: \u00A3' + totalExpensesCash.toFixed(2) + ' | Bank: \u00A3' + totalExpensesBank.toFixed(2) + '</div></div>' +
      '<div class="summary-box" style="border-color:#6f42c1"><div class="summary-label">VAT COLLECTED</div><div class="summary-amount" style="color:#6f42c1">\u00A3' + grandTotalVAT.toFixed(2) + '</div></div>' +
      '</div>' +
      // 💰 NEW: COGS + Profit block
      '<div class="summary-grid" style="margin-top:15px">' +
      '<div class="summary-box" style="border-color:#6c757d"><div class="summary-label">COST OF GOODS SOLD</div><div class="summary-amount" style="color:#6c757d">\u00A3' + grandTotalSalesCost.toFixed(2) + '</div><div style="font-size:11px;color:#666;margin-top:5px">(sum of cost_price \u00D7 qty across all bills)</div></div>' +
      '<div class="summary-box" style="border-color:' + (grandTotalProfit >= 0 ? '#28a745' : '#dc3545') + '"><div class="summary-label">GROSS ' + (grandTotalProfit >= 0 ? 'PROFIT' : 'LOSS') + ' ON SALES</div><div class="summary-amount" style="color:' + (grandTotalProfit >= 0 ? '#28a745' : '#dc3545') + '">\u00A3' + Math.abs(grandTotalProfit).toFixed(2) + '</div><div style="font-size:11px;color:#666;margin-top:5px">= Net Sales (ex-VAT) \u00A3' + grandTotalSalesValue.toFixed(2) + ' \u2212 COGS</div></div>' +
      '<div class="summary-box" style="border-color:#20c997"><div class="summary-label">MARGIN %</div><div class="summary-amount" style="color:#20c997">' + (grandTotalSalesValue > 0 ? ((grandTotalProfit / grandTotalSalesValue) * 100).toFixed(1) : '0.0') + '%</div><div style="font-size:11px;color:#666;margin-top:5px">Profit / Net Sales (ex-VAT)</div></div>' +
      '</div>' +
      '<div style="margin-top:25px;padding:20px;background:linear-gradient(135deg,#d4edda 0%,#c3e6cb 100%);border:3px solid #28a745;border-radius:8px;text-align:center">' +
      '<div style="font-size:18px;font-weight:600;color:#155724;margin-bottom:10px">NET REVENUE (Cash + Bank - Expenses)</div>' +
      '<div style="font-size:36px;font-weight:700;color:#155724">\u00A3' + netRevenue.toFixed(2) + '</div></div></div>' +
      
      productSummaryHTML +
      
      '<div class="breakdown-section"><h2>IN-DEPTH PRODUCT BREAKDOWN</h2>' +
      '<p style="color:#666;margin-bottom:20px">Detailed breakdown of every product sold, including customer name, bill number, quantity, price, and VAT.</p>' +
      productBreakdownHTML + '</div>' +
      
      cashTxnHTML +
      
      bankTxnHTML +
      
      creditGivenHTML +
      
      creditAccountsHTML +
      
      expensesHTML +
      
      '<div class="floor-section"><h2>PRODUCTS ON THE FLOOR</h2>' +
      
      '<h3 style="margin:20px 0 10px;color:#2c5282;font-size:16px;border-bottom:2px solid #2c5282;padding-bottom:6px">Per m\u00B2 Products</h3>' +
      '<table><thead><tr><th>Product Name</th><th>Color</th><th style="text-align:right">Starting Stock</th><th style="text-align:right">Sold</th><th style="text-align:right">Restock</th><th style="text-align:right">Remaining</th></tr></thead>' +
      '<tbody>' + floorSqmHTML + '</tbody>' +
      '<tfoot><tr style="background:#e9ecef;font-weight:700"><td colspan="5" style="text-align:right;padding:10px">Total Variations:</td>' +
      '<td style="padding:10px">' + productsOnFloorSqm.length + '</td></tr></tfoot></table>' +
      
      '<h3 style="margin:30px 0 10px;color:#2c5282;font-size:16px;border-bottom:2px solid #2c5282;padding-bottom:6px">Products by Size</h3>' +
      '<table><thead><tr><th>Product Name</th><th>Size</th><th>Color</th><th style="text-align:right">Starting Stock</th><th style="text-align:right">Sold</th><th style="text-align:right">Restock</th><th style="text-align:right">Remaining</th></tr></thead>' +
      '<tbody>' + floorSizeHTML + '</tbody>' +
      '<tfoot><tr style="background:#e9ecef;font-weight:700"><td colspan="6" style="text-align:right;padding:10px">Total Variations:</td>' +
      '<td style="padding:10px">' + productsOnFloorSize.length + '</td></tr></tfoot></table></div>' +
      
      '<div class="footer"><strong>PLUSH LIVING LTD</strong><br>Unit 30 Adderley Rd, Saltley, Birmingham B8 1AN<br>' +
      'Tel: 0121 840 1673 | Email: Plushlivingltd@outlook.com | Website: Plush-livingltd.co.uk</div>' +
      '</body></html>';

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank', 'width=800,height=1000');
    if (!printWindow) {
      alert('Please allow popups to download reports');
    } else {
      printWindow.onload = () => { setTimeout(() => { printWindow.print(); }, 500); };
    }
    
    setShowWarehouseBreakdownModal(false);
  };

  const downloadAllCustomersReportOld = () => {
    if (!customers || customers.length === 0) {
      alert('No customers found');
      return;
    }

    // Calculate totals for all customers - USE ONLY fulfilledOrders
    // CRITICAL FIX: Do NOT combine fulfilledOrders + creditAccountsOrders 
    // as creditAccountsOrders is a SUBSET of fulfilledOrders, causing double counting
    const allCustomersData = customers
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map(customer => {
        const customerOrders = fulfilledOrders.filter(order => 
          order.customer_id === customer.id && 
          order.status !== 'returned' && 
          order.status !== 'cancelled'
        );
        
        let totalGrandTotal = 0;
        let totalCashPaid = 0;
        let totalBankPaid = 0;
        
        customerOrders.forEach(order => {
          totalGrandTotal += parseFloat(order.grand_total || 0);
          totalCashPaid += parseFloat(order.cash_paid_amount || 0);
          totalBankPaid += parseFloat(order.bank_paid_amount || 0);
        });
        
        // 💰 BULLETPROOF: Include lump-sum (account-level) payments in cash/bank breakdown
        const accountPaymentsData = customerAccountPayments[customer.id] || { total: 0, payments: [] };
        (accountPaymentsData.payments || []).forEach(p => {
          const amt = parseFloat(p.amount || 0);
          const method = (p.payment_method || '').toUpperCase();
          if (method === 'BANK') {
            totalBankPaid += amt;
          } else {
            totalCashPaid += amt;
          }
        });
        
        // 💰 BULLETPROOF: Credit = outstanding from creditAccountsOrders - account payments
        let totalCredit = 0;
        (creditAccountsOrders || []).filter(o => o.customer_id === customer.id).forEach(order => {
          totalCredit += parseFloat(order.outstanding_balance || 0);
        });
        totalCredit = Math.max(0, totalCredit - accountPaymentsData.total);
        
        // 💰 BULLETPROOF: Total Paid = Total Bill - Credit Owed (guaranteed match)
        const totalPaid = Math.max(0, totalGrandTotal - totalCredit);
        
        return {
          customer,
          customerOrders,
          totalGrandTotal,
          totalPaid,
          totalCashPaid,
          totalBankPaid,
          totalCredit
        };
      })
      .filter(data => data.customerOrders.length > 0); // Only include customers with orders

    if (allCustomersData.length === 0) {
      alert('No customers with purchase history found');
      return;
    }

    // Generate comprehensive HTML report
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>All Customers Warehouse Breakdown</title>
        <style>
          @page { 
            margin: 0.5in; 
            size: A4;
          }
          body { 
            font-family: 'Arial', sans-serif; 
            margin: 0; 
            padding: 20px;
            color: #333;
            line-height: 1.4;
          }
          .header {
            background-color: #3f51b5;
            color: white;
            padding: 20px;
            text-align: center;
            margin-bottom: 30px;
          }
          .header h1 {
            margin: 0;
            font-size: 28px;
          }
          .report-info {
            background: #f8f9fa;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 30px;
            text-align: center;
          }
          .customer-section {
            page-break-inside: avoid;
            margin-bottom: 40px;
            border: 2px solid #ddd;
            border-radius: 8px;
            overflow: hidden;
          }
          .customer-header {
            background: #e3f2fd;
            padding: 20px;
            border-bottom: 2px solid #3f51b5;
          }
          .customer-header h2 {
            margin: 0 0 10px 0;
            color: #333;
          }
          .customer-summary {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            margin-top: 15px;
          }
          .summary-box {
            padding: 15px;
            background: white;
            border: 2px solid #ddd;
            border-radius: 8px;
            text-align: center;
          }
          .summary-box.paid {
            border-color: #28a745;
          }
          .summary-box.credit {
            border-color: #dc3545;
          }
          .summary-label {
            font-size: 14px;
            color: #666;
            margin-bottom: 5px;
          }
          .summary-amount {
            font-size: 24px;
            font-weight: bold;
          }
          .summary-amount.paid {
            color: #28a745;
          }
          .summary-amount.credit {
            color: #dc3545;
          }
          .customer-body {
            padding: 20px;
            background: white;
          }
          .order {
            margin-bottom: 25px;
          }
          .order-header {
            background: #f8f9fa;
            padding: 10px 15px;
            border-radius: 6px 6px 0 0;
            border: 1px solid #ddd;
            border-bottom: none;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 15px;
            border: 1px solid #ddd;
          }
          th {
            background-color: #f8f9fa;
            padding: 10px;
            text-align: left;
            border: 1px solid #ddd;
            font-weight: 600;
          }
          td {
            padding: 10px;
            border: 1px solid #ddd;
          }
          .totals {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 10px;
            padding: 15px;
            background: #f8f9fa;
            border: 1px solid #ddd;
            border-radius: 0 0 6px 6px;
          }
          .total-item {
            text-align: center;
          }
          .total-label {
            font-size: 12px;
            color: #666;
            margin-bottom: 5px;
          }
          .total-value {
            font-size: 16px;
            font-weight: 600;
          }
          .total-value.grand {
            color: #007bff;
            font-size: 18px;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #ddd;
            text-align: center;
            font-size: 12px;
            color: #666;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>PLUSH LIVING LTD</h1>
          <p style="margin: 5px 0;">Complete Warehouse Breakdown - All Customers</p>
        </div>

        <div class="report-info">
          <strong>Report Generated:</strong> ${new Date().toLocaleDateString('en-GB')}<br>
          <strong>Total Customers:</strong> ${allCustomersData.length}
        </div>

        ${allCustomersData.map(data => {
          const { customer, customerOrders, totalPaid, totalCashPaid, totalBankPaid, totalCredit } = data;
          
          return `
            <div class="customer-section">
              <div class="customer-header">
                <h2>${customer.name || 'N/A'}</h2>
                <p style="margin: 5px 0;"><strong>Customer #:</strong> ${customer.customer_number || 'N/A'}</p>
                
                <div class="customer-summary">
                  <div class="summary-box paid">
                    <div class="summary-label">Total Paid</div>
                    <div class="summary-amount paid">£${totalPaid.toFixed(2)}</div>
                    <div style="margin-top: 10px; font-size: 14px; color: #666;">
                      💵 Cash: £${totalCashPaid.toFixed(2)}<br>
                      🏦 Bank: £${totalBankPaid.toFixed(2)}
                    </div>
                  </div>
                  <div class="summary-box credit">
                    <div class="summary-label">Credit Outstanding</div>
                    <div class="summary-amount credit">£${totalCredit.toFixed(2)}</div>
                  </div>
                </div>
              </div>

              <div class="customer-body">
                <h3 style="margin-bottom: 15px; color: #333;">Purchase History</h3>

                ${customerOrders.map(order => {
                  const subtotal = parseFloat(order.subtotal || 0);
                  const fittingCharge = parseFloat(order.fitting_charge || 0);
                  const cutPieceCharge = parseFloat(order.cut_piece_charge || 0);
                  const subtotalWithServices = subtotal + fittingCharge + cutPieceCharge;
                  // FIXED: Use stored vat_amount for per-item VAT accuracy
                  const vatAmount = parseFloat(order.vat_amount || 0);
                  const grandTotal = parseFloat(order.grand_total || 0);
                  const orderDate = new Date(order.date).toLocaleDateString('en-GB');

                  return `
                    <div class="order">
                      <div class="order-header">
                        <div>
                          <strong>Order #${order.id.substring(0, 8)}</strong>
                          <span style="margin-left: 15px; color: #666;">${orderDate}</span>
                        </div>
                        <div style="font-weight: 600;">
                          Grand Total: £${grandTotal.toFixed(2)}
                        </div>
                      </div>

                      <table>
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th style="text-align: center;">Quantity</th>
                            <th style="text-align: right;">Unit Price</th>
                            <th style="text-align: right;">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          ${order.items && order.items.length > 0 ? order.items.map(item => `
                            <tr>
                              <td>
                                <div><strong>${item.product_name || 'N/A'}</strong></div>
                                <div style="font-size: 12px; color: #666;">
                                  ${item.size && item.size !== 'N/A' ? `Size: ${item.size}` : ''}
                                  ${item.color ? ` | Color: ${item.color}` : ''}
                                </div>
                              </td>
                              <td style="text-align: center;">
                                ${(() => {
                                  let qty = 0;
                                  const unitPrice = parseFloat(item.unit_price || 1);
                                  const totalPrice = parseFloat(item.total_price || 0);
                                  if (item.measurement_unit === 'per_square_meter') {
                                    qty = parseFloat(item.sqm || 0);
                                    if (qty === 0 && unitPrice > 0) qty = totalPrice / unitPrice;
                                    return qty.toFixed(2) + ' m²';
                                  } else {
                                    qty = parseFloat(item.quantity || 0);
                                    if (qty === 0 && unitPrice > 0) qty = totalPrice / unitPrice;
                                    return Math.round(qty) + ' units';
                                  }
                                })()}
                              </td>
                              <td style="text-align: right;">£${parseFloat(item.unit_price || 0).toFixed(2)}</td>
                              <td style="text-align: right;"><strong>£${parseFloat(item.total_price || 0).toFixed(2)}</strong></td>
                            </tr>
                          `).join('') : '<tr><td colspan="4" style="text-align: center; color: #666;">No items found</td></tr>'}
                        </tbody>
                      </table>

                      <div style="padding: 15px; background: white; border: 1px solid #ddd; border-radius: 6px;">
                        <div style="display: grid; grid-template-columns: 1fr auto; gap: 10px; margin-bottom: 8px;">
                          <div style="color: #666;">Items Subtotal:</div>
                          <div style="font-weight: 600; text-align: right;">£${(order.items?.reduce((sum, item) => sum + parseFloat(item.total_price || 0), 0) || 0).toFixed(2)}</div>
                        </div>
                        ${order.fitting_charge > 0 ? `
                        <div style="display: grid; grid-template-columns: 1fr auto; gap: 10px; margin-bottom: 8px;">
                          <div style="color: #666;">Fitting Charge:</div>
                          <div style="font-weight: 600; text-align: right;">£${parseFloat(order.fitting_charge || 0).toFixed(2)}</div>
                        </div>
                        ` : ''}
                        ${order.cut_piece_charge > 0 ? `
                        <div style="display: grid; grid-template-columns: 1fr auto; gap: 10px; margin-bottom: 8px;">
                          <div style="color: #666;">Cut Piece Charge:</div>
                          <div style="font-weight: 600; text-align: right;">£${parseFloat(order.cut_piece_charge || 0).toFixed(2)}</div>
                        </div>
                        ` : ''}
                        ${order.discount > 0 ? `
                        <div style="display: grid; grid-template-columns: 1fr auto; gap: 10px; margin-bottom: 8px;">
                          <div style="color: #dc3545;">Discount:</div>
                          <div style="font-weight: 600; text-align: right; color: #dc3545;">-£${parseFloat(order.discount || 0).toFixed(2)}</div>
                        </div>
                        ` : ''}
                        <div style="border-top: 1px solid #ddd; margin-top: 8px; padding-top: 8px; display: grid; grid-template-columns: 1fr auto; gap: 10px; margin-bottom: 8px;">
                          <div style="color: #666;">Subtotal (No VAT):</div>
                          <div style="font-weight: 600; font-size: 18px; text-align: right;">£${subtotal.toFixed(2)}</div>
                        </div>
                        <div style="display: grid; grid-template-columns: 1fr auto; gap: 10px; margin-bottom: 8px;">
                          <div style="color: #666;">VAT (20%):</div>
                          <div style="font-weight: 600; font-size: 18px; text-align: right;">£${vatAmount.toFixed(2)}</div>
                        </div>
                        <div style="border-top: 2px solid #333; margin-top: 8px; padding-top: 8px; display: grid; grid-template-columns: 1fr auto; gap: 10px;">
                          <div style="font-size: 20px; font-weight: 700; color: #333;">Grand Total (Inc VAT):</div>
                          <div style="font-size: 22px; font-weight: 700; color: #007bff; text-align: right;">£${grandTotal.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  `;
                }).join('')}
              </div>
            </div>
          `;
        }).join('')}

        <!-- SUMMARY SECTION -->
        <div style="page-break-before: always; margin-top: 40px; padding: 30px; background: #f8f9fa; border: 3px solid #3f51b5; border-radius: 10px;">
          <h2 style="text-align: center; color: #3f51b5; margin-bottom: 30px; font-size: 28px;">
            📊 COMPLETE SALES SUMMARY
          </h2>

          ${(() => {
            // Aggregate all products with detailed price breakdown
            const productSummary = {};
            let totalCash = 0;
            let totalBank = 0;
            let totalVAT = 0;

            allCustomersData.forEach(data => {
              const { customerOrders, totalCashPaid, totalBankPaid } = data;
              
              totalCash += totalCashPaid;
              totalBank += totalBankPaid;
              
              customerOrders.forEach(order => {
                // Add VAT
                const vatAmount = parseFloat(order.vat_amount || 0);
                totalVAT += vatAmount;
                
                // Aggregate products
                if (order.items && order.items.length > 0) {
                  order.items.forEach(item => {
                    const productName = item.product_name || 'Unknown Product';
                    
                    if (!productSummary[productName]) {
                      productSummary[productName] = {
                        name: productName,
                        unit: item.measurement_unit === 'per_square_meter' ? 'm²' : 'units',
                        costPrice: 0,
                        salesBreakdown: {}, // { unitPrice: { qty, total } }
                      };
                    }
                    
                    // Calculate quantity
                    let qty = 0;
                    const unitPrice = parseFloat(item.unit_price || 0);
                    const totalPrice = parseFloat(item.total_price || 0);
                    
                    if (item.measurement_unit === 'per_square_meter') {
                      qty = parseFloat(item.sqm || 0);
                      if (qty === 0 && unitPrice > 0) qty = totalPrice / unitPrice;
                    } else {
                      qty = parseFloat(item.quantity || 0);
                      if (qty === 0 && unitPrice > 0) qty = totalPrice / unitPrice;
                    }
                    
                    // Look up wholesale price (cost)
                    let wholesalePrice = 0;
                    const product = products.find(p => p.name === item.product_name);
                    if (product && product.variations) {
                      const variation = product.variations.find(v => v.color === item.color);
                      if (variation && variation.wholesale_price) {
                        wholesalePrice = parseFloat(variation.wholesale_price);
                      }
                    }
                    
                    // Set cost price (use first found, should be same for all)
                    if (productSummary[productName].costPrice === 0) {
                      productSummary[productName].costPrice = wholesalePrice;
                    }
                    
                    // Group by unit price AND VAT status AND size/variation (sold price)
                    const hasVat = parseFloat(order.vat_amount || 0) > 0;
                    const sizeInfo = item.size || item.color || '';
                    const priceKey = `${unitPrice.toFixed(2)}_${hasVat ? 'vat' : 'novat'}_${sizeInfo}`;
                    if (!productSummary[productName].salesBreakdown[priceKey]) {
                      productSummary[productName].salesBreakdown[priceKey] = {
                        unitPrice: unitPrice,
                        quantity: 0,
                        totalSales: 0,
                        hasVat: hasVat,
                        size: sizeInfo,
                        orderIds: []
                      };
                    }
                    
                    productSummary[productName].salesBreakdown[priceKey].quantity += qty;
                    productSummary[productName].salesBreakdown[priceKey].totalSales += totalPrice;
                    // Track order ID for this sale
                    const shortOrderId = order.id ? order.id.substring(0, 8) : 'N/A';
                    if (!productSummary[productName].salesBreakdown[priceKey].orderIds.includes(shortOrderId)) {
                      productSummary[productName].salesBreakdown[priceKey].orderIds.push(shortOrderId);
                    }
                  });
                }
              });
            });

            // Convert to sorted array
            const productArray = Object.values(productSummary).sort((a, b) => {
              const aTotalValue = Object.values(a.salesBreakdown).reduce((sum, s) => sum + s.totalSales, 0);
              const bTotalValue = Object.values(b.salesBreakdown).reduce((sum, s) => sum + s.totalSales, 0);
              return bTotalValue - aTotalValue;
            });

            return `
              <div style="margin-bottom: 30px;">
                <h3 style="color: #000; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 20px;">
                  Products Sold Summary - Detailed Breakdown
                </h3>
                
                ${productArray.map((product, idx) => {
                  // Calculate totals for this product
                  const salesArray = Object.values(product.salesBreakdown).sort((a, b) => b.quantity - a.quantity);
                  const totalQty = salesArray.reduce((sum, s) => sum + s.quantity, 0);
                  const totalSales = salesArray.reduce((sum, s) => sum + s.totalSales, 0);
                  const totalCost = product.costPrice * totalQty;
                  const totalProfit = totalSales - totalCost;
                  
                  // Calculate totals correctly based on VAT status
                  const totalVatSales = salesArray.filter(s => s.hasVat).reduce((sum, s) => sum + s.totalSales, 0);
                  const totalNoVatSales = salesArray.filter(s => !s.hasVat).reduce((sum, s) => sum + s.totalSales, 0);
                  const subtotalFromVatSales = totalVatSales / 1.20;
                  const vatFromVatSales = totalVatSales - subtotalFromVatSales;
                  const totalSubtotal = subtotalFromVatSales + totalNoVatSales;
                  const hasAnyVat = totalVatSales > 0;
                  
                  return `
                    <div style="margin-bottom: 25px; border: 2px solid #000; border-radius: 8px; overflow: hidden; background: white;">
                      <!-- Product Header -->
                      <div style="background: #f8f9fa; padding: 15px; border-bottom: 2px solid #000;">
                        <h4 style="margin: 0; color: #000; font-size: 18px;">${product.name}</h4>
                        <div style="margin-top: 8px; color: #000; font-size: 14px;">
                          <strong>Cost Price:</strong> £${product.costPrice.toFixed(2)} per ${product.unit}
                        </div>
                      </div>
                      
                      <!-- Sales Breakdown Table -->
                      <table style="width: 100%; border-collapse: collapse;">
                        <thead>
                          <tr style="background: #e9ecef;">
                            <th style="padding: 10px; text-align: left; border: 1px solid #000; color: #000; font-weight: 700;">Size/Variant</th>
                            <th style="padding: 10px; text-align: left; border: 1px solid #000; color: #000; font-weight: 700;">Order ID(s)</th>
                            <th style="padding: 10px; text-align: left; border: 1px solid #000; color: #000; font-weight: 700;">Quantity Sold</th>
                            <th style="padding: 10px; text-align: right; border: 1px solid #000; color: #000; font-weight: 700;">Sold Price (per ${product.unit})</th>
                            <th style="padding: 10px; text-align: right; border: 1px solid #000; color: #000; font-weight: 700;">Subtotal (No VAT)</th>
                            <th style="padding: 10px; text-align: right; border: 1px solid #000; color: #000; font-weight: 700;">VAT (20%)</th>
                            <th style="padding: 10px; text-align: right; border: 1px solid #000; color: #000; font-weight: 700;">Total Inc VAT</th>
                            <th style="padding: 10px; text-align: right; border: 1px solid #000; color: #000; font-weight: 700;">Total Cost</th>
                            <th style="padding: 10px; text-align: right; border: 1px solid #000; color: #000; font-weight: 700;">Profit</th>
                          </tr>
                        </thead>
                        <tbody>
                          ${salesArray.map(sale => {
                            const cost = product.costPrice * sale.quantity;
                            const totalSaleValue = sale.totalSales;
                            // Only calculate VAT if this sale had VAT charged
                            let subtotalNoVAT, vatAmount, totalIncVAT;
                            if (sale.hasVat) {
                              // Sale includes VAT, extract it
                              totalIncVAT = totalSaleValue;
                              subtotalNoVAT = totalIncVAT / 1.20;
                              vatAmount = totalIncVAT - subtotalNoVAT;
                            } else {
                              // No VAT - total is the subtotal
                              subtotalNoVAT = totalSaleValue;
                              vatAmount = 0;
                              totalIncVAT = totalSaleValue;
                            }
                            const profit = totalIncVAT - cost;
                            const sizeDisplay = sale.size || 'Standard';
                            const orderIdsDisplay = (sale.orderIds || []).join(', ') || 'N/A';
                            return `
                              <tr>
                                <td style="padding: 10px; border: 1px solid #ddd; color: #000; font-weight: 600;">${sizeDisplay}</td>
                                <td style="padding: 10px; border: 1px solid #ddd; color: #000; font-size: 11px;">${orderIdsDisplay}</td>
                                <td style="padding: 10px; border: 1px solid #ddd; color: #000;">${sale.quantity.toFixed(2)} ${product.unit}${sale.hasVat ? '' : ' (No VAT)'}</td>
                                <td style="padding: 10px; text-align: right; border: 1px solid #ddd; color: #000; font-weight: 600;">£${sale.unitPrice.toFixed(2)}</td>
                                <td style="padding: 10px; text-align: right; border: 1px solid #ddd; color: #000;">£${subtotalNoVAT.toFixed(2)}</td>
                                <td style="padding: 10px; text-align: right; border: 1px solid #ddd; color: #000; font-weight: 600;">${sale.hasVat ? '£' + vatAmount.toFixed(2) : '-'}</td>
                                <td style="padding: 10px; text-align: right; border: 1px solid #ddd; color: #000; font-weight: 600;">£${totalIncVAT.toFixed(2)}</td>
                                <td style="padding: 10px; text-align: right; border: 1px solid #ddd; color: #000;">£${cost.toFixed(2)}</td>
                                <td style="padding: 10px; text-align: right; border: 1px solid #ddd; color: #000; font-weight: 600;">£${profit.toFixed(2)}</td>
                              </tr>
                            `;
                          }).join('')}
                          <!-- Product Totals -->
                          <tr style="background: #fff3cd; font-weight: 700;">
                            <td style="padding: 12px; border: 2px solid #000; color: #000; font-weight: 700;">TOTAL</td>
                            <td style="padding: 12px; border: 2px solid #000; color: #000;">-</td>
                            <td style="padding: 12px; border: 2px solid #000; color: #000;">${totalQty.toFixed(2)} ${product.unit}</td>
                            <td style="padding: 12px; text-align: right; border: 2px solid #000; color: #000;">-</td>
                            <td style="padding: 12px; text-align: right; border: 2px solid #000; color: #000; font-size: 16px;">£${totalSubtotal.toFixed(2)}</td>
                            <td style="padding: 12px; text-align: right; border: 2px solid #000; color: #000; font-size: 16px;">${hasAnyVat ? '£' + vatFromVatSales.toFixed(2) : '-'}</td>
                            <td style="padding: 12px; text-align: right; border: 2px solid #000; color: #000; font-size: 16px;">£${totalSales.toFixed(2)}</td>
                            <td style="padding: 12px; text-align: right; border: 2px solid #000; color: #000; font-size: 16px;">£${totalCost.toFixed(2)}</td>
                            <td style="padding: 12px; text-align: right; border: 2px solid #000; color: #000; font-size: 16px;">£${totalProfit.toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  `;
                }).join('')}
                
                <!-- Grand Totals Table -->
                <div style="margin-top: 30px; border: 3px solid #000; border-radius: 8px; overflow: hidden;">
                  <div style="background: #000; color: white; padding: 15px; text-align: center;">
                    <h4 style="margin: 0; font-size: 20px;">GRAND TOTALS</h4>
                  </div>
                  <table style="width: 100%; border-collapse: collapse; background: white;">
                    <tr>
                      <td style="padding: 15px; border: 1px solid #000; color: #000; font-weight: 700; font-size: 16px;">Total Sales Value:</td>
                      <td style="padding: 15px; border: 1px solid #000; text-align: right; color: #000; font-weight: 700; font-size: 18px;">
                        £${productArray.reduce((sum, p) => {
                          return sum + Object.values(p.salesBreakdown).reduce((s, sale) => s + sale.totalSales, 0);
                        }, 0).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td style="padding: 15px; border: 1px solid #000; color: #000; font-weight: 700; font-size: 16px;">Total Cost:</td>
                      <td style="padding: 15px; border: 1px solid #000; text-align: right; color: #000; font-weight: 700; font-size: 18px;">
                        £${productArray.reduce((sum, p) => {
                          const qty = Object.values(p.salesBreakdown).reduce((s, sale) => s + sale.quantity, 0);
                          return sum + (p.costPrice * qty);
                        }, 0).toFixed(2)}
                      </td>
                    </tr>
                    <tr style="background: #d4edda;">
                      <td style="padding: 15px; border: 2px solid #000; color: #000; font-weight: 700; font-size: 18px;">TOTAL PROFIT:</td>
                      <td style="padding: 15px; border: 2px solid #000; text-align: right; color: #000; font-weight: 700; font-size: 22px;">
                        £${productArray.reduce((sum, p) => {
                          const qty = Object.values(p.salesBreakdown).reduce((s, sale) => s + sale.quantity, 0);
                          const sales = Object.values(p.salesBreakdown).reduce((s, sale) => s + sale.totalSales, 0);
                          const cost = p.costPrice * qty;
                          return sum + (sales - cost);
                        }, 0).toFixed(2)}
                      </td>
                    </tr>
                  </table>
                </div>
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin-top: 30px;">
                <div style="background: white; padding: 20px; border-radius: 8px; border: 2px solid #000; text-align: center;">
                  <div style="font-size: 14px; color: #000; margin-bottom: 10px; font-weight: 600;">💵 TOTAL CASH COLLECTED</div>
                  <div style="font-size: 32px; font-weight: 700; color: #000;">£${totalCash.toFixed(2)}</div>
                </div>
                <div style="background: white; padding: 20px; border-radius: 8px; border: 2px solid #000; text-align: center;">
                  <div style="font-size: 14px; color: #000; margin-bottom: 10px; font-weight: 600;">🏦 TOTAL BANK COLLECTED</div>
                  <div style="font-size: 32px; font-weight: 700; color: #000;">£${totalBank.toFixed(2)}</div>
                </div>
                <div style="background: white; padding: 20px; border-radius: 8px; border: 2px solid #000; text-align: center;">
                  <div style="font-size: 14px; color: #000; margin-bottom: 10px; font-weight: 600;">💰 TOTAL VAT COLLECTED</div>
                  <div style="font-size: 32px; font-weight: 700; color: #000;">£${totalVAT.toFixed(2)}</div>
                </div>
              </div>

              <div style="margin-top: 30px; padding: 20px; background: white; border: 2px solid #000; border-radius: 8px;">
                <div style="display: grid; grid-template-columns: 1fr auto; gap: 10px; font-size: 20px;">
                  <div style="font-weight: 600; color: #000;">GRAND TOTAL REVENUE:</div>
                  <div style="font-weight: 700; color: #000; font-size: 24px;">£${(totalCash + totalBank).toFixed(2)}</div>
                </div>
              </div>
            `;
          })()}
        </div>

        <div class="footer">
          <strong>PLUSH LIVING LTD</strong><br>
          Unit 30 Adderley Rd, Saltley, Birmingham B8 1AN<br>
          Tel: 0121 840 1673 | Email: Plushlivingltd@outlook.com<br>
          Website: Plush-livingltd.co.uk
        </div>
      </body>
      </html>
    `;

    // Create and open print window
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    const printWindow = window.open(url, '_blank', 'width=800,height=1000');
    if (!printWindow) {
      alert('Please allow popups to download reports');
    } else {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };
    }
  };

  const handleApplicationApproval = async (applicationId, status, reviewNotes = '') => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.put(`${API}/api/admin-portal/wholesale-applications/${applicationId}`, {
        status: status,
        review_notes: reviewNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.message) {
        alert(`Application ${status} successfully!`);
        // Refresh the applications list
        fetchWholesaleApplications();
      }
    } catch (error) {
      console.error('Error updating application:', error);
      alert(`Failed to ${status} application`);
    }
  };

  // Check for existing customer session
  useEffect(() => {
    const checkCustomerSession = async () => {
      const token = localStorage.getItem('customer_token');
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/customer/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCustomer(response.data);
          
          // Fetch wholesale status
          const wholesaleResponse = await axios.get(`${API}/customer/wholesale-application`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setWholesaleStatus(wholesaleResponse.data);
        } catch (error) {
          localStorage.removeItem('customer_token');
        }
      }
    };

    // Customer Google sign-in removed: skip OAuth callback handling
    checkCustomerSession();
  }, []);

  const addToCart = (product, variation, quantity = 1) => {
    setCart(prev => {
      const existingItem = prev.find(item => 
        item.id === product.id && 
        JSON.stringify(item.variation) === JSON.stringify(variation)
      );

      if (existingItem) {
        return prev.map(item =>
          item.id === product.id && JSON.stringify(item.variation) === JSON.stringify(variation)
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      return [...prev, { ...product, variation, quantity }];
    });
  };

  const removeFromCart = (productId, variation) => {
    setCart(prev => prev.filter(item => 
      !(item.id === productId && JSON.stringify(item.variation) === JSON.stringify(variation))
    ));
  };

  // Page Components
  const Header = () => (
    <header className="header">
      <div className="header-content">
        {/* Plush Living LTD Logo */}
        <div className="logo-section">
          <div className="himalaya-logo">
            <img src="https://customer-assets.emergentagent.com/job_carpet-ai/artifacts/h7morq89_Screenshot%20%28161%29.png" alt="Plush Living LTD" className="plush-logo-img" />
          </div>
        </div>
        
        {/* Exact Himalaya Navigation */}
        <nav>
          <ul className="nav-links">
            <li><a href="#" className={currentPage === 'home' ? 'nav-link active' : 'nav-link'} onClick={(e) => { e.preventDefault(); setCurrentPage('home'); }}>HOME</a></li>
            <li><a href="#" className={currentPage === 'products' ? 'nav-link active' : 'nav-link'} onClick={(e) => { e.preventDefault(); setCurrentPage('products'); }}>PRODUCTS</a></li>
            <li><a href="#" className={currentPage === 'gallery' ? 'nav-link active' : 'nav-link'} onClick={(e) => { e.preventDefault(); setCurrentPage('gallery'); }}>GALLERY</a></li>
            <li><a href="#" className={currentPage === 'about' ? 'nav-link active' : 'nav-link'} onClick={(e) => { e.preventDefault(); setCurrentPage('about'); }}>ABOUT US</a></li>
            <li><a href="#" className={currentPage === 'contact' ? 'nav-link active' : 'nav-link'} onClick={(e) => { e.preventDefault(); setCurrentPage('contact'); }}>CONTACT</a></li>
          </ul>
        </nav>
        
        <div className="header-actions">
          <button className="contact-btn" onClick={() => setCurrentPage('contact')}>
            CONTACT
          </button>
          
          {/* Unified Sign In/Admin/Sign Out Button */}
          {customer ? (
            <div className="customer-menu">
              <img 
                src={customer.picture || '/default-avatar.png'} 
                alt="Profile" 
                className="profile-image"
                onClick={() => setCurrentPage('profile')}
                style={{
                  width: '35px',
                  height: '35px', 
                  borderRadius: '50%',
                  cursor: 'pointer',
                  border: '2px solid var(--himalaya-yellow)',
                  objectFit: 'cover'
                }}
              />
            </div>
          ) : isAdminLoggedIn ? (
            <>
              {/* Admin Portal button hidden from public view - access via URL parameter ?admin=true */}
              {showAdminPortalButton && (
                <button className="auth-btn secondary" onClick={() => { setCurrentPage('admin-portal'); setActiveTab('dashboard'); }}>Admin Portal</button>
              )}
              
              {/* Dynamic button: ADMIN when not in admin portal, SIGN OUT when in admin portal */}
              {currentPage === 'admin-portal' ? (
                <button className="auth-btn danger" data-testid="btn-sign-out" onClick={() => { localStorage.removeItem('admin_token'); setIsAdminLoggedIn(false); setDashboardUserInAdminMode(false); setCurrentPage('home'); }}>SIGN OUT ({loggedInAdminUsername})</button>
              ) : (
                <button className="auth-btn secondary" onClick={() => { setCurrentPage('admin-portal'); setActiveTab('dashboard'); }}>ADMIN</button>
              )}
            </>
          ) : (
            <>
              {/* Show Admin Portal button when ?admin=true parameter is present */}
              {showAdminPortalButton && (
                <button className="auth-btn secondary" onClick={() => { setCurrentPage('admin-portal'); setActiveTab('dashboard'); }}>Admin Portal</button>
              )}
              <button data-testid="btn-sign-in" className="auth-btn" onClick={() => setShowSignInModal(true)}>SIGN IN</button>
            </>
          )}
        </div>
      </div>
    </header>
  );

  const HeroSlider = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    
    const slides = [
      {
        id: 0,
        type: 'lvt',
        brand: 'plush living ltd',
        title: 'LVT CLICK',
        subtitle: '0.5MM WEAR LAYER BUILT IN UNDERLAY',
        cta: 'TAKE IT AWAY TODAY',
        background: 'https://images.unsplash.com/photo-1723897917319-3958c7b4aaa1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHx3b29kJTIwZmxvb3Jpbmd8ZW58MHx8fHwxNzU0ODM5OTE3fDA&ixlib=rb-4.1.0&q=85'
      },
      {
        id: 1,
        type: 'luxury',
        brand: 'plush living ltd',
        title: 'ULTIMATE LUXURY CARPET',
        subtitle: 'SUITABLE FOR DOMESTIC AND COMMERCIAL',
        cta: 'FITTING FREE | UNDERLAY',
        background: 'https://images.pexels.com/photos/259962/pexels-photo-259962.jpeg'
      },
      {
        id: 2,
        type: 'rugs',
        brand: 'plush living ltd',
        title: 'PREMIUM RUG COLLECTION',
        subtitle: 'TRADITIONAL • MODERN • PERSIAN • MOROCCAN • VINTAGE',
        cta: 'EXPLORE OUR COLLECTION',
        background: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw5fHxydWd8ZW58MHx8fHwxNzU0ODM5OTE3fDA&ixlib=rb-4.1.0&q=85'
      }
    ];

    useEffect(() => {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);
      return () => clearInterval(timer);
    }, []);

    const goToSlide = (index) => {
      setCurrentSlide(index);
    };

    const nextSlide = () => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    };

    const prevSlide = () => {
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    };

    return (
      <div className="hero-slider">
        {slides.map((slide, index) => (
          <div 
            key={slide.id}
            className={`hero-slide ${slide.type === 'lvt' ? 'lvt-slide' : ''} ${index === currentSlide ? 'active' : ''}`}
            style={{ 
              backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${slide.background})`
            }}
          >
            <div className="hero-content">
              <div className="hero-brand">{slide.brand}</div>
              <h1 className="hero-title">{slide.title}</h1>
              <p className="hero-subtitle">{slide.subtitle}</p>
              <div className="hero-cta-text">{slide.cta}</div>
            </div>
          </div>
        ))}
        
        {/* Slider Navigation */}
        <button className="slider-arrows slider-arrow-left" onClick={prevSlide}>
          &#8249;
        </button>
        <button className="slider-arrows slider-arrow-right" onClick={nextSlide}>
          &#8250;
        </button>
        
        <div className="slider-nav">
          {slides.map((_, index) => (
            <button 
              key={index}
              className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      </div>
    );
  };

  const FeaturedProductsSection = () => {
    // Get product image helper function with enhanced debugging and validation
    const getProductImage = (product) => {
      if (!product) {
        console.warn('getProductImage (Featured): No product provided');
        return null;
      }
      
      // 1) Main product base64 image
      if (product.image_base64 && product.image_base64.trim()) {
        const img = product.image_base64.startsWith('data:')
          ? product.image_base64
          : `data:image/jpeg;base64,${product.image_base64}`;
        console.log('✅ Found main product image_base64 for featured:', product.name);
        return img;
      }

      // 2) New multiple images array (first image)
      if (Array.isArray(product.images) && product.images.length > 0) {
        const firstImage = product.images[0];
        if (firstImage && firstImage.trim()) {
          const img = firstImage.startsWith('data:')
            ? firstImage
            : `data:image/jpeg;base64,${firstImage}`;
          console.log('✅ Found main product images[0] for featured:', product.name);
          return img;
        }
      }

      // 3) Main product URL(s)
      if (product.image_url && typeof product.image_url === 'string' && product.image_url.trim()) {
        console.log('✅ Found main product image_url for featured:', product.name);
        return product.image_url;
      }
      if (Array.isArray(product.image_urls) && product.image_urls.length > 0 && product.image_urls[0]) {
        console.log('✅ Found main product image_urls[0] for featured:', product.name);
        return product.image_urls[0];
      }

      // 4) Variation-level images (base64 or URL)
      const firstVarWithB64 = product.variations?.find(v => v.image_base64 && v.image_base64.trim());
      if (firstVarWithB64?.image_base64) {
        const img = firstVarWithB64.image_base64.startsWith('data:')
          ? firstVarWithB64.image_base64
          : `data:image/jpeg;base64,${firstVarWithB64.image_base64}`;
        console.log('✅ Found variation image_base64 for featured:', product.name);
        return img;
      }
      
      // Check for variation enhanced images
      if (product.variations && Array.isArray(product.variations) && product.variations.length > 0) {
        for (const variation of product.variations) {
          if (Array.isArray(variation.images) && variation.images.length > 0) {
            const firstVarImage = variation.images[0];
            if (firstVarImage && firstVarImage.trim()) {
              const img = firstVarImage.startsWith('data:')
                ? firstVarImage
                : `data:image/jpeg;base64,${firstVarImage}`;
              console.log('✅ Found variation images[0] for featured:', product.name);
              return img;
            }
          }
        }
      }
      
      const firstVarWithUrl = product.variations?.find(v => v.image_url && typeof v.image_url === 'string' && v.image_url.trim());
      if (firstVarWithUrl?.image_url) {
        console.log('✅ Found variation image_url for featured:', product.name);
        return firstVarWithUrl.image_url;
      }
      
      // Check for variation image_urls array
      if (product.variations && Array.isArray(product.variations) && product.variations.length > 0) {
        for (const variation of product.variations) {
          if (Array.isArray(variation.image_urls) && variation.image_urls.length > 0 && variation.image_urls[0]) {
            console.log('✅ Found variation image_urls[0] for featured:', product.name);
            return variation.image_urls[0];
          }
        }
      }
      
      // Only log in development mode
      if (process.env.NODE_ENV === 'development') {
        console.warn('❌ No image found for featured product:', product.name);
      }
      
      return null;
    };

    return (
      <section id="featured-products" className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">FEATURED PRODUCTS</h2>
          </div>
          
          <div className="products-slider">
            <div className="products-slider-container">
              {featuredProducts.map(product => {
                const productImage = getProductImage(product);
                
                return (
                  <div key={product.id} className="product-slide">
                    <div className="product-card" onClick={() => handleProductClick(product)}>
                      <div className="product-image">
                        {productImage ? (
                          <img 
                            src={productImage} 
                            alt={product.name}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className="placeholder-image" style={{display: productImage ? 'none' : 'flex'}}>
                          <span>No Image</span>
                        </div>
                      </div>
                      <div className="product-info">
                        <h3 className="product-name">{product.name}</h3>
                        <div className="product-price">
                          <span className="price contact-price">Contact for Price</span>
                        </div>
                        {product.measurement_unit && product.measurement_unit === 'per_square_meter' && (
                          <div className="product-calculator">
                            <small>Get a quote for your m² requirements</small>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    );
  };

  const CategoriesSection = () => (
    <section id="categories" className="section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">POPULAR CATEGORIES</h2>
        </div>
        
        <div className="categories-grid">
          {categories.map(category => (
            <div 
              key={category.id} 
              className="category-card"
              onClick={() => setCurrentPage(category.id)}
            >
              <div 
                className="category-image"
                style={{ backgroundImage: `url(${category.image})` }}
              ></div>
              <div className="category-content">
                <h3 className="category-title">{category.name}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const BrandLogos = () => (
    <section className="brands-section">
      <div className="container">
        <div className="brands-grid">
          <img src="https://images.pexels.com/photos/259962/pexels-photo-259962.jpeg?auto=compress&cs=tinysrgb&h=60&w=120" alt="Brand 1" className="brand-logo" />
          <img src="https://images.pexels.com/photos/259962/pexels-photo-259962.jpeg?auto=compress&cs=tinysrgb&h=60&w=120" alt="Brand 2" className="brand-logo" />
          <img src="https://images.pexels.com/photos/259962/pexels-photo-259962.jpeg?auto=compress&cs=tinysrgb&h=60&w=120" alt="Brand 3" className="brand-logo" />
          <img src="https://images.pexels.com/photos/259962/pexels-photo-259962.jpeg?auto=compress&cs=tinysrgb&h=60&w=120" alt="Brand 4" className="brand-logo" />
          <img src="https://images.pexels.com/photos/259962/pexels-photo-259962.jpeg?auto=compress&cs=tinysrgb&h=60&w=120" alt="Brand 5" className="brand-logo" />
        </div>
      </div>
    </section>
  );



  const TransformSection = () => (
    <section className="transform-section section">
      <div className="container">
        <div className="transform-content">
          <h2 className="section-title">TRANSFORM YOUR HOME WITH PLUSH LIVING LTD</h2>
          <p className="transform-description">
            Transform your living space into a haven of comfort and style with Plush Living LTD. 
            Elevate your home aesthetics and embrace the warmth and luxury that our exquisite carpets bring. 
            Immerse yourself in the allure of timeless design and impeccable craftsmanship, as each carpet 
            tells a story of tradition and quality. Unveil a new chapter in your home decor journey with 
            Plush Living LTD, where every step is a touch of elegance and every fibre is a testament to 
            refined living. Upgrade your surroundings and let the charm of Plush Living LTD redefine the 
            art of home transformation.
          </p>
          <button className="hero-cta" onClick={() => setCurrentPage('contact')}>
            CONTACT US
          </button>
        </div>
      </div>
    </section>
  );



  const TestimonialsSection = () => (
    <section className="testimonials-section section">
      <div className="container">
        <div className="section-header">
          <h4 className="section-subtitle">What our customers are saying?</h4>
        </div>
        
        <div className="testimonials-grid">
          <div className="testimonial-card">
            <h3>Exceptional Quality & Service</h3>
            <p className="testimonial-quote">
              The team at Plush Living LTD transformed our home with their exquisite carpet collection. From the initial 
              consultation to the final installation, every detail was handled with professionalism. The quality exceeded 
              our expectations, and the craftsmanship is truly remarkable. Our family has been recommending them to everyone!
            </p>
            <div className="testimonial-author">Sarah Mitchell</div>
          </div>
          
          <div className="testimonial-card">
            <h3>Outstanding Customer Experience</h3>
            <p className="testimonial-quote">
              I was amazed by the personalized attention we received. The staff took time to understand our vision and 
              guided us through their premium collection. The installation was seamless, and the result is absolutely 
              stunning. Plush Living LTD has earned a customer for life!
            </p>
            <div className="testimonial-author">Michael Thompson</div>
          </div>
          
          <div className="testimonial-card">
            <h3>Luxury That Speaks for Itself</h3>
            <p className="testimonial-quote">
              After visiting multiple showrooms, Plush Living LTD stood out with their elegant displays and knowledgeable 
              team. The Persian rugs we selected have become the centerpiece of our living room. The attention to detail 
              and commitment to excellence is truly impressive. Worth every penny!
            </p>
            <div className="testimonial-author">Emily Rodriguez</div>
          </div>
        </div>
      </div>
    </section>
  );

  const AboutPage = () => (
    <div className="about-page">
      <div className="about-hero">
        <div className="about-hero-content">
          <h1>About Plush Living LTD</h1>
        </div>
      </div>
      
      <div className="container">
        <div className="about-content">
          <div className="about-story">
            <div className="about-text-section">
              <h2>Our Story - How We Got Here</h2>
              <p>
                For generations, our family has been dedicated to the art of fine flooring, curating spaces where comfort meets elegance. With decades of experience, we bring not only expert craftsmanship but also the warmth and care that come from a proud family tradition. Whether you dream of a plush retreat for your living room, a refined statement for your office, or a complete transformation of your home, we are here to make it a reality.
              </p>
              
              <p>
                Backed by extensive stock, long-standing partnerships with trusted suppliers, and a keen eye for quality, we ensure you enjoy the very best—without compromising your budget. Our commitment to excellence has earned us something far more valuable than sales: the loyalty of generations of customers. Word-of-mouth has been our greatest advertisement, drawing visitors from across the country to experience our flagship store.
              </p>
            </div>
            
            <div className="about-image-section">
              <img 
                src="https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHx3b29kJTIwZmxvb3Jpbmd8ZW58MHx8fHwxNzU0ODM5OTE3fDA&ixlib=rb-4.1.0&q=85" 
                alt="About Plush Living LTD" 
                className="about-main-image"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const AdminPortal = () => {
    const [adminUsername, setAdminUsername] = useState('');
    const [adminPassword, setAdminPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    // activeTab state moved to main App component level

    // activeTab state moved to main App component level

    // Note: fetchDashboardData, handleDashboardLogout, and handleDashboardLogin are defined at App level

    const handleAdminLogin = async (e) => {
      e.preventDefault();
      console.log('Attempting robust admin login...');
      
      // Clear any existing authentication state before attempting login
      localStorage.removeItem('admin_token');
      localStorage.removeItem('customer_token');
      setIsAdminLoggedIn(false);
      setLoginError(''); // Clear any previous errors

      const loginData = { username: adminUsername, password: adminPassword };
      
      // MULTI-ATTEMPT LOGIN: Try with different configurations to ensure success
      const loginAttempts = [
        { timeout: 10000, name: 'standard' },
        { timeout: 20000, name: 'extended-timeout' },
        { timeout: 15000, withCredentials: true, name: 'with-credentials' }
      ];

      let lastError = null;

      for (let i = 0; i < loginAttempts.length; i++) {
        const attempt = loginAttempts[i];
        
        try {
          console.log(`🔐 Admin portal login attempt ${i + 1}/3 (${attempt.name})`);
          
          const response = await axios.post(`${API}/api/admin-portal/login`, loginData, {
            timeout: attempt.timeout,
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            withCredentials: attempt.withCredentials || false
          });
          
          console.log('✅ Admin portal login response:', response.data);
          
          if (response.data.access_token) {
            localStorage.setItem('admin_token', response.data.access_token);
            localStorage.setItem('admin_username', adminUsername); // Store logged-in username
            setLoggedInAdminUsername(adminUsername); // Track in state
            
            // Store admin permissions from login response
            if (response.data.user) {
              const userPerms = {
                allowedSections: response.data.user.allowed_sections || null,
                spectateSections: response.data.user.spectate_sections || null,
                role: response.data.user.role || null,
                priceAccessLevels: response.data.user.price_access_levels || ['retail'],
                canAccessDashboard: response.data.user.can_access_dashboard || false,
                canAccessSettings: response.data.user.can_access_settings || false
              };
              setAdminPermissions(userPerms);
              // Also store in localStorage for persistence across page refreshes
              localStorage.setItem('admin_permissions', JSON.stringify(userPerms));
              console.log('📋 Admin permissions loaded:', userPerms);
            }
            
            setIsAdminLoggedIn(true);
            setCurrentPage('admin-portal'); // Switch to admin portal page
            setActiveTab('dashboard'); // Reset to dashboard on login
            setShowSignInModal(false); // Close the sign-in modal
            console.log('✅ Admin logged in successfully as:', adminUsername);
            
            // Clear form data after successful login
            setAdminUsername('');
            setAdminPassword('');
            setLoginError('');
            return; // SUCCESS - exit function
          }
        } catch (error) {
          console.error(`❌ Admin portal login attempt ${i + 1} failed:`, error);
          lastError = error;
          
          // If this is a 401, don't retry (wrong credentials)
          if (error.response?.status === 401) {
            break;
          }
          
          // Wait briefly before next attempt (except for last attempt)
          if (i < loginAttempts.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }

      // ALL ATTEMPTS FAILED
      console.error('❌ All admin portal login attempts failed. Last error:', lastError);
      
      // Clear any partial authentication states on error
      localStorage.removeItem('admin_token');
      localStorage.removeItem('customer_token');
      setIsAdminLoggedIn(false);
      
      // Enhanced error messages
      if (lastError) {
        if (lastError.code === 'ECONNABORTED' || lastError.message.includes('timeout')) {
          setLoginError('Connection timeout after multiple attempts. Please check your internet connection.');
        } else if (lastError?.response?.status === 401) {
          setLoginError('Invalid username or password');
        } else if (lastError?.message?.includes('Network Error')) {
          setLoginError('Network error after multiple attempts. Please check your internet connection.');
        } else if (lastError?.response?.status >= 500) {
          setLoginError('Server error after multiple attempts. Please try again later.');
        } else {
          setLoginError('Login failed after multiple attempts. Please try again.');
        }
      } else {
        setLoginError('Login failed. Please try again.');
      }
    };

  // Admin Sections Data
  const adminSections = [
    {
        id: 'products',
        title: 'PRODUCTS',
        icon: '📦',
        description: 'Manage products, variations, and inventory',
        color: '#8B4513'
      },
      {
        id: 'product-enquiries',
        title: 'PRODUCT ENQUIRIES',
        icon: '🔍',
        description: 'Quick product lookup for customer enquiries',
        color: '#38B2AC'
      },
      {
        id: 'customers',
        title: 'CUSTOMERS', 
        icon: '👥',
        description: 'Customer management and information',
        color: '#4A90E2'
      },
      {
        id: 'orders',
        title: 'ORDERS',
        icon: '📋',
        description: 'Pending orders and order management',
        color: '#5A67D8'
      },
      {
        id: 'fulfilled-orders',
        title: 'FULFILLED ORDERS',
        icon: '✅',
        description: 'Completed and fulfilled orders',
        color: '#48BB78'
      },
      {
        id: 'daily-activity',
        title: 'DAILY ACTIVITY',
        icon: '📅',
        description: 'Daily orders and payments by customer',
        color: '#9F7AEA'
      },
      {
        id: 'deleted-orders',
        title: 'DELETED ORDERS',
        icon: '🗑️',
        description: 'Review and verify deleted orders',
        color: '#E53E3E'
      },
      {
        id: 'edited-orders',
        title: 'EDITED ORDERS',
        icon: '✏️',
        description: 'Review orders that have been modified',
        color: '#ED8936'
      },
      {
        id: 'invoices',
        title: 'INVOICES',
        icon: '🧾',
        description: 'Invoice generation and management',
        color: '#6B73FF'
      },
      {
        id: 'credit-accounts',
        title: 'CREDIT ACCOUNTS',
        icon: '💳',
        description: 'Credit accounts and bill management', 
        color: '#38A169'
      },
      {
        id: 'accounting',
        title: 'ACCOUNTING',
        icon: '📊',
        description: 'Financial reports and analytics',
        color: '#D69E2E'
      },
      {
        id: 'warehouse-breakdown',
        title: 'WAREHOUSE BREAKDOWN',
        icon: '🏭',
        description: 'Customer purchase history and credit analysis',
        color: '#6366F1'
      },
      {
        id: 'expenses',
        title: 'EXPENSES',
        icon: '💰',
        description: 'Track business expenses and recurring costs',
        color: '#E53E3E'
      },
      {
        id: 'gallery',
        title: 'GALLERY',
        icon: '🖼️',
        description: 'Media and image gallery management',
        color: '#ED8936'
      },
      {
        id: 'queries',
        title: 'QUERIES',
        icon: '❓',
        description: 'Customer inquires and support',
        color: '#E53E3E'
      },
      {
        id: 'store-locations',
        title: 'STORE LOCATIONS',
        icon: '📍',
        description: 'Store locations and contact info',
        color: '#9F7AEA'
      },

      {
        id: 'settings',
        title: 'SETTINGS',
        icon: '⚙️',
        description: 'System settings and configuration',
        color: '#A0AEC0'
      },
      {
        id: 'session-management',
        title: 'SESSION MANAGEMENT',
        icon: '🔐',
        description: 'View and manage active login sessions',
        color: '#DC2626'
      },
      {
        id: 'business-dashboard',
        title: 'BUSINESS DASHBOARD',
        icon: '📈',
        description: 'Sales overview, charts and business metrics',
        color: '#10B981'
      },
      {
        id: 'catalogue',
        title: 'CATALOGUE',
        icon: '📖',
        description: 'Generate product catalogues',
        color: '#9F7AEA'
      }
    ];

    if (isAdminLoggedIn) {
      return (
        <div className="admin-portal-full">
          {/* Return to Dashboard User - subtle indicator strip */}
          {dashboardUserInAdminMode && (
            <div style={{
              background: '#1e293b',
              padding: '6px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #334155'
            }}>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <div style={{width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e'}}></div>
                <span style={{color: '#94a3b8', fontSize: '12px', fontWeight: '500'}}>Dashboard View</span>
              </div>
              <button
                onClick={returnToDashboardUser}
                style={{
                  background: 'transparent',
                  color: '#94a3b8',
                  border: '1px solid #475569',
                  padding: '4px 12px',
                  borderRadius: '4px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontSize: '11px',
                  transition: 'all 0.2s'
                }}
                onMouseOver={e => { e.currentTarget.style.background = '#334155'; e.currentTarget.style.color = '#fff'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
              >
                ← Exit to Dashboard
              </button>
            </div>
          )}
          
          {/* Admin Portal Title with Real-time Sync Status */}
          <div className="admin-portal-title">
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <h1>Admin Portal Dashboard</h1>
              </div>
              {/* Sync runs automatically in background - no visible indicator needed */}
            </div>
          </div>
          
          {/* Admin Dashboard - Show cards when on dashboard, show content when on specific tab */}
          {activeTab === 'dashboard' ? (
            <div className="admin-dashboard-grid">
              {adminSections
                .filter(section => {
                  const isManager = isManagerAccount();
                  
                  // SETTINGS - only managers OR users with can_access_settings permission
                  if (section.id === 'settings' && !isManager && !adminPermissions.canAccessSettings) return false;
                  // DELETED ORDERS only visible to managers
                  if (section.id === 'deleted-orders' && !isManager) return false;
                  // EDITED ORDERS only visible to managers
                  if (section.id === 'edited-orders' && !isManager) return false;
                  // DAILY ACTIVITY only visible to managers
                  if (section.id === 'daily-activity' && !isManager) return false;
                  // SESSION MANAGEMENT only visible to managers
                  if (section.id === 'session-management' && !isManager) return false;
                  // BUSINESS DASHBOARD - only managers OR users with can_access_dashboard permission
                  if (section.id === 'business-dashboard' && !isManager && !adminPermissions.canAccessDashboard) return false;
                  return true;
                })
                .map((section) => (
                <div 
                  key={section.id}
                  className="admin-card"
                  onClick={() => handleCardClick(section.id)}
                  style={{ '--card-color': section.color }}
                >
                  <div className="admin-card-icon">
                    <span>{section.icon}</span>
                  </div>
                  <div className="admin-card-content">
                    <h3 className="admin-card-title">{section.title}</h3>
                    <p className="admin-card-description">{section.description}</p>
                    <div className="admin-card-arrow">→</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Show section content when not on dashboard */
            <div className="admin-section-content">
              {/* ORDERS SECTION - Already working perfectly */}
              {activeTab === 'orders' && (
                <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div className="admin-section-header">
                    <div className="section-nav">
                      <button 
                        className="btn secondary"
                        onClick={() => setActiveTab('dashboard')}
                        style={{marginRight: '1rem'}}
                      >
                        ← Back to Dashboard
                      </button>
                      <h3 style={{color: '#b8860b', margin: 0}}>Order Management</h3>
                    </div>
                    <button 
                      className="admin-btn primary"
                      onClick={() => {
                        setShowCreateOrderModal(true);
                        loadCustomers();
                      }}
                    >
                      + Create Order
                    </button>
                  </div>
                  
                  <div className="admin-stats-row" style={{margin: '2rem 0'}}>
                    <div className="stat-item">
                      <span className="stat-number">
                        {Array.isArray(orders) ? orders.filter(order => order && order.status === 'pending').length : 0}
                      </span>
                      <span className="stat-label">Pending Orders</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">
                        £{Array.isArray(orders) ? orders.reduce((total, order) => {
                          if (order && order.grand_total) {
                            return total + parseFloat(order.grand_total);
                          }
                          return total;
                        }, 0).toFixed(2) : '0.00'}
                      </span>
                      <span className="stat-label">Total Value</span>
                    </div>
                  </div>

                  <div className="admin-table-container">
                    <div className="table-toolbar" style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:'1rem', flexWrap:'wrap'}}>
                      <div></div>
                    </div>
                    <h4>Recent Orders</h4>
                    {loadingOrders ? (
                      <div className="loading-message">Loading orders...</div>
                    ) : orders && orders.length > 0 ? (
                      <div className="table-responsive">
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th>Order ID</th>
                              <th>Customer</th>
                              <th>Sale Type</th>
                              <th>Items</th>
                              <th>Storage Location</th>
                              <th>Total</th>
                              <th>Status</th>
                              <th>Date</th>
                              <th style={{minWidth: '200px', width: '200px'}}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ordersFiltered.map((order, index) => (
                              <tr key={order?.id || `order-${index}`}>
                                <td style={{wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '80px', lineHeight: '1.3', minWidth: '70px'}}>{(() => {
                                  // Use Customer Management data but fallback to backend data
                                  if (order?.customer_id === null || !order?.customer_id || order.customer_id === 'guest') {
                                    // FIXED: Use order.customer_name for guest customers
                                    return order.customer_name || 'Guest Customer';
                                  }
                                  
                                  // Find customer in Customer Management database
                                  const customer = customers.find(c => c.id === order.customer_id);
                                  if (customer) {
                                    return customer.company_name || customer.name || 'No Company';
                                  }
                                  
                                  // Fallback to backend enriched data if customer not found
                                  return order.customer_name || 'Customer name';
                                })()}</td>
                                <td style={{wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '80px', lineHeight: '1.3', minWidth: '70px'}}>{(() => {
                                  // Use Customer Management data but fallback to backend data
                                  if (order?.customer_id === null || !order?.customer_id || order.customer_id === 'guest') {
                                    // FIXED: Use order.customer_name for guest customers
                                    return order.customer_name || 'Guest Customer';
                                  }
                                  
                                  // Find customer in Customer Management database FIRST
                                  const customer = customers.find(c => c.id === order.customer_id);
                                  if (customer) {
                                    return customer.name || 'No Name';
                                  }
                                  
                                  // Fallback to backend enriched data if customer not found
                                  return order.customer_name || 'Customer name';
                                })()}</td>
                                <td>
                                  <span className={`sale-type-badge ${order?.sale_type || 'retail'}`}>
                                    {order?.sale_type ? order.sale_type.charAt(0).toUpperCase() + order.sale_type.slice(1) : 'Retail'}
                                  </span>
                                </td>
                                <td>{order?.items?.length || 0} items</td>
                                <td>
                                  {(() => {
                                    // Extract storage locations from order items
                                    const storageLocations = order?.items?.map(item => item.storage_location || item.storageLocation)
                                      .filter(location => location) // Remove null/undefined
                                      .filter((location, index, arr) => arr.indexOf(location) === index); // Remove duplicates
                                    
                                    if (storageLocations && storageLocations.length > 0) {
                                      return storageLocations.join(', ');
                                    } else {
                                      return <span style={{color: '#999', fontStyle: 'italic'}}>Not specified</span>;
                                    }
                                  })()}
                                </td>
                                <td>£{order?.grand_total ? parseFloat(order.grand_total).toFixed(2) : '0.00'}</td>
                                <td>
                                  <span className={`status ${order?.status || 'pending'}`}>
                                    {order?.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : 'Pending'}
                                  </span>
                                </td>
                                <td>{order?.date ? new Date(order.date).toLocaleDateString() : 'N/A'}</td>
                                <td>
                                  <div style={{display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'flex-start'}}>
                                    <button 
                                      className="btn small secondary"
                                      onClick={() => handleViewOrder(order.id)}
                                      style={{width: '160px', fontSize: '11px', padding: '3px 6px'}}
                                    >
                                      View
                                    </button>
                                    {order?.status === 'fulfilled' ? (
                                      <button 
                                        className="btn small success"
                                        style={{width: '160px', fontSize: '11px', padding: '3px 6px'}}
                                        disabled
                                      >
                                        Fulfilled
                                      </button>
                                    ) : (
                                      <button 
                                        className="btn small primary"
                                        onClick={() => handleMarkFulfilled(order.id)}
                                        style={{width: '160px', fontSize: '11px', padding: '3px 6px'}}
                                        disabled={canCreateOrderOnly()}
                                        title={canCreateOrderOnly() ? 'Workers cannot fulfill orders - contact manager' : ''}
                                      >
                                        {canCreateOrderOnly() ? 'Fulfill' : 'Fulfill'}
                                      </button>
                                    )}
                                    <button 
                                      className="btn small primary"
                                      onClick={() => handleEditOrder(order.id)}
                                      style={{width: '160px', fontSize: '11px', padding: '3px 6px'}}
                                    >
                                      Edit
                                    </button>
                                    <button 
                                      className="btn small danger"
                                      onClick={() => handleReturnOrder(order.id)}
                                      style={{width: '160px', fontSize: '11px', padding: '3px 6px'}}
                                    >
                                      Return
                                    </button>
                                    <button 
                                      className="btn small danger"
                                      onClick={() => handleDeleteOrder(order.id)}
                                      style={{width: '160px', fontSize: '11px', padding: '3px 6px', backgroundColor: '#dc3545', borderColor: '#dc3545'}}
                                      disabled={canCreateOrderOnly()}
                                      title={canCreateOrderOnly() ? 'Workers cannot delete orders - contact manager' : ''}
                                    >
                                      {canCreateOrderOnly() ? 'Delete' : 'Delete'}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="no-orders-message">
                        <p>No orders found. Create your first order using the "Create Order" button above.</p>
                      </div>
                    )}
                  </div>
                  
                  <p style={{marginTop: '2rem', color: '#6c757d'}}>Manage pending orders and shipping coordination.</p>
                </div>
              )}

              {/* PRODUCTS SECTION - New inline format */}
              {activeTab === 'products' && (
                <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div className="admin-section-header">
                    <div className="section-nav">
                      <button 
                        className="btn secondary"
                        onClick={() => setActiveTab('dashboard')}
                        style={{marginRight: '1rem'}}
                      >
                        ← Back to Dashboard
                      </button>
                      <h3 style={{color: '#b8860b', margin: 0}}>Product Management</h3>
                    </div>
                    <div className="btn-group" style={{display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'wrap'}}>
                      <button 
                        className="admin-btn secondary"
                        onClick={async () => {
                          console.log('Manual refresh requested');
                          setProductsLastUpdated(Date.now()); // Force memoization refresh
                          await loadProducts();
                        }}
                        style={{border: '1px solid #b8860b', color: '#b8860b'}}
                      >
                        🔄 Refresh
                      </button>
                      
                      <button 
                        className="admin-btn primary"
                        onClick={() => setShowAddProductModal(true)}
                        disabled={isSpectateOnly('products')}
                        style={{
                          opacity: isSpectateOnly('products') ? 0.5 : 1,
                          cursor: isSpectateOnly('products') ? 'not-allowed' : 'pointer'
                        }}
                        title={isSpectateOnly('products') ? 'Spectate mode - cannot add products' : ''}
                      >
                        {isSpectateOnly('products') ? '🔒 Add Product' : '+ Add Product'}
                      </button>
                      <button 
                        className="admin-btn secondary"
                        onClick={() => setShowCreatePdfPanel(true)}
                      >
                        Create PDF
                      </button>
                      {/* 📊 Date range for Individual & Combined Product Reports */}
                      <div style={{display:'flex', alignItems:'center', gap:'6px', padding:'6px 10px', background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:'6px'}} data-testid="product-report-date-range">
                        <span style={{fontSize:'11px', color:'#666', fontWeight:600}}>📅 Report Range:</span>
                        <input
                          type="date"
                          value={productReportFromDate}
                          onChange={(e) => setProductReportFromDate(e.target.value)}
                          style={{fontSize:'11px', padding:'3px 6px', border:'1px solid #ccc', borderRadius:'4px'}}
                          data-testid="product-report-from-date"
                          title="From date (optional)"
                        />
                        <span style={{fontSize:'11px', color:'#666'}}>to</span>
                        <input
                          type="date"
                          value={productReportToDate}
                          onChange={(e) => setProductReportToDate(e.target.value)}
                          style={{fontSize:'11px', padding:'3px 6px', border:'1px solid #ccc', borderRadius:'4px'}}
                          data-testid="product-report-to-date"
                          title="To date (optional)"
                        />
                        {(productReportFromDate || productReportToDate) && (
                          <button
                            onClick={() => { setProductReportFromDate(''); setProductReportToDate(''); }}
                            style={{fontSize:'10px', padding:'2px 6px', border:'1px solid #dc3545', background:'white', color:'#dc3545', borderRadius:'4px', cursor:'pointer'}}
                            data-testid="product-report-clear-dates"
                            title="Clear dates (All Time)"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      {selectedProductsForReport.length > 0 && (
                        <button 
                          className="admin-btn"
                          onClick={() => generateMultiProductReport(selectedProductsForReport)}
                          style={{background: '#17a2b8', color: 'white', border: 'none'}}
                        >
                          📊 Combined Report ({selectedProductsForReport.length})
                        </button>
                      )}
                      {selectedProductsForReport.length > 0 && (
                        <button 
                          className="admin-btn secondary"
                          onClick={() => setSelectedProductsForReport([])}
                          style={{border: '1px solid #dc3545', color: '#dc3545'}}
                        >
                          Clear Selection
                        </button>
                      )}
                    </div>
                  </div>



                  <div className="admin-stats-row" style={{margin: '1.25rem 0 2rem'}}>
                    <div className="stat-item">
                      <span className="stat-number">{stats.totalProducts}</span>
                      <span className="stat-label">Total Products</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">{stats.totalStock}</span>
                      <span className="stat-label">Units in Stock</span>
                    </div>
                    <div className="stat-item">

                  {showCreatePdfPanel && (
                    <div className="pdf-panel" style={{background:'#0b1323', color:'#fff', padding:'1rem', borderRadius:'8px', marginBottom:'1rem', border:'1px solid #b8860b'}}>
                      <div style={{display:'flex', gap:'1rem', alignItems:'flex-start', flexWrap:'wrap'}}>
                        <div style={{minWidth:'260px', flex:'1'}}>
                          <label style={{display:'block', marginBottom:'0.5rem'}}>Select products</label>
                          <input 
                            type="text"
                            placeholder="Search products..."
                            className="form-control"
                            onChange={(e) => {
                              const term = e.target.value.toLowerCase();
                              const matches = products.filter(p => (p?.name||'').toLowerCase().includes(term));
                              // Pre-select matches if term non-empty; else keep existing selection untouched
                              if (term) {
                                setPdfSelectedProducts(matches.map(m => m.id));
                              }
                            }}
                          />
                          <div className="pdf-product-list" style={{maxHeight:'200px', overflowY:'auto', background:'#0f1a35', border:'1px solid #243354', borderRadius:'6px', padding:'0.5rem', marginTop:'0.5rem'}}>
                            {products.map(p => (
                              <label key={p.id} style={{display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.25rem 0', borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
                                <input 
                                  type="checkbox"
                                  checked={pdfSelectedProducts.includes(p.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) setPdfSelectedProducts(prev => [...new Set([...prev, p.id])]);
                                    else setPdfSelectedProducts(prev => prev.filter(id => id !== p.id));
                                  }}
                                />
                                <span>{p.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label style={{display:'block', marginBottom:'0.5rem'}}>Price Type</label>
                          <select className="form-control" value={pdfPriceType} onChange={(e)=> setPdfPriceType(e.target.value)}>
                            <option value="retail">Retail</option>
                            <option value="wholesale">Wholesale</option>
                            <option value="fitter">Fitter</option>
                          </select>
                        </div>
                        <div style={{display:'flex', gap:'0.5rem', alignItems:'flex-end'}}>
                          <button className="btn primary" onClick={async ()=>{
                            if (!pdfSelectedProducts.length) { alert('Please select at least one product'); return; }
                            try {
                              const res = await axios.post(`${API}/api/products/pdf`, {
                                product_ids: pdfSelectedProducts,
                                price_type: pdfPriceType
                              }, {
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` },
                                responseType: 'blob'
                              });
                              // trigger download
                              const blob = new Blob([res.data], { type: 'application/pdf' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `plush_catalog_${new Date().toISOString().slice(0,10)}.pdf`;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              URL.revokeObjectURL(url);
                            } catch (e) {
                              console.error('PDF generation failed', e);
                              alert('Failed to generate PDF: ' + (e.response?.data?.detail || e.message));
                            }
                          }}>Generate</button>
                          <button className="btn secondary" onClick={()=> setShowCreatePdfPanel(false)}>Close</button>
                        </div>
                      </div>
                    </div>
                  )}

                      <span className="stat-number">{stats.totalSold}</span>
                      <span className="stat-label">Units Sold</span>
                    </div>
                  </div>


                  <div className="admin-table-container">
                    <div className="table-toolbar" style={{marginBottom:'0.75rem'}}>

                    </div>
                    <h4>Product Inventory</h4>
                    <div className="table-responsive">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th style={{width: '40px', textAlign: 'center'}}>
                              <input 
                                type="checkbox"
                                checked={selectedProductsForReport.length > 0 && selectedProductsForReport.length === filteredProducts.length}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedProductsForReport(filteredProducts);
                                  } else {
                                    setSelectedProductsForReport([]);
                                  }
                                }}
                                title="Select all for combined report"
                                style={{cursor: 'pointer', width: '16px', height: '16px'}}
                              />
                            </th>
                            <th>Image</th>
                            <th>Name</th>
                            <th>Storage</th>
                            <th>Category</th>
                            <th>Retail Price</th>
                            <th>Wholesale Price</th>
                            <th>Fitter Price</th>
                            <th>Cost Price</th>
                            <th>Stock</th>
                            <th>Status</th>
                            <th style={{minWidth: '200px', width: '200px'}}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredProducts.length > 0 ? (
                            filteredProducts.map(product => {
                              // Compute total stock correctly across variations
                              const isSqm = product?.measurement_unit === 'per_square_meter' || product?.measurement_unit === 'per_sqm';
                              const totalStock = (() => {
                                if (product?.variations && product.variations.length > 0) {
                                  if (isSqm) {
                                    return product.variations.reduce((sum, v) => sum + (parseFloat(v.square_meters_available ?? v.square_meters ?? 0) || 0), 0);
                                  }
                                  return product.variations.reduce((sum, v) => sum + (parseInt(v.stock_quantity ?? v.quantity ?? v.stock ?? 0) || 0), 0);
                                }
                                // Fallback to legacy quantity on product
                                return parseInt(product.quantity || 0) || 0;
                              })();

                              const unitLabel = isSqm ? 'sqm' : 'units';
                              const priceUnit = isSqm ? '/m²' : '';

                              return (
                                <tr key={product.id} style={{background: selectedProductsForReport.some(p => p.id === product.id) ? '#e6f3ff' : 'inherit'}}>
                                  <td style={{textAlign: 'center'}}>
                                    <input 
                                      type="checkbox"
                                      checked={selectedProductsForReport.some(p => p.id === product.id)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedProductsForReport(prev => [...prev, product]);
                                        } else {
                                          setSelectedProductsForReport(prev => prev.filter(p => p.id !== product.id));
                                        }
                                      }}
                                      title="Select for combined report"
                                      style={{cursor: 'pointer', width: '16px', height: '16px'}}
                                    />
                                  </td>
                                  <td>
                                    <div style={{width: '60px', height: '60px'}}>
                                      {(() => {
                                        // Get product image using same logic as getProductImage
                                        let productImage = null;
                                        
                                        // 1) Main product base64 image
                                        if (product.image_base64) {
                                          productImage = product.image_base64.startsWith('data:')
                                            ? product.image_base64
                                            : `data:image/jpeg;base64,${product.image_base64}`;
                                        }
                                        // 2) New multiple images array (first image)
                                        else if (Array.isArray(product.images) && product.images.length > 0) {
                                          const firstImage = product.images[0];
                                          if (firstImage) {
                                            productImage = firstImage.startsWith('data:')
                                              ? firstImage
                                              : `data:image/jpeg;base64,${firstImage}`;
                                          }
                                        }
                                        // 3) Check first variation image
                                        else if (product.variations && product.variations.length > 0) {
                                          const firstVariation = product.variations[0];
                                          if (firstVariation.images && Array.isArray(firstVariation.images) && firstVariation.images.length > 0) {
                                            const varImage = firstVariation.images[0];
                                            if (varImage) {
                                              productImage = varImage.startsWith('data:')
                                                ? varImage
                                                : `data:image/jpeg;base64,${varImage}`;
                                            }
                                          } else if (firstVariation.image_base64) {
                                            productImage = firstVariation.image_base64.startsWith('data:')
                                              ? firstVariation.image_base64
                                              : `data:image/jpeg;base64,${firstVariation.image_base64}`;
                                          }
                                        }

                                        return productImage ? (
                                          <img 
                                            src={productImage} 
                                            alt={product.name}
                                            style={{
                                              width: '60px',
                                              height: '60px',
                                              objectFit: 'cover',
                                              borderRadius: '4px',
                                              border: '1px solid #ddd'
                                            }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                              e.target.nextSibling.style.display = 'flex';
                                            }}
                                          />
                                        ) : (
                                          <div style={{
                                            width: '60px',
                                            height: '60px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#f8f9fa',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: '0.7rem',
                                            color: '#6c757d'
                                          }}>
                                            No Image
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </td>
                                  <td style={{wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '60px', lineHeight: '1.2', minWidth: '50px', padding: '6px 8px'}}>
                                    <div className="product-name-cell" style={{fontSize: '0.85em'}}>
                                      <span className="product-name">{product.name}</span>
                                      <span className="product-collection">{product.collection}</span>
                                    </div>
                                  </td>
                                  <td style={{wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '60px', lineHeight: '1.2', minWidth: '50px', padding: '6px 8px'}}>
                                    {product.storage_location ? (
                                      <span className="storage-location-cell" style={{color: '#b8860b', fontSize: '0.85em', fontWeight: '500', display: 'block'}}>
                                        📍 {product.storage_location}
                                      </span>
                                    ) : (
                                      <span style={{color: '#666', fontStyle: 'italic', fontSize: '0.85em'}}>-</span>
                                    )}
                                  </td>
                                  <td style={{wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '60px', lineHeight: '1.2', minWidth: '50px', padding: '6px 8px', fontSize: '0.85em'}}>{product.category || '-'}</td>
                                  <td style={{wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '60px', lineHeight: '1.2', minWidth: '50px', padding: '6px 8px', fontSize: '0.85em'}}>
                                    {(() => {
                                      // Show price range for products with variations
                                      if (product.variations && product.variations.length > 0) {
                                        const retailPrices = product.variations
                                          .map(v => parseFloat(v.retail_price) || 0)
                                          .filter(p => p > 0);
                                        
                                        if (retailPrices.length > 0) {
                                          const minPrice = Math.min(...retailPrices);
                                          const maxPrice = Math.max(...retailPrices);
                                          
                                          if (minPrice === maxPrice) {
                                            return `£${minPrice.toFixed(2)} ${priceUnit}`;
                                          } else {
                                            return (
                                              <div style={{lineHeight: '1.2'}}>
                                                <div>£{minPrice.toFixed(2)} -</div>
                                                <div>£{maxPrice.toFixed(2)} {priceUnit}</div>
                                              </div>
                                            );
                                          }
                                        }
                                      }
                                      
                                      // Fallback to main product retail price
                                      const retailPrice = parseFloat(product.retail_price) || 0;
                                      return `£${retailPrice.toFixed(2)} ${priceUnit}`;
                                    })()}
                                  </td>
                                  <td style={{wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '60px', lineHeight: '1.2', minWidth: '50px', padding: '6px 8px', fontSize: '0.85em'}}>
                                    {(() => {
                                      // Show price range for products with variations
                                      if (product.variations && product.variations.length > 0) {
                                        const wholesalePrices = product.variations
                                          .map(v => parseFloat(v.wholesale_price) || 0)
                                          .filter(p => p > 0);
                                        
                                        if (wholesalePrices.length > 0) {
                                          const minPrice = Math.min(...wholesalePrices);
                                          const maxPrice = Math.max(...wholesalePrices);
                                          
                                          if (minPrice === maxPrice) {
                                            return `£${minPrice.toFixed(2)} ${priceUnit}`;
                                          } else {
                                            return (
                                              <div style={{lineHeight: '1.2'}}>
                                                <div>£{minPrice.toFixed(2)} -</div>
                                                <div>£{maxPrice.toFixed(2)} {priceUnit}</div>
                                              </div>
                                            );
                                          }
                                        }
                                      }
                                      
                                      // Fallback to main product wholesale price
                                      const wholesalePrice = parseFloat(product.wholesale_price) || 0;
                                      return `£${wholesalePrice.toFixed(2)} ${priceUnit}`;
                                    })()}
                                  </td>
                                  <td style={{wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '60px', lineHeight: '1.2', minWidth: '50px', padding: '6px 8px', fontSize: '0.85em'}}>
                                    {(() => {
                                      // Show price range for products with variations
                                      if (product.variations && product.variations.length > 0) {
                                        const fitterPrices = product.variations
                                          .map(v => parseFloat(v.fitter_price) || 0)
                                          .filter(p => p > 0);
                                        
                                        if (fitterPrices.length > 0) {
                                          const minPrice = Math.min(...fitterPrices);
                                          const maxPrice = Math.max(...fitterPrices);
                                          
                                          if (minPrice === maxPrice) {
                                            return `£${minPrice.toFixed(2)} ${priceUnit}`;
                                          } else {
                                            return (
                                              <div style={{lineHeight: '1.2'}}>
                                                <div>£{minPrice.toFixed(2)} -</div>
                                                <div>£{maxPrice.toFixed(2)} {priceUnit}</div>
                                              </div>
                                            );
                                          }
                                        }
                                      }
                                      
                                      // Fallback to main product fitter price
                                      const fitterPrice = parseFloat(product.fitter_price) || 0;
                                      return `£${fitterPrice.toFixed(2)} ${priceUnit}`;
                                    })()}
                                  </td>
                                  <td style={{wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '60px', lineHeight: '1.2', minWidth: '50px', padding: '6px 8px', fontSize: '0.85em'}}>
                                    {(() => {
                                      // Show price range for products with variations
                                      if (product.variations && product.variations.length > 0) {
                                        const costPrices = product.variations
                                          .map(v => parseFloat(v.cost_price) || 0)
                                          .filter(p => p > 0);
                                        
                                        if (costPrices.length > 0) {
                                          const minPrice = Math.min(...costPrices);
                                          const maxPrice = Math.max(...costPrices);
                                          
                                          if (minPrice === maxPrice) {
                                            return `£${minPrice.toFixed(2)} ${priceUnit}`;
                                          } else {
                                            return (
                                              <div style={{lineHeight: '1.2'}}>
                                                <div>£{minPrice.toFixed(2)} -</div>
                                                <div>£{maxPrice.toFixed(2)} {priceUnit}</div>
                                              </div>
                                            );
                                          }
                                        }
                                      }
                                      
                                      // Fallback to main product cost price
                                      const costPrice = parseFloat(product.cost_price) || 0;
                                      return `£${costPrice.toFixed(2)} ${priceUnit}`;
                                    })()}
                                  </td>
                                  <td>
                                    {(() => {
                                      // Determine appropriate stock status class
                                      let stockClass = 'stock-status-good';
                                      let stockSuffix = '';
                                      
                                      if (totalStock <= 0) {
                                        stockClass = 'stock-status-out';
                                        stockSuffix = ' 🚫 OUT OF STOCK';
                                      } else if (totalStock <= 5) {
                                        stockClass = 'stock-status-critical';
                                        stockSuffix = ' 🔥 CRITICAL';
                                      } else if (totalStock <= 15) {
                                        stockClass = 'stock-status-low';
                                        stockSuffix = ' ⚠️ LOW STOCK';
                                      } else {
                                        stockSuffix = ' ✅ IN STOCK';
                                      }
                                      
                                      return (
                                        <span className={stockClass}>
                                          {isSqm ? (totalStock % 1 === 0 ? Math.round(totalStock) : parseFloat(totalStock).toFixed(1)) : Math.round(totalStock)} {unitLabel}{stockSuffix}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td>
                                    <span className={`status ${product.status || 'active'}`}>
                                      {product.status ? product.status.charAt(0).toUpperCase() + product.status.slice(1) : 'Active'}
                                    </span>
                                  </td>
                                  <td>
                                    <div style={{display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'flex-start'}}>
                                      <button 
                                        className="btn small secondary"
                                        onClick={() => setSelectedProduct(product)}
                                        style={{width: '160px', fontSize: '11px', padding: '3px 6px'}}
                                      >
                                        View
                                      </button>
                                      <button 
                                        className="btn small primary"
                                        onClick={() => handleEditProduct(product.id)}
                                        style={{width: '160px', fontSize: '11px', padding: '3px 6px'}}
                                        disabled={isSpectateOnly('products')}
                                        title={isSpectateOnly('products') ? 'Spectate mode - cannot edit' : ''}
                                      >
                                        {isSpectateOnly('products') ? '🔒 Edit' : 'Edit'}
                                      </button>
                                      <button 
                                        className="btn small danger"
                                        onClick={() => handleDeleteProduct(product.id)}
                                        style={{width: '160px', fontSize: '11px', padding: '3px 6px'}}
                                        disabled={isSpectateOnly('products')}
                                        title={isSpectateOnly('products') ? 'Spectate mode - cannot delete' : ''}
                                      >
                                        {isSpectateOnly('products') ? '🔒 Delete' : 'Delete'}
                                      </button>
                                      <button 
                                        className="btn small"
                                        onClick={() => generateIndividualProductReport(product)}
                                        style={{width: '160px', fontSize: '11px', padding: '3px 6px', background: '#17a2b8', color: 'white', border: 'none'}}
                                      >
                                        📊 Report
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="12" style={{textAlign:'center', padding:'1.5rem'}}>No products found.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* CUSTOMERS SECTION - New inline format */}
              <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: activeTab === 'customers' ? 'block' : 'none'}}>
                  <div className="admin-section-header">
                    <div className="section-nav">
                      <button 
                        className="btn secondary"
                        onClick={() => setActiveTab('dashboard')}
                        style={{marginRight: '1rem'}}
                      >
                        ← Back to Dashboard
                      </button>
                      <h3 style={{color: '#b8860b', margin: 0}}>Customer Management</h3>
                    </div>
                    <button 
                      className="admin-btn primary"
                      onClick={() => setShowAddCustomerModal(true)}
                      disabled={isSavingCustomer}
                    >
                      {isSavingCustomer ? 'Adding…' : '+ Add Customer'}
                    </button>
                  </div>

                  <div className="admin-stats-row" style={{margin: '2rem 0'}}>
                    <div className="stat-item">
                      <span className="stat-number">{customers.length}</span>
                      <span className="stat-label">Total Customers</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">{customers.filter(c => c.status === 'active').length}</span>
                      <span className="stat-label">Active Customers</span>
                    </div>
                  </div>

                  <div className="admin-table-container">
                    <div className="table-toolbar" style={{marginBottom:'1rem'}}>
                      <PLUFilter
                        label="Filter Customers"
                        placeholder="001, 002, 003..."
                        onFilterChange={setCustomersSearchInput}
                      />
                    </div>
                    <h4>Customer Database</h4>
                    <div className="table-responsive">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Customer #</th>
                            <th>Customer ID</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th style={{minWidth: '200px', width: '200px'}}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customersFiltered.length > 0 ? customersFiltered.map(customer => (
                            <tr key={customer.id}>
                              <td>
                                <strong style={{color: '#007bff', fontSize: '11px'}}>
                                  {customer.customer_number || 'N/A'}
                                </strong>
                              </td>
                              <td style={{wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '85px', lineHeight: '1.3', minWidth: '70px'}}>{customer.company_name || customer.name || 'N/A'}</td>
                              <td style={{wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '75px', lineHeight: '1.3', minWidth: '65px'}}>{customer.name || 'N/A'}</td>
                              <td style={{wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '100px', lineHeight: '1.3', minWidth: '85px'}}>{customer.email || 'N/A'}</td>
                              <td style={{wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '85px', lineHeight: '1.3', minWidth: '70px', fontFamily: 'monospace'}}>{customer.contact_number || customer.phone || 'N/A'}</td>
                              <td>
                                <span className={`customer-type-badge ${customer.customer_type || 'retail'}`}>
                                  {customer.customer_type ? customer.customer_type.charAt(0).toUpperCase() + customer.customer_type.slice(1) : 'Retail'}
                                </span>
                              </td>
                              <td>
                                <span className={`status ${customer.status || 'active'}`}>
                                  {customer.status ? customer.status.charAt(0).toUpperCase() + customer.status.slice(1) : 'Active'}
                                </span>
                              </td>
                              <td>
                                <div style={{display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'flex-start'}}>
                                  <button 
                                    className="btn small secondary"
                                    onClick={() => handleViewCustomer(customer)}
                                    style={{width: '160px', fontSize: '11px', padding: '3px 6px'}}
                                  >
                                    View
                                  </button>
                                  <button 
                                    className="btn small"
                                    onClick={() => setPaymentHistoryCustomer(customer)}
                                    data-testid={`payment-history-btn-${customer.customer_number}`}
                                    style={{width: '160px', fontSize: '11px', padding: '3px 6px', background: '#17a2b8', color: 'white', border: 'none'}}
                                  >
                                    💳 Payment History
                                  </button>
                                  <button 
                                    className="btn small primary"
                                    onClick={() => handleEditCustomer(customer)}
                                    style={{width: '160px', fontSize: '11px', padding: '3px 6px'}}
                                  >
                                    Edit
                                  </button>
                                  <button 
                                    className="btn small danger"
                                    onClick={() => handleDeleteCustomer(customer.id)}
                                    style={{width: '160px', fontSize: '11px', padding: '3px 6px'}}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan="8" style={{textAlign: 'center', padding: '2rem'}}>
                                No customers found. Add your first customer using the "Add Customer" button above.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

              {/* GALLERY SECTION - New inline format */}
              {activeTab === 'gallery' && (
                <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div className="admin-section-header">
                    <div className="section-nav">
                      <button 
                        className="btn secondary"
                        onClick={() => setActiveTab('dashboard')}
                        style={{marginRight: '1rem'}}
                      >
                        ← Back to Dashboard
                      </button>
                      <h3 style={{color: '#b8860b', margin: 0}}>Gallery Management</h3>
                    </div>
                    <button 
                      className="admin-btn primary"
                      onClick={() => setShowAddGalleryModal(true)}
                    >
                      + Add Gallery Item
                    </button>
                  </div>

                  <div className="admin-stats-row" style={{margin: '2rem 0'}}>
                    <div className="stat-item">
                      <span className="stat-number">{galleryItems.length}</span>
                      <span className="stat-label">Gallery Items</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">{galleryItems.filter(item => item.status === 'active').length}</span>
                      <span className="stat-label">Active Items</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">{galleryItems.filter(item => item.featured).length}</span>
                      <span className="stat-label">Featured Items</span>
                    </div>
                  </div>

                  <div className="admin-table-container">
                    <h4>Gallery Collection</h4>
                    <div className="gallery-grid">
                      {galleryItems.length > 0 ? galleryItems.map(item => (
                        <div key={item.id} className="gallery-item-card">
                          <div className="gallery-item-image">
                            <img src={item.image_url || '/placeholder-image.jpg'} alt={item.title} />
                          </div>
                          <div className="gallery-item-info">
                            <h5>{item.title || 'Untitled'}</h5>
                            <p>{item.description || 'No description'}</p>
                            <div className="gallery-item-meta">
                              <span className={`status ${item.status || 'active'}`}>
                                {item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : 'Active'}
                              </span>
                              {item.featured && <span className="featured-badge">Featured</span>}
                            </div>
                            <div className="gallery-item-actions">
                              <button className="btn small secondary">View</button>
                              <button className="btn small primary">Edit</button>
                              <button className="btn small danger">Delete</button>
                            </div>
                          </div>
                        </div>
                      )) : (
                        <div className="no-gallery-message">
                          <p>No gallery items found. Add your first gallery item using the "Add Gallery Item" button above.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* FULFILLED ORDERS SECTION */}
              {false && activeTab === 'fulfilled-orders' && (
                <FulfilledOrdersSection
                  fulfilledOrders={fulfilledOrders}
                  loading={loadingFulfilledOrders}
                  paidAmountSuccessMessage={paidAmountSuccessMessage}
                  customers={customers}
                  highlightedOrderId={highlightedOrderId}
                  API={API}
                />
              )}

              {/* TEMP: Using old code until table is added to component */}
              <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: activeTab === 'fulfilled-orders' ? 'block' : 'none'}}>
                  <div className="admin-section-header">
                    <div className="section-nav" style={{display:'flex', alignItems:'center', gap:'1rem'}}>
                      <button 
                        className="btn secondary"
                        onClick={() => setActiveTab('dashboard')}
                      >
                        ← Back to Dashboard
                      </button>
                      <h3 style={{color: '#b8860b', margin: 0, flex: 1}}>Fulfilled Orders</h3>
                      {loggedInAdminUsername.toLowerCase() === 'zobayr' && (
                        <button
                          data-testid="view-cancelled-bills-btn"
                          onClick={async () => { await loadCancelledOrders(); setShowCancelledModal(true); }}
                          style={{background:'#9B2C2C', color:'white', border:'none', padding:'8px 14px', borderRadius:'6px', fontWeight:'600', cursor:'pointer', fontSize:'13px'}}
                          title="View and restore cancelled bills (Owner only)"
                        >
                          🚫 Cancelled Bills
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="admin-table-container">
                    <div className="table-toolbar" style={{marginBottom:'0.75rem'}}>

                    </div>
                    {/* Top counters */}
                    <div className="counters" style={{display:'flex', gap:'1rem', marginBottom:'1rem'}}>
                      {(() => {
                        const orders = fulfilledOrders || [];
                        let totalCashReceived = 0;
                        let totalBankReceived = 0;
                        
                        for (const o of orders) {
                          // Cash received = cash_paid_amount across all orders
                          totalCashReceived += parseFloat(o.cash_paid_amount || 0) || 0;
                          // Bank received = bank_paid_amount across all orders
                          totalBankReceived += parseFloat(o.bank_paid_amount || 0) || 0;
                        }
                        
                        // 💰 BULLETPROOF: Lump-sum (account-level) payments are real cash/bank money received.
                        // Add them to the appropriate counter so the math closes:
                        // Total Sales = Cash + Bank + Credit Outstanding
                        Object.values(customerAccountPayments || {}).forEach(data => {
                          (data.payments || []).forEach(p => {
                            const amt = parseFloat(p.amount || 0);
                            const method = (p.payment_method || '').toUpperCase();
                            if (method === 'BANK') totalBankReceived += amt;
                            else totalCashReceived += amt; // cash, cheque, other → cash bucket
                          });
                        });
                        
                        // 💰 BULLETPROOF: Credit Outstanding = SAME SOURCE as Credit Accounts page & Warehouse Report
                        // Use creditAccountsOrders (which already excludes deleted, includes all statuses)
                        // and subtract account payments per-customer (capped at gross outstanding)
                        const grossByCustomer = {};
                        let totalCreditGross = 0;
                        (creditAccountsOrders || []).forEach(o => {
                          const out = parseFloat(o.outstanding_balance || 0);
                          if (out <= 0) return;
                          totalCreditGross += out;
                          const cid = o.customer_id;
                          if (cid) {
                            grossByCustomer[cid] = (grossByCustomer[cid] || 0) + out;
                          }
                        });
                        let totalApplied = 0;
                        Object.entries(customerAccountPayments || {}).forEach(([cid, data]) => {
                          const acctTotal = parseFloat((data && data.total) || 0);
                          if (acctTotal <= 0) return;
                          const gross = grossByCustomer[cid] || 0;
                          totalApplied += Math.min(acctTotal, gross);
                        });
                        const totalCreditOutstanding = Math.max(0, totalCreditGross - totalApplied);
                        
                        // Calculate total expenses by payment method
                        const totalCashExpensesClient = expenses.filter(e => e.payment_method === 'cash').reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
                        const totalBankExpensesClient = expenses.filter(e => e.payment_method === 'bank').reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
                        
                        // 💰 SINGLE SOURCE OF TRUTH: Prefer server-computed values when available.
                        // This guarantees identical numbers across all browsers/devices regardless of
                        // which JS bundle version is cached. Client values used only as fallback.
                        const s = serverDashboardStats;
                        const finalCash = s ? s.cash_sales : totalCashReceived;
                        const finalBank = s ? s.bank_sales : totalBankReceived;
                        const finalCredit = s ? s.credit_unpaid : totalCreditOutstanding;
                        const totalCashExpenses = s ? s.expenses_cash : totalCashExpensesClient;
                        const totalBankExpenses = s ? s.expenses_bank : totalBankExpensesClient;
                        
                        // Calculate net amounts after expenses
                        const netCash = finalCash - totalCashExpenses;
                        const netBank = finalBank - totalBankExpenses;
                        
                        return (
                          <>
                            <div className="stat-item" style={{background:'#0f1a35', color:'#fff', padding:'0.2cm', borderRadius:'8px', border:'1px solid #243354', fontSize:'16px', fontWeight:'700', minWidth:'fit-content', display:'inline-flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center'}}>
                              <div style={{fontSize: '18px'}}>💵 Cash Sales: £{finalCash.toFixed(2)}</div>
                              {totalCashExpenses > 0 && (
                                <>
                                  <div style={{fontSize: '14px', color: '#ff6b6b', marginTop: '4px'}}>- Expenses: £{totalCashExpenses.toFixed(2)}</div>
                                  <div style={{fontSize: '16px', color: '#51cf66', marginTop: '4px', borderTop: '1px solid #fff', paddingTop: '4px'}}>= Net: £{netCash.toFixed(2)}</div>
                                </>
                              )}
                            </div>
                            <div className="stat-item" style={{background:'#0f1a35', color:'#fff', padding:'0.2cm', borderRadius:'8px', border:'1px solid #243354', fontSize:'16px', fontWeight:'700', minWidth:'fit-content', display:'inline-flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center'}}>
                              <div style={{fontSize: '18px'}}>🏦 Bank Sales: £{finalBank.toFixed(2)}</div>
                              {totalBankExpenses > 0 && (
                                <>
                                  <div style={{fontSize: '14px', color: '#ff6b6b', marginTop: '4px'}}>- Expenses: £{totalBankExpenses.toFixed(2)}</div>
                                  <div style={{fontSize: '16px', color: '#51cf66', marginTop: '4px', borderTop: '1px solid #fff', paddingTop: '4px'}}>= Net: £{netBank.toFixed(2)}</div>
                                </>
                              )}
                            </div>
                            <div className="stat-item" style={{background:'#0f1a35', color:'#fff', padding:'0.2cm', borderRadius:'8px', border:'1px solid #243354', fontSize:'18px', fontWeight:'700', minWidth:'fit-content', display:'inline-flex', alignItems:'center', justifyContent:'center', textAlign:'center', whiteSpace:'nowrap'}}>
                              💳 Credit Sales (Unpaid): £{finalCredit.toFixed(2)}
                            </div>
                          </>
                        );
                      })()}
                      
                      {/* Sales Report Generator - Same color as other counters */}
                      <div style={{
                        background: '#0f1a35',
                        color: '#fff',
                        padding: '0.2cm',
                        borderRadius: '8px',
                        border: '1px solid #243354',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.3cm',
                        marginLeft: 'auto',
                        minWidth: 'fit-content',
                        fontSize: '18px',
                        fontWeight: '700',
                        whiteSpace: 'nowrap'
                      }}>
                        <label style={{fontWeight: '700', fontSize: '18px', whiteSpace: 'nowrap', display:'flex', alignItems:'center', gap:'0.3cm'}}>
                          📊 Report:
                        </label>
                        <select 
                          value={reportPeriod}
                          onChange={(e) => setReportPeriod(e.target.value)}
                          style={{
                            padding: '0.15cm 0.25cm',
                            borderRadius: '4px',
                            border: 'none',
                            fontSize: '16px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            background: 'white',
                            color: '#0f1a35'
                          }}
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="yearly">Yearly</option>
                          <option value="custom">Custom Range</option>
                        </select>
                        {reportPeriod === 'custom' && (
                          <>
                            <input
                              type="date"
                              value={reportFromDate}
                              onChange={(e) => setReportFromDate(e.target.value)}
                              style={{
                                padding: '0.15cm 0.2cm',
                                borderRadius: '4px',
                                border: 'none',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                background: 'white',
                                color: '#0f1a35'
                              }}
                              title="From Date"
                            />
                            <span style={{color: 'white', fontWeight: '600'}}>to</span>
                            <input
                              type="date"
                              value={reportToDate}
                              onChange={(e) => setReportToDate(e.target.value)}
                              style={{
                                padding: '0.15cm 0.2cm',
                                borderRadius: '4px',
                                border: 'none',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                background: 'white',
                                color: '#0f1a35'
                              }}
                              title="To Date"
                            />
                          </>
                        )}
                        <button
                          onClick={() => generateSalesReport(reportPeriod, reportFromDate, reportToDate)}
                          style={{
                            padding: '0.15cm 0.3cm',
                            background: 'white',
                            color: '#0f1a35',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '16px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Generate
                        </button>
                      </div>
                    </div>

                    <h4>Fulfilled Orders</h4>
                    
                    {/* Success message for paid amount updates */}
                    {paidAmountSuccessMessage && (
                      <div style={{
                        background: '#d4edda',
                        color: '#155724',
                        padding: '0.75rem',
                        marginBottom: '1rem',
                        borderRadius: '4px',
                        border: '1px solid #c3e6cb',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}>
                        {paidAmountSuccessMessage}
                      </div>
                    )}
                    
                    {/* Isolated Search - prevents App re-renders */}
                    <div style={{marginBottom: '1rem', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap'}}>
                      <PLUFilter
                        label="Filter Orders"
                        placeholder="001, 002, 003..."
                        onFilterChange={setFulfilledSearchInput}
                      />
                          <div>
                            <label style={{marginRight: '0.5rem', fontWeight: 'bold', color: '#000'}}>Filter by Payment Status:</label>
                            <select 
                              value={fulfilledPaymentFilter || ''}
                              onChange={(e) => setFulfilledPaymentFilter(e.target.value)}
                              style={{padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd', color: '#000', backgroundColor: '#fff'}}
                            >
                              <option value="">All Orders (Credit First)</option>
                              <option value="credit">Credit Orders Only</option>
                              <option value="paid">Paid Orders Only</option>
                            </select>
                          </div>
                          <div>
                            <label style={{marginRight: '0.5rem', fontWeight: 'bold', color: '#000'}}>Sort by Order ID:</label>
                            <button 
                              onClick={() => setFulfilledOrderIdSort(prev => prev === 'asc' ? 'desc' : 'asc')}
                              style={{
                                padding: '0.5rem 1rem', 
                                borderRadius: '4px', 
                                border: '1px solid #ddd',
                                background: fulfilledOrderIdSort ? '#007bff' : '#fff',
                                color: fulfilledOrderIdSort ? '#fff' : '#000',
                                cursor: 'pointer'
                              }}
                            >
                              Order ID {fulfilledOrderIdSort === 'asc' ? '↑ A-Z' : fulfilledOrderIdSort === 'desc' ? '↓ Z-A' : 'Sort'}
                            </button>
                          </div>
                        </div>
                    
                    {loadingFulfilledOrders ? (
                      <div className="loading-message">Loading fulfilled orders...</div>
                    ) : fulfilledOrders && fulfilledOrders.length > 0 ? (
                      <>
                        <div className="table-responsive">
                        <table className="admin-table" style={{tableLayout: 'fixed', width: '100%'}}>
                          <thead>
                            <tr>
                              <th style={{width: '7%'}}>Order ID</th>
                              <th style={{width: '8%'}}>Customer #</th>
                              <th style={{width: '13%'}}>Company</th>
                              <th style={{width: '11%'}}>Customer</th>
                              <th style={{width: '5%'}}>Items</th>
                              <th style={{width: '7%'}}>Total</th>
                              <th style={{width: '8%'}}>Date</th>
                              <th style={{width: '7%'}}>Paid By</th>
                              <th style={{width: '11%'}}>Paid</th>
                              <th style={{width: '7%'}}>Balance</th>
                              <th style={{width: '16%'}}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fulfilledFiltered.map((order, index) => {
                              const paymentMethod = (order.payment_method||'').toLowerCase();
                              const isCredit = paymentMethod === 'credit';
                              const isCash = paymentMethod === 'cash';  
                              const isBank = paymentMethod === 'bank';
                              const gt = parseFloat(order.grand_total||0)||0;
                              
                              // FIXED: Calculate total paid amount from ALL payment channels
                              // (not just based on payment_method)
                              const cashPaid = parseFloat(order.cash_paid_amount||0)||0;
                              const bankPaid = parseFloat(order.bank_paid_amount||0)||0;
                              const creditPaid = parseFloat(order.credit_paid_amount||0)||0;
                              const paid = cashPaid + bankPaid + creditPaid;
                              
                              const fullyPaid = (Math.max(0, gt - paid) <= 0.0001 || (order.payment_status||'').toLowerCase()==='fully_paid');
                              
                              // Check if order was previously deleted (restored order)
                              const wasDeleted = order.was_deleted === true;
                              
                              return (
                              <tr 
                                key={order?.id || `f-order-${index}`}
                                style={{
                                  backgroundColor: wasDeleted 
                                    ? '#FEE2E2'  // Light red for restored/deleted orders
                                    : highlightedOrderId === order?.id 
                                      ? '#fffbdd' 
                                      : 'transparent',
                                  transition: 'background-color 0.3s ease',
                                  borderLeft: wasDeleted ? '4px solid #E53E3E' : 'none'
                                }}
                              >
                                <td style={{fontSize: '12px', color: wasDeleted ? '#E53E3E' : '#6c757d', fontFamily: 'monospace'}}>
                                  {wasDeleted && <span title="This order was restored from deletion" style={{color: '#E53E3E', fontWeight: 'bold'}}>🔄 </span>}
                                  {order?.id?.substring(0, 8) || 'N/A'}
                                </td>
                                <td>
                                  <strong style={{color: '#0056b3', fontSize: '13px', fontWeight: '600', letterSpacing: '0.3px'}}>
                                    {order?.customer_number || 'N/A'}
                                  </strong>
                                </td>
                                <td>{(() => {
                                  // Use Customer Management data but fallback to backend data
                                  if (order?.customer_id === null || !order?.customer_id || order.customer_id === 'guest') {
                                    // FIXED: Use order.customer_name for guest customers
                                    return order.customer_name || 'Guest Customer';
                                  }
                                  
                                  // Find customer in Customer Management database
                                  const customer = customers.find(c => c.id === order.customer_id);
                                  if (customer) {
                                    return customer.company_name || customer.name || 'No Company';
                                  }
                                  
                                  // Fallback to backend enriched data if customer not found
                                  return order.customer_name || 'Customer name';
                                })()}</td>
                                <td>{(() => {
                                  // Use Customer Management data but fallback to backend data
                                  if (order?.customer_id === null || !order?.customer_id || order.customer_id === 'guest') {
                                    // FIXED: Use order.customer_name for guest customers
                                    return order.customer_name || 'Guest Customer';
                                  }
                                  
                                  // Find customer in Customer Management database FIRST
                                  const customer = customers.find(c => c.id === order.customer_id);
                                  if (customer) {
                                    return customer.name || 'No Name';
                                  }
                                  
                                  // Fallback to backend enriched data if customer not found
                                  return order.customer_name || 'Customer name';
                                })()}</td>
                                <td style={{
                                  textAlign: 'center', 
                                  color: '#6b7280', 
                                  fontSize: '14px',
                                  fontWeight: '400'
                                }}>{order?.items?.length || 0}</td>
                                <td className="total-amount" style={{
                                  fontWeight: '700', 
                                  fontSize: '16px', 
                                  color: '#111827',
                                  fontFamily: 'Inter, sans-serif',
                                  letterSpacing: '-0.02em'
                                }}>£{order?.grand_total ? parseFloat(order.grand_total).toFixed(2) : '0.00'}</td>
                                <td style={{
                                  fontSize: '14px', 
                                  color: '#6b7280'
                                }}>{order?.date ? (
                                  <>
                                    <div>{new Date(order.date).toLocaleDateString('en-GB')}</div>
                                    <div style={{fontSize: '12px', color: '#9ca3af'}}>{new Date(order.date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
                                    {order.edit_date && (
                                      <div 
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (order.original_order_snapshot) {
                                            setOriginalOrderToView(order.original_order_snapshot);
                                            setShowOriginalOrderModal(true);
                                          } else {
                                            alert('Original order data not available for this edit.');
                                          }
                                        }}
                                        style={{
                                          fontSize: '11px', 
                                          color: '#f59e0b', 
                                          fontStyle: 'italic', 
                                          marginTop: '2px',
                                          cursor: 'pointer',
                                          textDecoration: 'underline'
                                        }}
                                        title="Click to view original order before edit"
                                      >
                                        📝 Edited {new Date(order.edit_date).toLocaleDateString('en-GB')}
                                      </div>
                                    )}
                                  </>
                                ) : 'N/A'}</td>
                                <td>
                                  {fullyPaid ? (
                                    <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                                      {(() => {
                                        // FIXED: Show actual payment channels used, not payment_method
                                        const cashPaid = parseFloat(order.cash_paid_amount || 0);
                                        const bankPaid = parseFloat(order.bank_paid_amount || 0);
                                        const creditPaid = parseFloat(order.credit_paid_amount || 0);
                                        
                                        const paidBy = [];
                                        if (cashPaid > 0) paidBy.push(`Cash £${cashPaid.toFixed(2)}`);
                                        if (bankPaid > 0) paidBy.push(`Bank £${bankPaid.toFixed(2)}`);
                                        if (creditPaid > 0) paidBy.push(`Credit £${creditPaid.toFixed(2)}`);
                                        
                                        if (paidBy.length === 0) {
                                          // Fallback if no payment amounts recorded
                                          return (
                                            <span style={{
                                              color: '#10b981', 
                                              fontWeight: '600',
                                              fontSize: '12px',
                                              padding: '4px 10px',
                                              backgroundColor: '#d1fae5',
                                              borderRadius: '6px',
                                              display: 'inline-block',
                                              textTransform: 'uppercase',
                                              letterSpacing: '0.3px'
                                            }}>
                                              ✓ PAID
                                            </span>
                                          );
                                        }
                                        
                                        return paidBy.map((payment, idx) => (
                                          <span key={idx} style={{
                                            color: '#10b981', 
                                            fontWeight: '600',
                                            fontSize: '12px',
                                            padding: '4px 10px',
                                            backgroundColor: '#d1fae5',
                                            borderRadius: '6px',
                                            display: 'inline-block'
                                          }}>
                                            {payment}
                                          </span>
                                        ));
                                      })()}
                                    </div>
                                  ) : isCredit ? (
                                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px'}}>
                                      <span style={{
                                        color: '#6b7280',
                                        fontWeight: '500',
                                        fontSize: '13px'
                                      }}>
                                        💳 CREDIT
                                      </span>
                                      {/* Mark as Paid button - VISUAL ONLY: removes from Credit Accounts, resets credit-due days.
                                          The money was already recorded via Lump Sum Payment, so this MUST NOT touch Cash/Bank/Credit totals. */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!window.confirm('Mark this bill as PAID?\n\nThis is VISUAL ONLY — it removes the bill from Credit Accounts and resets the credit-due counter. It does NOT add money to Cash/Bank/Credit totals (use Lump Sum Payment to record the actual money received).')) return;
                                          
                                          // ⚡ INSTANT optimistic UI update — flip the badge locally NOW
                                          const optimisticUpdate = (list, setter) => {
                                            if (!list || !setter) return;
                                            setter(list.map(o => o.id === order.id ? {...o, payment_status: 'fully_paid'} : o));
                                          };
                                          optimisticUpdate(fulfilledOrders, setFulfilledOrders);
                                          optimisticUpdate(orders, setOrders);
                                          optimisticUpdate(creditAccountsOrders, setCreditAccountsOrders);
                                          
                                          // Fire API call in the background (don't block the user)
                                          const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token') || localStorage.getItem('token');
                                          fetch(`${API}/api/orders/${order.id}/mark-bill-paid`, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
                                          })
                                            .then(r => r.json())
                                            .then(data => {
                                              if (!data.success) {
                                                console.warn('Mark paid backend response:', data);
                                              }
                                            })
                                            .catch(err => {
                                              console.warn('Mark paid network issue (UI already updated):', err?.message);
                                            });
                                        }}
                                        style={{
                                          padding: '2px 8px',
                                          fontSize: '10px',
                                          background: '#10b981',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: 'pointer',
                                          fontWeight: '600'
                                        }}
                                      >
                                        ✓ MARK PAID
                                      </button>
                                    </div>
                                  ) : (
                                    <select 
                                      className="form-control" 
                                      style={{
                                        fontSize: '13px', 
                                        padding: '6px 10px', 
                                        height: 'auto',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        backgroundColor: '#ffffff',
                                        color: '#374151'
                                      }}
                                      value={(['cash','bank','credit'].includes((order.payment_method||'').toLowerCase()) ? (order.payment_method||'').toLowerCase() : '')} 
                                      onChange={async (e)=>{
                                      const pm = e.target.value;
                                      try {
                                        // If Credit selected, ask for credit days
                                        let creditDays = 30; // Default
                                        if (pm === 'credit') {
                                          const daysInput = prompt('Payment due in how many days?', '30');
                                          if (daysInput === null) return; // Cancelled
                                          creditDays = parseInt(daysInput) || 30;
                                          
                                          // Set credit terms
                                          await axios.put(`${API}/api/orders/${order.id}/credit-terms`, { credit_days: creditDays }, { headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` } });
                                        }
                                        
                                        await axios.put(`${API}/api/orders/${order.id}`, { payment_method: pm }, { headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` } });
                                        
                                        // Refresh order data
                                        const updatedOrder = await axios.get(`${API}/api/orders/${order.id}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` } });
                                        setFulfilledOrders(prev => prev.map(o => o.id===order.id ? updatedOrder.data : o));
                                        
                                        if (pm === 'credit') {
                                          alert(`✅ Credit terms set: Payment due in ${creditDays} days`);
                                        }
                                      } catch (err) {
                                        console.error('Failed to set payment method', err);
                                        alert('Failed to update payment method');
                                      }
                                    }}>
                                      <option value="" disabled>Select</option>
                                      <option value="cash">Payment Type</option>
                                      <option value="credit">Credit</option>
                                    </select>
                                  )}
                                </td>
                                {/* Paid Amount Column - Show payment breakdown for completed orders or input for unpaid credit */}
                                <td>
                                  {fullyPaid ? (
                                    <div style={{display:'flex', flexDirection:'column', gap:'6px', padding: '2px 0'}}>
                                      {(() => {
                                        // Build payment breakdown display
                                        const breakdown = [];
                                        const cashPaid = parseFloat(order.cash_paid_amount || 0);
                                        const bankPaid = parseFloat(order.bank_paid_amount || 0);
                                        const creditPaid = parseFloat(order.credit_paid_amount || 0);
                                        
                                        if (cashPaid > 0) {
                                          breakdown.push(
                                            <div key="cash" style={{
                                              fontSize:'13px', 
                                              color:'#374151', 
                                              fontWeight:'500'
                                            }}>
                                              💵 Cash: £{cashPaid.toFixed(2)}
                                            </div>
                                          );
                                        }
                                        if (bankPaid > 0) {
                                          breakdown.push(
                                            <div key="bank" style={{
                                              fontSize:'13px', 
                                              color:'#374151', 
                                              fontWeight:'500'
                                            }}>
                                              🏦 Bank: £{bankPaid.toFixed(2)}
                                            </div>
                                          );
                                        }
                                        if (creditPaid > 0) {
                                          breakdown.push(
                                            <div key="credit" style={{
                                              fontSize:'13px', 
                                              color:'#374151', 
                                              fontWeight:'500'
                                            }}>
                                              💳 Credit: £{creditPaid.toFixed(2)}
                                            </div>
                                          );
                                        }
                                        
                                        // If no breakdown available, show FULLY PAID as fallback
                                        if (breakdown.length === 0) {
                                          return <span style={{color:'#10b981', fontWeight:'600', fontSize: '13px'}}>✓ PAID</span>;
                                        }
                                        
                                        return (
                                          <>
                                            {breakdown}
                                            <div style={{
                                              fontSize:'13px', 
                                              color:'#111827', 
                                              marginTop:'4px', 
                                              paddingTop:'4px', 
                                              fontWeight:'600',
                                              borderTop:'1px solid #e5e7eb'
                                            }}>
                                              Total: £{(cashPaid + bankPaid + creditPaid).toFixed(2)}
                                            </div>
                                          </>
                                        );
                                      })()}
                                    </div>
                                  ) : (paymentMethod === 'cash' || paymentMethod === 'bank' || paymentMethod === 'credit') ? (
                                    // UNIVERSAL SPLIT PAYMENT UI - Uses isolated component to prevent input clearing
                                    // SPECTATOR CHECK: Block payment recording for spectators
                                    isSpectateOnly('fulfilled-orders') ? (
                                      <div style={{color: '#9ca3af', fontSize: '12px', padding: '8px', background: '#f3f4f6', borderRadius: '6px', textAlign: 'center'}}>
                                        🔒 View Only - Cannot record payments
                                      </div>
                                    ) : (
                                      <PaymentInputRow
                                        orderId={order.id}
                                        paymentMethod={paymentMethod}
                                        grandTotal={gt}
                                        currentPaid={paid}
                                        isCredit={isCredit}
                                        onRecordPayment={async ({ cashAmount, bankAmount, creditAmount }) => {
                                          // Build update data for split payment - ONLY include non-zero amounts
                                          const updateData = {};
                                          
                                          if (cashAmount > 0) {
                                            updateData.cash_paid_amount = cashAmount;
                                          }
                                          if (bankAmount > 0) {
                                            updateData.bank_paid_amount = bankAmount;
                                          }
                                          
                                          // Add credit payment if applicable (no channel needed)
                                          if (paymentMethod === 'credit' && creditAmount > 0) {
                                            updateData.credit_paid_amount = creditAmount;
                                          }
                                          
                                          console.log('💰 Recording split payment:', updateData);
                                          
                                          const response = await axios.put(`${API}/api/orders/${order.id}`, updateData, { 
                                            headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` } 
                                          });
                                          
                                          const updatedOrderData = response.data;
                                          setFulfilledOrders(prev => prev.map(o => o.id === order.id ? { ...updatedOrderData } : o));
                                          
                                          // Show success message
                                          const totalPaid = cashAmount + bankAmount + creditAmount;
                                          const breakdown = [];
                                          if (cashAmount > 0) breakdown.push(`Cash: £${cashAmount.toFixed(2)}`);
                                          if (bankAmount > 0) breakdown.push(`Bank: £${bankAmount.toFixed(2)}`);
                                          if (creditAmount > 0) breakdown.push(`Credit: £${creditAmount.toFixed(2)}`);
                                          
                                          setPaidAmountSuccessMessage(`✅ Split payment recorded: ${breakdown.join(' + ')} = £${totalPaid.toFixed(2)}`);
                                          setTimeout(() => setPaidAmountSuccessMessage(""), 5000);
                                          refreshAllData();
                                        }}
                                      />
                                    )
                                  ) : (
                                    <span style={{color: '#999'}}>Select Payment Method</span>
                                  )}
                                </td>
                                {/* Balance Column - Show remaining balance or PAID */}
                                <td>
                                  {(paymentMethod === 'cash' || paymentMethod === 'bank' || paymentMethod === 'credit') ? (() => {
                                    const bal = Math.max(0, gt - paid);
                                    if (bal <= 0) {
                                      return (
                                        <span style={{
                                          color: '#10b981',
                                          fontWeight: '600',
                                          fontSize: '12px',
                                          padding: '4px 10px',
                                          backgroundColor: '#d1fae5',
                                          borderRadius: '6px',
                                          display: 'inline-block',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.3px'
                                        }}>
                                          ✓ PAID
                                        </span>
                                      );
                                    }
                                    return (
                                      <span className="balance-owed" style={{
                                        color: '#dc2626',
                                        fontWeight: '700',
                                        fontSize: '16px',
                                        fontFamily: 'Inter, sans-serif',
                                        letterSpacing: '-0.02em'
                                      }}>
                                        £{bal.toFixed(2)}
                                      </span>
                                    );
                                  })() : (
                                    <span style={{color: '#9ca3af', fontSize: '14px'}}>-</span>
                                  )}
                                  
                                  {/* Add Balance - Allow admin to reverse incorrect payments */}
                                  {/* SPECTATOR CHECK: Hide Add Balance for spectators */}
                                  {!isSpectateOnly('fulfilled-orders') && (paymentMethod === 'cash' || paymentMethod === 'bank' || paymentMethod === 'credit') && !fullyPaid && (
                                    <div style={{marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #e9ecef', width: '100%', display: 'flex', justifyContent: 'flex-start'}}>
                                      <div style={{display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'flex-start'}}>
                                        <label style={{fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '2px'}}>Add to Balance:</label>
                                        <input 
                                          id={`add-balance-${order.id}`}
                                          type="number" 
                                          className="form-control" 
                                          style={{width: '70px', fontSize: '13px', padding: '4px 6px', textAlign: 'center'}}
                                          placeholder="0.00"
                                          step="0.01"
                                          min="0"
                                        />
                                        <button 
                                          className="btn btn-sm btn-warning"
                                          onClick={async () => {
                                            const input = document.getElementById(`add-balance-${order.id}`);
                                            const amountToAdd = parseFloat(input?.value || 0) || 0;
                                            
                                            if (amountToAdd <= 0) {
                                              alert('Please enter a positive amount to add back to balance');
                                              return;
                                            }
                                            
                                            // Get current payments
                                            const currentCash = parseFloat(order.cash_paid_amount || 0);
                                            const currentBank = parseFloat(order.bank_paid_amount || 0);
                                            const currentCredit = parseFloat(order.credit_paid_amount || 0);
                                            const totalPaid = currentCash + currentBank + currentCredit;
                                            
                                            if (amountToAdd > totalPaid) {
                                              alert(`Cannot add £${amountToAdd.toFixed(2)} to balance.\nTotal paid is only £${totalPaid.toFixed(2)}`);
                                              return;
                                            }
                                            
                                            // Confirm action
                                            if (!confirm(`Add £${amountToAdd.toFixed(2)} back to the balance?\n\nThis will deduct from the most recent payment.`)) {
                                              return;
                                            }
                                            
                                            try {
                                              // Deduct from the payment channel with highest amount
                                              let updateData = {};
                                              
                                              if (currentCredit >= amountToAdd) {
                                                updateData.credit_paid_amount = -(amountToAdd);
                                              } else if (currentBank >= amountToAdd) {
                                                updateData.bank_paid_amount = -(amountToAdd);
                                              } else if (currentCash >= amountToAdd) {
                                                updateData.cash_paid_amount = -(amountToAdd);
                                              } else {
                                                // Split deduction across channels
                                                let remaining = amountToAdd;
                                                if (currentCredit > 0) {
                                                  const deduct = Math.min(currentCredit, remaining);
                                                  updateData.credit_paid_amount = -(deduct);
                                                  remaining -= deduct;
                                                }
                                                if (remaining > 0 && currentBank > 0) {
                                                  const deduct = Math.min(currentBank, remaining);
                                                  updateData.bank_paid_amount = -(deduct);
                                                  remaining -= deduct;
                                                }
                                                if (remaining > 0 && currentCash > 0) {
                                                  updateData.cash_paid_amount = -(remaining);
                                                }
                                              }
                                              
                                              console.log('💰 Adding back to balance (negative payment):', updateData);
                                              
                                              const response = await axios.put(`${API}/api/orders/${order.id}`, updateData, { 
                                                headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` } 
                                              });
                                              
                                              const updatedOrderData = response.data;
                                              setFulfilledOrders(prev => prev.map(o => o.id === order.id ? { ...updatedOrderData } : o));
                                              
                                              input.value = '';
                                              alert(`✅ Added £${amountToAdd.toFixed(2)} back to balance`);
                                              
                                            } catch (err) {
                                              console.error('❌ Failed to add balance', err);
                                              alert('Failed to update balance: ' + (err.response?.data?.detail || err.message));
                                            }
                                          }}
                                          style={{
                                            fontSize:'11px', 
                                            padding:'5px 8px', 
                                            whiteSpace:'normal !important', 
                                            wordWrap: 'break-word', 
                                            width: '70px !important', 
                                            maxWidth: '70px !important', 
                                            minWidth: '70px !important',
                                            height: '40px',
                                            textAlign: 'center', 
                                            lineHeight: '1.2', 
                                            display: 'flex !important', 
                                            flexDirection: 'column !important',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '1px'
                                          }}
                                        >
                                          <span style={{display: 'block', width: '100%', fontWeight: '600'}}>+ Add</span>
                                          <span style={{display: 'block', width: '100%', fontWeight: '600'}}>Balance</span>
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <div style={{display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'stretch'}}>
                                    {/* View Order - Secondary/Gray */}
                                    <button 
                                      className="button button-secondary" 
                                      style={{
                                        fontSize: '13px', 
                                        padding: '8px 14px', 
                                        fontWeight: '500', 
                                        whiteSpace: 'nowrap',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        backgroundColor: '#f9fafb',
                                        color: '#374151',
                                        transition: 'all 0.2s ease',
                                        cursor: 'pointer'
                                      }} 
                                      onClick={() => handleViewOrder(order.id)}
                                      onMouseEnter={(e) => {
                                        e.target.style.opacity = '0.9';
                                        e.target.style.transform = 'translateY(-1px)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.opacity = '1';
                                        e.target.style.transform = 'translateY(0)';
                                      }}
                                    >
                                      📋 View Order
                                    </button>
                                    
                                    {/* Edit - Primary/Blue */}
                                    <button 
                                      className="button button-primary"
                                      onClick={() => handleEditOrder(order.id)}
                                      disabled={isSpectateOnly('fulfilled-orders')}
                                      style={{
                                        fontSize: '13px', 
                                        padding: '8px 14px', 
                                        fontWeight: '500',
                                        borderRadius: '6px',
                                        backgroundColor: isSpectateOnly('fulfilled-orders') ? '#9ca3af' : '#2563eb',
                                        color: '#ffffff',
                                        border: 'none',
                                        transition: 'all 0.2s ease',
                                        cursor: isSpectateOnly('fulfilled-orders') ? 'not-allowed' : 'pointer',
                                        opacity: isSpectateOnly('fulfilled-orders') ? 0.6 : 1
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isSpectateOnly('fulfilled-orders')) {
                                          e.target.style.opacity = '0.9';
                                          e.target.style.transform = 'translateY(-1px)';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!isSpectateOnly('fulfilled-orders')) {
                                          e.target.style.opacity = '1';
                                          e.target.style.transform = 'translateY(0)';
                                        }
                                      }}
                                      title={isSpectateOnly('fulfilled-orders') ? 'Spectate mode - cannot edit' : ''}
                                    >
                                      {isSpectateOnly('fulfilled-orders') ? '🔒 Edit' : '✏️ Edit'}
                                    </button>
                                    
                                    {/* Invoice Status/Generate */}
                                    {order.is_invoiced ? (
                                      <button 
                                        className="button button-success" 
                                        style={{
                                          fontSize: '13px', 
                                          padding: '8px 14px', 
                                          backgroundColor: '#10b981', 
                                          color: 'white', 
                                          fontWeight: '500', 
                                          whiteSpace: 'nowrap',
                                          border: 'none',
                                          borderRadius: '6px',
                                          transition: 'all 0.2s ease',
                                          cursor: 'pointer'
                                        }} 
                                        onClick={() => setActiveTab('invoices')}
                                        onMouseEnter={(e) => {
                                          e.target.style.opacity = '0.9';
                                          e.target.style.transform = 'translateY(-1px)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.opacity = '1';
                                          e.target.style.transform = 'translateY(0)';
                                        }}
                                      >
                                        ✓ Invoiced
                                      </button>
                                    ) : (
                                      <button 
                                        className="button button-primary" 
                                        style={{
                                          fontSize: '13px', 
                                          padding: '8px 14px', 
                                          fontWeight: '500', 
                                          whiteSpace: 'nowrap',
                                          borderRadius: '6px',
                                          backgroundColor: isSpectateOnly('fulfilled-orders') ? '#9ca3af' : '#2563eb',
                                          color: '#ffffff',
                                          border: 'none',
                                          transition: 'all 0.2s ease',
                                          cursor: isSpectateOnly('fulfilled-orders') ? 'not-allowed' : 'pointer',
                                          opacity: isSpectateOnly('fulfilled-orders') ? 0.6 : 1
                                        }} 
                                        onClick={() => !isSpectateOnly('fulfilled-orders') && handleGenerateInvoice(order)}
                                        disabled={isSpectateOnly('fulfilled-orders')}
                                        onMouseEnter={(e) => {
                                          if (!isSpectateOnly('fulfilled-orders')) {
                                            e.target.style.opacity = '0.9';
                                            e.target.style.transform = 'translateY(-1px)';
                                          }
                                        }}
                                        onMouseLeave={(e) => {
                                          if (!isSpectateOnly('fulfilled-orders')) {
                                            e.target.style.opacity = '1';
                                            e.target.style.transform = 'translateY(0)';
                                          }
                                        }}
                                        title={isSpectateOnly('fulfilled-orders') ? 'Spectate mode - cannot generate invoice' : ''}
                                      >
                                        {isSpectateOnly('fulfilled-orders') ? '🔒 Invoice' : '📄 Invoice'}
                                      </button>
                                    )}
                                    
                                    {/* Return - Secondary/Gray */}
                                    <button 
                                      className="button button-secondary" 
                                      onClick={() => handleReturnOrder(order.id)} 
                                      disabled={isSpectateOnly('fulfilled-orders')}
                                      style={{
                                        fontSize: '13px', 
                                        padding: '8px 14px', 
                                        fontWeight: '500',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        backgroundColor: isSpectateOnly('fulfilled-orders') ? '#e5e7eb' : '#f9fafb',
                                        color: isSpectateOnly('fulfilled-orders') ? '#9ca3af' : '#374151',
                                        transition: 'all 0.2s ease',
                                        cursor: isSpectateOnly('fulfilled-orders') ? 'not-allowed' : 'pointer',
                                        opacity: isSpectateOnly('fulfilled-orders') ? 0.6 : 1
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isSpectateOnly('fulfilled-orders')) {
                                          e.target.style.opacity = '0.9';
                                          e.target.style.transform = 'translateY(-1px)';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!isSpectateOnly('fulfilled-orders')) {
                                          e.target.style.opacity = '1';
                                          e.target.style.transform = 'translateY(0)';
                                        }
                                      }}
                                      title={isSpectateOnly('fulfilled-orders') ? 'Spectate mode - cannot process returns' : ''}
                                    >
                                      {isSpectateOnly('fulfilled-orders') ? '🔒 Return' : '↩️ Return'}
                                    </button>
                                    
                                    {/* Delete - Danger/Red */}
                                    <button 
                                      className="button button-danger" 
                                      onClick={() => handleDeleteOrder(order.id)} 
                                      disabled={isSpectateOnly('fulfilled-orders')}
                                      style={{
                                        fontSize: '13px', 
                                        padding: '8px 14px', 
                                        backgroundColor: isSpectateOnly('fulfilled-orders') ? '#fca5a5' : '#ef4444', 
                                        border: 'none', 
                                        fontWeight: '500',
                                        borderRadius: '6px',
                                        color: 'white',
                                        transition: 'all 0.2s ease',
                                        cursor: isSpectateOnly('fulfilled-orders') ? 'not-allowed' : 'pointer',
                                        opacity: isSpectateOnly('fulfilled-orders') ? 0.6 : 1
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isSpectateOnly('fulfilled-orders')) {
                                          e.target.style.opacity = '0.9';
                                          e.target.style.transform = 'translateY(-1px)';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!isSpectateOnly('fulfilled-orders')) {
                                          e.target.style.opacity = '1';
                                          e.target.style.transform = 'translateY(0)';
                                        }
                                      }}
                                      title={isSpectateOnly('fulfilled-orders') ? 'Spectate mode - cannot delete' : ''}
                                    >
                                      {isSpectateOnly('fulfilled-orders') ? '🔒 Delete' : '🗑️ Delete'}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )
                            })}
                          </tbody>
                        </table>
                      </div>
                      </>
                    ) : (
                      <div className="no-orders-message">
                        <p>No fulfilled orders found.</p>
                      </div>
                    )}
                  </div>
                </div>
              {/* END TEMP PLACEHOLDER */}

              {/* DELETED ORDERS SECTION - Boss Verification */}
              <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: activeTab === 'deleted-orders' ? 'block' : 'none'}}>
                <div className="admin-section-header">
                  <div className="section-nav">
                    <button className="btn secondary" onClick={() => setActiveTab('dashboard')} style={{marginRight: '1rem'}}>← Back to Dashboard</button>
                    <h3 style={{color: '#E53E3E', margin: 0}}>🗑️ Deleted Orders - Boss Verification</h3>
                  </div>
                </div>

                <div className="admin-table-container">
                  <div style={{marginBottom: '1rem', padding: '1rem', backgroundColor: '#FFF5F5', border: '1px solid #E53E3E', borderRadius: '8px'}}>
                    <p style={{margin: 0, color: '#C53030'}}>
                      <strong>⚠️ Important:</strong> All deleted orders are shown here for review. You can verify legitimate deletions or restore orders that were deleted by mistake.
                    </p>
                  </div>

                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                    <h4 style={{margin: 0}}>Deleted Orders ({deletedOrders.length})</h4>
                    <button 
                      className="btn primary" 
                      onClick={() => loadDeletedOrders()}
                      style={{backgroundColor: '#E53E3E', color: 'white'}}
                    >
                      🔄 Refresh List
                    </button>
                  </div>

                  {loadingDeletedOrders ? (
                    <div className="loading-message">Loading deleted orders...</div>
                  ) : deletedOrders.length > 0 ? (
                    <table className="admin-table" style={{border: '2px solid #E53E3E'}}>
                      <thead style={{backgroundColor: '#E53E3E', color: 'white'}}>
                        <tr>
                          <th>Order ID</th>
                          <th>PLU</th>
                          <th>Customer</th>
                          <th>Total</th>
                          <th>Paid</th>
                          <th>Order Date</th>
                          <th>Deleted At</th>
                          <th>Deleted By</th>
                          <th>Verified</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deletedOrders.map((order) => {
                          const totalPaid = (parseFloat(order.cash_paid_amount || 0) + parseFloat(order.bank_paid_amount || 0) + parseFloat(order.credit_paid_amount || 0)).toFixed(2);
                          return (
                            <tr key={order.id || order.original_id} style={{backgroundColor: '#FFF5F5'}}>
                              <td style={{fontFamily: 'monospace', fontSize: '0.85rem'}}>
                                {(order.id || order.original_id || '').substring(0, 8)}...
                              </td>
                              <td>
                                <span style={{backgroundColor: '#3182CE', color: 'white', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold'}}>
                                  {order.plu_code || 'N/A'}
                                </span>
                              </td>
                              <td>{order.customer_name || 'Guest'}</td>
                              <td style={{fontWeight: 'bold'}}>£{parseFloat(order.grand_total || order.total_price || 0).toFixed(2)}</td>
                              <td style={{color: parseFloat(totalPaid) > 0 ? '#38A169' : '#718096'}}>
                                £{totalPaid}
                              </td>
                              <td style={{fontSize: '0.85rem', backgroundColor: '#EDF2F7'}}>
                                {order.date ? new Date(order.date).toLocaleDateString('en-GB') : 'N/A'}
                              </td>
                              <td style={{fontSize: '0.85rem', color: '#C53030'}}>
                                {order.deleted_at ? new Date(order.deleted_at).toLocaleString('en-GB') : 'N/A'}
                              </td>
                              <td style={{fontWeight: 'bold', color: '#C53030'}}>{order.deleted_by || 'Unknown'}</td>
                              <td>
                                {order.verified_by_boss ? (
                                  <span style={{backgroundColor: '#38A169', color: 'white', padding: '4px 8px', borderRadius: '4px'}}>
                                    ✓ Verified
                                  </span>
                                ) : (
                                  <span style={{backgroundColor: '#DD6B20', color: 'white', padding: '4px 8px', borderRadius: '4px'}}>
                                    ⏳ Pending
                                  </span>
                                )}
                              </td>
                              <td>
                                <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
                                  <button 
                                    className="btn secondary"
                                    onClick={() => {
                                      setSelectedOrder(order);
                                      setShowOrderViewModal(true);
                                    }}
                                    style={{backgroundColor: '#4A5568', color: 'white', padding: '6px 12px', fontSize: '0.85rem'}}
                                  >
                                    👁️ View
                                  </button>
                                  {!order.verified_by_boss && (
                                    <button 
                                      className="btn primary"
                                      onClick={() => verifyDeletedOrder(order.id || order.original_id)}
                                      style={{backgroundColor: '#38A169', color: 'white', padding: '6px 12px', fontSize: '0.85rem'}}
                                    >
                                      ✓ Verify
                                    </button>
                                  )}
                                  <button 
                                    className="btn secondary"
                                    onClick={() => restoreDeletedOrder(order.id || order.original_id)}
                                    style={{backgroundColor: '#3182CE', color: 'white', padding: '6px 12px', fontSize: '0.85rem'}}
                                  >
                                    ↩️ Restore
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className="no-orders-message" style={{padding: '2rem', textAlign: 'center', backgroundColor: '#F0FFF4', borderRadius: '8px'}}>
                      <p style={{color: '#38A169', margin: 0}}>✅ No deleted orders pending review. All orders are accounted for.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* EDITED ORDERS SECTION - Boss Review */}
              <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: activeTab === 'edited-orders' ? 'block' : 'none'}}>
                <div className="admin-section-header">
                  <div className="section-nav">
                    <button className="btn secondary" onClick={() => setActiveTab('dashboard')} style={{marginRight: '1rem'}}>← Back to Dashboard</button>
                    <h3 style={{color: '#ED8936', margin: 0}}>✏️ Edited Orders - Boss Review</h3>
                  </div>
                </div>

                <div className="admin-table-container">
                  <div style={{marginBottom: '1rem', padding: '1rem', backgroundColor: '#FFFAF0', border: '1px solid #ED8936', borderRadius: '8px'}}>
                    <p style={{margin: 0, color: '#C05621'}}>
                      <strong>📋 Review:</strong> These orders have been modified after creation. Review changes to ensure accuracy.
                    </p>
                  </div>

                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                    <h4 style={{margin: 0}}>Edited Orders ({editedOrders.length})</h4>
                    <button 
                      className="btn primary" 
                      onClick={() => loadEditedOrders()}
                      style={{backgroundColor: '#ED8936', color: 'white'}}
                    >
                      🔄 Refresh List
                    </button>
                  </div>

                  {loadingEditedOrders ? (
                    <div className="loading-message">Loading edited orders...</div>
                  ) : editedOrders.length > 0 ? (
                    <table className="admin-table" style={{border: '2px solid #ED8936'}}>
                      <thead style={{backgroundColor: '#ED8936', color: 'white'}}>
                        <tr>
                          <th>Order ID</th>
                          <th>PLU</th>
                          <th>Customer</th>
                          <th>Total</th>
                          <th>Original Date</th>
                          <th>Edit Date</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editedOrders.map((order) => (
                          <tr key={order.id} style={{backgroundColor: '#FFFAF0'}}>
                            <td style={{fontFamily: 'monospace', fontSize: '0.85rem'}}>
                              {(order.id || '').substring(0, 8)}...
                            </td>
                            <td>
                              <span style={{backgroundColor: '#3182CE', color: 'white', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold'}}>
                                {order.plu_code || 'N/A'}
                              </span>
                            </td>
                            <td>{order.customer_name || 'Guest'}</td>
                            <td style={{fontWeight: 'bold'}}>£{parseFloat(order.grand_total || order.total_price || 0).toFixed(2)}</td>
                            <td style={{fontSize: '0.85rem'}}>
                              {order.original_date ? new Date(order.original_date).toLocaleDateString('en-GB') : (order.date ? new Date(order.date).toLocaleDateString('en-GB') : 'N/A')}
                            </td>
                            <td style={{fontSize: '0.85rem', color: '#C05621', fontWeight: 'bold'}}>
                              {order.edit_date ? new Date(order.edit_date).toLocaleDateString('en-GB') : 'N/A'}
                            </td>
                            <td>
                              {(() => {
                                const s = (order.status || 'unknown').toLowerCase();
                                const palette = {
                                  fulfilled: '#48BB78',
                                  returned: '#E53E3E',
                                  cancelled: '#9B2C2C',
                                  pending: '#4299E1',
                                  deleted: '#718096',
                                };
                                const bg = palette[s] || '#4A5568';
                                return (
                                  <span style={{backgroundColor: bg, color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem', textTransform: 'capitalize'}}>
                                    {s === 'returned' ? '↩ Returned' : s === 'cancelled' ? '✖ Cancelled' : (order.status || 'Unknown')}
                                  </span>
                                );
                              })()}
                            </td>
                            <td>
                              <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
                                <button 
                                  className="btn secondary"
                                  onClick={() => handleViewOrder(order.id)}
                                  style={{backgroundColor: '#4A5568', color: 'white', padding: '6px 12px', fontSize: '0.85rem'}}
                                >
                                  👁️ View
                                </button>
                                {order.original_order_snapshot && (
                                  <button 
                                    className="btn secondary"
                                    onClick={() => {
                                      setOriginalOrderToView(order.original_order_snapshot);
                                      setShowOriginalOrderModal(true);
                                    }}
                                    style={{backgroundColor: '#805AD5', color: 'white', padding: '6px 12px', fontSize: '0.85rem'}}
                                  >
                                    📜 Original
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="no-orders-message" style={{padding: '2rem', textAlign: 'center', backgroundColor: '#F0FFF4', borderRadius: '8px'}}>
                      <p style={{color: '#38A169', margin: 0}}>✅ No edited orders to review. All orders are in their original state.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* DAILY ACTIVITY SECTION - Boss Review */}
              <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: activeTab === 'daily-activity' ? 'block' : 'none'}}>
                <div className="admin-section-header">
                  <div className="section-nav">
                    <button className="btn secondary" onClick={() => setActiveTab('dashboard')} style={{marginRight: '1rem'}}>← Back to Dashboard</button>
                    <h3 style={{color: '#9F7AEA', margin: 0}}>📅 Daily Activity Report</h3>
                  </div>
                </div>

                <div className="admin-table-container">
                  <div style={{marginBottom: '1rem', padding: '1rem', backgroundColor: '#FAF5FF', border: '1px solid #9F7AEA', borderRadius: '8px'}}>
                    <p style={{margin: 0, color: '#6B46C1'}}>
                      <strong>📋 Daily Summary:</strong> View all orders and payments grouped by customer. Print or download for record keeping.
                    </p>
                  </div>

                  {/* Date Selector and Actions */}
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                      <label style={{fontWeight: 'bold', color: '#4a5568'}}>Select Date:</label>
                      <input 
                        type="date" 
                        value={dailyActivityDate}
                        onChange={(e) => {
                          setDailyActivityDate(e.target.value);
                          loadDailyActivity(e.target.value);
                        }}
                        style={{padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '14px'}}
                      />
                      <button 
                        className="btn secondary"
                        onClick={() => {
                          const today = new Date().toISOString().split('T')[0];
                          setDailyActivityDate(today);
                          loadDailyActivity(today);
                        }}
                        style={{padding: '8px 12px', fontSize: '13px'}}
                      >
                        Today
                      </button>
                    </div>
                    <div style={{display: 'flex', gap: '0.5rem'}}>
                      <button 
                        className="btn primary" 
                        onClick={() => loadDailyActivity(dailyActivityDate)}
                        style={{backgroundColor: '#9F7AEA', color: 'white'}}
                      >
                        🔄 Refresh
                      </button>
                      <button 
                        className="btn primary" 
                        onClick={printDailyActivityReport}
                        disabled={!dailyActivity}
                        style={{backgroundColor: '#38A169', color: 'white'}}
                      >
                        🖨️ Print / Download
                      </button>
                    </div>
                  </div>

                  {loadingDailyActivity ? (
                    <div className="loading-message">Loading daily activity...</div>
                  ) : dailyActivity ? (
                    <>
                      {/* Summary Cards */}
                      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '2rem'}}>
                        <div style={{background: '#EDF2F7', padding: '1rem', borderRadius: '8px', textAlign: 'center'}}>
                          <div style={{fontSize: '24px', fontWeight: 'bold', color: '#2D3748'}}>{dailyActivity.total_orders}</div>
                          <div style={{fontSize: '12px', color: '#718096'}}>Total Orders</div>
                        </div>
                        <div style={{background: '#E6FFFA', padding: '1rem', borderRadius: '8px', textAlign: 'center'}}>
                          <div style={{fontSize: '24px', fontWeight: 'bold', color: '#234E52'}}>£{dailyActivity.total_orders_value.toFixed(2)}</div>
                          <div style={{fontSize: '12px', color: '#718096'}}>Orders Value</div>
                        </div>
                        <div style={{background: '#FEF3C7', padding: '1rem', borderRadius: '8px', textAlign: 'center'}}>
                          <div style={{fontSize: '24px', fontWeight: 'bold', color: '#92400E'}}>{dailyActivity.total_payments}</div>
                          <div style={{fontSize: '12px', color: '#718096'}}>Total Payments</div>
                        </div>
                        <div style={{background: '#D1FAE5', padding: '1rem', borderRadius: '8px', textAlign: 'center'}}>
                          <div style={{fontSize: '24px', fontWeight: 'bold', color: '#065F46'}}>£{dailyActivity.total_payments_value.toFixed(2)}</div>
                          <div style={{fontSize: '12px', color: '#718096'}}>Payments Value</div>
                        </div>
                      </div>

                      {/* Bills Taken Section */}
                      <div style={{marginBottom: '2rem'}}>
                        <h4 style={{color: '#2D3748', borderBottom: '2px solid #9F7AEA', paddingBottom: '8px', marginBottom: '1rem'}}>
                          📋 BILLS TAKEN - {dailyActivity.date_formatted}
                        </h4>
                        {dailyActivity.customers_with_orders.length === 0 ? (
                          <p style={{color: '#718096', fontStyle: 'italic'}}>No orders for this date.</p>
                        ) : (
                          dailyActivity.customers_with_orders.map((customer, idx) => (
                            <div key={idx} style={{marginBottom: '1.5rem', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden'}}>
                              <div style={{background: '#4A5568', color: 'white', padding: '10px 15px', fontWeight: 'bold'}}>
                                {customer.customer_name} ({customer.order_count} bill{customer.order_count > 1 ? 's' : ''})
                              </div>
                              <table style={{width: '100%', borderCollapse: 'collapse', background: 'white'}}>
                                <thead>
                                  <tr style={{background: '#EDF2F7'}}>
                                    <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#000'}}>Bill #</th>
                                    <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#000'}}>PLU</th>
                                    <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#000'}}>Time</th>
                                    <th style={{padding: '8px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#000'}}>Edited</th>
                                    <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#000'}}>By</th>
                                    <th style={{padding: '8px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#000'}}>Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {customer.orders.map((order, orderIdx) => (
                                    <tr key={orderIdx} style={{borderBottom: '1px solid #f0f0f0', background: 'white'}}>
                                      <td style={{padding: '8px', fontFamily: 'monospace', fontSize: '12px', color: '#000'}}>{order.id ? order.id.substring(0, 10) + '...' : 'N/A'}</td>
                                      <td style={{padding: '8px'}}><span style={{background: '#3182CE', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '11px'}}>{order.plu_code || 'N/A'}</span></td>
                                      <td style={{padding: '8px', fontSize: '13px', color: '#000'}}>{order.time ? new Date(order.time).toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'}) : 'N/A'}</td>
                                      <td style={{padding: '8px', textAlign: 'center', color: '#000'}}>{order.edited ? <span style={{background: '#ED8936', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '10px'}}>EDITED</span> : '-'}</td>
                                      <td style={{padding: '8px', fontSize: '13px', color: '#000'}}>{order.created_by || 'admin'}</td>
                                      <td style={{padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#000'}}>£{order.total.toFixed(2)}</td>
                                    </tr>
                                  ))}
                                  <tr style={{background: '#EDF2F7', fontWeight: 'bold'}}>
                                    <td colSpan="5" style={{padding: '10px', textAlign: 'right', color: '#000'}}>TOTAL for {customer.customer_name}:</td>
                                    <td style={{padding: '10px', textAlign: 'right', color: '#2D3748'}}>£{customer.total_amount.toFixed(2)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Payments Received Section */}
                      <div>
                        <h4 style={{color: '#2D3748', borderBottom: '2px solid #38A169', paddingBottom: '8px', marginBottom: '1rem'}}>
                          💰 PAYMENTS RECEIVED - {dailyActivity.date_formatted}
                        </h4>
                        {dailyActivity.customers_with_payments.length === 0 ? (
                          <p style={{color: '#718096', fontStyle: 'italic'}}>No payments for this date.</p>
                        ) : (
                          dailyActivity.customers_with_payments.map((customer, idx) => (
                            <div key={idx} style={{marginBottom: '1.5rem', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden'}}>
                              <div style={{background: '#38A169', color: 'white', padding: '10px 15px', fontWeight: 'bold'}}>
                                {customer.customer_name} ({customer.payment_count} payment{customer.payment_count > 1 ? 's' : ''})
                              </div>
                              <table style={{width: '100%', borderCollapse: 'collapse', background: 'white'}}>
                                <thead>
                                  <tr style={{background: '#F0FFF4'}}>
                                    <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#000'}}>Bill #</th>
                                    <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#000'}}>PLU</th>
                                    <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#000'}}>Bill Date</th>
                                    <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#000'}}>Payment Time</th>
                                    <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#000'}}>Type</th>
                                    <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#000'}}>By</th>
                                    <th style={{padding: '8px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#000'}}>Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {customer.payments.map((payment, paymentIdx) => (
                                    <tr key={paymentIdx} style={{borderBottom: '1px solid #f0f0f0', background: 'white'}}>
                                      <td style={{padding: '8px', fontFamily: 'monospace', fontSize: '12px', color: '#000'}}>{payment.order_id ? payment.order_id.substring(0, 10) + '...' : 'N/A'}</td>
                                      <td style={{padding: '8px'}}><span style={{background: '#3182CE', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '11px'}}>{payment.plu_code || 'N/A'}</span></td>
                                      <td style={{padding: '8px', fontSize: '13px', color: '#000'}}>{payment.order_date ? new Date(payment.order_date).toLocaleDateString('en-GB') : 'N/A'}</td>
                                      <td style={{padding: '8px', fontSize: '13px', color: '#000'}}>{payment.payment_date ? new Date(payment.payment_date).toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'}) : 'N/A'}</td>
                                      <td style={{padding: '8px', fontSize: '12px'}}><span style={{background: payment.payment_type === 'cash' ? '#48BB78' : payment.payment_type === 'bank' ? '#4299E1' : '#ED8936', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '10px'}}>{payment.payment_type || 'N/A'}</span></td>
                                      <td style={{padding: '8px', fontSize: '13px', color: '#000'}}>{payment.recorded_by || 'admin'}</td>
                                      <td style={{padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#38A169'}}>£{payment.amount.toFixed(2)}</td>
                                    </tr>
                                  ))}
                                  <tr style={{background: '#F0FFF4', fontWeight: 'bold'}}>
                                    <td colSpan="6" style={{padding: '10px', textAlign: 'right', color: '#000'}}>TOTAL for {customer.customer_name}:</td>
                                    <td style={{padding: '10px', textAlign: 'right', color: '#38A169'}}>£{customer.total_paid.toFixed(2)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="no-orders-message" style={{padding: '2rem', textAlign: 'center', backgroundColor: '#FAF5FF', borderRadius: '8px'}}>
                      <p style={{color: '#6B46C1', margin: 0}}>Select a date to view daily activity.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* SESSION MANAGEMENT SECTION - Only visible to Zobayr (boss) */}
              <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: activeTab === 'session-management' ? 'block' : 'none'}}>
                <div className="admin-section-header">
                  <div className="section-nav">
                    <button className="btn secondary" onClick={() => setActiveTab('dashboard')} style={{marginRight: '1rem'}}>← Back to Dashboard</button>
                    <h3 style={{color: '#DC2626', margin: 0}}>🔐 Session Management</h3>
                  </div>
                  <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
                    <button 
                      className="btn primary" 
                      onClick={loadActiveSessions}
                      style={{backgroundColor: '#3B82F6', color: 'white'}}
                    >
                      🔄 Refresh
                    </button>
                    <button 
                      className="btn" 
                      onClick={terminateAllOtherSessions}
                      style={{backgroundColor: '#F59E0B', color: 'white'}}
                    >
                      🚫 Log Out Others
                    </button>
                    <button 
                      className="btn" 
                      onClick={terminateAllSessions}
                      style={{backgroundColor: '#DC2626', color: 'white'}}
                    >
                      ⚠️ TERMINATE ALL
                    </button>
                    <button 
                      className="btn" 
                      onClick={clearSessionHistory}
                      style={{backgroundColor: '#6B7280', color: 'white'}}
                    >
                      🗑️ Clear History
                    </button>
                  </div>
                </div>

                <div className="admin-table-container">
                  <div style={{marginBottom: '1rem', padding: '1rem', backgroundColor: '#FEF2F2', border: '1px solid #DC2626', borderRadius: '8px'}}>
                    <p style={{margin: 0, color: '#991B1B'}}>
                      <strong>🔐 Security Control:</strong> View ALL login sessions (active & inactive). Terminate sessions to force users to re-login. 
                      After terminating all sessions, change the password - only employees with the new password can log back in.
                    </p>
                  </div>

                  {loadingActiveSessions ? (
                    <div className="loading-message">Loading sessions...</div>
                  ) : activeSessions.length === 0 ? (
                    <div style={{padding: '2rem', textAlign: 'center', backgroundColor: '#F3F4F6', borderRadius: '8px'}}>
                      <p style={{color: '#6B7280', margin: 0}}>No sessions found. Sessions will appear here after users log in.</p>
                    </div>
                  ) : (
                    <>
                      {/* Summary - Updated to show active vs inactive */}
                      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '2rem'}}>
                        <div style={{background: '#DBEAFE', padding: '1rem', borderRadius: '8px', textAlign: 'center'}}>
                          <div style={{fontSize: '24px', fontWeight: 'bold', color: '#1E40AF'}}>{activeSessions.length}</div>
                          <div style={{fontSize: '12px', color: '#1E3A8A'}}>Total Sessions</div>
                        </div>
                        <div style={{background: '#D1FAE5', padding: '1rem', borderRadius: '8px', textAlign: 'center'}}>
                          <div style={{fontSize: '24px', fontWeight: 'bold', color: '#065F46'}}>{activeSessions.filter(s => s.is_active).length}</div>
                          <div style={{fontSize: '12px', color: '#047857'}}>Active (Logged In)</div>
                        </div>
                        <div style={{background: '#FEE2E2', padding: '1rem', borderRadius: '8px', textAlign: 'center'}}>
                          <div style={{fontSize: '24px', fontWeight: 'bold', color: '#991B1B'}}>{activeSessions.filter(s => !s.is_active).length}</div>
                          <div style={{fontSize: '12px', color: '#7F1D1D'}}>Inactive (Terminated)</div>
                        </div>
                        <div style={{background: '#FEF3C7', padding: '1rem', borderRadius: '8px', textAlign: 'center'}}>
                          <div style={{fontSize: '24px', fontWeight: 'bold', color: '#92400E'}}>{[...new Set(activeSessions.map(s => s.username))].length}</div>
                          <div style={{fontSize: '12px', color: '#B45309'}}>Unique Users</div>
                        </div>
                      </div>

                      {/* Sessions by User */}
                      {[...new Set(activeSessions.map(s => s.username))].map(username => {
                        const userSessions = activeSessions.filter(s => s.username === username);
                        const activeUserSessions = userSessions.filter(s => s.is_active);
                        const inactiveUserSessions = userSessions.filter(s => !s.is_active);
                        return (
                          <div key={username} style={{marginBottom: '1.5rem', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden'}}>
                            <div style={{background: '#1F2937', color: 'white', padding: '10px 15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                              <div>
                                <span style={{fontWeight: 'bold', fontSize: '16px'}}>{username}</span>
                                <span style={{marginLeft: '10px', fontSize: '12px', opacity: 0.8}}>
                                  ({userSessions.length} total: {activeUserSessions.length} active, {inactiveUserSessions.length} inactive)
                                </span>
                                <span style={{
                                  marginLeft: '10px', 
                                  fontSize: '10px', 
                                  padding: '2px 8px', 
                                  borderRadius: '4px',
                                  background: userSessions[0]?.user_type === 'admin' ? '#3B82F6' : '#10B981'
                                }}>
                                  {userSessions[0]?.user_type === 'admin' ? 'ADMIN' : 'DASHBOARD'}
                                </span>
                              </div>
                              {activeUserSessions.length > 0 && (
                                <button
                                  onClick={() => terminateAllUserSessions(username)}
                                  style={{
                                    background: '#DC2626',
                                    color: 'white',
                                    border: 'none',
                                    padding: '6px 12px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Log Out All ({activeUserSessions.length})
                                </button>
                              )}
                            </div>
                            <table style={{width: '100%', borderCollapse: 'collapse', background: 'white'}}>
                              <thead>
                                <tr style={{background: '#F3F4F6'}}>
                                  <th style={{padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#000', fontSize: '12px'}}>Status</th>
                                  <th style={{padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#000', fontSize: '12px'}}>Device</th>
                                  <th style={{padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#000', fontSize: '12px'}}>IP Address</th>
                                  <th style={{padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#000', fontSize: '12px'}}>Login Time</th>
                                  <th style={{padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#000', fontSize: '12px'}}>Last Activity</th>
                                  <th style={{padding: '10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#000', fontSize: '12px'}}>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {userSessions.map((session, idx) => (
                                  <tr key={session.session_id} style={{
                                    borderBottom: '1px solid #f0f0f0', 
                                    background: session.is_active 
                                      ? (idx % 2 === 0 ? '#F0FDF4' : '#DCFCE7')  // Green tint for active
                                      : (idx % 2 === 0 ? '#FEF2F2' : '#FEE2E2')  // Red tint for inactive
                                  }}>
                                    <td style={{padding: '10px'}}>
                                      <span style={{
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                        background: session.is_active ? '#22C55E' : '#EF4444',
                                        color: 'white'
                                      }}>
                                        {session.is_active ? '● ACTIVE' : '○ INACTIVE'}
                                      </span>
                                    </td>
                                    <td style={{padding: '10px', color: '#000'}}>
                                      <div style={{fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px'}}>
                                        {/* Device type icon */}
                                        <span style={{fontSize: '16px'}}>
                                          {session.device_type === 'Phone' ? '📱' : 
                                           session.device_type === 'Tablet' ? '📱' : '💻'}
                                        </span>
                                        {session.device_display || 'Unknown Device'}
                                      </div>
                                      <div style={{fontSize: '11px', color: '#6B7280', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                        {session.device_info?.substring(0, 50) || 'N/A'}...
                                      </div>
                                    </td>
                                    <td style={{padding: '10px', fontFamily: 'monospace', fontSize: '13px', color: '#000'}}>{session.ip_address || 'Unknown'}</td>
                                    <td style={{padding: '10px', fontSize: '13px', color: '#000'}}>
                                      {session.login_time ? new Date(session.login_time).toLocaleString('en-GB', {
                                        day: '2-digit', month: 'short', year: 'numeric',
                                        hour: '2-digit', minute: '2-digit'
                                      }) : 'N/A'}
                                    </td>
                                    <td style={{padding: '10px', fontSize: '13px', color: '#000'}}>
                                      {session.last_activity ? new Date(session.last_activity).toLocaleString('en-GB', {
                                        day: '2-digit', month: 'short', year: 'numeric',
                                        hour: '2-digit', minute: '2-digit'
                                      }) : 'N/A'}
                                    </td>
                                    <td style={{padding: '10px', textAlign: 'center'}}>
                                      {session.is_active ? (
                                        <button
                                          onClick={() => terminateSession(session.session_id, session.username)}
                                          disabled={terminatingSession === session.session_id}
                                          style={{
                                            background: terminatingSession === session.session_id ? '#9CA3AF' : '#EF4444',
                                            color: 'white',
                                            border: 'none',
                                            padding: '6px 12px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            cursor: terminatingSession === session.session_id ? 'wait' : 'pointer'
                                          }}
                                        >
                                          {terminatingSession === session.session_id ? 'Logging out...' : '🚫 Log Out'}
                                        </button>
                                      ) : (
                                        <span style={{color: '#9CA3AF', fontSize: '12px'}}>Already logged out</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>

              {/* INVOICES SECTION */}
              <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: activeTab === 'invoices' ? 'block' : 'none'}}>
                  <div className="admin-section-header">
                    <div className="section-nav">
                      <button className="btn secondary" onClick={() => setActiveTab('dashboard')} style={{marginRight: '1rem'}}>← Back to Dashboard</button>
                      <h3 style={{color: '#b8860b', margin: 0}}>Invoices</h3>
                    </div>
                  </div>

                  <div className="admin-table-container">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                      <h4 style={{margin: 0}}>Invoices Archive</h4>
                      <button 
                        className="btn primary" 
                        onClick={() => setShowStandaloneInvoiceModal(true)}
                        style={{backgroundColor: '#007bff', color: 'white', padding: '10px 20px'}}
                      >
                        ➕ Create Invoice
                      </button>
                    </div>
                    {loadingInvoices ? (
                      <div className="loading-message">Loading invoices...</div>
                    ) : (
                      <>
                        <div style={{marginBottom: '1rem'}}>
                          <PLUFilter
                            label="Filter Invoices"
                            placeholder="001, 002, 003..."
                            onFilterChange={setInvoicesSearchInput}
                          />
                        </div>
                        {invoicesFiltered && invoicesFiltered.length > 0 ? (
                          <div className="table-responsive">
                            <table className="admin-table">
                          <thead>
                            <tr>
                              <th>Invoice #</th>
                              <th>Order</th>
                              <th>Customer</th>
                              <th>Customer #</th>
                              <th>Total</th>
                              <th>Date</th>
                              <th style={{minWidth: '200px', width: '200px'}}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoicesFiltered.map((inv, index) => (
                              <tr key={inv?.id || `inv-${index}`}>
                                <td>{inv?.invoice_number || `INV-${new Date(inv?.date || Date.now()).getFullYear()}${String(new Date(inv?.date || Date.now()).getMonth() + 1).padStart(2, '0')}${String(new Date(inv?.date || Date.now()).getDate()).padStart(2, '0')}-${(inv?.id || '').substring(0, 4)}`}</td>
                                <td>
                                  {inv?.order_id === 'STANDALONE' ? (
                                    <span style={{color: '#666', fontStyle: 'italic'}}>N/A</span>
                                  ) : (
                                    <button
                                      onClick={() => {
                                        // Navigate to Fulfilled Orders tab and highlight the order
                                        setActiveTab('fulfilled-orders');
                                        setHighlightedOrderId(inv?.order_id);
                                        
                                        // Clear highlight after 5 seconds
                                        setTimeout(() => {
                                          setHighlightedOrderId("");
                                        }, 5000);
                                      }}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#007bff',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        padding: '0',
                                        font: 'inherit'
                                      }}
                                      title="Go to Fulfilled Orders"
                                    >
                                      #{inv?.order_id ? inv.order_id.substring(0, 8) : 'N/A'}...
                                    </button>
                                  )}
                                </td>
                                <td>{inv?.customer_name || inv?.customer_id || 'N/A'}</td>
                                <td><strong>{inv?.customer_number || 'N/A'}</strong></td>
                                <td>£{inv?.total_amount ? parseFloat(inv.total_amount).toFixed(2) : '0.00'}</td>
                                <td>{inv?.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : 'N/A'}</td>
                                <td>
                                  <div style={{display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'flex-start'}}>
                                    <button className="btn small secondary" style={{width: '160px', fontSize: '15px', padding: '3px 6px'}} onClick={() => handleViewInvoice(inv)}>View invoice</button>
                                    <button className="btn small danger" style={{width: '160px', fontSize: '15px', padding: '3px 6px'}} onClick={() => handleDeleteInvoice(inv.id)}>Delete</button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                        ) : (
                          <div className="no-orders-message">
                            <p>No invoices found.</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

              {/* ACCOUNTING SECTION */}
              {activeTab === 'accounting' && (
                <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div className="admin-section-header">
                    <div className="section-nav">
                      <button className="btn secondary" onClick={() => setActiveTab('dashboard')} style={{marginRight: '1rem'}}>← Back to Dashboard</button>
                      <h3 style={{color: '#b8860b', margin: 0}}>Accounting</h3>
                    </div>
                  </div>
                  <div className="admin-table-container">
                    <div className="table-toolbar" style={{display:'flex', gap:'1rem', marginBottom:'1rem', alignItems:'center'}}>
                      <button 
                        className="btn primary"
                        onClick={() => setShowInvoiceDownloadModal(true)}
                        style={{backgroundColor: '#007bff', color: 'white', padding: '10px 20px'}}
                      >
                        Download Invoices
                      </button>
                      <button 
                        className="btn secondary"
                        onClick={() => setShowBusinessAnalysisModal(true)}
                        style={{backgroundColor: '#6c757d', color: 'white', padding: '10px 20px'}}
                      >
                        Business Analysis
                      </button>
                    </div>
                    <h4>All Invoices</h4>
                    <div className="table-responsive">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Invoice #</th>
                            <th>Order</th>
                            <th>Customer</th>
                            <th>Total</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayedInvoices && displayedInvoices.length > 0 ? displayedInvoices.map((inv, idx) => (
                            <tr key={inv?.id || `acc-inv-${idx}`}>
                              <td>{inv?.invoice_number || `INV-${new Date(inv?.date || Date.now()).getFullYear()}${String(new Date(inv?.date || Date.now()).getMonth() + 1).padStart(2, '0')}${String(new Date(inv?.date || Date.now()).getDate()).padStart(2, '0')}-${(inv?.id || '').substring(0, 4)}`}</td>
                              <td>
                                <button
                                  onClick={() => {
                                    // Navigate to Fulfilled Orders tab and highlight the order
                                    setActiveTab('fulfilled-orders');
                                    setHighlightedOrderId(inv?.order_id);
                                    
                                    // Clear highlight after 5 seconds
                                    setTimeout(() => {
                                      setHighlightedOrderId("");
                                    }, 5000);
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#007bff',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    padding: '0',
                                    font: 'inherit'
                                  }}
                                  title="Go to Fulfilled Orders"
                                >
                                  #{inv?.order_id ? inv.order_id.substring(0,8) : 'N/A'}...
                                </button>
                              </td>
                              <td>{inv?.customer_name || inv?.customer_id || 'N/A'}</td>
                              <td>£{parseFloat(inv?.grand_total || inv?.total_amount || 0).toFixed(2)}</td>
                              <td>{inv?.date ? new Date(inv.date).toLocaleDateString('en-GB', { timeZone: 'Europe/London' }) : 'N/A'}</td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                {!displayedInvoices ? 'Loading invoices...' : 'No invoices found'}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* WAREHOUSE BREAKDOWN SECTION */}
              {activeTab === 'warehouse-breakdown' && (
                <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div className="admin-section-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                    <div className="section-nav" style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                      <button className="btn secondary" onClick={() => setActiveTab('dashboard')}>← Back to Dashboard</button>
                      <h3 style={{color: '#b8860b', margin: 0}}>Warehouse Breakdown</h3>
                    </div>
                    <button
                      onClick={downloadAllCustomersReport}
                      style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                    >
                      📥 Download All
                    </button>
                  </div>
                  
                  <div className="admin-table-container">
                    <h4>Customer Purchase & Credit History</h4>
                    <p style={{color: '#666', marginBottom: '1.5rem'}}>Click on any customer to view detailed purchase history</p>
                    
                    <div style={{display: 'grid', gap: '1rem'}}>
                      {customers && customers.length > 0 ? (
                        customers
                          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                          .map((customer) => {
                            const customerOrders = fulfilledOrders.filter(order => 
                              order.customer_id === customer.id && 
                              order.status !== 'returned' && 
                              order.status !== 'cancelled'
                            );
                            
                            // 💰 BULLETPROOF: Derive Paid from the truth (grand_total - outstanding).
                            // This guarantees: Paid + Credit = Total Bill, no matter HOW the payment was tracked
                            // (cash, bank, credit, lump-sum, or any combo).
                            let totalGrandTotal = 0;
                            let totalCashPaid = 0;
                            let totalBankPaid = 0;
                            
                            customerOrders.forEach(order => {
                              totalGrandTotal += parseFloat(order.grand_total || 0);
                              totalCashPaid += parseFloat(order.cash_paid_amount || 0);
                              totalBankPaid += parseFloat(order.bank_paid_amount || 0);
                            });
                            
                            // Include lump-sum (account-level) payments in cash/bank breakdown
                            const accountPaymentsData = customerAccountPayments[customer.id] || { total: 0, payments: [] };
                            (accountPaymentsData.payments || []).forEach(p => {
                              const amt = parseFloat(p.amount || 0);
                              const method = (p.payment_method || '').toUpperCase();
                              if (method === 'BANK') {
                                totalBankPaid += amt;
                              } else {
                                totalCashPaid += amt;
                              }
                            });
                            
                            // Credit owed: outstanding from current orders - account payments
                            const customerCreditOrders = (creditAccountsOrders || []).filter(o => 
                              o.customer_id === customer.id
                            );
                            let totalCredit = 0;
                            customerCreditOrders.forEach(order => {
                              totalCredit += parseFloat(order.outstanding_balance || 0);
                            });
                            totalCredit = Math.max(0, totalCredit - accountPaymentsData.total);
                            
                            // 💰 Total Paid = Total Bill - Credit Owed (guaranteed correct)
                            const totalPaid = Math.max(0, totalGrandTotal - totalCredit);
                            
                            const isExpanded = expandedCustomerId === customer.id;
                            
                            return (
                              <div key={customer.id} style={{
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                background: isExpanded ? '#f8f9fa' : 'white'
                              }}>
                                {/* Customer Summary Header */}
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: '2fr 1fr 1fr 100px 50px',
                                  gap: '1rem',
                                  alignItems: 'center',
                                  background: isExpanded ? '#e3f2fd' : 'white',
                                  transition: 'background 0.2s'
                                }}>
                                  <div
                                    onClick={() => setExpandedCustomerId(isExpanded ? null : customer.id)}
                                    style={{
                                      padding: '1rem 1.5rem',
                                      cursor: 'pointer',
                                      display: 'contents'
                                    }}
                                  >
                                    <div>
                                      <strong style={{fontSize: '1.1rem', color: '#333'}}>
                                        {customer.name || 'N/A'}
                                      </strong>
                                      {customer.customer_number && (
                                        <span style={{
                                          marginLeft: '0.5rem',
                                          color: '#666',
                                          fontSize: '0.9rem'
                                        }}>
                                          #{customer.customer_number}
                                        </span>
                                      )}
                                    </div>
                                    <div style={{textAlign: 'center'}}>
                                      <div style={{fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem'}}>Paid</div>
                                      <div style={{fontSize: '1.1rem', fontWeight: '600', color: '#28a745'}}>
                                        £{totalPaid.toFixed(2)}
                                      </div>
                                      <div style={{fontSize: '0.75rem', color: '#666', marginTop: '0.25rem'}}>
                                        💵 Cash: £{totalCashPaid.toFixed(2)}
                                      </div>
                                      <div style={{fontSize: '0.75rem', color: '#666'}}>
                                        🏦 Bank: £{totalBankPaid.toFixed(2)}
                                      </div>
                                    </div>
                                    <div style={{textAlign: 'center'}}>
                                      <div style={{fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem'}}>Credit</div>
                                      <div style={{fontSize: '1.1rem', fontWeight: '600', color: '#dc3545'}}>
                                        £{totalCredit.toFixed(2)}
                                      </div>
                                    </div>
                                  </div>
                                  <div style={{textAlign: 'center', padding: '0 1rem'}}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        downloadCustomerReport(customer, customerOrders);
                                      }}
                                      style={{
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '0.9rem',
                                        fontWeight: '500',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        whiteSpace: 'nowrap'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0056b3'}
                                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#007bff'}
                                    >
                                      📥 Download
                                    </button>
                                  </div>
                                  <div 
                                    onClick={() => setExpandedCustomerId(isExpanded ? null : customer.id)}
                                    style={{textAlign: 'center', fontSize: '1.5rem', color: '#666', cursor: 'pointer', padding: '1rem'}}
                                  >
                                    {isExpanded ? '▲' : '▼'}
                                  </div>
                                </div>
                                
                                {/* Expanded Purchase History */}
                                {isExpanded && customerOrders.length > 0 && (
                                  <div style={{padding: '1.5rem', borderTop: '1px solid #ddd', background: 'white'}}>
                                    <h5 style={{marginBottom: '1rem', color: '#333'}}>Purchase History</h5>
                                    
                                    {customerOrders.map((order, idx) => {
                                      // Get stored values
                                      const storedSubtotal = parseFloat(order.subtotal || 0);
                                      const storedVatAmount = parseFloat(order.vat_amount || 0);
                                      const storedGrandTotal = parseFloat(order.grand_total || 0);
                                      
                                      // Recalculate from items to ensure accuracy
                                      let recalculatedSubtotal = 0;
                                      if (order.items && order.items.length > 0) {
                                        recalculatedSubtotal = order.items.reduce((sum, item) => {
                                          return sum + parseFloat(item.total_price || 0);
                                        }, 0);
                                      } else {
                                        recalculatedSubtotal = storedSubtotal;
                                      }
                                      
                                      // Add service charges
                                      recalculatedSubtotal += parseFloat(order.fitting_charge || 0);
                                      recalculatedSubtotal += parseFloat(order.cut_piece_charge || 0);
                                      
                                      // Subtract discount
                                      recalculatedSubtotal -= parseFloat(order.discount || 0);
                                      
                                      // Calculate VAT using the order's VAT rate (not hardcoded)
                                      const vatRate = parseFloat(order.vat_rate || 0);
                                      const recalculatedVatAmount = recalculatedSubtotal * vatRate;
                                      const recalculatedGrandTotal = recalculatedSubtotal + recalculatedVatAmount;
                                      
                                      // Use recalculated values
                                      const subtotal = recalculatedSubtotal;
                                      const vatAmount = recalculatedVatAmount;
                                      const grandTotal = recalculatedGrandTotal;
                                      
                                      const orderDate = new Date(order.date).toLocaleDateString('en-GB');
                                      
                                      return (
                                        <div key={order.id} style={{
                                          marginBottom: '1.5rem',
                                          padding: '1rem',
                                          background: '#f8f9fa',
                                          borderRadius: '6px',
                                          border: '1px solid #e0e0e0'
                                        }}>
                                          {/* Order Header */}
                                          <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            marginBottom: '1rem',
                                            paddingBottom: '0.5rem',
                                            borderBottom: '1px solid #ddd'
                                          }}>
                                            <div>
                                              <strong>Order #{order.id.substring(0, 8)}</strong>
                                              <span style={{marginLeft: '1rem', color: '#666'}}>{orderDate}</span>
                                            </div>
                                            <div style={{fontWeight: '600', color: '#333'}}>
                                              Grand Total: £{grandTotal.toFixed(2)}
                                            </div>
                                          </div>
                                          
                                          {/* Items List */}
                                          <div style={{marginBottom: '1rem'}}>
                                            <strong style={{fontSize: '0.95rem', color: '#333'}}>Items Purchased:</strong>
                                            <table style={{width: '100%', marginTop: '0.5rem', fontSize: '0.9rem', color: '#333'}}>
                                              <thead>
                                                <tr style={{borderBottom: '2px solid #333', backgroundColor: '#f8f9fa'}}>
                                                  <th style={{textAlign: 'left', padding: '0.75rem', color: '#333', fontWeight: '600'}}>Product</th>
                                                  <th style={{textAlign: 'center', padding: '0.75rem', color: '#333', fontWeight: '600'}}>Qty</th>
                                                  <th style={{textAlign: 'right', padding: '0.75rem', color: '#333', fontWeight: '600'}}>Unit Price</th>
                                                  <th style={{textAlign: 'right', padding: '0.75rem', color: '#333', fontWeight: '600'}}>Total</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {order.items && order.items.length > 0 ? (
                                                  order.items.map((item, itemIdx) => (
                                                    <tr key={itemIdx} style={{borderBottom: '1px solid #ddd'}}>
                                                      <td style={{padding: '0.75rem', color: '#333'}}>
                                                        <div style={{fontWeight: '500'}}>{item.product_name || 'N/A'}</div>
                                                        <div style={{fontSize: '0.85rem', color: '#666', marginTop: '0.25rem'}}>
                                                          {item.size && item.size !== 'N/A' ? `Size: ${item.size}` : ''}
                                                          {item.color ? ` | Color: ${item.color}` : ''}
                                                        </div>
                                                      </td>
                                                      <td style={{textAlign: 'center', padding: '0.75rem', color: '#333'}}>
                                                        {(() => {
                                                          // Calculate quantity from total_price / unit_price if not stored
                                                          let qty = 0;
                                                          if (item.measurement_unit === 'per_square_meter') {
                                                            qty = item.sqm || (parseFloat(item.total_price || 0) / parseFloat(item.unit_price || 1));
                                                          } else {
                                                            qty = item.quantity || (parseFloat(item.total_price || 0) / parseFloat(item.unit_price || 1));
                                                          }
                                                          return item.measurement_unit === 'per_square_meter' 
                                                            ? `${qty.toFixed(2)} m²`
                                                            : `${Math.round(qty)} units`;
                                                        })()}
                                                      </td>
                                                      <td style={{textAlign: 'right', padding: '0.75rem', color: '#333'}}>
                                                        £{parseFloat(item.unit_price || 0).toFixed(2)}
                                                      </td>
                                                      <td style={{textAlign: 'right', padding: '0.75rem', color: '#333', fontWeight: '600'}}>
                                                        £{parseFloat(item.total_price || 0).toFixed(2)}
                                                      </td>
                                                    </tr>
                                                  ))
                                                ) : (
                                                  <tr>
                                                    <td colSpan="4" style={{padding: '1rem', textAlign: 'center', color: '#666'}}>
                                                      No items found
                                                    </td>
                                                  </tr>
                                                )}
                                              </tbody>
                                            </table>
                                          </div>
                                          
                                          {/* Order Totals */}
                                          <div style={{
                                            padding: '1rem',
                                            background: 'white',
                                            borderRadius: '4px',
                                            border: '1px solid #ddd'
                                          }}>
                                            <div style={{display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', marginBottom: '0.5rem'}}>
                                              <div style={{color: '#666'}}>Items Subtotal:</div>
                                              <div style={{fontWeight: '600', textAlign: 'right'}}>£{(order.items?.reduce((sum, item) => sum + parseFloat(item.total_price || 0), 0) || 0).toFixed(2)}</div>
                                            </div>
                                            
                                            {order.fitting_charge > 0 && (
                                              <div style={{display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', marginBottom: '0.5rem'}}>
                                                <div style={{color: '#666'}}>Fitting Charge:</div>
                                                <div style={{fontWeight: '600', textAlign: 'right'}}>£{parseFloat(order.fitting_charge || 0).toFixed(2)}</div>
                                              </div>
                                            )}
                                            
                                            {order.cut_piece_charge > 0 && (
                                              <div style={{display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', marginBottom: '0.5rem'}}>
                                                <div style={{color: '#666'}}>Cut Piece Charge:</div>
                                                <div style={{fontWeight: '600', textAlign: 'right'}}>£{parseFloat(order.cut_piece_charge || 0).toFixed(2)}</div>
                                              </div>
                                            )}
                                            
                                            {order.discount > 0 && (
                                              <div style={{display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', marginBottom: '0.5rem'}}>
                                                <div style={{color: '#dc3545'}}>Discount:</div>
                                                <div style={{fontWeight: '600', textAlign: 'right', color: '#dc3545'}}>-£{parseFloat(order.discount || 0).toFixed(2)}</div>
                                              </div>
                                            )}
                                            
                                            <div style={{borderTop: '1px solid #ddd', marginTop: '0.5rem', paddingTop: '0.5rem', display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', marginBottom: '0.5rem'}}>
                                              <div style={{color: '#666'}}>Subtotal (No VAT):</div>
                                              <div style={{fontWeight: '600', fontSize: '1.1rem', textAlign: 'right'}}>£{subtotal.toFixed(2)}</div>
                                            </div>
                                            
                                            <div style={{display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', marginBottom: '0.5rem'}}>
                                              <div style={{color: '#666'}}>VAT (20%):</div>
                                              <div style={{fontWeight: '600', fontSize: '1.1rem', textAlign: 'right'}}>£{vatAmount.toFixed(2)}</div>
                                            </div>
                                            
                                            <div style={{borderTop: '2px solid #333', marginTop: '0.5rem', paddingTop: '0.5rem', display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem'}}>
                                              <div style={{fontSize: '1.1rem', fontWeight: '600', color: '#333'}}>Grand Total (Inc VAT):</div>
                                              <div style={{fontSize: '1.2rem', fontWeight: '700', color: '#007bff', textAlign: 'right'}}>
                                                £{grandTotal.toFixed(2)}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                                
                                {isExpanded && customerOrders.length === 0 && (
                                  <div style={{
                                    padding: '2rem',
                                    textAlign: 'center',
                                    color: '#666',
                                    background: 'white'
                                  }}>
                                    No purchase history found for this customer
                                  </div>
                                )}
                              </div>
                            );
                          })
                      ) : (
                        <div style={{
                          padding: '3rem',
                          textAlign: 'center',
                          color: '#666',
                          background: '#f8f9fa',
                          borderRadius: '8px'
                        }}>
                          No customers found
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* EXPENSES SECTION */}
              {activeTab === 'expenses' && (
                <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div className="admin-section-header">
                    <div className="section-nav">
                      <button className="btn secondary" onClick={() => setActiveTab('dashboard')} style={{marginRight: '1rem'}}>← Back to Dashboard</button>
                      <h3 style={{color: '#b8860b', margin: 0}}>Expenses</h3>
                    </div>
                  </div>
                  
                  <div style={{marginBottom: '2rem'}}>
                    <button 
                      className="btn primary"
                      onClick={() => setShowExpensePdfModal(true)}
                      style={{backgroundColor: '#007bff', color: 'white', padding: '10px 20px'}}
                    >
                      📄 EXPENSES PDF
                    </button>
                  </div>

                  {/* Tab Navigation */}
                  <div style={{display: 'flex', gap: '1rem', marginBottom: '2rem', borderBottom: '2px solid #ddd'}}>
                    <button
                      onClick={() => setExpenseTab('list')}
                      style={{
                        padding: '10px 20px',
                        background: expenseTab === 'list' ? '#007bff' : 'transparent',
                        color: expenseTab === 'list' ? 'white' : '#333',
                        border: 'none',
                        borderBottom: expenseTab === 'list' ? '3px solid #007bff' : 'none',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '600'
                      }}
                    >
                      📋 Expense List
                    </button>
                    <button
                      onClick={() => setExpenseTab('onetime')}
                      style={{
                        padding: '10px 20px',
                        background: expenseTab === 'onetime' ? '#007bff' : 'transparent',
                        color: expenseTab === 'onetime' ? 'white' : '#333',
                        border: 'none',
                        borderBottom: expenseTab === 'onetime' ? '3px solid #007bff' : 'none',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '600'
                      }}
                    >
                      ➕ Add One-Time
                    </button>
                    <button
                      onClick={() => setExpenseTab('recurring')}
                      style={{
                        padding: '10px 20px',
                        background: expenseTab === 'recurring' ? '#007bff' : 'transparent',
                        color: expenseTab === 'recurring' ? 'white' : '#333',
                        border: 'none',
                        borderBottom: expenseTab === 'recurring' ? '3px solid #007bff' : 'none',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '600'
                      }}
                    >
                      🔄 Recurring Expenses
                    </button>
                  </div>

                  {/* EXPENSE LIST TAB - View all expenses */}
                  {expenseTab === 'list' && (
                    <div>
                      <h4 style={{color: '#333', marginBottom: '1rem'}}>All Expenses</h4>
                      {loadingExpenses ? (
                        <p style={{color: '#333'}}>Loading expenses...</p>
                      ) : expenses.length === 0 ? (
                        <p style={{color: '#666'}}>No expenses found. Add your first expense using the "Add One-Time" or "Recurring Expenses" tabs.</p>
                      ) : (
                        <div style={{overflowX: 'auto'}}>
                          <table className="admin-table">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Description</th>
                                <th>Amount</th>
                                <th>Payment Method</th>
                                <th>Type</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {expenses.map((expense) => (
                                editingExpense && editingExpense.id === expense.id ? (
                                  <tr key={expense.id} style={{background: '#fff3cd'}}>
                                    <td>
                                      <input
                                        type="date"
                                        value={editingExpense.date}
                                        onChange={(e) => setEditingExpense({...editingExpense, date: e.target.value})}
                                        style={{padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px', width: '140px'}}
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        value={editingExpense.expense_description}
                                        onChange={(e) => setEditingExpense({...editingExpense, expense_description: e.target.value})}
                                        style={{padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px', width: '100%'}}
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        step="0.01"
                                        value={editingExpense.amount}
                                        onChange={(e) => setEditingExpense({...editingExpense, amount: e.target.value})}
                                        style={{padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px', width: '100px'}}
                                      />
                                    </td>
                                    <td>
                                      <select
                                        value={editingExpense.payment_method}
                                        onChange={(e) => setEditingExpense({...editingExpense, payment_method: e.target.value})}
                                        style={{padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px'}}
                                      >
                                        <option value="cash">Cash</option>
                                        <option value="bank">Bank</option>
                                      </select>
                                    </td>
                                    <td></td>
                                    <td style={{display: 'flex', gap: '4px'}}>
                                      <button
                                        className="btn small"
                                        onClick={() => handleUpdateExpense(expense.id, {
                                          date: editingExpense.date,
                                          expense_description: editingExpense.expense_description,
                                          amount: parseFloat(editingExpense.amount),
                                          payment_method: editingExpense.payment_method
                                        })}
                                        style={{padding: '4px 8px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}
                                      >
                                        Save
                                      </button>
                                      <button
                                        className="btn small"
                                        onClick={() => setEditingExpense(null)}
                                        style={{padding: '4px 8px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}
                                      >
                                        Cancel
                                      </button>
                                    </td>
                                  </tr>
                                ) : (
                                <tr key={expense.id}>
                                  <td>{new Date(expense.date).toLocaleDateString()}</td>
                                  <td>{expense.expense_description}</td>
                                  <td><strong>£{parseFloat(expense.amount).toFixed(2)}</strong></td>
                                  <td style={{textTransform: 'capitalize'}}>
                                    {expense.payment_method === 'cash' ? '💵 Cash' : '🏦 Bank'}
                                  </td>
                                  <td>
                                    {expense.created_by === 'system_recurring' ? (
                                      <span style={{color: '#007bff', fontSize: '12px'}}>🔄 Auto-generated</span>
                                    ) : (
                                      <span style={{color: '#28a745', fontSize: '12px'}}>✏️ Manual</span>
                                    )}
                                  </td>
                                  <td style={{display: 'flex', gap: '4px'}}>
                                    <button
                                      className="btn small"
                                      data-testid={`edit-expense-${expense.id}`}
                                      onClick={() => setEditingExpense({
                                        id: expense.id,
                                        date: expense.date,
                                        expense_description: expense.expense_description,
                                        amount: expense.amount,
                                        payment_method: expense.payment_method
                                      })}
                                      style={{padding: '4px 8px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}
                                    >
                                      ✏️ Edit
                                    </button>
                                    <button
                                      className="btn small danger"
                                      onClick={() => handleDeleteExpense(expense.id)}
                                      style={{padding: '4px 8px'}}
                                    >
                                      🗑️ Delete
                                    </button>
                                  </td>
                                </tr>
                                )
                              ))}
                            </tbody>
                            <tfoot>
                              <tr style={{fontWeight: 'bold', background: '#f8f9fa'}}>
                                <td colSpan="2" style={{textAlign: 'right', color: '#333'}}>TOTALS:</td>
                                <td><strong style={{color: '#333'}}>£{expenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0).toFixed(2)}</strong></td>
                                <td style={{color: '#333'}}>
                                  💵 £{expenses.filter(e => e.payment_method === 'cash').reduce((sum, e) => sum + parseFloat(e.amount || 0), 0).toFixed(2)}
                                </td>
                                <td style={{color: '#333'}}>
                                  🏦 £{expenses.filter(e => e.payment_method === 'bank').reduce((sum, e) => sum + parseFloat(e.amount || 0), 0).toFixed(2)}
                                </td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ONE-TIME EXPENSES TAB */}
                  {expenseTab === 'onetime' && (
                    <div>
                      <h4 style={{color: '#333', marginBottom: '1rem'}}>Add One-Time Expense</h4>
                      <form key="onetime-expense-form" onSubmit={handleCreateExpense} style={{marginBottom: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #ddd'}}>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem'}}>
                          <div>
                            <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333'}}>Date</label>
                            <input
                              type="date"
                              value={newExpense.date}
                              onChange={(e) => {
                                const value = e.target.value;
                                setNewExpense(prev => ({...prev, date: value}));
                              }}
                              style={{width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', color: '#333'}}
                              required
                            />
                          </div>
                          <div>
                            <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333'}}>Payment Method</label>
                            <select
                              value={newExpense.payment_method}
                              onChange={(e) => setNewExpense({...newExpense, payment_method: e.target.value})}
                              style={{width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', color: '#333'}}
                            >
                              <option value="cash">💵 Cash</option>
                              <option value="bank">🏦 Bank</option>
                            </select>
                          </div>
                        </div>
                        <div style={{background:'rgba(184,134,11,0.05)', padding:'1rem', borderRadius:'6px', marginBottom:'1rem', border:'1px solid rgba(184,134,11,0.2)'}}>
                          <h4 style={{color:'#b8860b', marginBottom:'0.5rem', fontSize:'1.1rem'}}>📝 Description / Notes</h4>
                          <p style={{fontSize:'0.85rem', color:'#666', marginBottom:'0.75rem'}}>
                            Add notes about this payment (optional)
                          </p>
                          <textarea
                            name="expense_description"
                            defaultValue={newExpense.expense_description}
                            onBlur={(e) => setNewExpense({...newExpense, expense_description: e.target.value})}
                            placeholder="Example: Payment for carpet installation, Balance payment for order, Deposit for upcoming order..."
                            style={{
                              width: '100%',
                              padding: '8px',
                              minHeight: '100px',
                              resize: 'vertical',
                              fontFamily: 'inherit',
                              fontSize: '0.95rem',
                              color: '#333',
                              backgroundColor: '#fff',
                              border: '1px solid #ccc',
                              borderRadius: '4px'
                            }}
                          />
                        </div>
                        <div style={{marginBottom: '1rem'}}>
                          <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333'}}>Amount (£)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={newExpense.amount}
                            onChange={(e) => {
                              const value = e.target.value;
                              setNewExpense(prev => ({...prev, amount: value}));
                            }}
                            placeholder="0.00"
                            style={{width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', color: '#333'}}
                            required
                          />
                        </div>
                        <button type="submit" className="btn primary" style={{backgroundColor: '#28a745', color: 'white'}}>
                          💾 Save Expense
                        </button>
                      </form>

                      <h4 style={{color: '#333', marginBottom: '1rem'}}>Recent Expenses</h4>
                      {loadingExpenses ? (
                        <p style={{color: '#333'}}>Loading expenses...</p>
                      ) : expenses.length === 0 ? (
                        <p style={{color: '#666'}}>No expenses found. Add your first expense above.</p>
                      ) : (
                        <div style={{overflowX: 'auto'}}>
                          <table className="admin-table">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Description</th>
                                <th>Amount</th>
                                <th>Payment Method</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {expenses.slice(0, 10).map((expense) => (
                                <tr key={expense.id}>
                                  <td>{new Date(expense.date).toLocaleDateString()}</td>
                                  <td>{expense.expense_description}</td>
                                  <td><strong>£{parseFloat(expense.amount).toFixed(2)}</strong></td>
                                  <td style={{textTransform: 'capitalize'}}>
                                    {expense.payment_method === 'cash' ? '💵 Cash' : '🏦 Bank'}
                                  </td>
                                  <td>
                                    <button
                                      className="btn small danger"
                                      onClick={() => handleDeleteExpense(expense.id)}
                                      style={{padding: '4px 8px'}}
                                    >
                                      🗑️ Delete
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr style={{fontWeight: 'bold', background: '#f8f9fa'}}>
                                <td colSpan="2" style={{textAlign: 'right', color: '#333'}}>TOTAL (Last 10):</td>
                                <td><strong style={{color: '#333'}}>£{expenses.slice(0, 10).reduce((sum, e) => sum + parseFloat(e.amount || 0), 0).toFixed(2)}</strong></td>
                                <td colSpan="2" style={{color: '#666', fontSize: '12px'}}>
                                  <a href="#" onClick={(e) => { e.preventDefault(); setExpenseTab('list'); }} style={{color: '#007bff'}}>View All Expenses →</a>
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* RECURRING EXPENSES TAB */}
                  {expenseTab === 'recurring' && (
                    <div>
                      <h4 style={{color: '#333', marginBottom: '1rem'}}>{editingRecurringExpense ? 'Edit Recurring Expense' : 'Add Recurring Expense'}</h4>
                      <form key="recurring-expense-form" onSubmit={handleCreateRecurringExpense} style={{marginBottom: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #ddd'}}>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem'}}>
                          <div>
                            <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333'}}>Frequency</label>
                            <select
                              value={newRecurringExpense.frequency}
                              onChange={(e) => {
                                const value = e.target.value;
                                setNewRecurringExpense(prev => ({...prev, frequency: value}));
                              }}
                              style={{width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', color: '#333'}}
                            >
                              <option value="weekly">📅 Weekly</option>
                              <option value="monthly">📆 Monthly</option>
                            </select>
                          </div>
                          <div>
                            <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333'}}>Payment Method</label>
                            <select
                              value={newRecurringExpense.payment_method}
                              onChange={(e) => {
                                const value = e.target.value;
                                setNewRecurringExpense(prev => ({...prev, payment_method: value}));
                              }}
                              style={{width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', color: '#333'}}
                            >
                              <option value="cash">💵 Cash</option>
                              <option value="bank">🏦 Bank</option>
                            </select>
                          </div>
                        </div>
                        <div style={{background:'rgba(184,134,11,0.05)', padding:'1rem', borderRadius:'6px', marginBottom:'1rem', border:'1px solid rgba(184,134,11,0.2)'}}>
                          <h4 style={{color:'#b8860b', marginBottom:'0.5rem', fontSize:'1.1rem'}}>📝 Description / Notes</h4>
                          <p style={{fontSize:'0.85rem', color:'#666', marginBottom:'0.75rem'}}>
                            Add notes about this payment (optional)
                          </p>
                          <textarea
                            name="recurring_expense_description"
                            defaultValue={newRecurringExpense.expense_description}
                            onBlur={(e) => setNewRecurringExpense({...newRecurringExpense, expense_description: e.target.value})}
                            placeholder="Example: Payment for carpet installation, Balance payment for order, Deposit for upcoming order..."
                            style={{
                              width: '100%',
                              padding: '8px',
                              minHeight: '100px',
                              resize: 'vertical',
                              fontFamily: 'inherit',
                              fontSize: '0.95rem',
                              color: '#333',
                              backgroundColor: '#fff',
                              border: '1px solid #ccc',
                              borderRadius: '4px'
                            }}
                          />
                        </div>
                        <div style={{marginBottom: '1rem'}}>
                          <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333'}}>Amount (£)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={newRecurringExpense.amount}
                            onChange={(e) => {
                              const value = e.target.value;
                              setNewRecurringExpense(prev => ({...prev, amount: value}));
                            }}
                            placeholder="0.00"
                            style={{width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', color: '#333'}}
                            required
                          />
                        </div>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem'}}>
                          <div>
                            <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333'}}>Start Date</label>
                            <input
                              type="date"
                              value={newRecurringExpense.start_date}
                              onChange={(e) => {
                                const value = e.target.value;
                                setNewRecurringExpense(prev => ({...prev, start_date: value}));
                              }}
                              style={{width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', color: '#333'}}
                              required
                            />
                          </div>
                          <div>
                            <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333'}}>End Date (Optional)</label>
                            <input
                              type="date"
                              value={newRecurringExpense.end_date}
                              onChange={(e) => {
                                const value = e.target.value;
                                setNewRecurringExpense(prev => ({...prev, end_date: value}));
                              }}
                              style={{width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', color: '#333'}}
                              placeholder="Leave blank for ongoing"
                            />
                          </div>
                        </div>
                        <div style={{display: 'flex', gap: '1rem'}}>
                          <button type="submit" className="btn primary" style={{backgroundColor: '#28a745', color: 'white'}}>
                            💾 {editingRecurringExpense ? 'Update' : 'Save'} Recurring Expense
                          </button>
                          {editingRecurringExpense && (
                            <button
                              type="button"
                              className="btn secondary"
                              onClick={() => {
                                setEditingRecurringExpense(null);
                                setNewRecurringExpense({
                                  expense_description: '',
                                  amount: '',
                                  payment_method: 'cash',
                                  frequency: 'monthly',
                                  start_date: new Date().toISOString().split('T')[0],
                                  end_date: ''
                                });
                              }}
                            >
                              Cancel Edit
                            </button>
                          )}
                        </div>
                      </form>

                      <h4 style={{color: '#333', marginBottom: '1rem'}}>Recurring Expenses Templates</h4>
                      {loadingExpenses ? (
                        <p style={{color: '#333'}}>Loading recurring expenses...</p>
                      ) : recurringExpenses.length === 0 ? (
                        <p style={{color: '#666'}}>No recurring expenses found. Add your first recurring expense above (e.g., rent, utilities, wages).</p>
                      ) : (
                        <div style={{overflowX: 'auto'}}>
                          <table className="admin-table">
                            <thead>
                              <tr>
                                <th>Description</th>
                                <th>Amount</th>
                                <th>Frequency</th>
                                <th>Payment Method</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {recurringExpenses.map((expense) => (
                                <tr key={expense.id}>
                                  <td>{expense.expense_description}</td>
                                  <td><strong>£{parseFloat(expense.amount).toFixed(2)}</strong></td>
                                  <td style={{textTransform: 'capitalize'}}>
                                    {expense.frequency === 'weekly' ? '📅 Weekly' : '📆 Monthly'}
                                  </td>
                                  <td style={{textTransform: 'capitalize'}}>
                                    {expense.payment_method === 'cash' ? '💵 Cash' : '🏦 Bank'}
                                  </td>
                                  <td>{new Date(expense.start_date).toLocaleDateString()}</td>
                                  <td>{expense.end_date ? new Date(expense.end_date).toLocaleDateString() : '♾️ Ongoing'}</td>
                                  <td>
                                    <div style={{display: 'flex', gap: '0.5rem'}}>
                                      <button
                                        className="btn small secondary"
                                        onClick={() => handleEditRecurringExpense(expense)}
                                        style={{padding: '4px 8px'}}
                                      >
                                        ✏️ Edit
                                      </button>
                                      <button
                                        className="btn small danger"
                                        onClick={() => handleDeleteRecurringExpense(expense.id)}
                                        style={{padding: '4px 8px'}}
                                      >
                                        🗑️ Delete
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Expenses PDF Download Modal */}
              {showExpensePdfModal && (
                <div className="modal-overlay" onClick={() => setShowExpensePdfModal(false)}>
                  <div className="modal" onClick={(e) => e.stopPropagation()} style={{maxWidth: '500px'}}>
                    <div className="modal-header">
                      <h3 style={{color: '#333'}}>Download Expenses PDF</h3>
                      <button className="modal-close" onClick={() => setShowExpensePdfModal(false)}>×</button>
                    </div>
                    <div className="modal-body">
                      <div style={{marginBottom: '1rem'}}>
                        <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333'}}>From Date</label>
                        <input
                          type="date"
                          value={expensePdfFromDate}
                          onChange={(e) => setExpensePdfFromDate(e.target.value)}
                          style={{width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', color: '#333'}}
                        />
                      </div>
                      <div style={{marginBottom: '1rem'}}>
                        <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333'}}>To Date</label>
                        <input
                          type="date"
                          value={expensePdfToDate}
                          onChange={(e) => setExpensePdfToDate(e.target.value)}
                          style={{width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', color: '#333'}}
                        />
                      </div>
                      <button
                        className="btn primary"
                        onClick={handleDownloadExpensesPdf}
                        style={{width: '100%', backgroundColor: '#007bff', color: 'white'}}
                      >
                        📄 Download PDF
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* PRODUCT ENQUIRIES SECTION */}
              {activeTab === 'product-enquiries' && (
                <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div className="admin-section-header">
                    <div className="section-nav">
                      <button className="btn secondary" onClick={() => setActiveTab('dashboard')} style={{marginRight: '1rem'}}>← Back to Dashboard</button>
                      <h3 style={{color: '#38B2AC', margin: 0}}>Product Enquiries</h3>
                    </div>
                  </div>

                  <div style={{marginTop: '2rem', marginBottom: '2rem'}}>
                    <p style={{color: '#666', marginBottom: '1rem'}}>
                      🔍 Quick product lookup for customer enquiries. Search by product name to see stock, pricing, and all details instantly.
                    </p>
                  </div>

                  {/* Search Box */}
                  <div style={{marginBottom: '2rem', maxWidth: '600px'}}>
                    <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333', fontSize: '16px'}}>
                      Search Product
                    </label>
                    <input
                      type="text"
                      value={productEnquirySearch}
                      onChange={(e) => handleProductEnquirySearch(e.target.value)}
                      placeholder="Type product name... (e.g., Falquon, Herringbone, Kaindl)"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        fontSize: '16px',
                        borderRadius: '8px',
                        border: '2px solid #38B2AC',
                        color: '#333',
                        outline: 'none'
                      }}
                      autoFocus
                    />
                    {searchingProducts && (
                      <p style={{color: '#38B2AC', marginTop: '0.5rem', fontSize: '14px'}}>⏳ Searching...</p>
                    )}
                  </div>

                  {/* Search Results */}
                  {productEnquirySearch.length >= 2 && (
                    <div>
                      {productEnquiryResults.length === 0 ? (
                        <div style={{
                          padding: '2rem',
                          textAlign: 'center',
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          border: '1px solid #ddd'
                        }}>
                          <p style={{color: '#666', fontSize: '18px', margin: 0}}>
                            ❌ No products found for "{productEnquirySearch}"
                          </p>
                          <p style={{color: '#999', fontSize: '14px', marginTop: '0.5rem'}}>
                            Try a different search term
                          </p>
                        </div>
                      ) : (
                        <div>
                          <h4 style={{color: '#333', marginBottom: '1rem'}}>
                            📦 Found {productEnquiryResults.length} product{productEnquiryResults.length !== 1 ? 's' : ''}
                          </h4>
                          
                          {/* Product Cards */}
                          <div style={{display: 'grid', gap: '1.5rem'}}>
                            {productEnquiryResults.map((product) => {
                              // Get variations - ensure we have a clean array
                              const allVariations = Array.isArray(product.variations) ? product.variations : [];
                              
                              // Filter variations based on search term if search is specific
                              const lowerSearch = productEnquirySearch.toLowerCase();
                              let variations = allVariations;
                              
                              // If search term is more than just product name, filter variations
                              if (allVariations.length > 0 && lowerSearch.length > 0) {
                                const matchingVariations = allVariations.filter(v => 
                                  v.color?.toLowerCase().includes(lowerSearch) ||
                                  v.design?.toLowerCase().includes(lowerSearch)
                                );
                                
                                // If we found specific matching variations, show only those
                                // Otherwise show all (user might have searched by product name)
                                if (matchingVariations.length > 0 && matchingVariations.length < allVariations.length) {
                                  variations = matchingVariations;
                                  console.log(`🔍 Filtered to ${variations.length} matching variations for search "${productEnquirySearch}"`);
                                }
                              }
                              
                              // Debug logging
                              console.log(`📦 Product: ${product.name}, Showing ${variations.length}/${allVariations.length} variations`);
                              if (variations.length > 0) {
                                console.log(`   Colors: ${variations.map(v => v.color).join(', ')}`);
                              }
                              
                              // Calculate total stock across displayed variations
                              const totalStock = variations.length > 0 
                                ? variations.reduce((sum, v) => sum + (parseFloat(v.stock_quantity) || 0), 0)
                                : parseFloat(product.stock_quantity || product.quantity || 0);

                              return (
                                <div 
                                  key={product.id}
                                  style={{
                                    background: '#f8f9fa',
                                    padding: '1.5rem',
                                    borderRadius: '12px',
                                    border: '2px solid #38B2AC',
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                  }}
                                >
                                  <div style={{marginBottom: '1rem', borderBottom: '2px solid #38B2AC', paddingBottom: '1rem'}}>
                                    <h3 style={{color: '#38B2AC', margin: 0, fontSize: '24px'}}>
                                      {product.name}
                                    </h3>
                                    {product.collection && (
                                      <p style={{color: '#666', margin: '0.5rem 0 0 0', fontSize: '14px'}}>
                                        Collection: {product.collection}
                                      </p>
                                    )}
                                  </div>

                                  {/* Product Info Summary */}
                                  <div style={{marginBottom: '1.5rem', padding: '1rem', background: 'white', borderRadius: '8px'}}>
                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                      <div>
                                        <div style={{color: '#999', fontSize: '12px', marginBottom: '0.25rem'}}>TOTAL STOCK</div>
                                        <div style={{color: '#333', fontSize: '24px', fontWeight: 'bold'}}>
                                          📦 {totalStock} units
                                        </div>
                                      </div>
                                      <div style={{textAlign: 'right'}}>
                                        <div style={{color: '#999', fontSize: '12px', marginBottom: '0.25rem'}}>VARIATIONS</div>
                                        <div style={{color: '#38B2AC', fontSize: '24px', fontWeight: 'bold'}}>
                                          {variations.length} {variations.length === 1 ? 'type' : 'types'}
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Variations Table */}
                                  {variations.length > 0 ? (
                                    <div style={{marginTop: '1rem'}}>
                                      <h4 style={{color: '#333', marginBottom: '1rem', fontSize: '18px'}}>
                                        🎨 Product Variations & Pricing
                                      </h4>
                                      <div style={{overflowX: 'auto'}}>
                                        <table className="admin-table" style={{fontSize: '14px'}}>
                                          <thead>
                                            <tr>
                                              <th>Size</th>
                                              <th>Color</th>
                                              <th>Storage</th>
                                              <th>Stock</th>
                                              <th>Wholesale</th>
                                              <th>Retail</th>
                                              <th>Fitter</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {variations.map((variation, idx) => {
                                              // Determine if this is per square meter or per unit
                                              const isPerSquareMeter = product.measurement_unit === 'per_square_meter';
                                              const stockDisplay = isPerSquareMeter 
                                                ? `${parseFloat(variation.square_meters_available || variation.square_meters || 0).toFixed(2)} m²`
                                                : (variation.stock_quantity || variation.stock || 0);
                                              
                                              // Get prices with robust fallback to product prices if variation prices are missing
                                              const getPrice = (varPrice, prodPrice) => {
                                                const vPrice = parseFloat(varPrice);
                                                const pPrice = parseFloat(prodPrice);
                                                if (!isNaN(vPrice) && vPrice > 0) return vPrice;
                                                if (!isNaN(pPrice) && pPrice > 0) return pPrice;
                                                return 0;
                                              };
                                              
                                              const wholesalePrice = getPrice(variation.wholesale_price, product.wholesale_price);
                                              const retailPrice = getPrice(variation.retail_price, product.retail_price);
                                              const fitterPrice = getPrice(variation.fitter_price, product.fitter_price);
                                              
                                              // Debug price issues
                                              if (wholesalePrice === 0 && (variation.wholesale_price || product.wholesale_price)) {
                                                console.warn(`⚠️ Price issue for ${product.name} - ${variation.color}:`, {
                                                  varWholesale: variation.wholesale_price,
                                                  prodWholesale: product.wholesale_price,
                                                  calculated: wholesalePrice
                                                });
                                              }
                                              
                                              return (
                                                <tr key={idx}>
                                                  <td><strong>{variation.size || (isPerSquareMeter ? 'Per m²' : 'Standard')}</strong></td>
                                                  <td>{variation.color || product.color || 'N/A'}</td>
                                                  <td style={{textTransform: 'uppercase'}}>
                                                    {variation.storage_location || product.storage_location || 'N/A'}
                                                  </td>
                                                  <td>
                                                    <strong style={{color: '#333', fontSize: '16px'}}>
                                                      {stockDisplay}
                                                    </strong>
                                                  </td>
                                                  <td style={{color: '#007bff', fontWeight: 'bold', fontSize: '15px'}}>
                                                    £{wholesalePrice.toFixed(2)}{isPerSquareMeter ? '/m²' : ''}
                                                  </td>
                                                  <td style={{color: '#28a745', fontWeight: 'bold', fontSize: '15px'}}>
                                                    £{retailPrice.toFixed(2)}{isPerSquareMeter ? '/m²' : ''}
                                                  </td>
                                                  <td style={{color: '#ff6b6b', fontWeight: 'bold', fontSize: '15px'}}>
                                                    £{fitterPrice.toFixed(2)}{isPerSquareMeter ? '/m²' : ''}
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                          <tfoot>
                                            <tr style={{background: '#f8f9fa', fontWeight: 'bold'}}>
                                              <td colSpan="3" style={{textAlign: 'right', color: '#333'}}>TOTAL STOCK:</td>
                                              <td style={{color: '#333', fontSize: '16px'}}>
                                                {variations.reduce((sum, v) => sum + (parseFloat(v.stock_quantity) || 0), 0)}
                                              </td>
                                              <td colSpan="3"></td>
                                            </tr>
                                          </tfoot>
                                        </table>
                                      </div>
                                    </div>
                                  ) : (
                                    <div style={{marginTop: '1rem', padding: '1rem', background: 'white', borderRadius: '8px'}}>
                                      {(() => {
                                        // Apply same robust price getter for non-variation products
                                        const getPrice = (price) => {
                                          const p = parseFloat(price);
                                          return (!isNaN(p) && p > 0) ? p : 0;
                                        };
                                        
                                        const wholesalePrice = getPrice(product.wholesale_price);
                                        const retailPrice = getPrice(product.retail_price);
                                        const fitterPrice = getPrice(product.fitter_price);
                                        
                                        // Debug logging for products without variations
                                        if (wholesalePrice === 0 || retailPrice === 0) {
                                          console.warn(`⚠️ Price issue for ${product.name} (no variations):`, {
                                            wholesale: product.wholesale_price,
                                            retail: product.retail_price,
                                            fitter: product.fitter_price
                                          });
                                        }
                                        
                                        return (
                                          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', textAlign: 'center'}}>
                                            <div>
                                              <div style={{color: '#999', fontSize: '12px', marginBottom: '0.5rem'}}>WHOLESALE</div>
                                              <div style={{color: '#007bff', fontSize: '20px', fontWeight: 'bold'}}>
                                                £{wholesalePrice.toFixed(2)}
                                              </div>
                                            </div>
                                            <div>
                                              <div style={{color: '#999', fontSize: '12px', marginBottom: '0.5rem'}}>RETAIL</div>
                                              <div style={{color: '#28a745', fontSize: '20px', fontWeight: 'bold'}}>
                                                £{retailPrice.toFixed(2)}
                                              </div>
                                            </div>
                                            <div>
                                              <div style={{color: '#999', fontSize: '12px', marginBottom: '0.5rem'}}>FITTER</div>
                                              <div style={{color: '#ff6b6b', fontSize: '20px', fontWeight: 'bold'}}>
                                                £{fitterPrice.toFixed(2)}
                                              </div>
                                            </div>
                                            <div>
                                              <div style={{color: '#999', fontSize: '12px', marginBottom: '0.5rem'}}>STORAGE</div>
                                              <div style={{color: '#333', fontSize: '16px', fontWeight: 'bold', textTransform: 'uppercase'}}>
                                                {product.storage_location || 'N/A'}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Help Text */}
                  {productEnquirySearch.length === 0 && (
                    <div style={{
                      padding: '2rem',
                      textAlign: 'center',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #ddd'
                    }}>
                      <div style={{fontSize: '48px', marginBottom: '1rem'}}>🔍</div>
                      <h4 style={{color: '#333', marginBottom: '0.5rem'}}>Start typing to search</h4>
                      <p style={{color: '#666', margin: 0}}>
                        Type at least 2 characters to search for products
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* WHOLESALE APPLICATIONS SECTION */}
              {activeTab === 'wholesale-applications' && (
                <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div className="admin-section-header">
                    <div className="section-nav">
                      <button className="btn secondary" onClick={() => setActiveTab('dashboard')} style={{marginRight: '1rem'}}>← Back to Dashboard</button>
                      <h3 style={{color: '#b8860b', margin: 0}}>Wholesale Applications</h3>
                    </div>
                  </div>
                  <div className="admin-table-container">
                    <div className="table-toolbar" style={{marginBottom:'0.75rem'}}>

                    </div>
                    <h4>Applications</h4>
                    <div className="table-responsive">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Company</th>
                            <th>Contact</th>
                            <th>Email</th>
                            <th>Status</th>
                            <th>Applied</th>
                            <th style={{minWidth: '200px', width: '200px'}}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {wholesaleApplications.length > 0 ? wholesaleApplications
                            .filter(app => {
                              const q = (wholesaleSearch||'').toLowerCase();
                              return !q || [app.company_name, app.customer_name, app.company_email].some(v => (v||'').toLowerCase().includes(q));
                            })
                            .map(app => (
                            <tr key={app.id}>
                              <td>{app.company_name}</td>
                              <td>{app.customer_name}</td>
                              <td>{app.company_email}</td>
                              <td><span className={`status ${app.status}`}>{app.status}</span></td>
                              <td>{app.application_date ? new Date(app.application_date).toLocaleDateString() : 'N/A'}</td>
                              <td>
                                <button className="btn small success" style={{marginRight:'0.5rem'}} onClick={async()=>{
                                  try {
                                    await axios.put(`${API}/api/admin-portal/wholesale-applications/${app.id}`, { status: 'approved' }, { headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }});
                                    fetchWholesaleApplications();
                                    alert('Application approved');
                                  } catch (e) {
                                    alert('Failed to approve');
                                  }
                                }}>Accept</button>
                                <button className="btn small danger" onClick={async()=>{
                                  try {
                                    await axios.put(`${API}/api/admin-portal/wholesale-applications/${app.id}`, { status: 'rejected' }, { headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }});
                                    fetchWholesaleApplications();
                                    alert('Application rejected');
                                  } catch (e) {
                                    alert('Failed to reject');
                                  }
                                }}>Reject</button>
                              </td>
                            </tr>
                          )) : (
                            <tr><td colSpan="6" style={{textAlign:'center', padding:'2rem'}}>No applications found.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* CREDIT ACCOUNTS SECTION */}
              <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: activeTab === 'credit-accounts' ? 'block' : 'none'}}>
                  <div className="admin-section-header">
                    <div className="section-nav" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                      <div style={{display: 'flex', alignItems: 'center'}}>
                        <button className="btn secondary" onClick={() => setActiveTab('dashboard')} style={{marginRight: '1rem'}}>← Back to Dashboard</button>
                        <h3 style={{color: '#b8860b', margin: 0}}>Credit Accounts</h3>
                      </div>
                      <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                        {/* Download Credit Notes Button - for selected customers */}
                        <button 
                          data-testid="download-credit-notes-btn"
                          className="btn primary"
                          disabled={Object.values(selectedCreditCustomers).filter(Boolean).length === 0}
                          onClick={async () => {
                            try {
                              const selectedIds = Object.entries(selectedCreditCustomers)
                                .filter(([_, selected]) => selected)
                                .map(([id, _]) => id);
                              
                              if (selectedIds.length === 0) {
                                alert('Please select at least one customer using the checkboxes');
                                return;
                              }
                              
                              const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
                              const response = await fetch(`${API}/api/credit-accounts/credit-notes`, {
                                method: 'POST',
                                headers: { 
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({ customer_ids: selectedIds })
                              });
                              
                              if (!response.ok) {
                                const errorData = await response.json();
                                throw new Error(errorData.detail || 'Failed to generate credit notes');
                              }
                              
                              const blob = await response.blob();
                              const url = URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = `Credit_Notes_${new Date().toISOString().split('T')[0]}.pdf`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              URL.revokeObjectURL(url);
                              
                              // Clear selection after download
                              setSelectedCreditCustomers({});
                            } catch (err) {
                              console.error('Failed to generate credit notes:', err);
                              alert('Failed to generate credit notes: ' + err.message);
                            }
                          }}
                          style={{
                            background: Object.values(selectedCreditCustomers).filter(Boolean).length > 0 ? '#007bff' : '#6c757d',
                            color: 'white',
                            padding: '8px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: Object.values(selectedCreditCustomers).filter(Boolean).length > 0 ? 'pointer' : 'not-allowed'
                          }}
                        >
                          📄 Download Credit Notes ({Object.values(selectedCreditCustomers).filter(Boolean).length})
                        </button>
                        <button 
                          className="btn primary"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
                              const response = await fetch(`${API}/api/credit-accounts/export-pdf`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              
                              if (!response.ok) {
                                throw new Error('Failed to generate PDF');
                              }
                              
                              const blob = await response.blob();
                              const url = URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = `Credit_Accounts_Summary_${new Date().toISOString().split('T')[0]}.pdf`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              URL.revokeObjectURL(url);
                            } catch (err) {
                              console.error('Failed to export PDF:', err);
                              alert('Failed to export PDF: ' + err.message);
                            }
                          }}
                          style={{
                            background: '#28a745',
                            color: 'white',
                            padding: '8px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          📥 Export as PDF
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="admin-table-container">
                    <div className="table-toolbar" style={{marginBottom:'1rem', display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end'}}>
                      <PLUFilter
                        label="Filter Credit Accounts"
                        placeholder="001, 002, 003..."
                        onFilterChange={setCreditSearchInput}
                      />
                    </div>
                    {/* Credit Accounts: Grouped by Customer with Expandable Tabs */}
                    {(() => {
                      // 💰 IRON-CLAD: Use creditAccountsOrders which comes directly from /api/credit-accounts/orders
                      // This is the SINGLE SOURCE OF TRUTH for outstanding balances
                      const outstandingCreditOrders = (creditAccountsOrders||[]);
                      
                      // Apply search filter
                      const filtered = outstandingCreditOrders.filter(o => {
                        const q = (deferredCreditSearchInput||'').toLowerCase().trim();
                        if (!q) return true;
                        if (/^\d+$/.test(q)) {
                          // Match PLU numbers - handle both "PLU-0063" and "plu-0063" formats
                          const customerNumber = (o.customer_number || '').toLowerCase();
                          // Check if customer_number contains the number (e.g., "plu-0063" contains "0063")
                          // Also handle exact match like "plu-0063" === "plu-0063"
                          const allowedPLU = `plu-${q}`;
                          return customerNumber === allowedPLU || customerNumber.includes(q) || customerNumber.endsWith(`-${q}`);
                        }
                        return [o.id, o.customer_name, o.company_name].some(v => (v||'').toLowerCase().includes(q));
                      });
                      
                      // Group orders by customer_id (or composite key for guest customers)
                      // 💰 IRON-CLAD: Each guest customer (customer_id=null) must stay separate
                      const customerGroups = {};
                      filtered.forEach(order => {
                        const isGuest = !order.customer_id;
                        // For guest customers, group by customer_number (or order id as last fallback)
                        // For real customers, group by customer_id
                        const customerId = isGuest
                          ? `guest:${order.customer_number || order.id}`
                          : order.customer_id;
                        if (!customerGroups[customerId]) {
                          // Get customer info (only for real customers; guests don't exist in customers table)
                          const customer = isGuest ? null : customers.find(c => c.id === order.customer_id);
                          // 💰 Account payments lookup: for guests use customer_number, for real customers use customer_id
                          const acctKey = isGuest ? order.customer_number : order.customer_id;
                          const accountPaymentsData = (acctKey && customerAccountPayments[acctKey]) || { total: 0, payments: [] };
                          customerGroups[customerId] = {
                            customerId,
                            isGuest,
                            realCustomerId: isGuest ? null : order.customer_id,
                            customerNumber: order.customer_number || 'N/A',
                            customerName: customer?.name || order.customer_name || 'Guest',
                            companyName: customer?.company_name || order.company_name || '',
                            orders: [],
                            totalOrderOutstanding: 0, // Sum of order-level outstanding
                            accountPaymentsTotal: accountPaymentsData.total, // Lump sum payments not applied to specific orders
                            accountPayments: accountPaymentsData.payments || [],
                            totalOutstanding: 0, // Effective outstanding = orders - account payments
                            latestDueDate: null,
                            worstStatus: 'safe' // safe, warning, urgent, overdue
                          };
                        }
                        
                        // 💰 Use outstanding_balance from API if available, otherwise calculate
                        // Use explicit null check (not ||) since outstanding_balance could be 0
                        const outstandingBalance = (order.outstanding_balance != null && order.outstanding_balance !== undefined) 
                          ? order.outstanding_balance 
                          : calculateOrderOutstanding(order);
                        
                        console.log(`📊 Order ${order.id?.substring(0,8)}: GT=${order.grand_total}, Cash=${order.cash_paid_amount}, Bank=${order.bank_paid_amount}, Credit=${order.credit_paid_amount}, Outstanding=${outstandingBalance}`);
                        
                        customerGroups[customerId].orders.push(order);
                        customerGroups[customerId].totalOrderOutstanding += outstandingBalance;
                        
                        // Determine worst status based on due dates
                        // 🔴 SKIP overdue calculation if bill is marked as paid
                        const paymentStatus = (order.payment_status || '').toLowerCase().trim();
                        const isFullyPaid = paymentStatus === 'paid' || paymentStatus === 'fully_paid';
                        
                        if (order.credit_due_date && !isFullyPaid) {
                          const daysRemaining = calculateDaysRemaining(order.credit_due_date);
                          if (daysRemaining !== null) {
                            const currentWorst = customerGroups[customerId].worstStatus;
                            if (daysRemaining < 0) {
                              customerGroups[customerId].worstStatus = 'overdue';
                            } else if (daysRemaining === 0 && currentWorst !== 'overdue') {
                              customerGroups[customerId].worstStatus = 'urgent';
                            } else if (daysRemaining <= 7 && currentWorst !== 'overdue' && currentWorst !== 'urgent') {
                              customerGroups[customerId].worstStatus = 'warning';
                            }
                            // Track latest due date for display
                            if (!customerGroups[customerId].latestDueDate || new Date(order.credit_due_date) < new Date(customerGroups[customerId].latestDueDate)) {
                              customerGroups[customerId].latestDueDate = order.credit_due_date;
                            }
                          }
                        }
                      });
                      
                      // 💰 NET BALANCE RULE: A customer only appears if their NET balance > £0.
                      // Net = sum of per-bill outstanding − lump-sum account payments.
                      // If account payments fully cover (or exceed) outstanding, the customer
                      // is settled and should NOT show in Credit Accounts.
                      Object.values(customerGroups).forEach(group => {
                        group.totalOutstanding = Math.max(0, group.totalOrderOutstanding - group.accountPaymentsTotal);
                        group.hasAnyOutstandingBill = group.orders.some(o => parseFloat(o.outstanding_balance || 0) > 0);
                      });
                      
                      // Filter: show only customers whose net balance exceeds £0.005 (sub-cent tolerance)
                      const customerList = Object.values(customerGroups)
                        .filter(g => g.totalOutstanding > 0.005)
                        .sort((a, b) => {
                          // Sort by status priority: overdue > urgent > warning > safe
                          const statusPriority = { overdue: 0, urgent: 1, warning: 2, safe: 3 };
                          return statusPriority[a.worstStatus] - statusPriority[b.worstStatus];
                        });
                      
                      const totalOutstanding = customerList.reduce((t, g) => t + g.totalOutstanding, 0);
                      
                      // Helper function to get status badge
                      const getStatusBadge = (status, latestDueDate) => {
                        if (status === 'overdue') {
                          const days = latestDueDate ? Math.abs(calculateDaysRemaining(latestDueDate)) : 0;
                          return <span style={{background: '#dc3545', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold', animation: 'pulse 1s infinite'}}>🔴 OVERDUE {days > 0 ? `(${days} days)` : ''}</span>;
                        } else if (status === 'urgent') {
                          return <span style={{background: '#fd7e14', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold'}}>🔔 DUE TODAY</span>;
                        } else if (status === 'warning') {
                          const days = latestDueDate ? calculateDaysRemaining(latestDueDate) : 0;
                          return <span style={{background: '#ffc107', color: '#000', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold'}}>⚠️ Due: {days} days</span>;
                        }
                        return <span style={{background: '#28a745', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '11px'}}>✅ On Track</span>;
                      };
                      
                      return (
                        <>
                          <div style={{marginBottom:'1rem', padding: '10px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <strong style={{color: '#000'}}>Outstanding Credit: £{totalOutstanding.toFixed(2)} | Customers: {customerList.length} | Total Bills: {outstandingCreditOrders.length}</strong>
                            {/* Select All / Deselect All buttons */}
                            <div style={{display: 'flex', gap: '8px'}}>
                              <button 
                                className="btn btn-sm"
                                onClick={() => {
                                  const allSelected = {};
                                  customerList.forEach(g => allSelected[g.customerId] = true);
                                  setSelectedCreditCustomers(allSelected);
                                }}
                                style={{fontSize: '11px', padding: '4px 8px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px'}}
                              >
                                Select All
                              </button>
                              <button 
                                className="btn btn-sm"
                                onClick={() => setSelectedCreditCustomers({})}
                                style={{fontSize: '11px', padding: '4px 8px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px'}}
                              >
                                Deselect All
                              </button>
                              
                              {/* Fix All Balances - MANAGERS ONLY */}
                              {isManagerAccount() && (
                                <button 
                                  data-testid="fix-issues-btn"
                                  className="btn btn-sm"
                                  onClick={async () => {
                                    setFixIssuesLoading(true);
                                    setShowFixIssuesModal(true);
                                    try {
                                      // Try admin_token first, then dashboard_token
                                      let token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
                                      if (!token) {
                                        throw new Error('No authentication token found. Please log in again.');
                                      }
                                      const response = await axios.get(`${API}/api/credit-accounts/audit-issues`, {
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      });
                                      setFixIssuesData(response.data);
                                      setSelectedIssuesToFix({});
                                    } catch (error) {
                                      alert('❌ Error loading issues: ' + (error.response?.data?.detail || error.message));
                                      setShowFixIssuesModal(false);
                                    } finally {
                                      setFixIssuesLoading(false);
                                    }
                                  }}
                                  style={{fontSize: '11px', padding: '4px 12px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', fontWeight: '700'}}
                                  title="Scan and fix ALL data issues - ZOBAYR ONLY"
                                >
                                  🔧 FIX ISSUES
                                </button>
                              )}
                              {/* 🚨 EMERGENCY REVERSAL BUTTON - zobayr only - HIDDEN */}
                            </div>
                          </div>
                          {loadingFulfilledOrders ? (
                            <div className="loading-message">Loading credit accounts...</div>
                          ) : customerList.length > 0 ? (
                            <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                              {customerList.map((group) => {
                                const isExpanded = expandedCreditCustomers[group.customerId];
                                const isSelected = selectedCreditCustomers[group.customerId] || false;
                                return (
                                  <div key={group.customerId} style={{border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden'}}>
                                    {/* Customer Tab Header - Clickable */}
                                    <div 
                                      data-testid={`credit-customer-tab-${group.customerNumber}`}
                                      onClick={async () => {
                                        const wasExpanded = expandedCreditCustomers[group.customerId];
                                        setExpandedCreditCustomers(prev => ({...prev, [group.customerId]: !prev[group.customerId]}));
                                        
                                        // Fetch account payments when expanding (if not already loaded)
                                        // 💰 Guest customers also supported: use customer_number as the lookup key
                                        const acctLookupId = group.isGuest ? group.customerNumber : group.realCustomerId;
                                        const acctCacheKey = group.isGuest ? group.customerNumber : group.customerId;
                                        if (!wasExpanded && acctLookupId && !customerAccountPayments[acctCacheKey]) {
                                          try {
                                            const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
                                            const response = await axios.get(`${API}/api/credit-accounts/${acctLookupId}/account-payments`, {
                                              headers: { 'Authorization': `Bearer ${token}` }
                                            });
                                            if (response.data) {
                                              setCustomerAccountPayments(prev => ({
                                                ...prev,
                                                [acctCacheKey]: {
                                                  total: response.data.total_account_payments || 0,
                                                  payments: response.data.payments || []
                                                }
                                              }));
                                            }
                                          } catch (err) {
                                            console.error('Failed to fetch account payments:', err);
                                          }
                                        }
                                      }}
                                      style={{
                                        padding: '12px 16px',
                                        background: group.worstStatus === 'overdue' ? '#fff5f5' : group.worstStatus === 'urgent' ? '#fff8e6' : group.worstStatus === 'warning' ? '#fffce6' : '#f8f9fa',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        borderLeft: `4px solid ${group.worstStatus === 'overdue' ? '#dc3545' : group.worstStatus === 'urgent' ? '#fd7e14' : group.worstStatus === 'warning' ? '#ffc107' : '#28a745'}`,
                                        transition: 'background 0.2s'
                                      }}
                                    >
                                      <div style={{display: 'flex', alignItems: 'center', gap: '12px', flex: 1}}>
                                        {/* Checkbox for credit note selection */}
                                        <input
                                          type="checkbox"
                                          data-testid={`credit-checkbox-${group.customerId}`}
                                          checked={isSelected}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            setSelectedCreditCustomers(prev => ({
                                              ...prev,
                                              [group.customerId]: e.target.checked
                                            }));
                                          }}
                                          style={{
                                            width: '18px',
                                            height: '18px',
                                            cursor: 'pointer',
                                            accentColor: '#007bff'
                                          }}
                                        />
                                        <span style={{fontSize: '18px', fontWeight: 'bold', color: '#007bff'}}>{group.customerNumber}</span>
                                        <div style={{display: 'flex', flexDirection: 'column'}}>
                                          <span style={{fontWeight: '600', fontSize: '15px', color: '#333'}}>{group.customerName}</span>
                                          {group.companyName && <span style={{fontSize: '12px', color: '#666'}}>{group.companyName}</span>}
                                        </div>
                                      </div>
                                      <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                                        <div style={{textAlign: 'right'}}>
                                          <div style={{fontWeight: 'bold', fontSize: '16px', color: '#dc3545'}}>£{group.totalOutstanding.toFixed(2)}</div>
                                          <div style={{fontSize: '11px', color: '#666'}}>{group.orders.length} bill{group.orders.length > 1 ? 's' : ''}</div>
                                        </div>
                                        {getStatusBadge(group.worstStatus, group.latestDueDate)}
                                        {/* 🔒 OWNER-ONLY: Delete this customer from Credit Accounts (visual only) */}
                                        {loggedInAdminUsername.toLowerCase() === 'zobayr' && (
                                          <button
                                            data-testid={`delete-credit-customer-${group.customerNumber}`}
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              const confirmMsg = `Remove ${group.customerName} (${group.customerNumber}) from Credit Accounts?\n\n` +
                                                `This is VISUAL ONLY — it marks all ${group.orders.length} outstanding bill(s) as paid so they disappear from this list. ` +
                                                `Order records, sales totals and history are NOT changed. This can be reversed by unmarking each bill.`;
                                              if (!window.confirm(confirmMsg)) return;
                                              try {
                                                const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
                                                const deleteId = group.isGuest ? group.customerNumber : group.realCustomerId;
                                                const res = await axios.delete(`${API}/api/credit-accounts/customer/${deleteId}`, {
                                                  headers: { 'Authorization': `Bearer ${token}` }
                                                });
                                                if (res.data?.success) {
                                                  alert(`✅ ${res.data.message}`);
                                                  await refreshAllData();
                                                }
                                              } catch (err) {
                                                alert('❌ Failed to remove customer: ' + (err.response?.data?.detail || err.message));
                                              }
                                            }}
                                            title="Remove this customer from Credit Accounts (Owner only)"
                                            style={{
                                              background: '#dc3545',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '6px',
                                              padding: '6px 10px',
                                              fontSize: '14px',
                                              fontWeight: '700',
                                              cursor: 'pointer',
                                              lineHeight: 1,
                                            }}
                                          >
                                            🗑️
                                          </button>
                                        )}
                                        <span style={{fontSize: '20px', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}}>▼</span>
                                      </div>
                                    </div>
                                    
                                    {/* Expanded Orders Table */}
                                    {isExpanded && (
                                      <div style={{padding: '16px', background: '#fff', borderTop: '1px solid #dee2e6'}}>
                                        {/* Lump Sum Payment Bar */}
                                        <div style={{
                                          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                          border: '2px solid #28a745',
                                          borderRadius: '8px',
                                          padding: '12px 16px',
                                          marginBottom: '16px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          gap: '12px',
                                          flexWrap: 'wrap'
                                        }}>
                                          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                            <span style={{fontSize: '20px'}}>💰</span>
                                            <div>
                                              <div style={{fontWeight: '600', color: '#333', fontSize: '14px'}}>Record Lump Sum Payment</div>
                                              <div style={{fontSize: '12px', color: '#666'}}>Instantly deduct from balance without micro-transactions</div>
                                            </div>
                                          </div>
                                          <button
                                            data-testid={`lump-sum-btn-${group.customerId}`}
                                            className="btn btn-primary"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              // SPECTATOR CHECK: Block modal from opening
                                              if (isSpectateOnly('credit-accounts')) {
                                                alert('🔒 Spectate Mode: You cannot record payments. Contact a manager.');
                                                return;
                                              }
                                              // 💰 GUEST SUPPORT: Use customer_number as the synthetic ID for guests
                                              const lumpSumId = group.isGuest ? group.customerNumber : group.realCustomerId;
                                              setLumpSumCustomer({
                                                id: lumpSumId,
                                                name: group.customerName,
                                                company: group.companyName,
                                                customerNumber: group.customerNumber,
                                                totalOutstanding: group.totalOutstanding,
                                                isGuest: group.isGuest
                                              });
                                              setLumpSumAmount("");
                                              setLumpSumMethod("cash");
                                              setLumpSumNotes("");
                                              setLumpSumPaymentDate("");
                                              setShowLumpSumModal(true);
                                            }}
                                            disabled={isSpectateOnly('credit-accounts')}
                                            style={{
                                              background: isSpectateOnly('credit-accounts') ? '#9ca3af' : '#28a745',
                                              color: 'white',
                                              padding: '10px 20px',
                                              fontWeight: '600',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '6px',
                                              border: 'none',
                                              borderRadius: '6px',
                                              cursor: isSpectateOnly('credit-accounts') ? 'not-allowed' : 'pointer',
                                              opacity: isSpectateOnly('credit-accounts') ? 0.6 : 1
                                            }}
                                            title={isSpectateOnly('credit-accounts') ? 'Spectate mode - cannot record payments. Report to higher ups.' : ''}
                                          >
                                            {isSpectateOnly('credit-accounts') ? '🔒 Record Payment' : '💵 Record Payment'}
                                          </button>
                                        </div>
                                        
                                        {/* Download History Section with Date Filter */}
                                        <div style={{
                                          background: '#f8f9fa',
                                          border: '1px solid #dee2e6',
                                          borderRadius: '8px',
                                          padding: '12px',
                                          marginBottom: '12px'
                                        }}>
                                          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px'}}>
                                            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                              <span style={{fontSize: '16px'}}>📥</span>
                                              <strong style={{fontSize: '14px', color: '#000'}}>Download Statement</strong>
                                            </div>
                                            
                                            {/* Date Range Inputs for Download */}
                                            <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                                              <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                                                <label style={{fontSize: '12px', color: '#000', fontWeight: '500'}}>From:</label>
                                                <input
                                                  type="date"
                                                  data-testid={`download-date-start-${group.customerId}`}
                                                  value={creditDateFilterStart}
                                                  onChange={(e) => setCreditDateFilterStart(e.target.value)}
                                                  onClick={(e) => e.stopPropagation()}
                                                  style={{padding: '4px 8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '12px', color: '#000', background: '#fff'}}
                                                />
                                              </div>
                                              <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                                                <label style={{fontSize: '12px', color: '#000', fontWeight: '500'}}>To:</label>
                                                <input
                                                  type="date"
                                                  data-testid={`download-date-end-${group.customerId}`}
                                                  value={creditDateFilterEnd}
                                                  onChange={(e) => setCreditDateFilterEnd(e.target.value)}
                                                  onClick={(e) => e.stopPropagation()}
                                                  style={{padding: '4px 8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '12px', color: '#000', background: '#fff'}}
                                                />
                                              </div>
                                              {(creditDateFilterStart || creditDateFilterEnd) && (
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCreditDateFilterStart("");
                                                    setCreditDateFilterEnd("");
                                                  }}
                                                  style={{padding: '4px 8px', background: '#e9ecef', color: '#000', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: '500'}}
                                                >
                                                  Clear
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                          
                                          {/* Download Buttons */}
                                          <div style={{display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap'}}>
                                            {/* Download Full History - Always downloads EVERYTHING */}
                                            <button 
                                              className="btn btn-sm" 
                                              onClick={async (e) => {
                                                e.stopPropagation();
                                                try {
                                                  const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
                                                  // NO date filter - always get full history
                                                  const url = `${API}/api/credit-accounts/statement/${group.customerId}?full_history=true`;
                                                  
                                                  const response = await fetch(url, {
                                                    headers: { 'Authorization': `Bearer ${token}` }
                                                  });
                                                  if (!response.ok) throw new Error('Failed to download statement');
                                                  const blob = await response.blob();
                                                  const urlObj = URL.createObjectURL(blob);
                                                  const link = document.createElement('a');
                                                  link.href = urlObj;
                                                  link.download = `Credit_Statement_FULL_${group.customerName}_${new Date().toISOString().split('T')[0]}.pdf`;
                                                  document.body.appendChild(link);
                                                  link.click();
                                                  setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(urlObj); }, 100);
                                                } catch (err) {
                                                  alert('Failed to download statement: ' + err.message);
                                                }
                                              }}
                                              style={{fontSize: '12px', padding: '8px 16px', background: '#28a745', color: '#FFFFFF', border: 'none', fontWeight: '600', borderRadius: '4px'}}
                                            >
                                              📄 Download Full History
                                            </button>
                                            
                                            {/* Download Filtered - Uses date inputs */}
                                            {(creditDateFilterStart || creditDateFilterEnd) && (
                                              <button 
                                                className="btn btn-sm" 
                                                onClick={async (e) => {
                                                  e.stopPropagation();
                                                  try {
                                                    const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
                                                    if (group.isGuest) {
                                                      alert('Statement download is not available for guest customers.');
                                                      return;
                                                    }
                                                    let url = `${API}/api/credit-accounts/statement/${group.realCustomerId}?full_history=true`;
                                                    if (creditDateFilterStart) url += `&start_date=${creditDateFilterStart}`;
                                                    if (creditDateFilterEnd) url += `&end_date=${creditDateFilterEnd}`;
                                                    
                                                    const response = await fetch(url, {
                                                      headers: { 'Authorization': `Bearer ${token}` }
                                                    });
                                                    if (!response.ok) throw new Error('Failed to download statement');
                                                    const blob = await response.blob();
                                                    const urlObj = URL.createObjectURL(blob);
                                                    const link = document.createElement('a');
                                                    link.href = urlObj;
                                                    link.download = `Credit_Statement_${group.customerName}_${creditDateFilterStart || 'start'}_to_${creditDateFilterEnd || 'now'}.pdf`;
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(urlObj); }, 100);
                                                  } catch (err) {
                                                    alert('Failed to download statement: ' + err.message);
                                                  }
                                                }}
                                                style={{fontSize: '12px', padding: '8px 16px', background: '#007bff', color: '#FFFFFF', border: 'none', fontWeight: '600', borderRadius: '4px'}}
                                              >
                                                📅 Download: {creditDateFilterStart || '...'} to {creditDateFilterEnd || 'now'}
                                              </button>
                                            )}
                                            
                                            {/* Outstanding Only */}
                                            <button 
                                              className="btn btn-sm" 
                                              onClick={async (e) => {
                                                e.stopPropagation();
                                                try {
                                                  const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
                                                  if (group.isGuest) {
                                                    alert('Statement download is not available for guest customers.');
                                                    return;
                                                  }
                                                  const response = await fetch(`${API}/api/credit-accounts/statement/${group.realCustomerId}`, {
                                                    headers: { 'Authorization': `Bearer ${token}` }
                                                  });
                                                  if (!response.ok) throw new Error('Failed to download statement');
                                                  const blob = await response.blob();
                                                  const urlObj = URL.createObjectURL(blob);
                                                  const link = document.createElement('a');
                                                  link.href = urlObj;
                                                  link.download = `Credit_Statement_Outstanding_${group.customerName}_${new Date().toISOString().split('T')[0]}.pdf`;
                                                  document.body.appendChild(link);
                                                  link.click();
                                                  setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(urlObj); }, 100);
                                                } catch (err) {
                                                  alert('Failed to download statement: ' + err.message);
                                                }
                                              }}
                                              style={{fontSize: '12px', padding: '8px 16px', background: '#6c757d', color: '#FFFFFF', border: 'none', fontWeight: '600', borderRadius: '4px'}}
                                            >
                                              📋 Outstanding Only
                                            </button>
                                          </div>
                                        </div>
                                        <table className="admin-table" style={{marginBottom: 0}}>
                                          <thead>
                                            <tr style={{background: '#f8f9fa'}}>
                                              <th style={{fontSize: '12px'}}>Bill Total</th>
                                              <th style={{fontSize: '12px'}}>Paid</th>
                                              <th style={{fontSize: '12px'}}>Balance</th>
                                              <th style={{fontSize: '12px'}}>Payment Due</th>
                                              <th style={{fontSize: '12px'}}>Date</th>
                                              <th style={{fontSize: '12px'}}>Action</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {group.orders.map((o, idx) => {
                                              // 💰 IRON-CLAD: Use centralized calculation
                                              const grandTotal = parseFloat(o.grand_total || 0);
                                              const cashPaid = parseFloat(o.cash_paid_amount || 0);
                                              const bankPaid = parseFloat(o.bank_paid_amount || 0);
                                              const creditPaid = parseFloat(o.credit_paid_amount || 0);
                                              const totalPaid = cashPaid + bankPaid + creditPaid;
                                              const outstandingBalance = calculateOrderOutstanding(o);
                                              
                                              return (
                                                <tr key={o?.id || `bill-${idx}`} style={{background: o.has_calculation_warning ? '#fff3cd' : 'inherit'}}>
                                                  <td style={{fontWeight: '500'}}>
                                                    {/* ⚠️ Warning icon for calculation mismatch */}
                                                    {o.has_calculation_warning && (
                                                      <span 
                                                        title="Calculation mismatch - click to edit this order"
                                                        style={{
                                                          display: 'inline-block',
                                                          width: '20px',
                                                          height: '20px',
                                                          background: '#dc3545',
                                                          color: 'white',
                                                          borderRadius: '4px',
                                                          textAlign: 'center',
                                                          lineHeight: '20px',
                                                          fontSize: '14px',
                                                          marginRight: '6px',
                                                          cursor: 'pointer'
                                                        }}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleEditOrder(o.id);
                                                        }}
                                                      >!</span>
                                                    )}
                                                    £{grandTotal.toFixed(2)}
                                                  </td>
                                                  <td>
                                                    <div style={{display:'flex', flexDirection:'column', gap:'2px', fontSize: '12px'}}>
                                                      {cashPaid > 0 && <span style={{color:'#28a745'}}>💵 £{cashPaid.toFixed(2)}</span>}
                                                      {bankPaid > 0 && <span style={{color:'#007bff'}}>🏦 £{bankPaid.toFixed(2)}</span>}
                                                      {creditPaid > 0 && <span style={{color:'#ffc107'}}>💳 £{creditPaid.toFixed(2)}</span>}
                                                      <strong>Total: £{totalPaid.toFixed(2)}</strong>
                                                    </div>
                                                  </td>
                                                  <td style={{color: '#dc3545', fontWeight: 'bold'}}>£{outstandingBalance.toFixed(2)}</td>
                                                  <td>
                                                    {/* Show PAID status instead of overdue for paid bills */}
                                                    {(o.payment_status || '').toLowerCase() === 'fully_paid' ? (
                                                      <span style={{color: '#28a745', fontWeight: 'bold'}}>✅ PAID</span>
                                                    ) : o.credit_due_date ? (() => {
                                                      const daysRemaining = calculateDaysRemaining(o.credit_due_date);
                                                      if (daysRemaining === null) return <span>-</span>;
                                                      if (daysRemaining > 7) return <span style={{color: '#28a745'}}>✅ {daysRemaining} days</span>;
                                                      if (daysRemaining > 0) return <span style={{color: '#ffc107', fontWeight: 'bold'}}>⚠️ {daysRemaining} days</span>;
                                                      if (daysRemaining === 0) return <span style={{color: '#fd7e14', fontWeight: 'bold'}}>🔔 TODAY</span>;
                                                      return <span style={{color: '#dc3545', fontWeight: 'bold'}}>🔴 {Math.abs(daysRemaining)} days overdue</span>;
                                                    })() : (
                                                      <div style={{display: 'flex', gap: '4px', alignItems: 'center'}}>
                                                        <input id={`credit-days-input-${o.id}`} type="number" placeholder="30" style={{width: '45px', fontSize: '11px', padding: '2px 4px'}} defaultValue="30" min="1" onClick={(e) => e.stopPropagation()} />
                                                        <button 
                                                          className="btn btn-sm btn-primary"
                                                          onClick={async (e) => {
                                                            e.stopPropagation();
                                                            const input = document.getElementById(`credit-days-input-${o.id}`);
                                                            const days = parseInt(input?.value) || 30;
                                                            if (days < 1) { alert('Please enter at least 1 day'); return; }
                                                            try {
                                                              await axios.put(`${API}/api/orders/${o.id}/credit-terms`, { credit_days: days }, {
                                                                headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
                                                              });
                                                              await loadFulfilledOrders();
                                                              alert(`✅ Payment due in ${days} days`);
                                                            } catch (err) {
                                                              alert('Failed: ' + (err.response?.data?.detail || err.message));
                                                            }
                                                          }}
                                                          style={{fontSize: '10px', padding: '2px 6px'}}
                                                        >Set</button>
                                                      </div>
                                                    )}
                                                  </td>
                                                  <td style={{fontSize: '12px'}}>{o?.date ? new Date(o.date).toLocaleDateString() : 'N/A'}</td>
                                                  <td>
                                                    <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          // Use the order data we already have
                                                          setSelectedOrder(o);
                                                          setShowOrderViewModal(true);
                                                        }}
                                                        style={{
                                                          padding: '6px 12px',
                                                          fontSize: '11px',
                                                          background: '#3b82f6',
                                                          color: 'white',
                                                          border: 'none',
                                                          borderRadius: '4px',
                                                          cursor: 'pointer',
                                                          fontWeight: '600'
                                                        }}
                                                      >
                                                        VIEW
                                                      </button>
                                                      {(o.payment_status || '').toLowerCase() === 'fully_paid' ? (
                                                        <span style={{
                                                          padding: '6px 12px',
                                                          fontSize: '11px',
                                                          background: '#6b7280',
                                                          color: 'white',
                                                          borderRadius: '4px',
                                                          fontWeight: '600'
                                                        }}>
                                                          PAID
                                                        </span>
                                                      ) : (
                                                        <button
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!window.confirm('Mark this bill as PAID?')) return;
                                                            
                                                            // ⚡ INSTANT optimistic UI update
                                                            const optimisticUpdate = (list, setter) => {
                                                              if (!list || !setter) return;
                                                              setter(list.map(x => x.id === o.id ? {...x, payment_status: 'fully_paid'} : x));
                                                            };
                                                            optimisticUpdate(fulfilledOrders, setFulfilledOrders);
                                                            optimisticUpdate(orders, setOrders);
                                                            optimisticUpdate(creditAccountsOrders, setCreditAccountsOrders);
                                                            
                                                            // Background API call
                                                            const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token') || localStorage.getItem('token');
                                                            fetch(`${API}/api/orders/${o.id}/mark-bill-paid`, {
                                                              method: 'POST',
                                                              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
                                                            })
                                                              .then(r => r.json())
                                                              .then(data => {
                                                                if (!data.success) console.warn('Mark paid backend response:', data);
                                                              })
                                                              .catch(err => console.warn('Mark paid network issue (UI already updated):', err?.message));
                                                          }}
                                                          style={{
                                                            padding: '6px 12px',
                                                            fontSize: '11px',
                                                            background: '#10b981',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontWeight: '600'
                                                          }}
                                                        >
                                                          PAID
                                                        </button>
                                                      )}
                                                    </div>
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                        
                                        {/* Account Payments Section - Lump sum payments NOT applied to specific bills */}
                                        {group.accountPayments && group.accountPayments.length > 0 && (
                                          <div style={{
                                            marginTop: '16px',
                                            padding: '12px',
                                            background: '#e8f5e9',
                                            borderRadius: '8px',
                                            border: '2px solid #28a745'
                                          }}>
                                            <h5 style={{margin: '0 0 12px 0', color: '#155724', display: 'flex', alignItems: 'center', gap: '8px'}}>
                                              💰 Account Payments (Not applied to specific bills)
                                              <span style={{
                                                background: '#28a745',
                                                color: 'white',
                                                padding: '2px 8px',
                                                borderRadius: '12px',
                                                fontSize: '12px'
                                              }}>
                                                Total: £{group.accountPaymentsTotal.toFixed(2)}
                                              </span>
                                            </h5>
                                            <table style={{width: '100%', fontSize: '13px', borderCollapse: 'collapse'}}>
                                              <thead>
                                                <tr style={{background: '#c8e6c9'}}>
                                                  <th style={{padding: '8px', textAlign: 'left'}}>Date</th>
                                                  <th style={{padding: '8px', textAlign: 'left'}}>Method</th>
                                                  <th style={{padding: '8px', textAlign: 'right'}}>Amount</th>
                                                  <th style={{padding: '8px', textAlign: 'left'}}>Notes</th>
                                                  <th style={{padding: '8px', textAlign: 'center'}}>Actions</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {group.accountPayments.map((payment, pIdx) => (
                                                  <tr key={payment.id || pIdx} style={{borderBottom: '1px solid #c8e6c9'}}>
                                                    <td style={{padding: '8px'}}>
                                                      {payment.created_at ? new Date(payment.created_at).toLocaleDateString() + ' ' + new Date(payment.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'N/A'}
                                                    </td>
                                                    <td style={{padding: '8px'}}>
                                                      {payment.payment_method === 'CASH' ? '💵 Cash' : '🏦 Bank'}
                                                    </td>
                                                    <td style={{padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#28a745'}}>
                                                      £{parseFloat(payment.amount || 0).toFixed(2)}
                                                    </td>
                                                    <td style={{padding: '8px', color: '#666', fontSize: '12px'}}>
                                                      {payment.notes || '-'}
                                                    </td>
                                                    <td style={{padding: '8px', textAlign: 'center'}}>
                                                      {isManagerAccount() && (
                                                        <button
                                                          data-testid={`delete-account-payment-${payment.id}`}
                                                          onClick={() => handleDeleteAccountPayment(payment.id, group.customerName, payment.amount)}
                                                          style={{
                                                            background: '#dc3545',
                                                            color: 'white',
                                                            border: 'none',
                                                            padding: '3px 8px',
                                                            borderRadius: '4px',
                                                            fontSize: '11px',
                                                            cursor: 'pointer',
                                                            fontWeight: '600'
                                                          }}
                                                        >
                                                          Delete
                                                        </button>
                                                      )}
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        )}
                                        
                                        {/* Summary showing effective balance */}
                                        {group.accountPaymentsTotal > 0 && (
                                          <div style={{
                                            marginTop: '12px',
                                            padding: '10px 12px',
                                            background: '#f8f9fa',
                                            borderRadius: '6px',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                          }}>
                                            <div style={{display: 'flex', gap: '20px', fontSize: '13px'}}>
                                              <span>Bills Total: <strong>£{group.totalOrderOutstanding.toFixed(2)}</strong></span>
                                              <span style={{color: '#28a745'}}>Account Payments: <strong>-£{group.accountPaymentsTotal.toFixed(2)}</strong></span>
                                            </div>
                                            <div style={{fontWeight: 'bold', fontSize: '14px', color: '#dc3545'}}>
                                              Net Outstanding: £{group.totalOutstanding.toFixed(2)}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div style={{textAlign: 'center', padding: '40px', color: '#6c757d'}}>
                              <h4>No Outstanding Credit Accounts</h4>
                              <p>All credit orders have been fully paid. Fully paid credit orders can be found in the Fulfilled Orders section.</p>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

              {/* QUERIES SECTION */}
              {activeTab === 'queries' && (
                <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div className="admin-section-header">
                    <div className="section-nav">
                      <button 
                        className="btn secondary"
                        onClick={() => setActiveTab('dashboard')}
                        style={{marginRight: '1rem'}}
                      >
                        ← Back to Dashboard
                      </button>
                      <h3 style={{color: '#E53E3E', margin: 0}}>Customer Queries & Contact Forms</h3>
                    </div>
                  </div>

                  <h4>Contact Form Submissions</h4>
                  {loadingQueries ? (
                    <div className="loading-message">Loading queries...</div>
                  ) : queries && queries.length > 0 ? (
                    <div className="table-responsive">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Subject</th>
                            <th>Status</th>
                            <th style={{minWidth: '200px', width: '200px'}}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {queries.map((query, index) => (
                            <tr key={query?.id || `query-${index}`} className={query.status === 'unread' ? 'unread-query' : ''}>
                              <td>{query?.created_at ? new Date(query.created_at).toLocaleDateString() : 'N/A'}</td>
                              <td style={{cursor: 'pointer', color: 'var(--himalaya-navy)', fontWeight: '500', textDecoration: 'underline'}} onClick={() => handleViewQueryDetails(query)}>
                                {query?.name || 'N/A'}
                              </td>
                              <td>{query?.email || 'N/A'}</td>
                              <td>{query?.phone || '-'}</td>
                              <td>{query?.subject || 'General Enquiry'}</td>
                              <td>
                                <span className={`status ${query.status || 'unread'}`}>
                                  {query.status === 'read' ? 'Read' : 'Unread'}
                                </span>
                              </td>
                              <td style={{minWidth: '320px', whiteSpace: 'nowrap'}}>
                                <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-start'}}>
                                  <button 
                                    className="btn small secondary" 
                                    onClick={() => handleViewQueryDetails(query)}
                                    style={{fontSize: '11px', padding: '3px 6px'}}
                                  >
                                    📋 View
                                  </button>
                                  {query.status !== 'read' && (
                                    <button 
                                      className="btn small primary"
                                      onClick={() => handleMarkQueryRead(query.id)}
                                      style={{fontSize: '11px', padding: '3px 6px'}}
                                    >
                                      ✓ Read
                                    </button>
                                  )}
                                  <button 
                                    className="btn small danger" 
                                    onClick={() => handleDeleteQuery(query.id, query.name)}
                                    title="Delete Query"
                                    style={{fontSize: '11px', padding: '3px 6px'}}
                                  >
                                    🗑️ Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="no-queries-message">
                      <p>No customer queries found.</p>
                    </div>
                  )}
                </div>
              )}

              {/* STORE LOCATIONS SECTION */}
              {activeTab === 'store-locations' && (
                <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div className="admin-section-header">
                    <div className="section-nav">
                      <button 
                        className="btn secondary"
                        onClick={() => setActiveTab('dashboard')}
                        style={{marginRight: '1rem'}}
                      >
                        ← Back to Dashboard
                      </button>
                      <h3 style={{color: '#805AD5', margin: 0}}>Store Locations Management</h3>
                    </div>
                    <button 
                      className="btn primary" 
                      onClick={() => setShowAddLocationModal(true)}
                      style={{marginLeft: 'auto'}}
                    >
                      + Add Location
                    </button>
                  </div>

                  <h4>Store Locations (shown in website footer)</h4>
                  {loadingStoreLocations ? (
                    <div className="loading-message">Loading store locations...</div>
                  ) : storeLocations && storeLocations.length > 0 ? (
                    <div className="table-responsive">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Address</th>
                            <th>Postcode</th>
                            <th>Phone</th>
                            <th style={{minWidth: '200px', width: '200px'}}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {storeLocations.map((location, index) => (
                            <tr key={location?.id || `location-${index}`}>
                              <td>{location?.name || 'N/A'}</td>
                              <td style={{whiteSpace: 'pre-line'}}>{location?.address || 'N/A'}</td>
                              <td>{location?.postcode || 'N/A'}</td>
                              <td>{location?.phone || 'N/A'}</td>
                              <td>
                                <div style={{display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'flex-start'}}>
                                  <button 
                                    className="btn small primary" 
                                    style={{width: '160px', fontSize: '11px', padding: '3px 6px'}} 
                                    onClick={() => setEditingLocation(location)}
                                  >
                                    Edit
                                  </button>
                                  <button 
                                    className="btn small danger"
                                    style={{width: '160px', fontSize: '11px', padding: '3px 6px'}} 
                                    onClick={() => handleDeleteLocation(location.id)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="no-locations-message">
                      <p>No store locations found. Add your first location to display it on the website.</p>
                    </div>
                  )}
                </div>
              )}

              {/* SETTINGS SECTION */}
              {activeTab === 'settings' && (
                <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div className="admin-section-header">
                    <div className="section-nav">
                      <button 
                        className="btn secondary"
                        onClick={() => setActiveTab('dashboard')}
                        style={{marginRight: '1rem'}}
                      >
                        ← Back to Dashboard
                      </button>
                      <h3 style={{color: '#4299E1', margin: 0}}>System Settings</h3>
                    </div>
                  </div>

                  <div className="settings-sections">
                    {/* My Account Settings */}
                    <div className="settings-subsection" style={{marginBottom: '2rem', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1.5rem'}}>
                      <div className="settings-subsection-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                        <h4 style={{color: '#2d3748', margin: 0}}>My Account Settings</h4>
                        <button 
                          className="btn primary" 
                          onClick={() => setShowCredentialModal(true)}
                        >
                          Change Login Credentials
                        </button>
                      </div>
                      
                      <p style={{color: '#718096', marginBottom: '1rem'}}>Update your username and password for admin login.</p>
                    </div>

                    {/* Edit Tracking Migration - runs automatically in background, hidden from UI */}

                    {/* Admin Management Section */}
                    <div className="settings-subsection" style={{marginBottom: '2rem', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1.5rem'}}>
                      <div className="settings-subsection-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                        <h4 style={{color: '#2d3748', margin: 0}}>Admin Account Management</h4>
                        <div style={{display: 'flex', gap: '0.5rem'}}>
                          <button 
                            className="btn secondary" 
                            onClick={() => {
                              setShowAdminOverrideModal(true);
                              loadAdminAccounts();
                            }}
                          >
                            View All Admin Accounts
                          </button>
                          <button 
                            className="btn primary" 
                            onClick={() => setShowCreateAdminModal(true)}
                          >
                            + Create Admin Account
                          </button>
                        </div>
                      </div>
                      
                      <p style={{color: '#718096', marginBottom: '1rem'}}>Create and manage admin accounts with manual override capabilities.</p>
                      
                      {loadingAdmins ? (
                        <div className="loading-message">Loading admin accounts...</div>
                      ) : admins && admins.length > 0 ? (
                        <div className="table-responsive">
                          <table className="admin-table">
                            <thead>
                              <tr>
                                <th>Username</th>
                                <th>Email</th>
                                <th>Allowed Sections</th>
                                <th>Price Access</th>
                                <th>Created</th>
                                <th style={{minWidth: '200px', width: '200px'}}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {admins.map((admin, index) => (
                                <tr key={admin?.id || `admin-${index}`}>
                                  <td>{admin?.username || 'N/A'}</td>
                                  <td>{admin?.email || 'N/A'}</td>
                                  <td>
                                    <div style={{fontSize: '0.85rem'}}>
                                      {admin?.allowed_sections?.join(', ') || 'All'}
                                    </div>
                                  </td>
                                  <td>
                                    <span className="status retail">
                                      {admin?.price_access?.join(', ') || 'Retail'}
                                    </span>
                                  </td>
                                  <td>{admin?.created_at ? new Date(admin.created_at).toLocaleDateString() : 'N/A'}</td>
                                  <td>
                                    <div style={{display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'flex-start'}}>
                                      <button 
                                        className="btn small danger"
                                        style={{width: '160px', fontSize: '11px', padding: '3px 6px'}} 
                                        onClick={() => {
                                          if (window.confirm('Delete this admin account?')) {
                                            // handleDeleteAdmin(admin.id);
                                            alert('Delete functionality will be implemented');
                                          }
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="no-admins-message">
                          <p>No additional admin accounts found. You are currently using the main admin account.</p>
                        </div>
                      )}
                    </div>

                    {/* System Information Section */}
                    <div className="settings-subsection" style={{border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1.5rem'}}>
                      <h4 style={{color: '#2d3748', margin: '0 0 1rem 0'}}>System Information</h4>
                      <div className="system-info">
                        <div className="info-item" style={{display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f7fafc'}}>
                          <span style={{fontWeight: '500'}}>Application Version:</span>
                          <span>v1.0.0</span>
                        </div>
                        <div className="info-item" style={{display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f7fafc'}}>
                          <span style={{fontWeight: '500'}}>Database Status:</span>
                          <span style={{color: '#38a169'}}>Connected</span>
                        </div>
                        <div className="info-item" style={{display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0'}}>
                          <span style={{fontWeight: '500'}}>Current Admin:</span>
                          <span>admin</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* CATALOGUE SECTION */}
              {activeTab === 'catalogue' && (
                <div className="admin-section" style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div className="admin-section-header">
                    <div className="section-nav">
                      <button 
                        className="btn secondary"
                        onClick={() => setActiveTab('dashboard')}
                        style={{marginRight: '1rem'}}
                      >
                        ← Back to Dashboard
                      </button>
                      <h3 style={{color: '#9F7AEA', margin: 0}}>Product Catalogue Generator</h3>
                    </div>
                  </div>

                  <div className="catalogue-generator" style={{maxWidth: '800px'}}>
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target);
                      
                      // Get selected products
                      const selectedProducts = [];
                      const productCheckboxes = e.target.querySelectorAll('input[name="products"]:checked');
                      productCheckboxes.forEach(cb => {
                        const product = products.find(p => p.id === cb.value);
                        if (product) selectedProducts.push(product);
                      });
                      
                      const catalogueData = {
                        products: selectedProducts,
                        priceType: formData.get('price_type')
                      };
                      
                      if (selectedProducts.length === 0) {
                        alert('Please select at least one product for the catalogue.');
                        return;
                      }
                      
                      await handleGenerateCatalogue(catalogueData);
                    }}>
                      
                      <div className="form-group" style={{marginBottom: '2rem'}}>
                        <label style={{fontSize: '1.1rem', fontWeight: '600', color: '#2d3748', marginBottom: '0.75rem', display: 'block'}}>
                          Select Price Type for Catalogue:
                        </label>
                        <div className="radio-group" style={{display: 'flex', gap: '1.5rem'}}>
                          <label className="radio-label" style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                            <input 
                              type="radio" 
                              name="price_type" 
                              value="retail" 
                              checked={cataloguePriceType === 'retail'}
                              onChange={(e) => setCataloguePriceType(e.target.value)}
                            />
                            <span style={{fontWeight: '500'}}>Retail Prices</span>
                          </label>
                          <label className="radio-label" style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                            <input 
                              type="radio" 
                              name="price_type" 
                              value="wholesale" 
                              checked={cataloguePriceType === 'wholesale'}
                              onChange={(e) => setCataloguePriceType(e.target.value)}
                            />
                            <span style={{fontWeight: '500'}}>Wholesale Prices</span>
                          </label>
                          <label className="radio-label" style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                            <input 
                              type="radio" 
                              name="price_type" 
                              value="fitter" 
                              checked={cataloguePriceType === 'fitter'}
                              onChange={(e) => setCataloguePriceType(e.target.value)}
                            />
                            <span style={{fontWeight: '500'}}>Fitter Prices</span>
                          </label>
                        </div>
                      </div>

                      <div className="form-group" style={{marginBottom: '2rem'}}>
                        <label style={{fontSize: '1.1rem', fontWeight: '600', color: '#2d3748', marginBottom: '0.75rem', display: 'block'}}>
                          Select Products for Catalogue:
                        </label>
                        <div className="products-selection" style={{maxHeight: '400px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem'}}>
                          {products && products.length > 0 ? (
                            <div className="products-grid" style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem'}}>
                              {products.map((product) => (
                                <label key={product.id} className="product-checkbox-card" style={{
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '0.75rem', 
                                  padding: '1rem', 
                                  border: '1px solid #e2e8f0', 
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s'
                                }}>
                                  <input 
                                    type="checkbox" 
                                    name="products" 
                                    value={product.id}
                                    style={{width: '16px', height: '16px'}}
                                  />
                                  <div className="product-info" style={{flex: 1}}>
                                    <div style={{fontWeight: '500', fontSize: '0.9rem', marginBottom: '0.25rem'}}>
                                      {product.name}
                                    </div>
                                    <div style={{fontSize: '0.8rem', color: '#718096'}}>
                                      {product.collection} | {product.category}
                                    </div>
                                    <div style={{fontSize: '0.8rem', color: '#2d3748', marginTop: '0.25rem'}}>
                                      {(() => {
                                        let price = 0;
                                        let label = '';
                                        
                                        if (cataloguePriceType === 'retail') {
                                          price = parseFloat(product.retail_price || 0);
                                          label = 'Retail';
                                        } else if (cataloguePriceType === 'wholesale') {
                                          price = parseFloat(product.wholesale_price || 0);
                                          label = 'Wholesale';  
                                        } else if (cataloguePriceType === 'fitter') {
                                          // Get fitter price from variations
                                          const fitterPrices = (product.variations || [])
                                            .map(v => parseFloat(v.fitter_price) || 0)
                                            .filter(p => p > 0);
                                          
                                          if (fitterPrices.length > 0) {
                                            price = Math.max(...fitterPrices); // Show highest fitter price
                                          } else {
                                            price = parseFloat(product.fitter_price || 0); // Fallback to product level
                                          }
                                          label = 'Fitter';
                                        }
                                        
                                        return `£${price.toFixed(2)} (${label})`;
                                      })()}
                                    </div>
                                  </div>
                                </label>
                              ))}
                            </div>
                          ) : (
                            <div style={{textAlign: 'center', color: '#718096', padding: '2rem'}}>
                              <p>No products available. Add products first to create catalogues.</p>
                            </div>
                          )}
                          
                          <div className="selection-actions" style={{marginTop: '1rem', padding: '1rem 0', borderTop: '1px solid #e2e8f0'}}>
                            <button 
                              type="button" 
                              className="btn secondary" 
                              style={{marginRight: '1rem'}}
                              onClick={(e) => {
                                const checkboxes = e.target.closest('.products-selection').querySelectorAll('input[name="products"]');
                                checkboxes.forEach(cb => cb.checked = true);
                              }}
                            >
                              Select All
                            </button>
                            <button 
                              type="button" 
                              className="btn secondary" 
                              onClick={(e) => {
                                const checkboxes = e.target.closest('.products-selection').querySelectorAll('input[name="products"]');
                                checkboxes.forEach(cb => cb.checked = false);
                              }}
                            >
                              Clear All
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="form-actions">
                        <button type="submit" className="btn primary" style={{fontSize: '1.1rem', padding: '0.75rem 2rem'}}>
                          📖 Generate Catalogue
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

            </div>
          )}

        </div>
      );
    }

    // If not logged in, show login form
    return (
      <div className="admin-login-page">
        <div className="admin-login-container">
          <div className="admin-login-card">
            <div className="admin-login-header">
              <div className="plush-logo">
                <div className="brand-text">
                  <span className="brand-plush">PLUSH</span>
                  <span className="brand-living">LIVING LTD</span>
                </div>
              </div>
              <h2>Admin Portal</h2>
            </div>
            
            <form onSubmit={handleAdminLogin}>
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Username"
                  value={adminUsername}
                  onChange={(e) => setAdminUsername(e.target.value)}
                  className="admin-input"
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="password"
                  placeholder="Password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  className="admin-input"
                  required
                />
              </div>
              {loginError && (
                <div className="admin-error">
                  {loginError}
                </div>
              )}
              <button type="submit" className="admin-btn primary full-width">
                Access Admin Portal
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  };

  const ContactPage = () => {
    const [formData, setFormData] = useState({
      name: '',
      email: '',
      phone: '',
      product_interest: '',
      message: '',
      preferred_contact: 'email'
    });
    const [submitStatus, setSubmitStatus] = useState('');

    // Auto-fill form with customer data
    useEffect(() => {
      if (customer) {
        setFormData(prev => ({
          ...prev,
          name: customer.name || prev.name,
          email: customer.email || prev.email,
          phone: customer.phone || prev.phone
        }));
      }
    }, [customer]);
    
    // Separate effect for product context to ensure it triggers
    useEffect(() => {
      if (contactProductContext) {
        console.log('Pre-filling contact form with product context:', contactProductContext);
        
        // Add a small delay to ensure the form is fully rendered
        setTimeout(() => {
          const productDetails = [];
          productDetails.push(`Product: ${contactProductContext.name}`);
          
          if (contactProductContext.selectedSize) {
            productDetails.push(`Size: ${contactProductContext.selectedSize}`);
          }
          
          if (contactProductContext.selectedColor) {
            productDetails.push(`Color: ${contactProductContext.selectedColor}`);
          }
          
          if (contactProductContext.design) {
            productDetails.push(`Design: ${contactProductContext.design}`);
          }
          
          if (contactProductContext.collection) {
            productDetails.push(`Collection: ${contactProductContext.collection}`);
          }
          
          if (contactProductContext.retailPrice) {
            productDetails.push(`Retail Price: £${contactProductContext.retailPrice}`);
          }
          
          if (contactProductContext.wholesalePrice) {
            productDetails.push(`Wholesale Price: £${contactProductContext.wholesalePrice}`);
          }
          
          const detailedMessage = `I am interested in the following product:\n\n${productDetails.join('\n')}\n\nPlease provide more information about availability, installation options, and any additional details.`;
          
          console.log('Setting contact form message to:', detailedMessage);
          setFormData(prev => {
            const newData = {
              ...prev,
              product_interest: contactProductContext.category || contactProductContext.collection || prev.product_interest,
              message: detailedMessage
            };
            console.log('Updated form data:', newData);
            return newData;
          });
        }, 100);
        
        // Clear the product context after using it
        setContactProductContext(null);
      }
    }, [contactProductContext]);

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      console.log('Submitting contact form with data:', formData);
      
      // Transform form data to match backend QueryCreate model
      const submitData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        subject: formData.product_interest || 'Product Enquiry',
        message: `${formData.message}${formData.preferred_contact ? `\n\nPreferred Contact: ${formData.preferred_contact}` : ''}`
      };
      
      console.log('Transformed data for backend:', submitData);
      
      try {
        const response = await axios.post(`${API}/api/contact`, submitData);
        console.log('Contact form response:', response.data);
        if (response.data.success) {
          setSubmitStatus('success');
          setFormData({
            name: '',
            email: '',
            phone: '',
            product_interest: '',
            message: '',
            preferred_contact: 'email'
          });
          // Clear success message after 5 seconds
          setTimeout(() => setSubmitStatus(''), 5000);
        } else {
          console.warn('Backend returned success: false:', response.data);
          setSubmitStatus('error');
        }
      } catch (error) {
        console.error('Contact form submission error:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        setSubmitStatus('error');
      }
    };

    return (
      <div className="contact-page section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Contact Us</h2>
            <p className="section-subtitle">Get in touch for product enquiries and expert advice</p>
          </div>
          
          <div className="contact-content">
            <div className="contact-form-section">
              <h3>Product Enquiry Form</h3>
              {customer && (
                <div className="auto-fill-notice">
                  <span className="auto-fill-icon">✓</span>
                  Your profile information has been automatically filled in
                </div>
              )}
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="product_interest">Product Interest</label>
                    <select
                      id="product_interest"
                      name="product_interest"
                      value={formData.product_interest}
                      onChange={handleInputChange}
                      className="form-control"
                    >
                      <option value="">Select a category...</option>
                      <option value="Laminate/LVT">Laminate/LVT</option>
                      <option value="Carpets">Carpets</option>
                      <option value="Remnants">Remnants</option>
                      <option value="Vinyl">Vinyl</option>
                      <option value="Rugs">Rugs</option>
                      <option value="Artificial Grass">Artificial Grass</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Message / Enquiry Details *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    className="form-control"
                    placeholder="Tell us about your flooring needs, room size, preferred style, etc."
                  ></textarea>
                </div>
                
                <div className="form-group">
                  <label>Preferred Contact Method</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="preferred_contact"
                        value="email"
                        checked={formData.preferred_contact === 'email'}
                        onChange={handleInputChange}
                      />
                      Email
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="preferred_contact"
                        value="phone"
                        checked={formData.preferred_contact === 'phone'}
                        onChange={handleInputChange}
                      />
                      Phone
                    </label>
                  </div>
                </div>
                
                <button type="submit" className="submit-btn">Send Enquiry</button>
                
                {submitStatus === 'success' && (
                  <div className="success-message">
                    Thank you! Your enquiry has been sent. We'll get back to you soon.
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="error-message">
                    Sorry, there was an error sending your enquiry. Please try again.
                  </div>
                )}
              </form>
            </div>
            
            {/* Store information removed as requested */}
          </div>
        </div>
      </div>
    );
  };

  const ProfilePage = () => {
    if (!customer) {
      return (
        <div className="profile-page section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Access Denied</h2>
              <p className="section-subtitle">Please sign in to view your profile</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="profile-page section">
        <div className="container">
          {/* Profile Header */}
          <div className="profile-page-header">
            <div className="profile-user-info">
              <img 
                src={customer.picture || '/default-avatar.png'} 
                alt="Profile" 
                className="profile-page-image"
              />
              <div className="profile-user-details">
                <h1 className="profile-name">{customer.name}</h1>
                <p className="profile-email">{customer.email}</p>
                {wholesaleStatus && wholesaleStatus.status === 'approved' && (
                  <span className="wholesale-badge-profile">Wholesale Customer</span>
                )}
              </div>
            </div>
          </div>

          {/* Profile Tabs */}
          <div className="profile-tabs-container">
            <div className="profile-tabs">
              <button 
                className={`profile-tab ${activeProfileTab === 'profile' ? 'active' : ''}`}
                onClick={() => setActiveProfileTab('profile')}
              >
                Profile
              </button>
              <button 
                className={`profile-tab ${activeProfileTab === 'enquiries' ? 'active' : ''}`}
                onClick={() => setActiveProfileTab('enquiries')}
              >
                Previous Enquires
              </button>
              <button 
                className={`profile-tab ${activeProfileTab === 'favourites' ? 'active' : ''}`}
                onClick={() => setActiveProfileTab('favourites')}
              >
                Favourited Products
              </button>
              <button 
                className={`profile-tab ${activeProfileTab === 'wholesale' ? 'active' : ''}`}
                onClick={() => setActiveProfileTab('wholesale')}
              >
                Apply for Wholesale Account
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="profile-content-area">
            {activeProfileTab === 'profile' && (
              <div className="profile-tab-content">
                <h2>Profile Information</h2>
                <div className="profile-info-grid">
                  <div className="profile-info-item">
                    <label>Name</label>
                    <p>{customer.name}</p>
                  </div>
                  <div className="profile-info-item">
                    <label>Email</label>
                    <p>{customer.email}</p>
                  </div>
                  <div className="profile-info-item">
                    <label>Phone</label>
                    <p>{customer.phone || 'Not provided'}</p>
                  </div>
                  <div className="profile-info-item">
                    <label>Account Type</label>
                    <p>{wholesaleStatus?.status === 'approved' ? 'Wholesale Customer' : 'Retail Customer'}</p>
                  </div>
                </div>
                
                <div className="profile-actions-section">
                  <button className="profile-action-btn secondary" onClick={() => setActiveProfileTab('edit-profile')}>
                    Edit Profile
                  </button>
                  <button className="profile-action-btn logout" onClick={handleCustomerLogout}>
                    Sign Out
                  </button>
                </div>
              </div>
            )}

            {activeProfileTab === 'edit-profile' && (
              <div className="profile-tab-content">
                <h2>Edit Profile Information</h2>
                <form className="edit-profile-form" onSubmit={handleProfileUpdate}>
                  <div className="form-group">
                    <label>Name</label>
                    <input 
                      type="text" 
                      name="name" 
                      className="form-control" 
                      defaultValue={customer.name}
                      required 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Email</label>
                    <input 
                      type="email" 
                      name="email" 
                      className="form-control" 
                      defaultValue={customer.email}
                      required 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Phone</label>
                    <input 
                      type="tel" 
                      name="phone" 
                      className="form-control" 
                      defaultValue={customer.phone || ''}
                      placeholder="Enter your phone number"
                    />
                  </div>
                  
                  <div className="profile-form-actions">
                    <button type="button" className="profile-form-btn secondary" onClick={() => setActiveProfileTab('profile')}>
                      Cancel
                    </button>
                    <button type="submit" className="profile-form-btn primary">
                      Update Profile
                    </button>
                  </div>
                </form>
              </div>
            )}

            {activeProfileTab === 'enquiries' && (
              <div className="profile-tab-content">
                <h2>Previous Enquires</h2>
                <div className="enquiries-list">
                  <div className="no-enquiries">
                    <p>You haven't made any enquiries yet.</p>
                    <button 
                      className="contact-us-btn"
                      onClick={() => setCurrentPage('contact')}
                    >
                      Contact Us
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeProfileTab === 'favourites' && (
              <div className="profile-tab-content">
                <h2>Favourited Products</h2>
                <div className="favourites-list">
                  <div className="no-favourites">
                    <p>You haven't favourited any products yet.</p>
                    <button 
                      className="browse-products-btn"
                      onClick={() => setCurrentPage('home')}
                    >
                      Browse Products
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeProfileTab === 'wholesale' && (
              <div className="profile-tab-content">
                <h2>Wholesale Account Application</h2>
                
                {wholesaleStatus && wholesaleStatus.status === 'approved' ? (
                  <div className="wholesale-status-section">
                    <div className="wholesale-status-card status-approved">
                      <div className="status-header">
                        <h3>Application Status: Approved</h3>
                        <span className="status-indicator approved">✓</span>
                      </div>
                      
                      <div className="status-details">
                        <p><strong>Company:</strong> {wholesaleStatus.company_name}</p>
                        <p><strong>Applied:</strong> {new Date(wholesaleStatus.application_date).toLocaleDateString()}</p>
                        
                        <div className="status-message approved">
                          Congratulations! You are approved for wholesale pricing. Enjoy discounted rates on all products.
                        </div>
                      </div>
                    </div>
                  </div>
                ) : wholesaleStatus && wholesaleStatus.status === 'pending' ? (
                  <div className="wholesale-status-section">
                    <div className="wholesale-status-card status-pending">
                      <div className="status-header">
                        <h3>Application Status: Pending</h3>
                        <span className="status-indicator pending">⏳</span>
                      </div>
                      
                      <div className="status-details">
                        <p><strong>Company:</strong> {wholesaleStatus.company_name}</p>
                        <p><strong>Applied:</strong> {new Date(wholesaleStatus.application_date).toLocaleDateString()}</p>
                        
                        <div className="status-message pending">
                          Your wholesale application is being reviewed. You will be notified once approved.
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="no-application-section">
                    <p>You haven't applied for wholesale pricing yet. Submit an application to get access to discounted wholesale rates.</p>
                  </div>
                )}
                
                {/* Always show the application form unless already approved */}
                {(!wholesaleStatus || wholesaleStatus.status !== 'approved') && (
                  <div className="wholesale-application-section">
                    <h3>Submit Wholesale Application</h3>
                    <form className="wholesale-application-form-page" onSubmit={handleWholesaleApplicationSubmit}>
                      <div className="form-group">
                        <label>Company Name *</label>
                        <input 
                          type="text" 
                          name="companyName" 
                          className="form-control" 
                          required 
                          placeholder="Enter your company name"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Company Email *</label>
                        <input 
                          type="email" 
                          name="companyEmail" 
                          className="form-control" 
                          required 
                          placeholder="Enter company email address"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Company Address *</label>
                        <textarea 
                          name="companyAddress" 
                          className="form-control" 
                          rows="3" 
                          required 
                          placeholder="Enter complete company address"
                        />
                      </div>
                      
                      <div className="application-note">
                        <p><strong>Note:</strong> Applications are reviewed manually to ensure business legitimacy. 
                        Please provide accurate information as it will be verified.</p>
                      </div>
                      
                      <button type="submit" className="submit-application-btn">
                        Submit Application
                      </button>
                    </form>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const ProductsPage = () => {
    const [selectedCategory, setSelectedCategory] = useState('');
    const [sortBy, setSortBy] = useState('name');

    // SIMPLIFIED: Use main app's products state directly - no separate loading needed
    // The main app already preloads all products, so we just use that data
    
    // Filter products by category - use main app's products
    const filteredProducts = products.filter(product => {
      if (!selectedCategory) return true;
      return product.category === selectedCategory || product.collection === selectedCategory;
    });

    // Sort products
    const sortedProducts = [...filteredProducts].sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return (a.retail_price || 0) - (b.retail_price || 0);
        case 'price-high':
          return (b.retail_price || 0) - (a.retail_price || 0);
        case 'stock':
          const aStock = a.variations?.reduce((total, v) => total + (v.stock_quantity || v.quantity || 0), 0) || a.quantity || 0;
          const bStock = b.variations?.reduce((total, v) => total + (v.stock_quantity || v.quantity || 0), 0) || b.quantity || 0;
          return bStock - aStock;
        case 'name':
        default:
          return a.name.localeCompare(b.name);
      }
    });

    // Get unique categories for filter
    const categories = [...new Set(products.map(p => p.category || p.collection).filter(Boolean))];

    // FIXED: Calculate total stock for a product (handles both measurement units)
    const getProductStock = (product) => {
      if (product.variations && product.variations.length > 0) {
        return product.variations.reduce((total, variation) => {
          // For per_square_meter products, use square_meters_available
          if (product.measurement_unit === 'per_square_meter') {
            return total + (variation.square_meters_available || variation.square_meters || 0);
          } else {
            // For size-based products, use stock_quantity
            return total + (variation.stock_quantity || variation.quantity || 0);
          }
        }, 0);
      }
      
      // For products without variations, use main product quantity
      if (product.measurement_unit === 'per_square_meter') {
        return product.square_meters_available || product.square_meters || 0;
      } else {
        return product.quantity || 0;
      }
    };

    // Get product image with enhanced debugging and validation
    const getProductImage = (product) => {
      if (!product) {
        console.warn('getProductImage: No product provided');
        return null;
      }
      
      // 1) Main product base64 image
      if (product.image_base64 && product.image_base64.trim()) {
        const img = product.image_base64.startsWith('data:')
          ? product.image_base64
          : `data:image/jpeg;base64,${product.image_base64}`;
        console.log('✅ Found main product image_base64 for:', product.name);
        return img;
      }

      // 2) New multiple images array (first image)
      if (Array.isArray(product.images) && product.images.length > 0) {
        const firstImage = product.images[0];
        if (firstImage && firstImage.trim()) {
          const img = firstImage.startsWith('data:')
            ? firstImage
            : `data:image/jpeg;base64,${firstImage}`;
          console.log('✅ Found main product images[0] for:', product.name);
          return img;
        }
      }

      // 3) Main product URL(s)
      if (product.image_url && typeof product.image_url === 'string' && product.image_url.trim()) {
        console.log('✅ Found main product image_url for:', product.name);
        return product.image_url;
      }
      if (Array.isArray(product.image_urls) && product.image_urls.length > 0 && product.image_urls[0]) {
        console.log('✅ Found main product image_urls[0] for:', product.name);
        return product.image_urls[0];
      }

      // 4) Variation-level images (base64 or URL)
      const firstVarWithB64 = product.variations?.find(v => v.image_base64 && v.image_base64.trim());
      if (firstVarWithB64?.image_base64) {
        const img = firstVarWithB64.image_base64.startsWith('data:')
          ? firstVarWithB64.image_base64
          : `data:image/jpeg;base64,${firstVarWithB64.image_base64}`;
        console.log('✅ Found variation image_base64 for:', product.name);
        return img;
      }
      
      // Check for variation enhanced images
      if (product.variations && Array.isArray(product.variations) && product.variations.length > 0) {
        for (const variation of product.variations) {
          if (Array.isArray(variation.images) && variation.images.length > 0) {
            const firstVarImage = variation.images[0];
            if (firstVarImage && firstVarImage.trim()) {
              const img = firstVarImage.startsWith('data:')
                ? firstVarImage
                : `data:image/jpeg;base64,${firstVarImage}`;
              console.log('✅ Found variation images[0] for:', product.name);
              return img;
            }
          }
        }
      }
      
      const firstVarWithUrl = product.variations?.find(v => v.image_url && typeof v.image_url === 'string' && v.image_url.trim());
      if (firstVarWithUrl?.image_url) {
        console.log('✅ Found variation image_url for:', product.name);
        return firstVarWithUrl.image_url;
      }
      
      // Check for variation image_urls array
      if (product.variations && Array.isArray(product.variations) && product.variations.length > 0) {
        for (const variation of product.variations) {
          if (Array.isArray(variation.image_urls) && variation.image_urls.length > 0 && variation.image_urls[0]) {
            console.log('✅ Found variation image_urls[0] for:', product.name);
            return variation.image_urls[0];
          }
        }
      }
      
      // Only log in development mode
      if (process.env.NODE_ENV === 'development') {
        console.warn('❌ No image found for product:', product.name);
      }
      
      return null;
    };

    return (
      <div className="products-page section">
        <div className="container">
          {/* Products Header */}
          <div className="section-header">
            <h1 className="section-title">OUR PRODUCTS</h1>
            <p className="section-subtitle">Discover our premium collection of luxury carpets and flooring</p>
          </div>

          {/* Filters and Sorting */}
          <div className="products-controls">
            <div className="products-filters">
              <select 
                value={selectedCategory} 
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="filter-select"
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="name">Sort by Name</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="stock">Sort by Stock</option>
              </select>
            </div>
            
            <div className="products-count">
              Showing {sortedProducts.length} of {products.length} products
            </div>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="products-loading">
              <div className="loading-spinner"></div>
              <p>Loading products...</p>
            </div>
          ) : sortedProducts.length === 0 ? (
            <div className="no-products">
              <h3>No products found</h3>
              <p>Try adjusting your filters or check back later for new arrivals.</p>
            </div>
          ) : (
            <div className="products-grid">
              {sortedProducts.map(product => {
                const productStock = getProductStock(product);
                const productImage = getProductImage(product);
                
                return (
                  <div key={product.id} className="product-card" onClick={() => handleProductClick(product)}>
                    {/* Product Image */}
                    <div className="product-image-container">
                      {productImage ? (
                        <img 
                          src={productImage}
                          alt={product.name}
                          className="product-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="product-placeholder" style={{
                        display: productImage ? 'none' : 'flex',
                        width: '100%',
                        height: '200px',
                        backgroundColor: '#f8f9fa',
                        border: '2px dashed #dee2e6',
                        borderRadius: '8px',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <span style={{fontSize: '48px', marginBottom: '10px', opacity: 0.5}}>🏠</span>
                        <span style={{color: '#6c757d', fontSize: '14px'}}>No Image Available</span>
                        <span style={{color: '#9ca3af', fontSize: '12px', marginTop: '5px'}}>Upload images in admin panel</span>
                      </div>
                      
                      {/* IMPROVED Stock Badge with Detailed Quantities */}
                      <div className={`stock-badge ${productStock === 0 ? 'out-of-stock' : productStock < 10 ? 'low-stock' : 'in-stock'}`}>
                        {productStock === 0 ? 'Out of Stock' : 
                         product.measurement_unit === 'per_square_meter' ? `${productStock % 1 === 0 ? Math.round(productStock) : parseFloat(productStock).toFixed(1)}m² Available` :
                         productStock < 10 ? `${Math.round(productStock)} Units Left` : 
                         `${Math.round(productStock)} Units Available`}
                      </div>
                      
                      {/* Admin Image Upload Indicator */}
                      {!productImage && customer && customer.role === 'admin' && (
                        <div style={{
                          position: 'absolute',
                          top: '10px',
                          left: '10px',
                          background: 'rgba(212, 175, 55, 0.9)',
                          color: 'white',
                          padding: '3px 6px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}>
                          📷 ADD IMAGES
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="product-info">
                      <h3 className="product-name">{product.name}</h3>
                      {product.description && (
                        <p className="product-description">{product.description}</p>
                      )}
                      
                      {/* ENHANCED Stock Information with Variation Details */}
                      <div className="stock-info">
                        <p className="stock-summary">
                          <strong>Total Stock: </strong>
                          {product.measurement_unit === 'per_square_meter' ? 
                            `${productStock % 1 === 0 ? Math.round(productStock) : parseFloat(productStock).toFixed(1)} m² available` : 
                            `${Math.round(productStock)} units available`}
                        </p>
                        {product.variations && product.variations.length > 1 && (
                          <div className="variation-stock">
                            <p className="variation-label">Available colors/sizes:</p>
                            <div className="variation-list">
                              {product.variations.slice(0, 3).map((variation, idx) => {
                                const variationStock = product.measurement_unit === 'per_square_meter' ? 
                                  (variation.square_meters_available || 0) : 
                                  (variation.stock_quantity || 0);
                                return (
                                  <span key={idx} className="variation-item">
                                    {(variation.color || '').trim()} ({product.measurement_unit === 'per_square_meter' ? parseFloat(variationStock).toFixed(2) : Math.round(variationStock)}{product.measurement_unit === 'per_square_meter' ? 'm²' : ' units'})
                                  </span>
                                );
                              })}
                              {product.variations.length > 3 && (
                                <span className="more-variations">+{product.variations.length - 3} more</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="product-details">
                        <div className="product-category">{product.category || product.collection}</div>
                        {product.design && <div className="product-design">Design: {product.design}</div>}
                      </div>

                      {/* Price Display - Contact for pricing */}
                      <div className="product-pricing">
                        <div className="retail-price contact-price" style={{
                          fontSize: '1rem',
                          fontWeight: '700',
                          color: '#28a745',
                          background: '#f0f9f0',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: '2px solid #28a745',
                          display: 'inline-block'
                        }}>
                          Contact for Price
                        </div>
                      </div>

                      {/* Variations Summary */}
                      {product.variations && product.variations.length > 0 && (
                        <div className="product-variations">
                          <div className="variations-summary">
                            <span>{product.variations.length} variations available</span>
                            <div className="variation-colors">
                              {product.variations.slice(0, 5).map((variation, index) => (
                                <span key={index} className="variation-dot" title={`${variation.color} - ${variation.size}`}>
                                  {variation.color?.charAt(0) || '•'}
                                </span>
                              ))}
                              {product.variations.length > 5 && <span className="more-variations">+{product.variations.length - 5}</span>}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Stock Info */}
                      <div className="product-stock-info">
                        <span className="stock-count">Stock: {product.measurement_unit === 'per_square_meter' ? (productStock % 1 === 0 ? Math.round(productStock) : parseFloat(productStock).toFixed(1)) : Math.round(productStock)} available</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const GalleryPage = () => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [viewMode, setViewMode] = useState('grid'); // grid or masonry

    // Sample gallery data - in production this would come from backend
    const sampleGalleryItems = [
      {
        id: 1,
        title: "Luxury Persian Carpet Installation",
        description: "Premium hand-woven Persian carpet installation in residential living room",
        type: "image",
        url: "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='300'><rect width='400' height='300' fill='%23f3f4f6'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='18'>No Image</text></svg>",
        category: "residential",
        date: "2024-01-15",
        location: "London, UK"
      },
      {
        id: 2,
        title: "Commercial Office Flooring",
        description: "Complete office flooring renovation with premium LVT tiles",
        type: "video",
        url: "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='300'><rect width='400' height='300' fill='%23f3f4f6'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='18'>No Image</text></svg>",
        thumbnail: "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='300'><rect width='400' height='300' fill='%23f3f4f6'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='18'>No Image</text></svg>",
        category: "commercial",
        date: "2024-01-10",
        location: "Manchester, UK"
      },
      {
        id: 3,
        title: "Hardwood Floor Restoration",
        description: "Restoration of century-old hardwood floors in Victorian home",
        type: "image",
        url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='250'%3E%3Crect fill='%23ddd' width='400' height='250'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='18' dy='10.5' font-weight='bold' x='50%25' y='50%25' text-anchor='middle'%3ERestoration Gallery%3C/text%3E%3C/svg%3E",
        category: "restoration",
        date: "2024-01-05",
        location: "Edinburgh, UK"
      }
    ];

    // Use sample data for now - later connect to backend
    const displayItems = galleryItems.length > 0 ? galleryItems : sampleGalleryItems;

    // Filter items by category
    const filteredItems = selectedCategory === 'all' 
      ? displayItems 
      : displayItems.filter(item => item.category === selectedCategory);

    // Get unique categories
    const categories = ['all', ...new Set(displayItems.map(item => item.category))];

    const handleItemClick = (item) => {
      if (item.type === 'video') {
        // Handle video playback
        alert(`Playing video: ${item.title}`);
      } else {
        // Handle image lightbox
        alert(`Viewing image: ${item.title}`);
      }
    };

    return (
      <div className="gallery-page section">
        <div className="container">
          {/* Gallery Header */}
          <div className="section-header">
            <h1 className="section-title">OUR GALLERY</h1>
            <p className="section-subtitle">Showcase of our premium installations and craftsmanship</p>
          </div>

          {/* Gallery Controls */}
          <div className="gallery-controls">
            <div className="gallery-filters">
              <label>Filter by Category:</label>
              <select 
                value={selectedCategory} 
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="gallery-filter-select"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="gallery-view-modes">
              <button 
                className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
              >
                Grid View
              </button>
              <button 
                className={`view-mode-btn ${viewMode === 'masonry' ? 'active' : ''}`}
                onClick={() => setViewMode('masonry')}
              >
                Masonry View
              </button>
            </div>
          </div>

          {/* Gallery Content */}
          {filteredItems.length === 0 ? (
            <div className="no-gallery-items">
              <h3>No gallery items found</h3>
              <p>Check back soon for updates on our latest projects and installations.</p>
            </div>
          ) : (
            <div className={`gallery-grid ${viewMode === 'masonry' ? 'masonry' : ''}`}>
              {filteredItems.map((item) => (
                <div key={item.id} className="gallery-item" onClick={() => handleItemClick(item)}>
                  <div className="gallery-item-image">
                    {item.type === 'video' ? (
                      <div className="video-thumbnail">
                        <img src={item.thumbnail || item.url} alt={item.title} />
                        <div className="video-play-overlay">
                          <div className="play-button">▶</div>
                        </div>
                      </div>
                    ) : (
                      <img src={item.url} alt={item.title} />
                    )}
                    
                    <div className="gallery-item-overlay">
                      <div className="gallery-item-info">
                        <h3 className="gallery-item-title">{item.title}</h3>
                        <p className="gallery-item-description">{item.description}</p>
                        <div className="gallery-item-meta">
                          <span className="gallery-date">{new Date(item.date).toLocaleDateString()}</span>
                          <span className="gallery-location">{item.location}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="gallery-item-details">
                    <div className="gallery-item-badges">
                      <span className={`category-badge ${item.category}`}>
                        {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                      </span>
                      <span className={`type-badge ${item.type}`}>
                        {item.type === 'video' ? '🎥' : '📷'} {item.type.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Gallery Stats */}
          <div className="gallery-stats">
            <div className="stat-item">
              <span className="stat-number">{filteredItems.length}</span>
              <span className="stat-label">Projects Showcased</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{displayItems.filter(item => item.type === 'image').length}</span>
              <span className="stat-label">Photos</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{displayItems.filter(item => item.type === 'video').length}</span>
              <span className="stat-label">Videos</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{categories.length - 1}</span>
              <span className="stat-label">Categories</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const Footer = () => (
    <footer className="footer">
      <div className="footer-content">
        <div className="footer-section footer-brand">
          <div className="footer-logo-section">
            <div className="himalaya-logo">
              <img src="https://customer-assets.emergentagent.com/job_carpet-ai/artifacts/h7morq89_Screenshot%20%28161%29.png" alt="Plush Living LTD" className="plush-logo-img-large" />
            </div>
          </div>
        </div>
        
        <div className="footer-section store-locations-section">
          <h3>STORE LOCATIONS</h3>
          
          {storeLocations && storeLocations.length > 0 ? (
            <div className="locations-grid">
              {storeLocations.map((location, index) => (
                <div key={location.id || `location-${index}`} className="location-card">
                  <h4 className="location-name">{location.name}</h4>
                  <div className="location-address">
                    {location.address}
                  </div>
                  {location.phone && (
                    <p className="location-phone">Tel: {location.phone}</p>
                  )}
                  <button 
                    className="get-directions-btn"
                    onClick={() => {
                      const address = encodeURIComponent(location.address);
                      window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}`, '_blank');
                    }}
                  >
                    🗺️ Get Directions
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-locations">
              <p>Store locations will appear here once they are added.</p>
            </div>
          )}
        </div>
      </div>
      
      <div className="footer-bottom">
        <p>&copy; 2024 Plush Living LTD. All rights reserved.</p>
      </div>
    </footer>
  );



  const CategoryProductsPage = ({ categoryName }) => {
    const [categoryProducts, setCategoryProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(true);

    useEffect(() => {
      loadCategoryProducts();
    }, [categoryName]);

  const loadCategoryProducts = async () => {
    try {
      setLoadingProducts(true);
      
      // Safety check for products array
      if (!products || !Array.isArray(products)) {
        setCategoryProducts([]);
        return;
      }
      
      // Filter products by category OR collection (case insensitive)
      const categoryFilteredProducts = products.filter(product => {
        if (!product) return false; // Safety check for product object
        
        const productCategory = (product.category || '').toLowerCase();
        const productCollection = (product.collection || '').toLowerCase();
        const searchCategory = (categoryName || '').toLowerCase();
        
        const matches = productCategory === searchCategory || productCollection === searchCategory;
        return matches;
      });
      
      setCategoryProducts(categoryFilteredProducts || []);
    } catch (error) {
      console.error('Error loading category products:', error);
      setCategoryProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

    return (
      <div className="category-page section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{categoryName}</h2>
            <p className="section-subtitle">Discover our premium {categoryName.toLowerCase()} collection</p>
          </div>
          
          <div className="category-products">
            {loadingProducts ? (
              <div className="loading-products">
                <div className="spinner"></div>
                <p>Loading products...</p>
              </div>
            ) : categoryProducts.length > 0 ? (
              <div className="products-grid">
                {categoryProducts.map((product) => (
                  <div key={product.id} className="product-card">
                    <div className="product-image">
                      {(() => {
                        // Use the same logic as getProductImage function
                        let productImage = null;
                        
                        // 1) Main product base64 image
                        if (product.image_base64) {
                          productImage = product.image_base64.startsWith('data:')
                            ? product.image_base64
                            : `data:image/jpeg;base64,${product.image_base64}`;
                        }
                        // 2) New multiple images array (first image)
                        else if (Array.isArray(product.images) && product.images.length > 0) {
                          const firstImage = product.images[0];
                          productImage = firstImage.startsWith('data:')
                            ? firstImage
                            : `data:image/jpeg;base64,${firstImage}`;
                        }
                        // 3) Main product URL(s)
                        else if (product.image_url && typeof product.image_url === 'string') {
                          productImage = product.image_url;
                        }
                        else if (Array.isArray(product.image_urls) && product.image_urls.length > 0) {
                          productImage = product.image_urls[0];
                        }
                        // 4) Variation-level images
                        else {
                          const firstVarWithB64 = product.variations?.find(v => v.image_base64);
                          if (firstVarWithB64?.image_base64) {
                            productImage = firstVarWithB64.image_base64.startsWith('data:')
                              ? firstVarWithB64.image_base64
                              : `data:image/jpeg;base64,${firstVarWithB64.image_base64}`;
                          } else {
                            const firstVarWithUrl = product.variations?.find(v => v.image_url);
                            if (firstVarWithUrl?.image_url) {
                              productImage = firstVarWithUrl.image_url;
                            }
                          }
                        }

                        return productImage ? (
                          <img src={productImage} alt={product.name} />
                        ) : (
                          <div className="product-image-placeholder">
                            <span>No Image</span>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="product-info">
                      <h3 className="product-name">{product.name}</h3>
                      <p className="product-description">{product.description}</p>
                      
                      {/* Main Product Details */}
                      <div className="product-details">
                        <div className="product-price">
                          <span className="price-label">Price:</span>
                          <span className="price-value contact-price">Contact for Quote</span>
                        </div>
                        <div className="product-stock">
                          <span className="stock-label">Available:</span>
                          <span className={`stock-value ${product.quantity < 10 ? 'low-stock' : ''}`}>
                            {product.measurement_unit === 'per_square_meter' ? 
                              ((product.quantity || 0) % 1 === 0 ? Math.round(product.quantity || 0) : parseFloat(product.quantity || 0).toFixed(1)) : 
                              Math.round(product.quantity || 0)} {product.measurement_unit === 'per_square_meter' ? 'm²' : 'units'}
                          </span>
                        </div>
                        {product.color && (
                          <div className="product-color">
                            <span className="color-label">Color:</span>
                            <span className="color-value">{product.color}</span>
                          </div>
                        )}
                        {product.size && (
                          <div className="product-size">
                            <span className="size-label">Size:</span>
                            <span className="size-value">{product.size}</span>
                          </div>
                        )}
                        {product.collection && (
                          <div className="product-collection">
                            <span className="collection-label">Collection:</span>
                            <span className="collection-value">{product.collection}</span>
                          </div>
                        )}
                      </div>

                      {/* Product Variations Display */}
                      {product.variations && product.variations.length > 0 && (
                        <div className="product-variations">
                          <h4 className="variations-title">Available Options:</h4>
                          <div className="variations-list">
                            {product.variations.map((variation, index) => (
                              <div key={index} className="variation-item-customer">
                                <div className="variation-details">
                                  {variation.color && (
                                    <span className="variation-color">Color: {variation.color}</span>
                                  )}
                                  {variation.size && (
                                    <span className="variation-size">Size: {variation.size}</span>
                                  )}
                                  <span className="variation-price">
                                    £{(() => {
                                      let price = parseFloat(variation.retail_price || variation.price) || 0;
                                      // If price is placeholder £1 or zero, use cost-based pricing
                                      if (price === 0 || price === 1) {
                                        const costPrice = parseFloat(variation.cost_price) || 0;
                                        if (costPrice > 0) {
                                          price = costPrice * 1.8; // 80% markup
                                        }
                                      }
                                      return price.toFixed(2);
                                    })()}
                                    {product.measurement_unit === 'per_square_meter' ? '/m²' : ''}
                                  </span>
                                  <span className="variation-stock">
                                    {product.measurement_unit === 'per_square_meter' ? 
                                      parseFloat(variation.stock_quantity || variation.quantity || 0).toFixed(2) : 
                                      Math.round(variation.stock_quantity || variation.quantity || 0)} available
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="product-actions">
                        <button 
                          className="contact-product-btn"
                          onClick={() => setCurrentPage('contact')}
                        >
                          Contact for Quote
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-products">
                <h3>No products available in this category</h3>
                <p>Please contact us for more information about our {categoryName.toLowerCase()} collection.</p>
                <button 
                  className="contact-btn-center"
                  onClick={() => setCurrentPage('contact')}
                >
                  Contact Us
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render main content based on current page
  const renderContent = () => {
    // OPTIMIZED INSTANT LOADING: Pre-render all pages and show/hide with CSS for instant transitions
    if (currentPage === 'admin-portal') {
      return <AdminPortal />;
    }
    
    return (
      <>
        {/* INSTANT PAGE SWITCHING: All pages rendered simultaneously, controlled by display CSS */}
        <div style={{ display: currentPage === 'home' ? 'block' : 'none' }}>
          <HeroSlider />
          <FeaturedProductsSection />
          <TransformSection />
          <TestimonialsSection />
        </div>
        
        <div style={{ display: currentPage === 'products' ? 'block' : 'none' }}>
          <ProductsPage />
        </div>
        
        <div style={{ display: currentPage === 'gallery' ? 'block' : 'none' }}>
          <GalleryPage />
        </div>
        
        <div style={{ display: currentPage === 'contact' ? 'block' : 'none' }}>
          <ContactPage />
        </div>
        
        <div style={{ display: currentPage === 'about' ? 'block' : 'none' }}>
          <AboutPage />
        </div>
        
        <div style={{ display: currentPage === 'profile' ? 'block' : 'none' }}>
          <ProfilePage />
        </div>
        
        {/* Category pages - show products from that category */}
        {categories.map(cat => {
          const categoryMatches = cat.name.toLowerCase().replace(/\s+/g, '-') === currentPage ||
                                 cat.name.toLowerCase() === currentPage ||
                                 cat.name === currentPage;
          return categoryMatches ? (
            <div key={cat.id} style={{ display: 'block' }}>
              <CategoryProductsPage categoryName={cat.name} />
            </div>
          ) : null;
        })}
      </>
    );
  };


  // If dashboard user is logged in, show dashboard view
  if (isDashboardUser) {
    return (
      <DashboardUserView
        dashboardData={dashboardData}
        handleDashboardLogout={handleDashboardLogout}
        fulfilledOrders={fulfilledOrders}
        orders={orders}
        creditAccountsOrders={creditAccountsOrders}
        customerAccountPayments={customerAccountPayments}
        setDashboardUserInAdminMode={setDashboardUserInAdminMode}
        setIsAdminLoggedIn={setIsAdminLoggedIn}
        setCurrentPage={setCurrentPage}
        setActiveTab={setActiveTab}
        setIsDashboardUser={setIsDashboardUser}
        setLoggedInAdminUsername={setLoggedInAdminUsername}
        adminPermissions={adminPermissions}
        loggedInAdminUsername={loggedInAdminUsername}
        refreshDashboardData={async () => {
          // Refresh ALL data for INSTANT sync with admin
          await refreshAllData();
        }}
      />
    );
  }

  return (
    <div className="App">
      {/* Permanent Header - always visible */}
      <Header />

      <main className="main-content">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : (
          renderContent()
        )}
      </main>
      
      {/* Permanent Footer - always visible */}
      <Footer />
      
      {/* Dashboard Login Modal */}
      {showDashboardLogin && !isDashboardUser && (
        <div className="modal-overlay" onClick={() => setShowDashboardLogin(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '400px', background: 'linear-gradient(135deg, #0f1a35 0%, #1a2744 100%)'}}>
            <div className="modal-header" style={{background: '#b8860b', color: 'white', borderRadius: '8px 8px 0 0'}}>
              <h2 style={{margin: 0}}>📊 Dashboard Login</h2>
              <button className="close-modal" onClick={() => setShowDashboardLogin(false)} style={{background: 'transparent', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer'}}>×</button>
            </div>
            <form onSubmit={handleDashboardLogin} style={{padding: '30px'}}>
              <div style={{marginBottom: '20px'}}>
                <label style={{display: 'block', color: '#9ca3af', marginBottom: '8px', fontSize: '14px'}}>Username</label>
                <input
                  type="text"
                  value={dashboardUsername}
                  onChange={(e) => setDashboardUsername(e.target.value)}
                  placeholder="Enter username"
                  style={{width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #374151', background: '#1f2937', color: 'white', fontSize: '16px'}}
                  required
                />
              </div>
              <div style={{marginBottom: '25px'}}>
                <label style={{display: 'block', color: '#9ca3af', marginBottom: '8px', fontSize: '14px'}}>Password</label>
                <input
                  type="password"
                  value={dashboardPassword}
                  onChange={(e) => setDashboardPassword(e.target.value)}
                  placeholder="Enter password"
                  style={{width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #374151', background: '#1f2937', color: 'white', fontSize: '16px'}}
                  required
                />
              </div>
              <button
                type="submit"
                style={{width: '100%', padding: '14px', background: '#b8860b', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer'}}
              >
                Login to Dashboard
              </button>
            </form>
          </div>
        </div>
      )}
      
      <SignInModal 
        visible={showSignInModal}
        onClose={() => setShowSignInModal(false)}
        onAdminSuccess={(token, username) => {
          if (token) {
            console.log('🎯 Admin login success, setting up admin state...');
            
            // Store token and set admin state
            localStorage.setItem('admin_token', token);
            if (username) {
              localStorage.setItem('admin_username', username);
              setLoggedInAdminUsername(username); // CRITICAL: Set username for role-based filtering
              console.log('✅ Admin username set:', username);
            }
            setIsAdminLoggedIn(true);
            setCurrentPage('admin-portal');
            setActiveTab('dashboard'); // Ensure dashboard tab is active
            
            // Force immediate product reload for admin
            setTimeout(async () => {
              console.log('🔄 Force loading products for admin...');
              setIsAdminLoggedIn(true); // Ensure state is set
              await loadProducts(); // Force load products with admin token
            }, 100);
          }
        }}
      />

      {selectedProduct && (
        <div className="modal-overlay" onClick={() => setSelectedProduct(null)}>
          <div className="modal-content product-view-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedProduct(null)}>×</button>
            
            <div className="product-view-content">
              <div className="product-media-section">
                {(() => {
                  let selectedVariation = null;
                  
                  // Use state variables instead of DOM queries
                  const selectedSize = selectedVariationSize;
                  const selectedColor = selectedVariationColor;
                  
                  // If product has variations, find the matching variation
                  if (selectedProduct.variations && selectedProduct.variations.length > 0) {
                    if (selectedProduct.measurement_unit === 'per_square_meter') {
                      // For square meter products: match by color only (case-insensitive)
                      const searchColor = (selectedColor || "").trim().toLowerCase();
                      selectedVariation = selectedProduct.variations.find(v => {
                        const variationColor = (v.color || "").trim().toLowerCase();
                        return (searchColor && variationColor && searchColor === variationColor) || (!searchColor && !variationColor);
                      });
                    } else {
                      // For size-based: match by size and color (case-insensitive)
                      const searchColor = (selectedColor || "").trim().toLowerCase();
                      selectedVariation = selectedProduct.variations.find(v => {
                        const variationColor = (v.color || "").trim().toLowerCase();
                        const colorMatch = (searchColor && variationColor && searchColor === variationColor) || (!searchColor && !variationColor);
                        const sizeMatch = (!selectedSize || v.size === selectedSize);
                        return sizeMatch && colorMatch;
                      });
                    }
                  }
                  
                  // Get all available media for this product/variation
                  const media = getAllProductMedia(selectedProduct, selectedVariation);
                  
                  return (
                    <div className="product-media-gallery">
                      {/* Main Media Display */}
                      <div className="main-media-container">
                        {media.images.length > 0 ? (
                          <>
                            {/* Current Image */}
                            <div className="main-image-container">
                              <img 
                                src={media.images[currentImageIndex] || media.images[0]} 
                                alt={`${selectedProduct.name} - Image ${currentImageIndex + 1}`}
                                className="main-product-image"
                                style={{
                                  width: '100%',
                                  height: 'auto',
                                  maxHeight: '400px',
                                  objectFit: 'contain',
                                  borderRadius: '8px'
                                }}
                                onError={(e) => {
                                  console.error('Image load error:', e);
                                  e.target.style.display = 'none';
                                }}
                              />
                              
                              {/* Image Navigation Arrows */}
                              {media.images.length > 1 && (
                                <>
                                  <button 
                                    className="media-nav-btn prev-btn"
                                    onClick={() => setCurrentImageIndex(prev => 
                                      prev > 0 ? prev - 1 : media.images.length - 1
                                    )}
                                    style={{
                                      position: 'absolute',
                                      left: '10px',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      background: 'rgba(0,0,0,0.7)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '50%',
                                      width: '40px',
                                      height: '40px',
                                      cursor: 'pointer',
                                      fontSize: '18px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    ‹
                                  </button>
                                  
                                  <button 
                                    className="media-nav-btn next-btn"
                                    onClick={() => setCurrentImageIndex(prev => 
                                      prev < media.images.length - 1 ? prev + 1 : 0
                                    )}
                                    style={{
                                      position: 'absolute',
                                      right: '10px',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      background: 'rgba(0,0,0,0.7)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '50%',
                                      width: '40px',
                                      height: '40px',
                                      cursor: 'pointer',
                                      fontSize: '18px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    ›
                                  </button>
                                </>
                              )}
                              
                              {/* Image Counter */}
                              {media.images.length > 1 && (
                                <div style={{
                                  position: 'absolute',
                                  bottom: '10px',
                                  right: '10px',
                                  background: 'rgba(0,0,0,0.7)',
                                  color: 'white',
                                  padding: '3px 6px',
                                  borderRadius: '12px',
                                  fontSize: '12px'
                                }}>
                                  {currentImageIndex + 1} / {media.images.length}
                                </div>
                              )}
                            </div>
                            
                            {/* Image Thumbnails */}
                            {media.images.length > 1 && (
                              <div className="image-thumbnails" style={{
                                display: 'flex',
                                gap: '8px',
                                marginTop: '10px',
                                justifyContent: 'center',
                                flexWrap: 'wrap'
                              }}>
                                {media.images.map((img, index) => (
                                  <button
                                    key={index}
                                    onClick={() => setCurrentImageIndex(index)}
                                    style={{
                                      width: '60px',
                                      height: '60px',
                                      border: currentImageIndex === index ? '2px solid #d4af37' : '2px solid transparent',
                                      borderRadius: '4px',
                                      overflow: 'hidden',
                                      cursor: 'pointer',
                                      background: 'none',
                                      padding: 0
                                    }}
                                  >
                                    <img 
                                      src={img}
                                      alt={`Thumbnail ${index + 1}`}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                      }}
                                    />
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="product-image-placeholder" style={{
                            width: '100%',
                            height: '200px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#f8f9fa',
                            borderRadius: '8px',
                            border: '2px dashed #dee2e6'
                          }}>
                            <span style={{fontSize: '48px', marginBottom: '10px'}}>📷</span>
                            <p style={{color: '#6c757d', margin: 0}}>No Image Available</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Video Player */}
                      {media.video && (
                        <div className="product-video-section" style={{
                          marginTop: '20px',
                          textAlign: 'center'
                        }}>
                          <h4 style={{color: '#d4af37', marginBottom: '10px', fontSize: '16px'}}>
                            🎬 Product Video
                          </h4>
                          <video 
                            controls 
                            style={{
                              width: '100%',
                              maxWidth: '400px',
                              height: 'auto',
                              borderRadius: '8px',
                              border: '2px solid #d4af37'
                            }}
                            preload="metadata"
                          >
                            <source src={media.video} type="video/mp4" />
                            <p style={{color: '#6c757d'}}>Your browser does not support the video tag.</p>
                          </video>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              
              <div className="product-details-section">
                <h2>{selectedProduct.name}</h2>
                <p className="product-description">{selectedProduct.description}</p>
                
                {/* Show variations if available */}
                {selectedProduct.variations && selectedProduct.variations.length > 0 && (
                  <>
                    {/* Conditional display based on measurement unit */}
                    {selectedProduct.measurement_unit === 'size' ? (
                      <>
                        {/* Size dropdown - only for products measured by size */}
                        <div className="product-option">
                          <label>SIZE</label>
                          <select 
                            className="product-select" 
                            id="size-selector"
                            value={selectedVariationSize || 'Select Size'}
                            onChange={(e) => {
                              const newSize = e.target.value === 'Select Size' ? null : e.target.value;
                              setSelectedVariationSize(newSize);
                              setSelectedVariationColor(null); // Reset color when size changes
                              setCurrentImageIndex(0); // Reset to first image when variation changes
                              
                              // Filter available colors based on selected size
                              if (newSize && selectedProduct?.variations) {
                                const colorsForSize = [...new Set(
                                  selectedProduct.variations
                                    .filter(v => v.size === newSize)
                                    .map(v => v.color)
                                    .filter(Boolean)
                                )];
                                setFilteredColors(colorsForSize);
                              } else {
                                // If no size selected, show all colors
                                setFilteredColors([...new Set(selectedProduct.variations.map(v => v.color).filter(Boolean))]);
                              }
                            }}
                          >
                            <option>Select Size</option>
                            {[...new Set(selectedProduct.variations.map(v => v.size))].filter(size => size).map((size, index) => (
                              <option key={index} value={size}>{size}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Square Meters input - only for products measured per square meter */}
                        <div className="product-option">
                          <label>SQUARE METERS</label>
                          <input 
                            type="number" 
                            min="1" 
                            step="0.1"
                            defaultValue="1" 
                            className="quantity-input" 
                            id="square-meters-input"
                            placeholder="Enter square meters needed"
                          />
                        </div>
                      </>
                    )}

                    {/* Color dropdown - filtered based on selected size */}
                    <div className="product-option">
                      <label>COLOR</label>
                      <select 
                        className="product-select" 
                        id="color-selector"
                        value={selectedVariationColor || 'Select Color'}
                        onChange={(e) => {
                          const newColor = e.target.value === 'Select Color' ? null : e.target.value;
                          setSelectedVariationColor(newColor);
                          setCurrentImageIndex(0); // Reset to first image when variation changes
                        }}
                      >
                        <option>Select Color</option>
                        {(filteredColors.length > 0 ? filteredColors : [...new Set(selectedProduct.variations.map(v => v.color))].filter(color => color)).map((color, index) => (
                          <option key={index} value={color}>
                            {color}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
                
                {/* Show Contact for Price message */}
                <div className="product-price">
                  <p style={{fontSize: '14px', color: '#666', marginTop: '8px'}}>
                    Please use the Contact Seller button below to get a quote for this product
                  </p>
                </div>
                
                {/* Quantity input only for size-based products */}
                {selectedProduct.measurement_unit === 'size' && (
                  <div className="product-option">
                    <label>QUANTITY</label>
                    <input type="number" min="1" defaultValue="1" className="quantity-input" id="quantity-input" />
                  </div>
                )}
                
                <button 
                  className="action-btn contact-seller-btn"
                  onClick={() => {
                    console.log('CONTACT SELLER clicked for product:', selectedProduct);
                    
                    // Get selected values based on measurement unit
                    const colorSelector = document.getElementById('color-selector');
                    const selectedColor = colorSelector?.value !== 'Select Color' ? colorSelector?.value : '';
                    
                    let selectedSize = '';
                    let selectedQuantity = 1;
                    let selectedSquareMeters = 1;
                    
                    if (selectedProduct.measurement_unit === 'size') {
                      const sizeSelector = document.getElementById('size-selector');
                      const quantityInput = document.getElementById('quantity-input');
                      selectedSize = sizeSelector?.value !== 'Select Size' ? sizeSelector?.value : '';
                      selectedQuantity = parseInt(quantityInput?.value || '1');
                    } else {
                      const squareMetersInput = document.getElementById('square-meters-input');
                      selectedSquareMeters = parseFloat(squareMetersInput?.value || '1');
                    }
                    
                    console.log('Selected values:', { selectedSize, selectedColor, selectedQuantity, selectedSquareMeters });
                    
                    // Find the specific variation if size and color are selected
                    let selectedVariation = null;
                    if (selectedProduct.variations && (selectedSize || selectedColor)) {
                      selectedVariation = selectedProduct.variations.find(v => 
                        (!selectedSize || v.size === selectedSize) && 
                        (!selectedColor || v.color === selectedColor)
                      );
                    }
                    
                    console.log('Found variation:', selectedVariation);
                    
                    // Set enhanced product context for the contact form
                    const enhancedProductContext = {
                      ...selectedProduct,
                      selectedSize,
                      selectedColor,
                      selectedQuantity,
                      selectedSquareMeters,
                      selectedVariation,
                      retailPrice: selectedVariation?.retail_price || selectedProduct.retail_price,
                      wholesalePrice: selectedVariation?.wholesale_price || selectedProduct.wholesale_price
                    };
                    
                    console.log('Setting contact product context:', enhancedProductContext);
                    setContactProductContext(enhancedProductContext);
                    setCurrentPage('contact');
                    setSelectedProduct(null);
                  }}
                >
                  CONTACT SELLER
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddProductModal && (
        <div className="modal-overlay" onClick={closeAddProductModal}>
          <div className="modal-content add-product-modal-advanced" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeAddProductModal}>×</button>
            <h2 className="modal-title-gold">{isEditMode ? 'EDIT PRODUCT' : 'ADD NEW PRODUCT'}</h2>
            
            <form className="add-product-form-advanced" onSubmit={handleAddProduct}>
              {/* Basic Product Info */}
              <div className="product-basic-section">
                <div className="form-field">
                  <input 
                    type="text" 
                    name="productName" 
                    placeholder="Product Name" 
                    className="dark-input" 
                    defaultValue={isEditMode ? products.find(p => p.id === editingProductId)?.name || '' : ''}
                    required 
                  />
                </div>
                <div className="form-field">
                  <textarea 
                    name="description" 
                    placeholder="Description" 
                    className="dark-input" 
                    rows="3"
                    defaultValue={isEditMode ? products.find(p => p.id === editingProductId)?.description || '' : ''}
                  ></textarea>
                </div>
                <div className="form-field">
                  <input 
                    type="text" 
                    name="design" 
                    placeholder="Design" 
                    className="dark-input"
                    defaultValue={isEditMode ? products.find(p => p.id === editingProductId)?.design || '' : ''}
                  />
                </div>
              </div>

              {/* Measurement Unit */}
              <div className="measurement-section">
                <h4 className="section-title-gold">MEASUREMENT UNIT:</h4>
                <select 
                  name="measurementUnit" 
                  className="measurement-select"
                  defaultValue={isEditMode ? (products.find(p => p.id === editingProductId)?.measurement_unit === 'per_square_meter' ? 'per_square_meter' : 'size') : 'size'}
                  onChange={(e) => setCurrentMeasurementUnit(e.target.value)}
                >
                  <option value="size">By Size</option>
                  <option value="per_square_meter">Per Square Meter</option>
                </select>
              </div>

              {/* Product Image */}
              <div className="image-section">
                <h4 className="section-title-gold">Product Images:</h4>
                <div className="image-upload-container">
                  <input 
                    type="file" 
                    name="productImages" 
                    accept="image/jpeg,image/jpg,image/png,.jpeg,.jpg,.png" 
                    className="file-input" 
                    multiple
                    onChange={handleMultipleImageUpload}
                  />
                  <div className="image-preview-container">
                    {uploadedImages.map((image, index) => (
                      <div key={index} className="image-preview">
                        <img src={image.preview} alt={`Product ${index + 1}`} style={{width: '80px', height: '80px', objectFit: 'cover', borderRadius: '4px'}} />
                        <button type="button" onClick={() => removeImage(index)} className="remove-image-btn">×</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Product Category */}
              <div className="category-section">
                <h4 className="section-title-gold">CATEGORY:</h4>
                <div className="category-controls">
                  <select 
                    name="category" 
                    className="measurement-select category-select" 
                    required
                    defaultValue={isEditMode ? products.find(p => p.id === editingProductId)?.category || '' : ''}
                  >
                    <option value="">Select Category</option>
                    {categories && categories.length > 0 ? (
                      categories.map(category => (
                        <option 
                          key={category.id} 
                          value={category.name}
                        >
                          {category.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No categories available</option>
                    )}
                  </select>
                  <button 
                    type="button" 
                    className="btn-add"
                    onClick={() => {
                      const categoryName = prompt('Enter new category name:');
                      if (categoryName) {
                        handleAddCategory(categoryName);
                      }
                    }}
                  >
                    + Add
                  </button>
                  <button 
                    type="button" 
                    className="btn-delete"
                    onClick={() => {
                      const categorySelect = document.querySelector('select[name="category"]');
                      const selectedCategory = categorySelect.value;
                      if (selectedCategory) {
                        handleDeleteCategory(selectedCategory);
                      } else {
                        alert('Please select a category to delete');
                      }
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Collection */}
              <div className="collection-section">
                <h4 className="section-title-gold">Collection:</h4>
                <div className="collection-controls">
                  <input 
                    type="text" 
                    name="collection" 
                    defaultValue={isEditMode ? products.find(p => p.id === editingProductId)?.collection || 'General' : 'General'}
                    className="collection-input" 
                  />
                  <button type="button" className="btn-add">+ Add</button>
                  <button type="button" className="btn-delete">Delete</button>
                </div>
              </div>

              {/* Color Variations & Pricing */}
              <div className="variations-section">
                <h4 className="section-title-gold">COLOR VARIATIONS & PRICING</h4>
                
                <div>
                  <>
                    {productVariations.map((variation, index) => (
                      <div key={variation.id} className="variation-item">
                        <div className="variation-header">
                      Color Variation #{index + 1}
                      {productVariations.length > 1 && (
                        <button 
                          type="button" 
                          className="btn-remove-variation"
                          onClick={() => removeProductVariation(variation.id)}
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <div className="variation-fields">
                      <div className="variation-row-multiline">
                        <div className="variation-row-line">
                          {/* Dynamic first field based on measurement unit */}
                          {currentMeasurementUnit === 'per_square_meter' ? (
                            <input 
                              type="number" 
                              placeholder="Square Meters" 
                              className="variation-field-wide"
                              step="0.01"
                              value={variation.squareMeters || ''}
                              onChange={(e) => updateProductVariation(variation.id, 'squareMeters', e.target.value)}
                            />
                          ) : (
                            <input 
                              type="text" 
                              placeholder="Size" 
                              className="variation-field-wide"
                              value={variation.size || ''}
                              onChange={(e) => updateProductVariation(variation.id, 'size', e.target.value)}
                            />
                          )}
                          <input 
                            type="text" 
                            placeholder="Color" 
                            className="variation-field-wide"
                            value={variation.color || ''}
                            onChange={(e) => updateProductVariation(variation.id, 'color', e.target.value)}
                          />
                          <input 
                            type="text" 
                            placeholder="Storage Location" 
                            className="variation-field-wide"
                            value={variation.storageLocation || ''}
                            onChange={(e) => updateProductVariation(variation.id, 'storageLocation', e.target.value)}
                          />
                          <input 
                            type="number" 
                            placeholder="Wholesale" 
                            className="variation-field-wide" 
                            step="0.01"
                            value={variation.wholesale || ''}
                            onChange={(e) => updateProductVariation(variation.id, 'wholesale', e.target.value)}
                          />
                        </div>
                        <div className="variation-row-line">
                          <input 
                            type="number" 
                            placeholder="Retail" 
                            className="variation-field-wide" 
                            step="0.01"
                            value={variation.retail || ''}
                            onChange={(e) => updateProductVariation(variation.id, 'retail', e.target.value)}
                          />
                          <input 
                            type="number" 
                            placeholder="Fitter" 
                            className="variation-field-wide" 
                            step="0.01"
                            value={variation.fitter || ''}
                            onChange={(e) => updateProductVariation(variation.id, 'fitter', e.target.value)}
                          />
                          <input 
                            type="number" 
                            placeholder="Cost Price" 
                            className="variation-field-wide" 
                            step="0.01"
                            value={variation.cost || ''}
                            onChange={(e) => updateProductVariation(variation.id, 'cost', e.target.value)}
                          />
                        </div>
                        <div className="variation-row-line">
                          {/* Dynamic stock field based on measurement unit */}
                          {currentMeasurementUnit === 'per_square_meter' ? null : (
                            <input 
                              type="number" 
                              placeholder="Quantity Available" 
                              className="variation-field-wide"
                              value={variation.stock || ''}
                              onChange={(e) => updateProductVariation(variation.id, 'stock', e.target.value)}
                            />
                          )}
                        </div>
                      </div>
                      
                      {/* Enhanced Media Upload Section - 4 Images + 1 Video */}
                      <div style={{
                          background:'rgba(248, 249, 250, 0.8)', 
                          padding:'2rem', 
                          borderRadius:'12px', 
                          border:'1px solid rgba(220,220,220,0.5)',
                          marginBottom:'2rem',
                          boxShadow:'0 2px 8px rgba(0,0,0,0.1)',
                          width: '100%'
                        }}>
                          <h4 style={{
                            color:'#2c3e50', 
                            marginBottom:'2rem', 
                            fontSize:'1.4rem',
                            fontWeight:'600',
                            textAlign:'center',
                            borderBottom:'3px solid #3498db',
                            paddingBottom:'1rem'
                          }}>
                            📷 Product Media Gallery (4 Images + 1 Video)
                          </h4>
                          
                          {/* Image Upload Grid */}
                          <div style={{
                            display:'grid', 
                            gridTemplateColumns:'1fr 1fr', 
                            gap:'2rem', 
                            marginBottom:'2rem',
                            padding:'1rem'
                          }}>
                            {[0, 1, 2, 3].map(imageIndex => (
                              <div key={imageIndex} style={{
                                border:'2px dashed #bdc3c7', 
                                borderRadius:'8px', 
                                padding:'2rem',
                                background:'white',
                                minHeight:'200px',
                                display:'flex',
                                flexDirection:'column',
                                alignItems:'center',
                                justifyContent:'center',
                                transition:'border-color 0.3s ease',
                                position:'relative',
                                ...(variation.images && variation.images[imageIndex] ? {
                                  border: '2px solid #3498db',
                                  background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)'
                                } : {})
                              }}>
                                <label style={{
                                  display:'block', 
                                  marginBottom:'0.5rem', 
                                  color:'#34495e', 
                                  fontSize:'0.8rem',
                                  fontWeight:'500',
                                  textAlign:'center'
                                }}>
                                  🖼️ Image {imageIndex + 1} {imageIndex === 0 ? '(Main)' : ''}
                                </label>
                                
                                {/* Image Preview */}
                                {variation.images && variation.images[imageIndex] && (
                                  <div style={{position:'relative', marginBottom:'0.5rem', textAlign:'center'}}>
                                    {typeof variation.images[imageIndex] === 'string' ? (
                                      // Base64 string preview (existing image from database)
                                      <img 
                                        src={variation.images[imageIndex]}
                                        alt={`Image ${imageIndex + 1}`}
                                        style={{
                                          width:'80px', 
                                          height:'50px', 
                                          objectFit:'cover',
                                          border:'1px solid #74b9ff', 
                                          borderRadius:'4px',
                                          boxShadow:'0 1px 3px rgba(0,0,0,0.1)'
                                        }}
                                      />
                                    ) : (
                                      // File object preview (newly uploaded image)
                                      <img 
                                        src={URL.createObjectURL(variation.images[imageIndex])}
                                        alt={`Image ${imageIndex + 1}`}
                                        style={{
                                          width:'80px', 
                                          height:'50px', 
                                          objectFit:'cover',
                                          border:'1px solid #74b9ff', 
                                          borderRadius:'4px',
                                          boxShadow:'0 1px 3px rgba(0,0,0,0.1)'
                                        }}
                                      />
                                    )}
                                    <button 
                                      type="button"
                                      onClick={() => removeVariationImage(variation.id, imageIndex)}
                                      style={{
                                        position:'absolute', 
                                        top:'-6px', 
                                        right:'calc(50% - 48px)', 
                                        background:'#e74c3c', 
                                        color:'white', 
                                        border:'none', 
                                        borderRadius:'50%', 
                                        width:'18px', 
                                        height:'18px', 
                                        fontSize:'12px', 
                                        cursor:'pointer',
                                        fontWeight:'bold',
                                        boxShadow:'0 1px 3px rgba(0,0,0,0.2)'
                                      }}
                                      title="Remove image"
                                    >
                                      ×
                                    </button>
                                  </div>
                                )}
                                
                                <input 
                                  type="file" 
                                  accept="image/jpeg,image/jpg,image/png,.jpeg,.jpg,.png" 
                                  id={`image-${variation.id}-${imageIndex}`}
                                  style={{ display: 'none' }}
                                  onChange={(e) => {
                                    if (e.target.files[0]) {
                                      updateVariationImage(variation.id, imageIndex, e.target.files[0]);
                                    }
                                  }}
                                />
                                <button 
                                  type="button" 
                                  onClick={() => document.getElementById(`image-${variation.id}-${imageIndex}`).click()}
                                  style={{
                                    width:'100%', 
                                    padding:'0.5rem', 
                                    background: variation.images && variation.images[imageIndex] ? '#27ae60' : '#3498db', 
                                    color:'white', 
                                    border:'none', 
                                    borderRadius:'4px', 
                                    fontSize:'0.7rem', 
                                    cursor:'pointer',
                                    fontWeight:'500',
                                    transition:'background-color 0.3s ease',
                                    boxShadow:'0 1px 3px rgba(0,0,0,0.1)'
                                  }}
                                >
                                  {variation.images && variation.images[imageIndex] ? '🔄 Change' : '📤 Upload'}
                                </button>
                                {variation.images && variation.images[imageIndex] && (
                                  <div style={{marginTop:'0.5rem', fontSize:'0.8rem', color:'#27ae60', textAlign:'center', fontWeight:'500'}}>
                                    ✅ {typeof variation.images[imageIndex] === 'string' ? `Image ${imageIndex + 1}` : variation.images[imageIndex].name}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          
                          {/* Video Upload Section */}
                          <div style={{
                            border:'2px dashed #9b59b6', 
                            borderRadius:'8px', 
                            padding:'1rem', 
                            background:'linear-gradient(135deg, rgba(155, 89, 182, 0.05), rgba(142, 68, 173, 0.05))',
                            textAlign:'center'
                          }}>
                            <label style={{
                              display:'block', 
                              marginBottom:'0.5rem', 
                              color:'#8e44ad', 
                              fontSize:'0.9rem',
                              fontWeight:'600'
                            }}>
                              🎥 Product Video (Optional)
                            </label>
                            
                            {variation.video && (
                              <div style={{position:'relative', marginBottom:'0.5rem', display:'inline-block'}}>
                                {typeof variation.video === 'string' ? (
                                  // Base64 string preview (existing video from database)
                                  <div style={{
                                    width:'120px', 
                                    height:'60px', 
                                    background:'linear-gradient(135deg, #a29bfe, #6c5ce7)', 
                                    border:'1px solid #a29bfe', 
                                    borderRadius:'4px', 
                                    display:'flex', 
                                    alignItems:'center', 
                                    justifyContent:'center', 
                                    fontSize:'0.7rem', 
                                    color:'white',
                                    fontWeight:'500',
                                    boxShadow:'0 1px 3px rgba(0,0,0,0.1)'
                                  }}>
                                    🎬 Video Loaded
                                  </div>
                                ) : (
                                  // File object preview (newly uploaded video)
                                  <div style={{
                                    width:'120px', 
                                    height:'60px', 
                                    background:'linear-gradient(135deg, #a29bfe, #6c5ce7)', 
                                    border:'1px solid #a29bfe', 
                                    borderRadius:'4px', 
                                    display:'flex', 
                                    alignItems:'center', 
                                    justifyContent:'center', 
                                    fontSize:'0.7rem', 
                                    color:'white',
                                    fontWeight:'500',
                                    boxShadow:'0 1px 3px rgba(0,0,0,0.1)'
                                  }}>
                                    🎬 {variation.video.name.substring(0, 10)}...
                                  </div>
                                )}
                                <button 
                                  type="button"
                                  onClick={() => removeVariationVideo(variation.id)}
                                  style={{
                                    position:'absolute', 
                                    top:'-6px', 
                                    right:'-6px', 
                                    background:'#e74c3c', 
                                    color:'white', 
                                    border:'none', 
                                    borderRadius:'50%', 
                                    width:'20px', 
                                    height:'20px', 
                                    fontSize:'12px', 
                                    cursor:'pointer',
                                    fontWeight:'bold',
                                    boxShadow:'0 1px 3px rgba(0,0,0,0.2)'
                                  }}
                                  title="Remove video"
                                >
                                  ×
                                </button>
                              </div>
                            )}
                            
                            <input 
                              type="file" 
                              accept="video/mp4,video/avi,video/mov,video/wmv,.mp4,.avi,.mov,.wmv" 
                              id={`video-${variation.id}`}
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  updateVariationVideo(variation.id, e.target.files[0]);
                                }
                              }}
                            />
                            <button 
                              type="button" 
                              onClick={() => document.getElementById(`video-${variation.id}`).click()}
                              style={{
                                padding:'0.5rem 1rem', 
                                background: variation.video ? '#27ae60' : '#9b59b6', 
                                color:'white', 
                                border:'none', 
                                borderRadius:'4px', 
                                fontSize:'0.8rem', 
                                cursor:'pointer',
                                fontWeight:'500',
                                marginRight:'0.5rem',
                                boxShadow:'0 1px 3px rgba(0,0,0,0.1)',
                                transition:'all 0.3s ease'
                              }}
                            >
                              {variation.video ? '🔄 Change Video' : '🎬 Upload Video'}
                            </button>
                            {variation.video && (
                              <div style={{
                                marginTop:'0.5rem', 
                                fontSize:'0.7rem', 
                                color:'#27ae60', 
                                fontWeight:'500'
                              }}>
                                ✅ {typeof variation.video === 'string' ? 'Video Loaded' : variation.video.name}
                              </div>
                            )}
                            <div style={{
                              marginTop:'0.5rem', 
                              fontSize:'0.6rem', 
                              color:'#7f8c8d',
                              fontStyle:'italic'
                            }}>
                              📋 Formats: MP4, AVI, MOV, WMV • Max: 100MB
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    ))}
                    
                    <button type="button" className="btn-add-variation" onClick={addProductVariation}>
                      + Add Another Area
                    </button>
                  </>
                </div>
              </div>
              
              <div className="form-actions-advanced">
                <button type="button" className="btn secondary" onClick={closeAddProductModal}>
                  CANCEL
                </button>
                <button type="submit" className="btn primary">
                  {isEditMode ? 'UPDATE PRODUCT' : 'ADD PRODUCT'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Catalog Modal */}
      {showCreateCatalogModal && (
        <div className="modal-overlay" onClick={() => setShowCreateCatalogModal(false)}>
          <div className="modal-content create-catalog-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowCreateCatalogModal(false)}>×</button>
            <h2>Create Product PDF</h2>
            <form className="create-catalog-form" onSubmit={handleGeneratePDF}>
              <div className="form-group">
                <label>SELECT PRODUCT</label>
                <select className="catalog-select" name="selectedProduct" required>
                  <option value="">Choose a product...</option>
                  <option value="Premium Luxury Carpet">Premium Luxury Carpet</option>
                  <option value="LVT Click Flooring">LVT Click Flooring</option>
                  <option value="Persian Silk Rug">Persian Silk Rug</option>
                  <option value="All Products">All Products</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>PRICE TYPE</label>
                <div className="checkbox-group">
                  <label className="checkbox-item">
                    <input type="checkbox" name="retailPrices" defaultChecked />
                    <span className="checkmark"></span>
                    RETAIL PRICES
                  </label>
                  <label className="checkbox-item">
                    <input type="checkbox" name="wholesalePrices" />
                    <span className="checkmark"></span>
                    WHOLESALE PRICES
                  </label>
                </div>
              </div>
              
              <div className="form-actions">
                <button type="button" className="btn secondary" onClick={() => setShowCreateCatalogModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn primary">
                  Generate PDF
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Gallery Item Modal */}
      {showAddGalleryModal && (
        <div className="modal-overlay" onClick={() => setShowAddGalleryModal(false)}>
          <div className="add-gallery-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowAddGalleryModal(false)}>×</button>
            
            <h2>Add Gallery Item</h2>
            <p className="modal-subtitle">Add images or videos of your previous installations and projects</p>
            
            <form className="add-gallery-form" onSubmit={handleAddGalleryItem}>
              <div className="form-group">
                <label>Item Type *</label>
                <select name="itemType" className="form-control" required>
                  <option value="">Select type...</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Title *</label>
                <input 
                  type="text" 
                  name="title" 
                  className="form-control" 
                  required 
                  placeholder="Enter project title"
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea 
                  name="description" 
                  className="form-control" 
                  rows="3" 
                  placeholder="Describe the project or installation"
                />
              </div>
              
              <div className="form-group">
                <label>Category *</label>
                <select name="category" className="form-control" required>
                  <option value="">Select category...</option>
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                  <option value="restoration">Restoration</option>
                  <option value="installation">Installation</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Location</label>
                <input 
                  type="text" 
                  name="location" 
                  className="form-control" 
                  placeholder="Project location (e.g., London, UK)"
                />
              </div>
              
              <div className="form-group">
                <label>Upload File *</label>
                <input 
                  type="file" 
                  name="mediaFile" 
                  className="form-control" 
                  accept="image/*,video/*" 
                  required 
                />
                <small className="form-help">Supported formats: JPG, PNG, GIF (max 800MB) | MP4, MOV (max 2GB)</small>
              </div>
              
              <div className="form-actions">
                <button type="button" className="btn secondary" onClick={() => setShowAddGalleryModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn primary">
                  Add to Gallery
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddCustomerModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Add New Customer</h2>
              <button className="close-modal" onClick={closeAddCustomerModal}>×</button>
            </div>
            <form onSubmit={handleAddCustomer} className="admin-form">
              <div className="form-group">
                <label>Customer Name *</label>
                <input 
                  type="text" 
                  className="form-control" 
                  required 
                  placeholder="Enter customer name"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>Company Name</label>
                <input 
                  type="text" 
                  className="form-control" 
                  placeholder="Enter company name"
                  value={newCustomer.company_name}
                  onChange={(e) => setNewCustomer({...newCustomer, company_name: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>Customer Type *</label>
                <select 
                  className="form-control" 
                  required
                  value={newCustomer.customer_type}
                  onChange={(e) => setNewCustomer({...newCustomer, customer_type: e.target.value})}
                >
                  <option value="wholesale">Wholesale</option>
                  <option value="fitter">Fitter</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Address</label>
                <textarea 
                  className="form-control" 
                  rows="3" 
                  placeholder="Enter customer address"
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({...newCustomer, address: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>Contact Number *</label>
                <input 
                  type="tel" 
                  className="form-control" 
                  required 
                  placeholder="Enter contact number"
                  value={newCustomer.contact_number}
                  onChange={(e) => setNewCustomer({...newCustomer, contact_number: e.target.value})}
                />
              </div>
              
              <div className="form-actions">
                <button type="button" className="btn secondary" onClick={closeAddCustomerModal}>
                  Cancel
                </button>
                <button type="submit" className="btn primary">
                  Add Customer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Order Modal */}
      {showCreateOrderModal && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h2>{newOrder.isEditMode ? `Edit Order - #${newOrder.editOrderId?.substring(0, 8) || 'N/A'}` : 'Create New Order'}</h2>
              <button className="close-modal" onClick={closeCreateOrderModal}>×</button>
            </div>
            <form onSubmit={handleCreateOrder} className="admin-form">
              
              {/* Sale Type */}
              <div className="form-row">
                <div className="form-group">
                  <label>Sale Type *</label>
                  <select 
                    className="form-control" 
                    required
                    value={newOrder.sale_type}
                    onChange={(e) => {
                      const newSaleType = e.target.value;
                      
                      // Recalculate prices for all existing order items
                      const updatedItems = (newOrder.order_items || []).map(item => {
                        const product = products.find(p => p.id === item.product_id);
                        if (!product) return item;
                        
                        const variation = product.variations?.find(v => {
                          if (product.measurement_unit === "per_square_meter") {
                            const itemColor = (item.color || "").trim().toLowerCase();
                            const varColor = (v.color || "").trim().toLowerCase();
                            return itemColor === varColor;
                          } else {
                            return v.size === item.size && 
                                   (v.color || "").trim().toLowerCase() === (item.color || "").trim().toLowerCase();
                          }
                        });
                        
                        if (!variation) return item;
                        
                        // Get new price based on sale type
                        let newUnitPrice = null;
                        switch (newSaleType) {
                          case 'wholesale':
                            newUnitPrice = variation.wholesale_price ?? product.wholesale_price ?? null;
                            break;
                          case 'retail':
                            newUnitPrice = variation.retail_price ?? product.retail_price ?? null;
                            break;
                          case 'fitter':
                            newUnitPrice = variation.fitter_price ?? product.fitter_price ?? null;
                            break;
                          default:
                            newUnitPrice = variation.retail_price ?? product.retail_price ?? null;
                        }

                        if (newUnitPrice === null || newUnitPrice === undefined) {
                          alert('Missing price');
                          return { ...item, unit_price: 0, total_price: 0, _missing_price: true };
                        }
                        
                        const up = parseFloat(parseFloat(newUnitPrice).toFixed(2));
                        const qty = product.measurement_unit === 'per_square_meter' ? parseFloat(item.sqm || item.quantity || 0) : parseFloat(item.quantity || 0);
                        return {
                          ...item,
                          unit_price: up,
                          total_price: parseFloat((up * qty).toFixed(2)),
                          _missing_price: false
                        };
                      });

                      // Update current form item price as well, if a product is selected
                      let updatedCurrent = { ...currentOrderItem };
                      if (currentOrderItem?.product_id) {
                        const selProduct = products.find(p => p.id === currentOrderItem.product_id);
                        if (selProduct) {
                          // Find variation by measurement unit rules
                          let vSel = null;
                          if (currentOrderItem.measurement_unit === 'per_square_meter') {
                            const c = (currentOrderItem.color || '').trim().toLowerCase();
                            vSel = selProduct.variations?.find(v => (v.color || '').trim().toLowerCase() === c);
                          } else {
                            const c = (currentOrderItem.color || '').trim().toLowerCase();
                            vSel = selProduct.variations?.find(v => v.size === currentOrderItem.size && (v.color || '').trim().toLowerCase() === c);
                          }
                          if (vSel) {
                            let price = null;
                            switch (newSaleType) {
                              case 'wholesale': price = vSel.wholesale_price ?? selProduct.wholesale_price ?? null; break;
                              case 'retail': price = vSel.retail_price ?? selProduct.retail_price ?? null; break;
                              case 'fitter': price = vSel.fitter_price ?? selProduct.fitter_price ?? null; break;
                              default: price = vSel.retail_price ?? selProduct.retail_price ?? null;
                            }
                            if (price === null || price === undefined) {
                              alert('Missing price');
                              updatedCurrent = { ...updatedCurrent, unit_price: 0, total_price: 0, _missing_price: true };
                            } else {
                              const upc = parseFloat(parseFloat(price).toFixed(2));
                              const qtyc = selProduct.measurement_unit === 'per_square_meter' ? parseFloat(updatedCurrent.sqm || updatedCurrent.quantity || 0) : parseFloat(updatedCurrent.quantity || 0);
                              updatedCurrent = { ...updatedCurrent, unit_price: upc, total_price: parseFloat((upc * qtyc).toFixed(2)), _missing_price: false };
                            }
                          }
                        }
                      }

                      // Apply updates atomically
                      setNewOrder(prev => ({ ...prev, sale_type: newSaleType, order_items: updatedItems }));
                      setCurrentOrderItem(updatedCurrent);
                    }}
                  >
                    <option value="retail">Retail</option>
                    <option value="wholesale">Wholesale</option>
                    <option value="fitter">Fitter</option>
                  </select>
                </div>
                
                {/* Select Customer */}
                <div className="form-group">
                  <label>Select Customer *</label>
                  <select 
                    className="form-control" 
                    required
                    value={newOrder.customer_id}
                    onChange={async (e) => {
                      const selectedCustomerId = e.target.value;
                      
                      // Find the selected customer to get their type
                      const selectedCustomer = customers.find(c => c.id === selectedCustomerId);
                      let saleType = 'retail'; // Default for guest
                      
                      if (selectedCustomer && selectedCustomer.customer_type) {
                        saleType = selectedCustomer.customer_type; // 'wholesale' or 'fitter'
                      }
                      
                      // Fetch calculated balance from backend (uses snapshot memory if available)
                      let calculatedOldBalance = 0;
                      if (selectedCustomerId && selectedCustomerId !== 'guest') {
                        try {
                          const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
                          // Use credit-accounts-balance endpoint - pulls directly from Credit Accounts source
                          const response = await axios.get(`${API}/api/customers/${selectedCustomerId}/credit-accounts-balance`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          calculatedOldBalance = response.data.credit_accounts_balance || 0;
                          console.log('💰 Credit Accounts balance:', response.data);
                        } catch (error) {
                          console.error('Error fetching credit accounts balance:', error);
                          // Fallback to local calculation using creditAccountsOrders (same source as Credit Accounts tab)
                          const customerOutstandingOrders = (creditAccountsOrders || []).filter(o => 
                            o.customer_id === selectedCustomerId
                          );
                          
                          customerOutstandingOrders.forEach(order => {
                            // 💰 RULE 1: Trust payment_status - if marked as paid, skip it
                            const paymentStatus = (order.payment_status || '').toLowerCase().trim();
                            if (paymentStatus === 'paid' || paymentStatus === 'fully_paid') {
                              return; // Skip this order
                            }
                            
                            const grandTotal = parseFloat(order.grand_total || 0);
                            const cashPaid = parseFloat(order.cash_paid_amount || 0);
                            const bankPaid = parseFloat(order.bank_paid_amount || 0);
                            const creditPaid = parseFloat(order.credit_paid_amount || 0);
                            const totalPaid = cashPaid + bankPaid + creditPaid;
                            const outstanding = grandTotal - totalPaid;
                            if (outstanding > 0) {
                              calculatedOldBalance += outstanding;
                            }
                          });
                          
                          // Subtract account payments (lump sum)
                          const accountPaymentsData = customerAccountPayments[selectedCustomerId] || { total: 0 };
                          calculatedOldBalance = Math.max(0, calculatedOldBalance - accountPaymentsData.total);
                        }
                      }
                      
                      setNewOrder({
                        ...newOrder, 
                        customer_id: selectedCustomerId,
                        sale_type: saleType,
                        calculated_old_balance: Math.round(calculatedOldBalance * 100) / 100,
                        old_balance_override: null // Reset override when changing customer
                      });
                    }}
                  >
                    <option value="">Choose customer...</option>
                    <option value="guest">Guest Customer</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name} {customer.company_name ? `(${customer.company_name})` : ''} - {customer.customer_type ? customer.customer_type.charAt(0).toUpperCase() + customer.customer_type.slice(1) : 'Retail'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Custom Date - ONLY for zobayr (Owner) - allows backdating orders for stock taken yesterday */}
              {loggedInAdminUsername.toLowerCase() === 'zobayr' && !newOrder.isEditMode && (
                <div className="form-row" style={{background:'rgba(139,92,246,0.1)', padding:'1rem', borderRadius:'6px', margin:'0.5rem 0 1rem 0', border:'1px solid rgba(139,92,246,0.3)'}}>
                  <div className="form-group" style={{margin: 0}}>
                    <label style={{color:'#7c3aed', fontWeight:'600', display:'flex', alignItems:'center', gap:'0.5rem'}}>
                      Custom Bill Date (Owner Only)
                      <span style={{fontSize:'11px', fontWeight:'normal', color:'#666'}}>- Leave blank for today&apos;s date</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={newOrder.custom_date}
                      onChange={(e) => setNewOrder({...newOrder, custom_date: e.target.value})}
                      max={new Date().toISOString().split('T')[0]}
                      style={{
                        maxWidth:'200px',
                        border:'2px solid #7c3aed',
                        borderRadius:'6px',
                        padding:'0.5rem'
                      }}
                    />
                    <p style={{fontSize:'11px', color:'#666', marginTop:'0.5rem'}}>
                      Use this to record stock taken on previous days with the correct date.
                    </p>
                  </div>
                </div>
              )}

              {/* Guest Customer Details - Show only when "guest" is selected */}
              {newOrder.customer_id === 'guest' && (
                <div className="form-row" style={{background:'rgba(74,144,226,0.05)', padding:'1rem', borderRadius:'6px', margin:'1rem 0', border:'1px solid rgba(74,144,226,0.2)'}}>
                  <h4 style={{color:'#4a90e2', marginBottom:'1rem', fontSize:'1.1rem'}}>Guest Customer Details (Temporary)</h4>
                  <p style={{fontSize:'12px', color:'#666', marginBottom:'1rem'}}>
                    Note: These details will be used only for this order and invoice. The customer will NOT be saved to your customer database.
                  </p>
                  <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(250px, 1fr))', gap:'1rem'}}>
                    <div className="form-group">
                      <label>Customer Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter customer name"
                        value={newOrder.guest_customer_name || ''}
                        onChange={(e) => setNewOrder({...newOrder, guest_customer_name: e.target.value})}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone Number</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter phone number"
                        value={newOrder.guest_customer_phone || ''}
                        onChange={(e) => setNewOrder({...newOrder, guest_customer_phone: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label>Address</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter address"
                        value={newOrder.guest_customer_address || ''}
                        onChange={(e) => setNewOrder({...newOrder, guest_customer_address: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label>Postcode</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter postcode"
                        value={newOrder.guest_customer_postcode || ''}
                        onChange={(e) => setNewOrder({...newOrder, guest_customer_postcode: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Date Information - Dual dates for edit mode */}
              {newOrder.isEditMode && (
                <div className="form-row" style={{background:'rgba(220,220,220,0.1)', padding:'1rem', borderRadius:'6px', margin:'1rem 0', border:'1px solid rgba(220,220,220,0.3)'}}>
                  <h4 style={{color:'#666', marginBottom:'1rem', fontSize:'1.1rem'}}>Order Dates</h4>
                  <div style={{display:'flex', gap:'2rem', flexWrap:'wrap'}}>
                    <div className="form-group">
                      <label>Original Order Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={newOrder.original_date ? newOrder.original_date.split('T')[0] : ''}
                        readOnly
                        style={{background:'#f5f5f5', cursor:'not-allowed'}}
                        title="This is the original date when the order was first created"
                      />
                    </div>
                    <div className="form-group">
                      <label>Edit Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={newOrder.edit_date || new Date().toISOString().split('T')[0]}
                        onChange={(e) => setNewOrder({...newOrder, edit_date: e.target.value})}
                        title="This is the date when the order is being edited"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Service Options */}
              <div className="form-row" style={{background:'rgba(184,134,11,0.05)', padding:'1rem', borderRadius:'6px', margin:'1rem 0', border:'1px solid rgba(184,134,11,0.2)'}}>
                <h4 style={{color:'#b8860b', marginBottom:'1rem', fontSize:'1.1rem'}}>Additional Services</h4>
                
                <div style={{display:'flex', gap:'2rem', flexWrap:'wrap'}}>
                  <div className="form-group">
                    <label>Include Fitting</label>
                    <select 
                      className="form-control" 
                      value={newOrder.include_fitting}
                      onChange={(e) => {
                        const includeFitting = e.target.value;
                        const newFittingCharge = includeFitting === 'yes' ? (newOrder.fitting_charge || 0) : 0;
                        // IRONCLAD: Recalculate when toggling fitting
                        const calc = calculateIronclad(
                          newOrder.order_items,
                          newFittingCharge,
                          newOrder.cut_piece_charge,
                          newOrder.discount
                        );
                        setNewOrder({
                          ...newOrder, 
                          include_fitting: includeFitting,
                          fitting_charge: newFittingCharge,
                          vat_amount: calc.vatAmount,
                          grand_total: calc.grandTotal
                        });
                      }}
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                  
                  {/* Dynamic Fitting Charge Input */}
                  {newOrder.include_fitting === 'yes' && (
                    <div className="form-group">
                      <label>Fitting Charge (£)</label>
                      <input 
                        type="number" 
                        min="0" 
                        step="0.50"
                        placeholder="Enter fitting charge"
                        className="form-control"
                        value={newOrder.fitting_charge || ''}
                        onChange={(e) => {
                          const fittingCharge = parseFloat(e.target.value) || 0;
                          // IRONCLAD: Recalculate totals when fitting charge changes
                          const calc = calculateIronclad(
                            newOrder.order_items,
                            fittingCharge,
                            newOrder.cut_piece_charge,
                            newOrder.discount
                          );
                          setNewOrder({
                            ...newOrder,
                            fitting_charge: fittingCharge,
                            vat_amount: calc.vatAmount,
                            grand_total: calc.grandTotal
                          });
                        }}
                      />
                    </div>
                  )}
                  
                  <div className="form-group">
                    <label>Cut Piece</label>
                    <select 
                      className="form-control" 
                      value={newOrder.cut_piece}
                      onChange={(e) => {
                        const cutPiece = e.target.value;
                        const newCutCharge = cutPiece === 'yes' ? (newOrder.cut_piece_charge || 0) : 0;
                        // IRONCLAD: Recalculate when toggling cut piece
                        const calc = calculateIronclad(
                          newOrder.order_items,
                          newOrder.fitting_charge,
                          newCutCharge,
                          newOrder.discount
                        );
                        setNewOrder({
                          ...newOrder, 
                          cut_piece: cutPiece,
                          cut_piece_charge: newCutCharge,
                          vat_amount: calc.vatAmount,
                          grand_total: calc.grandTotal
                        });
                      }}
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                  
                  {/* Dynamic Cut Piece Charge Input */}
                  {newOrder.cut_piece === 'yes' && (
                    <div className="form-group">
                      <label>Cut Piece Charge (£)</label>
                      <input 
                        type="number" 
                        min="0" 
                        step="0.50"
                        placeholder="Enter cut piece charge"
                        className="form-control"
                        value={newOrder.cut_piece_charge || ''}
                        onChange={(e) => {
                          const cutPieceCharge = parseFloat(e.target.value) || 0;
                          // IRONCLAD: Recalculate totals when cut piece charge changes
                          const calc = calculateIronclad(
                            newOrder.order_items,
                            newOrder.fitting_charge,
                            cutPieceCharge,
                            newOrder.discount
                          );
                          setNewOrder({
                            ...newOrder,
                            cut_piece_charge: cutPieceCharge,
                            vat_amount: calc.vatAmount,
                            grand_total: calc.grandTotal
                          });
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Order Items Section */}
              <div className="order-items-section">
                <h4>Add Order Items</h4>
                <div style={{fontSize: '0.9rem', color: '#666', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <span>
                    {products.length > 0 ? (
                      `${getInStockProducts().length} products available (${products.length} total loaded)`
                    ) : (
                      'Loading products... Please wait.'
                    )}
                  </span>
                  {products.length === 0 && (
                    <button 
                      type="button"
                      onClick={async () => {
                        console.log('🔄 Manual product refresh requested from order modal');
                        setProductsLastUpdated(Date.now());
                        await loadProducts();
                      }}
                      style={{
                        padding: '0.3rem 0.8rem',
                        fontSize: '0.8rem',
                        background: '#1e40af',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      🔄 Load Products
                    </button>
                  )}
                </div>
                
                {/* Current Item Form */}
                <div className="order-item-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Select Product *</label>
                      <select 
                        className="form-control"
                        value={currentOrderItem.product_id}
                        onChange={(e) => handleProductSelection(e.target.value)}
                      >
                        <option value="">Choose product...</option>
                        {getInStockProducts().map(product => {
                          const totalStock = product.variations?.reduce((total, variation) => {
                            if (product.measurement_unit === 'per_square_meter') {
                              return total + (variation.square_meters || variation.square_meters_available || 0);
                            } else {
                              return total + (variation.stock_quantity || variation.quantity || 0);
                            }
                          }, 0) || 0;
                          return (
                            <option key={product.id} value={product.id}>
                              {product.name} ({product.measurement_unit === 'per_square_meter' ? 'Per m²' : 'By Size'}) - {product.measurement_unit === 'per_square_meter' ? parseFloat(totalStock).toFixed(2) : Math.round(totalStock)} {product.measurement_unit === 'per_square_meter' ? 'm²' : 'units'} available
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    
                    {currentOrderItem.product_id && (
                      <>
                        {/* Dynamic Size Field based on measurement unit */}
                        <div className="form-group">
                          {currentOrderItem.measurement_unit === 'per_square_meter' ? (
                            <div className="form-row">
                              <div className="form-group">
                                <label>Width *</label>
                                <div style={{display: 'flex', gap: '10px'}}>
                                  <input 
                                    type="number" 
                                    className="form-control"
                                    min="0.1"
                                    step="0.1"
                                    placeholder="Enter width"
                                    value={currentOrderItem.wide || ''}
                                    style={{flex: 2}}
                                    onChange={(e) => {
                                      const wideValue = parseFloat(e.target.value) || 0;
                                      const lengthValue = parseFloat(currentOrderItem.length) || 0;
                                      
                                      // Convert to meters for calculation
                                      const wideInMeters = convertToMeters(wideValue, currentOrderItem.wide_unit);
                                      const lengthInMeters = convertToMeters(lengthValue, currentOrderItem.length_unit);
                                      const totalSqm = Math.round((wideInMeters * lengthInMeters) * 100) / 100; // Round to 2 decimal places
                                      
                                      // Display format shows original units
                                      const wideDisplay = wideValue > 0 ? `${wideValue}${currentOrderItem.wide_unit === 'feet' ? 'FT' : 'M'}` : '';
                                      const lengthDisplay = lengthValue > 0 ? `${lengthValue}${currentOrderItem.length_unit === 'feet' ? 'FT' : 'M'}` : '';
                                      const sizeString = (wideValue > 0 && lengthValue > 0) ? `${wideDisplay}x${lengthDisplay}` : '';
                                      
                                      console.log('🔍 WIDTH CHANGE DEBUG:', {
                                        wideValue,
                                        lengthValue,
                                        wide_unit: currentOrderItem.wide_unit,
                                        length_unit: currentOrderItem.length_unit,
                                        wideDisplay,
                                        lengthDisplay,
                                        sizeString
                                      });
                                      
                                      setCurrentOrderItem({
                                        ...currentOrderItem,
                                        wide: wideValue,
                                        length: lengthValue, // Ensure length is preserved
                                        sqm: totalSqm,
                                        size: sizeString,
                                        total_price: currentOrderItem.unit_price * totalSqm
                                      });
                                    }}
                                  />
                                  <select 
                                    className="form-control"
                                    value={currentOrderItem.wide_unit || 'meters'}
                                    style={{flex: 1}}
                                    onChange={(e) => {
                                      const newUnit = e.target.value;
                                      console.log('🔍 WIDTH UNIT CHANGE:', { 
                                        oldUnit: currentOrderItem.wide_unit, 
                                        newUnit: newUnit,
                                        currentWideValue: currentOrderItem.wide 
                                      });
                                      
                                      const wideValue = parseFloat(currentOrderItem.wide) || 0;
                                      const lengthValue = parseFloat(currentOrderItem.length) || 0;
                                      
                                      // Recalculate with new unit
                                      const wideInMeters = convertToMeters(wideValue, newUnit);
                                      const lengthInMeters = convertToMeters(lengthValue, currentOrderItem.length_unit || 'meters');
                                      const totalSqm = Math.round((wideInMeters * lengthInMeters) * 100) / 100;
                                      
                                      // Update display format
                                      const wideDisplay = wideValue > 0 ? `${wideValue}${newUnit === 'feet' ? 'FT' : 'M'}` : '';
                                      const lengthDisplay = lengthValue > 0 ? `${lengthValue}${(currentOrderItem.length_unit || 'meters') === 'feet' ? 'FT' : 'M'}` : '';
                                      const sizeString = (wideValue > 0 && lengthValue > 0) ? `${wideDisplay}x${lengthDisplay}` : '';
                                      
                                      console.log('🔍 WIDTH UNIT RESULT:', { wideDisplay, lengthDisplay, sizeString });
                                      
                                      setCurrentOrderItem({
                                        ...currentOrderItem,
                                        wide_unit: newUnit,
                                        sqm: totalSqm,
                                        size: sizeString,
                                        total_price: currentOrderItem.unit_price * totalSqm
                                      });
                                    }}
                                  >
                                    <option value="meters">Meters</option>
                                    <option value="feet">Feet</option>
                                  </select>
                                </div>
                              </div>
                              
                              <div className="form-group">
                                <label>Length *</label>
                                <div style={{display: 'flex', gap: '10px'}}>
                                  <input 
                                    type="number" 
                                    className="form-control"
                                    min="0.1"
                                    step="0.1"
                                    placeholder="Enter length"
                                    value={currentOrderItem.length || ''}
                                    style={{flex: 2}}
                                    onChange={(e) => {
                                      const lengthValue = parseFloat(e.target.value) || 0;
                                      const wideValue = parseFloat(currentOrderItem.wide) || 0;
                                      
                                      // Convert to meters for calculation
                                      const wideInMeters = convertToMeters(wideValue, currentOrderItem.wide_unit);
                                      const lengthInMeters = convertToMeters(lengthValue, currentOrderItem.length_unit);
                                      const totalSqm = Math.round((wideInMeters * lengthInMeters) * 100) / 100; // Round to 2 decimal places
                                      
                                      // Display format shows original units
                                      const wideDisplay = wideValue > 0 ? `${wideValue}${currentOrderItem.wide_unit === 'feet' ? 'FT' : 'M'}` : '';
                                      const lengthDisplay = lengthValue > 0 ? `${lengthValue}${currentOrderItem.length_unit === 'feet' ? 'FT' : 'M'}` : '';
                                      const sizeString = (wideValue > 0 && lengthValue > 0) ? `${wideDisplay}x${lengthDisplay}` : '';
                                      
                                      setCurrentOrderItem({
                                        ...currentOrderItem,
                                        wide: wideValue, // Ensure width is preserved
                                        length: lengthValue,
                                        sqm: totalSqm,
                                        size: sizeString,
                                        total_price: currentOrderItem.unit_price * totalSqm
                                      });
                                    }}
                                  />
                                  <select 
                                    className="form-control"
                                    value={currentOrderItem.length_unit}
                                    style={{flex: 1}}
                                    onChange={(e) => {
                                      const newUnit = e.target.value;
                                      const wideValue = parseFloat(currentOrderItem.wide) || 0;
                                      const lengthValue = parseFloat(currentOrderItem.length) || 0;
                                      
                                      // Recalculate with new unit
                                      const wideInMeters = convertToMeters(wideValue, currentOrderItem.wide_unit);
                                      const lengthInMeters = convertToMeters(lengthValue, newUnit);
                                      const totalSqm = Math.round((wideInMeters * lengthInMeters) * 100) / 100;
                                      
                                      // Update display format
                                      const wideDisplay = wideValue > 0 ? `${wideValue}${currentOrderItem.wide_unit === 'feet' ? 'FT' : 'M'}` : '';
                                      const lengthDisplay = lengthValue > 0 ? `${lengthValue}${newUnit === 'feet' ? 'FT' : 'M'}` : '';
                                      const sizeString = (wideValue > 0 && lengthValue > 0) ? `${wideDisplay}x${lengthDisplay}` : '';
                                      
                                      setCurrentOrderItem({
                                        ...currentOrderItem,
                                        length_unit: newUnit,
                                        sqm: totalSqm,
                                        size: sizeString,
                                        total_price: currentOrderItem.unit_price * totalSqm
                                      });
                                    }}
                                  >
                                    <option value="meters">Meters</option>
                                    <option value="feet">Feet</option>
                                  </select>
                                </div>
                              </div>
                              
                              <div className="form-group">
                                <label>Total Square Meters</label>
                                <input 
                                  type="number" 
                                  className="form-control"
                                  value={currentOrderItem.sqm || 0}
                                  readOnly
                                  style={{backgroundColor: '#f5f5f5', cursor: 'not-allowed'}}
                                />
                                <small className="form-help">Automatically calculated: Wide × Length = {(currentOrderItem.wide || 0).toFixed(1)} × {(currentOrderItem.length || 0).toFixed(1)} = {(currentOrderItem.sqm || 0).toFixed(2)} m²</small>
                              </div>
                            </div>
                          ) : (
                            <>
                              <label>Size *</label>
                              <select 
                                className="form-control"
                                value={currentOrderItem.size}
                                onChange={(e) => handleSizeSelection(e.target.value)}
                              >
                                <option value="">Choose size...</option>
                                {availableSizes.map(size => (
                                  <option key={size} value={size}>{size}</option>
                                ))}
                              </select>
                            </>
                          )}
                        </div>
                        
                        <div className="form-group">
                          <label>Color *</label>
                          <select 
                            className="form-control"
                            value={currentOrderItem.color}
                            onChange={(e) => handleColorSelection(e.target.value)}
                          >
                            <option value="">Choose color...</option>
                            {availableColors.map(color => (
                              <option key={color} value={color}>{color}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {currentOrderItem.color && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>
                          {currentOrderItem.measurement_unit === 'per_square_meter' ? 'Square Meters *' : 'Quantity *'}
                        </label>
                        {(() => {
                          const selectedProduct = products.find(p => p.id === currentOrderItem.product_id);
                          const availableStock = getAvailableStock(selectedProduct, currentOrderItem.size, currentOrderItem.color);
                          return (
                            <>
                              <input 
                                type="number" 
                                className="form-control"
                                min="0.1"
                                max={availableStock}
                                step={currentOrderItem.measurement_unit === 'per_square_meter' ? '0.1' : '1'}
                                value={currentOrderItem.measurement_unit === 'per_square_meter' ? currentOrderItem.sqm : currentOrderItem.quantity}
                                onChange={(e) => {
                                  const requestedQty = parseFloat(e.target.value) || 0;
                                  const validation = validateOrderQuantity(selectedProduct, currentOrderItem.size, currentOrderItem.color, requestedQty);
                                  
                                  if (validation.valid) {
                                    handleQuantityChange(e.target.value);
                                  } else {
                                    alert(validation.message);
                                    // Set to maximum available
                                    handleQuantityChange(validation.maxAvailable.toString());
                                  }
                                }}
                              />
                              <small className="form-help stock-info" style={{display: 'block', marginTop: '0.5rem'}}>
                                Available: {availableStock} {currentOrderItem.measurement_unit === 'per_square_meter' ? 'm²' : 'units'}
                              </small>
                            </>
                          );
                        })()}
                      </div>
                      
                      <div className="form-group">
                        <label>Unit Price</label>
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                          <span style={{fontSize: '1.1rem', fontWeight: '600'}}>£</span>
                          <input 
                            type="number" 
                            className="form-control"
                            value={currentOrderItem.unit_price}
                            onChange={(e) => {
                              const newUnitPrice = parseFloat(e.target.value) || 0;
                              const newTotalPrice = currentOrderItem.measurement_unit === 'per_square_meter' 
                                ? newUnitPrice * (currentOrderItem.square_meters || 0)
                                : newUnitPrice * (currentOrderItem.quantity || 0);
                              
                              setCurrentOrderItem({
                                ...currentOrderItem,
                                unit_price: newUnitPrice,
                                total_price: newTotalPrice
                              });
                            }}
                            step="0.01"
                            min="0"
                            style={{flex: 1}}
                          />
                          {currentOrderItem.measurement_unit === 'per_square_meter' && (
                            <span style={{fontSize: '0.9rem', color: '#666'}}>/m²</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label>Total Price</label>
                        <input 
                          type="text" 
                          className="form-control"
                          value={`£${currentOrderItem.total_price.toFixed(2)}`}
                          readOnly
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>&nbsp;</label>
                        <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                          <button 
                            type="button" 
                            className="btn primary"
                            onClick={addItemToOrder}
                            disabled={!currentOrderItem.total_price}
                            title="Add item and clear form"
                          >
                            Add Item
                          </button>
                          <button 
                            type="button" 
                            style={{
                              background: '#059669',
                              color: 'white',
                              border: 'none',
                              padding: '0.5rem 1rem',
                              borderRadius: '4px',
                              cursor: currentOrderItem.total_price ? 'pointer' : 'not-allowed',
                              opacity: currentOrderItem.total_price ? 1 : 0.5,
                              fontSize: '0.9rem',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            onClick={addItemAndKeepProduct}
                            disabled={!currentOrderItem.total_price}
                            title="Add item and keep product selected for adding more colors/sizes"
                          >
                            <span style={{fontSize: '1.1rem', fontWeight: 'bold'}}>+</span> Add Another Variant
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Order Items List */}
                {newOrder.order_items.length > 0 && (
                  <div className="order-items-list">
                    <h5>Order Items ({newOrder.order_items.length})</h5>
                    <div className="items-table">
                      <div className="items-header">
                        <span>Product</span>
                        <span>Size</span>
                        <span>Color</span>
                        <span>Qty/SQM</span>
                        <span>Unit Price</span>
                        <span>Total</span>
                        <span style={{textAlign: 'center'}}>VAT</span>
                        <span>Action</span>
                      </div>
                      {newOrder.order_items.map((item, itemIndex) => (
                        <div key={`order-item-${itemIndex}-${item.product_id}`} className="items-row">
                          <span>{item.product_name}</span>
                          <span>
                            {
                              // For square meter products with dimensions, display width x length with units
                              (item.wide && item.length && item.wide_unit && item.length_unit) ? 
                                `${item.wide}${item.wide_unit === 'feet' ? 'FT' : 'M'}x${item.length}${item.length_unit === 'feet' ? 'FT' : 'M'}` :
                              // Use the size field if it contains formatted dimensions
                              (item.size && item.size.includes && item.size.includes('x') && item.size !== 'N/A') ? item.size :
                              // For square meter products without units, try basic wide x length
                              (item.measurement_unit === 'per_square_meter' && item.wide && item.length) ? `${item.wide}x${item.length}` :
                              // Default fallback
                              (item.size || 'N/A')
                            }
                          </span>
                          <span>{item.color}</span>
                          <span>
                            {item.measurement_unit === 'per_square_meter' ? `${item.sqm} m²` : `${item.quantity} units`}
                          </span>
                          <span>
                            <input 
                              type="number"
                              value={item.unit_price}
                              onChange={(e) => {
                                const newUnitPrice = parseFloat(e.target.value) || 0;
                                // Use index-based update for reliability when same product appears multiple times
                                const updatedItems = newOrder.order_items.map((orderItem, idx) => {
                                  if (idx === itemIndex) {
                                    const newTotalPrice = orderItem.measurement_unit === 'per_square_meter'
                                      ? newUnitPrice * (orderItem.sqm || 0)
                                      : newUnitPrice * (orderItem.quantity || 0);
                                    return { ...orderItem, unit_price: newUnitPrice, total_price: newTotalPrice };
                                  }
                                  return orderItem;
                                });
                                
                                // IRONCLAD CALCULATION
                                const calc = calculateIronclad(
                                  updatedItems,
                                  newOrder.fitting_charge,
                                  newOrder.cut_piece_charge,
                                  newOrder.discount
                                );
                                
                                setNewOrder({
                                  ...newOrder,
                                  order_items: updatedItems,
                                  subtotal: calc.subtotal,
                                  vat_amount: calc.vatAmount,
                                  grand_total: calc.grandTotal
                                });
                              }}
                              step="0.01"
                              min="0"
                              style={{
                                width: '80px',
                                padding: '4px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                fontSize: '0.9rem'
                              }}
                            />
                          </span>
                          <span>£{item.total_price.toFixed(2)}</span>
                          <span style={{textAlign: 'center'}}>
                            <input 
                              type="checkbox"
                              checked={item.has_vat !== false}
                              onChange={(e) => {
                                // FIXED: Use index-based update to handle multiple items of same product
                                const updatedItems = newOrder.order_items.map((orderItem, idx) => {
                                  if (idx === itemIndex) {
                                    return { ...orderItem, has_vat: e.target.checked };
                                  }
                                  return orderItem;
                                });
                                
                                // IRONCLAD CALCULATION
                                const calc = calculateIronclad(
                                  updatedItems,
                                  newOrder.fitting_charge,
                                  newOrder.cut_piece_charge,
                                  newOrder.discount
                                );
                                
                                console.log('🔒 VAT Toggle:', {
                                  item: item.product_name,
                                  newValue: e.target.checked,
                                  vatAmount: calc.vatAmount.toFixed(2),
                                  grandTotal: calc.grandTotal.toFixed(2)
                                });
                                
                                setNewOrder({
                                  ...newOrder,
                                  order_items: updatedItems,
                                  vat_amount: calc.vatAmount,
                                  grand_total: calc.grandTotal
                                });
                              }}
                              style={{
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer',
                                accentColor: '#b8860b'
                              }}
                              title={item.has_vat !== false ? "VAT applies to this item (20%)" : "No VAT on this item"}
                            />
                          </span>
                          <span>
                            <button 
                              type="button" 
                              className="btn danger small"
                              onClick={() => removeItemFromOrderByIndex(itemIndex)}
                            >
                              Remove
                            </button>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Order Totals */}
                {newOrder.order_items.length > 0 && (
                  <div className="order-totals">
                    <div className="totals-row">
                      <span>Total Stock:</span>
                      <span>£{newOrder.subtotal.toFixed(2)}</span>
                    </div>
                    
                    <div className="totals-row">
                      <span>Cutting Cost:</span>
                      <span>£{newOrder.cut_piece_charge.toFixed(2)}</span>
                    </div>
                    
                    <div className="totals-row">
                      <span>Fitting Cost:</span>
                      <span>£{newOrder.fitting_charge.toFixed(2)}</span>
                    </div>
                    
                    <div className="totals-row">
                      <span>Subtotal:</span>
                      <span>£{(newOrder.subtotal + newOrder.fitting_charge + newOrder.cut_piece_charge).toFixed(2)}</span>
                    </div>
                    
                    {/* VAT - Calculated per item (items with VAT checkbox ticked) */}
                    <div className="totals-row">
                      <span>VAT (20%):</span>
                      <span style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <span>£{(newOrder.vat_amount || 0).toFixed(2)}</span>
                        <span style={{fontSize: '0.75rem', color: '#666'}}>
                          ({newOrder.order_items.filter(i => i.has_vat !== false).length} of {newOrder.order_items.length} items)
                        </span>
                      </span>
                    </div>
                    
                    {/* Discount Input Field - BEFORE Total Bill */}
                    <div className="totals-row discount-row" style={{color: '#d32f2f'}}>
                      <span>Discount:</span>
                      <div className="discount-input-container" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div>
                          <span>-£</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={newOrder.discount || ''}
                            onChange={(e) => {
                              const discountValue = parseFloat(e.target.value) || 0;
                              
                              // Use IRONCLAD calculation for bulletproof totals
                              const calc = calculateIronclad(
                                newOrder.order_items,
                                newOrder.fitting_charge,
                                newOrder.cut_piece_charge,
                                discountValue
                              );
                              
                              setNewOrder({
                                ...newOrder,
                                discount: discountValue,
                                vat_amount: calc.vatAmount,
                                grand_total: calc.grandTotal
                              });
                            }}
                            placeholder="0.00"
                            className="discount-input"
                          />
                        </div>
                        {newOrder.discount > 0 && (
                          <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                            -£{parseFloat(newOrder.discount || 0).toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Total Bill (after discount) */}
                    <div className="totals-row" style={{fontWeight: '600', borderTop: '1px solid #ddd', paddingTop: '8px', marginTop: '8px'}}>
                      <span>Total Bill:</span>
                      <span>£{newOrder.grand_total.toFixed(2)}</span>
                    </div>
                    
                    {/* Old Balance - Show customer's current outstanding balance from Credit Accounts */}
                    {(() => {
                      const oldBalance = parseFloat(newOrder.calculated_old_balance || 0);
                      const totalBill = parseFloat(newOrder.grand_total || 0);
                      const grandTotalWithOldBalance = totalBill + oldBalance;
                      
                      // 💰 PERMANENT: ALWAYS show Old Balance row for EVERY customer including guests.
                      // Guests are treated identically — if a guest has prior outstanding it will show.
                      return (
                        <>
                          <div className="totals-row">
                            <span>Old Balance:</span>
                            <span>£{oldBalance.toFixed(2)}</span>
                          </div>
                          <div className="totals-row grand-total">
                            <span>Grand Total:</span>
                            <span>£{grandTotalWithOldBalance.toFixed(2)}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
              
              {/* Special Instructions / Notes Section */}
              <div className="form-row" style={{background:'rgba(184,134,11,0.05)', padding:'1rem', borderRadius:'6px', margin:'1rem 0', border:'1px solid rgba(184,134,11,0.2)'}}>
                <h4 style={{color:'#b8860b', marginBottom:'0.5rem', fontSize:'1.1rem'}}>📝 Special Instructions / Notes</h4>
                <p style={{fontSize:'0.85rem', color:'#666', marginBottom:'0.75rem'}}>
                  Add any special instructions for delivery, handling, or installation (optional)
                </p>
                <textarea 
                  className="form-control"
                  placeholder="Example: Deliver to back entrance, Handle with care - fragile items, Call customer 30 mins before delivery..."
                  value={newOrder.notes || ''}
                  onChange={(e) => setNewOrder({...newOrder, notes: e.target.value})}
                  style={{
                    minHeight: '100px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    fontSize: '0.95rem'
                  }}
                />
              </div>
              
              <div className="form-actions">
                <button type="button" className="btn secondary" onClick={closeCreateOrderModal}>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn primary"
                  disabled={newOrder.order_items.length === 0}
                >
                  {newOrder.isEditMode ? 'Update Order' : 'Create Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Original Order Modal - Shows the order before it was edited */}
      {showOriginalOrderModal && originalOrderToView && (
        <div className="modal-overlay" onClick={() => {setShowOriginalOrderModal(false); setOriginalOrderToView(null);}}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '800px', maxHeight: '90vh', overflow: 'auto'}}>
            <div className="modal-header" style={{background: '#f59e0b', color: 'white'}}>
              <h2>📋 Original Order (Before Edit)</h2>
              <button className="close-modal" onClick={() => {setShowOriginalOrderModal(false); setOriginalOrderToView(null);}}>×</button>
            </div>
            <div style={{padding: '20px'}}>
              <div style={{background: originalOrderToView._migration_note ? '#fee2e2' : '#fef3c7', padding: '15px', borderRadius: '8px', marginBottom: '20px'}}>
                <p style={{margin: 0, color: originalOrderToView._migration_note ? '#991b1b' : '#92400e', fontWeight: '600'}}>
                  {originalOrderToView._migration_note ? (
                    <>⚠️ This order was edited before the tracking system was implemented. This shows the order state at the time of migration, not the true original.</>
                  ) : (
                    <>⚠️ This is the original order data before any edits were made. Compare with current order to see what changed.</>
                  )}
                </p>
              </div>
              
              {/* Order Info */}
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px'}}>
                <div>
                  <h4 style={{color: '#374151', marginBottom: '10px'}}>Order Details</h4>
                  <p><strong>Order ID:</strong> {originalOrderToView.id?.substring(0, 12)}...</p>
                  <p><strong>Date:</strong> {originalOrderToView.date ? new Date(originalOrderToView.date).toLocaleDateString('en-GB') + ' ' + new Date(originalOrderToView.date).toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'}) : 'N/A'}</p>
                  <p><strong>Status:</strong> {originalOrderToView.status || 'N/A'}</p>
                  <p><strong>Sale Type:</strong> {originalOrderToView.sale_type || 'N/A'}</p>
                </div>
                <div>
                  <h4 style={{color: '#374151', marginBottom: '10px'}}>Customer Details</h4>
                  <p><strong>Customer:</strong> {originalOrderToView.customer_name || 'N/A'}</p>
                  <p><strong>Customer #:</strong> {originalOrderToView.customer_number || 'N/A'}</p>
                  <p><strong>Payment Method:</strong> {originalOrderToView.payment_method || 'N/A'}</p>
                </div>
              </div>
              
              {/* Items Table */}
              <h4 style={{color: '#374151', marginBottom: '10px'}}>Items</h4>
              <table style={{width: '100%', borderCollapse: 'collapse', marginBottom: '20px'}}>
                <thead>
                  <tr style={{background: '#f3f4f6'}}>
                    <th style={{padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb'}}>Product</th>
                    <th style={{padding: '10px', textAlign: 'center', borderBottom: '2px solid #e5e7eb'}}>Qty</th>
                    <th style={{padding: '10px', textAlign: 'right', borderBottom: '2px solid #e5e7eb'}}>Unit Price</th>
                    <th style={{padding: '10px', textAlign: 'right', borderBottom: '2px solid #e5e7eb'}}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(originalOrderToView.items || []).map((item, idx) => (
                    <tr key={idx} style={{borderBottom: '1px solid #e5e7eb'}}>
                      <td style={{padding: '10px'}}>{item.product_name || 'N/A'}</td>
                      <td style={{padding: '10px', textAlign: 'center'}}>{item.quantity || item.sqm || 0}</td>
                      <td style={{padding: '10px', textAlign: 'right'}}>£{parseFloat(item.unit_price || 0).toFixed(2)}</td>
                      <td style={{padding: '10px', textAlign: 'right'}}>£{parseFloat(item.total_price || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Totals */}
              <div style={{background: '#f9fafb', padding: '15px', borderRadius: '8px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px'}}>
                  <span>Subtotal:</span>
                  <span>£{parseFloat(originalOrderToView.subtotal || 0).toFixed(2)}</span>
                </div>
                {parseFloat(originalOrderToView.discount || 0) > 0 && (
                  <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#dc2626'}}>
                    <span>Discount:</span>
                    <span>-£{parseFloat(originalOrderToView.discount || 0).toFixed(2)}</span>
                  </div>
                )}
                {parseFloat(originalOrderToView.vat_amount || 0) > 0 && (
                  <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px'}}>
                    <span>VAT ({((originalOrderToView.vat_rate || 0) * 100).toFixed(0)}%):</span>
                    <span>£{parseFloat(originalOrderToView.vat_amount || 0).toFixed(2)}</span>
                  </div>
                )}
                <div style={{display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '18px', borderTop: '2px solid #e5e7eb', paddingTop: '10px'}}>
                  <span>Grand Total:</span>
                  <span>£{parseFloat(originalOrderToView.grand_total || 0).toFixed(2)}</span>
                </div>
                
                {/* Payment Info */}
                <div style={{marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e5e7eb'}}>
                  <h5 style={{marginBottom: '8px'}}>Payment Details:</h5>
                  <div style={{display: 'flex', gap: '20px', flexWrap: 'wrap'}}>
                    {parseFloat(originalOrderToView.cash_paid_amount || 0) > 0 && (
                      <span style={{background: '#dcfce7', padding: '4px 8px', borderRadius: '4px', color: '#166534'}}>
                        💵 Cash: £{parseFloat(originalOrderToView.cash_paid_amount || 0).toFixed(2)}
                      </span>
                    )}
                    {parseFloat(originalOrderToView.bank_paid_amount || 0) > 0 && (
                      <span style={{background: '#dbeafe', padding: '4px 8px', borderRadius: '4px', color: '#1e40af'}}>
                        🏦 Bank: £{parseFloat(originalOrderToView.bank_paid_amount || 0).toFixed(2)}
                      </span>
                    )}
                    {parseFloat(originalOrderToView.credit_paid_amount || 0) > 0 && (
                      <span style={{background: '#fef3c7', padding: '4px 8px', borderRadius: '4px', color: '#92400e'}}>
                        💳 Credit: £{parseFloat(originalOrderToView.credit_paid_amount || 0).toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div style={{marginTop: '20px', textAlign: 'center'}}>
                <button 
                  onClick={() => {setShowOriginalOrderModal(false); setOriginalOrderToView(null);}}
                  style={{
                    padding: '10px 30px',
                    background: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🔧 FIX ISSUES Modal - Comprehensive Data Audit Report - FULLSCREEN */}
      {showFixIssuesModal && (
        <div className="modal-overlay" style={{zIndex: 9999, background: 'rgba(0,0,0,0.95)'}}>
          <div className="modal-content" style={{maxWidth: '100%', width: '100%', height: '100vh', maxHeight: '100vh', overflow: 'auto', background: '#0d1117', color: '#fff', borderRadius: '0', padding: '0'}}>
            {/* Header */}
            <div style={{background: '#161b22', padding: '24px 32px', borderBottom: '2px solid #30363d', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10}}>
              <div>
                <h2 style={{margin: 0, fontSize: '32px', fontWeight: '800', color: '#e94560', letterSpacing: '1px'}}>🔧 DATA ISSUES AUDIT</h2>
                {fixIssuesData && (
                  <p style={{margin: '12px 0 0 0', fontSize: '16px', color: '#8b949e'}}>
                    Generated: {new Date(fixIssuesData.generated_at).toLocaleString()} | 
                    Scanned: <strong style={{color: '#58a6ff'}}>{fixIssuesData.checked?.orders || 0}</strong> orders, <strong style={{color: '#58a6ff'}}>{fixIssuesData.checked?.customers || 0}</strong> customers, <strong style={{color: '#58a6ff'}}>{fixIssuesData.checked?.account_payments || 0}</strong> account payments
                  </p>
                )}
              </div>
              <button 
                onClick={() => {setShowFixIssuesModal(false); setFixIssuesData(null); setSelectedIssuesToFix({});}}
                style={{background: '#dc3545', border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer', padding: '8px 16px', borderRadius: '8px', fontWeight: '700'}}
              >✕ CLOSE</button>
            </div>
            
            {fixIssuesLoading ? (
              <div style={{padding: '100px', textAlign: 'center'}}>
                <div style={{fontSize: '72px', marginBottom: '24px'}}>🔍</div>
                <p style={{fontSize: '24px', color: '#8b949e'}}>Scanning for issues...</p>
              </div>
            ) : fixIssuesData ? (
              <>
                {/* Summary Cards */}
                <div style={{padding: '32px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px', borderBottom: '2px solid #30363d'}}>
                  <div style={{background: fixIssuesData.total_issues > 0 ? '#dc354530' : '#28a74530', padding: '24px', borderRadius: '12px', border: `3px solid ${fixIssuesData.total_issues > 0 ? '#dc3545' : '#28a745'}`}}>
                    <div style={{fontSize: '48px', fontWeight: '800', color: fixIssuesData.total_issues > 0 ? '#dc3545' : '#28a745'}}>{fixIssuesData.total_issues}</div>
                    <div style={{fontSize: '16px', color: '#8b949e', textTransform: 'uppercase', fontWeight: '600', marginTop: '8px'}}>Total Issues</div>
                  </div>
                  <div style={{background: '#dc354530', padding: '24px', borderRadius: '12px', border: '3px solid #dc3545'}}>
                    <div style={{fontSize: '48px', fontWeight: '800', color: '#dc3545'}}>{fixIssuesData.by_severity?.HIGH || 0}</div>
                    <div style={{fontSize: '16px', color: '#8b949e', textTransform: 'uppercase', fontWeight: '600', marginTop: '8px'}}>High Priority</div>
                  </div>
                  <div style={{background: '#ffc10730', padding: '24px', borderRadius: '12px', border: '3px solid #ffc107'}}>
                    <div style={{fontSize: '48px', fontWeight: '800', color: '#ffc107'}}>{fixIssuesData.by_severity?.MEDIUM || 0}</div>
                    <div style={{fontSize: '16px', color: '#8b949e', textTransform: 'uppercase', fontWeight: '600', marginTop: '8px'}}>Medium Priority</div>
                  </div>
                  <div style={{background: '#17a2b830', padding: '24px', borderRadius: '12px', border: '3px solid #17a2b8'}}>
                    <div style={{fontSize: '48px', fontWeight: '800', color: '#17a2b8'}}>{fixIssuesData.by_severity?.LOW || 0}</div>
                    <div style={{fontSize: '16px', color: '#8b949e', textTransform: 'uppercase', fontWeight: '600', marginTop: '8px'}}>Low Priority</div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div style={{padding: '20px 32px', background: '#161b22', borderBottom: '2px solid #30363d', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap'}}>
                  <button
                    onClick={() => {
                      const all = {};
                      (fixIssuesData.issues || []).forEach(i => all[i.id] = true);
                      setSelectedIssuesToFix(all);
                    }}
                    style={{padding: '12px 24px', background: '#007bff', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '16px'}}
                  >
                    ☑️ Select All
                  </button>
                  <button
                    onClick={() => setSelectedIssuesToFix({})}
                    style={{padding: '12px 24px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '16px'}}
                  >
                    ☐ Deselect All
                  </button>
                  <button
                    onClick={() => {
                      const highOnly = {};
                      (fixIssuesData.issues || []).filter(i => i.severity === 'HIGH').forEach(i => highOnly[i.id] = true);
                      setSelectedIssuesToFix(highOnly);
                    }}
                    style={{padding: '12px 24px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '16px'}}
                  >
                    🔴 Select High Priority Only
                  </button>
                  <div style={{flex: 1}}></div>
                  <span style={{color: '#8b949e', fontSize: '18px', fontWeight: '600'}}>
                    {Object.keys(selectedIssuesToFix).filter(k => selectedIssuesToFix[k]).length} selected
                  </span>
                  <button
                    disabled={Object.keys(selectedIssuesToFix).filter(k => selectedIssuesToFix[k]).length === 0}
                    onClick={async () => {
                      const selectedIds = Object.keys(selectedIssuesToFix).filter(k => selectedIssuesToFix[k]);
                      if (selectedIds.length === 0) {
                        alert('Please select at least one issue to fix');
                        return;
                      }
                      if (!window.confirm(`Fix ${selectedIds.length} selected issues?\n\nThis will apply the fixes to your database.`)) {
                        return;
                      }
                      try {
                        const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
                        if (!token) {
                          throw new Error('No authentication token found. Please log in again.');
                        }
                        const response = await axios.post(`${API}/api/credit-accounts/fix-selected-issues`, 
                          { issue_ids: selectedIds },
                          { headers: { 'Authorization': `Bearer ${token}` }}
                        );
                        const result = response.data;
                        alert(`✅ Fixed ${result.fixed_count} issues\n${result.failed_count > 0 ? `⚠️ ${result.failed_count} failed` : ''}`);
                        // Refresh the audit
                        setFixIssuesLoading(true);
                        const refreshResponse = await axios.get(`${API}/api/credit-accounts/audit-issues`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        setFixIssuesData(refreshResponse.data);
                        setSelectedIssuesToFix({});
                        setFixIssuesLoading(false);
                        // Also refresh credit accounts
                        await loadCreditAccountsOrders();
                      } catch (error) {
                        alert('❌ Error: ' + (error.response?.data?.detail || error.message));
                      }
                    }}
                    style={{
                      padding: '10px 24px', 
                      background: Object.keys(selectedIssuesToFix).filter(k => selectedIssuesToFix[k]).length > 0 ? '#28a745' : '#6c757d', 
                      color: '#fff', 
                      border: 'none', 
                      borderRadius: '6px', 
                      cursor: Object.keys(selectedIssuesToFix).filter(k => selectedIssuesToFix[k]).length > 0 ? 'pointer' : 'not-allowed',
                      fontWeight: '700',
                      fontSize: '14px'
                    }}
                  >
                    🔧 FIX SELECTED ({Object.keys(selectedIssuesToFix).filter(k => selectedIssuesToFix[k]).length})
                  </button>
                </div>
                
                {/* Issues Table - LARGER FONT */}
                <div style={{padding: '0 32px 32px 32px'}}>
                  {fixIssuesData.total_issues === 0 ? (
                    <div style={{padding: '100px', textAlign: 'center'}}>
                      <div style={{fontSize: '96px', marginBottom: '24px'}}>✅</div>
                      <h3 style={{color: '#28a745', margin: '0 0 12px 0', fontSize: '32px'}}>No Issues Found!</h3>
                      <p style={{color: '#8b949e', fontSize: '18px'}}>All data is consistent and correct.</p>
                    </div>
                  ) : (
                    <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '16px', marginTop: '24px'}}>
                      <thead>
                        <tr style={{background: '#161b22'}}>
                          <th style={{padding: '16px 12px', textAlign: 'left', borderBottom: '2px solid #30363d', width: '50px'}}>
                            <input
                              type="checkbox"
                              style={{width: '20px', height: '20px', cursor: 'pointer'}}
                              checked={Object.keys(selectedIssuesToFix).filter(k => selectedIssuesToFix[k]).length === fixIssuesData.issues.length}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  const all = {};
                                  (fixIssuesData.issues || []).forEach(i => all[i.id] = true);
                                  setSelectedIssuesToFix(all);
                                } else {
                                  setSelectedIssuesToFix({});
                                }
                              }}
                            />
                          </th>
                          <th style={{padding: '16px 12px', textAlign: 'left', borderBottom: '2px solid #30363d', color: '#8b949e', fontSize: '14px', fontWeight: '700', textTransform: 'uppercase'}}>SEVERITY</th>
                          <th style={{padding: '16px 12px', textAlign: 'left', borderBottom: '2px solid #30363d', color: '#8b949e', fontSize: '14px', fontWeight: '700', textTransform: 'uppercase'}}>DATE</th>
                          <th style={{padding: '16px 12px', textAlign: 'left', borderBottom: '2px solid #30363d', color: '#8b949e', fontSize: '14px', fontWeight: '700', textTransform: 'uppercase'}}>CUSTOMER</th>
                          <th style={{padding: '16px 12px', textAlign: 'left', borderBottom: '2px solid #30363d', color: '#8b949e', fontSize: '14px', fontWeight: '700', textTransform: 'uppercase'}}>ISSUE TYPE</th>
                          <th style={{padding: '16px 12px', textAlign: 'left', borderBottom: '2px solid #30363d', color: '#8b949e', fontSize: '14px', fontWeight: '700', textTransform: 'uppercase'}}>CURRENT VALUE</th>
                          <th style={{padding: '16px 12px', textAlign: 'left', borderBottom: '2px solid #30363d', color: '#8b949e', fontSize: '14px', fontWeight: '700', textTransform: 'uppercase'}}>CORRECT VALUE</th>
                          <th style={{padding: '16px 12px', textAlign: 'center', borderBottom: '2px solid #30363d', color: '#8b949e', fontSize: '14px', fontWeight: '700', textTransform: 'uppercase'}}>ACTION</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(fixIssuesData.issues || []).map((issue, idx) => (
                          <tr 
                            key={issue.id} 
                            style={{
                              background: selectedIssuesToFix[issue.id] ? '#28a74530' : (idx % 2 === 0 ? '#0d1117' : '#161b22'),
                              transition: 'background 0.2s'
                            }}
                          >
                            <td style={{padding: '16px 12px', borderBottom: '1px solid #30363d'}}>
                              <input
                                type="checkbox"
                                style={{width: '20px', height: '20px', cursor: 'pointer'}}
                                checked={!!selectedIssuesToFix[issue.id]}
                                onChange={(e) => {
                                  setSelectedIssuesToFix(prev => ({...prev, [issue.id]: e.target.checked}));
                                }}
                              />
                            </td>
                            <td style={{padding: '16px 12px', borderBottom: '1px solid #30363d'}}>
                              <span style={{
                                padding: '6px 14px',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: '700',
                                background: issue.severity === 'HIGH' ? '#dc3545' : issue.severity === 'MEDIUM' ? '#ffc107' : '#17a2b8',
                                color: issue.severity === 'MEDIUM' ? '#000' : '#fff'
                              }}>
                                {issue.severity}
                              </span>
                            </td>
                            <td style={{padding: '16px 12px', borderBottom: '1px solid #30363d', color: '#e6edf3', fontFamily: 'monospace', fontSize: '16px', fontWeight: '600'}}>{issue.date || 'N/A'}</td>
                            <td style={{padding: '16px 12px', borderBottom: '1px solid #30363d', color: '#e6edf3', fontSize: '16px', fontWeight: '600'}}>{issue.customer_name || issue.customer_number || 'N/A'}</td>
                            <td style={{padding: '16px 12px', borderBottom: '1px solid #30363d'}}>
                              <span style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontWeight: '600',
                                background: '#21262d',
                                color: '#58a6ff',
                                textTransform: 'uppercase',
                                whiteSpace: 'nowrap'
                              }}>
                                {issue.type?.replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td style={{padding: '16px 12px', borderBottom: '1px solid #30363d', color: '#f85149', fontFamily: 'monospace', fontSize: '15px', fontWeight: '600'}}>{issue.current_value}</td>
                            <td style={{padding: '16px 12px', borderBottom: '1px solid #30363d', color: '#3fb950', fontFamily: 'monospace', fontSize: '15px', fontWeight: '600'}}>{issue.expected_value}</td>
                            <td style={{padding: '16px 12px', borderBottom: '1px solid #30363d', textAlign: 'center'}}>
                              {issue.order_id && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    // Close the fix issues modal and open the order view
                                    try {
                                      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
                                      // Fetch the full order data
                                      const response = await axios.get(`${API}/api/admin-portal/orders`, {
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      });
                                      const orders = response.data;
                                      const order = orders.find(o => o.id === issue.order_id || o.id?.startsWith(issue.order_id?.substring(0, 8)));
                                      if (order) {
                                        setSelectedOrder(order);
                                        setShowOrderViewModal(true);
                                        setShowFixIssuesModal(false);
                                      } else {
                                        alert('Order not found. It may have been deleted or the ID is invalid.');
                                      }
                                    } catch (error) {
                                      alert('Error loading order: ' + error.message);
                                    }
                                  }}
                                  style={{
                                    padding: '8px 16px',
                                    background: '#238636',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontWeight: '700',
                                    fontSize: '13px',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  👁️ VIEW BILL
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            ) : (
              <div style={{padding: '100px', textAlign: 'center'}}>
                <p style={{color: '#f85149', fontSize: '20px'}}>Failed to load issues data</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Order View Modal - Professional Invoice Format */}
      {showOrderViewModal && selectedOrder && (
        <div className="modal-overlay">
          <div className="modal-content invoice-modal">
            <button className="close-modal invoice-close-btn" onClick={() => {setShowOrderViewModal(false); setSelectedOrder(null)}}>×</button>
            
            {/* ⚠️ Calculation Warning Banner - Shows if has_calculation_warning flag is set */}
            {selectedOrder.has_calculation_warning && (
              <div style={{
                background: '#fff3cd',
                border: '2px solid #dc3545',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{fontSize: '24px'}}>⚠️</span>
                <div>
                  <strong style={{color: '#dc3545', display: 'block'}}>Calculation Mismatch Detected</strong>
                  <span style={{fontSize: '0.9rem', color: '#856404'}}>
                    The total on this bill doesn&apos;t match the sum of items + charges. Please edit this order to fix.
                  </span>
                </div>
                <button 
                  onClick={() => {
                    setShowOrderViewModal(false);
                    handleEditOrder(selectedOrder.id);
                  }}
                  style={{
                    marginLeft: 'auto',
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Edit Order
                </button>
              </div>
            )}
            
            <div className="invoice-container">
              {/* Invoice Header - Clean white design */}
              <div className="invoice-header invoice-header-white">
                <div className="company-info company-info-centered">
                  <h1 className="company-name company-name-black">PLUSH LIVING LTD</h1>
                  <p className="company-tagline company-tagline-black">Making your house a home</p>
                  <div className="company-address-below">
                    <span>Unit 30, 197 adderley road, Birmingham, B8 1AN</span>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="customer-info-table">
                <div className="customer-row">
                  <div className="customer-label">customer name:</div>
                  <div className="customer-value">
                    {(() => {
                      // FIXED: Use enriched customer data from backend first, including guest customer details
                      if (selectedOrder.customer_id === null || !selectedOrder.customer_id || selectedOrder.customer_id === 'guest') {
                        // FIXED: Always show guest customer name, no filtering
                        return selectedOrder.customer_name || 'Guest Customer';
                      }
                      
                      // Use enriched customer data if available
                      if (selectedOrder.customer_name && selectedOrder.customer_name !== 'Customer Not Found') {
                        return selectedOrder.customer_name;
                      }
                      
                      // Fallback to manual lookup
                      const customer = customers.find(c => c.id === selectedOrder.customer_id);
                      return customer ? customer.name : 'Customer Not Found';
                    })()}
                  </div>
                </div>
                <div className="customer-row">
                  <div className="customer-label">customer #:</div>
                  <div className="customer-value">
                    <strong>{selectedOrder.customer_number || 'N/A'}</strong>
                  </div>
                </div>
                <div className="customer-row">
                  <div className="customer-label">customer Address:</div>
                  <div className="customer-value">
                    {(() => {
                      // FIXED: Use enriched customer data from backend first, including guest customer details
                      if (selectedOrder.customer_id === null || !selectedOrder.customer_id || selectedOrder.customer_id === 'guest') {
                        // Show guest customer address if available
                        return selectedOrder.customer_address && selectedOrder.customer_address !== 'Address not provided'
                          ? selectedOrder.customer_address
                          : 'Address not provided';
                      }
                      
                      // Use enriched customer data if available
                      if (selectedOrder.customer_address && selectedOrder.customer_address !== 'Address not provided') {
                        return selectedOrder.customer_address;
                      }
                      
                      // Fallback to manual lookup
                      const customer = customers.find(c => c.id === selectedOrder.customer_id);
                      return customer && customer.address ? customer.address : 'Address not provided';
                    })()}
                  </div>
                </div>
                <div className="customer-row">
                  <div className="customer-label">customer contact detail:</div>
                  <div className="customer-value">
                    {(() => {
                      // FIXED: Use enriched customer data from backend first, including guest customer details
                      if (selectedOrder.customer_id === null || !selectedOrder.customer_id || selectedOrder.customer_id === 'guest') {
                        // Show guest customer contact if available
                        return selectedOrder.customer_contact && selectedOrder.customer_contact !== 'Contact not provided'
                          ? selectedOrder.customer_contact
                          : 'Contact not provided';
                      }
                      
                      // Use enriched customer data if available  
                      if (selectedOrder.customer_contact && selectedOrder.customer_contact !== 'Contact not provided') {
                        return selectedOrder.customer_contact;
                      }
                      
                      // Fallback to manual lookup
                      const customer = customers.find(c => c.id === selectedOrder.customer_id);
                      return customer && (customer.contact_number || customer.phone) ? (customer.contact_number || customer.phone) : 'Contact not provided';
                    })()}
                  </div>
                </div>
              </div>

              {/* Order Header */}
              <div className="order-header-section">
                <div className="order-title">SALE ORDER</div>
                <div className="order-date">
                  <div className="date-label">DATE</div>
                  <div className="date-value">
                    {selectedOrder.date ? new Date(selectedOrder.date).toLocaleDateString('en-GB', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    }) : new Date().toLocaleDateString('en-GB', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </div>
                </div>
              </div>

              {/* Order Items Table */}
              <div className="order-items-table">
                <div className="table-header">
                  <div className="col-description">Description</div>
                  <div className="col-size">Size</div>
                  <div className="col-qnty">Qnty</div>
                  <div className="col-price">Price</div>
                  <div className="col-total">Total Price</div>
                </div>
                
                {(() => {
                  const modalItemsToDisplay = calculateRemainingQuantities(selectedOrder);
                  return modalItemsToDisplay.length > 0 ? (
                    modalItemsToDisplay.map((item, index) => (
                      <div key={index} className="table-row">
                        <div className="col-description">
                          <div className="item-name">
                            {item.product_name || item.name || 'Item'}
                            {item.returned_quantity > 0 && (
                              <span style={{color: '#888', fontSize: '0.9em', marginLeft: '8px'}}>
                                ({item.returned_quantity} returned)
                              </span>
                            )}
                          </div>
                          <div className="item-details">
                            {(item.color || item.storage_location || item.storageLocation) ? 
                              [item.color, item.storage_location || item.storageLocation].filter(Boolean).join(' | ') : 
                              'Product details'
                            }
                          </div>
                        </div>
                        <div className="col-size">
                          {
                            // For square meter products with dimensions and units, display with units
                            (item.wide && item.length && item.wide_unit && item.length_unit) ? 
                              (item.wide + (item.wide_unit === 'feet' ? 'FT' : 'M') + 'x' + item.length + (item.length_unit === 'feet' ? 'FT' : 'M')) :
                            // For square meter products with dimensions but no units, use basic format
                            (item.wide && item.length) ? (item.wide + 'x' + item.length) :
                            // If size contains dimensions format with units, use it
                            (item.size && item.size.includes && item.size.includes('x') && item.size !== 'N/A') ? item.size :
                            // For square meter products, use size field
                            (item.measurement_unit === 'per_square_meter' && item.size) ? item.size :
                            // Default fallback
                            (item.size || 'N/A')
                          }
                        </div>
                        <div className="col-qnty">
                          {item.display_quantity}
                        </div>
                        <div className="col-price">
                          £{item.unit_price ? parseFloat(item.unit_price).toFixed(2) : (item.price ? parseFloat(item.price).toFixed(2) : '0.00')}
                        </div>
                        <div className="col-total">
                          £{item.display_total_price}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="table-row">
                      <div className="col-description">No remaining items (all returned)</div>
                      <div className="col-size">-</div>
                      <div className="col-qnty">-</div>
                      <div className="col-price">-</div>
                      <div className="col-total">-</div>
                    </div>
                  );
                })()}
              </div>

              {/* Totals Section */}
              <div className="totals-section">
                <div className="totals-left">
                  <div className="total-stock-box">
                    <span className="stock-label">Total Stock</span>
                    <span className="stock-value">
                      {(() => {
                        const modalItemsToDisplay = calculateRemainingQuantities(selectedOrder);
                        const remainingTotal = modalItemsToDisplay.reduce((total, item) => total + parseFloat(item.display_quantity || 0), 0);
                        const returnedTotal = selectedOrder.return_items ? 
                          selectedOrder.return_items.reduce((total, returnItem) => total + (returnItem.return_quantity || returnItem.returned_quantity || 0), 0) : 0;
                        
                        return remainingTotal.toFixed(1) + (returnedTotal > 0 ? ` (${returnedTotal} returned)` : '');
                      })()}
                    </span>
                  </div>
                </div>
                <div className="totals-right">
                  {/* New Bill Format: Total Stock -> Cutting Cost -> Fitting Cost -> Subtotal -> VAT -> Discount -> Total Bill -> Grand Total */}
                  <div className="total-row">
                    <span className="total-label">Total Stock</span>
                    <span className="total-value">£{(() => {
                      const modalItemsToDisplay = calculateRemainingQuantities(selectedOrder);
                      const recalculatedSubtotal = modalItemsToDisplay.reduce((total, item) => {
                        return total + (parseFloat(item.unit_price || 0) * parseFloat(item.display_quantity || 0));
                      }, 0);
                      return recalculatedSubtotal.toFixed(2);
                    })()}</span>
                  </div>
                  
                  {/* Cutting Cost - Only show if > 0 */}
                  {parseFloat(selectedOrder.cut_piece_charge || 0) > 0 && (
                    <div className="total-row">
                      <span className="total-label">Cutting Cost</span>
                      <span className="total-value">£{parseFloat(selectedOrder.cut_piece_charge || 0).toFixed(2)}</span>
                    </div>
                  )}
                  
                  {/* Fitting Cost - Only show if > 0 */}
                  {parseFloat(selectedOrder.fitting_charge || 0) > 0 && (
                    <div className="total-row">
                      <span className="total-label">Fitting Cost</span>
                      <span className="total-value">£{parseFloat(selectedOrder.fitting_charge || 0).toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="total-row">
                    <span className="total-label">Subtotal</span>
                    <span className="total-value">£{(() => {
                      const modalItemsToDisplay = calculateRemainingQuantities(selectedOrder);
                      const recalculatedSubtotal = modalItemsToDisplay.reduce((total, item) => {
                        return total + (parseFloat(item.unit_price || 0) * parseFloat(item.display_quantity || 0));
                      }, 0);
                      const additionalServices = parseFloat(selectedOrder.fitting_charge || 0) + parseFloat(selectedOrder.cut_piece_charge || 0);
                      return (recalculatedSubtotal + additionalServices).toFixed(2);
                    })()}</span>
                  </div>
                  
                  {/* VAT - Calculate based on per-item VAT settings */}
                  {(() => {
                    const modalItemsToDisplay = calculateRemainingQuantities(selectedOrder);
                    
                    // CRITICAL FIX: Use helper to get effective VAT for consistent calculation
                    const { vatAmount: calculatedVatAmount, vatableCount: vatableItemCount, totalItems: totalItemCount } = getFinalVatAmount(selectedOrder, modalItemsToDisplay);
                    
                    const shouldShowVat = calculatedVatAmount > 0;
                    
                    return shouldShowVat ? (
                      <div className="total-row">
                        <span className="total-label">VAT (20%)</span>
                        <span className="total-value">
                          £{calculatedVatAmount.toFixed(2)}
                          {vatableItemCount < totalItemCount && (
                            <span style={{fontSize: '0.8em', color: '#666', marginLeft: '5px'}}>
                              ({vatableItemCount} of {totalItemCount} items)
                            </span>
                          )}
                        </span>
                      </div>
                    ) : null;
                  })()}
                  
                  {/* Discount - Only show if > 0 */}
                  {parseFloat(selectedOrder.discount || 0) > 0 && (
                    <div className="total-row discount-row" style={{color: '#d32f2f'}}>
                      <span className="total-label">Discount</span>
                      <span className="total-value">-£{parseFloat(selectedOrder.discount || 0).toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="total-row" style={{fontWeight: '600'}}>
                    <span className="total-label">Total Bill</span>
                    <span className="total-value">£{(() => {
                      const modalItemsToDisplay = calculateRemainingQuantities(selectedOrder);
                      const recalculatedSubtotal = modalItemsToDisplay.reduce((total, item) => {
                        return total + (parseFloat(item.unit_price || 0) * parseFloat(item.display_quantity || 0));
                      }, 0);
                      const additionalServices = parseFloat(selectedOrder.fitting_charge || 0) + parseFloat(selectedOrder.cut_piece_charge || 0);
                      const subtotalWithServices = recalculatedSubtotal + additionalServices;
                      
                      // CRITICAL FIX: Use helper to get effective VAT
                      const { vatAmount } = getFinalVatAmount(selectedOrder, modalItemsToDisplay);
                      
                      const discount = parseFloat(selectedOrder.discount || 0);
                      return (subtotalWithServices + vatAmount - discount).toFixed(2);
                    })()}</span>
                  </div>
                  
                  {/* Old Balance - Calculate LIVE from current credit data */}
                  {(() => {
                    // 💰 PERMANENT FIX: Old Balance now shows on EVERY order (cash, bank, credit)
                    // for EVERY customer including guests. Guests are identified by their
                    // PLU-GUEST-G#### customer_number — the same key used for guest lump-sums.
                    let liveOldBalance = 0;
                    const isGuest = !selectedOrder.customer_id || selectedOrder.customer_id === 'guest';
                    const lookupKey = isGuest ? selectedOrder.customer_number : selectedOrder.customer_id;
                    
                    if (lookupKey) {
                      const customerOrders = (window.__creditAccountsOrders || []).filter(o => {
                        if (o.id === selectedOrder.id) return false;
                        const otherIsGuest = !o.customer_id || o.customer_id === 'guest';
                        const otherKey = otherIsGuest ? o.customer_number : o.customer_id;
                        return otherKey === lookupKey;
                      });
                      const totalOtherOutstanding = customerOrders.reduce((sum, o) => sum + parseFloat(o.outstanding_balance || 0), 0);
                      const customerPayments = window.__customerAccountPayments || {};
                      const acctTotal = parseFloat((customerPayments[lookupKey] || {}).total || 0);
                      liveOldBalance = Math.max(0, Math.round((totalOtherOutstanding - acctTotal) * 100) / 100);
                    }
                    
                    const totalBill = (() => {
                      const modalItemsToDisplay = calculateRemainingQuantities(selectedOrder);
                      const recalculatedSubtotal = modalItemsToDisplay.reduce((total, item) => {
                        return total + (parseFloat(item.unit_price || 0) * parseFloat(item.display_quantity || 0));
                      }, 0);
                      const additionalServices = parseFloat(selectedOrder.fitting_charge || 0) + parseFloat(selectedOrder.cut_piece_charge || 0);
                      const subtotalWithServices = recalculatedSubtotal + additionalServices;
                      const { vatAmount } = getFinalVatAmount(selectedOrder, modalItemsToDisplay);
                      const discount = parseFloat(selectedOrder.discount || 0);
                      return subtotalWithServices + vatAmount - discount;
                    })();
                    const grandTotalWithOldBalance = totalBill + liveOldBalance;
                    
                    // 💰 PERMANENT: ALWAYS render the Old Balance row for EVERY customer including
                    // guests — guests are treated identically. No more conditional layouts.
                    return (
                      <>
                        <div className="total-row">
                          <span className="total-label">Old Balance</span>
                          <span className="total-value">£{liveOldBalance.toFixed(2)}</span>
                        </div>
                        <div className="total-row final-total">
                          <span className="total-label">Grand Total</span>
                          <span className="total-value">£{grandTotalWithOldBalance.toFixed(2)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Order Notes Section */}
              {selectedOrder.notes && (
                <div style={{
                  marginTop: '15px',
                  padding: '12px',
                  background: '#fff9e6',
                  borderLeft: '3px solid #d4af37',
                  borderRadius: '4px'
                }}>
                  <strong style={{color: '#000', fontSize: '12px', display: 'block', marginBottom: '8px'}}>
                    📝 SPECIAL INSTRUCTIONS / NOTES:
                  </strong>
                  <p style={{margin: 0, color: '#333', fontSize: '11px', lineHeight: '1.5', whiteSpace: 'pre-wrap'}}>
                    {selectedOrder.notes}
                  </p>
                </div>
              )}

              {/* Payment Agreement */}
              <div className="payment-agreement">
                <strong>PAYMENT: AGREEMENT:</strong> We, Plush Living LTD, are giving our well-known customers a 30 day credit. This credit is being given out of our trust towards both the customer and also the shop. For business of the month, if this payment is delayed by payment in lieu of goods by us we shall demand before the public on a non-notional from the conditions stated by the customer.
              </div>
            </div>
            
            {/* VAT Invoice Number - Editable */}
            {isManagerAccount() && selectedOrder && (
              <div style={{padding: '10px 20px', background: '#f8f9fa', borderTop: '1px solid #dee2e6', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap'}}>
                <span style={{fontWeight: '600', fontSize: '13px', color: '#333'}}>VAT Invoice No:</span>
                <input
                  type="text"
                  data-testid="vat-invoice-number-input"
                  value={selectedOrder.vat_invoice_number || ''}
                  onChange={(e) => setSelectedOrder({...selectedOrder, vat_invoice_number: e.target.value})}
                  placeholder={`PLV-${selectedOrder.id ? selectedOrder.id.substring(0, 8).toUpperCase() : '000000'}`}
                  style={{
                    padding: '4px 8px',
                    border: '2px solid #007bff',
                    borderRadius: '4px',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    width: '160px'
                  }}
                />
                <button
                  data-testid="save-vat-number-btn"
                  onClick={async () => {
                    const newNum = (selectedOrder.vat_invoice_number || '').trim();
                    if (!newNum) { alert('Invoice number cannot be empty'); return; }
                    try {
                      const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
                      await axios.put(`${API}/api/orders/${selectedOrder.id}/vat-invoice-number`, 
                        { vat_invoice_number: newNum },
                        { headers: { 'Authorization': `Bearer ${token}` } }
                      );
                      setFulfilledOrders(prev => prev.map(o => o.id === selectedOrder.id ? {...o, vat_invoice_number: newNum} : o));
                      alert('VAT invoice number saved!');
                    } catch (err) {
                      alert('Error: ' + (err.response?.data?.detail || err.message));
                    }
                  }}
                  style={{padding: '4px 12px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: '600'}}
                >
                  Save
                </button>
              </div>
            )}
            
            <div className="modal-footer">
              <button 
                className="btn secondary" 
                onClick={() => {setShowOrderViewModal(false); setSelectedOrder(null)}}
              >
                Close
              </button>
              <button 
                className="btn primary" 
                onClick={() => {
                  // CRITICAL FIX: Normalize items when entering edit mode
                  // Ensure has_vat is explicitly TRUE unless explicitly false
                  const normalizedOrder = {
                    ...selectedOrder,
                    items: (selectedOrder.items || []).map(item => ({
                      ...item,
                      has_vat: item.has_vat === false ? false : true // Explicit boolean
                    }))
                  };
                  
                  // Recalculate VAT based on normalized items
                  const vatableItemsTotal = normalizedOrder.items
                    .filter(i => i.has_vat === true)
                    .reduce((sum, i) => sum + parseFloat(i.total_price || 0), 0);
                  const fittingCharge = parseFloat(selectedOrder.fitting_charge || 0);
                  const cutPieceCharge = parseFloat(selectedOrder.cut_piece_charge || 0);
                  const calculatedVat = (vatableItemsTotal + fittingCharge + cutPieceCharge) * 0.20;
                  
                  // Update vat_amount if it was 0 but should have VAT
                  const storedVat = parseFloat(selectedOrder.vat_amount || 0);
                  if (storedVat < 0.01 && calculatedVat > 0.01) {
                    normalizedOrder.vat_amount = calculatedVat;
                    // Recalculate grand total
                    const subtotal = parseFloat(selectedOrder.subtotal || 0);
                    const discount = parseFloat(selectedOrder.discount || 0);
                    normalizedOrder.grand_total = subtotal + fittingCharge + cutPieceCharge + calculatedVat - discount;
                  }
                  
                  console.log('📝 Edit mode - normalized items:', normalizedOrder.items.map(i => ({
                    name: i.product_name,
                    has_vat: i.has_vat,
                    total_price: i.total_price
                  })));
                  
                  setSelectedOrder(normalizedOrder);
                  setShowOrderViewModal(false);
                  setShowOrderEditModal(true);
                }}
                disabled={isSpectateOnly('fulfilled-orders') && selectedOrder?.status === 'fulfilled'}
                style={{
                  opacity: (isSpectateOnly('fulfilled-orders') && selectedOrder?.status === 'fulfilled') ? 0.5 : 1,
                  cursor: (isSpectateOnly('fulfilled-orders') && selectedOrder?.status === 'fulfilled') ? 'not-allowed' : 'pointer'
                }}
                title={(isSpectateOnly('fulfilled-orders') && selectedOrder?.status === 'fulfilled') ? 'Spectate mode - cannot edit fulfilled orders' : ''}
              >
                {(isSpectateOnly('fulfilled-orders') && selectedOrder?.status === 'fulfilled') ? '🔒 Edit Order' : 'Edit Order'}
              </button>
              <button 
                className="btn success" 
                onClick={() => {
                  console.log('🖨️ PRINT BUTTON CLICKED - selectedOrder data:', {
                    id: selectedOrder?.id,
                    customer_id: selectedOrder?.customer_id,
                    customer_name: selectedOrder?.customer_name,
                    customer_address: selectedOrder?.customer_address,
                    customer_contact: selectedOrder?.customer_contact,
                    hasEnrichedData: !!(selectedOrder?.customer_name && selectedOrder?.customer_name !== 'Customer Not Found')
                  });
                  printSaleOrder(selectedOrder);
                }}
              >
                Print Sale Order
              </button>
              <button 
                className="btn primary" 
                onClick={() => printVATInvoice(selectedOrder)}
              >
                Print VAT Invoice
              </button>
              {isManagerAccount() && (
                <button 
                  className="btn"
                  data-testid="generate-credit-note-btn"
                  onClick={() => {
                    const order = selectedOrder;
                    setCreditNoteData({
                      order_ids: [order.id],
                      credit_note_number: 'CN-' + Date.now().toString(36).toUpperCase(),
                      customer_name: order.customer_name || '',
                      customer_number: order.customer_number || '',
                      items: (order.items || []).map(item => ({
                        description: item.product_name || item.name || '',
                        specification: `${item.size || ''} ${item.color || ''}`.trim(),
                        quantity: item.quantity || 0,
                        unit_price: item.price || item.unit_price || 0,
                        amount: item.total_price || (item.quantity * (item.price || 0)),
                        included: true
                      })),
                      reason: '',
                      notes: '',
                      total_amount: parseFloat(order.grand_total || 0),
                      vat_amount: parseFloat(order.vat_amount || 0),
                      subtotal: parseFloat(order.subtotal || 0),
                      original_invoice: order.vat_invoice_number || `PLV-${order.id ? order.id.substring(0,8).toUpperCase() : 'N/A'}`
                    });
                    setShowCreditNoteModal(true);
                  }}
                  style={{background: '#fd7e14', color: 'white', border: 'none'}}
                >
                  Credit Note
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Order Edit Modal */}
      {showOrderEditModal && selectedOrder && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h2>Edit Order - #{selectedOrder.id ? selectedOrder.id.substring(0, 8) : 'N/A'}</h2>
              <button className="close-modal" onClick={() => {setShowOrderEditModal(false); setSelectedOrder(null)}}>×</button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleSaveOrderChanges(selectedOrder);
            }} className="admin-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Customer ID</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedOrder.customer_id || ''}
                    onChange={(e) => setSelectedOrder({...selectedOrder, customer_id: e.target.value})}
                    placeholder="Enter customer ID or leave empty for guest"
                  />
                </div>
                <div className="form-group">
                  <label>Sale Type</label>
                  <select
                    className="form-control"
                    value={selectedOrder.sale_type || 'retail'}
                    onChange={(e) => setSelectedOrder({...selectedOrder, sale_type: e.target.value})}
                  >
                    <option value="retail">Retail</option>
                    <option value="wholesale">Wholesale</option>
                    <option value="fitter">Fitter</option>
                  </select>
                </div>
              </div>
              
              {/* ADDED: Customer Details for Guest Orders */}
              <div className="form-row">
                <div className="form-group">
                  <label>Customer Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedOrder.customer_name || ''}
                    onChange={(e) => setSelectedOrder({...selectedOrder, customer_name: e.target.value})}
                    placeholder="Customer name"
                  />
                </div>
                <div className="form-group">
                  <label>Customer Contact</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedOrder.customer_contact || ''}
                    onChange={(e) => setSelectedOrder({...selectedOrder, customer_contact: e.target.value})}
                    placeholder="Phone number"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group" style={{flex: 1}}>
                  <label>Customer Address</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedOrder.customer_address || ''}
                    onChange={(e) => setSelectedOrder({...selectedOrder, customer_address: e.target.value})}
                    placeholder="Full address"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Order Status</label>
                  <select
                    className="form-control"
                    value={selectedOrder.status || 'pending'}
                    onChange={(e) => setSelectedOrder({...selectedOrder, status: e.target.value})}
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="fulfilled">Fulfilled</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Order Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={selectedOrder.date ? selectedOrder.date.split('T')[0] : ''}
                    onChange={(e) => setSelectedOrder({...selectedOrder, date: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Order Notes</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={selectedOrder.notes || ''}
                  onChange={(e) => setSelectedOrder({...selectedOrder, notes: e.target.value})}
                  placeholder="Add any notes about this order..."
                />
              </div>

              {/* Order Items List with Remove Option */}
              <div className="form-group">
                <label style={{marginBottom: '10px', display: 'block'}}>Order Items ({selectedOrder.items?.length || 0})</label>
                <div style={{maxHeight: '300px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px'}}>
                  <table style={{width: '100%', borderCollapse: 'collapse'}}>
                    <thead style={{background: '#f7fafc', position: 'sticky', top: 0}}>
                      <tr>
                        <th style={{padding: '10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0'}}>Product</th>
                        <th style={{padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0'}}>Size</th>
                        <th style={{padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0'}}>Qty</th>
                        <th style={{padding: '10px', textAlign: 'right', borderBottom: '2px solid #e2e8f0'}}>Unit Price</th>
                        <th style={{padding: '10px', textAlign: 'right', borderBottom: '2px solid #e2e8f0'}}>Total</th>
                        <th style={{padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0'}}>VAT</th>
                        <th style={{padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0'}}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedOrder.items || []).map((item, index) => (
                        <tr key={index} style={{borderBottom: '1px solid #e2e8f0'}}>
                          <td style={{padding: '10px'}}>{item.product_name || 'N/A'}</td>
                          <td style={{padding: '10px', textAlign: 'center'}}>{item.size || 'N/A'}</td>
                          <td style={{padding: '10px', textAlign: 'center'}}>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.quantity || item.sqm || 0}
                              onChange={(e) => {
                                const newQty = parseFloat(e.target.value) || 0;
                                const newItems = [...selectedOrder.items];
                                newItems[index] = {
                                  ...newItems[index],
                                  quantity: newQty,
                                  sqm: newQty,
                                  total_price: newQty * parseFloat(item.unit_price || 0)
                                };
                                // Recalculate with per-item VAT
                                const newSubtotal = newItems.reduce((sum, i) => sum + parseFloat(i.total_price || 0), 0);
                                const fittingCharge = parseFloat(selectedOrder.fitting_charge || 0);
                                const cutPieceCharge = parseFloat(selectedOrder.cut_piece_charge || 0);
                                const discount = parseFloat(selectedOrder.discount || 0);
                                const vatableItemsTotal = newItems
                                  .filter(i => i.has_vat !== false)
                                  .reduce((sum, i) => sum + parseFloat(i.total_price || 0), 0);
                                const newVat = (vatableItemsTotal + fittingCharge + cutPieceCharge) * 0.20;
                                const newGrandTotal = newSubtotal + fittingCharge + cutPieceCharge + newVat - discount;
                                setSelectedOrder({
                                  ...selectedOrder,
                                  items: newItems,
                                  subtotal: newSubtotal,
                                  vat_amount: newVat,
                                  grand_total: newGrandTotal
                                });
                              }}
                              style={{width: '70px', padding: '4px', textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: '4px'}}
                            />
                          </td>
                          <td style={{padding: '10px', textAlign: 'right'}}>£{parseFloat(item.unit_price || 0).toFixed(2)}</td>
                          <td style={{padding: '10px', textAlign: 'right'}}>£{parseFloat(item.total_price || 0).toFixed(2)}</td>
                          <td style={{padding: '10px', textAlign: 'center'}}>
                            <input 
                              type="checkbox"
                              checked={item.has_vat !== false}
                              onChange={(e) => {
                                const newItems = [...selectedOrder.items];
                                newItems[index] = { ...newItems[index], has_vat: e.target.checked };
                                // Recalculate with per-item VAT
                                const newSubtotal = newItems.reduce((sum, i) => sum + parseFloat(i.total_price || 0), 0);
                                const fittingCharge = parseFloat(selectedOrder.fitting_charge || 0);
                                const cutPieceCharge = parseFloat(selectedOrder.cut_piece_charge || 0);
                                const discount = parseFloat(selectedOrder.discount || 0);
                                const vatableItemsTotal = newItems
                                  .filter(i => i.has_vat !== false)
                                  .reduce((sum, i) => sum + parseFloat(i.total_price || 0), 0);
                                const newVat = (vatableItemsTotal + fittingCharge + cutPieceCharge) * 0.20;
                                const newGrandTotal = newSubtotal + fittingCharge + cutPieceCharge + newVat - discount;
                                setSelectedOrder({
                                  ...selectedOrder,
                                  items: newItems,
                                  vat_amount: newVat,
                                  grand_total: newGrandTotal
                                });
                              }}
                              style={{width: '18px', height: '18px', cursor: 'pointer', accentColor: '#b8860b'}}
                              title={item.has_vat !== false ? "VAT applies (20%)" : "No VAT"}
                            />
                          </td>
                          <td style={{padding: '10px', textAlign: 'center'}}>
                            <button
                              type="button"
                              onClick={() => {
                                if (window.confirm(`Remove "${item.product_name}" from this order?`)) {
                                  const newItems = selectedOrder.items.filter((_, i) => i !== index);
                                  // Recalculate with per-item VAT
                                  const newSubtotal = newItems.reduce((sum, i) => sum + parseFloat(i.total_price || 0), 0);
                                  const fittingCharge = parseFloat(selectedOrder.fitting_charge || 0);
                                  const cutPieceCharge = parseFloat(selectedOrder.cut_piece_charge || 0);
                                  const discount = parseFloat(selectedOrder.discount || 0);
                                  const vatableItemsTotal = newItems
                                    .filter(i => i.has_vat !== false)
                                    .reduce((sum, i) => sum + parseFloat(i.total_price || 0), 0);
                                  const newVat = (vatableItemsTotal + fittingCharge + cutPieceCharge) * 0.20;
                                  const newGrandTotal = newSubtotal + fittingCharge + cutPieceCharge + newVat - discount;
                                  setSelectedOrder({
                                    ...selectedOrder,
                                    items: newItems,
                                    subtotal: newSubtotal,
                                    vat_amount: newVat,
                                    grand_total: newGrandTotal
                                  });
                                }
                              }}
                              style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              🗑️ Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                      {(!selectedOrder.items || selectedOrder.items.length === 0) && (
                        <tr>
                          <td colSpan="7" style={{padding: '20px', textAlign: 'center', color: '#718096'}}>
                            No items in this order
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="current-totals">
                <h4>Current Order Totals</h4>
                <div className="totals-display">
                  <div className="total-item">
                    <span>Items:</span>
                    <span>{selectedOrder.items?.length || 0}</span>
                  </div>
                  <div className="total-item">
                    <span>Subtotal:</span>
                    <span>£{selectedOrder.subtotal ? parseFloat(selectedOrder.subtotal).toFixed(2) : '0.00'}</span>
                  </div>
                  <div className="total-item">
                    <span>VAT (20%):</span>
                    <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                      £{(() => {
                        // Calculate VAT only on items with has_vat=true
                        const items = selectedOrder.items || [];
                        const fittingCharge = parseFloat(selectedOrder.fitting_charge || 0);
                        const cutPieceCharge = parseFloat(selectedOrder.cut_piece_charge || 0);
                        const vatableItemsTotal = items
                          .filter(i => i.has_vat !== false)
                          .reduce((sum, i) => sum + parseFloat(i.total_price || 0), 0);
                        return ((vatableItemsTotal + fittingCharge + cutPieceCharge) * 0.20).toFixed(2);
                      })()}
                      <span style={{fontSize: '0.75rem', color: '#666'}}>
                        ({(selectedOrder.items || []).filter(i => i.has_vat !== false).length}/{(selectedOrder.items || []).length} items)
                      </span>
                    </span>
                  </div>
                  <div className="total-item" style={{background: '#fef2f2', padding: '8px', borderRadius: '4px'}}>
                    <span style={{color: '#dc2626'}}><strong>Discount:</strong></span>
                    <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                      <span>£</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={selectedOrder.discount || 0}
                        onChange={(e) => {
                          const newDiscount = parseFloat(e.target.value) || 0;
                          const subtotal = parseFloat(selectedOrder.subtotal || 0);
                          const fittingCharge = parseFloat(selectedOrder.fitting_charge || 0);
                          const cutPieceCharge = parseFloat(selectedOrder.cut_piece_charge || 0);
                          const items = selectedOrder.items || [];
                          // Calculate VAT per-item
                          const vatableItemsTotal = items
                            .filter(i => i.has_vat !== false)
                            .reduce((sum, i) => sum + parseFloat(i.total_price || 0), 0);
                          const vatAmount = (vatableItemsTotal + fittingCharge + cutPieceCharge) * 0.20;
                          const newGrandTotal = subtotal + fittingCharge + cutPieceCharge + vatAmount - newDiscount;
                          setSelectedOrder({
                            ...selectedOrder,
                            discount: newDiscount,
                            vat_amount: vatAmount,
                            grand_total: Math.max(0, newGrandTotal)
                          });
                        }}
                        style={{
                          width: '80px',
                          padding: '4px 8px',
                          border: '1px solid #dc2626',
                          borderRadius: '4px',
                          textAlign: 'right'
                        }}
                      />
                    </div>
                  </div>
                  <div className="total-item grand-total">
                    <span><strong>Grand Total:</strong></span>
                    <span><strong>£{selectedOrder.grand_total ? parseFloat(selectedOrder.grand_total).toFixed(2) : '0.00'}</strong></span>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn secondary" onClick={() => {setShowOrderEditModal(false); setSelectedOrder(null)}}>
                  Cancel
                </button>
                <button type="submit" className="btn primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Customer View Modal */}
      {showCustomerViewModal && selectedCustomer && (
        <div className="modal-overlay" onClick={() => setShowCustomerViewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Customer Details</h3>
              <button className="modal-close" onClick={() => setShowCustomerViewModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Company Name:</label>
                <p>{selectedCustomer.company_name || selectedCustomer.name || 'N/A'}</p>
              </div>
              <div className="form-group">
                <label>Full Name:</label>
                <p>{selectedCustomer.name || 'N/A'}</p>
              </div>
              <div className="form-group">
                <label>Email:</label>
                <p>{selectedCustomer.email}</p>
              </div>
              <div className="form-group">
                <label>Phone:</label>
                <p>{selectedCustomer.contact_number || selectedCustomer.phone || 'N/A'}</p>
              </div>
              <div className="form-group">
                <label>Address:</label>
                <p>{selectedCustomer.address || 'N/A'}</p>
              </div>
              <div className="form-group">
                <label>Customer Type:</label>
                <p>{selectedCustomer.customer_type ? selectedCustomer.customer_type.charAt(0).toUpperCase() + selectedCustomer.customer_type.slice(1) : 'Retail'}</p>
              </div>
              <div className="form-group">
                <label>Status:</label>
                <p>{selectedCustomer.status ? selectedCustomer.status.charAt(0).toUpperCase() + selectedCustomer.status.slice(1) : 'Active'}</p>
              </div>
            </div>
            <div className="form-actions">
              <button className="btn secondary" onClick={() => setShowCustomerViewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Edit Modal */}
      {showCustomerEditModal && selectedCustomer && (
        <div className="modal-overlay" onClick={() => setShowCustomerEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Customer</h3>
              <button className="modal-close" onClick={() => setShowCustomerEditModal(false)}>×</button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                const formData = new FormData(e.target);
                const updateData = {
                  name: formData.get('name'),
                  email: formData.get('email'),
                  company_name: formData.get('company_name'),
                  address: formData.get('address'), 
                  contact_number: formData.get('contact_number'),
                  customer_type: formData.get('customer_type')
                };
                
                console.log('Updating customer with data:', updateData);
                
                const response = await axios.put(`${API}/api/admin-portal/customers/${selectedCustomer.id}`, updateData, {
                  headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token')}` }
                });
                
                console.log('Customer update response:', response.data);
                
                await loadCustomers();
                setShowCustomerEditModal(false);
                setSelectedCustomer(null);
                alert('Customer updated successfully');
              } catch (err) {
                console.error('Error updating customer:', err);
                console.error('Error response:', err.response?.data);
                alert(`Error updating customer: ${err.response?.data?.detail || err.message}`);
              }
            }}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Customer Name:</label>
                  <input 
                    type="text" 
                    name="name" 
                    defaultValue={selectedCustomer.name || ''} 
                    className="form-control"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email:</label>
                  <input 
                    type="email" 
                    name="email" 
                    defaultValue={selectedCustomer.email || ''} 
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Company Name:</label>
                  <input 
                    type="text" 
                    name="company_name" 
                    defaultValue={selectedCustomer.company_name || ''} 
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Address:</label>
                  <textarea 
                    name="address" 
                    defaultValue={selectedCustomer.address || ''} 
                    className="form-control"
                    rows="3"
                  />
                </div>
                <div className="form-group">
                  <label>Contact Number:</label>
                  <input 
                    type="tel" 
                    name="contact_number" 
                    defaultValue={selectedCustomer.contact_number || ''} 
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Customer Type:</label>
                  <select name="customer_type" defaultValue={selectedCustomer.customer_type || 'retail'} className="form-control">
                    <option value="retail">Retail</option>
                    <option value="wholesale">Wholesale</option>
                    <option value="fitter">Fitter</option>
                  </select>
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn secondary" onClick={() => setShowCustomerEditModal(false)}>Cancel</button>
                <button type="submit" className="btn primary">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Store Location Modal */}
      {showAddLocationModal && (
        <div className="modal-overlay" onClick={() => setShowAddLocationModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Store Location</h3>
              <button className="modal-close" onClick={() => setShowAddLocationModal(false)}>×</button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const locationData = {
                name: formData.get('name'),
                address: formData.get('address'),
                postcode: formData.get('postcode'),
                phone: formData.get('phone') || null
              };
              handleAddLocation(locationData);
            }}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Location Name:</label>
                  <input type="text" name="name" className="form-control" required placeholder="e.g., Main Store, Birmingham Branch" />
                </div>
                <div className="form-group">
                  <label>Address:</label>
                  <textarea name="address" className="form-control" required rows="3" placeholder="Full address (use line breaks for multiple lines)"></textarea>
                </div>
                <div className="form-group">
                  <label>Postcode:</label>
                  <input type="text" name="postcode" className="form-control" required placeholder="e.g., UB1 3EJ" />
                </div>
                <div className="form-group">
                  <label>Phone (optional):</label>
                  <input type="text" name="phone" className="form-control" placeholder="e.g., 020 1234 5678" />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn secondary" onClick={() => setShowAddLocationModal(false)}>Cancel</button>
                <button type="submit" className="btn primary">Add Location</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Store Location Modal */}
      {editingLocation && (
        <div className="modal-overlay" onClick={() => setEditingLocation(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Store Location</h3>
              <button className="modal-close" onClick={() => setEditingLocation(null)}>×</button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const locationData = {
                name: formData.get('name'),
                address: formData.get('address'),
                postcode: formData.get('postcode'),
                phone: formData.get('phone') || null
              };
              handleEditLocation(editingLocation.id, locationData);
            }}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Location Name:</label>
                  <input type="text" name="name" className="form-control" required defaultValue={editingLocation.name} />
                </div>
                <div className="form-group">
                  <label>Address:</label>
                  <textarea name="address" className="form-control" required rows="3" defaultValue={editingLocation.address}></textarea>
                </div>
                <div className="form-group">
                  <label>Postcode:</label>
                  <input type="text" name="postcode" className="form-control" required defaultValue={editingLocation.postcode} />
                </div>
                <div className="form-group">
                  <label>Phone (optional):</label>
                  <input type="text" name="phone" className="form-control" defaultValue={editingLocation.phone || ''} />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn secondary" onClick={() => setEditingLocation(null)}>Cancel</button>
                <button type="submit" className="btn primary">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invoice Download Modal */}
      {showInvoiceDownloadModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Invoice Filter</h3>
              <button 
                className="close-btn" 
                onClick={() => setShowInvoiceDownloadModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div style={{display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap'}}>
                <div>
                  <label>From Date:</label>
                  <input 
                    type="date" 
                    className="form-control"
                    value={invoiceDownloadFromDate} 
                    onChange={(e) => setInvoiceDownloadFromDate(e.target.value)}
                    style={{marginTop: '5px'}}
                  />
                </div>
                <div>
                  <label>To Date:</label>
                  <input 
                    type="date" 
                    className="form-control"
                    value={invoiceDownloadToDate} 
                    onChange={(e) => setInvoiceDownloadToDate(e.target.value)}
                    style={{marginTop: '5px'}}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn secondary" 
                onClick={() => setShowInvoiceDownloadModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn primary" 
                onClick={handleDownloadInvoicesPDF}
              >
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Business Analysis Modal */}
      {showBusinessAnalysisModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Business Analysis Filter</h3>
              <button 
                className="close-btn" 
                onClick={() => setShowBusinessAnalysisModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div style={{display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap'}}>
                <div>
                  <label>From Date:</label>
                  <input 
                    type="date" 
                    className="form-control"
                    value={businessAnalysisFromDate} 
                    onChange={(e) => setBusinessAnalysisFromDate(e.target.value)}
                    style={{marginTop: '5px'}}
                  />
                </div>
                <div>
                  <label>To Date:</label>
                  <input 
                    type="date" 
                    className="form-control"
                    value={businessAnalysisToDate} 
                    onChange={(e) => setBusinessAnalysisToDate(e.target.value)}
                    style={{marginTop: '5px'}}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn secondary" 
                onClick={() => setShowBusinessAnalysisModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn primary" 
                onClick={generateBusinessAnalysis}
              >
                Generate Analysis
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Account Modal */}
      {showCreateAdminModal && (
        <div className="modal-overlay" onClick={() => setShowCreateAdminModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '500px'}}>
            <div className="modal-header">
              <h3>Create Account</h3>
              <button className="modal-close" onClick={() => setShowCreateAdminModal(false)}>×</button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const selectedRole = formData.get('account_role');
              
              let adminData;
              if (selectedRole === 'manager') {
                adminData = {
                  username: formData.get('username'),
                  email: formData.get('email'),
                  password: formData.get('password'),
                  allowed_sections: null,
                  spectate_sections: null,
                  role: 'admin',
                  price_access: ['retail', 'wholesale', 'fitter', 'cost'],
                  can_access_dashboard: true,
                  can_access_settings: true
                };
              } else {
                adminData = {
                  username: formData.get('username'),
                  email: formData.get('email'),
                  password: formData.get('password'),
                  allowed_sections: ['orders', 'product-enquiries', 'customers'],
                  spectate_sections: ['fulfilled-orders', 'credit-accounts', 'accounting', 'expenses', 'warehouse-breakdown', 'invoices'],
                  role: 'worker',
                  price_access: ['retail'],
                  can_access_dashboard: false,
                  can_access_settings: false
                };
              }
              
              handleCreateAdmin(adminData);
            }}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Username:</label>
                  <input type="text" name="username" className="form-control" required placeholder="e.g., john" data-testid="create-username" />
                </div>
                <div className="form-group">
                  <label>Email:</label>
                  <input type="email" name="email" className="form-control" required placeholder="user@plush-living.co.uk" data-testid="create-email" />
                </div>
                <div className="form-group">
                  <label>Password:</label>
                  <input type="password" name="password" className="form-control" required placeholder="Enter password" data-testid="create-password" />
                </div>
                
                <div className="form-group" style={{background: '#f8f9fa', padding: '15px', borderRadius: '8px'}}>
                  <label style={{fontWeight: '600', marginBottom: '12px', display: 'block'}}>Account Role:</label>
                  <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap'}}>
                    <label data-testid="role-manager" style={{display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '12px 16px', border: '2px solid #2563eb', borderRadius: '8px', cursor: 'pointer', background: 'white', flex: '1', minWidth: '180px'}}>
                      <input type="radio" name="account_role" value="manager" style={{marginTop: '3px'}} />
                      <div>
                        <strong style={{color: '#2563eb'}}>Manager</strong>
                        <p style={{fontSize: '11px', color: '#666', margin: '4px 0 0'}}>Full access to all sections. Can edit, create, delete, and manage everything.</p>
                      </div>
                    </label>
                    <label data-testid="role-worker" style={{display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '12px 16px', border: '2px solid #059669', borderRadius: '8px', cursor: 'pointer', background: 'white', flex: '1', minWidth: '180px'}}>
                      <input type="radio" name="account_role" value="worker" defaultChecked style={{marginTop: '3px'}} />
                      <div>
                        <strong style={{color: '#059669'}}>Worker</strong>
                        <p style={{fontSize: '11px', color: '#666', margin: '4px 0 0'}}>Can view all sections and create orders. Cannot edit, fulfill, or delete.</p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn secondary" onClick={() => setShowCreateAdminModal(false)}>Cancel</button>
                <button type="submit" className="btn primary" data-testid="create-account-submit">Create Account</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Warehouse Breakdown Date Filter Modal */}
      {showWarehouseBreakdownModal && (
        <div className="modal-overlay" onClick={() => setShowWarehouseBreakdownModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '500px'}}>
            <div className="modal-header" style={{background: 'linear-gradient(135deg, #1a365d 0%, #2c5282 100%)', color: 'white'}}>
              <h3 style={{margin: 0}}>📦 Warehouse Breakdown Report</h3>
              <button className="modal-close" onClick={() => setShowWarehouseBreakdownModal(false)} style={{color: 'white'}}>×</button>
            </div>
            <div className="modal-body" style={{padding: '25px'}}>
              <p style={{color: '#666', marginBottom: '20px'}}>
                Select the date range for your report. The report will include all sales, expenses, and inventory changes within this period.
              </p>
              
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px'}}>
                <div className="form-group">
                  <label style={{fontWeight: '600', marginBottom: '8px', display: 'block'}}>Start Date:</label>
                  <input 
                    type="date" 
                    className="form-control" 
                    value={warehouseReportStartDate}
                    onChange={(e) => setWarehouseReportStartDate(e.target.value)}
                    style={{width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd'}}
                  />
                </div>
                <div className="form-group">
                  <label style={{fontWeight: '600', marginBottom: '8px', display: 'block'}}>End Date:</label>
                  <input 
                    type="date" 
                    className="form-control" 
                    value={warehouseReportEndDate}
                    onChange={(e) => setWarehouseReportEndDate(e.target.value)}
                    style={{width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd'}}
                  />
                </div>
              </div>
              
              <div style={{background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px'}}>
                <h4 style={{margin: '0 0 10px 0', fontSize: '14px', color: '#333'}}>Report will include:</h4>
                <ul style={{margin: 0, paddingLeft: '20px', color: '#666', fontSize: '13px'}}>
                  <li>Financial Summary (Cash + Bank sales, Credit Outstanding, Expenses, COGS, Profit, Margin)</li>
                  <li>Total Products Sold Summary (overview of all products sold)</li>
                  <li>In-depth product breakdown (Customer, Bill #, Qty, Price, VAT)</li>
                  <li><strong>In-depth cash transactions</strong> (Every cash payment: bills + lump-sums)</li>
                  <li><strong>In-depth bank transactions</strong> (Every bank payment: bills + lump-sums)</li>
                  <li><strong>Credits given in period</strong> (Every credit sale with cash/bank/credit split)</li>
                  <li>Current outstanding credit accounts (Who owes how much across all time)</li>
                  <li>In-depth expenses (Every expense with date, description, method)</li>
                  <li>Products on the Floor (Current remaining inventory, sold, restocked in period)</li>
                </ul>
              </div>
              
              <div style={{display: 'flex', gap: '10px'}}>
                <button 
                  type="button" 
                  className="btn secondary" 
                  onClick={() => setShowWarehouseBreakdownModal(false)}
                  style={{flex: 1}}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn primary" 
                  onClick={generateWarehouseBreakdownReport}
                  style={{flex: 1, background: 'linear-gradient(135deg, #1a365d 0%, #2c5282 100%)'}}
                >
                  Generate Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Credentials Modal */}
      {showCredentialModal && (
        <div className="modal-overlay" onClick={() => setShowCredentialModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '500px'}}>
            <div className="modal-header">
              <h3>Change Login Credentials</h3>
              <button className="modal-close" onClick={() => setShowCredentialModal(false)}>×</button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              
              const credentialData = {
                current_password: formData.get('current_password'),
                new_username: formData.get('new_username') || null,
                new_password: formData.get('new_password') || null
              };
              
              // Validate that at least one field is being changed
              if (!credentialData.new_username && !credentialData.new_password) {
                alert('Please provide a new username and/or password');
                return;
              }
              
              handleChangeCredentials(credentialData);
            }}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Current Password: *</label>
                  <input type="password" name="current_password" className="form-control" required placeholder="Enter your current password" />
                </div>
                <div className="form-group">
                  <label>New Username: (leave blank to keep current)</label>
                  <input type="text" name="new_username" className="form-control" placeholder="Enter new username (optional)" />
                </div>
                <div className="form-group">
                  <label>New Password: (leave blank to keep current)</label>
                  <input type="password" name="new_password" className="form-control" placeholder="Enter new password (optional)" />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn secondary" onClick={() => setShowCredentialModal(false)}>Cancel</button>
                <button type="submit" className="btn primary">Update Credentials</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Admin Account Management Modal */}
      {/* 📦 Stock Audit & Heal Modal (zobayr-only) */}
      {showStockAudit && (
        <div className="modal-overlay" onClick={() => setShowStockAudit(false)} data-testid="stock-audit-modal">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '1100px', maxHeight: '85vh', overflow: 'auto'}}>
            <div className="modal-header">
              <h3>📦 Stock Audit &amp; Heal</h3>
              <button className="modal-close" onClick={() => setShowStockAudit(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{fontSize:'13px', color:'#555', marginTop:0}}>
                Detects stock that was reduced but never sold (e.g. by a 400 error on any device). Legitimate orders and returns are the ground truth. Click <strong>Restore Leaked Stock</strong> to put orphaned units back on the shelf — every restoration is logged in the variation&rsquo;s restock history with note <code>stock-heal</code>.
              </p>

              {stockAuditSummary && (
                <div data-testid="stock-audit-summary" style={{display:'flex', gap:'10px', flexWrap:'wrap', margin:'12px 0', padding:'10px', background:'#fff8e6', border:'1px solid #f2c94c', borderRadius:'6px', fontSize:'13px'}}>
                  <div><strong>Total variations:</strong> {stockAuditSummary.total_variations}</div>
                  <div style={{color: stockAuditSummary.negative_current_stock > 0 ? '#c53030' : '#555'}}>
                    <strong>Negative stock:</strong> {stockAuditSummary.negative_current_stock}
                  </div>
                  <div style={{color: stockAuditSummary.leaks_detected > 0 ? '#c53030' : '#059669', fontWeight: 600}}>
                    <strong>Leaks detected:</strong> {stockAuditSummary.leaks_detected}
                  </div>
                  <button
                    data-testid="run-stock-heal-btn"
                    onClick={runStockHeal}
                    style={{marginLeft:'auto', padding:'6px 14px', background:'#dc2626', color:'#fff', border:'none', borderRadius:'4px', fontSize:'12px', fontWeight:600, cursor:'pointer'}}
                  >
                    🔧 Restore Leaked Stock
                  </button>
                </div>
              )}

              <div style={{overflow:'auto', maxHeight:'55vh'}}>
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:'12px'}}>
                  <thead>
                    <tr style={{background:'#f8f9fa', textAlign:'left', position:'sticky', top:0}}>
                      <th style={{padding:'8px', borderBottom:'1px solid #dee2e6'}}>Product</th>
                      <th style={{padding:'8px', borderBottom:'1px solid #dee2e6'}}>Color</th>
                      <th style={{padding:'8px', borderBottom:'1px solid #dee2e6'}}>Size</th>
                      <th style={{padding:'8px', borderBottom:'1px solid #dee2e6', textAlign:'right'}}>Current</th>
                      <th style={{padding:'8px', borderBottom:'1px solid #dee2e6', textAlign:'right'}}>Sold</th>
                      <th style={{padding:'8px', borderBottom:'1px solid #dee2e6', textAlign:'right'}}>Returned</th>
                      <th style={{padding:'8px', borderBottom:'1px solid #dee2e6', textAlign:'right'}}>Restocks</th>
                      <th style={{padding:'8px', borderBottom:'1px solid #dee2e6', textAlign:'right'}}>Implied Opening</th>
                      <th style={{padding:'8px', borderBottom:'1px solid #dee2e6'}}>Issue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockAuditRows.map(r => (
                      <tr key={r.variation_id || (r.product_id + '_' + (r.color || '') + '_' + (r.size || ''))}
                          data-testid={`stock-audit-row-${r.variation_id || (r.product_id + '_' + (r.color || '') + '_' + (r.size || ''))}`}
                          style={{borderBottom:'1px solid #f1f3f5', background: (r.issues || []).length > 0 ? '#fef2f2' : 'white'}}>
                        <td style={{padding:'8px'}}>{r.product_name}</td>
                        <td style={{padding:'8px'}}>{r.color || '—'}</td>
                        <td style={{padding:'8px'}}>{r.size || '—'}</td>
                        <td style={{padding:'8px', textAlign:'right', fontWeight: r.current_stock < 0 ? 700 : 400, color: r.current_stock < 0 ? '#c53030' : 'inherit'}}>{r.current_stock} {r.unit}</td>
                        <td style={{padding:'8px', textAlign:'right'}}>{r.sold_total}</td>
                        <td style={{padding:'8px', textAlign:'right'}}>{r.returned_total}</td>
                        <td style={{padding:'8px', textAlign:'right'}}>{r.restocks_added}</td>
                        <td style={{padding:'8px', textAlign:'right', fontWeight: r.implied_opening_stock < 0 ? 700 : 400, color: r.implied_opening_stock < 0 ? '#c53030' : 'inherit'}}>{r.implied_opening_stock}</td>
                        <td style={{padding:'8px', color:'#c53030', fontSize:'11px'}}>{(r.issues || []).join(', ') || 'OK'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {stockAuditRows.length === 0 && (
                  <div style={{padding:'20px', textAlign:'center', color:'#666'}}>No variations found.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 💾 Automated MongoDB Backups Modal (zobayr-only) */}
      {showBackupsModal && (
        <div className="modal-overlay" onClick={() => setShowBackupsModal(false)} data-testid="backups-modal">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '900px', maxHeight: '85vh', overflow: 'auto'}}>
            <div className="modal-header">
              <h3>💾 Automated MongoDB Backups</h3>
              <button className="modal-close" onClick={() => setShowBackupsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{padding:'12px 14px', background:'#e0f2fe', border:'1px solid #7dd3fc', borderRadius:'6px', fontSize:'13px', color:'#0c4a6e', marginBottom:'16px'}}>
                <strong>🔄 Auto-backup schedule:</strong> Every {Math.round((backupsMeta.interval_seconds || 3600) / 60)} minutes the entire database is snapshotted and saved as a downloadable .zip archive. The most recent <strong>{backupsMeta.retention}</strong> backups are kept (older ones are auto-pruned).
                <br />
                <span style={{fontSize:'12px', color:'#075985'}}>Every collection is included — orders, customers, products, credit notes, expenses, users, everything. Download any backup as insurance against accidental data loss.</span>
              </div>

              <div style={{display:'flex', gap:'10px', alignItems:'center', marginBottom:'14px', flexWrap:'wrap'}}>
                <div style={{fontSize:'13px', color:'#333'}}>
                  <strong>{backupsMeta.count}</strong> backup{backupsMeta.count === 1 ? '' : 's'} available
                </div>
                <button
                  data-testid="create-backup-now-btn"
                  onClick={triggerManualBackup}
                  disabled={creatingBackup}
                  style={{marginLeft:'auto', padding:'8px 16px', background:'#059669', color:'#fff', border:'none', borderRadius:'4px', fontSize:'12px', fontWeight:600, cursor: creatingBackup ? 'not-allowed' : 'pointer', opacity: creatingBackup ? 0.6 : 1}}
                >
                  {creatingBackup ? '⏳ Creating…' : '📸 Backup Now'}
                </button>
              </div>

              <div style={{overflow:'auto', maxHeight:'55vh', border:'1px solid #e5e7eb', borderRadius:'6px'}}>
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:'13px'}}>
                  <thead>
                    <tr style={{background:'#f8f9fa', textAlign:'left', position:'sticky', top:0}}>
                      <th style={{padding:'10px', borderBottom:'1px solid #dee2e6'}}>Filename</th>
                      <th style={{padding:'10px', borderBottom:'1px solid #dee2e6'}}>Created At</th>
                      <th style={{padding:'10px', borderBottom:'1px solid #dee2e6'}}>Type</th>
                      <th style={{padding:'10px', borderBottom:'1px solid #dee2e6', textAlign:'right'}}>Size</th>
                      <th style={{padding:'10px', borderBottom:'1px solid #dee2e6', textAlign:'center', width:'220px'}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backupsList.map(b => (
                      <tr key={b.filename} data-testid={`backup-row-${b.filename}`} style={{borderBottom:'1px solid #f1f3f5'}}>
                        <td style={{padding:'10px', fontFamily:'monospace', fontSize:'11px'}}>{b.filename}</td>
                        <td style={{padding:'10px', fontSize:'12px', color:'#555'}}>{new Date(b.created_at).toLocaleString('en-GB')}</td>
                        <td style={{padding:'10px'}}>
                          <span style={{padding:'2px 8px', borderRadius:'10px', fontSize:'11px', fontWeight:600, background: b.trigger === 'manual' ? '#fef3c7' : '#dbeafe', color: b.trigger === 'manual' ? '#92400e' : '#1e40af'}}>
                            {b.trigger === 'manual' ? '🖐️ Manual' : '⏰ Hourly'}
                          </span>
                        </td>
                        <td style={{padding:'10px', textAlign:'right', fontFamily:'monospace'}}>{b.size_human}</td>
                        <td style={{padding:'10px', textAlign:'center'}}>
                          <button
                            data-testid={`download-backup-${b.filename}`}
                            onClick={() => downloadBackup(b.filename)}
                            style={{padding:'5px 10px', background:'#2563eb', color:'#fff', border:'none', borderRadius:'4px', fontSize:'11px', cursor:'pointer', marginRight:'6px'}}
                          >⬇️ Download</button>
                          <button
                            data-testid={`delete-backup-${b.filename}`}
                            onClick={() => deleteBackup(b.filename)}
                            style={{padding:'5px 10px', background:'#dc2626', color:'#fff', border:'none', borderRadius:'4px', fontSize:'11px', cursor:'pointer'}}
                          >🗑️ Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {backupsList.length === 0 && (
                  <div style={{padding:'30px', textAlign:'center', color:'#666'}}>
                    No backups yet. The first hourly snapshot will run automatically, or click <strong>📸 Backup Now</strong> above to create one immediately.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showAdminOverrideModal && (
        <div className="modal-overlay" onClick={() => setShowAdminOverrideModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '900px'}}>
            <div className="modal-header">
              <h3>Account Management</h3>
              <button className="modal-close" onClick={() => setShowAdminOverrideModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{display: 'flex', gap: '12px', marginBottom: '1rem', flexWrap: 'wrap'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                  <span style={{display:'inline-block', width:'10px', height:'10px', borderRadius:'50%', background:'#7c3aed'}}></span>
                  <span style={{fontSize:'12px', color:'#555'}}>Owner (full control)</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                  <span style={{display:'inline-block', width:'10px', height:'10px', borderRadius:'50%', background:'#2563eb'}}></span>
                  <span style={{fontSize:'12px', color:'#555'}}>Manager (full access, editable)</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                  <span style={{display:'inline-block', width:'10px', height:'10px', borderRadius:'50%', background:'#059669'}}></span>
                  <span style={{fontSize:'12px', color:'#555'}}>Worker (view + create orders only)</span>
                </div>
                {loggedInAdminUsername.toLowerCase() === 'zobayr' && (
                  <button
                    data-testid="open-stock-audit-btn"
                    className="btn small"
                    style={{marginLeft:'auto', fontSize: '12px', padding: '6px 12px', background:'#f59e0b', color:'#fff', border:'none', fontWeight:600, borderRadius:'4px'}}
                    onClick={loadStockAudit}
                    disabled={loadingStockAudit}
                  >
                    {loadingStockAudit ? '⏳ Loading…' : '📦 Stock Audit & Heal'}
                  </button>
                )}
                {loggedInAdminUsername.toLowerCase() === 'zobayr' && (
                  <button
                    data-testid="open-backups-btn"
                    className="btn small"
                    style={{fontSize: '12px', padding: '6px 12px', background:'#0ea5e9', color:'#fff', border:'none', fontWeight:600, borderRadius:'4px'}}
                    onClick={loadBackups}
                    disabled={loadingBackups}
                  >
                    {loadingBackups ? '⏳ Loading…' : '💾 Backups'}
                  </button>
                )}
              </div>
              
              {loadingAdminAccounts ? (
                <div className="loading-message">Loading accounts...</div>
              ) : (
                <div className="table-responsive">
                  <table className="admin-table" data-testid="admin-accounts-table">
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Role</th>
                        <th>Password</th>
                        <th>Dashboard</th>
                        <th>Settings</th>
                        <th style={{minWidth: '220px'}}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminAccounts.map((account, index) => {
                        const isOwner = account.username.toLowerCase() === 'zobayr';
                        const isSelf = account.username.toLowerCase() === loggedInAdminUsername.toLowerCase();
                        const roleColor = isOwner ? '#7c3aed' : account.role === 'Manager' ? '#2563eb' : '#059669';
                        
                        return (
                          <tr key={'acct-' + index} data-testid={'account-row-' + account.username}>
                            <td>
                              <strong>{account.username}</strong>
                              {isOwner && <span style={{fontSize:'10px', color:'#7c3aed', marginLeft:'6px'}}>(OWNER)</span>}
                              {isSelf && <span style={{fontSize:'10px', color:'#f59e0b', marginLeft:'6px'}}>(YOU)</span>}
                            </td>
                            <td>
                              <span data-testid={'role-badge-' + account.username} style={{
                                padding: '4px 10px', 
                                borderRadius: '12px', 
                                fontSize: '11px',
                                fontWeight: '600',
                                background: roleColor,
                                color: 'white'
                              }}>
                                {account.role}
                              </span>
                            </td>
                            <td>
                              <code style={{backgroundColor: '#f7fafc', padding: '3px 6px', borderRadius: '4px', fontSize: '0.85rem'}}>
                                {account.password}
                              </code>
                            </td>
                            <td style={{textAlign: 'center'}}>
                              <span style={{color: (isOwner || account.can_access_dashboard) ? '#059669' : '#dc2626', fontWeight:'600', fontSize:'12px'}}>
                                {(isOwner || account.can_access_dashboard) ? 'YES' : 'NO'}
                              </span>
                            </td>
                            <td style={{textAlign: 'center'}}>
                              <span style={{color: (isOwner || account.can_access_settings) ? '#059669' : '#dc2626', fontWeight:'600', fontSize:'12px'}}>
                                {(isOwner || account.can_access_settings) ? 'YES' : 'NO'}
                              </span>
                            </td>
                            <td>
                              {isOwner ? (
                                <span style={{fontSize:'11px', color:'#888', fontStyle:'italic'}}>Protected account</span>
                              ) : (
                                <div style={{display: 'flex', gap: '4px', flexWrap: 'wrap'}}>
                                  {account.role !== 'Manager' && (
                                    <button 
                                      data-testid={'promote-' + account.username}
                                      className="btn small primary"
                                      style={{fontSize: '10px', padding: '3px 8px'}}
                                      onClick={async () => {
                                        if (window.confirm('Promote ' + account.username + ' to Manager? They will have full access to all sections.')) {
                                          try {
                                            const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
                                            await axios.put(API + '/api/admin/update-role/' + account.username, {role: 'manager'}, {headers: {'Authorization': 'Bearer ' + token}});
                                            loadAdminAccounts();
                                          } catch(err) {
                                            alert(err.response?.data?.detail || 'Error updating role');
                                          }
                                        }
                                      }}
                                    >
                                      Promote to Manager
                                    </button>
                                  )}
                                  {account.role !== 'Worker' && (
                                    <button 
                                      data-testid={'demote-' + account.username}
                                      className="btn small"
                                      style={{fontSize: '10px', padding: '3px 8px', background: '#f59e0b', color: 'white', border: 'none'}}
                                      onClick={async () => {
                                        if (window.confirm('Set ' + account.username + ' as Worker? They will only be able to view sections and create orders.')) {
                                          try {
                                            const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
                                            await axios.put(API + '/api/admin/update-role/' + account.username, {role: 'worker'}, {headers: {'Authorization': 'Bearer ' + token}});
                                            loadAdminAccounts();
                                          } catch(err) {
                                            alert(err.response?.data?.detail || 'Error updating role');
                                          }
                                        }
                                      }}
                                    >
                                      Set as Worker
                                    </button>
                                  )}
                                  <button 
                                    className="btn small secondary"
                                    style={{fontSize: '10px', padding: '3px 8px'}}
                                    onClick={() => {
                                      const newPassword = prompt('Enter new password for ' + account.username + ':');
                                      if (newPassword) handleResetAdminPassword(account.username, newPassword);
                                    }}
                                  >
                                    Reset Password
                                  </button>
                                  {!isSelf && (
                                    <button 
                                      data-testid={'delete-' + account.username}
                                      className="btn small danger"
                                      style={{fontSize: '10px', padding: '3px 8px'}}
                                      onClick={() => handleDeleteAdmin(account.username)}
                                    >
                                      Delete
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  
                  {adminAccounts.length === 0 && (
                    <p style={{textAlign: 'center', color: '#718096', padding: '2rem'}}>
                      No accounts found.
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="form-actions">
              <button type="button" className="btn secondary" onClick={() => setShowAdminOverrideModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Query Details Modal */}
      {showQueryDetailsModal && selectedQuery && (
        <div className="modal-overlay" onClick={() => setShowQueryDetailsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '700px'}}>
            <div className="modal-header">
              <h3>Query Details</h3>
              <button className="modal-close" onClick={() => setShowQueryDetailsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="query-details">
                <div className="detail-row">
                  <label>Date Submitted:</label>
                  <span>{selectedQuery.created_at ? new Date(selectedQuery.created_at).toLocaleDateString() + ' at ' + new Date(selectedQuery.created_at).toLocaleTimeString() : 'N/A'}</span>
                </div>
                
                <div className="detail-row">
                  <label>Name:</label>
                  <span>{selectedQuery.name || 'N/A'}</span>
                </div>
                
                <div className="detail-row">
                  <label>Email:</label>
                  <span>{selectedQuery.email || 'N/A'}</span>
                </div>
                
                <div className="detail-row">
                  <label>Phone:</label>
                  <span>{selectedQuery.phone || 'Not provided'}</span>
                </div>
                
                <div className="detail-row">
                  <label>Subject:</label>
                  <span>{selectedQuery.subject || 'General Enquiry'}</span>
                </div>
                
                <div className="detail-row">
                  <label>Status:</label>
                  <span className={`status ${selectedQuery.status || 'unread'}`}>
                    {selectedQuery.status === 'read' ? 'Read' : 'Unread'}
                  </span>
                </div>
                
                <div className="detail-row message-row">
                  <label>Message:</label>
                  <div className="message-content">
                    {selectedQuery.message ? selectedQuery.message.split('\n').map((line, index) => (
                      <p key={index}>{line}</p>
                    )) : 'No message provided'}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              {selectedQuery.status === 'unread' && (
                <button 
                  className="btn primary" 
                  onClick={() => {
                    handleMarkQueryRead(selectedQuery.id);
                    setShowQueryDetailsModal(false);
                  }}
                >
                  Mark as Read & Close
                </button>
              )}
              <button 
                className="btn secondary" 
                onClick={() => setShowQueryDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Return Modal */}
      {showReturnModal && returnOrder && (
        <div className="modal-overlay" onClick={() => setShowReturnModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '900px', maxHeight: '80vh', overflow: 'auto'}}>
            <div className="modal-header">
              <h3>Process Return - Order #{returnOrder.id.substring(0, 8)}</h3>
              <button className="modal-close" onClick={() => setShowReturnModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{marginBottom: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '4px'}}>
                <h4>Customer: {returnOrder.customer_name || 'Guest Customer'}</h4>
                <p>Order Date: {new Date(returnOrder.date).toLocaleDateString()}</p>
                <p>Order Status: {returnOrder.status}</p>
              </div>
              
              <h4>Select Items to Return:</h4>
              <div className="return-items-list">
                {returnItems.map((item, index) => (
                  <div key={index} className="return-item" style={{
                    border: '1px solid #ddd', 
                    borderRadius: '4px', 
                    padding: '1rem', 
                    marginBottom: '1rem',
                    background: item.selected ? '#f0f8ff' : '#fff'
                  }}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                      <input 
                        type="checkbox" 
                        checked={item.selected}
                        onChange={(e) => {
                          const updated = [...returnItems];
                          updated[index].selected = e.target.checked;
                          if (!e.target.checked) updated[index].return_quantity = 0;
                          else updated[index].return_quantity = item.max_returnable || item.remaining_quantity || item.quantity;
                          setReturnItems(updated);
                        }}
                      />
                      <div style={{flex: 1}}>
                        <strong>{item.product_name}</strong>
                        <div>Size: {(() => {
                          // For square meter products, display width x length dimensions
                          if (item.wide && item.length) {
                            return `${item.wide}x${item.length}`;
                          }
                          // For regular products, display the size field
                          return item.size || 'N/A';
                        })()} | Color: {item.color || 'N/A'}</div>
                        <div>Unit Price: £{parseFloat(item.unit_price || 0).toFixed(2)}</div>
                        <div>Available for Return: {item.remaining_quantity || item.quantity} {item.measurement_unit === 'per_square_meter' ? 'm²' : 'units'}</div>
                        {item.returned_quantity > 0 && (
                          <div style={{color: '#888', fontSize: '0.9em'}}>
                            (Originally {item.original_quantity || item.quantity}, {item.returned_quantity} already returned)
                          </div>
                        )}
                      </div>
                      <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                        <label>Return Quantity:</label>
                        <input 
                          type="number" 
                          min="0" 
                          max={item.max_returnable || item.remaining_quantity || item.quantity}
                          value={item.return_quantity || 0}
                          onChange={(e) => {
                            const updated = [...returnItems];
                            const qty = parseInt(e.target.value) || 0;
                            const maxReturnable = item.max_returnable || item.remaining_quantity || item.quantity;
                            updated[index].return_quantity = Math.min(qty, maxReturnable);
                            updated[index].selected = qty > 0;
                            setReturnItems(updated);
                          }}
                          style={{width: '80px', padding: '4px', textAlign: 'center'}}
                          disabled={!item.selected}
                        />
                        <span>{item.measurement_unit === 'per_square_meter' ? 'm²' : 'units'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div style={{marginTop: '1rem', padding: '1rem', background: '#fff3cd', borderRadius: '4px'}}>
                <h5>Return Summary:</h5>
                <p><strong>Selected Items:</strong> {returnItems.filter(item => item.selected && item.return_quantity > 0).length}</p>
                <p><strong>Stock to Restore:</strong></p>
                <ul>
                  {returnItems
                    .filter(item => item.selected && item.return_quantity > 0)
                    .map((item, idx) => (
                      <li key={idx}>
                        {item.product_name} ({item.size} | {item.color}): +{item.return_quantity} {item.measurement_unit === 'per_square_meter' ? 'm²' : 'units'}
                      </li>
                    ))
                  }
                </ul>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn secondary" 
                onClick={() => setShowReturnModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn primary" 
                onClick={processReturn}
                disabled={returnItems.filter(item => item.selected && item.return_quantity > 0).length === 0}
              >
                Process Return & Restore Stock
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Invoice Amount Modal for Credit Orders */}
      {showCustomInvoiceModal && customInvoiceOrder && (
        <div className="modal-overlay" onClick={() => setShowCustomInvoiceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '500px'}}>
            <div className="modal-header">
              <h3>Generate Invoice - Credit Order</h3>
              <button className="modal-close" onClick={() => setShowCustomInvoiceModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p><strong>Order:</strong> #{customInvoiceOrder.id.substring(0, 8)}...</p>
              <p><strong>Total Order Value:</strong> £{parseFloat(customInvoiceOrder.grand_total || 0).toFixed(2)}</p>
              <p><strong>Amount Paid:</strong> £{parseFloat(customInvoiceOrder.credit_paid_amount || 0).toFixed(2)}</p>
              <p><strong>Balance Remaining:</strong> £{parseFloat(customInvoiceOrder.credit_balance || customInvoiceOrder.grand_total || 0).toFixed(2)}</p>
              
              <div className="form-group" style={{marginTop: '1rem'}}>
                <label><strong>Invoice Amount:</strong></label>
                <input 
                  type="number" 
                  className="form-control" 
                  placeholder="Enter amount to invoice"
                  value={customInvoiceAmount}
                  onChange={(e) => setCustomInvoiceAmount(e.target.value)}
                  step="0.01"
                  min="0.01"
                  max={parseFloat(customInvoiceOrder.grand_total || 0)}
                />
                <small className="form-help">
                  Enter the amount for this specific invoice. This allows you to create multiple invoices for partial payments.
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn secondary" 
                onClick={() => setShowCustomInvoiceModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn primary" 
                onClick={async () => {
                  if (!customInvoiceAmount || parseFloat(customInvoiceAmount) <= 0) {
                    alert('Please enter a valid invoice amount');
                    return;
                  }
                  
                  await generateInvoiceWithAmount(customInvoiceOrder, parseFloat(customInvoiceAmount));
                  setShowCustomInvoiceModal(false);
                  setCustomInvoiceOrder(null);
                  setCustomInvoiceAmount('');
                }}
                disabled={!customInvoiceAmount || parseFloat(customInvoiceAmount) <= 0}
              >
                Generate Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Standalone Invoice Modal */}
      {showStandaloneInvoiceModal && (
        <div className="modal-overlay" onClick={() => setShowStandaloneInvoiceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '600px'}}>
            <div className="modal-header">
              <h3>Create Standalone Invoice</h3>
              <button className="modal-close" onClick={() => setShowStandaloneInvoiceModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{marginBottom: '1rem', color: '#666', fontSize: '0.9rem'}}>
                Create an invoice for a customer payment that doesn&apos;t have a corresponding order
              </p>

              {/* Customer Selection */}
              <div className="form-group" style={{marginBottom: '1rem'}}>
                <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600'}}>Customer *</label>
                <select
                  className="form-control"
                  value={standaloneInvoice.customer_id}
                  onChange={(e) => {
                    const selectedCustomer = customers.find(c => c.id === e.target.value);
                    setStandaloneInvoice({
                      ...standaloneInvoice,
                      customer_id: e.target.value,
                      customer_name: selectedCustomer ? selectedCustomer.name : '',
                      customer_number: selectedCustomer ? selectedCustomer.customer_number : ''
                    });
                  }}
                  required
                >
                  <option value="">Select a customer...</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} {customer.customer_number ? `(${customer.customer_number})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div className="form-group" style={{marginBottom: '1rem'}}>
                <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600'}}>Date *</label>
                <input
                  type="date"
                  className="form-control"
                  value={standaloneInvoice.date}
                  onChange={(e) => setStandaloneInvoice({...standaloneInvoice, date: e.target.value})}
                  required
                />
              </div>

              {/* Amount */}
              <div className="form-group" style={{marginBottom: '1rem'}}>
                <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600'}}>Amount (£) *</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="0.00"
                  value={standaloneInvoice.amount}
                  onChange={(e) => setStandaloneInvoice({...standaloneInvoice, amount: e.target.value})}
                  step="0.01"
                  min="0.01"
                  required
                />
              </div>

              {/* VAT Toggle */}
              <div className="form-group" style={{marginBottom: '1rem'}}>
                <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
                  <input
                    type="checkbox"
                    checked={standaloneInvoice.include_vat}
                    onChange={(e) => setStandaloneInvoice({...standaloneInvoice, include_vat: e.target.checked})}
                    style={{marginRight: '0.5rem'}}
                  />
                  <span style={{fontWeight: '600'}}>Amount includes VAT (20%)</span>
                </label>
                <small style={{display: 'block', marginTop: '0.25rem', color: '#666'}}>
                  {standaloneInvoice.include_vat 
                    ? 'VAT will be calculated from the total amount' 
                    : 'VAT will be added on top of the amount'}
                </small>
              </div>

              {/* Payment Method */}
              <div className="form-group" style={{marginBottom: '1rem'}}>
                <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600'}}>Payment Method *</label>
                <select
                  className="form-control"
                  value={standaloneInvoice.payment_method}
                  onChange={(e) => setStandaloneInvoice({...standaloneInvoice, payment_method: e.target.value})}
                >
                  <option value="cash">💵 Cash</option>
                  <option value="bank">🏦 Bank</option>
                </select>
              </div>

              {/* Description / Notes */}
              <div style={{background:'rgba(184,134,11,0.05)', padding:'1rem', borderRadius:'6px', marginBottom:'1rem', border:'1px solid rgba(184,134,11,0.2)'}}>
                <h4 style={{color:'#b8860b', marginBottom:'0.5rem', fontSize:'1.1rem'}}>📝 Description / Notes</h4>
                <p style={{fontSize:'0.85rem', color:'#666', marginBottom:'0.75rem'}}>
                  Add notes about this payment (optional)
                </p>
                <textarea
                  value={standaloneInvoice.description}
                  onChange={(e) => setStandaloneInvoice({...standaloneInvoice, description: e.target.value})}
                  placeholder="Example: Payment for carpet installation, Balance payment for order, Deposit for upcoming order..."
                  style={{
                    width: '100%',
                    padding: '8px',
                    minHeight: '100px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    fontSize: '0.95rem',
                    color: '#333',
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
              </div>

              {/* Summary Box */}
              {standaloneInvoice.amount && parseFloat(standaloneInvoice.amount) > 0 && (
                <div style={{
                  padding: '1rem',
                  background: '#f8f9fa',
                  borderRadius: '6px',
                  border: '1px solid #dee2e6',
                  marginTop: '1rem'
                }}>
                  <h5 style={{margin: '0 0 0.5rem 0'}}>Invoice Summary:</h5>
                  {standaloneInvoice.include_vat ? (
                    <>
                      <p style={{margin: '0.25rem 0'}}>
                        <strong>Total Amount:</strong> £{parseFloat(standaloneInvoice.amount).toFixed(2)}
                      </p>
                      <p style={{margin: '0.25rem 0'}}>
                        <strong>Subtotal (excl. VAT):</strong> £{(parseFloat(standaloneInvoice.amount) / 1.20).toFixed(2)}
                      </p>
                      <p style={{margin: '0.25rem 0'}}>
                        <strong>VAT (20%):</strong> £{(parseFloat(standaloneInvoice.amount) - (parseFloat(standaloneInvoice.amount) / 1.20)).toFixed(2)}
                      </p>
                    </>
                  ) : (
                    <>
                      <p style={{margin: '0.25rem 0'}}>
                        <strong>Subtotal:</strong> £{parseFloat(standaloneInvoice.amount).toFixed(2)}
                      </p>
                      <p style={{margin: '0.25rem 0'}}>
                        <strong>VAT (20%):</strong> £{(parseFloat(standaloneInvoice.amount) * 0.20).toFixed(2)}
                      </p>
                      <p style={{margin: '0.25rem 0'}}>
                        <strong>Total Amount:</strong> £{(parseFloat(standaloneInvoice.amount) * 1.20).toFixed(2)}
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn secondary" 
                onClick={() => setShowStandaloneInvoiceModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn primary" 
                onClick={handleCreateStandaloneInvoice}
                disabled={!standaloneInvoice.customer_id || !standaloneInvoice.amount || parseFloat(standaloneInvoice.amount) <= 0}
              >
                Create Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Report Modal */}
      {showProductReportModal && (
        <div className="modal-overlay" onClick={() => setShowProductReportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '90%', width: '1200px', maxHeight: '90%'}}>
            <div className="modal-header">
              <h2>📊 Product Report</h2>
              <button className="modal-close" onClick={() => setShowProductReportModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{maxHeight: '70vh', overflowY: 'auto'}}>
              
              {/* Date Range Selection */}
              <div style={{marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px'}}>
                <h4>Select Date Range</h4>
                <div style={{display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap'}}>
                  <div>
                    <label>From Date:</label>
                    <input 
                      type="date" 
                      className="form-control"
                      value={reportDateFrom}
                      onChange={(e) => setReportDateFrom(e.target.value)}
                      style={{width: '150px'}}
                    />
                  </div>
                  <div>
                    <label>To Date:</label>
                    <input 
                      type="date" 
                      className="form-control"
                      value={reportDateTo}
                      onChange={(e) => setReportDateTo(e.target.value)}
                      style={{width: '150px'}}
                    />
                  </div>
                  <button 
                    className="admin-btn primary"
                    onClick={generateProductReport}
                    disabled={!reportDateFrom || !reportDateTo}
                  >
                    Generate Report
                  </button>
                </div>
              </div>

              {/* Report Results */}
              {productReportData && (
                <div>
                  <div style={{marginBottom: '20px', padding: '15px', background: '#e3f2fd', borderRadius: '8px'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
                      <h4>📊 Product Report Summary ({reportDateFrom} to {reportDateTo})</h4>
                      <button 
                        className="admin-btn success"
                        onClick={downloadProductReport}
                        style={{
                          background: '#28a745',
                          color: 'white',
                          border: 'none',
                          padding: '10px 20px',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        📥 Download PDF Report
                      </button>
                    </div>
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginTop: '10px'}}>
                      <div className="stat-card" style={{background: 'white', padding: '15px', borderRadius: '8px', border: '2px solid #1976d2'}}>
                        <div className="stat-label" style={{fontSize: '13px', color: '#666'}}>Total Products</div>
                        <div className="stat-value" style={{fontSize: '28px', color: '#1976d2', fontWeight: 'bold'}}>{productReportData.summary?.totalProducts || 0}</div>
                      </div>
                      <div className="stat-card" style={{background: 'white', padding: '15px', borderRadius: '8px', border: '2px solid #28a745'}}>
                        <div className="stat-label" style={{fontSize: '13px', color: '#666'}}>Total Variations</div>
                        <div className="stat-value" style={{fontSize: '28px', color: '#28a745', fontWeight: 'bold'}}>{productReportData.summary?.totalVariations || 0}</div>
                      </div>
                      <div className="stat-card" style={{background: 'white', padding: '15px', borderRadius: '8px', border: '2px solid #ff9800'}}>
                        <div className="stat-label" style={{fontSize: '13px', color: '#666'}}>Products with Sales</div>
                        <div className="stat-value" style={{fontSize: '28px', color: '#ff9800', fontWeight: 'bold'}}>{productReportData.summary?.productsSold || 0}</div>
                      </div>
                    </div>
                  </div>

                  {/* Product Details Table - Simplified */}
                  <div style={{overflowX: 'auto'}}>
                    <table className="admin-table" style={{width: '100%', fontSize: '13px'}}>
                      <thead>
                        <tr style={{background: '#f8f9fa'}}>
                          <th style={{minWidth: '200px', textAlign: 'left'}}>Product Name</th>
                          <th style={{textAlign: 'center'}}>Qty Sold</th>
                          <th style={{textAlign: 'center'}}>Available Stock</th>
                          <th style={{textAlign: 'center'}}>Restock Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          let currentProductName = '';
                          const rows = [];
                          
                          productReportData.products?.forEach((item, index) => {
                            // Add product header row if this is a new product
                            if (item.productName !== currentProductName) {
                              currentProductName = item.productName;
                              
                              // Calculate totals for this product group
                              const productVariations = productReportData.products.filter(p => p.productName === item.productName);
                              const productTotals = productVariations.reduce((totals, variation) => ({
                                quantitySold: totals.quantitySold + variation.quantitySold,
                                availableStock: totals.availableStock + variation.availableStock
                              }), {
                                quantitySold: 0, availableStock: 0
                              });
                              
                              rows.push(
                                <tr key={`header-${item.productName}`} style={{background: '#e3f2fd', fontWeight: 'bold', borderTop: '2px solid #1976d2'}}>
                                  <td style={{fontWeight: 'bold', fontSize: '14px', color: '#1976d2', padding: '12px'}}>
                                    📦 {item.productName} ({productVariations.length} variation{productVariations.length > 1 ? 's' : ''})
                                  </td>
                                  <td style={{fontWeight: 'bold', textAlign: 'center'}}>{productTotals.quantitySold.toFixed(2)} {item.unit}</td>
                                  <td style={{fontWeight: 'bold', textAlign: 'center'}}>{productTotals.availableStock.toFixed(2)} {item.unit}</td>
                                  <td style={{fontWeight: 'bold', textAlign: 'center'}}>-</td>
                                </tr>
                              );
                            }
                            
                            // Add variation row
                            rows.push(
                              <tr key={`variation-${index}`} style={{background: 'white', borderBottom: '1px solid #e0e0e0'}}>
                                <td style={{paddingLeft: '30px', fontSize: '13px', padding: '10px 10px 10px 30px'}}>
                                  └─ {item.variationName || 'Standard'}
                                </td>
                                <td style={{textAlign: 'center'}}>{item.quantitySold.toFixed(2)} {item.unit}</td>
                                <td style={{textAlign: 'center'}}>{item.availableStock.toFixed(2)} {item.unit}</td>
                                <td style={{textAlign: 'center', color: '#666'}}>{item.restockDate || 'N/A'}</td>
                              </tr>
                            );
                          });
                          
                          return rows;
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 💳 Payment History Modal - Full audit view of a customer's orders + payments */}
      {paymentHistoryCustomer && (
        <div className="modal-overlay" onClick={() => setPaymentHistoryCustomer(null)} style={{background: 'rgba(0,0,0,0.65)'}} data-testid="payment-history-modal">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '1200px', width: '95%', maxHeight: '92vh', overflowY: 'auto', background: 'white'}}>
            <div className="modal-header" style={{background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)', color: 'white', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <div>
                <h2 style={{margin: 0, display: 'flex', alignItems: 'center', gap: '10px'}}>
                  💳 Payment History — {paymentHistoryCustomer.name}
                </h2>
                <div style={{fontSize: '13px', marginTop: '4px', opacity: 0.95}}>
                  {paymentHistoryCustomer.customer_number || 'N/A'}{paymentHistoryCustomer.company_name ? ` · ${paymentHistoryCustomer.company_name}` : ''}
                </div>
              </div>
              <button onClick={() => setPaymentHistoryCustomer(null)} data-testid="close-payment-history-btn" style={{background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px'}}>✕ Close</button>
            </div>
            <div style={{padding: '20px'}}>
              {paymentHistoryLoading && <div style={{textAlign: 'center', padding: '40px', color: '#666'}}>Loading...</div>}
              {!paymentHistoryLoading && paymentHistoryData && (() => {
                const orders = paymentHistoryData.orders || [];
                const accountPayments = paymentHistoryData.accountPayments || [];
                const accountPaymentsTotal = paymentHistoryData.accountPaymentsTotal || 0;
                let gross = 0, paidCash = 0, paidBank = 0, paidCredit = 0, outstanding = 0;
                orders.forEach(o => {
                  if (['deleted', 'cancelled', 'returned', 'refunded'].includes((o.status || '').toLowerCase())) return;
                  gross += parseFloat(o.grand_total || 0);
                  paidCash += parseFloat(o.cash_paid_amount || 0);
                  paidBank += parseFloat(o.bank_paid_amount || 0);
                  paidCredit += parseFloat(o.credit_paid_amount || 0);
                  outstanding += Math.max(0, parseFloat(o._computed_outstanding || 0));
                });
                const netOutstanding = Math.max(0, outstanding - accountPaymentsTotal);
                return (
                  <>
                    {/* Summary cards */}
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginBottom: '20px'}}>
                      {[
                        {label: 'Total Bills', value: `£${gross.toFixed(2)}`, color: '#1a365d'},
                        {label: 'Paid (Cash)', value: `£${paidCash.toFixed(2)}`, color: '#28a745'},
                        {label: 'Paid (Bank)', value: `£${paidBank.toFixed(2)}`, color: '#0d6efd'},
                        {label: 'Paid (Credit)', value: `£${paidCredit.toFixed(2)}`, color: '#6f42c1'},
                        {label: 'Lump Sums', value: `£${accountPaymentsTotal.toFixed(2)}`, color: '#fd7e14'},
                        {label: 'Net Outstanding', value: `£${netOutstanding.toFixed(2)}`, color: netOutstanding > 0 ? '#dc3545' : '#28a745'}
                      ].map((s, i) => (
                        <div key={i} style={{background: '#f8f9fa', padding: '12px', borderRadius: '6px', borderLeft: `4px solid ${s.color}`}}>
                          <div style={{fontSize: '11px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px'}}>{s.label}</div>
                          <div style={{fontSize: '20px', fontWeight: 700, color: s.color, marginTop: '4px'}}>{s.value}</div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Orders table */}
                    <h3 style={{marginTop: 0, color: '#1a365d'}}>📋 All Orders ({orders.length})</h3>
                    {orders.length === 0 ? (
                      <div style={{padding: '20px', textAlign: 'center', color: '#666', background: '#f8f9fa', borderRadius: '6px'}}>No orders found for this customer</div>
                    ) : (
                      <div style={{overflowX: 'auto'}}>
                        <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px'}}>
                          <thead>
                            <tr style={{background: '#1a365d', color: 'white'}}>
                              <th style={{padding: '8px', textAlign: 'left'}}>Date</th>
                              <th style={{padding: '8px', textAlign: 'left'}}>Order #</th>
                              <th style={{padding: '8px', textAlign: 'left'}}>Status</th>
                              <th style={{padding: '8px', textAlign: 'left'}}>Pay Status</th>
                              <th style={{padding: '8px', textAlign: 'right'}}>Total</th>
                              <th style={{padding: '8px', textAlign: 'right'}}>Cash</th>
                              <th style={{padding: '8px', textAlign: 'right'}}>Bank</th>
                              <th style={{padding: '8px', textAlign: 'right'}}>Credit Paid</th>
                              <th style={{padding: '8px', textAlign: 'right'}}>Outstanding</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orders.map(o => {
                              const out = parseFloat(o._computed_outstanding || 0);
                              const isExcluded = ['deleted', 'cancelled', 'returned', 'refunded'].includes((o.status || '').toLowerCase());
                              return (
                                <tr key={o.id} style={{borderBottom: '1px solid #eee', opacity: isExcluded ? 0.5 : 1, background: out > 0 && !isExcluded ? '#fff3cd' : 'white'}}>
                                  <td style={{padding: '8px'}}>{(o.date || '').substring(0, 10)}</td>
                                  <td style={{padding: '8px', fontFamily: 'monospace', fontSize: '11px'}}>{o.vat_invoice_number || (o.id || '').substring(0, 8)}</td>
                                  <td style={{padding: '8px'}}><span style={{padding: '2px 6px', borderRadius: '3px', background: '#e9ecef', fontSize: '11px'}}>{o.status}</span></td>
                                  <td style={{padding: '8px', fontSize: '11px'}}>{o.payment_status || '-'}</td>
                                  <td style={{padding: '8px', textAlign: 'right', fontWeight: 600}}>£{parseFloat(o.grand_total || 0).toFixed(2)}</td>
                                  <td style={{padding: '8px', textAlign: 'right'}}>£{parseFloat(o.cash_paid_amount || 0).toFixed(2)}</td>
                                  <td style={{padding: '8px', textAlign: 'right'}}>£{parseFloat(o.bank_paid_amount || 0).toFixed(2)}</td>
                                  <td style={{padding: '8px', textAlign: 'right'}}>£{parseFloat(o.credit_paid_amount || 0).toFixed(2)}</td>
                                  <td style={{padding: '8px', textAlign: 'right', color: out > 0 ? '#dc3545' : '#28a745', fontWeight: 700}}>£{out.toFixed(2)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr style={{background: '#1a365d', color: 'white', fontWeight: 700}}>
                              <td colSpan="4" style={{padding: '10px'}}>TOTALS</td>
                              <td style={{padding: '10px', textAlign: 'right'}}>£{gross.toFixed(2)}</td>
                              <td style={{padding: '10px', textAlign: 'right'}}>£{paidCash.toFixed(2)}</td>
                              <td style={{padding: '10px', textAlign: 'right'}}>£{paidBank.toFixed(2)}</td>
                              <td style={{padding: '10px', textAlign: 'right'}}>£{paidCredit.toFixed(2)}</td>
                              <td style={{padding: '10px', textAlign: 'right'}}>£{outstanding.toFixed(2)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                    
                    {/* Lump-sum payments table */}
                    <h3 style={{marginTop: '24px', color: '#fd7e14'}}>💵 Lump-Sum Payments ({accountPayments.length})</h3>
                    {accountPayments.length === 0 ? (
                      <div style={{padding: '20px', textAlign: 'center', color: '#666', background: '#f8f9fa', borderRadius: '6px'}}>No lump-sum payments on record</div>
                    ) : (
                      <div style={{overflowX: 'auto'}}>
                        <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px'}}>
                          <thead>
                            <tr style={{background: '#fd7e14', color: 'white'}}>
                              <th style={{padding: '8px', textAlign: 'left'}}>Date</th>
                              <th style={{padding: '8px', textAlign: 'left'}}>Method</th>
                              <th style={{padding: '8px', textAlign: 'right'}}>Amount</th>
                              <th style={{padding: '8px', textAlign: 'left'}}>Notes</th>
                              <th style={{padding: '8px', textAlign: 'left'}}>Recorded By</th>
                            </tr>
                          </thead>
                          <tbody>
                            {accountPayments.map((p, i) => (
                              <tr key={p.id || i} style={{borderBottom: '1px solid #eee'}}>
                                <td style={{padding: '8px'}}>{((p.payment_date || p.created_at || '') + '').substring(0, 10)}</td>
                                <td style={{padding: '8px'}}><span style={{padding: '2px 6px', borderRadius: '3px', background: '#e9ecef', fontSize: '11px', textTransform: 'uppercase'}}>{p.payment_method || 'cash'}</span></td>
                                <td style={{padding: '8px', textAlign: 'right', fontWeight: 700, color: '#28a745'}}>£{parseFloat(p.amount || 0).toFixed(2)}</td>
                                <td style={{padding: '8px', fontSize: '12px'}}>{p.notes || '-'}</td>
                                <td style={{padding: '8px', fontSize: '12px'}}>{p.recorded_by || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr style={{background: '#fd7e14', color: 'white', fontWeight: 700}}>
                              <td colSpan="2" style={{padding: '10px'}}>TOTAL LUMP SUMS</td>
                              <td style={{padding: '10px', textAlign: 'right'}}>£{accountPaymentsTotal.toFixed(2)}</td>
                              <td colSpan="2"></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                    
                    {/* Reconciliation banner */}
                    <div style={{marginTop: '24px', padding: '16px', background: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '6px'}}>
                      <strong style={{color: '#856404'}}>📐 Reconciliation:</strong>{' '}
                      <span style={{fontSize: '14px'}}>
                        Total Bills £{gross.toFixed(2)} − Paid on Bills £{(paidCash + paidBank + paidCredit).toFixed(2)} = Bill Outstanding £{outstanding.toFixed(2)} − Lump Sums £{accountPaymentsTotal.toFixed(2)} = <strong style={{color: netOutstanding > 0 ? '#dc3545' : '#28a745'}}>Net Outstanding £{netOutstanding.toFixed(2)}</strong>
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Lump Sum Payment Modal */}
      {/* 🔒 ZOBAYR ONLY: Cancelled Bills Modal */}
      {showCancelledModal && (
        <div className="modal-overlay" onClick={() => setShowCancelledModal(false)} style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999}}>
          <div onClick={(e)=>e.stopPropagation()} style={{background:'white', borderRadius:'10px', width:'min(900px, 92vw)', maxHeight:'85vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 12px 48px rgba(0,0,0,0.3)'}}>
            <div style={{padding:'18px 24px', borderBottom:'1px solid #e9ecef', display:'flex', justifyContent:'space-between', alignItems:'center', background:'linear-gradient(135deg, #9B2C2C 0%, #c53030 100%)', color:'white'}}>
              <div>
                <div style={{fontSize:'18px', fontWeight:'700'}}>🚫 Cancelled Bills</div>
                <div style={{fontSize:'12px', opacity:0.9}}>Restore any cancelled bill back to Fulfilled Orders (Owner only)</div>
              </div>
              <button onClick={()=>setShowCancelledModal(false)} style={{background:'rgba(255,255,255,0.2)', color:'white', border:'none', padding:'6px 12px', borderRadius:'4px', cursor:'pointer', fontWeight:'700', fontSize:'18px'}}>×</button>
            </div>
            <div style={{padding:'16px 24px', overflowY:'auto', flex:1}}>
              {loadingCancelled ? (
                <div style={{textAlign:'center', padding:'30px', color:'#666'}}>Loading cancelled bills…</div>
              ) : cancelledOrders.length === 0 ? (
                <div style={{textAlign:'center', padding:'40px', color:'#666'}}>
                  <div style={{fontSize:'32px', marginBottom:'8px'}}>✨</div>
                  <div style={{fontWeight:'600', color:'#333'}}>No cancelled bills</div>
                  <div style={{fontSize:'13px', marginTop:'4px'}}>You&apos;re all clear — nothing to restore.</div>
                </div>
              ) : (
                <>
                  <div style={{fontSize:'13px', color:'#666', marginBottom:'12px'}}><strong>{cancelledOrders.length}</strong> cancelled bill{cancelledOrders.length===1?'':'s'}</div>
                  <table style={{width:'100%', borderCollapse:'collapse', fontSize:'13px'}}>
                    <thead>
                      <tr style={{background:'#f8f9fa', textAlign:'left'}}>
                        <th style={{padding:'8px', borderBottom:'1px solid #dee2e6'}}>Order ID</th>
                        <th style={{padding:'8px', borderBottom:'1px solid #dee2e6'}}>Customer</th>
                        <th style={{padding:'8px', borderBottom:'1px solid #dee2e6'}}>Date</th>
                        <th style={{padding:'8px', borderBottom:'1px solid #dee2e6', textAlign:'right'}}>Total</th>
                        <th style={{padding:'8px', borderBottom:'1px solid #dee2e6', textAlign:'right'}}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cancelledOrders.map(o => (
                        <tr key={o.id} style={{borderBottom:'1px solid #f1f3f5'}} data-testid={`cancelled-row-${o.id}`}>
                          <td style={{padding:'10px 8px', fontFamily:'monospace', fontSize:'12px'}}>#{(o.id||'').slice(0,8)}</td>
                          <td style={{padding:'10px 8px'}}>
                            <div style={{fontWeight:'600'}}>{o.customer_name || 'Unknown'}</div>
                            {o.customer_number && <div style={{fontSize:'11px', color:'#666'}}>{o.customer_number}</div>}
                          </td>
                          <td style={{padding:'10px 8px', color:'#555'}}>{o.date ? new Date(o.date).toLocaleDateString('en-GB') : '—'}</td>
                          <td style={{padding:'10px 8px', textAlign:'right', fontWeight:'600'}}>£{parseFloat(o.grand_total||0).toFixed(2)}</td>
                          <td style={{padding:'10px 8px', textAlign:'right'}}>
                            <button
                              data-testid={`restore-cancelled-${o.id}`}
                              onClick={()=>restoreCancelledOrder(o.id)}
                              style={{background:'#28a745', color:'white', border:'none', padding:'6px 12px', borderRadius:'4px', cursor:'pointer', fontSize:'12px', fontWeight:'600'}}
                            >
                              ↩ Restore
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}


      {showLumpSumModal && lumpSumCustomer && (        <div className="modal-overlay" onClick={() => setShowLumpSumModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '500px', width: '100%'}}>
            <div className="modal-header" style={{background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)', color: 'white'}}>
              <h2 style={{margin: 0, display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '24px'}}>💰</span>
                Record Lump Sum Payment
              </h2>
              <button className="modal-close" onClick={() => setShowLumpSumModal(false)} style={{color: 'white', fontSize: '28px'}}>×</button>
            </div>
            <div className="modal-body" style={{padding: '20px'}}>
              {/* Customer Info */}
              <div style={{
                background: '#f8f9fa',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '20px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                  <span style={{fontSize: '24px', fontWeight: 'bold', color: '#007bff'}}>{lumpSumCustomer.customerNumber}</span>
                  <div>
                    <div style={{fontWeight: '600', fontSize: '16px', color: '#333'}}>{lumpSumCustomer.name}</div>
                    {lumpSumCustomer.company && <div style={{fontSize: '13px', color: '#666'}}>{lumpSumCustomer.company}</div>}
                  </div>
                </div>
                <div style={{marginTop: '12px', padding: '10px', background: '#fff', borderRadius: '6px', border: '1px solid #dc3545'}}>
                  <div style={{fontSize: '13px', color: '#666'}}>Current Outstanding Balance</div>
                  <div style={{fontSize: '24px', fontWeight: 'bold', color: '#dc3545'}}>£{lumpSumCustomer.totalOutstanding.toFixed(2)}</div>
                </div>
              </div>

              {/* Payment Form */}
              <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                {/* Amount Input */}
                <div>
                  <label style={{display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333'}}>
                    Payment Amount <span style={{color: '#dc3545'}}>*</span>
                  </label>
                  <div style={{position: 'relative'}}>
                    <span style={{position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '18px', color: '#666'}}>£</span>
                    <input
                      type="number"
                      data-testid="lump-sum-amount-input"
                      value={lumpSumAmount}
                      onChange={(e) => setLumpSumAmount(e.target.value)}
                      placeholder="0.00"
                      step="0.01"
                      min="0.01"
                      max={lumpSumCustomer.totalOutstanding}
                      style={{
                        width: '100%',
                        padding: '12px 12px 12px 30px',
                        fontSize: '18px',
                        border: '2px solid #dee2e6',
                        borderRadius: '6px',
                        fontWeight: '600'
                      }}
                    />
                  </div>
                  {parseFloat(lumpSumAmount || 0) > lumpSumCustomer.totalOutstanding && (
                    <div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
                      ⚠️ Amount exceeds outstanding balance
                    </div>
                  )}
                </div>

                {/* Payment Method */}
                <div>
                  <label style={{display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333'}}>
                    Payment Method <span style={{color: '#dc3545'}}>*</span>
                  </label>
                  <div style={{display: 'flex', gap: '10px'}}>
                    <button
                      data-testid="lump-sum-method-cash"
                      onClick={() => setLumpSumMethod('cash')}
                      style={{
                        flex: 1,
                        padding: '12px',
                        border: lumpSumMethod === 'cash' ? '2px solid #28a745' : '2px solid #dee2e6',
                        borderRadius: '6px',
                        background: lumpSumMethod === 'cash' ? '#d4edda' : 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontWeight: lumpSumMethod === 'cash' ? '600' : '400'
                      }}
                    >
                      💵 Cash
                    </button>
                    <button
                      data-testid="lump-sum-method-bank"
                      onClick={() => setLumpSumMethod('bank')}
                      style={{
                        flex: 1,
                        padding: '12px',
                        border: lumpSumMethod === 'bank' ? '2px solid #007bff' : '2px solid #dee2e6',
                        borderRadius: '6px',
                        background: lumpSumMethod === 'bank' ? '#cce5ff' : 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontWeight: lumpSumMethod === 'bank' ? '600' : '400'
                      }}
                    >
                      🏦 Bank Transfer
                    </button>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label style={{display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333'}}>
                    Payment Date <span style={{color: '#dc3545'}}>*</span>
                  </label>
                  <input
                    type="date"
                    data-testid="lump-sum-date-input"
                    value={lumpSumPaymentDate}
                    onChange={(e) => setLumpSumPaymentDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #dee2e6',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  <p style={{fontSize: '11px', color: '#666', marginTop: '4px'}}>
                    Select the actual date the payment was received. Defaults to today if left blank.
                  </p>
                </div>

                {/* Notes */}
                <div>
                  <label style={{display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333'}}>
                    Notes (Optional)
                  </label>
                  <textarea
                    data-testid="lump-sum-notes-input"
                    value={lumpSumNotes}
                    onChange={(e) => setLumpSumNotes(e.target.value)}
                    placeholder="e.g., Cash payment received in store"
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #dee2e6',
                      borderRadius: '6px',
                      resize: 'none'
                    }}
                  />
                </div>

                {/* Preview */}
                {parseFloat(lumpSumAmount || 0) > 0 && parseFloat(lumpSumAmount || 0) <= lumpSumCustomer.totalOutstanding && (
                  <div style={{
                    background: '#d4edda',
                    border: '1px solid #28a745',
                    borderRadius: '6px',
                    padding: '12px'
                  }}>
                    <div style={{fontWeight: '600', color: '#155724', marginBottom: '8px'}}>📊 Payment Preview</div>
                    <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px'}}>
                      <span>Current Balance:</span>
                      <span style={{fontWeight: '600'}}>£{lumpSumCustomer.totalOutstanding.toFixed(2)}</span>
                    </div>
                    <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px', color: '#28a745'}}>
                      <span>Payment Amount:</span>
                      <span style={{fontWeight: '600'}}>- £{parseFloat(lumpSumAmount || 0).toFixed(2)}</span>
                    </div>
                    <hr style={{margin: '8px 0', border: 'none', borderTop: '1px solid #28a745'}} />
                    <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: 'bold'}}>
                      <span>New Balance:</span>
                      <span style={{color: (lumpSumCustomer.totalOutstanding - parseFloat(lumpSumAmount || 0)) <= 0.01 ? '#28a745' : '#dc3545'}}>
                        £{Math.max(0, lumpSumCustomer.totalOutstanding - parseFloat(lumpSumAmount || 0)).toFixed(2)}
                      </span>
                    </div>
                    {(lumpSumCustomer.totalOutstanding - parseFloat(lumpSumAmount || 0)) <= 0.01 && (
                      <div style={{marginTop: '8px', color: '#155724', fontSize: '13px', textAlign: 'center'}}>
                        ✅ This will fully clear the customer&apos;s balance!
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer" style={{display: 'flex', gap: '10px', justifyContent: 'flex-end', padding: '15px 20px', borderTop: '1px solid #dee2e6'}}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowLumpSumModal(false)}
                style={{padding: '10px 20px'}}
              >
                Cancel
              </button>
              <button
                data-testid="lump-sum-submit-btn"
                className="btn btn-primary"
                disabled={
                  lumpSumLoading || 
                  !lumpSumAmount || 
                  parseFloat(lumpSumAmount || 0) <= 0 || 
                  parseFloat(lumpSumAmount || 0) > lumpSumCustomer.totalOutstanding
                }
                onClick={async () => {
                  const amount = parseFloat(lumpSumAmount || 0);
                  if (amount <= 0 || amount > lumpSumCustomer.totalOutstanding) {
                    alert('Please enter a valid payment amount');
                    return;
                  }
                  
                  if (!window.confirm(`Are you sure you want to record a ${lumpSumMethod.toUpperCase()} account payment of £${amount.toFixed(2)} for ${lumpSumCustomer.name}?\n\nThis will be recorded as an ACCOUNT PAYMENT - not applied to any specific bill.`)) {
                    return;
                  }
                  
                  setLumpSumLoading(true);
                  try {
                    const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
                    const response = await axios.post(
                      `${API}/api/credit-accounts/${lumpSumCustomer.id}/lump-sum-payment`,
                      {
                        amount: amount,
                        payment_method: lumpSumMethod,
                        notes: lumpSumNotes,
                        payment_date: lumpSumPaymentDate || null
                      },
                      {
                        headers: { 'Authorization': `Bearer ${token}` }
                      }
                    );
                    
                    if (response.data.success) {
                      alert(`✅ Account Payment Recorded!\n\n${response.data.note}\n\nPrevious Balance: £${response.data.previous_outstanding.toFixed(2)}\nPayment: £${response.data.amount_paid.toFixed(2)}\nNew Balance: £${response.data.new_outstanding.toFixed(2)}`);
                      setShowLumpSumModal(false);
                      
                      // Refresh account payments for this customer
                      try {
                        const paymentsResponse = await axios.get(`${API}/api/credit-accounts/${lumpSumCustomer.id}/account-payments`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (paymentsResponse.data) {
                          setCustomerAccountPayments(prev => ({
                            ...prev,
                            [lumpSumCustomer.id]: {
                              total: paymentsResponse.data.total_account_payments || 0,
                              payments: paymentsResponse.data.payments || []
                            }
                          }));
                        }
                      } catch (e) {
                        console.error('Failed to refresh account payments:', e);
                      }
                      
                      // Refresh fulfilled orders to update the Credit Accounts display
                      refreshAllData();
                    } else {
                      throw new Error(response.data.message || 'Payment failed');
                    }
                  } catch (err) {
                    console.error('Lump sum payment error:', err);
                    alert('Failed to record payment: ' + (err.response?.data?.detail || err.message));
                  } finally {
                    setLumpSumLoading(false);
                  }
                }}
                style={{
                  padding: '10px 24px',
                  background: lumpSumLoading ? '#6c757d' : '#28a745',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {lumpSumLoading ? (
                  <>⏳ Processing...</>
                ) : (
                  <>💰 Record Payment</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credit Note Modal */}
      {showCreditNoteModal && (
        <div className="modal-overlay" onClick={() => setShowCreditNoteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '700px', width: '100%', maxHeight: '90vh', overflowY: 'auto'}}>
            <div className="modal-header" style={{background: '#fd7e14', color: 'white'}}>
              <h2 style={{margin: 0}}>Credit Note</h2>
              <button className="modal-close" onClick={() => setShowCreditNoteModal(false)} style={{color: 'white', fontSize: '28px'}}>×</button>
            </div>
            <div className="modal-body" style={{padding: '20px'}}>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px'}}>
                <div>
                  <label style={{fontWeight: '600', fontSize: '13px', display: 'block', marginBottom: '4px'}}>Customer Name</label>
                  <input type="text" value={creditNoteData.customer_name} onChange={(e) => setCreditNoteData({...creditNoteData, customer_name: e.target.value})} style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}} />
                </div>
                <div>
                  <label style={{fontWeight: '600', fontSize: '13px', display: 'block', marginBottom: '4px'}}>Customer #</label>
                  <input type="text" value={creditNoteData.customer_number} onChange={(e) => setCreditNoteData({...creditNoteData, customer_number: e.target.value})} style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}} />
                </div>
              </div>
              <div style={{marginBottom: '16px'}}>
                <label style={{fontWeight: '600', fontSize: '13px', display: 'block', marginBottom: '4px'}}>Credit Note Number</label>
                <input type="text" value={creditNoteData.credit_note_number || ''} onChange={(e) => setCreditNoteData({...creditNoteData, credit_note_number: e.target.value})} style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontFamily: 'monospace', fontWeight: '600'}} />
              </div>
              <div style={{marginBottom: '16px'}}>
                <label style={{fontWeight: '600', fontSize: '13px', display: 'block', marginBottom: '4px'}}>Original Invoice Ref</label>
                <input type="text" value={creditNoteData.original_invoice || ''} onChange={(e) => setCreditNoteData({...creditNoteData, original_invoice: e.target.value})} style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}} />
              </div>
              
              <h4 style={{marginBottom: '8px', color: '#333'}}>Items</h4>
              <table style={{width: '100%', borderCollapse: 'collapse', marginBottom: '16px', fontSize: '13px'}}>
                <thead>
                  <tr style={{background: '#f8f9fa'}}>
                    <th style={{padding: '6px', textAlign: 'left', borderBottom: '2px solid #ddd'}}>Include</th>
                    <th style={{padding: '6px', textAlign: 'left', borderBottom: '2px solid #ddd'}}>Description</th>
                    <th style={{padding: '6px', textAlign: 'right', borderBottom: '2px solid #ddd'}}>Qty</th>
                    <th style={{padding: '6px', textAlign: 'right', borderBottom: '2px solid #ddd'}}>Price</th>
                    <th style={{padding: '6px', textAlign: 'right', borderBottom: '2px solid #ddd'}}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(creditNoteData.items || []).map((item, idx) => (
                    <tr key={idx} style={{opacity: item.included ? 1 : 0.4}}>
                      <td style={{padding: '6px'}}><input type="checkbox" checked={item.included} onChange={(e) => {
                        const newItems = [...creditNoteData.items];
                        newItems[idx] = {...newItems[idx], included: e.target.checked};
                        const newTotal = newItems.filter(i => i.included).reduce((s, i) => s + parseFloat(i.amount || 0), 0);
                        setCreditNoteData({...creditNoteData, items: newItems, total_amount: newTotal});
                      }} /></td>
                      <td style={{padding: '6px'}}>
                        <input type="text" value={item.description} onChange={(e) => {
                          const newItems = [...creditNoteData.items];
                          newItems[idx] = {...newItems[idx], description: e.target.value};
                          setCreditNoteData({...creditNoteData, items: newItems});
                        }} style={{width: '100%', padding: '3px', border: '1px solid #ddd', borderRadius: '3px'}} />
                      </td>
                      <td style={{padding: '6px', textAlign: 'right'}}>
                        <input type="number" value={item.quantity} onChange={(e) => {
                          const newItems = [...creditNoteData.items];
                          const qty = parseFloat(e.target.value) || 0;
                          newItems[idx] = {...newItems[idx], quantity: qty, amount: qty * item.unit_price};
                          const newTotal = newItems.filter(i => i.included).reduce((s, i) => s + parseFloat(i.amount || 0), 0);
                          setCreditNoteData({...creditNoteData, items: newItems, total_amount: newTotal});
                        }} step="0.01" style={{width: '70px', padding: '3px', border: '1px solid #ddd', borderRadius: '3px', textAlign: 'right'}} />
                      </td>
                      <td style={{padding: '6px', textAlign: 'right'}}>£{parseFloat(item.unit_price || 0).toFixed(2)}</td>
                      <td style={{padding: '6px', textAlign: 'right', fontWeight: '600'}}>£{parseFloat(item.amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px'}}>
                <div>
                  <label style={{fontWeight: '600', fontSize: '13px', display: 'block', marginBottom: '4px'}}>Reason</label>
                  <textarea value={creditNoteData.reason} onChange={(e) => setCreditNoteData({...creditNoteData, reason: e.target.value})} rows={2} placeholder="Reason for credit note..." style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', resize: 'none'}} />
                </div>
                <div>
                  <label style={{fontWeight: '600', fontSize: '13px', display: 'block', marginBottom: '4px'}}>Notes</label>
                  <textarea value={creditNoteData.notes} onChange={(e) => setCreditNoteData({...creditNoteData, notes: e.target.value})} rows={2} placeholder="Additional notes..." style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', resize: 'none'}} />
                </div>
              </div>

              <div style={{textAlign: 'right', fontSize: '18px', fontWeight: '700', color: '#dc3545', marginBottom: '16px'}}>
                Credit Total: £{parseFloat(creditNoteData.total_amount || 0).toFixed(2)}
              </div>
            </div>
            <div className="modal-footer" style={{display: 'flex', gap: '10px', justifyContent: 'flex-end', padding: '15px 20px', borderTop: '1px solid #dee2e6'}}>
              <button className="btn btn-secondary" onClick={() => setShowCreditNoteModal(false)} style={{padding: '10px 20px'}}>Cancel</button>
              <button
                className="btn"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('admin_token') || localStorage.getItem('dashboard_token');
                    const includedItems = (creditNoteData.items || []).filter(i => i.included);
                    await axios.post(`${API}/api/credit-notes`, {
                      ...creditNoteData,
                      items: includedItems,
                      total_amount: includedItems.reduce((s, i) => s + parseFloat(i.amount || 0), 0)
                    }, { headers: { 'Authorization': `Bearer ${token}` } });
                    alert('Credit note saved for accounting records.');
                  } catch (err) {
                    alert('Error saving: ' + (err.response?.data?.detail || err.message));
                  }
                }}
                style={{padding: '10px 20px', background: '#28a745', color: 'white', border: 'none'}}
              >
                Save for Records
              </button>
              <button
                className="btn"
                data-testid="print-credit-note-btn"
                onClick={() => {
                  const includedItems = (creditNoteData.items || []).filter(i => i.included);
                  const totalAmt = parseFloat(creditNoteData.total_amount || 0);
                  const cnNumber = creditNoteData.credit_note_number || ('CN-' + Date.now().toString(36).toUpperCase());
                  let itemRows = '';
                  includedItems.forEach(item => {
                    itemRows += '<tr><td>' + item.description + '</td><td style="text-align:right">' + parseFloat(item.quantity).toFixed(2) + '</td><td style="text-align:right">£' + parseFloat(item.unit_price).toFixed(2) + '</td><td style="text-align:right;font-weight:bold">£' + parseFloat(item.amount).toFixed(2) + '</td></tr>';
                  });
                  const html = '<!DOCTYPE html><html><head><title>Credit Note - ' + cnNumber + '</title><style>body{font-family:Arial;margin:40px;color:#333}h1{text-align:center;margin-bottom:5px}table{width:100%;border-collapse:collapse;margin:20px 0}th,td{padding:8px 12px;border:1px solid #ddd;font-size:13px}th{background:#f8f9fa;text-align:left}.header{text-align:center;margin-bottom:30px}.meta{display:flex;justify-content:space-between;margin-bottom:20px}.total{text-align:right;font-size:20px;font-weight:bold;margin-top:20px;color:#dc3545}@media print{body{margin:20px}}</style></head><body><div class="header"><h1>PLUSH LIVING LTD</h1><p>Making your house a home</p><p>Unit 30 Adderley Rd, Saltley, Birmingham B8 1AN<br>Tel: 0121 649 1673 | Email: Plushlivingltd@outlook.com</p><p>VAT No: 453471464 | Company Registration No: 16465782</p></div><h2 style="color:#dc3545">CREDIT NOTE</h2><div class="meta"><div><strong>Credit Note No:</strong> ' + cnNumber + '<br><strong>Date:</strong> ' + new Date().toLocaleDateString('en-GB') + '<br><strong>Original Invoice:</strong> ' + (creditNoteData.original_invoice || 'N/A') + '</div><div style="text-align:right"><strong>' + creditNoteData.customer_name + '</strong><br>Customer #: ' + creditNoteData.customer_number + '</div></div><table><thead><tr><th>Description</th><th style="text-align:right">Qty</th><th style="text-align:right">Unit Price</th><th style="text-align:right">Amount</th></tr></thead><tbody>' + itemRows + '</tbody></table><div class="total">Credit Total: £' + totalAmt.toFixed(2) + '</div>' + (creditNoteData.reason ? '<p><strong>Reason:</strong> ' + creditNoteData.reason + '</p>' : '') + (creditNoteData.notes ? '<p><strong>Notes:</strong> ' + creditNoteData.notes + '</p>' : '') + '<hr style="margin-top:40px"><p style="text-align:center;font-size:11px;color:#666">PLUSH LIVING LTD - Credit Note for accounting purposes</p></body></html>';
                  const w = window.open('', '_blank');
                  w.document.write(html);
                  w.document.close();
                  w.print();
                }}
                style={{padding: '10px 20px', background: '#fd7e14', color: 'white', border: 'none'}}
              >
                Print Credit Note
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function SignInModal({ visible, onClose, onAdminSuccess }) {
  const [email, setEmail] = useState(""); // For admin login
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const closeModal = () => {
    if (onClose) onClose();
    setLoginError("");
    setEmail("");
    setPassword("");
  };

  // Customer Google Sign-in removed per owner directive
  const handleCustomerGoogleSignIn = () => {};


  // ROBUST LOGIN: Multi-attempt login with fallback and comprehensive error handling
  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError("");

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://plush-living.co.uk';
    const loginData = { username: email, password: password };
    
    // Try admin login FIRST (this is the main login path)
    const loginUrl = `${backendUrl}/api/admin-portal/login`;
    console.log('🔐 Attempting admin login to:', loginUrl);

    // MULTI-ATTEMPT LOGIN: Try with different configurations to ensure success
    const loginAttempts = [
      // Attempt 1: Standard configuration
      {
        timeout: 10000,
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        withCredentials: false,
        name: 'standard'
      },
      // Attempt 2: Extended timeout
      {
        timeout: 20000,
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        withCredentials: false,
        name: 'extended-timeout'
      },
      // Attempt 3: With credentials
      {
        timeout: 15000,
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        withCredentials: true,
        name: 'with-credentials'
      }
    ];

    let lastError = null;
    
    for (let i = 0; i < loginAttempts.length; i++) {
      const attempt = loginAttempts[i];
      
      try {
        console.log(`🔐 Login attempt ${i + 1}/3 (${attempt.name})`);
        
        const response = await axios.post(loginUrl, loginData, attempt);

        console.log('✅ Login response received:', {
          status: response.status,
          hasToken: !!response.data?.access_token,
          tokenLength: response.data?.access_token?.length || 0,
          attempt: attempt.name
        });

        if (response.data && response.data.access_token) {
          console.log('✅ Valid token received, proceeding with authentication');
          
          // Clear form state immediately
          const loggedInUsername = email; // Store username before clearing
          setEmail("");
          setPassword("");
          setLoginError("");
          
          // Store token AND username AND permissions immediately
          localStorage.setItem('admin_token', response.data.access_token);
          localStorage.setItem('admin_username', loggedInUsername);
          
          // Store permissions from response
          if (response.data.user) {
            const userPerms = {
              allowedSections: response.data.user.allowed_sections || null,
              spectateSections: response.data.user.spectate_sections || null,
              role: response.data.user.role || null,
              priceAccessLevels: response.data.user.price_access_levels || ['retail'],
              canAccessDashboard: response.data.user.can_access_dashboard || false,
              canAccessSettings: response.data.user.can_access_settings || false
            };
            localStorage.setItem('admin_permissions', JSON.stringify(userPerms));
            console.log('📋 Stored admin permissions:', userPerms);
          }
          
          console.log('✅ Stored admin_username:', loggedInUsername);
          
          // Call parent success handler with token AND username
          if (onAdminSuccess) {
            try {
              onAdminSuccess(response.data.access_token, loggedInUsername);
            } catch (callbackError) {
              console.warn('Warning: Success callback error:', callbackError);
              // Still proceed with login since token is valid
            }
          }
          
          closeModal();
          setIsLoading(false);
          return; // SUCCESS - exit function
        }
      } catch (error) {
        console.error(`❌ Login attempt ${i + 1} failed:`, error);
        lastError = error;
        
        // If this is a 401, don't retry (wrong credentials)
        if (error.response?.status === 401) {
          break;
        }
        
        // Wait briefly before next attempt (except for last attempt)
        if (i < loginAttempts.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }

    // ALL ATTEMPTS FAILED
    console.error('❌ All login attempts failed. Last error:', lastError);
    setIsLoading(false);
    
    // Enhanced error messages
    if (lastError) {
      if (lastError.code === 'ECONNABORTED' || lastError.message.includes('timeout')) {
        setLoginError('Connection timeout. Please check your internet connection.');
      } else if (lastError?.response?.status === 401) {
        setLoginError('Invalid username or password');
      } else if (lastError?.message?.includes('Network Error')) {
        setLoginError('Network error. Please check your internet connection.');
      } else if (lastError?.response?.status >= 500) {
        setLoginError('Server error. Please try again later.');
      } else {
        setLoginError('Login failed. Please try again.');
      }
    } else {
      setLoginError('Login failed. Please try again.');
    }
  };

  if (!visible) return null;

  return (
    <div className="modal-overlay" onClick={closeModal}>
      <div className="modal-content customer-sign-in-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={closeModal}>×</button>
        <h2 className="modal-title">SIGN IN</h2>
        
        {/* Admin login section with email/password - vertically stacked */}
        <form onSubmit={handleAdminSubmit}>
          <input 
            className="form-control-new" 
            type="text"
            value={email} 
            onChange={(e) => setEmail(e.target.value)} 
            placeholder="Username" 
            required
            disabled={isLoading}
            autoComplete="username"
          />
          <input 
            type="password"
            className="form-control-new" 
            value={password} 
            onChange={(e) => setPassword(e.target.value)} 
            placeholder="Password" 
            required
            disabled={isLoading}
            autoComplete="current-password"
          />
          <button 
            type="submit" 
            className="signin-btn-new"
            disabled={isLoading || !email.trim() || !password}
          >
            {isLoading ? 'Signing In...' : 'SIGN IN'}
          </button>
          {loginError && (
            <div className="error-message-new" style={{color: '#dc2626', fontSize: '0.875rem', marginBottom: '1rem', textAlign: 'center'}}>
              {loginError}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}




// Error Boundary Component

// Wrap App with Error Boundary
const AppWithErrorBoundary = () => {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
};

export default AppWithErrorBoundary;